/**
 * Generated by orval v8.18.0 🍺
 * Do not edit manually.
 * product-x-next-service
 * OpenAPI spec version: 0.1.0
 */
import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import {
  faker
} from '@faker-js/faker';

import {
  HttpResponse,
  http
} from 'msw';
import type {
  RequestHandlerOptions
} from 'msw';

import { customFetch } from '../client';
/**
 * A period's runs and tokens. Token sums cover the runs that have counts.
 */
export interface AIModelRuns {
  run_count: number;
  unmeasured_run_count: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
}

/**
 * The runs of one entry that ran on one model; ``None`` when none was recorded.
 */
export interface AIModelByModel {
  model_key: string | null;
  runs: AIModelRuns;
}

/**
 * A profile deleted since it ran here, as the ledger recorded it (spec 208).
 */
export interface AIModelDeletedProfile {
  key: string;
  name: string;
}

/**
 * The closed set of effort levels the Claude Agent SDK accepts.
 *
 * Mirrors the ``EffortLevel`` literal ``src/worker/__main__.py`` already defines for
 * the same five values, but as a native ENUM rather than free text (research.md D2) —
 * an invalid effort is unrepresentable here rather than defensively parsed at
 * execution time the way ``AnthropicSettings.effort: str`` must be.
 */
export type LLMProfileEffort = typeof LLMProfileEffort[keyof typeof LLMProfileEffort];


export const LLMProfileEffort = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  xhigh: 'xhigh',
  max: 'max',
} as const;

/**
 * One profile as the API presents it.
 *
 * Carries no internal ``id`` — ``slug`` is the only identity anything outside this
 * feature ever needs (research.md D7 of 066): it is what a workflow step and a
 * workflow's YAML export both reference.
 */
export interface LLMProfileView {
  slug: string;
  name: string;
  ai_provider_id: string;
  model: string;
  effort: LLMProfileEffort;
  max_turns: number | null;
  description: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface AIModelUseStep {
  id: string;
  name: string;
  unpublished: boolean;
}

/**
 * One workflow of the workspace with steps on the entry's profile.
 */
export interface AIModelUse {
  workflow_id: string;
  workflow_key: string;
  workflow_name: string;
  draft: boolean;
  steps: AIModelUseStep[];
}

/**
 * One profile, a profile deleted since it ran, or the runs that named no profile.
 *
 * ``profile`` is set for a profile in the registry now. For a deleted one it is null,
 * ``deleted`` is true and ``deleted_profile`` names it; its runs keep their entry
 * rather than joining the runs that named no profile (spec 208). With all three
 * empty, the entry is those runs.
 */
export interface AIModelEntry {
  profile: LLMProfileView | null;
  deleted: boolean;
  deleted_profile: AIModelDeletedProfile | null;
  uses: AIModelUse[];
  runs: AIModelRuns;
  by_model: AIModelByModel[];
}

/**
 * One workflow, profile or connection's share of a period's spend.
 *
 * ``key`` names what the line is: a workflow's key, a profile's slug (or, for a
 * profile deleted before its run recorded a slug, the profile's id), a connection's
 * id. ``None`` for the line of runs whose workflow, profile or connection is not
 * known, which ``label`` says in words; those runs are a line, never dropped.
 */
export interface AIModelSpendLine {
  key: string | null;
  label: string;
  deleted: boolean;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency?: string;
  total_tokens: number;
  run_count: number;
  unmeasured_run_count: number;
  unpriced_run_count: number;
}

/**
 * A top-level line, and the lines inside it, ordered the same way.
 */
export interface AIModelSpendEntry {
  key: string | null;
  label: string;
  deleted: boolean;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency?: string;
  total_tokens: number;
  run_count: number;
  unmeasured_run_count: number;
  unpriced_run_count: number;
  children: AIModelSpendLine[];
}

export type AIModelWorkflowStepType = typeof AIModelWorkflowStepType[keyof typeof AIModelWorkflowStepType];


export const AIModelWorkflowStepType = {
  ai_step: 'ai_step',
  human_gate: 'human_gate',
  linked_workflow: 'linked_workflow',
} as const;

/**
 * One author step of a workflow row, in builder order (spec 173).
 */
export interface AIModelWorkflowStep {
  id: string;
  name: string;
  type: AIModelWorkflowStepType;
  order: number;
  profile_slug: string | null;
  unpublished: boolean;
}

/**
 * One workflow of the workspace, every step in order, and its runs (spec 173).
 */
export interface AIModelWorkflow {
  workflow_id: string;
  workflow_key: string;
  workflow_name: string;
  draft: boolean;
  runs: AIModelRuns;
  steps: AIModelWorkflowStep[];
}

/**
 * The outcome of the most recent live connection test for a provider.
 *
 * ``UNTESTED`` is the default for every provider until the first test call — a
 * distinct state from ``FAILED``, so "never checked" and "checked and rejected"
 * are never confused in the UI (mirrors ``LLMProfileConnectionStatus``, spec 076).
 */
export type AIProviderConnectionStatus = typeof AIProviderConnectionStatus[keyof typeof AIProviderConnectionStatus];


export const AIProviderConnectionStatus = {
  untested: 'untested',
  ok: 'ok',
  failed: 'failed',
} as const;

/**
 * Optional overrides for ``POST /api/ai-providers/{id}/test``.
 *
 * An admin editing a provider wants to know whether the credentials *in the
 * form* work, before committing them — testing the stored pair instead reports
 * on values they are in the middle of replacing, which reads as a spurious
 * failure (and did: a placeholder token left a real edit looking rejected).
 *
 * Either field omitted falls back to what is stored, so the body is optional in
 * full and ``POST`` with no body keeps its original "test the saved provider"
 * meaning. ``token`` here is a candidate value, never persisted by this call —
 * see ``AIProvidersService.test_connection`` for why the outcome of an
 * overridden check is deliberately not recorded.
 */
export interface AIProviderConnectionTestRequest {
  base_url?: string | null;
  token?: string | null;
}

/**
 * Response for ``POST /api/ai-providers/{id}/test``.
 *
 * Mirrors ``LLMProfileConnectionTestResult``'s own contract exactly: a failed
 * check is still a normal ``200`` with ``ok=False`` and a sanitized ``message`` —
 * never a raised error. This is the *immediate call result* — the provider's
 * persisted status is a separate concern, read back via ``AIProviderView`` above.
 */
export interface AIProviderConnectionTestResult {
  ok: boolean;
  message: string;
}

/**
 * The upstream LLM service an AI Provider connects to.
 *
 * One usable member today: only ``ANTHROPIC`` can be created — any other member is
 * rejected by a service-level check (research.md D2), never by narrowing this enum
 * down to one value, which would make that rejection unreachable/untestable.
 * ``OPENAI``/``MISTRAL`` exist now, reserved and unusable, so the "coming soon"
 * provider types the frontend surfaces are real, stable values rather than
 * something invented client-side.
 */
export type AIProviderType = typeof AIProviderType[keyof typeof AIProviderType];


export const AIProviderType = {
  anthropic: 'anthropic',
  openai: 'openai',
  mistral: 'mistral',
} as const;

/**
 * One provider as the API presents it.
 */
export interface AIProviderView {
  id: string;
  name: string;
  provider_type: AIProviderType;
  base_url: string;
  has_token: boolean;
  connection_status: AIProviderConnectionStatus;
  connection_message: string | null;
  connection_checked_at: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface AIProviderList {
  items: AIProviderView[];
}

/**
 * What an audit entry records. Slice 1 writes creation and deletion, slice 2 the
 * backfill; slice 3 adds the membership changes and slice 6 the admin entries.
 */
export type AccessEventKind = typeof AccessEventKind[keyof typeof AccessEventKind];


export const AccessEventKind = {
  workspace_created: 'workspace_created',
  workspace_deleted: 'workspace_deleted',
  members_backfilled: 'members_backfilled',
  member_added: 'member_added',
  member_removed: 'member_removed',
  member_left: 'member_left',
  role_changed: 'role_changed',
  visibility_changed: 'visibility_changed',
  renamed: 'renamed',
  access_requested: 'access_requested',
  access_request_dismissed: 'access_request_dismissed',
  admin_opened: 'admin_opened',
  admin_acted: 'admin_acted',
  members_review_requested: 'members_review_requested',
} as const;

export type AccessLogEntryDetail = { [key: string]: unknown };

/**
 * The workspace an entry is about, by the name it had then.
 */
export interface AccessLogWorkspace {
  id: string;
  name: string;
}

/**
 * A person, as every other feature renders them.
 *
 * The one shape used wherever a human is shown — an author, an actor on an
 * activity row, a gate's assignee. Shared rather than per-feature so the surfaces
 * cannot drift into naming the same person differently, which is the same reason
 * the frontend keeps a single actor component.
 *
 * Carries no ``subject``: that is the identity provider's opaque identifier, it is
 * meaningless to a reader, and putting it on the wire invites callers to key off
 * it instead of ``id``.
 */
export interface UserSummary {
  id: string;
  email: string;
  display_name: string;
}

/**
 * One audit entry (FR-001).
 */
export interface AccessLogEntry {
  id: string;
  workspace: AccessLogWorkspace;
  kind: AccessEventKind;
  at: string;
  actor?: UserSummary | null;
  subject?: UserSummary | null;
  as_org_admin?: boolean;
  detail?: AccessLogEntryDetail;
}

export interface AccessLogPage {
  items: AccessLogEntry[];
  next_before?: string | null;
}

/**
 * One open request, as an owner answers it.
 */
export interface AccessRequestView {
  id: string;
  user: UserSummary;
  requested_at: string;
}

export interface AccessRequests {
  items: AccessRequestView[];
}

export type ActorViewKind = typeof ActorViewKind[keyof typeof ActorViewKind];


export const ActorViewKind = {
  human: 'human',
  ai: 'ai',
  unattributed: 'unattributed',
} as const;

/**
 * Flat, discriminated-by-`kind` actor shape (research.md D1/D2).
 *
 * Exactly one of the two field pairs is populated, mirroring the database's
 * exactly-one-actor-kind CHECK constraint.
 */
export interface ActorView {
  kind: ActorViewKind;
  user_id?: string | null;
  user_name?: string | null;
  task_id?: string | null;
  step_id?: string | null;
  workflow_name?: string | null;
  step_name?: string | null;
  task_name?: string | null;
}

/**
 * A person's role in a workspace (spec 151 FR-002): owners also manage it.
 */
export type WorkspaceRole = typeof WorkspaceRole[keyof typeof WorkspaceRole];


export const WorkspaceRole = {
  owner: 'owner',
  member: 'member',
} as const;

/**
 * Grant a request: the person joins with this role (members, unless chosen).
 */
export interface AddFromRequest {
  role?: WorkspaceRole;
}

export interface AddMemberRequest {
  user_id: string;
  role?: WorkspaceRole;
}

/**
 * The task comment a thread message was copied into (FR-009).
 */
export interface AddedToTaskContextView {
  comment_id: string;
}

export type AdminWorkspaceSort = typeof AdminWorkspaceSort[keyof typeof AdminWorkspaceSort];


export const AdminWorkspaceSort = {
  name: 'name',
  last_activity: 'last_activity',
  members: 'members',
} as const;

/**
 * Who may see a workspace besides its members (spec 151 FR-001).
 *
 * ``private``: its owners and members only (and org admins). ``organization``:
 * everyone signed in may read it; only members change it.
 */
export type WorkspaceVisibility = typeof WorkspaceVisibility[keyof typeof WorkspaceVisibility];


export const WorkspaceVisibility = {
  private: 'private',
  organization: 'organization',
} as const;

export interface WorkspaceOwner {
  user: UserSummary;
  is_active: boolean;
}

/**
 * One workspace, as the admin console lists it (FR-004).
 */
export interface AdminWorkspaceView {
  id: string;
  name: string;
  visibility: WorkspaceVisibility;
  owners: WorkspaceOwner[];
  member_count: number;
  has_active_owner: boolean;
  created_at: string;
  last_activity_at: string;
}

export interface AdminWorkspaces {
  items: AdminWorkspaceView[];
  without_active_owner: number;
  total: number;
}

export type AgentSubtaskStatus = typeof AgentSubtaskStatus[keyof typeof AgentSubtaskStatus];


export const AgentSubtaskStatus = {
  done: 'done',
  needs_attention: 'needs_attention',
  needs_review: 'needs_review',
  not_started: 'not_started',
  in_progress: 'in_progress',
} as const;

/**
 * One direct child of the calling run's own task, as its agent sees it (spec 119).
 */
export interface AgentSubtask {
  id: string;
  title: string;
  description?: string | null;
  status: AgentSubtaskStatus;
}

export type AgentSubtaskDetailStatus = typeof AgentSubtaskDetailStatus[keyof typeof AgentSubtaskDetailStatus];


export const AgentSubtaskDetailStatus = {
  done: 'done',
  needs_attention: 'needs_attention',
  needs_review: 'needs_review',
  not_started: 'not_started',
  in_progress: 'in_progress',
} as const;

export type TaskActorType = typeof TaskActorType[keyof typeof TaskActorType];


export const TaskActorType = {
  user: 'user',
  system: 'system',
} as const;

/**
 * One entry in a task's timeline. Immutable, so there is no ``updated_at``.
 */
export interface TaskCommentView {
  id: string;
  task_id: string;
  body: string;
  actor_type: TaskActorType;
  actor?: UserSummary | null;
  via?: string | null;
  created_at: string;
  caused_by_signal_id?: string | null;
  caused_by_trigger_profile_name?: string | null;
  execution_id?: string | null;
}

export interface AgentSubtaskDetail {
  id: string;
  title: string;
  description?: string | null;
  status: AgentSubtaskDetailStatus;
  comments: TaskCommentView[];
}

export interface AgentSubtaskList {
  children: AgentSubtask[];
}

/**
 * A partial edit of one subtask: whichever of title and description is sent.
 */
export interface AgentSubtaskUpdate {
  title?: string | null;
  description?: string | null;
}

/**
 * One timeline entry as a run reads it through the agent view (spec 174).
 */
export interface AgentTaskComment {
  id: string;
  task_id: string;
  body: string;
  actor_type: TaskActorType;
  actor?: UserSummary | null;
  via?: string | null;
  created_at: string;
  caused_by_signal_id?: string | null;
  caused_by_trigger_profile_name?: string | null;
  execution_id?: string | null;
  new_for_this_run?: boolean;
}

/**
 * A task's whole timeline, oldest first, as a run reads it (spec 174).
 */
export interface AgentTaskCommentList {
  items: AgentTaskComment[];
}

/**
 * The last thing a person said on the run before an agent acted on it.
 */
export interface ContextComment {
  id: string;
  author?: UserSummary | null;
  excerpt: string;
  created_at: string;
}

export type ChangeEntryOp = typeof ChangeEntryOp[keyof typeof ChangeEntryOp];


export const ChangeEntryOp = {
  create: 'create',
  edit: 'edit',
  delete: 'delete',
  split: 'split',
} as const;

export type FieldChangeField = typeof FieldChangeField[keyof typeof FieldChangeField];


export const FieldChangeField = {
  title: 'title',
  description: 'description',
} as const;

export interface FieldChange {
  field: FieldChangeField;
  before: string | null;
  after: string | null;
}

export type SplitDetailDirection = typeof SplitDetailDirection[keyof typeof SplitDetailDirection];


export const SplitDetailDirection = {
  out: 'out',
  in: 'in',
} as const;

export interface MovedSubtask {
  id: string;
  title: string;
}

/**
 * ``out``: this run gave subtasks to ``other_task``; ``in``: this run was made from it.
 */
export interface SplitDetail {
  direction: SplitDetailDirection;
  other_task_id: string;
  other_task_title: string;
  moved: MovedSubtask[];
}

export type ChangeEntryActorType = typeof ChangeEntryActorType[keyof typeof ChangeEntryActorType];


export const ChangeEntryActorType = {
  user: 'user',
  ai: 'ai',
  system: 'system',
} as const;

/**
 * One change to one of the run's subtasks.
 */
export interface ChangeEntry {
  kind?: 'change';
  id: string;
  at: string;
  op: ChangeEntryOp;
  subtask_id: string;
  subtask_title: string;
  subtask_exists: boolean;
  changes: FieldChange[];
  split?: SplitDetail | null;
  actor_type: ChangeEntryActorType;
  actor?: UserSummary | null;
}

/**
 * Everything one execution's agent changed, and who set it going.
 */
export interface AiChangeGroupEntry {
  kind?: 'ai_group';
  execution_id: string;
  step_id: string;
  step_name?: string | null;
  at: string;
  triggered_by?: UserSummary | null;
  context_comment?: ContextComment | null;
  changes: ChangeEntry[];
}

/**
 * One AI tool the person has used through ``/mcp``.
 */
export interface AiToolView {
  client_name: string;
  client_version?: string | null;
  first_used_at: string;
  last_used_at: string;
}

/**
 * Latest used first.
 */
export interface AiToolList {
  items: AiToolView[];
}

export type AppliedLimitThreshold = typeof AppliedLimitThreshold[keyof typeof AppliedLimitThreshold];


export const AppliedLimitThreshold = {
  under: 'under',
  approaching: 'approaching',
  exhausted: 'exhausted',
} as const;

/**
 * How often a spend limit's current-period spend renews (spec 083 FR-001).
 *
 * ``NONE`` is a lifetime cap, not "no limit" — the limit still applies, it simply never
 * renews on its own. Four members, matching exactly the four choices an administrator is
 * offered; a fifth would need its own boundary rule written here before it could exist
 * anywhere else.
 */
export type PeriodType = typeof PeriodType[keyof typeof PeriodType];


export const PeriodType = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  none: 'none',
} as const;

/**
 * Which period a summary describes.
 *
 * Sent explicitly so no client re-derives the period rule for any of the four types
 * (spec 083). ``ends_at`` is exclusive, and ``None`` exactly when ``period_type`` is
 * ``none`` — a lifetime period has no upper bound.
 */
export interface PeriodBounds {
  key: string;
  period_type: PeriodType;
  starts_at: string;
  ends_at: string | null;
  is_current: boolean;
}

/**
 * The spend limit in force for a workspace, and where its cost sits against it.
 *
 * ``is_override`` distinguishes a limit set for this workspace from the organization
 * default it would otherwise inherit — the same distinction the console's table shows,
 * sent rather than inferred so both surfaces say the same thing.
 */
export interface AppliedLimit {
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency?: string;
  is_override: boolean;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  consumed_amount: string;
  consumed_ratio: number;
  threshold: AppliedLimitThreshold;
  period: PeriodBounds;
}

/**
 * The two things a workflow file can name that must already exist here.
 *
 * Exactly two, and closed on purpose (spec 098 FR-026). A file's every other
 * field is a literal value, checked for shape and never looked up: connections,
 * secrets, agents, labels and triggers are not reachable from a workflow
 * definition, whatever a reader of the YAML might expect.
 */
export type ReferenceKind = typeof ReferenceKind[keyof typeof ReferenceKind];


export const ReferenceKind = {
  llm_profile: 'llm_profile',
  assignee: 'assignee',
} as const;

/**
 * One substitution an import honoured (spec 098 FR-021).
 *
 * A workflow built from a file that did not fully resolve is not that file.
 * Saying so on the result is the difference between a substitution being a
 * decision the caller made and one that quietly happened to them.
 */
export interface AppliedSubstitution {
  kind: ReferenceKind;
  from_value: string;
  to_value: string;
  step_ids?: string[];
}

export type ArtifactEventAction = typeof ArtifactEventAction[keyof typeof ArtifactEventAction];


export const ArtifactEventAction = {
  created: 'created',
  pushed: 'pushed',
} as const;

export type ArtifactEventKind = typeof ArtifactEventKind[keyof typeof ArtifactEventKind];


export const ArtifactEventKind = {
  merge_request: 'merge_request',
  pull_request: 'pull_request',
  branch: 'branch',
} as const;

/**
 * One thing a run did outside the platform, as the forge reported it.
 */
export interface ArtifactEvent {
  event_id: string;
  action: ArtifactEventAction;
  occurred_at: string;
  kind: ArtifactEventKind;
  /**
     * @minLength 1
     * @maxLength 255
     */
  forge_host: string;
  /**
     * @minLength 1
     * @maxLength 512
     */
  repository: string;
  project_id?: number | null;
  number?: number | null;
  web_url?: string | null;
  title?: string | null;
  /**
     * @minLength 1
     * @maxLength 255
     */
  source_branch: string;
  target_branch?: string | null;
  commit_sha?: string | null;
}

/**
 * A run, other than the creator, that pushed to an artifact's branch.
 */
export interface ArtifactUpdateView {
  execution_id: string;
  last_pushed_at: string;
  commit_sha: string | null;
}

export interface AttachmentView {
  name: string;
  path: string;
  size?: number | null;
  content_type?: string | null;
  modified_at?: string | null;
}

export interface BodyAttachFileToTask {
  file: Blob;
  replace?: boolean;
}

export interface BodyImportBundle {
  file: Blob;
  /** `BundleImportAnswers` as JSON. */
  answers?: string | null;
}

export interface BodyImportWorkflow {
  file: Blob;
  substitutions?: string | null;
}

export interface BodyPreflightBundleImport {
  file: Blob;
  /** `BundleImportAnswers` as JSON. */
  answers?: string | null;
}

export interface BodyPreflightWorkflow {
  file: Blob;
}

export interface BodyUploadWorkspaceFile {
  path: string;
  file: Blob;
  task_id?: string | null;
  step_id?: string | null;
}

/**
 * One row of a breakdown, by workflow, model or person.
 */
export interface BreakdownEntry {
  key: string | null;
  label: string;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency?: string;
  total_tokens: number;
  run_count: number;
  unmeasured_run_count: number;
}

export type BundleImportItemKind = typeof BundleImportItemKind[keyof typeof BundleImportItemKind];


export const BundleImportItemKind = {
  workflow: 'workflow',
  agent: 'agent',
  skill: 'skill',
} as const;

export type BundleImportItemAnswer = typeof BundleImportItemAnswer[keyof typeof BundleImportItemAnswer] | null;


export const BundleImportItemAnswer = {
  use_existing: 'use_existing',
  rename: 'rename',
  replace: 'replace',
} as const;

export type BundleItemState = typeof BundleItemState[keyof typeof BundleItemState];


export const BundleItemState = {
  new: 'new',
  existing: 'existing',
  conflict: 'conflict',
  answered: 'answered',
} as const;

/**
 * One item of the bundle, and what importing it would do.
 */
export interface BundleImportItem {
  kind: BundleImportItemKind;
  key: string;
  name: string;
  root: boolean;
  state: BundleItemState;
  answer?: BundleImportItemAnswer;
  new_key?: string | null;
  suggested_key?: string | null;
  used_by?: string[];
}

/**
 * Something a file names that cannot be found here (spec 098 FR-003).
 */
export interface UnresolvedReference {
  kind: ReferenceKind;
  value: string;
  step_id?: string | null;
  blocking: boolean;
}

export type ViolationCategory = typeof ViolationCategory[keyof typeof ViolationCategory];


export const ViolationCategory = {
  parse: 'parse',
  structure: 'structure',
  unsupported_shape: 'unsupported_shape',
  conflict: 'conflict',
  link_loop: 'link_loop',
  draft_link: 'draft_link',
} as const;

/**
 * One located problem with a submitted workflow definition.
 */
export interface ImportViolation {
  category: ViolationCategory;
  message: string;
  path?: string | null;
  line?: number | null;
  column?: number | null;
  limit?: number | null;
  actual?: number | null;
  step_id?: string | null;
  transition_key?: string | null;
  phase_id?: string | null;
  field?: string | null;
}

/**
 * What importing this bundle would do, with these answers, without doing it.
 */
export interface BundleImportPreview {
  root: string;
  source_workspace?: string | null;
  exported_at?: string | null;
  items: BundleImportItem[];
  unresolved?: UnresolvedReference[];
  violations?: ImportViolation[];
  blockers?: string[];
  importable: boolean;
}

export type LibraryItemKind = typeof LibraryItemKind[keyof typeof LibraryItemKind];


export const LibraryItemKind = {
  agent: 'agent',
  skill: 'skill',
  workflow: 'workflow',
} as const;

export type LibraryOutcome = typeof LibraryOutcome[keyof typeof LibraryOutcome];


export const LibraryOutcome = {
  added: 'added',
  already_installed: 'already_installed',
  updated: 'updated',
  refused: 'refused',
} as const;

export interface LibraryItemOutcome {
  kind: LibraryItemKind;
  key: string;
  outcome: LibraryOutcome;
  reason?: string | null;
}

export type WorkflowInputType = typeof WorkflowInputType[keyof typeof WorkflowInputType];


export const WorkflowInputType = {
  text: 'text',
  number: 'number',
  boolean: 'boolean',
  date: 'date',
  select: 'select',
  workspace_path: 'workspace_path',
  repository: 'repository',
} as const;

export interface JsonValue {}

export type WorkflowInputDefConfiguration = {[key: string]: JsonValue};

/**
 * Read projection of one stored workflow input (unchanged shape; tasks consume it).
 */
export interface WorkflowInputDef {
  key: string;
  name: string;
  description?: string | null;
  input_type: WorkflowInputType;
  required?: boolean;
  display_order?: number;
  default_value?: JsonValue | null;
  configuration?: WorkflowInputDefConfiguration;
}

/**
 * PRD-vocabulary step type; ``backlog``/``done`` occur only on system steps.
 */
export type WorkflowStepType = typeof WorkflowStepType[keyof typeof WorkflowStepType];


export const WorkflowStepType = {
  ai_step: 'ai_step',
  human_gate: 'human_gate',
  linked_workflow: 'linked_workflow',
  backlog: 'backlog',
  done: 'done',
} as const;

/**
 * Human Gate mode (PRD §23 ``mode``); NULL on every non-gate step.
 */
export type WorkflowGateMode = typeof WorkflowGateMode[keyof typeof WorkflowGateMode];


export const WorkflowGateMode = {
  always_human: 'always_human',
  ai_or_human: 'ai_or_human',
} as const;

/**
 * Whether a ``linked_workflow`` step's created tasks start on their own (spec 125).
 *
 * NULL on every other step, and absent on a sub-workflow step means ``auto``.
 */
export type StartChildren = typeof StartChildren[keyof typeof StartChildren];


export const StartChildren = {
  auto: 'auto',
  manual: 'manual',
} as const;

/**
 * A directed transition owned by its containing (source) step — no source field.
 */
export interface WorkflowTransitionDef {
  key: string;
  to: string;
  when: string;
  label?: string | null;
  traversal_limit?: number | null;
}

export interface WorkflowStepDef {
  id: string;
  name: string;
  type: WorkflowStepType;
  prompt?: string | null;
  llm?: string | null;
  image?: string | null;
  mode?: WorkflowGateMode | null;
  assignee_user_id?: string | null;
  assignee?: UserSummary | null;
  workflow?: string | null;
  start_children?: StartChildren | null;
  scheduling_instructions?: string | null;
  transitions?: WorkflowTransitionDef[];
}

export interface WorkflowPhaseDef {
  id: string;
  name: string;
  steps?: WorkflowStepDef[];
}

/**
 * A synthesized system step (start/done) — never a member of any phase (D6).
 */
export interface WorkflowSystemStep {
  id: string;
  type: WorkflowStepType;
  name: string;
}

/**
 * Lifecycle state (spec 005 FR-001).
 *
 * A ``draft`` may be incomplete — promptless steps, empty transition conditions,
 * steps parked in the reserved ``$unassigned`` phase — and is held only to
 * well-formedness. A ``published`` workflow is held to the complete validation
 * pipeline on *every* write, so it can never become broken while tasks run on it.
 * The transition is one-way: there is no unpublish (FR-008).
 */
export type WorkflowStatus = typeof WorkflowStatus[keyof typeof WorkflowStatus];


export const WorkflowStatus = {
  draft: 'draft',
  published: 'published',
} as const;

/**
 * One node's offset, relative to the band of the phase that owns it.
 */
export interface WorkflowNodePosition {
  x: number;
  y: number;
}

export type WorkflowLayoutNodes = {[key: string]: WorkflowNodePosition};

/**
 * Where the builder put each node. Presentation data, stored opaquely.
 *
 * Keys are step ids, plus the two reserved system ids. The service never
 * interprets the geometry — it validates the shape and that the ids resolve
 * (V-13), prunes entries for nodes the saved definition no longer contains
 * (FR-026), and otherwise hands the numbers back unchanged.
 */
export interface WorkflowLayout {
  nodes?: WorkflowLayoutNodes;
}

export interface WorkflowView {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  display_order: number;
  created_at: string;
  inputs: WorkflowInputDef[];
  phases: WorkflowPhaseDef[];
  start_step: WorkflowSystemStep;
  done_step: WorkflowSystemStep;
  status: WorkflowStatus;
  version: number;
  layout?: WorkflowLayout | null;
  start?: string | null;
  created_by?: UserSummary | null;
  image?: string | null;
  has_pending_changes?: boolean;
  pending_saved_by?: UserSummary | null;
  pending_saved_at?: string | null;
  pending_outdated?: boolean;
  last_saved_by?: UserSummary | null;
  last_saved_at?: string | null;
}

/**
 * What an import did, per item; ``blocked`` when profiles or people need answers.
 */
export interface BundleImportResult {
  blocked?: boolean;
  outcomes?: LibraryItemOutcome[];
  unresolved?: UnresolvedReference[];
  substitutions?: AppliedSubstitution[];
  workflow?: WorkflowView | null;
}

export type BundleItemKind = typeof BundleItemKind[keyof typeof BundleItemKind];


export const BundleItemKind = {
  agent: 'agent',
  skill: 'skill',
} as const;

/**
 * An agent or a skill in the workspace that the person may send along.
 */
export interface BundleItem {
  kind: BundleItemKind;
  key: string;
  suggested: boolean;
}

/**
 * One workflow that would travel.
 */
export interface BundleWorkflow {
  key: string;
  name: string;
  root: boolean;
  has_pending_changes: boolean;
}

/**
 * What one workflow's bundle would hold, and whether it can be made.
 */
export interface BundlePreview {
  root: string;
  workflows: BundleWorkflow[];
  agents: BundleItem[];
  skills: BundleItem[];
  blockers: string[];
}

/**
 * Return one gate on one task to the workflow's assignee (FR-032).
 */
export interface ClearStepAssigneeRequest {
  /** @minLength 1 */
  step_id: string;
}

/**
 * The address the person's agent commits are authored with (spec 138 US3).
 *
 * For someone whose forge account uses a different email from their sign-in one, so
 * their commits link to their forge profile. ``null`` clears it.
 */
export interface CommitEmailChange {
  commit_email: string | null;
}

/**
 * A limit as it stands after being set.
 */
export interface ConcurrencyLimitView {
  workspace_id: string | null;
  value: number;
}

export type ConnectionCreateConfig = {[key: string]: JsonValue};

/**
 * Body of the create-connection endpoint (FR-001, FR-002).
 *
 * ``enabled`` is required, with no default, mirroring
 * :class:`~app.features.triggers.contracts.TriggerProfileCreate`'s identical
 * choice for the identical reason: "whether it is enabled" is something a
 * caller states explicitly, not something the system infers.
 */
export interface ConnectionCreate {
  type: string;
  name: string;
  description?: string | null;
  enabled: boolean;
  config: ConnectionCreateConfig;
}

/**
 * Whether a connection worked the last time anything used it (spec 124 FR-001).
 */
export type ConnectionHealthStatus = typeof ConnectionHealthStatus[keyof typeof ConnectionHealthStatus];


export const ConnectionHealthStatus = {
  ok: 'ok',
  failing: 'failing',
} as const;

/**
 * What the last check of a connection found (spec 124 FR-001).
 *
 * ``reason`` is ``credentials_rejected``, ``access_denied`` or ``test_failed`` while
 * ``failing``, and ``None`` while ``ok``. ``message`` is the sanitized tester message
 * behind ``test_failed`` — never a secret, never a raw provider response.
 */
export interface ConnectionHealth {
  status: ConnectionHealthStatus;
  reason?: string | null;
  message?: string | null;
  checked_at: string;
}

export type ConnectionProviderFieldViewKind = typeof ConnectionProviderFieldViewKind[keyof typeof ConnectionProviderFieldViewKind];


export const ConnectionProviderFieldViewKind = {
  secret: 'secret',
  url: 'url',
  text: 'text',
  toggle: 'toggle',
} as const;

export interface ConnectionProviderFieldView {
  name: string;
  label: string;
  kind: ConnectionProviderFieldViewKind;
  required: boolean;
  advanced: boolean;
  default: string | null;
  help: string | null;
  env: string | null;
  depends_on?: string | null;
}

/**
 * One catalog type as the Connect sheet needs it — the UI holds no provider
 * knowledge of its own.
 */
export interface ConnectionProviderView {
  type: string;
  display_name: string;
  category: string;
  blurb: string;
  fields: ConnectionProviderFieldView[];
  exposed_env: string[];
  token_env: string;
  token_url: string;
  token_scopes: string[];
  multi_instance?: boolean;
}

/**
 * The outcome of one live "test connection" call (FR-003, US1 AC2).
 *
 * ``ok=False`` is this endpoint's normal outcome for a rejected credential or
 * an unreachable host — never a 5xx (contracts/connections-api.md). ``message``
 * is always sanitized: never the resolved secret value, never a raw exception
 * traceback (SC-006).
 */
export interface ConnectionTestResult {
  ok: boolean;
  message: string;
}

export type ConnectionUpdateConfig = {[key: string]: JsonValue} | null;

/**
 * Any of ``name``/``description``/``enabled``/``config`` (contracts/
 * connections-api.md's PATCH section). ``type`` is not editable — changing a
 * connection's provider type would invalidate whatever ``config`` shape it
 * already holds, so a caller creates a new connection instead.
 *
 * Every field defaults to ``None``, meaning "leave unchanged" — mirroring
 * :meth:`~app.features.connections.repository.ConnectionRepository.update`'s
 * own partial-update convention (itself mirroring
 * :class:`~app.features.labels.contracts.UpdateLabelRequest`) — a body
 * setting none of them is a no-op, not an error.
 */
export interface ConnectionUpdate {
  name?: string | null;
  description?: string | null;
  enabled?: boolean | null;
  config?: ConnectionUpdateConfig;
}

export type ConnectionViewConfig = {[key: string]: JsonValue};

/**
 * One connection as the API presents it — the persisted row, plus the read-
 * only ``trigger_profile_count`` (US1 AC5), never a resolved secret value.
 */
export interface ConnectionView {
  id: string;
  workspace_id: string;
  type: string;
  name: string;
  description: string | null;
  enabled: boolean;
  config: ConnectionViewConfig;
  created_at: string;
  updated_at: string;
  trigger_profile_count: number;
  exposed_env: string[];
  health?: ConnectionHealth | null;
}

export interface CreateAIProviderRequest {
  name: string;
  provider_type?: AIProviderType;
  base_url: string;
  token: string;
}

export interface CreateLLMProfileRequest {
  slug: string;
  name: string;
  ai_provider_id: string;
  model: string;
  effort: LLMProfileEffort;
  max_turns?: number | null;
  description?: string | null;
}

/**
 * Eight appearance slots, deliberately named for nothing.
 *
 * The database stores a *slot*, never a colour value. The brand palette lives
 * in the frontend's token layer, so a colour here would be a second copy that
 * can drift from the first and cannot be restyled without a data migration —
 * and a free colour would let a caller store one the board already uses to mean
 * something (eight of that palette's ten hues are bound to task state).
 *
 * A hue name such as ``purple`` would reintroduce exactly the semantic
 * collision the indirection exists to prevent, which is why these are numbers.
 */
export type LabelColor = typeof LabelColor[keyof typeof LabelColor];


export const LabelColor = {
  slot_1: 'slot_1',
  slot_2: 'slot_2',
  slot_3: 'slot_3',
  slot_4: 'slot_4',
  slot_5: 'slot_5',
  slot_6: 'slot_6',
  slot_7: 'slot_7',
  slot_8: 'slot_8',
} as const;

/**
 * Define a label. ``color`` is optional on purpose.
 *
 * When absent the service assigns the least-used slot in the workspace, so
 * creating a label is a one-field act — colour is not a decision anyone wants
 * at the moment they are trying to type a name.
 */
export interface CreateLabelRequest {
  /**
     * @minLength 1
     * @maxLength 32
     */
  name: string;
  color?: LabelColor | null;
}

/**
 * Body of the execution-scoped create-and-relate route (User Story 4, research D12).
 *
 * ``labels`` names labels by what they mean, not by id — an agent cannot know a
 * workspace's label ids, and normally can't know which names already exist
 * either. The service resolves each to a label, creating any the workspace
 * lacks yet (get-or-create); duplicates and blanks are dropped rather than
 * rejected, since there is no form here for a caller to correct.
 */
export interface CreateRelatedTaskRequest {
  workflow_id: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  description?: string | null;
  /**
     * @items.minLength 1
     * @items.maxLength 32
     */
  labels?: string[];
}

export type TaskStatus = typeof TaskStatus[keyof typeof TaskStatus];


export const TaskStatus = {
  active: 'active',
} as const;

export type TaskWorkdirStatus = typeof TaskWorkdirStatus[keyof typeof TaskWorkdirStatus];


export const TaskWorkdirStatus = {
  creating: 'creating',
  available: 'available',
  failed: 'failed',
  reclaimed: 'reclaimed',
} as const;

export interface TaskWorkdirView {
  pvc_name: string;
  status: TaskWorkdirStatus;
  last_used_at?: string | null;
}

export type TaskExecutionStatus = typeof TaskExecutionStatus[keyof typeof TaskExecutionStatus];


export const TaskExecutionStatus = {
  initial: 'initial',
  orchestrating: 'orchestrating',
  provisioning: 'provisioning',
  submitting: 'submitting',
  submitted: 'submitted',
  running: 'running',
  completed: 'completed',
  failed: 'failed',
  skipped: 'skipped',
  blocked: 'blocked',
  stopped: 'stopped',
  awaiting_children: 'awaiting_children',
  queued: 'queued',
} as const;

export type WorkflowStepKind = typeof WorkflowStepKind[keyof typeof WorkflowStepKind];


export const WorkflowStepKind = {
  backlog: 'backlog',
  ai: 'ai',
  human: 'human',
  done: 'done',
  linked_workflow: 'linked_workflow',
} as const;

/**
 * Which tier of spend limit refused a run (spec 080 FR-014, FR-034).
 *
 * Two members, and the organization default is deliberately not one of them. The
 * default refuses nothing directly: it refuses only by *being* a workspace's applied
 * limit, so a run stopped because no override existed was still stopped at the
 * workspace tier. A third member would answer "which cap do I show this person?" with
 * something that points at no cap they can see.
 *
 * Lives here rather than in ``tasks`` because the concept is this feature's, and is
 * imported by ``task_execution`` the way ``workflow_step_kind`` is imported from
 * ``workflows``: one enum object, so the two declarations cannot drift.
 */
export type SpendLimitScope = typeof SpendLimitScope[keyof typeof SpendLimitScope];


export const SpendLimitScope = {
  workspace: 'workspace',
  workflow: 'workflow',
} as const;

/**
 * Why a queued run is waiting (spec 109 FR-029, research R9).
 */
export type QueuedReason = typeof QueuedReason[keyof typeof QueuedReason];


export const QueuedReason = {
  workspace_limit: 'workspace_limit',
  system_limit: 'system_limit',
  admitting: 'admitting',
} as const;

/**
 * Outcome of capturing an execution's file changes (FR-009, data-model.md).
 *
 * The three must stay distinguishable: `incomplete` is a limit, `failed` is an
 * error, and only `captured` claims the record is whole. Null on `task_execution`
 * is a fourth state meaning capture never ran for that execution.
 */
export type FileCaptureStatus = typeof FileCaptureStatus[keyof typeof FileCaptureStatus];


export const FileCaptureStatus = {
  captured: 'captured',
  incomplete: 'incomplete',
  failed: 'failed',
} as const;

export type MemoryStopViewCause = typeof MemoryStopViewCause[keyof typeof MemoryStopViewCause];


export const MemoryStopViewCause = {
  budget: 'budget',
  pod_headroom: 'pod_headroom',
  daemon_headroom: 'daemon_headroom',
} as const;

/**
 * One command the run's memory guard stopped (spec 148). ``peak_mib`` is sampled —
 * the highest seen, not a measurement.
 */
export interface MemoryStopView {
  command: string;
  tool_use_id?: string | null;
  cause: MemoryStopViewCause;
  budget_mib: number;
  peak_mib: number;
  exit_code: number;
  stopped_at: string;
}

export type TaskExecutionSummaryCredentialSource = typeof TaskExecutionSummaryCredentialSource[keyof typeof TaskExecutionSummaryCredentialSource] | null;


export const TaskExecutionSummaryCredentialSource = {
  personal: 'personal',
  shared: 'shared',
  none: 'none',
  required: 'required',
} as const;

export type ForgeProvider = typeof ForgeProvider[keyof typeof ForgeProvider];


export const ForgeProvider = {
  gitlab: 'gitlab',
  github: 'github',
} as const;

export type RunForgeCredentialViewSource = typeof RunForgeCredentialViewSource[keyof typeof RunForgeCredentialViewSource];


export const RunForgeCredentialViewSource = {
  personal: 'personal',
  shared: 'shared',
  required: 'required',
} as const;

export type RunForgeCredentialViewReason = typeof RunForgeCredentialViewReason[keyof typeof RunForgeCredentialViewReason] | null;


export const RunForgeCredentialViewReason = {
  not_linked: 'not_linked',
  broken: 'broken',
  expired: 'expired',
  revoked: 'revoked',
  inactive_owner: 'inactive_owner',
  unreachable: 'unreachable',
} as const;

/**
 * What one forge connection of a run carried (stored on the run, FR-009).
 */
export interface RunForgeCredentialView {
  provider: ForgeProvider;
  host: string;
  source: RunForgeCredentialViewSource;
  forge_username?: string | null;
  reason?: RunForgeCredentialViewReason;
}

export interface TaskExecutionSummary {
  id: string;
  status: TaskExecutionStatus;
  step_id_snapshot: string;
  step_kind_snapshot: WorkflowStepKind;
  result_summary?: string | null;
  error_message?: string | null;
  blocked_scope?: SpendLimitScope | null;
  queue_position?: number | null;
  queued_reason?: QueuedReason | null;
  started_at?: string | null;
  ended_at?: string | null;
  created_at: string;
  chosen_transition_key?: string | null;
  chosen_transition_reason?: string | null;
  is_join_decision?: boolean;
  file_capture_status?: FileCaptureStatus | null;
  file_capture_reason?: string | null;
  model_snapshot?: string | null;
  cost?: string | null;
  currency?: string;
  max_turns?: number | null;
  num_turns?: number | null;
  memory_mib?: number | null;
  memory_budget_mib?: number | null;
  memory_enforced?: boolean | null;
  memory_stops?: MemoryStopView[];
  started_by?: UserSummary | null;
  credential_source?: TaskExecutionSummaryCredentialSource;
  forge_credentials?: RunForgeCredentialView[];
}

/**
 * A label as a *task* carries it — the label, plus who attached it.
 *
 * Not :class:`LabelView`: a label a person chose and a label a run stamped are
 * not the same fact, and a consumer that cannot tell them apart cannot act on
 * the difference. ``actor_type`` belongs to the assignment rather than the
 * label, so the same label may read differently on two tasks.
 */
export interface TaskLabelView {
  id: string;
  name: string;
  color: LabelColor;
  actor_type: TaskActorType;
}

export type LinkedTaskSummaryItemStatus = typeof LinkedTaskSummaryItemStatus[keyof typeof LinkedTaskSummaryItemStatus];


export const LinkedTaskSummaryItemStatus = {
  done: 'done',
  needs_attention: 'needs_attention',
  needs_review: 'needs_review',
  not_started: 'not_started',
  in_progress: 'in_progress',
} as const;

/**
 * One created task in an open `linked_workflow` join's batch — just
 * enough for the board card to name and color it without a second fetch.
 */
export interface LinkedTaskSummaryItem {
  task_id: string;
  title: string;
  status: LinkedTaskSummaryItemStatus;
}

export type SchedulerSummaryState = typeof SchedulerSummaryState[keyof typeof SchedulerSummaryState];


export const SchedulerSummaryState = {
  idle: 'idle',
  picking: 'picking',
  stalled: 'stalled',
} as const;

/**
 * What the scheduler is doing for a batch whose tasks it starts (spec 183 FR-012).
 *
 * ``picking``: a decision is in flight. ``stalled``: the last decision started nothing
 * (or failed) while nothing in the batch runs and tasks still wait, so a person must
 * start one. ``idle``: otherwise.
 */
export interface SchedulerSummary {
  state: SchedulerSummaryState;
  last_reason?: string | null;
  last_decided_at?: string | null;
}

/**
 * How many tasks an open `linked_workflow` join created, and how many of
 * those have reached their own workflow's Done step (spec 097 US5,
 * contracts/api-changes.md). Mirrors :class:`app.features.tasks.repository.
 * LinkedBatchStatus`, whose join-completion check (T016) already counts the
 * same batch this contract-facing shape only renders.
 */
export interface LinkedTasksSummary {
  done: number;
  total: number;
  not_started?: number;
  tasks?: LinkedTaskSummaryItem[];
  workflow_name?: string | null;
  scheduler?: SchedulerSummary | null;
}

export type TaskViewInputValues = {[key: string]: JsonValue};

export interface TaskView {
  id: string;
  workflow_id: string;
  title: string;
  description?: string | null;
  folder_path: string;
  input_values: TaskViewInputValues;
  position: string;
  version: number;
  status: TaskStatus;
  created_at: string;
  updated_at: string;
  workdir?: TaskWorkdirView | null;
  executions?: TaskExecutionSummary[];
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  subtree_cost?: string;
  subtree_cost_complete?: boolean;
  chain_in_progress?: boolean;
  hidden_at?: string | null;
  source_task_id?: string | null;
  created_by_execution_id?: string | null;
  parent_task_id?: string | null;
  labels?: TaskLabelView[];
  created_by?: UserSummary | null;
  created_via?: string | null;
  comment_count?: number;
  assignee?: UserSummary | null;
  assignee_is_override?: boolean;
  needs_review?: boolean;
  needs_attention?: boolean;
  linked_tasks?: LinkedTasksSummary | null;
  is_subtask?: boolean;
}

export type TaskRelationViewRelationType = typeof TaskRelationViewRelationType[keyof typeof TaskRelationViewRelationType];


export const TaskRelationViewRelationType = {
  plain: 'plain',
  blocks: 'blocks',
  relates_to: 'relates_to',
  duplicates: 'duplicates',
} as const;

export interface TaskRelationView {
  id: string;
  workspace_id: string;
  parent_task_id: string;
  child_task_id: string;
  relation_type: TaskRelationViewRelationType;
  created_at: string;
  parent_task_title: string;
  parent_task_workflow_id: string;
  parent_task_hidden: boolean;
  child_task_title: string;
  child_task_workflow_id: string;
  child_task_hidden: boolean;
}

export interface CreateRelatedTaskResponse {
  task: TaskView;
  relation: TaskRelationView;
}

export interface CreateSchedulerNoteRequest {
  /**
     * @minLength 1
     * @maxLength 2000
     */
  body: string;
}

/**
 * Something to say about a task.
 *
 * ``actor_type`` defaults to a person (FR-003): the overwhelming majority of
 * writes are somebody typing, and a caller recording what an agent said states
 * so explicitly rather than relying on a default it did not choose.
 */
export interface CreateTaskCommentRequest {
  /**
     * @minLength 1
     * @maxLength 10000
     */
  body: string;
  actor_type?: TaskActorType;
}

export interface CreateTaskRelationRequest {
  parent_task_id: string;
  child_task_id: string;
  relation_type?: string | null;
}

export interface CreateWorkspaceRequest {
  /** @minLength 1 */
  name: string;
}

/**
 * Whether a stored value may be read back after it is written.
 */
export type WorkspaceSecretKind = typeof WorkspaceSecretKind[keyof typeof WorkspaceSecretKind];


export const WorkspaceSecretKind = {
  secret: 'secret',
  variable: 'variable',
} as const;

export interface CreateWorkspaceSecretRequest {
  key: string;
  kind: WorkspaceSecretKind;
  value: string;
  description?: string | null;
}

/**
 * The signed-in person, plus what the token says they may do.
 *
 * ``roles`` was included so the frontend could hide a future administrative surface
 * without a second request. That future arrived: spec 072 gates the organization
 * console on it, which is why ``is_org_admin`` is now computed here rather than left
 * to each client to derive from the raw list.
 *
 * Both are sent. The raw ``roles`` stays because a client may need a role this
 * contract has no field for; ``is_org_admin`` exists so no client has to know the
 * realm role's *name* — that string is shared with Keycloak's realm definition and the
 * seeding script, and a fourth place to spell it wrong is a fourth place to fail
 * silently.
 */
export interface CurrentUserView {
  id: string;
  email: string;
  display_name: string;
  roles: string[];
  is_org_admin?: boolean;
}

/**
 * One line's share of one day, keyed and labelled as its period line is (spec 207).
 *
 * A client joins a day's part to the period breakdown's line by ``key``. A day's parts
 * add up to the day: money, tokens, runs and unmeasured runs.
 */
export interface DailyPart {
  key: string | null;
  label: string;
  deleted: boolean;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency?: string;
  total_tokens: number;
  run_count: number;
  unmeasured_run_count: number;
}

/**
 * One day's consumption within a period.
 *
 * Only days with activity appear. A client drawing a bar per day fills the gaps;
 * sending thirty zero rows so it need not would triple a quiet workspace's payload.
 */
export interface DailyEntry {
  date: string;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency?: string;
  total_tokens: number;
  run_count: number;
  unmeasured_run_count: number;
  parts?: DailyPart[] | null;
}

/**
 * One day of a period and its parts, for a read whose days carry no total of
 * their own (the AI models spend read's profile split, spec 207 FR-004).
 */
export interface DailySpendParts {
  date: string;
  parts: DailyPart[];
}

/**
 * What a day's figures may be split by on the usage summary (spec 207 FR-001).
 */
export type DailySplit = typeof DailySplit[keyof typeof DailySplit];


export const DailySplit = {
  workflow: 'workflow',
  person: 'person',
  model: 'model',
} as const;

/**
 * A task's direct relations (depth-1, as either parent or child), each with
 * its own relation id — needed to delete a specific relation, unlike
 * :class:`RelatedTaskItem` which only labels graph position (spec 063,
 * contracts/task-relations-api.md).
 */
export interface DirectTaskRelationsResponse {
  task_id: string;
  relations: TaskRelationView[];
}

export type ErrorDetails = {[key: string]: number} | null;

/**
 * Standard domain error envelope returned by the global handler.
 *
 * ``error`` carries a stable machine-readable code (a per-feature string, e.g.
 * ``"WIDGET_NOT_FOUND"``). Document this model on public routes via the route
 * ``responses=`` map so the generated frontend client exposes typed errors.
 */
export interface Error {
  error: string;
  message?: string | null;
  details?: ErrorDetails;
}

/**
 * How long something needing action may wait unacknowledged before it escalates
 * (spec 123): the organization's one setting. ``off`` is the default.
 */
export type EscalationDelay = typeof EscalationDelay[keyof typeof EscalationDelay];


export const EscalationDelay = {
  off: 'off',
  '1h': '1h',
  '4h': '4h',
  '24h': '24h',
} as const;

export type ExecutionArtifactRefRole = typeof ExecutionArtifactRefRole[keyof typeof ExecutionArtifactRefRole];


export const ExecutionArtifactRefRole = {
  created: 'created',
  updated: 'updated',
} as const;

/**
 * An artifact one run created or pushed to, as listed on that run's result.
 */
export interface ExecutionArtifactRef {
  artifact_id: string;
  role: ExecutionArtifactRefRole;
}

/**
 * Add an image to the catalog (US2). ``is_default`` is never set here — it
 * defaults to ``false``; an image becomes the default only via the dedicated
 * ``set-default`` endpoint (US3, single add action per image, per spec SC-005).
 */
export interface ExecutionImageCreate {
  connection_id: string;
  image_reference: string;
  label: string;
  description?: string | null;
}

/**
 * Partial update of a catalog entry (contracts/execution-images-api.md's PATCH
 * section). Any field left unset is left unchanged; ``connection_id`` is
 * revalidated against the connections feature the same way ``create`` does
 * whenever it (or ``image_reference``) is supplied.
 */
export interface ExecutionImageUpdate {
  label?: string | null;
  image_reference?: string | null;
  connection_id?: string | null;
  description?: string | null;
}

/**
 * One catalog entry as the API presents it.
 */
export interface ExecutionImageView {
  id: string;
  workspace_id: string;
  connection_id: string | null;
  image_reference: string;
  label: string;
  description?: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export type ResultLinkKind = typeof ResultLinkKind[keyof typeof ResultLinkKind];


export const ResultLinkKind = {
  pull_request: 'pull_request',
  merge_request: 'merge_request',
  other: 'other',
} as const;

/**
 * One thing a run created outside the workspace, with a working address.
 */
export interface ResultLink {
  /**
     * @minLength 1
     * @maxLength 2048
     * @pattern ^https?://
     */
  url: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  label: string;
  kind: ResultLinkKind;
}

/**
 * A stored result as read back: the summary and its verified links.
 */
export interface ExecutionResultBody {
  summary: string;
  links: ResultLink[];
}

/**
 * One finished run of the task, with its result or an explicit none.
 */
export interface ExecutionResultView {
  execution_id: string;
  step_id: string;
  status: TaskExecutionStatus;
  started_at: string | null;
  ended_at: string | null;
  result: ExecutionResultBody | null;
  artifacts?: ExecutionArtifactRef[];
  artifact_record_incomplete?: boolean;
}

export type TextEntryPhase = typeof TextEntryPhase[keyof typeof TextEntryPhase];


export const TextEntryPhase = {
  main: 'main',
  transition_selection: 'transition_selection',
} as const;

export type TextEntryRole = typeof TextEntryRole[keyof typeof TextEntryRole];


export const TextEntryRole = {
  assistant: 'assistant',
  user: 'user',
} as const;

export interface TextEntry {
  type?: 'text';
  phase: TextEntryPhase;
  role: TextEntryRole;
  content: string;
}

export type ToolCallEntryPhase = typeof ToolCallEntryPhase[keyof typeof ToolCallEntryPhase];


export const ToolCallEntryPhase = {
  main: 'main',
  transition_selection: 'transition_selection',
} as const;

export type ToolCallEntryInput = { [key: string]: unknown };

export interface ToolCallEntry {
  type?: 'tool_call';
  phase: ToolCallEntryPhase;
  tool_name: string;
  input: ToolCallEntryInput;
  summary?: string | null;
}

export type ToolResultEntryPhase = typeof ToolResultEntryPhase[keyof typeof ToolResultEntryPhase];


export const ToolResultEntryPhase = {
  main: 'main',
  transition_selection: 'transition_selection',
} as const;

export interface ToolResultEntry {
  type?: 'tool_result';
  phase: ToolResultEntryPhase;
  tool_use_id: string;
  is_error: boolean;
  content: string;
}

export type ResultEntryPhase = typeof ResultEntryPhase[keyof typeof ResultEntryPhase];


export const ResultEntryPhase = {
  main: 'main',
  transition_selection: 'transition_selection',
} as const;

export type ResultEntryUsage = { [key: string]: unknown } | null;

export interface ResultEntry {
  type?: 'result';
  phase: ResultEntryPhase;
  duration_ms?: number | null;
  cost?: number | null;
  num_turns?: number | null;
  stop_reason?: string | null;
  usage?: ResultEntryUsage;
}

/**
 * One run's activity record, fetched where it is rendered (081 US3).
 *
 * Split out of :class:`TaskExecutionSummary`, which the workspace aggregate carries
 * and a board polls every two seconds. A transcript is uncapped and only a detail
 * surface displays it, so it is requested once when somebody opens a run rather than
 * for every run on every poll (081 FR-010).
 */
export interface ExecutionTranscriptView {
  transcript?: (TextEntry | ToolCallEntry | ToolResultEntry | ResultEntry)[] | null;
  prompt_snapshot?: string | null;
}

export interface ExternalReferenceView {
  kind: string;
  connection_id: string;
  repository: string;
  external_number: number;
}

/**
 * Pydantic-facing mirror of `app.features.files.models.FileChangeEventType`.
 */
export type FileChangeEventType = typeof FileChangeEventType[keyof typeof FileChangeEventType];


export const FileChangeEventType = {
  created: 'created',
  modified: 'modified',
  deleted: 'deleted',
  moved: 'moved',
} as const;

export interface FileChangeEventView {
  id: string;
  workspace_id: string;
  path: string;
  event_type: FileChangeEventType;
  actor: ActorView;
  occurred_at: string;
  source_path?: string | null;
  execution_id?: string | null;
  via?: string | null;
}

export type FileEntryKind = typeof FileEntryKind[keyof typeof FileEntryKind];


export const FileEntryKind = {
  file: 'file',
  directory: 'directory',
} as const;

export interface FileEntry {
  name: string;
  path: string;
  kind: FileEntryKind;
  size?: number | null;
  content_type?: string | null;
  modified_at?: string | null;
}

export interface FileHistoryPage {
  items: FileChangeEventView[];
  total: number;
  limit: number;
  offset: number;
}

export interface FileListing {
  path: string;
  entries: FileEntry[];
}

/**
 * How a person came to follow a task (FR-002).
 */
export type FollowReason = typeof FollowReason[keyof typeof FollowReason];


export const FollowReason = {
  created: 'created',
  commented: 'commented',
  manual: 'manual',
} as const;

/**
 * Whether runs may act with the link (data-model.md).
 */
export type ForgeAccountStatus = typeof ForgeAccountStatus[keyof typeof ForgeAccountStatus];


export const ForgeAccountStatus = {
  working: 'working',
  broken: 'broken',
  expired: 'expired',
  revoked: 'revoked',
} as const;

/**
 * Why a link is not working (``status_reason``).
 */
export type ForgeAccountReason = typeof ForgeAccountReason[keyof typeof ForgeAccountReason];


export const ForgeAccountReason = {
  rejected: 'rejected',
  unreadable: 'unreadable',
  inactive_owner: 'inactive_owner',
  admin: 'admin',
} as const;

/**
 * How a link was made (spec 141): a pasted token, or signing in on the forge.
 */
export type LinkMethod = typeof LinkMethod[keyof typeof LinkMethod];


export const LinkMethod = {
  token: 'token',
  oauth: 'oauth',
} as const;

/**
 * A person's link, as they and the UI see it. Never the token.
 */
export interface ForgeAccountView {
  id: string;
  provider: ForgeProvider;
  host: string;
  forge_username: string;
  forge_commit_email: string | null;
  scopes: string[];
  scopes_unknown: boolean;
  expires_at: string | null;
  status: ForgeAccountStatus;
  status_reason: ForgeAccountReason | null;
  last_verified_at: string | null;
  link_method?: LinkMethod;
}

export interface ForgeAccountList {
  items: ForgeAccountView[];
}

/**
 * A forge host's sign-in app, as admins see it: never the secret.
 */
export interface ForgeAppView {
  provider: ForgeProvider;
  host: string;
  client_id: string | null;
  configured: boolean;
  changed_at: string | null;
}

export interface ForgeAppList {
  redirect_uri: string | null;
  available: boolean;
  items: ForgeAppView[];
}

/**
 * A column of the admins' list. ``unused``: only someone's old link names it.
 */
export interface ForgeHostColumn {
  provider: ForgeProvider;
  host: string;
  unused: boolean;
  workspace_ids?: string[];
  required_workspace_ids?: string[];
}

/**
 * A host some workspace connects to, and whether this person has linked it.
 */
export interface ForgeHostView {
  provider: ForgeProvider;
  host: string;
  linked: boolean;
  oauth_available?: boolean;
  workspace_ids?: string[];
  required_workspace_ids?: string[];
}

export interface ForgeHostList {
  items: ForgeHostView[];
}

/**
 * A workspace that requires personal accounts, and since when.
 */
export interface ForgePolicyItem {
  workspace_id: string;
  changed_at: string;
}

export interface ForgePolicyList {
  items: ForgePolicyItem[];
}

export interface ForgePolicyView {
  workspace_id: string;
  required: boolean;
}

/**
 * When an admin last asked the person to link or relink a host (spec 143).
 */
export interface ForgeReminderView {
  host: string;
  reminded_at: string;
}

export type ValidationErrorCtx = { [key: string]: unknown };

export interface ValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
  input?: unknown;
  ctx?: ValidationErrorCtx;
}

export interface HTTPValidationError {
  detail?: ValidationError[];
}

/**
 * What a file would do if imported, without importing it (spec 098 US1).
 *
 * Not a rejection. This door answers rather than refuses, whatever the file
 * contains (research R4): a door that declined to describe a bad file would
 * reproduce the dead end the feature exists to remove, and a client would need
 * two parsers to learn one thing.
 */
export interface ImportPreflight {
  violations?: ImportViolation[];
  unresolved?: UnresolvedReference[];
  importable: boolean;
}

/**
 * The entire body of every 409/413/422 rejection — no envelope around it.
 */
export interface ImportRejection {
  /** @minItems 1 */
  violations: ImportViolation[];
}

export type ImportSubstitutionsLlmProfiles = {[key: string]: string};

export type ImportSubstitutionsAssignees = {[key: string]: string};

/**
 * What the caller says to use instead, for this import only.
 *
 * Both maps default to empty, so a request carrying this model empty is
 * indistinguishable from one carrying nothing at all — which is what makes
 * "a caller who states nothing sees today's behaviour" provable rather than
 * asserted (spec 098 FR-010).
 *
 * Nothing is ever substituted that is not stated here. There is no inferred
 * default, no matching on the text of a slug, and no organisation-wide
 * fallback: a file names a profile, not a model, so there is no honest basis
 * for choosing one on the caller's behalf (FR-011).
 */
export interface ImportSubstitutions {
  llm_profiles?: ImportSubstitutionsLlmProfiles;
  assignees?: ImportSubstitutionsAssignees;
}

/**
 * Something an import chose not to fail over (spec 067 FR-034).
 *
 * Distinct from :class:`ImportViolation`, which only ever travels inside a
 * rejection. A warning means the workflow *was* stored, with one thing dropped —
 * and the reader has to be told which, because they are usually importing a file
 * somebody else wrote.
 */
export interface ImportWarning {
  code: 'assignee_not_found';
  message: string;
  step_id?: string | null;
  value?: string | null;
}

/**
 * What a bulk change does to each notification it names (spec 130 FR-003).
 */
export type InboxAction = typeof InboxAction[keyof typeof InboxAction];


export const InboxAction = {
  read: 'read',
  unread: 'unread',
  archive: 'archive',
  unarchive: 'unarchive',
} as const;

/**
 * What a bulk or whole-filter change did: how many notifications it changed —
 * those already in the asked-for state are not counted — and the unread count
 * after it.
 */
export interface InboxChange {
  unread: number;
  changed: number;
}

/**
 * The connection a profile uses, as the admin grid shows it (spec 121 FR-005).
 *
 * **Exactly five fields, and the omissions are the design.** ``AIProviderView``
 * also carries ``base_url``, ``has_token``, ``connection_message``,
 * ``connection_checked_at`` — and the stored row behind it carries ``ciphertext``
 * and ``key_version``. None of them are declared here, so no serializer, no filter
 * and no reviewer has to remember to exclude them: a field that does not exist
 * cannot leak. Reusing ``AIProviderView`` and stripping fields at assembly time
 * would invert that — the safe behaviour would depend on a line somebody could
 * delete, and the next field added to ``AIProviderView`` would appear here by
 * default.
 *
 * ``is_default`` is the *provider's* own default flag, not the profile's.
 */
export interface LLMProfileAdminProvider {
  id: string;
  name: string;
  provider_type: AIProviderType;
  connection_status: AIProviderConnectionStatus;
  is_default: boolean;
}

/**
 * What one profile has cost, all-time and organization-wide (FR-006).
 *
 * All three exclusion counts are **required, not defaulted**. A defaulted field is
 * optional on the wire, and a total that silently covered fewer runs than it appears
 * to is precisely the failure these counts exist to make visible — the service always
 * knows every one of them, so the contract says so.
 */
export interface LLMProfileSpend {
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency: string;
  unmeasured_run_count: number;
  unpriced_run_count: number;
  unattributed_run_count: number;
}

/**
 * One profile's consumption, per token kind.
 *
 * ``total_tokens`` is summed by the server rather than left to the client. Every
 * surface that adds four numbers itself is another chance to add them differently —
 * the same rule ``usage.TokenTotals`` already states for the usage dashboard.
 */
export interface LLMProfileTokens {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  total_tokens: number;
}

/**
 * A workspace whose workflows reference this profile — identity only (FR-008a).
 *
 * **Configuration, not execution.** The field is populated from
 * ``workflow_step.llm_profile_id`` references, so a workspace that has configured the
 * profile at a step and never run it still appears here, and a workspace that ran it
 * through a step since repointed elsewhere does not. What this list proves is where
 * the profile is wired up — which is what an administrator deciding whether a profile
 * is safe to change needs.
 *
 * ``id`` and ``name``, and deliberately **no ``slug``**: the grid names workspaces
 * so an administrator can see where a profile is used, and it offers no route into
 * their contents. A slug is the thing a route is built from, so not sending one is
 * what keeps that true.
 */
export interface LLMProfileUsageWorkspace {
  id: string;
  name: string;
}

/**
 * A workflow whose steps reference this profile, with the workspace it lives in.
 *
 * ``workspace_name`` travels on the row rather than being looked up against the
 * ``workspaces`` list: the grid renders "Workflow (Workspace)" in one cell, and a
 * client joining two arrays to do that is a join every client has to get right.
 */
export interface LLMProfileUsageWorkflow {
  id: string;
  key: string;
  name: string;
  workspace_id: string;
  workspace_name: string;
}

/**
 * Where one profile is used: the counts, and the things counted.
 *
 * Both, in the same response (decisions.md Q3). The counts are what the grid cell
 * shows at a glance and the lists are what its expansion shows, and a second request
 * for the lists would mean the number and the list could disagree about the same
 * profile at the same moment.
 */
export interface LLMProfileUsage {
  workspace_count: number;
  workflow_count: number;
  workflow_step_count: number;
  workspaces: LLMProfileUsageWorkspace[];
  workflows: LLMProfileUsageWorkflow[];
}

/**
 * One row of the admin grid: a profile, its connection, its spend and its usage.
 *
 * Carries no internal ``id``, exactly like :class:`LLMProfileView` and for the same
 * reason: ``slug`` is the only identity anything outside this feature needs.
 *
 * Every composed field is required. A profile with no ledger rows and no references
 * is a filled-in row of zeroes and empty lists, not a row of absent keys — the
 * difference between "nothing yet" and "unknown" is what the whole response is for.
 */
export interface LLMProfileAdminOverviewItem {
  slug: string;
  name: string;
  model: string;
  effort: LLMProfileEffort;
  max_turns: number | null;
  description: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  ai_provider: LLMProfileAdminProvider;
  spend: LLMProfileSpend;
  tokens: LLMProfileTokens;
  usage: LLMProfileUsage;
  deleted: false;
}

/**
 * A profile deleted since it ran, with what its runs cost (spec 208 FR-006).
 *
 * Its usage outlives it, so its spend stays in the grid rather than leaving it or
 * being shared out as unattributed. What it no longer has is not sent rather than
 * sent empty: no connection, default, model or settings, no workflow, step or
 * workspace that uses it, and no dates.
 */
export interface LLMProfileDeletedAdminOverviewItem {
  deleted: true;
  key: string;
  name: string;
  spend: LLMProfileSpend;
  tokens: LLMProfileTokens;
}

/**
 * Every profile in the registry, in ``slug`` order, then every deleted profile
 * with usage, in ``key`` order (spec 208).
 *
 * A wrapper rather than a bare list, and ``items`` may be empty: an organization
 * with no profiles configured yet gets ``200`` with ``{"items": []}``, never a
 * ``404``. The registry exists as a concept whether or not anything is in it, and
 * "not found" would say otherwise.
 *
 * ``slug`` order so the grid agrees row-for-row with ``GET /api/llm-profiles``,
 * which is ordered the same way by the repository it shares. ``deleted`` tells a
 * deleted profile's entry from a live one; the figures are all-time, so a deleted
 * profile with any usage has one.
 *
 * ``deleted`` is the union's published discriminator, so a client can narrow an
 * entry on it before reading ``slug``, ``usage`` or anything else only a live
 * profile has.
 */
export interface LLMProfileAdminOverview {
  items: (LLMProfileAdminOverviewItem | LLMProfileDeletedAdminOverviewItem)[];
}

export interface LLMProfileList {
  items: LLMProfileView[];
}

/**
 * One entry in a workspace's vocabulary.
 */
export interface LabelView {
  id: string;
  name: string;
  color: LabelColor;
  created_at: string;
  updated_at: string;
}

export interface LibraryDependency {
  kind: LibraryItemKind;
  key: string;
}

/**
 * Where this workspace stands against a library item (FR-022).
 */
export type LibraryItemState = typeof LibraryItemState[keyof typeof LibraryItemState];


export const LibraryItemState = {
  not_installed: 'not_installed',
  installed: 'installed',
  update_available: 'update_available',
  customized: 'customized',
  update_available_with_local_changes: 'update_available_with_local_changes',
  added_by_hand: 'added_by_hand',
} as const;

/**
 * One file inside an item's folder (spec 105 FR-010).
 *
 * The catalog reads every file of every item to build itself and has always
 * known this; it simply never said so. Carries no entity tag: the item's
 * ``digest`` already changes when any file changes, and the tag is the
 * storage's business, not the contract's.
 */
export interface LibraryItemFile {
  path: string;
  size: number;
}

export type LibraryItemOrigin = typeof LibraryItemOrigin[keyof typeof LibraryItemOrigin];


export const LibraryItemOrigin = {
  curated: 'curated',
  shared: 'shared',
} as const;

export interface LibraryItemView {
  kind: LibraryItemKind;
  key: string;
  name: string;
  description: string;
  digest: string;
  installable: boolean;
  reason?: string | null;
  requires?: LibraryDependency[];
  state: LibraryItemState;
  files?: LibraryItemFile[];
  origin?: LibraryItemOrigin;
  shared_by?: string | null;
  shared_at?: string | null;
  can_take_down?: boolean;
}

export interface LibraryCatalogView {
  items: LibraryItemView[];
}

/**
 * One file's text, for reading an item before installing it (FR-001).
 *
 * Nothing here is interpreted: front matter stays, YAML stays YAML, markdown
 * stays markdown. That is what lets one door serve agents, skills and
 * workflows alike, and it is why the page can render an agent as a document
 * and a workflow as an outline from the same answer (FR-014).
 */
export interface LibraryFileView {
  path: string;
  content_type: string;
  text: string;
  size: number;
  truncated: boolean;
}

export interface LibraryInstallRequest {
  kind: LibraryItemKind;
  key: string;
  substitutions?: ImportSubstitutions | null;
}

/**
 * What an install or update did, per item (FR-011).
 *
 * ``blocked`` is the workflow-import case: the definition names a model
 * profile or person this deployment lacks, nothing was written, and
 * ``unresolved`` says what; the same request with ``substitutions`` proceeds.
 */
export interface LibraryInstallResult {
  blocked?: boolean;
  outcomes?: LibraryItemOutcome[];
  unresolved?: UnresolvedReference[];
  substitutions?: AppliedSubstitution[];
  workflow?: WorkflowView | null;
}

export type LibraryPieceRefKind = typeof LibraryPieceRefKind[keyof typeof LibraryPieceRefKind];


export const LibraryPieceRefKind = {
  workflow: 'workflow',
  agent: 'agent',
  skill: 'skill',
} as const;

/**
 * One piece of a share, by kind and key (spec 166).
 */
export interface LibraryPieceRef {
  kind: LibraryPieceRefKind;
  key: string;
}

export interface LibraryUpdateRequest {
  kind: LibraryItemKind;
  key: string;
}

/**
 * Where a workspace's effective limit comes from (FR-006, FR-027).
 */
export type LimitSource = typeof LimitSource[keyof typeof LimitSource];


export const LimitSource = {
  workspace: 'workspace',
  organization: 'organization',
  fallback: 'fallback',
} as const;

/**
 * A limit as it stands after being set.
 */
export interface LimitView {
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency?: string;
  blocks_workspaces: number;
  period_type: PeriodType;
}

export interface LinkResult {
  account: ForgeAccountView;
  warnings: 'scopes_unknown'[];
}

export interface LinkTokenRequest {
  /**
     * @minLength 1
     * @maxLength 4096
     */
  token: string;
}

/**
 * Optional enrichments a caller may ask the list for (spec 070 FR-001).
 *
 * Opt-in rather than always-on, because :class:`WorkspaceSummary` documents an
 * invariant — identity and description, never contents — and adding fields
 * unconditionally would widen that contract for every existing caller, including
 * the ones that render none of it. Asking makes the exception visible in the URL
 * that wants it, and leaves the default response byte-identical (FR-002).
 */
export type ListInclude = typeof ListInclude[keyof typeof ListInclude];


export const ListInclude = {
  counts: 'counts',
  workflow_names: 'workflow_names',
  last_activity: 'last_activity',
  access: 'access',
} as const;

/**
 * How the list is ordered.
 *
 * ``created`` (the default) is the newest-created-first order spec 002 defines.
 * ``activity`` puts the workspace where something happened most recently
 * first, so "recently updated" holds across pages rather than only within the
 * page a client happens to hold.
 */
export type ListSort = typeof ListSort[keyof typeof ListSort];


export const ListSort = {
  created: 'created',
  activity: 'activity',
} as const;

export interface ManifestField {
  name: string;
  description?: string | null;
}

export interface ManifestGroup {
  name: string;
  fields: ManifestField[];
}

/**
 * What removing someone cleared in the workspace (FR-004).
 */
export interface MemberRemoval {
  gate_assignments_cleared: number;
  step_assignees_cleared: number;
  follows_dropped: number;
}

/**
 * One person in a workspace.
 */
export interface MemberView {
  user: UserSummary;
  role: WorkspaceRole;
  added_at: string;
  added_by?: UserSummary | null;
  is_active: boolean;
}

export type MemoryLimitViewUnit = typeof MemoryLimitViewUnit[keyof typeof MemoryLimitViewUnit];


export const MemoryLimitViewUnit = {
  MiB: 'MiB',
  GiB: 'GiB',
} as const;

/**
 * A memory limit as it stands after being set.
 */
export interface MemoryLimitView {
  workspace_id: string | null;
  value_mib: number;
  unit: MemoryLimitViewUnit;
}

export type MemoryStopInCause = typeof MemoryStopInCause[keyof typeof MemoryStopInCause];


export const MemoryStopInCause = {
  budget: 'budget',
  pod_headroom: 'pod_headroom',
  daemon_headroom: 'daemon_headroom',
} as const;

/**
 * One command the run's memory guard stopped, as the worker reports it.
 */
export interface MemoryStopIn {
  /** @maxLength 500 */
  command: string;
  tool_use_id?: string | null;
  cause: MemoryStopInCause;
  /** @minimum 1 */
  budget_mib: number;
  /** @minimum 0 */
  peak_mib: number;
  exit_code: number;
  stopped_at: string;
}

export type MissingPersonalAccountViewReason = typeof MissingPersonalAccountViewReason[keyof typeof MissingPersonalAccountViewReason];


export const MissingPersonalAccountViewReason = {
  not_linked: 'not_linked',
  broken: 'broken',
  expired: 'expired',
  revoked: 'revoked',
  inactive_owner: 'inactive_owner',
  unreachable: 'unreachable',
} as const;

export interface MissingPersonalAccountView {
  provider: ForgeProvider;
  host: string;
  reason: MissingPersonalAccountViewReason;
}

/**
 * A stored rate sheet, as it reads back.
 */
export interface ModelPriceView {
  id: string;
  model_key: string;
  effective_from: string;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  input_rate: string;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  output_rate: string;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  cache_read_rate: string;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  cache_creation_rate: string;
  created_at: string;
  created_by_user_id: string | null;
}

/**
 * Every model with a currently-effective price, organization-wide (spec 084).
 *
 * A wrapper rather than a bare list, for the same reason ``UnpricedModelsView`` is
 * one: "no model is priced yet" must be a response an empty ``prices`` list can
 * represent, not something indistinguishable from a route that was never reached.
 */
export interface ModelPricesView {
  prices: ModelPriceView[];
}

/**
 * Ask for one file or folder to be moved within a workspace (spec 059).
 */
export interface MoveFileRequest {
  source_path: string;
  destination_path: string;
  overwrite?: boolean;
  task_id?: string | null;
  step_id?: string | null;
}

export type MoveFileResultKind = typeof MoveFileResultKind[keyof typeof MoveFileResultKind];


export const MoveFileResultKind = {
  file: 'file',
  directory: 'directory',
} as const;

/**
 * What a completed move did.
 *
 * Deliberately a summary rather than a `FileChangeEventView`. Upload returns
 * the single event it produced, but a folder move produces one event per
 * descendant file — echoing a thousand of them would be wasteful and echoing
 * an arbitrary one would be misleading (spec 059 D11).
 */
export interface MoveFileResult {
  source_path: string;
  destination_path: string;
  kind: MoveFileResultKind;
  moved_count: number;
  source_emptied: boolean;
  occurred_at: string;
}

/**
 * Addressed by ``target_step_id`` (004 FR-012); the board names a column, not a transition.
 */
export interface MoveTaskRequest {
  target_step_id: string;
  expected_version: number;
  reason?: string | null;
}

export interface MoveTaskResponse {
  task: TaskView;
  execution: TaskExecutionSummary;
}

/**
 * The caller's own request to join, as the no-access page shows it.
 *
 * ``requested_at`` is ``None`` when they have no open request: never asked, or asked
 * and were answered (a dismissal is never told, decision Q17).
 */
export interface MyAccessRequest {
  requested_at?: string | null;
}

/**
 * One action on chosen notifications (spec 130 FR-003).
 *
 * Ids that are not the signed-in person's are skipped, not refused.
 */
export interface NotificationBulkChange {
  /**
     * @minItems 1
     * @maxItems 200
     */
  ids: string[];
  action: InboxAction;
}

/**
 * How a notification reaches a person: the columns of the preference matrix.
 *
 * ``alert`` is the in-app pop-up shown while the app is open (spec 121). It was
 * ``browser`` — an OS notification — until in-app alerts replaced that plan; the
 * rename kept every stored choice (FR-001).
 */
export type NotificationChannel = typeof NotificationChannel[keyof typeof NotificationChannel];


export const NotificationChannel = {
  in_app: 'in_app',
  email: 'email',
  alert: 'alert',
} as const;

/**
 * How many notifications a filter matches, across all the list's pages (FR-002).
 */
export interface NotificationCount {
  total: number;
}

/**
 * The inbox's three filters, and the rows of the preference matrix (spec 118).
 *
 * Never stored on a notification: a group is a function of the kind, and a second
 * column could only disagree with it. A group filter becomes ``kind IN (...)``.
 */
export type NotificationGroup = typeof NotificationGroup[keyof typeof NotificationGroup];


export const NotificationGroup = {
  needs_you: 'needs_you',
  outcome: 'outcome',
  workspace: 'workspace',
} as const;

/**
 * What happened (FR-005). Each kind belongs to exactly one group, see ``KIND_GROUP``.
 */
export type NotificationKind = typeof NotificationKind[keyof typeof NotificationKind];


export const NotificationKind = {
  gate_reached: 'gate_reached',
  gate_assigned: 'gate_assigned',
  task_failed: 'task_failed',
  task_blocked: 'task_blocked',
  task_stopped: 'task_stopped',
  child_needs_attention: 'child_needs_attention',
  task_done: 'task_done',
  children_finished: 'children_finished',
  task_commented: 'task_commented',
  spend_limit_reached: 'spend_limit_reached',
  trigger_failed: 'trigger_failed',
  task_mentioned: 'task_mentioned',
  escalated: 'escalated',
  connection_failed: 'connection_failed',
  spend_limit_approaching: 'spend_limit_approaching',
  linked_tasks_waiting: 'linked_tasks_waiting',
  run_replied: 'run_replied',
  forge_account_broken: 'forge_account_broken',
  forge_account_expiring: 'forge_account_expiring',
  forge_account_revoked: 'forge_account_revoked',
  forge_account_required: 'forge_account_required',
  forge_account_requested: 'forge_account_requested',
  workspace_member_added: 'workspace_member_added',
  workspace_member_removed: 'workspace_member_removed',
  workspace_access_requested: 'workspace_access_requested',
  workspace_without_owner: 'workspace_without_owner',
  workspace_members_review: 'workspace_members_review',
  scheduler_failed: 'scheduler_failed',
  scheduler_stalled: 'scheduler_stalled',
} as const;

/**
 * Whether a notification's cause is still going on — a list filter (spec 130 FR-001).
 *
 * Not a column: ``resolved_at`` is set or it is not.
 */
export type NotificationState = typeof NotificationState[keyof typeof NotificationState];


export const NotificationState = {
  open: 'open',
  resolved: 'resolved',
} as const;

/**
 * Which of the signed-in person's notifications are meant (spec 130 FR-001).
 *
 * The same fields narrow the list, the count, read-all and archive-all, so a count
 * always agrees with the list it counts (SC-003). A notification must match every
 * field that is set. Times are instants: ``created_from`` inclusive, ``created_to``
 * exclusive, each with its offset.
 */
export interface NotificationFilter {
  /** Only notifications not yet read. */
  unread?: boolean;
  /** Archived notifications too. Ignored when `archived` is set. */
  include_archived?: boolean;
  /** Only archived notifications (spec 120). When set, `include_archived` is ignored: either value lists the archived ones alone. */
  archived?: boolean;
  /** Only notifications of this group. `needs_you` lists only open ones: a resolved notification no longer needs anyone (spec 120 FR-004). */
  group?: NotificationGroup | null;
  workspace_id?: string | null;
  /** Only these kinds; repeat the parameter or separate them with commas (`kind=a,b`). With `group`, a notification must be of one of these kinds and in that group. Leave it out for every kind: an empty list (`kind=`, or `[]` in a body) is refused, not read as every kind. */
  kind?: NotificationKind[] | null;
  /** Only notifications created at or after this instant. */
  created_from?: string | null;
  /** Only notifications created before this instant. Before `created_from` is refused. */
  created_to?: string | null;
  /** Only open, or only resolved, notifications; both when absent. `needs_you` with `resolved` matches nothing. */
  state?: NotificationState | null;
}

/**
 * The workspace a notification belongs to — the inbox labels every row with it.
 */
export interface NotificationWorkspace {
  id: string;
  name?: string | null;
}

/**
 * What changed, named when it happened (FR-001).
 *
 * Every field is optional; each kind fills the ones that name its change
 * (data-model.md). Stored as JSONB and returned as stored, so a task renamed later
 * does not rewrite what an old notification said.
 */
export interface NotificationPayload {
  task_title?: string | null;
  step_id?: string | null;
  step_name?: string | null;
  child_task_id?: string | null;
  child_task_title?: string | null;
  spend_scope?: string | null;
  workflow_name?: string | null;
  trigger_name?: string | null;
  comment_excerpt?: string | null;
  failure_reason?: string | null;
  failure_count?: number | null;
  last_failed_at?: string | null;
  escalated_kind?: NotificationKind | null;
  waited_hours?: number | null;
  connection_id?: string | null;
  connection_name?: string | null;
  provider?: string | null;
  connection_reason?: string | null;
  period_type?: string | null;
  waiting_count?: number | null;
  reason?: string | null;
  execution_id?: string | null;
  thread_message_id?: string | null;
  forge_host?: string | null;
  expires_at?: string | null;
  forge_reason?: string | null;
  blocked_by?: string | null;
  member_role?: string | null;
  member_name?: string | null;
  member_left?: boolean | null;
  gate_assignments_cleared?: number | null;
  step_assignees_cleared?: number | null;
  follows_dropped?: number | null;
  access_request_id?: string | null;
}

/**
 * How loudly a notification may interrupt (spec 120 FR-006).
 *
 * A second classification beside the group: the group says which settings row and
 * inbox tab it belongs to, severity says how hard it may interrupt — alerts, email
 * timing, quiet hours and escalation key off it in later slices. Fixed per kind in
 * code, never stored, never chosen by a producer.
 */
export type NotificationSeverity = typeof NotificationSeverity[keyof typeof NotificationSeverity];


export const NotificationSeverity = {
  informational: 'informational',
  warning: 'warning',
  action_required: 'action_required',
  critical: 'critical',
} as const;

/**
 * Why a notification reached its recipient (spec 120 FR-007), most direct first.
 */
export type NotificationReason = typeof NotificationReason[keyof typeof NotificationReason];


export const NotificationReason = {
  mentioned: 'mentioned',
  assignee: 'assignee',
  follower: 'follower',
  workspace_owner: 'workspace_owner',
  org_admin: 'org_admin',
  escalation: 'escalation',
  asked: 'asked',
  own_account: 'own_account',
  membership: 'membership',
} as const;

/**
 * One notification, as its recipient reads it.
 */
export interface NotificationView {
  id: string;
  kind: NotificationKind;
  group: NotificationGroup;
  workspace: NotificationWorkspace;
  task_id: string | null;
  workflow_key?: string | null;
  actor: UserSummary | null;
  payload: NotificationPayload;
  created_at: string;
  read_at: string | null;
  archived_at: string | null;
  severity: NotificationSeverity;
  reason?: NotificationReason | null;
  resolved_at?: string | null;
  acknowledged_at?: string | null;
}

/**
 * A page of the inbox, newest first.
 *
 * ``next_before`` is the id to pass back as ``before`` for the next, older page —
 * ``None`` when there is none. A cursor rather than an offset because the inbox grows
 * at the top while someone pages through it (research R14).
 */
export interface NotificationPage {
  items: NotificationView[];
  next_before?: string | null;
}

/**
 * One change of the policy, as the history under the setting shows it.
 */
export interface NotificationPolicyAuditEntry {
  actor: UserSummary | null;
  changed_at: string;
  field: string;
  before: string;
  after: string;
}

/**
 * The organization's escalation setting and its history, newest first (FR-003).
 *
 * ``escalation_delay``: how long something needing action may wait unacknowledged
 * before the workspace owner — or, when the owner already has it, the org admins —
 * are told. ``off`` never escalates.
 */
export interface NotificationPolicy {
  escalation_delay: EscalationDelay;
  history: NotificationPolicyAuditEntry[];
}

/**
 * Set the escalation delay. Setting the value it already has records nothing.
 */
export interface NotificationPolicyChange {
  escalation_delay: EscalationDelay;
}

/**
 * One cell of the matrix: whether ``group`` reaches the person on ``channel``.
 */
export interface PreferenceCell {
  group: NotificationGroup;
  channel: NotificationChannel;
  enabled: boolean;
  is_default: boolean;
  required: boolean;
  default_kinds?: NotificationKind[];
}

/**
 * A workspace the person muted: nothing from it reaches them but in-app "Needs you".
 */
export interface WorkspaceMute {
  workspace: NotificationWorkspace;
  muted_at: string;
}

/**
 * A daily window with no alerts and no bundled email (spec 121 FR-005).
 *
 * Local wall-clock times in the person's own zone (``NotificationPreferences.timezone``,
 * UTC while unknown). ``ends`` before ``starts`` crosses midnight. Critical
 * notifications still alert and are still emailed inside it.
 */
export interface QuietHours {
  /** @pattern ^([01]\d|2[0-3]):[0-5]\d$ */
  starts: string;
  /** @pattern ^([01]\d|2[0-3]):[0-5]\d$ */
  ends: string;
}

/**
 * The signed-in person's preferences.
 *
 * ``cells`` always holds all nine, ordered by group then channel. ``mutes`` is ordered
 * by workspace name. Every write answers this whole document, so a client replaces
 * its copy rather than reading again (research R6). ``timezone`` and ``email_enabled``
 * are spec 119's: what the email column of the settings page needs to say.
 */
export interface NotificationPreferences {
  cells: PreferenceCell[];
  mutes: WorkspaceMute[];
  timezone?: string | null;
  email_enabled?: boolean;
  quiet_hours?: QuietHours | null;
}

/**
 * The ``data`` of every stream event (contracts/notifications-api.md).
 *
 * ``notification_id`` and ``kind`` are set on ``notification`` and
 * ``notification_resolved`` events (spec 120) and ``None`` on ``ready`` and
 * ``inbox_changed`` (spec 130). ``unread`` is read after the event, so it includes
 * what just arrived.
 */
export interface NotificationStreamEvent {
  notification_id?: string | null;
  kind?: NotificationKind | null;
  unread: number;
  alert?: boolean;
  notification?: NotificationView | null;
}

/**
 * Where to send the person's browser to approve the link.
 */
export interface OAuthStartView {
  authorize_url: string;
}

/**
 * One workspace on the organization's concurrency read (spec 110 FR-001).
 */
export interface WorkspaceConcurrencyRow {
  workspace_id: string;
  name: string;
  limit: number;
  source: LimitSource;
  clamped: boolean;
  running: number;
  queued: number;
}

/**
 * Every workspace's limit and load at once, for organization administrators.
 *
 * One read for the admin page (spec 110): a request per workspace is the N+1 the usage
 * console already refuses.
 */
export interface OrganizationConcurrencyView {
  default: number | null;
  fallback: number;
  ceiling: number;
  workspaces: WorkspaceConcurrencyRow[];
}

/**
 * Which deployment this is, as a label for whoever is reading the screen.
 *
 * Descriptive only. Nothing branches on it, and nothing should: behaviour that differs
 * between environments belongs in configuration, which is validated at startup and
 * cannot be changed by anyone with access to an admin form.
 */
export type OrganizationEnvironment = typeof OrganizationEnvironment[keyof typeof OrganizationEnvironment];


export const OrganizationEnvironment = {
  production: 'production',
  staging: 'staging',
} as const;

export type OrganizationMemoryViewDefaultUnit = typeof OrganizationMemoryViewDefaultUnit[keyof typeof OrganizationMemoryViewDefaultUnit] | null;


export const OrganizationMemoryViewDefaultUnit = {
  MiB: 'MiB',
  GiB: 'GiB',
} as const;

export type WorkspaceMemoryRowUnit = typeof WorkspaceMemoryRowUnit[keyof typeof WorkspaceMemoryRowUnit];


export const WorkspaceMemoryRowUnit = {
  MiB: 'MiB',
  GiB: 'GiB',
} as const;

/**
 * One workspace on the organization's memory read.
 */
export interface WorkspaceMemoryRow {
  workspace_id: string;
  name: string;
  limit_mib: number;
  unit: WorkspaceMemoryRowUnit;
  source: LimitSource;
}

/**
 * Every workspace's memory amount at once, for organization administrators.
 */
export interface OrganizationMemoryView {
  default_mib: number | null;
  default_unit: OrganizationMemoryViewDefaultUnit;
  fallback_mib: number;
  minimum_mib: number;
  workspaces: WorkspaceMemoryRow[];
}

/**
 * A period's money, and what is missing from it.
 *
 * Both exclusion counts are always present. A total that hid either would look exactly
 * like a complete one, and the whole design turns on those being distinguishable.
 */
export interface SpendTotals {
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency?: string;
  unmeasured_run_count: number;
  unpriced_run_count: number;
}

/**
 * Consumption for a period, per token kind.
 *
 * ``total_tokens`` is sent rather than left to the client to add up. Four surfaces
 * summing four fields is four chances to sum them differently.
 */
export interface TokenTotals {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  total_tokens: number;
}

export type WorkspaceLimitRowThreshold = typeof WorkspaceLimitRowThreshold[keyof typeof WorkspaceLimitRowThreshold] | null;


export const WorkspaceLimitRowThreshold = {
  under: 'under',
  approaching: 'approaching',
  exhausted: 'exhausted',
} as const;

/**
 * One row of the console's every-workspace table.
 */
export interface WorkspaceLimitRow {
  workspace_id: string;
  workspace_name: string;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  spend: string;
  currency?: string;
  limit: string | null;
  is_override: boolean;
  threshold: WorkspaceLimitRowThreshold;
  total_tokens: number;
  period_type: PeriodType | null;
  limit_consumed_amount?: string | null;
  limit_period?: PeriodBounds | null;
  default_consumed_amount?: string | null;
  default_period?: PeriodBounds | null;
  deleted: boolean;
}

/**
 * What the admin console's overview reads, in one response.
 */
export interface OrganizationUsageSummary {
  period: PeriodBounds;
  spend: SpendTotals;
  totals: TokenTotals;
  default_limit: string | null;
  workspaces_at_limit: number;
  workspaces_approaching_limit: number;
  workspaces: WorkspaceLimitRow[];
  earliest_period_key: string | null;
  ai_providers_count: number;
}

/**
 * The organization, as every surface renders it.
 *
 * Readable by any signed-in person (FR-004): the name identifies the product they are
 * using, and hiding it would make the application anonymous to its own users. Only
 * changing it is restricted.
 */
export interface OrganizationView {
  name: string;
  environment: OrganizationEnvironment;
  updated_at: string;
}

/**
 * A whole definition as the builder holds it, with no lifecycle around it.
 *
 * The body of every builder write: the direct replace (:class:`WorkflowReplace`
 * extends it with ``status`` and ``expected_version``) and a save of pending
 * changes (spec 127). It is also exactly what pending changes store, so the
 * stored document and the request that wrote it cannot drift apart.
 *
 * This is **not** the YAML shape, and diverges from it in deliberate places —
 * ``inputs`` in the read shape (D18), plus ``start`` and ``layout``. None of
 * those belong in a workflow file.
 *
 * ``inputs`` takes :class:`WorkflowInputDef` — what a read returns — so a client
 * that does not interpret inputs preserves them by echoing the array back
 * unchanged (FR-033). Ordering comes from the array; there is no display-order
 * field on the wire.
 */
export interface WorkflowDraftDefinition {
  name: string;
  description?: string | null;
  inputs?: WorkflowInputDef[];
  phases?: WorkflowPhaseDef[];
  start?: string | null;
  image?: string | null;
  layout?: WorkflowLayout | null;
}

/**
 * A published workflow's pending changes, as the builder reads them (spec 127).
 */
export interface PendingChangesView {
  definition: WorkflowDraftDefinition;
  revision: string;
  based_on_version: number;
  outdated: boolean;
  saved_by?: UserSummary | null;
  saved_via?: string | null;
  saved_at: string;
}

/**
 * One of a person's links, as an admin sees it: no token, scopes or email.
 */
export interface PersonLinkView {
  provider: ForgeProvider;
  host: string;
  forge_username: string;
  status: ForgeAccountStatus;
  status_reason: ForgeAccountReason | null;
  expires_at: string | null;
  last_verified_at: string | null;
  link_method?: LinkMethod;
}

export interface PersonLinksView {
  person: UserSummary;
  missing_count: number;
  links: PersonLinkView[];
  reminders?: ForgeReminderView[];
}

export interface PeopleLinksPage {
  hosts: ForgeHostColumn[];
  required_workspace_ids: string[];
  items: PersonLinksView[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * The caller in one workspace: does it require their own account, and for which
 * of its hosts do they lack a usable one (US5).
 */
export interface PersonalAccountStanding {
  required: boolean;
  missing: MissingPersonalAccountView[];
}

/**
 * The reply's answer, masked by the worker before it is sent.
 */
export interface PostReplyAnswerRequest {
  /**
     * @minLength 1
     * @maxLength 10000
     */
  body: string;
  cut_short?: boolean;
}

/**
 * What a scheduling decision chose: the tasks of its batch to start now, and why.
 */
export interface PostScheduleResultRequest {
  /** @maxItems 200 */
  start?: string[];
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
}

/**
 * A person's message, Markdown, as long as a comment may be.
 */
export interface PostThreadMessageRequest {
  /**
     * @minLength 1
     * @maxLength 10000
     */
  body: string;
}

/**
 * The new value of one cell.
 */
export interface PreferenceChange {
  enabled: boolean;
}

/**
 * Set quiet hours. ``starts`` and ``ends`` must differ: an empty window means
 * nothing, and a whole day would silence everything but Critical for good.
 */
export interface QuietHoursChange {
  /** @pattern ^([01]\d|2[0-3]):[0-5]\d$ */
  starts: string;
  /** @pattern ^([01]\d|2[0-3]):[0-5]\d$ */
  ends: string;
}

/**
 * A batch of captured events, delivered by the run that captured them.
 */
export interface RecordArtifactEventsRequest {
  /** @minimum 0 */
  captured: number;
  /** @maxItems 200 */
  events?: ArtifactEvent[];
}

export interface RecordArtifactEventsResponse {
  recorded: number;
  skipped: number;
}

/**
 * What a run delivers about itself when it ends.
 */
export interface RecordExecutionResultRequest {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  summary: string;
  /** @maxItems 50 */
  links?: ResultLink[];
  memory_budget_mib?: number | null;
  memory_enforced?: boolean | null;
  /** @maxItems 50 */
  memory_stops?: MemoryStopIn[];
}

export interface RecordExecutionResultResponse {
  execution_id: string;
  recorded_at: string;
}

export interface RegisterChangesEntry {
  path: string;
  event_type: FileChangeEventType;
}

export interface RegisterChangesRequest {
  task_id: string;
  step_id: string;
  entries: RegisterChangesEntry[];
}

export interface RegisterChangesResponse {
  events: FileChangeEventView[];
}

export type RegisterExternalReferenceRequestProvider = typeof RegisterExternalReferenceRequestProvider[keyof typeof RegisterExternalReferenceRequestProvider];


export const RegisterExternalReferenceRequestProvider = {
  github: 'github',
  gitlab: 'gitlab',
} as const;

/**
 * Body of the execution-scoped self-registration route (agent-facing MR/PR linking).
 *
 * ``provider`` names the connection type to resolve, not a connection id — an
 * agent cannot know the workspace's connection ids, only which host it pushed
 * to. ``repository`` must name the repo the way a GitLab/GitHub trigger
 * profile's ``config.repositories`` entry does; letter case does not matter
 * (spec 114 FR-006). Merge requests opened with ``glab``/``gh`` are
 * registered by the platform itself, so this is only for one opened another
 * way.
 */
export interface RegisterExternalReferenceRequest {
  provider: RegisterExternalReferenceRequestProvider;
  /** @minLength 1 */
  repository: string;
  /** @exclusiveMinimum 0 */
  external_number: number;
}

export type RelatedTaskItemRelationship = typeof RelatedTaskItemRelationship[keyof typeof RelatedTaskItemRelationship];


export const RelatedTaskItemRelationship = {
  parent: 'parent',
  child: 'child',
} as const;

export type RelatedTaskItemRelationType = typeof RelatedTaskItemRelationType[keyof typeof RelatedTaskItemRelationType];


export const RelatedTaskItemRelationType = {
  plain: 'plain',
  blocks: 'blocks',
  relates_to: 'relates_to',
  duplicates: 'duplicates',
} as const;

/**
 * One task in the related-tasks graph, reached from the selected task by
 * walking every relation edge in either direction (research D4, generalized).
 */
export interface RelatedTaskItem {
  task_id: string;
  title: string;
  step_name: string;
  status: TaskStatus;
  needs_review?: boolean;
  needs_attention?: boolean;
  relationship: RelatedTaskItemRelationship;
  /** @minimum 1 */
  depth: number;
  relation_type: RelatedTaskItemRelationType;
  via_task_id?: string | null;
}

export interface WorkflowRelatedGroup {
  workflow_id: string;
  workflow_name: string;
  tasks: RelatedTaskItem[];
  siblings_available?: number;
}

export interface RelatedTasksResponse {
  task_id: string;
  workflow_groups: WorkflowRelatedGroup[];
}

/**
 * A task's new title, and nothing else.
 *
 * One field per route rather than one partial ``PATCH`` carrying both: the two
 * are edited from two separate controls that never fire together, so a combined
 * body would have to distinguish "field omitted" from "field set to null" —
 * a tri-state this codebase has nowhere else, bought for a caller that does not
 * exist.
 *
 * Trimmed and bounded exactly as :class:`TaskCreate` does it, because a title
 * that could not have been created should not be reachable by renaming into it.
 */
export interface RenameTaskRequest {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
}

/**
 * The labels a task should have afterwards — the whole set, not a delta.
 *
 * Idempotent by construction, and the caller's editor needs no diffing. An
 * empty list clears every label and is a success, not an error.
 *
 * ``actor_type`` defaults to a person: the overwhelming majority of writes are
 * somebody clicking, and a caller recording that an agent did it says so
 * explicitly rather than relying on a default it did not choose. This mirrors
 * ``CreateTaskCommentRequest``.
 */
export interface ReplaceTaskLabelsRequest {
  label_ids?: string[];
  actor_type?: TaskActorType;
}

/**
 * The value, and nothing else.
 *
 * ``key`` and ``kind`` are absent by design (FR-014, FR-015): a field that cannot
 * change should not be presentable. ``extra="forbid"`` means a body carrying either
 * is refused rather than silently ignored, so a caller who believes they are renaming
 * something finds out that they are not.
 */
export interface ReplaceWorkspaceSecretValueRequest {
  value: string;
}

/**
 * The agent's message the answer became.
 */
export interface ReplyAnswerView {
  message_id: string;
}

/**
 * One reply run for a person's message: a first try, or a retry (FR-010).
 */
export interface ReplyAttemptView {
  execution_id: string;
  status: TaskExecutionStatus;
  created_at: string;
  ended_at?: string | null;
  cost?: string | null;
}

/**
 * A branch of the run's, for the reply to check out read-only.
 */
export interface ReplyCheckout {
  forge_host: string;
  repository: string;
  branch: string;
  commit_sha?: string | null;
}

/**
 * Everything a reply run's worker is told: its prompt, and where to read.
 */
export interface ReplyContextView {
  prompt: string;
  task_folder?: string | null;
  checkouts?: ReplyCheckout[];
  max_answer_length?: number;
}

/**
 * Where the answer to a person's message stands (FR-006).
 *
 * Derived when read from the message's latest reply attempt, never stored (plan §
 * Data model). ``unavailable``: nothing will answer — replies are switched off, or
 * the message was posted while they were.
 */
export type ReplyState = typeof ReplyState[keyof typeof ReplyState];


export const ReplyState = {
  unavailable: 'unavailable',
  queued: 'queued',
  waiting_for_slot: 'waiting_for_slot',
  reading: 'reading',
  answering: 'answering',
  done: 'done',
  failed: 'failed',
  refused: 'refused',
} as const;

/**
 * A person rerunning the run's step; ``None`` when nobody started it by hand.
 */
export interface RerunEntry {
  kind?: 'rerun';
  execution_id: string;
  at: string;
  step_id: string;
  step_name?: string | null;
  triggered_by?: UserSummary | null;
}

/**
 * A stop being asked for on the run's step.
 */
export interface StopEntry {
  kind?: 'stop';
  execution_id: string;
  at: string;
  step_id: string;
  step_name?: string | null;
  stopped_by?: UserSummary | null;
}

/**
 * One task a scheduling decision started. ``title`` is its title now, ``None`` if deleted.
 */
export interface ScheduleStartedTask {
  id: string;
  title?: string | null;
}

/**
 * A scheduling decision that has posted its result (spec 183 FR-011): what it started,
 * or that it started nothing, and why. A decision still in flight has no entry.
 */
export interface ScheduleEntry {
  kind?: 'schedule';
  execution_id: string;
  at: string;
  reason: string;
  started: ScheduleStartedTask[];
}

/**
 * What a person told the run's scheduler to do (a message to it, in the activity log).
 */
export interface SchedulerNoteEntry {
  kind?: 'note';
  id: string;
  at: string;
  body: string;
  author?: UserSummary | null;
  used?: boolean;
}

/**
 * A run's activity, newest first (spec 131). Whole, like `TaskMoveList`.
 */
export interface RunActivityList {
  items: (ChangeEntry | AiChangeGroupEntry | RerunEntry | StopEntry | ScheduleEntry | SchedulerNoteEntry)[];
}

/**
 * A running run's current token for one forge host (spec 141 FR-011).
 */
export interface RunForgeToken {
  token: string;
  expires_at: string | null;
  link_method?: LinkMethod;
}

/**
 * The ``data`` of a ``run_thread_changed`` stream event (spec 131 FR-006).
 *
 * Something on this run's thread committed: a message posted, or added to its task's
 * context. Names the run and nothing else — a client showing that thread re-reads its
 * newest page, and ignores the message otherwise.
 */
export interface RunThreadChangedEvent {
  workspace_id: string;
  task_id: string;
  execution_id: string;
}

export type ScheduleCommentViewAuthorKind = typeof ScheduleCommentViewAuthorKind[keyof typeof ScheduleCommentViewAuthorKind];


export const ScheduleCommentViewAuthorKind = {
  user: 'user',
  agent: 'agent',
  system: 'system',
} as const;

/**
 * One comment on the parent, as the scheduler reads it.
 */
export interface ScheduleCommentView {
  author: string;
  author_kind: ScheduleCommentViewAuthorKind;
  at: string;
  body: string;
  new_since_last_pick?: boolean;
}

/**
 * A person's message to the scheduler, as the decision reads it.
 */
export interface ScheduleNoteView {
  at: string;
  author: string;
  body: string;
}

export interface ScheduleParentView {
  id: string;
  title: string;
  description: string;
  comments: ScheduleCommentView[];
}

export type ScheduleTriggerViewKind = typeof ScheduleTriggerViewKind[keyof typeof ScheduleTriggerViewKind];


export const ScheduleTriggerViewKind = {
  parent_run_ended: 'parent_run_ended',
  child_done: 'child_done',
} as const;

export interface ScheduleTriggerView {
  kind: ScheduleTriggerViewKind;
  child_id?: string | null;
  step_report: string;
}

export type ScheduleSubtaskViewStatus = typeof ScheduleSubtaskViewStatus[keyof typeof ScheduleSubtaskViewStatus];


export const ScheduleSubtaskViewStatus = {
  done: 'done',
  needs_attention: 'needs_attention',
  needs_review: 'needs_review',
  not_started: 'not_started',
  in_progress: 'in_progress',
} as const;

export interface ScheduleSubtaskView {
  id: string;
  title: string;
  description: string;
  status: ScheduleSubtaskViewStatus;
  created_order: number;
}

export interface SchedulePreviousPick {
  at: string;
  started: string[];
  reason: string;
}

/**
 * Everything a scheduling decision is told (spec 183, contracts/schedule-routes.md).
 */
export interface ScheduleContextView {
  prompt: string;
  notes?: ScheduleNoteView[];
  parent: ScheduleParentView;
  trigger: ScheduleTriggerView;
  subtasks: ScheduleSubtaskView[];
  previous_picks: SchedulePreviousPick[];
  instructions?: string | null;
}

/**
 * A chosen id that was not started, and why.
 */
export interface ScheduleIgnoredId {
  id: string;
  reason: string;
}

/**
 * A chosen task whose start was refused (spend limit, a step that cannot run).
 */
export interface ScheduleRefusedStart {
  id: string;
  reason: string;
}

/**
 * What came of a decision: what started, what was ignored, what was refused.
 */
export interface ScheduleResultView {
  started?: string[];
  ignored?: ScheduleIgnoredId[];
  refused?: ScheduleRefusedStart[];
}

/**
 * The connection that owns an entry. ``name`` is the provider's display name
 * ("GitHub"), which is what the secrets page groups and labels by.
 */
export interface SecretManagedBy {
  connection_id: string;
  type: string;
  name: string;
}

/**
 * Set the organization default, or one workspace's override.
 *
 * ``ge=1`` is Pydantic's; the upper bound is the ceiling, checked by the service.
 * Removing a limit is ``DELETE`` (FR-025).
 */
export interface SetConcurrencyLimitRequest {
  /** @minimum 1 */
  value: number;
}

export interface SetForgeAppRequest {
  /**
     * @minLength 1
     * @maxLength 512
     */
  client_id: string;
  client_secret?: string | null;
}

export interface SetForgePolicyRequest {
  required: boolean;
}

/**
 * Set the organization default, or one workspace's override.
 *
 * ``gt=0`` is Pydantic's, not a hand-written check (constitution §6). Removing a limit
 * is ``DELETE``; an amount of nothing is not how it is expressed.
 */
export interface SetLimitRequest {
  amount: number | string;
  period_type?: PeriodType;
}

export type SetMemoryLimitRequestUnit = typeof SetMemoryLimitRequestUnit[keyof typeof SetMemoryLimitRequestUnit];


export const SetMemoryLimitRequestUnit = {
  MiB: 'MiB',
  GiB: 'GiB',
} as const;

/**
 * Set the organization's memory default, or one workspace's override (spec 148).
 *
 * ``ge=1`` is Pydantic's; the real range — the minimum and the ceiling — and the
 * whole-GiB rule are the service's. ``unit`` is required: a client that does not say how
 * the number was entered cannot have it shown back that way.
 */
export interface SetMemoryLimitRequest {
  /** @minimum 1 */
  value_mib: number;
  unit: SetMemoryLimitRequestUnit;
}

/**
 * A rate sheet for one model, from one instant (077 US2).
 *
 * All four rates together, never one at a time (FR-011). A vendor changes a whole price
 * sheet at once, and a partial submission would either need three rates carried over
 * from a previous row — silently mixing two announcements — or leave holes that read as
 * zero. Zero is a legitimate rate; "unspecified" is not representable, so it is refused
 * at the door instead.
 *
 * Rates are per *million* tokens, as vendors publish them (FR-012, ``pricing.py``).
 * Storing per-token would mean writing 0.000003 for three dollars, where a misplaced
 * zero is invisible to a reader and catastrophic to a total.
 */
export interface SetModelPriceRequest {
  /**
     * @minLength 1
     * @maxLength 200
     */
  model_key: string;
  effective_from: string;
  input_rate: number | string;
  output_rate: number | string;
  cache_read_rate: number | string;
  cache_creation_rate: number | string;
}

/**
 * Route one gate on one task to a person (spec 067 FR-030).
 *
 * ``step_id`` travels in the body rather than the path deliberately: step ids
 * are free-form Text, so a path segment would need encoding rules for slashes
 * and other characters — a needless class of bug for a value the body carries
 * safely.
 */
export interface SetStepAssigneeRequest {
  /** @minLength 1 */
  step_id: string;
  assignee_user_id: string;
}

/**
 * A task's new description, or ``null`` to clear it.
 *
 * The field is required in the body while its *value* is nullable: sending
 * ``{"description": null}`` is how a caller says "remove it", and that has to be
 * a thing said rather than a thing left out.
 *
 * Whitespace-only is stored as ``null``, not as blanks. This text is prepended
 * to every prompt the task's later steps render (``snapshots.render_prompt``),
 * and ``Task:    `` at the head of an agent's instructions is worse than no line
 * at all — while on the board it would draw a Description section with nothing
 * visible inside it.
 */
export interface SetTaskDescriptionRequest {
  description: string | null;
}

/**
 * The enabled flag, and nothing else (US1 AC3). History is untouched (FR-005).
 */
export interface SetTriggerProfileEnabledRequest {
  enabled: boolean;
}

export type ShareOutcome = typeof ShareOutcome[keyof typeof ShareOutcome];


export const ShareOutcome = {
  added: 'added',
  updated: 'updated',
  unchanged: 'unchanged',
  already_in_library: 'already_in_library',
  refused: 'refused',
  uses_library: 'uses_library',
} as const;

export type SharePieceKind = typeof SharePieceKind[keyof typeof SharePieceKind];


export const SharePieceKind = {
  workflow: 'workflow',
  agent: 'agent',
  skill: 'skill',
} as const;

export interface SharePiece {
  kind: SharePieceKind;
  key: string;
  outcome: ShareOutcome;
  reason?: string | null;
  can_use_library?: boolean;
}

/**
 * What sharing would do, piece by piece, and whether it can go ahead.
 */
export interface SharePreview {
  pieces?: SharePiece[];
  blockers?: string[];
  shareable: boolean;
}

/**
 * The agents and skills to share with the workflow; its linked workflows always go.
 */
export interface ShareRequest {
  agents?: string[];
  skills?: string[];
  use_library?: LibraryPieceRef[];
}

export interface ShareResult {
  pieces: SharePiece[];
}

/**
 * A sibling candidate; `relationship` is implicitly "sibling" and not repeated per item.
 */
export interface SiblingTaskItem {
  task_id: string;
  title: string;
  step_name: string;
  status: TaskStatus;
}

export interface SiblingsResponse {
  workflow_id: string;
  siblings: SiblingTaskItem[];
}

export type SignalViewPayload = {[key: string]: JsonValue};

/**
 * One signal's full record — a debugging aid, not FR-013's attribution mechanism.
 *
 * That guarantee is met by the `tasks` feature's own contracts
 * (`TaskCommentView`/`TaskMoveView`'s `caused_by_signal_id`/
 * `caused_by_trigger_profile_name`), not by this endpoint
 * (contracts/trigger-profiles-api.md).
 */
export interface SignalView {
  id: string;
  external_key: string;
  related_task_id: string | null;
  status: string;
  payload: SignalViewPayload;
  created_at: string;
  processed_at: string | null;
  error: string | null;
}

/**
 * A bounded page of a profile's signals, plus the size of the whole history.
 *
 * ``total`` counts every signal recorded against the profile, not the page. That
 * is the field this envelope exists for: without it a reader cannot tell "this
 * profile has received exactly fifty signals" from "here are the most recent
 * fifty of several thousand", and a client could only guess by asking for a page
 * it expects to come back empty.
 *
 * Before spec 090 this route returned a bare array of every signal a profile had
 * ever received. The shape change is safe precisely once — no client had ever
 * called the route — and grows more expensive with every client that does.
 *
 * ``limit``/``offset`` echo the effective values; since out-of-range input is
 * rejected rather than clamped, they always equal what was requested.
 */
export interface SignalListPage {
  items: SignalView[];
  total: number;
  limit: number;
  offset: number;
}

export type SortOrder = typeof SortOrder[keyof typeof SortOrder];


export const SortOrder = {
  asc: 'asc',
  desc: 'desc',
} as const;

/**
 * Move some of a waiting task's subtasks to a new task beside it (spec 119 US6).
 */
export interface SplitTaskRequest {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @minItems 1 */
  child_task_ids: string[];
}

export interface SplitTaskResponse {
  task: TaskView;
  moved_task_ids: string[];
}

export type StartLinkedTaskResultOutcome = typeof StartLinkedTaskResultOutcome[keyof typeof StartLinkedTaskResultOutcome];


export const StartLinkedTaskResultOutcome = {
  started: 'started',
  refused: 'refused',
} as const;

/**
 * What Start all did with one waiting task (spec 125 FR-006).
 */
export interface StartLinkedTaskResult {
  task_id: string;
  outcome: StartLinkedTaskResultOutcome;
  reason?: string | null;
}

/**
 * One result per task that was waiting; empty when none was — a second click
 * is harmless, not an error.
 */
export interface StartLinkedTasksResponse {
  results?: StartLinkedTaskResult[];
}

export type StayingPieceKind = typeof StayingPieceKind[keyof typeof StayingPieceKind];


export const StayingPieceKind = {
  workflow: 'workflow',
  agent: 'agent',
  skill: 'skill',
} as const;

/**
 * A linked piece a take-down leaves in the Library, and why (spec 167).
 */
export interface StayingPiece {
  kind: StayingPieceKind;
  key: string;
  name: string;
  reason: string;
}

export type TakeDownPieceKind = typeof TakeDownPieceKind[keyof typeof TakeDownPieceKind];


export const TakeDownPieceKind = {
  workflow: 'workflow',
  agent: 'agent',
  skill: 'skill',
} as const;

/**
 * A shared item, as a take-down names it (spec 167).
 */
export interface TakeDownPiece {
  kind: TakeDownPieceKind;
  key: string;
  name: string;
}

/**
 * What taking ``item`` down would take with it, and what stays in the Library (spec 167).
 */
export interface TakeDownPreview {
  item: TakeDownPiece;
  also?: TakeDownPiece[];
  staying?: StayingPiece[];
}

export interface TakeDownResult {
  taken_down: TakeDownPiece[];
}

/**
 * The linked pieces to take down with the item: some or all of the preview's ``also``.
 */
export interface TakeDownSetRequest {
  also?: LibraryPieceRef[];
}

export type TaskArtifactViewKind = typeof TaskArtifactViewKind[keyof typeof TaskArtifactViewKind];


export const TaskArtifactViewKind = {
  merge_request: 'merge_request',
  pull_request: 'pull_request',
  branch: 'branch',
} as const;

/**
 * One thing a task created, with who created it and who changed it since.
 */
export interface TaskArtifactView {
  id: string;
  kind: TaskArtifactViewKind;
  forge_host: string;
  repository: string;
  number: number | null;
  web_url: string | null;
  title: string | null;
  source_branch: string;
  target_branch: string | null;
  latest_commit: string | null;
  created_by_execution_id: string | null;
  created_at: string;
  updated_by?: ArtifactUpdateView[];
}

/**
 * Everything a task created outside the platform (spec 112 FR-018).
 *
 * Merge and pull requests first, then branches, each oldest first. Empty,
 * never null, for a task that created nothing.
 */
export interface TaskArtifactsView {
  artifacts?: TaskArtifactView[];
}

export interface TaskAttachmentsView {
  folder_path: string;
  attachments?: AttachmentView[];
}

/**
 * A task's whole timeline, oldest first (FR-006).
 *
 * Wrapped rather than returned as a bare array so the shape can gain a cursor
 * later without every client changing at once.
 */
export interface TaskCommentList {
  items: TaskCommentView[];
}

export type TaskCreateInputValues = {[key: string]: JsonValue};

/**
 * Body of the create-task endpoint. The workflow is taken from the path.
 *
 * Carries no ``position``: a created task always starts at its workflow's
 * synthesized start step, which the service resolves server-side (004 FR-006).
 * Every workflow has exactly one start step (spec 003 D6), so that placement is
 * a lookup that cannot fail. Leaving the field off the contract is what makes
 * it unsmuggleable — a caller cannot seed a task mid-workflow by naming a step,
 * and no caller needs to know the reserved "$" namespace to create normally.
 */
export interface TaskCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  description?: string | null;
  input_values?: TaskCreateInputValues;
}

/**
 * The latest change a task made to one path, attributed to a run.
 */
export interface TaskFileChangeView {
  path: string;
  event_type: FileChangeEventType;
  source_path: string | null;
  occurred_at: string;
  step_id: string | null;
  step_name: string | null;
  execution_id: string | null;
}

/**
 * Whether the signed-in person follows a task, and how that arose.
 */
export interface TaskFollowState {
  task_id: string;
  following: boolean;
  reason?: FollowReason | null;
}

/**
 * The result of replacing a task's labels.
 *
 * The resulting set rather than the whole task: the caller just sent the change
 * and knows the rest of the task, and a full re-serialization would make the
 * cheapest write in the feature its heaviest response.
 */
export interface TaskLabelList {
  labels: TaskLabelView[];
}

/**
 * One entry in a task's move history (research.md D6).
 *
 * There was no way to read a task's move history through any endpoint before
 * spec 063 — this is the read side of that gap, added specifically so
 * trigger-caused attribution is visible on the task itself.
 */
export interface TaskMoveView {
  id: string;
  task_id: string;
  from_position: string;
  to_position: string;
  actor_type: TaskActorType;
  reason: string | null;
  transition_id: string | null;
  task_version: number;
  caused_by_signal_id?: string | null;
  caused_by_trigger_profile_name?: string | null;
  via?: string | null;
  created_at: string;
}

/**
 * A task's whole move history, oldest first — mirrors `TaskCommentList`.
 */
export interface TaskMoveList {
  items: TaskMoveView[];
}

/**
 * Everything a task produced, in one read (spec 095 FR-014).
 *
 * Both lists are empty, never null, for a task with nothing yet (FR-017), so
 * a caller can tell "nothing yet" from "task not found".
 */
export interface TaskResultsView {
  executions?: ExecutionResultView[];
  files?: TaskFileChangeView[];
  also_changed?: TaskFileChangeView[];
}

export type ThreadAuthorKind = typeof ThreadAuthorKind[keyof typeof ThreadAuthorKind];


export const ThreadAuthorKind = {
  person: 'person',
  agent: 'agent',
} as const;

/**
 * The answer a person's message is waiting for, or got.
 */
export interface ThreadReplyView {
  state: ReplyState;
  reason?: string | null;
  attempts?: ReplyAttemptView[];
}

/**
 * One message on a run's thread.
 */
export interface ThreadMessageView {
  id: string;
  execution_id: string;
  author_kind: ThreadAuthorKind;
  author?: UserSummary | null;
  body: string;
  created_at: string;
  reply?: ThreadReplyView | null;
  answers_message_id?: string | null;
  cut_short?: boolean;
  reply_execution_id?: string | null;
  cost?: string | null;
  added_to_task_context_at?: string | null;
  task_comment_id?: string | null;
}

/**
 * A page of a thread, oldest first.
 *
 * The first page is the thread's newest messages. ``next_before`` is the id to pass
 * back as ``before`` for the page of messages just older than these — ``None`` when
 * there are none. A cursor, as the inbox's, because the thread grows while it is read.
 */
export interface ThreadMessagePage {
  items: ThreadMessageView[];
  next_before?: string | null;
}

/**
 * The browser's IANA time zone, e.g. ``Europe/Warsaw`` (spec 119 FR-007).
 */
export interface TimezoneChange {
  timezone: string;
}

export type TriggerProfileCreateConfig = {[key: string]: JsonValue};

/**
 * Body of the create-trigger-profile endpoint (FR-001, FR-002).
 *
 * ``enabled`` is required, with no default: FR-001 names "whether it is
 * enabled" as something a caller states when configuring a profile, not
 * something the system infers, so a caller states it explicitly every
 * time. ``schedule_type``/``connection_id``/``interval_seconds``/
 * ``cron_expression``/``timezone`` are caller-settable only for the
 * scheduled (`github`/`gitlab`) profile types (spec 065 T043,
 * contracts/scheduled-trigger-profiles-api.md) — for
 * ``workspace_task_change`` they stay absent/server-defaulted exactly as
 * Spec 1 left them (data-model.md); :func:`app.features.triggers.
 * validation.validate_scheduled_profile` (T044) enforces the scheduled
 * types' own requirements on them.
 */
export interface TriggerProfileCreate {
  name: string;
  description?: string | null;
  type: string;
  enabled: boolean;
  config: TriggerProfileCreateConfig;
  connection_id?: string | null;
  target_workflow_id?: string | null;
  schedule_type?: string;
  interval_seconds?: number | null;
  cron_expression?: string | null;
  timezone?: string | null;
}

export type TriggerProfileRunViewDetails = {[key: string]: JsonValue} | null;

/**
 * One evaluation of a profile against a transition — matched or not (FR-004).
 *
 * A profile with zero rows here has never been evaluated; one with rows all
 * showing ``signals_created: 0`` has run but never matched — the
 * distinction US4 AC asks for.
 */
export interface TriggerProfileRunView {
  id: string;
  trigger_profile_id: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  signals_created: number;
  signals_actioned: number;
  error: string | null;
  details: TriggerProfileRunViewDetails;
}

export type TriggerProfileUpdateConfig = {[key: string]: JsonValue} | null;

/**
 * PATCH body for a general profile edit (spec 074, FR-001/FR-002,
 * contracts/trigger-profile-lifecycle-api.md) — a third union member
 * alongside :class:`SetTriggerProfileEnabledRequest`/
 * :class:`UpdateTriggerScheduleRequest`.
 *
 * Partial: every field is optional and ``None``/absent means "leave this
 * alone" — mirrors :class:`app.features.connections.contracts.
 * ConnectionUpdate`'s existing shape exactly, not a new update convention.
 * Deliberately has **no** ``type`` field at all — driver type is immutable
 * after creation (FR-003); a caller has no field to submit a type change
 * through, so the refusal is structural, not a runtime check.
 */
export interface TriggerProfileUpdate {
  name?: string | null;
  description?: string | null;
  config?: TriggerProfileUpdateConfig;
  connection_id?: string | null;
  schedule_type?: string | null;
  interval_seconds?: number | null;
  cron_expression?: string | null;
  timezone?: string | null;
  target_workflow_id?: string | null;
}

export type TriggerProfileViewConfig = {[key: string]: JsonValue};

/**
 * One trigger profile as the API presents it.
 *
 * Mirrors the full persisted row, including the read-only §5 fields
 * (``schedule_type``/``connection_id``/``interval_seconds``/
 * ``cron_expression``/``timezone``) that this spec's driver never varies —
 * a stored column is read back through the entity's own view like every
 * other persisted field in this codebase's existing view contracts
 * (`TaskView`, `WorkflowView`, `WorkspaceSecretView`), even one no create
 * request accepts (contracts/trigger-profiles-api.md).
 */
export interface TriggerProfileView {
  id: string;
  workspace_id: string;
  type: string;
  name: string;
  description: string | null;
  enabled: boolean;
  connection_id: string | null;
  target_workflow_id: string | null;
  schedule_type: string;
  interval_seconds: number | null;
  cron_expression: string | null;
  timezone: string | null;
  config: TriggerProfileViewConfig;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

/**
 * A model whose runs no price covers, and how much of it there is (077 FR-002).
 *
 * Runs and tokens, never money: money is precisely what is unknown here. A reader
 * judging whether a gap is worth acting on needs its size, and tokens are the only
 * measure of size a run nobody could price still has.
 */
export interface UnpricedModelView {
  model_key: string | null;
  run_count: number;
  total_tokens: number;
}

/**
 * Every unpriced model in a period, organization-wide.
 *
 * A wrapper rather than a bare list so that "nothing is unpriced" is a response with an
 * empty ``models`` and a period attached, rather than an empty array a reader cannot
 * distinguish from a question that was never asked (FR-003).
 */
export interface UnpricedModelsView {
  period: PeriodBounds;
  models: UnpricedModelView[];
}

/**
 * Unread, unarchived notifications across every workspace (US3 scenario 2).
 */
export interface UnreadCount {
  unread: number;
}

/**
 * Edit a provider.
 *
 * ``provider_type`` is absent by design (data-model.md): changing a provider's
 * type after creation is not a supported operation — an admin who wants a
 * different type creates a new provider.
 *
 * ``token`` omitted (not sent) leaves the stored token unchanged; sending it
 * replaces it (spec FR-004). Distinguished via ``model_fields_set``, the same
 * mechanism ``UpdateLLMProfileRequest`` already uses for its own
 * "omitted means unchanged" fields.
 */
export interface UpdateAIProviderRequest {
  name?: string | null;
  base_url?: string | null;
  token?: string | null;
}

/**
 * Edit a profile.
 *
 * ``slug`` is absent by design (research.md D4 of 066): a field that cannot change
 * should not be presentable — the identical reasoning
 * ``ReplaceWorkspaceSecretValueRequest`` applies to ``workspace_secret.key``.
 *
 * Every field is optional and independently omittable, and — unlike a single-field
 * PATCH — "omitted" and "explicitly cleared" must be distinguishable here:
 * ``max_turns`` sent as ``null`` clears that override, while it left out of the
 * body leaves it as it is. ``description`` (spec 168) follows the same rule, and a
 * blank one clears it too. ``name``/``ai_provider_id``/``model``/``effort`` cannot
 * be cleared to ``null`` at all — the service rejects that combination (see
 * :mod:`.service`) — but still follow "omitted leaves unchanged" for the same
 * reason: resending every field on every edit is what a ``PUT`` is for, not a
 * ``PATCH``.
 */
export interface UpdateLLMProfileRequest {
  name?: string | null;
  ai_provider_id?: string | null;
  model?: string | null;
  effort?: LLMProfileEffort | null;
  max_turns?: number | null;
  description?: string | null;
}

/**
 * Rename, recolour, or both.
 *
 * A body setting neither is a no-op returning the label unchanged rather than
 * an error: there is nothing incorrect about it, and refusing it would make a
 * caller special-case a form submission that happened not to change anything.
 */
export interface UpdateLabelRequest {
  name?: string | null;
  color?: LabelColor | null;
}

export interface UpdateMemberRequest {
  role: WorkspaceRole;
}

/**
 * Change the organization's name, its environment, or both.
 *
 * Both fields optional so a caller may send one without restating the other. Sending
 * neither is accepted and changes nothing — a no-op is not an error, and rejecting it
 * would make a form that submits only dirty fields fail on a form nobody edited.
 */
export interface UpdateOrganizationRequest {
  name?: string | null;
  environment?: OrganizationEnvironment | null;
}

/**
 * PATCH body for editing a scheduled (`github`/`gitlab`) profile's cadence only
 * (US2 AC5, contracts/scheduled-trigger-profiles-api.md) — identity, ``config``,
 * ``connection_id``, and run history are all untouched; calls `handle.update(...)`
 * on the existing Temporal Schedule rather than delete-and-recreate.
 */
export interface UpdateTriggerScheduleRequest {
  schedule_type: string;
  interval_seconds?: number | null;
  cron_expression?: string | null;
  timezone?: string | null;
}

/**
 * Rename, change visibility, or both (spec 153 FR-005). Owners and org admins.
 */
export interface UpdateWorkspaceRequest {
  name?: string | null;
  visibility?: WorkspaceVisibility | null;
}

/**
 * The description, and nothing else (060 FR-008).
 *
 * Deliberately not a field on :class:`ReplaceWorkspaceSecretValueRequest`. That request
 * means "replace the value", so editing a secret's note through it would demand a value
 * the caller cannot read back — and relaxing that gate would make an empty value
 * ambiguous between "leave it" and "set it empty" on the one form where getting that
 * wrong is unrecoverable.
 *
 * ``description`` is **required and nullable**, which removes the ambiguity a ``PATCH``
 * usually carries: ``{"description": null}`` clears, and ``{}`` is refused rather than
 * silently meaning "change nothing". With exactly one field in the body, absence has no
 * useful meaning to preserve.
 */
export interface UpdateWorkspaceSecretDescriptionRequest {
  description: string | null;
}

/**
 * The signed-in person's commit email; ``None`` when their sign-in email is used.
 */
export interface UserCommitEmail {
  commit_email: string | null;
}

/**
 * Everyone who may be assigned to a Human Gate (FR-013).
 */
export interface UserListPage {
  items: UserSummary[];
}

/**
 * The signed-in person's stored zone; ``None`` while unknown (the digest uses UTC).
 */
export interface UserTimezone {
  timezone: string | null;
}

export interface VariableManifest {
  driver_type: string;
  groups: ManifestGroup[];
}

export type WorkflowBreakdownEntryThreshold = typeof WorkflowBreakdownEntryThreshold[keyof typeof WorkflowBreakdownEntryThreshold] | null;


export const WorkflowBreakdownEntryThreshold = {
  under: 'under',
  approaching: 'approaching',
  exhausted: 'exhausted',
} as const;

/**
 * One workflow's row of the breakdown, and the cap it sits against (spec 080 FR-019).
 *
 * A distinct type rather than two optional fields on :class:`BreakdownEntry`. That
 * model also serves ``by_model`` and ``by_person``, neither of which can ever carry a
 * cap — adding the fields there would spare one type at the cost of two contracts that
 * are permanently untrue of the things they describe.
 */
export interface WorkflowBreakdownEntry {
  key: string | null;
  label: string;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency?: string;
  total_tokens: number;
  run_count: number;
  unmeasured_run_count: number;
  limit?: string | null;
  threshold?: WorkflowBreakdownEntryThreshold;
  limit_consumed_amount?: string | null;
  limit_period?: PeriodBounds | null;
  deleted: boolean;
}

export type WorkflowInputSpecConfiguration = {[key: string]: JsonValue};

/**
 * One ``inputs`` mapping entry, in the PRD §23 shape (research D14).
 */
export interface WorkflowInputSpec {
  type: WorkflowInputType;
  title: string;
  description?: string | null;
  required?: boolean;
  default?: JsonValue | null;
  configuration?: WorkflowInputSpecConfiguration;
}

export type WorkflowCreateInputs = {[key: string]: WorkflowInputSpec};

/**
 * The YAML-facing / JSON-door create shape — one validator, two doors (D11).
 *
 * ``name`` is schema-optional so a missing name is a *semantic* violation
 * reported together with every other finding in one response (spec US1
 * scenario 4) instead of a schema failure that stops the pipeline; the
 * validator requires it before anything persists.
 */
export interface WorkflowCreate {
  key?: string | null;
  name?: string | null;
  description?: string | null;
  inputs?: WorkflowCreateInputs;
  phases?: WorkflowPhaseDef[];
  start?: string | null;
  image?: string | null;
}

/**
 * Create an empty draft from a name alone (research D1).
 *
 * Deliberately *not* a variant of :class:`WorkflowCreate`: that model is the
 * YAML-facing shape, and neither ``status`` nor an empty definition is something
 * a workflow *file* should be able to express. ``key`` is not accepted — it is
 * slugified from the name and immutable thereafter (FR-003).
 */
export interface WorkflowDraftCreate {
  name: string;
  description?: string | null;
}

/**
 * What the builder opens on: the published workflow, and any pending changes.
 */
export interface WorkflowEditView {
  workflow: WorkflowView;
  pending_changes?: PendingChangesView | null;
}

/**
 * What an import returns: the stored workflow, and what it let go of.
 *
 * A wrapper rather than fields on :class:`WorkflowView`, because a warning
 * describes the *import event*, not the workflow. Carried on the view it would
 * reappear on every later read — the board, the builder, the polled aggregate —
 * long after it stopped meaning anything.
 */
export interface WorkflowImportResult {
  workflow: WorkflowView;
  warnings?: ImportWarning[];
  substitutions?: AppliedSubstitution[];
}

export type WorkflowLimitRowThreshold = typeof WorkflowLimitRowThreshold[keyof typeof WorkflowLimitRowThreshold];


export const WorkflowLimitRowThreshold = {
  under: 'under',
  approaching: 'approaching',
  exhausted: 'exhausted',
} as const;

/**
 * One row of the console's workflow-limit table (spec 080 FR-001).
 *
 * Only workflows that *have* a limit appear. Limits are opt-in, so this is a list of
 * deliberate acts rather than of every workflow in the organization.
 */
export interface WorkflowLimitRow {
  workflow_id: string;
  workflow_name: string;
  workspace_id: string;
  workspace_name: string;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  spend: string;
  currency?: string;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  limit: string;
  threshold: WorkflowLimitRowThreshold;
  period_type: PeriodType;
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  limit_consumed_amount: string;
  limit_period: PeriodBounds;
}

/**
 * A workflow's limit as it stands after being set (spec 080 FR-007, FR-008).
 *
 * Separate from :class:`LimitView` rather than adding nullable fields to it.
 * ``blocks_workspaces`` and ``blocks_workflows`` count different things, and one model
 * carrying both would always have one of them meaningless.
 */
export interface WorkflowLimitView {
  /** @pattern ^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$ */
  amount: string;
  currency?: string;
  blocks_workflows: number;
  exceeds_workspace_limit: boolean;
  period_type: PeriodType;
}

/**
 * A few of a workspace's workflow names, and how many there are (spec 070 US2).
 *
 * Owned by this package rather than by ``workspaces``, for the same reason
 * :class:`WorkflowView` is: a workspace composes the shapes its children define,
 * and a workflow's name is a fact about a workflow.
 *
 * ``total`` is carried alongside because ``names`` is capped, and a truncated list
 * presented without its total reads as the whole set. With it a card can say "and
 * 9 more"; without it, it silently lies.
 *
 * This replaces the graph thumbnail the specification originally called for. A
 * workspace holds many workflows, so any single picture is of an arbitrary member
 * — and selecting "most recently updated" made it change whenever unrelated work
 * happened, which is the opposite of something you can learn to recognise.
 */
export interface WorkflowNames {
  /** @maxItems 3 */
  names: string[];
  /** @minimum 0 */
  total: number;
}

/**
 * Publish a workflow's pending changes: the revision the client is looking at.
 */
export interface WorkflowPendingChangesPublish {
  expected_revision: string;
}

/**
 * Save a whole definition as a published workflow's pending changes (spec 127).
 *
 * The two tokens answer different questions. ``expected_revision`` is the
 * pending changes the client loaded, or ``None`` when it loaded none — so a
 * second editor's save, or a save from a copy discarded since, is refused
 * (FR-014). ``expected_version`` is the published version the client loaded,
 * checked only on the first save, where it becomes what the pending changes
 * are based on (FR-003).
 */
export interface WorkflowPendingChangesSave {
  name: string;
  description?: string | null;
  inputs?: WorkflowInputDef[];
  phases?: WorkflowPhaseDef[];
  start?: string | null;
  image?: string | null;
  layout?: WorkflowLayout | null;
  expected_revision?: string | null;
  expected_version: number;
}

export interface WorkflowRename {
  name: string;
}

/**
 * Replace a workflow's entire definition in one all-or-nothing write (D2).
 *
 * The definition fields are :class:`WorkflowDraftDefinition`'s; this adds the
 * two that make it a write of the live definition.
 */
export interface WorkflowReplace {
  name: string;
  description?: string | null;
  inputs?: WorkflowInputDef[];
  phases?: WorkflowPhaseDef[];
  start?: string | null;
  image?: string | null;
  layout?: WorkflowLayout | null;
  status: WorkflowStatus;
  expected_version: number;
}

/**
 * A period's spend three ways, each line with what it is made of.
 *
 * Every breakdown covers every run of the period, so each adds up to the Usage page's
 * spend and run count. Lines are ordered by spend, then tokens; a client never
 * re-sorts them.
 */
export interface WorkspaceAIModelSpend {
  period: PeriodBounds;
  by_workflow: AIModelSpendEntry[];
  by_profile: AIModelSpendEntry[];
  by_connection: AIModelSpendEntry[];
  by_day: DailySpendParts[];
}

export interface WorkspaceAIModels {
  period: PeriodBounds;
  earliest_period_key: string | null;
  entries: AIModelEntry[];
  workflows?: AIModelWorkflow[];
}

/**
 * The caller's standing in one workspace.
 *
 * ``role`` is their membership; an org admin who has not joined has none, yet may
 * work and manage. With the access switch off, everyone may work and manage, as
 * before.
 */
export interface WorkspaceAccessView {
  role?: WorkspaceRole | null;
  can_work?: boolean;
  can_manage?: boolean;
  enforced?: boolean;
  as_org_admin?: boolean;
}

export type WorkspaceStatus = typeof WorkspaceStatus[keyof typeof WorkspaceStatus];


export const WorkspaceStatus = {
  active: 'active',
} as const;

/**
 * What every member sees with the workspace (FR-028): capacity is not money.
 */
export interface WorkspaceConcurrencySummary {
  limit: number;
  running: number;
  queued: number;
}

export interface WorkspaceAggregate {
  id: string;
  name: string;
  storage_prefix: string;
  status: WorkspaceStatus;
  created_at: string;
  updated_at: string;
  workflows: WorkflowView[];
  tasks: TaskView[];
  created_by?: UserSummary | null;
  labels: LabelView[];
  concurrency?: WorkspaceConcurrencySummary | null;
  visibility?: WorkspaceVisibility;
  access?: WorkspaceAccessView | null;
}

/**
 * The ``data`` of a ``workspace_changed`` stream event (spec 117 FR-004).
 *
 * Something a page shows in this workspace committed: a task created, moved, edited,
 * hidden or deleted, its labels changed, a run started or ended, or a comment added.
 * It names no change on purpose — the client re-reads the workspace if it is showing
 * it, and ignores the message otherwise.
 */
export interface WorkspaceChangedEvent {
  workspace_id: string;
}

/**
 * A workspace's limit, where it comes from, and its load (FR-027).
 *
 * For the workspace's creator and organization administrators.
 */
export interface WorkspaceConcurrencyView {
  limit: number;
  source: LimitSource;
  clamped: boolean;
  ceiling: number;
  running: number;
  queued: number;
}

/**
 * How much a workspace holds, without saying what it holds (spec 070 US1).
 *
 * Counts are about contents rather than contents themselves, which is the whole
 * of the distinction that lets them sit on a list entry at all. Both are always
 * present when asked for — an empty workspace reports zero rather than omitting
 * the field (FR-005), because a missing count and a count of none are different
 * claims and a reader cannot tell them apart.
 */
export interface WorkspaceCounts {
  /** @minimum 0 */
  workflows: number;
  /** @minimum 0 */
  tasks: number;
}

/**
 * One list entry: identity and description, never contents (FR-003/004/005).
 *
 * ``storage_prefix`` is deliberately absent — it is an internal storage detail
 * and stays on :class:`WorkspaceAggregate` (constitution §2.7).
 */
export interface WorkspaceSummary {
  id: string;
  name: string;
  status: WorkspaceStatus;
  created_at: string;
  updated_at: string;
  created_by?: UserSummary | null;
  counts?: WorkspaceCounts | null;
  workflow_names?: WorkflowNames | null;
  last_activity_at?: string | null;
  visibility?: WorkspaceVisibility | null;
  access?: WorkspaceAccessView | null;
}

/**
 * A bounded page of summaries plus the size of the whole collection.
 *
 * ``total`` counts every workspace, not the page, so a client can tell whether
 * more exist without requesting a page that returns nothing (FR-019, SC-009).
 * ``limit``/``offset`` echo the effective values; since out-of-range input is
 * rejected rather than clamped, they always equal what was requested.
 */
export interface WorkspaceListPage {
  items: WorkspaceSummary[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * The members, and what an owner needs before changing visibility (FR-001).
 */
export interface WorkspaceMembersView {
  members: MemberView[];
  visibility: WorkspaceVisibility;
  non_member_followers: number;
}

export type WorkspaceMemoryViewUnit = typeof WorkspaceMemoryViewUnit[keyof typeof WorkspaceMemoryViewUnit];


export const WorkspaceMemoryViewUnit = {
  MiB: 'MiB',
  GiB: 'GiB',
} as const;

/**
 * A workspace's memory amount and where it comes from.
 *
 * For the workspace's creator and organization administrators.
 */
export interface WorkspaceMemoryView {
  limit_mib: number;
  unit: WorkspaceMemoryViewUnit;
  source: LimitSource;
  minimum_mib: number;
}

/**
 * Who a picker in this workspace offers (FR-006).
 */
export interface WorkspacePeople {
  items: UserSummary[];
}

/**
 * One entry as the API presents it.
 *
 * ``value`` is populated for a ``variable`` and always ``None`` for a ``secret`` —
 * including in the response to the request that created it. The field is present on
 * both kinds so the response shape is uniform, and its nullability is the
 * discriminator. It carries nothing derived from a secret's plaintext: no masked
 * fragment, no length, no hash (FR-010).
 */
export interface WorkspaceSecretView {
  key: string;
  kind: WorkspaceSecretKind;
  value: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  managed_by: SecretManagedBy | null;
}

export interface WorkspaceSecretList {
  items: WorkspaceSecretView[];
}

/**
 * Everything the usage dashboard reads, in one response.
 */
export interface WorkspaceUsageSummary {
  period: PeriodBounds;
  totals: TokenTotals;
  run_count: number;
  unmeasured_run_count: number;
  spend: SpendTotals;
  limit: AppliedLimit | null;
  by_day: DailyEntry[];
  by_workflow: WorkflowBreakdownEntry[];
  by_model: BreakdownEntry[];
  by_person: BreakdownEntry[];
  earliest_period_key: string | null;
}

export type Health200 = {[key: string]: string};

export type ListAssignableUsersParams = {
search?: string | null;
};

export type ListPeopleForgeAccountsParams = {
search?: string | null;
/**
 * @minimum 1
 */
page?: number;
page_size?: number | null;
};

export type FinishForgeOauthParams = {
code?: string | null;
state?: string | null;
error?: string | null;
};

export type ListWorkspacesParams = {
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
include?: ListInclude[] | null;
/**
 * `created` (default): newest-created first. `activity`: the workspace where something happened most recently first, across every page.
 */
sort?: ListSort;
};

export type ReadWorkspaceParams = {
/**
 * Include tasks hidden from the board. Off by default; a hidden task still contributes to the workspace's spend, so there has to be a way to find one.
 */
include_hidden?: boolean;
};

export type ListWorkspacePeopleParams = {
search?: string | null;
};

export type ReadWorkspaceAccessLogParams = {
/**
 * The last entry of the previous page.
 */
before?: string | null;
limit?: number | null;
};

export type DiscardWorkflowPendingChangesParams = {
expected_revision: string;
};

export type GetTaskSiblingsParams = {
workflow_id: string;
};

export type ListRunThreadMessagesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * A message id from the previous page's `next_before`.
 */
before?: string | null;
};

export type ListWorkspaceFilesParams = {
path?: string;
};

export type DeleteWorkspaceFileParams = {
path: string;
/**
 * Delete everything stored under `path` as well, at any depth. Required to delete a folder: a folder is a key prefix rather than an object, so without this the path finds nothing to delete.
 */
recursive?: boolean;
task_id?: string | null;
step_id?: string | null;
};

export type ReadWorkspaceFileContentParams = {
path: string;
};

export type GetWorkspaceFileHistoryParams = {
/**
 * Narrow the history to one file, following it across renames. Omit it for every change in the workspace, newest first.
 */
path?: string | null;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTriggerProfileSignalsParams = {
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ReadWorkspaceUsageParams = {
/**
 * Calendar month as YYYY-MM (unchanged), or a prefixed key for the other period types ("daily:2026-09-15", "weekly:2026-09-14", "none"). Defaults to the current UTC month.
 */
period?: string | null;
/**
 * Split each day of by_day by workflow, person or model: every entry then carries parts, keyed as the period breakdown keys its lines (spec 207).
 */
split?: DailySplit | null;
};

export type ReadWorkspaceAiModelsParams = {
/**
 * Any period key the Usage page accepts: YYYY-MM, or a prefixed key ("daily:2026-09-15", "weekly:2026-09-14", "none"). Defaults to the current UTC month.
 */
period?: string | null;
};

export type ReadWorkspaceAiModelSpendParams = {
/**
 * Any period key the Usage page accepts: YYYY-MM, or a prefixed key ("daily:2026-09-15", "weekly:2026-09-14", "none"). Defaults to the current UTC month.
 */
period?: string | null;
};

export type ReadOrganizationUsageParams = {
/**
 * Calendar month as YYYY-MM (unchanged), or a prefixed key for the other period types ("daily:2026-09-15", "weekly:2026-09-14", "none"). Defaults to the current UTC month.
 */
period?: string | null;
};

export type ReadUnpricedModelsParams = {
/**
 * Calendar month as YYYY-MM (unchanged), or a prefixed key for the other period types ("daily:2026-09-15", "weekly:2026-09-14", "none"). Defaults to the current UTC month.
 */
period?: string | null;
};

export type ReadWorkflowLimitsParams = {
/**
 * Calendar month as YYYY-MM (unchanged), or a prefixed key for the other period types ("daily:2026-09-15", "weekly:2026-09-14", "none"). Defaults to the current UTC month.
 */
period?: string | null;
};

export type ListOrganizationWorkspacesParams = {
/**
 * Part of a workspace's name.
 */
search?: string | null;
sort?: AdminWorkspaceSort;
/**
 * Defaults to A→Z by name, and newest or largest first otherwise.
 */
order?: SortOrder | null;
without_active_owner?: boolean;
};

export type ReadOrganizationAccessLogParams = {
/**
 * First day, UTC.
 */
from?: string | null;
/**
 * Last day, UTC, inclusive.
 */
to?: string | null;
workspace_id?: string | null;
/**
 * The last entry of the previous page.
 */
before?: string | null;
limit?: number | null;
};

export type ExportOrganizationAccessLogParams = {
/**
 * First day, UTC.
 */
from: string;
/**
 * Last day, UTC, inclusive.
 */
to: string;
};

export type ReadLibraryItemFileParams = {
/**
 * Relative to the item's folder, as the catalog lists it.
 */
path: string;
};

export type ExportWorkflowBundleParams = {
/**
 * An agent to send along.
 */
agent?: string[] | null;
/**
 * A skill to send along.
 */
skill?: string[] | null;
};

export type ListNotificationsParams = {
/**
 * Only notifications not yet read.
 */
unread?: boolean;
/**
 * Archived notifications too. Ignored when `archived` is set.
 */
include_archived?: boolean;
/**
 * Only archived notifications (spec 120). When set, `include_archived` is ignored: either value lists the archived ones alone.
 */
archived?: boolean;
/**
 * Only notifications of this group. `needs_you` lists only open ones: a resolved notification no longer needs anyone (spec 120 FR-004).
 */
group?: NotificationGroup | null;
workspace_id?: string | null;
/**
 * Only these kinds; repeat the parameter or separate them with commas (`kind=a,b`). With `group`, a notification must be of one of these kinds and in that group. Leave it out for every kind: an empty list (`kind=`, or `[]` in a body) is refused, not read as every kind.
 */
kind?: NotificationKind[] | null;
/**
 * Only notifications created at or after this instant.
 */
created_from?: string | null;
/**
 * Only notifications created before this instant. Before `created_from` is refused.
 */
created_to?: string | null;
/**
 * Only open, or only resolved, notifications; both when absent. `needs_you` with `resolved` matches nothing.
 */
state?: NotificationState | null;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * A notification id from the previous page's `next_before`.
 */
before?: string | null;
};

export type CountNotificationsParams = {
/**
 * Only notifications not yet read.
 */
unread?: boolean;
/**
 * Archived notifications too. Ignored when `archived` is set.
 */
include_archived?: boolean;
/**
 * Only archived notifications (spec 120). When set, `include_archived` is ignored: either value lists the archived ones alone.
 */
archived?: boolean;
/**
 * Only notifications of this group. `needs_you` lists only open ones: a resolved notification no longer needs anyone (spec 120 FR-004).
 */
group?: NotificationGroup | null;
workspace_id?: string | null;
/**
 * Only these kinds; repeat the parameter or separate them with commas (`kind=a,b`). With `group`, a notification must be of one of these kinds and in that group. Leave it out for every kind: an empty list (`kind=`, or `[]` in a body) is refused, not read as every kind.
 */
kind?: NotificationKind[] | null;
/**
 * Only notifications created at or after this instant.
 */
created_from?: string | null;
/**
 * Only notifications created before this instant. Before `created_from` is refused.
 */
created_to?: string | null;
/**
 * Only open, or only resolved, notifications; both when absent. `needs_you` with `resolved` matches nothing.
 */
state?: NotificationState | null;
};

export type UnsubscribeFromEmailParams = {
/**
 * The signed token from the email's link.
 */
token: string;
};

export type UnsubscribeFromEmailOneClickParams = {
/**
 * The signed token from the email's link.
 */
token: string;
};

export type ReadRunForgeTokenParams = {
/**
 * @minLength 1
 */
host: string;
};

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type healthResponse200 = {
  data: Health200
  status: 200
}

export type healthResponseSuccess = (healthResponse200) & {
  headers: Headers;
};
;

export type healthResponse = (healthResponseSuccess)

export const getHealthUrl = () => {




  return `/health`
}

/**
 * @summary Health
 */
export const health = async ( options?: RequestInit): Promise<healthResponse> => {

  return customFetch<healthResponse>(getHealthUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getHealthQueryKey = () => {
    return [
    `/health`
    ] as const;
    }


export const getHealthQueryOptions = <TData = Awaited<ReturnType<typeof health>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getHealthQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof health>>> = ({ signal }) => health({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type HealthQueryResult = NonNullable<Awaited<ReturnType<typeof health>>>
export type HealthQueryError = unknown


export function useHealth<TData = Awaited<ReturnType<typeof health>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof health>>,
          TError,
          Awaited<ReturnType<typeof health>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useHealth<TData = Awaited<ReturnType<typeof health>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof health>>,
          TError,
          Awaited<ReturnType<typeof health>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useHealth<TData = Awaited<ReturnType<typeof health>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Health
 */

export function useHealth<TData = Awaited<ReturnType<typeof health>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getHealthQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readCurrentUserResponse200 = {
  data: CurrentUserView
  status: 200
}

export type readCurrentUserResponse401 = {
  data: Error
  status: 401
}

export type readCurrentUserResponseSuccess = (readCurrentUserResponse200) & {
  headers: Headers;
};
export type readCurrentUserResponseError = (readCurrentUserResponse401) & {
  headers: Headers;
};

export type readCurrentUserResponse = (readCurrentUserResponseSuccess | readCurrentUserResponseError)

export const getReadCurrentUserUrl = () => {




  return `/api/users/me`
}

/**
 * @summary Read Current User
 */
export const readCurrentUser = async ( options?: RequestInit): Promise<readCurrentUserResponse> => {

  return customFetch<readCurrentUserResponse>(getReadCurrentUserUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadCurrentUserQueryKey = () => {
    return [
    `/api/users/me`
    ] as const;
    }


export const getReadCurrentUserQueryOptions = <TData = Awaited<ReturnType<typeof readCurrentUser>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readCurrentUser>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadCurrentUserQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readCurrentUser>>> = ({ signal }) => readCurrentUser({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readCurrentUser>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadCurrentUserQueryResult = NonNullable<Awaited<ReturnType<typeof readCurrentUser>>>
export type ReadCurrentUserQueryError = Error


export function useReadCurrentUser<TData = Awaited<ReturnType<typeof readCurrentUser>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readCurrentUser>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof readCurrentUser>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadCurrentUser<TData = Awaited<ReturnType<typeof readCurrentUser>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readCurrentUser>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof readCurrentUser>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadCurrentUser<TData = Awaited<ReturnType<typeof readCurrentUser>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readCurrentUser>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Current User
 */

export function useReadCurrentUser<TData = Awaited<ReturnType<typeof readCurrentUser>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readCurrentUser>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadCurrentUserQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type setCurrentUserTimezoneResponse200 = {
  data: UserTimezone
  status: 200
}

export type setCurrentUserTimezoneResponse401 = {
  data: Error
  status: 401
}

export type setCurrentUserTimezoneResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type setCurrentUserTimezoneResponseSuccess = (setCurrentUserTimezoneResponse200) & {
  headers: Headers;
};
export type setCurrentUserTimezoneResponseError = (setCurrentUserTimezoneResponse401 | setCurrentUserTimezoneResponse422) & {
  headers: Headers;
};

export type setCurrentUserTimezoneResponse = (setCurrentUserTimezoneResponseSuccess | setCurrentUserTimezoneResponseError)

export const getSetCurrentUserTimezoneUrl = () => {




  return `/api/users/me/timezone`
}

/**
 * Store the browser's IANA time zone, for the 08:00 digest (spec 119 US4).
 *
 * An unknown name is refused with 422 and nothing changes.
 * @summary Set Current User Timezone
 */
export const setCurrentUserTimezone = async (timezoneChange: TimezoneChange, options?: RequestInit): Promise<setCurrentUserTimezoneResponse> => {

  return customFetch<setCurrentUserTimezoneResponse>(getSetCurrentUserTimezoneUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(timezoneChange)
  }
);}




export const getSetCurrentUserTimezoneMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setCurrentUserTimezone>>, TError,{data: TimezoneChange}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setCurrentUserTimezone>>, TError,{data: TimezoneChange}, TContext> => {

const mutationKey = ['setCurrentUserTimezone'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setCurrentUserTimezone>>, {data: TimezoneChange}> = (props) => {
          const {data} = props ?? {};

          return  setCurrentUserTimezone(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetCurrentUserTimezoneMutationResult = NonNullable<Awaited<ReturnType<typeof setCurrentUserTimezone>>>
    export type SetCurrentUserTimezoneMutationBody = TimezoneChange
    export type SetCurrentUserTimezoneMutationError = Error | HTTPValidationError

    /**
 * @summary Set Current User Timezone
 */
export const useSetCurrentUserTimezone = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setCurrentUserTimezone>>, TError,{data: TimezoneChange}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setCurrentUserTimezone>>,
        TError,
        {data: TimezoneChange},
        TContext
      > => {
      return useMutation(getSetCurrentUserTimezoneMutationOptions(options), queryClient);
    }

export type readCurrentUserCommitEmailResponse200 = {
  data: UserCommitEmail
  status: 200
}

export type readCurrentUserCommitEmailResponse401 = {
  data: Error
  status: 401
}

export type readCurrentUserCommitEmailResponseSuccess = (readCurrentUserCommitEmailResponse200) & {
  headers: Headers;
};
export type readCurrentUserCommitEmailResponseError = (readCurrentUserCommitEmailResponse401) & {
  headers: Headers;
};

export type readCurrentUserCommitEmailResponse = (readCurrentUserCommitEmailResponseSuccess | readCurrentUserCommitEmailResponseError)

export const getReadCurrentUserCommitEmailUrl = () => {




  return `/api/users/me/commit-email`
}

/**
 * The address the person's agent commits are authored with (spec 138 US3).
 *
 * ``null`` when unset: their sign-in email is used.
 * @summary Read Current User Commit Email
 */
export const readCurrentUserCommitEmail = async ( options?: RequestInit): Promise<readCurrentUserCommitEmailResponse> => {

  return customFetch<readCurrentUserCommitEmailResponse>(getReadCurrentUserCommitEmailUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadCurrentUserCommitEmailQueryKey = () => {
    return [
    `/api/users/me/commit-email`
    ] as const;
    }


export const getReadCurrentUserCommitEmailQueryOptions = <TData = Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadCurrentUserCommitEmailQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readCurrentUserCommitEmail>>> = ({ signal }) => readCurrentUserCommitEmail({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadCurrentUserCommitEmailQueryResult = NonNullable<Awaited<ReturnType<typeof readCurrentUserCommitEmail>>>
export type ReadCurrentUserCommitEmailQueryError = Error


export function useReadCurrentUserCommitEmail<TData = Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readCurrentUserCommitEmail>>,
          TError,
          Awaited<ReturnType<typeof readCurrentUserCommitEmail>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadCurrentUserCommitEmail<TData = Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readCurrentUserCommitEmail>>,
          TError,
          Awaited<ReturnType<typeof readCurrentUserCommitEmail>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadCurrentUserCommitEmail<TData = Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Current User Commit Email
 */

export function useReadCurrentUserCommitEmail<TData = Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readCurrentUserCommitEmail>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadCurrentUserCommitEmailQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type setCurrentUserCommitEmailResponse200 = {
  data: UserCommitEmail
  status: 200
}

export type setCurrentUserCommitEmailResponse401 = {
  data: Error
  status: 401
}

export type setCurrentUserCommitEmailResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type setCurrentUserCommitEmailResponseSuccess = (setCurrentUserCommitEmailResponse200) & {
  headers: Headers;
};
export type setCurrentUserCommitEmailResponseError = (setCurrentUserCommitEmailResponse401 | setCurrentUserCommitEmailResponse422) & {
  headers: Headers;
};

export type setCurrentUserCommitEmailResponse = (setCurrentUserCommitEmailResponseSuccess | setCurrentUserCommitEmailResponseError)

export const getSetCurrentUserCommitEmailUrl = () => {




  return `/api/users/me/commit-email`
}

/**
 * Set, or clear with ``null``, the person's commit email (spec 138 US3).
 *
 * Not an email address is refused with 422 and nothing changes.
 * @summary Set Current User Commit Email
 */
export const setCurrentUserCommitEmail = async (commitEmailChange: CommitEmailChange, options?: RequestInit): Promise<setCurrentUserCommitEmailResponse> => {

  return customFetch<setCurrentUserCommitEmailResponse>(getSetCurrentUserCommitEmailUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(commitEmailChange)
  }
);}




export const getSetCurrentUserCommitEmailMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setCurrentUserCommitEmail>>, TError,{data: CommitEmailChange}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setCurrentUserCommitEmail>>, TError,{data: CommitEmailChange}, TContext> => {

const mutationKey = ['setCurrentUserCommitEmail'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setCurrentUserCommitEmail>>, {data: CommitEmailChange}> = (props) => {
          const {data} = props ?? {};

          return  setCurrentUserCommitEmail(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetCurrentUserCommitEmailMutationResult = NonNullable<Awaited<ReturnType<typeof setCurrentUserCommitEmail>>>
    export type SetCurrentUserCommitEmailMutationBody = CommitEmailChange
    export type SetCurrentUserCommitEmailMutationError = Error | HTTPValidationError

    /**
 * @summary Set Current User Commit Email
 */
export const useSetCurrentUserCommitEmail = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setCurrentUserCommitEmail>>, TError,{data: CommitEmailChange}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setCurrentUserCommitEmail>>,
        TError,
        {data: CommitEmailChange},
        TContext
      > => {
      return useMutation(getSetCurrentUserCommitEmailMutationOptions(options), queryClient);
    }

export type listAssignableUsersResponse200 = {
  data: UserListPage
  status: 200
}

export type listAssignableUsersResponse401 = {
  data: Error
  status: 401
}

export type listAssignableUsersResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listAssignableUsersResponseSuccess = (listAssignableUsersResponse200) & {
  headers: Headers;
};
export type listAssignableUsersResponseError = (listAssignableUsersResponse401 | listAssignableUsersResponse422) & {
  headers: Headers;
};

export type listAssignableUsersResponse = (listAssignableUsersResponseSuccess | listAssignableUsersResponseError)

export const getListAssignableUsersUrl = (params?: ListAssignableUsersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/users?${stringifiedParams}` : `/api/users`
}

/**
 * @summary List Assignable Users
 */
export const listAssignableUsers = async (params?: ListAssignableUsersParams, options?: RequestInit): Promise<listAssignableUsersResponse> => {

  return customFetch<listAssignableUsersResponse>(getListAssignableUsersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAssignableUsersQueryKey = (params?: ListAssignableUsersParams,) => {
    return [
    `/api/users`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAssignableUsersQueryOptions = <TData = Awaited<ReturnType<typeof listAssignableUsers>>, TError = Error | HTTPValidationError>(params?: ListAssignableUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAssignableUsers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAssignableUsersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAssignableUsers>>> = ({ signal }) => listAssignableUsers(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAssignableUsers>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListAssignableUsersQueryResult = NonNullable<Awaited<ReturnType<typeof listAssignableUsers>>>
export type ListAssignableUsersQueryError = Error | HTTPValidationError


export function useListAssignableUsers<TData = Awaited<ReturnType<typeof listAssignableUsers>>, TError = Error | HTTPValidationError>(
 params: undefined |  ListAssignableUsersParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAssignableUsers>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAssignableUsers>>,
          TError,
          Awaited<ReturnType<typeof listAssignableUsers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAssignableUsers<TData = Awaited<ReturnType<typeof listAssignableUsers>>, TError = Error | HTTPValidationError>(
 params?: ListAssignableUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAssignableUsers>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAssignableUsers>>,
          TError,
          Awaited<ReturnType<typeof listAssignableUsers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAssignableUsers<TData = Awaited<ReturnType<typeof listAssignableUsers>>, TError = Error | HTTPValidationError>(
 params?: ListAssignableUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAssignableUsers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Assignable Users
 */

export function useListAssignableUsers<TData = Awaited<ReturnType<typeof listAssignableUsers>>, TError = Error | HTTPValidationError>(
 params?: ListAssignableUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAssignableUsers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListAssignableUsersQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listMyForgeAccountsResponse200 = {
  data: ForgeAccountList
  status: 200
}

export type listMyForgeAccountsResponse401 = {
  data: Error
  status: 401
}

export type listMyForgeAccountsResponse403 = {
  data: Error
  status: 403
}

export type listMyForgeAccountsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listMyForgeAccountsResponseSuccess = (listMyForgeAccountsResponse200) & {
  headers: Headers;
};
export type listMyForgeAccountsResponseError = (listMyForgeAccountsResponse401 | listMyForgeAccountsResponse403 | listMyForgeAccountsResponse422) & {
  headers: Headers;
};

export type listMyForgeAccountsResponse = (listMyForgeAccountsResponseSuccess | listMyForgeAccountsResponseError)

export const getListMyForgeAccountsUrl = () => {




  return `/api/users/me/forge-accounts`
}

/**
 * The signed-in person's linked forge accounts (spec 139 US1).
 * @summary List My Forge Accounts
 */
export const listMyForgeAccounts = async ( options?: RequestInit): Promise<listMyForgeAccountsResponse> => {

  return customFetch<listMyForgeAccountsResponse>(getListMyForgeAccountsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyForgeAccountsQueryKey = () => {
    return [
    `/api/users/me/forge-accounts`
    ] as const;
    }


export const getListMyForgeAccountsQueryOptions = <TData = Awaited<ReturnType<typeof listMyForgeAccounts>>, TError = Error | HTTPValidationError>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyForgeAccounts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyForgeAccountsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyForgeAccounts>>> = ({ signal }) => listMyForgeAccounts({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyForgeAccounts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyForgeAccountsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyForgeAccounts>>>
export type ListMyForgeAccountsQueryError = Error | HTTPValidationError


export function useListMyForgeAccounts<TData = Awaited<ReturnType<typeof listMyForgeAccounts>>, TError = Error | HTTPValidationError>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyForgeAccounts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyForgeAccounts>>,
          TError,
          Awaited<ReturnType<typeof listMyForgeAccounts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyForgeAccounts<TData = Awaited<ReturnType<typeof listMyForgeAccounts>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyForgeAccounts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyForgeAccounts>>,
          TError,
          Awaited<ReturnType<typeof listMyForgeAccounts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyForgeAccounts<TData = Awaited<ReturnType<typeof listMyForgeAccounts>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyForgeAccounts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List My Forge Accounts
 */

export function useListMyForgeAccounts<TData = Awaited<ReturnType<typeof listMyForgeAccounts>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyForgeAccounts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyForgeAccountsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listForgeHostsResponse200 = {
  data: ForgeHostList
  status: 200
}

export type listForgeHostsResponse401 = {
  data: Error
  status: 401
}

export type listForgeHostsResponse403 = {
  data: Error
  status: 403
}

export type listForgeHostsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listForgeHostsResponseSuccess = (listForgeHostsResponse200) & {
  headers: Headers;
};
export type listForgeHostsResponseError = (listForgeHostsResponse401 | listForgeHostsResponse403 | listForgeHostsResponse422) & {
  headers: Headers;
};

export type listForgeHostsResponse = (listForgeHostsResponseSuccess | listForgeHostsResponseError)

export const getListForgeHostsUrl = () => {




  return `/api/users/me/forge-hosts`
}

/**
 * The forge hosts the organization's workspaces connect to, and which are linked.
 * @summary List Forge Hosts
 */
export const listForgeHosts = async ( options?: RequestInit): Promise<listForgeHostsResponse> => {

  return customFetch<listForgeHostsResponse>(getListForgeHostsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListForgeHostsQueryKey = () => {
    return [
    `/api/users/me/forge-hosts`
    ] as const;
    }


export const getListForgeHostsQueryOptions = <TData = Awaited<ReturnType<typeof listForgeHosts>>, TError = Error | HTTPValidationError>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgeHosts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListForgeHostsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listForgeHosts>>> = ({ signal }) => listForgeHosts({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listForgeHosts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListForgeHostsQueryResult = NonNullable<Awaited<ReturnType<typeof listForgeHosts>>>
export type ListForgeHostsQueryError = Error | HTTPValidationError


export function useListForgeHosts<TData = Awaited<ReturnType<typeof listForgeHosts>>, TError = Error | HTTPValidationError>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgeHosts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listForgeHosts>>,
          TError,
          Awaited<ReturnType<typeof listForgeHosts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListForgeHosts<TData = Awaited<ReturnType<typeof listForgeHosts>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgeHosts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listForgeHosts>>,
          TError,
          Awaited<ReturnType<typeof listForgeHosts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListForgeHosts<TData = Awaited<ReturnType<typeof listForgeHosts>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgeHosts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Forge Hosts
 */

export function useListForgeHosts<TData = Awaited<ReturnType<typeof listForgeHosts>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgeHosts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListForgeHostsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type linkForgeAccountResponse200 = {
  data: LinkResult
  status: 200
}

export type linkForgeAccountResponse401 = {
  data: Error
  status: 401
}

export type linkForgeAccountResponse403 = {
  data: Error
  status: 403
}

export type linkForgeAccountResponse422 = {
  data: void
  status: 422
}

export type linkForgeAccountResponse502 = {
  data: Error
  status: 502
}

export type linkForgeAccountResponseSuccess = (linkForgeAccountResponse200) & {
  headers: Headers;
};
export type linkForgeAccountResponseError = (linkForgeAccountResponse401 | linkForgeAccountResponse403 | linkForgeAccountResponse422 | linkForgeAccountResponse502) & {
  headers: Headers;
};

export type linkForgeAccountResponse = (linkForgeAccountResponseSuccess | linkForgeAccountResponseError)

export const getLinkForgeAccountUrl = (host: string,) => {




  return `/api/users/me/forge-accounts/${host}`
}

/**
 * Link a personal token for a host, or replace the link (spec 139 FR-001–003).
 *
 * The forge is asked first; nothing is stored unless it accepts the token with what
 * pushing and opening merge requests need.
 * @summary Link Forge Account
 */
export const linkForgeAccount = async (host: string,
    linkTokenRequest: LinkTokenRequest, options?: RequestInit): Promise<linkForgeAccountResponse> => {

  return customFetch<linkForgeAccountResponse>(getLinkForgeAccountUrl(host),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(linkTokenRequest)
  }
);}




export const getLinkForgeAccountMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof linkForgeAccount>>, TError,{host: string;data: LinkTokenRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof linkForgeAccount>>, TError,{host: string;data: LinkTokenRequest}, TContext> => {

const mutationKey = ['linkForgeAccount'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof linkForgeAccount>>, {host: string;data: LinkTokenRequest}> = (props) => {
          const {host,data} = props ?? {};

          return  linkForgeAccount(host,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LinkForgeAccountMutationResult = NonNullable<Awaited<ReturnType<typeof linkForgeAccount>>>
    export type LinkForgeAccountMutationBody = LinkTokenRequest
    export type LinkForgeAccountMutationError = Error | void

    /**
 * @summary Link Forge Account
 */
export const useLinkForgeAccount = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof linkForgeAccount>>, TError,{host: string;data: LinkTokenRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof linkForgeAccount>>,
        TError,
        {host: string;data: LinkTokenRequest},
        TContext
      > => {
      return useMutation(getLinkForgeAccountMutationOptions(options), queryClient);
    }

export type unlinkForgeAccountResponse204 = {
  data: void
  status: 204
}

export type unlinkForgeAccountResponse401 = {
  data: Error
  status: 401
}

export type unlinkForgeAccountResponse403 = {
  data: Error
  status: 403
}

export type unlinkForgeAccountResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type unlinkForgeAccountResponseSuccess = (unlinkForgeAccountResponse204) & {
  headers: Headers;
};
export type unlinkForgeAccountResponseError = (unlinkForgeAccountResponse401 | unlinkForgeAccountResponse403 | unlinkForgeAccountResponse422) & {
  headers: Headers;
};

export type unlinkForgeAccountResponse = (unlinkForgeAccountResponseSuccess | unlinkForgeAccountResponseError)

export const getUnlinkForgeAccountUrl = (host: string,) => {




  return `/api/users/me/forge-accounts/${host}`
}

/**
 * Remove the person's link for a host; runs use the shared credential again.
 * @summary Unlink Forge Account
 */
export const unlinkForgeAccount = async (host: string, options?: RequestInit): Promise<unlinkForgeAccountResponse> => {

  return customFetch<unlinkForgeAccountResponse>(getUnlinkForgeAccountUrl(host),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getUnlinkForgeAccountMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlinkForgeAccount>>, TError,{host: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unlinkForgeAccount>>, TError,{host: string}, TContext> => {

const mutationKey = ['unlinkForgeAccount'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unlinkForgeAccount>>, {host: string}> = (props) => {
          const {host} = props ?? {};

          return  unlinkForgeAccount(host,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnlinkForgeAccountMutationResult = NonNullable<Awaited<ReturnType<typeof unlinkForgeAccount>>>

    export type UnlinkForgeAccountMutationError = Error | HTTPValidationError

    /**
 * @summary Unlink Forge Account
 */
export const useUnlinkForgeAccount = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlinkForgeAccount>>, TError,{host: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof unlinkForgeAccount>>,
        TError,
        {host: string},
        TContext
      > => {
      return useMutation(getUnlinkForgeAccountMutationOptions(options), queryClient);
    }

export type revokeForgeAccountResponse204 = {
  data: void
  status: 204
}

export type revokeForgeAccountResponse401 = {
  data: Error
  status: 401
}

export type revokeForgeAccountResponse403 = {
  data: Error
  status: 403
}

export type revokeForgeAccountResponse404 = {
  data: Error
  status: 404
}

export type revokeForgeAccountResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type revokeForgeAccountResponseSuccess = (revokeForgeAccountResponse204) & {
  headers: Headers;
};
export type revokeForgeAccountResponseError = (revokeForgeAccountResponse401 | revokeForgeAccountResponse403 | revokeForgeAccountResponse404 | revokeForgeAccountResponse422) & {
  headers: Headers;
};

export type revokeForgeAccountResponse = (revokeForgeAccountResponseSuccess | revokeForgeAccountResponseError)

export const getRevokeForgeAccountUrl = (userId: string,
    host: string,) => {




  return `/api/organization/users/${userId}/forge-accounts/${host}`
}

/**
 * An organization admin revokes a person's link; the person is told (FR-006), and
 * who revoked it is recorded (spec 140 FR-016). Revoking it again changes nothing.
 * @summary Revoke Forge Account
 */
export const revokeForgeAccount = async (userId: string,
    host: string, options?: RequestInit): Promise<revokeForgeAccountResponse> => {

  return customFetch<revokeForgeAccountResponse>(getRevokeForgeAccountUrl(userId,host),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getRevokeForgeAccountMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeForgeAccount>>, TError,{userId: string;host: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeForgeAccount>>, TError,{userId: string;host: string}, TContext> => {

const mutationKey = ['revokeForgeAccount'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeForgeAccount>>, {userId: string;host: string}> = (props) => {
          const {userId,host} = props ?? {};

          return  revokeForgeAccount(userId,host,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeForgeAccountMutationResult = NonNullable<Awaited<ReturnType<typeof revokeForgeAccount>>>

    export type RevokeForgeAccountMutationError = Error | HTTPValidationError

    /**
 * @summary Revoke Forge Account
 */
export const useRevokeForgeAccount = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeForgeAccount>>, TError,{userId: string;host: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof revokeForgeAccount>>,
        TError,
        {userId: string;host: string},
        TContext
      > => {
      return useMutation(getRevokeForgeAccountMutationOptions(options), queryClient);
    }

export type remindForgeAccountResponse200 = {
  data: ForgeReminderView
  status: 200
}

export type remindForgeAccountResponse401 = {
  data: Error
  status: 401
}

export type remindForgeAccountResponse403 = {
  data: Error
  status: 403
}

export type remindForgeAccountResponse404 = {
  data: Error
  status: 404
}

export type remindForgeAccountResponse409 = {
  data: Error
  status: 409
}

export type remindForgeAccountResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type remindForgeAccountResponseSuccess = (remindForgeAccountResponse200) & {
  headers: Headers;
};
export type remindForgeAccountResponseError = (remindForgeAccountResponse401 | remindForgeAccountResponse403 | remindForgeAccountResponse404 | remindForgeAccountResponse409 | remindForgeAccountResponse422) & {
  headers: Headers;
};

export type remindForgeAccountResponse = (remindForgeAccountResponseSuccess | remindForgeAccountResponseError)

export const getRemindForgeAccountUrl = (userId: string,
    host: string,) => {




  return `/api/organization/users/${userId}/forge-accounts/${host}/remind`
}

/**
 * An organization admin asks a person to link or relink a host (spec 143), at
 * most once a day per person and host.
 * @summary Remind Forge Account
 */
export const remindForgeAccount = async (userId: string,
    host: string, options?: RequestInit): Promise<remindForgeAccountResponse> => {

  return customFetch<remindForgeAccountResponse>(getRemindForgeAccountUrl(userId,host),
  {
    ...options,
    method: 'POST'


  }
);}




export const getRemindForgeAccountMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof remindForgeAccount>>, TError,{userId: string;host: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof remindForgeAccount>>, TError,{userId: string;host: string}, TContext> => {

const mutationKey = ['remindForgeAccount'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof remindForgeAccount>>, {userId: string;host: string}> = (props) => {
          const {userId,host} = props ?? {};

          return  remindForgeAccount(userId,host,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemindForgeAccountMutationResult = NonNullable<Awaited<ReturnType<typeof remindForgeAccount>>>

    export type RemindForgeAccountMutationError = Error | HTTPValidationError

    /**
 * @summary Remind Forge Account
 */
export const useRemindForgeAccount = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof remindForgeAccount>>, TError,{userId: string;host: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof remindForgeAccount>>,
        TError,
        {userId: string;host: string},
        TContext
      > => {
      return useMutation(getRemindForgeAccountMutationOptions(options), queryClient);
    }

export type listPeopleForgeAccountsResponse200 = {
  data: PeopleLinksPage
  status: 200
}

export type listPeopleForgeAccountsResponse401 = {
  data: Error
  status: 401
}

export type listPeopleForgeAccountsResponse403 = {
  data: Error
  status: 403
}

export type listPeopleForgeAccountsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listPeopleForgeAccountsResponseSuccess = (listPeopleForgeAccountsResponse200) & {
  headers: Headers;
};
export type listPeopleForgeAccountsResponseError = (listPeopleForgeAccountsResponse401 | listPeopleForgeAccountsResponse403 | listPeopleForgeAccountsResponse422) & {
  headers: Headers;
};

export type listPeopleForgeAccountsResponse = (listPeopleForgeAccountsResponseSuccess | listPeopleForgeAccountsResponseError)

export const getListPeopleForgeAccountsUrl = (params?: ListPeopleForgeAccountsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/organization/forge-accounts?${stringifiedParams}` : `/api/organization/forge-accounts`
}

/**
 * Every active person and their linked accounts, unlinked first (spec 140 US3).
 * @summary List People Forge Accounts
 */
export const listPeopleForgeAccounts = async (params?: ListPeopleForgeAccountsParams, options?: RequestInit): Promise<listPeopleForgeAccountsResponse> => {

  return customFetch<listPeopleForgeAccountsResponse>(getListPeopleForgeAccountsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPeopleForgeAccountsQueryKey = (params?: ListPeopleForgeAccountsParams,) => {
    return [
    `/api/organization/forge-accounts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPeopleForgeAccountsQueryOptions = <TData = Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError = Error | HTTPValidationError>(params?: ListPeopleForgeAccountsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPeopleForgeAccountsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPeopleForgeAccounts>>> = ({ signal }) => listPeopleForgeAccounts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListPeopleForgeAccountsQueryResult = NonNullable<Awaited<ReturnType<typeof listPeopleForgeAccounts>>>
export type ListPeopleForgeAccountsQueryError = Error | HTTPValidationError


export function useListPeopleForgeAccounts<TData = Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError = Error | HTTPValidationError>(
 params: undefined |  ListPeopleForgeAccountsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPeopleForgeAccounts>>,
          TError,
          Awaited<ReturnType<typeof listPeopleForgeAccounts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPeopleForgeAccounts<TData = Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError = Error | HTTPValidationError>(
 params?: ListPeopleForgeAccountsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPeopleForgeAccounts>>,
          TError,
          Awaited<ReturnType<typeof listPeopleForgeAccounts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPeopleForgeAccounts<TData = Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError = Error | HTTPValidationError>(
 params?: ListPeopleForgeAccountsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List People Forge Accounts
 */

export function useListPeopleForgeAccounts<TData = Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError = Error | HTTPValidationError>(
 params?: ListPeopleForgeAccountsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPeopleForgeAccounts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListPeopleForgeAccountsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listForgePoliciesResponse200 = {
  data: ForgePolicyList
  status: 200
}

export type listForgePoliciesResponse401 = {
  data: Error
  status: 401
}

export type listForgePoliciesResponse403 = {
  data: Error
  status: 403
}

export type listForgePoliciesResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listForgePoliciesResponseSuccess = (listForgePoliciesResponse200) & {
  headers: Headers;
};
export type listForgePoliciesResponseError = (listForgePoliciesResponse401 | listForgePoliciesResponse403 | listForgePoliciesResponse422) & {
  headers: Headers;
};

export type listForgePoliciesResponse = (listForgePoliciesResponseSuccess | listForgePoliciesResponseError)

export const getListForgePoliciesUrl = () => {




  return `/api/organization/forge-policy`
}

/**
 * The workspaces that require each person's own forge account (spec 140).
 * @summary List Forge Policies
 */
export const listForgePolicies = async ( options?: RequestInit): Promise<listForgePoliciesResponse> => {

  return customFetch<listForgePoliciesResponse>(getListForgePoliciesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListForgePoliciesQueryKey = () => {
    return [
    `/api/organization/forge-policy`
    ] as const;
    }


export const getListForgePoliciesQueryOptions = <TData = Awaited<ReturnType<typeof listForgePolicies>>, TError = Error | HTTPValidationError>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgePolicies>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListForgePoliciesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listForgePolicies>>> = ({ signal }) => listForgePolicies({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listForgePolicies>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListForgePoliciesQueryResult = NonNullable<Awaited<ReturnType<typeof listForgePolicies>>>
export type ListForgePoliciesQueryError = Error | HTTPValidationError


export function useListForgePolicies<TData = Awaited<ReturnType<typeof listForgePolicies>>, TError = Error | HTTPValidationError>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgePolicies>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listForgePolicies>>,
          TError,
          Awaited<ReturnType<typeof listForgePolicies>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListForgePolicies<TData = Awaited<ReturnType<typeof listForgePolicies>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgePolicies>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listForgePolicies>>,
          TError,
          Awaited<ReturnType<typeof listForgePolicies>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListForgePolicies<TData = Awaited<ReturnType<typeof listForgePolicies>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgePolicies>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Forge Policies
 */

export function useListForgePolicies<TData = Awaited<ReturnType<typeof listForgePolicies>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgePolicies>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListForgePoliciesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type setForgePolicyResponse200 = {
  data: ForgePolicyView
  status: 200
}

export type setForgePolicyResponse401 = {
  data: Error
  status: 401
}

export type setForgePolicyResponse403 = {
  data: Error
  status: 403
}

export type setForgePolicyResponse404 = {
  data: Error
  status: 404
}

export type setForgePolicyResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type setForgePolicyResponseSuccess = (setForgePolicyResponse200) & {
  headers: Headers;
};
export type setForgePolicyResponseError = (setForgePolicyResponse401 | setForgePolicyResponse403 | setForgePolicyResponse404 | setForgePolicyResponse422) & {
  headers: Headers;
};

export type setForgePolicyResponse = (setForgePolicyResponseSuccess | setForgePolicyResponseError)

export const getSetForgePolicyUrl = (workspaceId: string,) => {




  return `/api/organization/forge-policy/${workspaceId}`
}

/**
 * Require personal accounts in a workspace, or stop requiring them (spec 140 US1).
 * @summary Set Forge Policy
 */
export const setForgePolicy = async (workspaceId: string,
    setForgePolicyRequest: SetForgePolicyRequest, options?: RequestInit): Promise<setForgePolicyResponse> => {

  return customFetch<setForgePolicyResponse>(getSetForgePolicyUrl(workspaceId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setForgePolicyRequest)
  }
);}




export const getSetForgePolicyMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setForgePolicy>>, TError,{workspaceId: string;data: SetForgePolicyRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setForgePolicy>>, TError,{workspaceId: string;data: SetForgePolicyRequest}, TContext> => {

const mutationKey = ['setForgePolicy'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setForgePolicy>>, {workspaceId: string;data: SetForgePolicyRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  setForgePolicy(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetForgePolicyMutationResult = NonNullable<Awaited<ReturnType<typeof setForgePolicy>>>
    export type SetForgePolicyMutationBody = SetForgePolicyRequest
    export type SetForgePolicyMutationError = Error | HTTPValidationError

    /**
 * @summary Set Forge Policy
 */
export const useSetForgePolicy = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setForgePolicy>>, TError,{workspaceId: string;data: SetForgePolicyRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setForgePolicy>>,
        TError,
        {workspaceId: string;data: SetForgePolicyRequest},
        TContext
      > => {
      return useMutation(getSetForgePolicyMutationOptions(options), queryClient);
    }

export type readPersonalAccountStandingResponse200 = {
  data: PersonalAccountStanding
  status: 200
}

export type readPersonalAccountStandingResponse401 = {
  data: Error
  status: 401
}

export type readPersonalAccountStandingResponse403 = {
  data: Error
  status: 403
}

export type readPersonalAccountStandingResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type readPersonalAccountStandingResponseSuccess = (readPersonalAccountStandingResponse200) & {
  headers: Headers;
};
export type readPersonalAccountStandingResponseError = (readPersonalAccountStandingResponse401 | readPersonalAccountStandingResponse403 | readPersonalAccountStandingResponse422) & {
  headers: Headers;
};

export type readPersonalAccountStandingResponse = (readPersonalAccountStandingResponseSuccess | readPersonalAccountStandingResponseError)

export const getReadPersonalAccountStandingUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/personal-accounts`
}

/**
 * Whether this workspace requires the caller's own account, and which of its hosts
 * they still need to link (spec 140 US5).
 * @summary Read Personal Account Standing
 */
export const readPersonalAccountStanding = async (workspaceId: string, options?: RequestInit): Promise<readPersonalAccountStandingResponse> => {

  return customFetch<readPersonalAccountStandingResponse>(getReadPersonalAccountStandingUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadPersonalAccountStandingQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/personal-accounts`
    ] as const;
    }


export const getReadPersonalAccountStandingQueryOptions = <TData = Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError = Error | HTTPValidationError>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadPersonalAccountStandingQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readPersonalAccountStanding>>> = ({ signal }) => readPersonalAccountStanding(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadPersonalAccountStandingQueryResult = NonNullable<Awaited<ReturnType<typeof readPersonalAccountStanding>>>
export type ReadPersonalAccountStandingQueryError = Error | HTTPValidationError


export function useReadPersonalAccountStanding<TData = Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readPersonalAccountStanding>>,
          TError,
          Awaited<ReturnType<typeof readPersonalAccountStanding>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadPersonalAccountStanding<TData = Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readPersonalAccountStanding>>,
          TError,
          Awaited<ReturnType<typeof readPersonalAccountStanding>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadPersonalAccountStanding<TData = Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Personal Account Standing
 */

export function useReadPersonalAccountStanding<TData = Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readPersonalAccountStanding>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadPersonalAccountStandingQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listForgeAppsResponse200 = {
  data: ForgeAppList
  status: 200
}

export type listForgeAppsResponse401 = {
  data: Error
  status: 401
}

export type listForgeAppsResponse403 = {
  data: Error
  status: 403
}

export type listForgeAppsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listForgeAppsResponseSuccess = (listForgeAppsResponse200) & {
  headers: Headers;
};
export type listForgeAppsResponseError = (listForgeAppsResponse401 | listForgeAppsResponse403 | listForgeAppsResponse422) & {
  headers: Headers;
};

export type listForgeAppsResponse = (listForgeAppsResponseSuccess | listForgeAppsResponseError)

export const getListForgeAppsUrl = () => {




  return `/api/organization/forge-apps`
}

/**
 * Each forge host's sign-in app, and the address to register on the forge.
 * @summary List Forge Apps
 */
export const listForgeApps = async ( options?: RequestInit): Promise<listForgeAppsResponse> => {

  return customFetch<listForgeAppsResponse>(getListForgeAppsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListForgeAppsQueryKey = () => {
    return [
    `/api/organization/forge-apps`
    ] as const;
    }


export const getListForgeAppsQueryOptions = <TData = Awaited<ReturnType<typeof listForgeApps>>, TError = Error | HTTPValidationError>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgeApps>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListForgeAppsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listForgeApps>>> = ({ signal }) => listForgeApps({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listForgeApps>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListForgeAppsQueryResult = NonNullable<Awaited<ReturnType<typeof listForgeApps>>>
export type ListForgeAppsQueryError = Error | HTTPValidationError


export function useListForgeApps<TData = Awaited<ReturnType<typeof listForgeApps>>, TError = Error | HTTPValidationError>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgeApps>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listForgeApps>>,
          TError,
          Awaited<ReturnType<typeof listForgeApps>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListForgeApps<TData = Awaited<ReturnType<typeof listForgeApps>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgeApps>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listForgeApps>>,
          TError,
          Awaited<ReturnType<typeof listForgeApps>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListForgeApps<TData = Awaited<ReturnType<typeof listForgeApps>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgeApps>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Forge Apps
 */

export function useListForgeApps<TData = Awaited<ReturnType<typeof listForgeApps>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listForgeApps>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListForgeAppsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type setForgeAppResponse200 = {
  data: ForgeAppView
  status: 200
}

export type setForgeAppResponse401 = {
  data: Error
  status: 401
}

export type setForgeAppResponse403 = {
  data: Error
  status: 403
}

export type setForgeAppResponse404 = {
  data: Error
  status: 404
}

export type setForgeAppResponse422 = {
  data: void
  status: 422
}

export type setForgeAppResponseSuccess = (setForgeAppResponse200) & {
  headers: Headers;
};
export type setForgeAppResponseError = (setForgeAppResponse401 | setForgeAppResponse403 | setForgeAppResponse404 | setForgeAppResponse422) & {
  headers: Headers;
};

export type setForgeAppResponse = (setForgeAppResponseSuccess | setForgeAppResponseError)

export const getSetForgeAppUrl = (host: string,) => {




  return `/api/organization/forge-apps/${host}`
}

/**
 * Set a host's sign-in app. Without a secret, an existing app keeps its own.
 * @summary Set Forge App
 */
export const setForgeApp = async (host: string,
    setForgeAppRequest: SetForgeAppRequest, options?: RequestInit): Promise<setForgeAppResponse> => {

  return customFetch<setForgeAppResponse>(getSetForgeAppUrl(host),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setForgeAppRequest)
  }
);}




export const getSetForgeAppMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setForgeApp>>, TError,{host: string;data: SetForgeAppRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setForgeApp>>, TError,{host: string;data: SetForgeAppRequest}, TContext> => {

const mutationKey = ['setForgeApp'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setForgeApp>>, {host: string;data: SetForgeAppRequest}> = (props) => {
          const {host,data} = props ?? {};

          return  setForgeApp(host,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetForgeAppMutationResult = NonNullable<Awaited<ReturnType<typeof setForgeApp>>>
    export type SetForgeAppMutationBody = SetForgeAppRequest
    export type SetForgeAppMutationError = Error | void

    /**
 * @summary Set Forge App
 */
export const useSetForgeApp = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setForgeApp>>, TError,{host: string;data: SetForgeAppRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setForgeApp>>,
        TError,
        {host: string;data: SetForgeAppRequest},
        TContext
      > => {
      return useMutation(getSetForgeAppMutationOptions(options), queryClient);
    }

export type removeForgeAppResponse204 = {
  data: void
  status: 204
}

export type removeForgeAppResponse401 = {
  data: Error
  status: 401
}

export type removeForgeAppResponse403 = {
  data: Error
  status: 403
}

export type removeForgeAppResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type removeForgeAppResponseSuccess = (removeForgeAppResponse204) & {
  headers: Headers;
};
export type removeForgeAppResponseError = (removeForgeAppResponse401 | removeForgeAppResponse403 | removeForgeAppResponse422) & {
  headers: Headers;
};

export type removeForgeAppResponse = (removeForgeAppResponseSuccess | removeForgeAppResponseError)

export const getRemoveForgeAppUrl = (host: string,) => {




  return `/api/organization/forge-apps/${host}`
}

/**
 * Remove a host's sign-in app; links it made break at their next refresh.
 * @summary Remove Forge App
 */
export const removeForgeApp = async (host: string, options?: RequestInit): Promise<removeForgeAppResponse> => {

  return customFetch<removeForgeAppResponse>(getRemoveForgeAppUrl(host),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getRemoveForgeAppMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeForgeApp>>, TError,{host: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeForgeApp>>, TError,{host: string}, TContext> => {

const mutationKey = ['removeForgeApp'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeForgeApp>>, {host: string}> = (props) => {
          const {host} = props ?? {};

          return  removeForgeApp(host,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveForgeAppMutationResult = NonNullable<Awaited<ReturnType<typeof removeForgeApp>>>

    export type RemoveForgeAppMutationError = Error | HTTPValidationError

    /**
 * @summary Remove Forge App
 */
export const useRemoveForgeApp = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeForgeApp>>, TError,{host: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof removeForgeApp>>,
        TError,
        {host: string},
        TContext
      > => {
      return useMutation(getRemoveForgeAppMutationOptions(options), queryClient);
    }

export type startForgeOauthResponse200 = {
  data: OAuthStartView
  status: 200
}

export type startForgeOauthResponse401 = {
  data: Error
  status: 401
}

export type startForgeOauthResponse403 = {
  data: Error
  status: 403
}

export type startForgeOauthResponse404 = {
  data: Error
  status: 404
}

export type startForgeOauthResponse409 = {
  data: Error
  status: 409
}

export type startForgeOauthResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type startForgeOauthResponseSuccess = (startForgeOauthResponse200) & {
  headers: Headers;
};
export type startForgeOauthResponseError = (startForgeOauthResponse401 | startForgeOauthResponse403 | startForgeOauthResponse404 | startForgeOauthResponse409 | startForgeOauthResponse422) & {
  headers: Headers;
};

export type startForgeOauthResponse = (startForgeOauthResponseSuccess | startForgeOauthResponseError)

export const getStartForgeOauthUrl = (host: string,) => {




  return `/api/users/me/forge-accounts/${host}/oauth`
}

/**
 * Begin linking by signing in: where to send the browser to approve.
 * @summary Start Forge Oauth
 */
export const startForgeOauth = async (host: string, options?: RequestInit): Promise<startForgeOauthResponse> => {

  return customFetch<startForgeOauthResponse>(getStartForgeOauthUrl(host),
  {
    ...options,
    method: 'POST'


  }
);}




export const getStartForgeOauthMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startForgeOauth>>, TError,{host: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startForgeOauth>>, TError,{host: string}, TContext> => {

const mutationKey = ['startForgeOauth'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startForgeOauth>>, {host: string}> = (props) => {
          const {host} = props ?? {};

          return  startForgeOauth(host,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartForgeOauthMutationResult = NonNullable<Awaited<ReturnType<typeof startForgeOauth>>>

    export type StartForgeOauthMutationError = Error | HTTPValidationError

    /**
 * @summary Start Forge Oauth
 */
export const useStartForgeOauth = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startForgeOauth>>, TError,{host: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof startForgeOauth>>,
        TError,
        {host: string},
        TContext
      > => {
      return useMutation(getStartForgeOauthMutationOptions(options), queryClient);
    }

export type finishForgeOauthResponse303 = {
  data: void
  status: 303
}

export type finishForgeOauthResponse404 = {
  data: void
  status: 404
}

export type finishForgeOauthResponse422 = {
  data: HTTPValidationError
  status: 422
}

;
export type finishForgeOauthResponseError = (finishForgeOauthResponse303 | finishForgeOauthResponse404 | finishForgeOauthResponse422) & {
  headers: Headers;
};

export type finishForgeOauthResponse = (finishForgeOauthResponseError)

export const getFinishForgeOauthUrl = (params?: FinishForgeOauthParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/forge-oauth/callback?${stringifiedParams}` : `/api/forge-oauth/callback`
}

/**
 * Where the forge sends the person back: link them, then show them how it went.
 * @summary Finish Forge Oauth
 */
export const finishForgeOauth = async (params?: FinishForgeOauthParams, options?: RequestInit): Promise<finishForgeOauthResponse> => {

  return customFetch<finishForgeOauthResponse>(getFinishForgeOauthUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getFinishForgeOauthQueryKey = (params?: FinishForgeOauthParams,) => {
    return [
    `/api/forge-oauth/callback`, ...(params ? [params] : [])
    ] as const;
    }


export const getFinishForgeOauthQueryOptions = <TData = Awaited<ReturnType<typeof finishForgeOauth>>, TError = void | HTTPValidationError>(params?: FinishForgeOauthParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof finishForgeOauth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getFinishForgeOauthQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof finishForgeOauth>>> = ({ signal }) => finishForgeOauth(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof finishForgeOauth>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type FinishForgeOauthQueryResult = NonNullable<Awaited<ReturnType<typeof finishForgeOauth>>>
export type FinishForgeOauthQueryError = void | HTTPValidationError


export function useFinishForgeOauth<TData = Awaited<ReturnType<typeof finishForgeOauth>>, TError = void | HTTPValidationError>(
 params: undefined |  FinishForgeOauthParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof finishForgeOauth>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof finishForgeOauth>>,
          TError,
          Awaited<ReturnType<typeof finishForgeOauth>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useFinishForgeOauth<TData = Awaited<ReturnType<typeof finishForgeOauth>>, TError = void | HTTPValidationError>(
 params?: FinishForgeOauthParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof finishForgeOauth>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof finishForgeOauth>>,
          TError,
          Awaited<ReturnType<typeof finishForgeOauth>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useFinishForgeOauth<TData = Awaited<ReturnType<typeof finishForgeOauth>>, TError = void | HTTPValidationError>(
 params?: FinishForgeOauthParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof finishForgeOauth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Finish Forge Oauth
 */

export function useFinishForgeOauth<TData = Awaited<ReturnType<typeof finishForgeOauth>>, TError = void | HTTPValidationError>(
 params?: FinishForgeOauthParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof finishForgeOauth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getFinishForgeOauthQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listMyAiToolsResponse200 = {
  data: AiToolList
  status: 200
}

export type listMyAiToolsResponse401 = {
  data: Error
  status: 401
}

export type listMyAiToolsResponseSuccess = (listMyAiToolsResponse200) & {
  headers: Headers;
};
export type listMyAiToolsResponseError = (listMyAiToolsResponse401) & {
  headers: Headers;
};

export type listMyAiToolsResponse = (listMyAiToolsResponseSuccess | listMyAiToolsResponseError)

export const getListMyAiToolsUrl = () => {




  return `/api/users/me/ai-tools`
}

/**
 * The AI tools the signed-in person has used, latest first (spec 203).
 * @summary List My Ai Tools
 */
export const listMyAiTools = async ( options?: RequestInit): Promise<listMyAiToolsResponse> => {

  return customFetch<listMyAiToolsResponse>(getListMyAiToolsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyAiToolsQueryKey = () => {
    return [
    `/api/users/me/ai-tools`
    ] as const;
    }


export const getListMyAiToolsQueryOptions = <TData = Awaited<ReturnType<typeof listMyAiTools>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyAiTools>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyAiToolsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyAiTools>>> = ({ signal }) => listMyAiTools({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyAiTools>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyAiToolsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyAiTools>>>
export type ListMyAiToolsQueryError = Error


export function useListMyAiTools<TData = Awaited<ReturnType<typeof listMyAiTools>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyAiTools>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyAiTools>>,
          TError,
          Awaited<ReturnType<typeof listMyAiTools>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyAiTools<TData = Awaited<ReturnType<typeof listMyAiTools>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyAiTools>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyAiTools>>,
          TError,
          Awaited<ReturnType<typeof listMyAiTools>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyAiTools<TData = Awaited<ReturnType<typeof listMyAiTools>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyAiTools>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List My Ai Tools
 */

export function useListMyAiTools<TData = Awaited<ReturnType<typeof listMyAiTools>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyAiTools>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyAiToolsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type disconnectMyAiToolsResponse204 = {
  data: void
  status: 204
}

export type disconnectMyAiToolsResponse401 = {
  data: Error
  status: 401
}

export type disconnectMyAiToolsResponse502 = {
  data: Error
  status: 502
}

export type disconnectMyAiToolsResponseSuccess = (disconnectMyAiToolsResponse204) & {
  headers: Headers;
};
export type disconnectMyAiToolsResponseError = (disconnectMyAiToolsResponse401 | disconnectMyAiToolsResponse502) & {
  headers: Headers;
};

export type disconnectMyAiToolsResponse = (disconnectMyAiToolsResponseSuccess | disconnectMyAiToolsResponseError)

export const getDisconnectMyAiToolsUrl = () => {




  return `/api/users/me/ai-tools/disconnect`
}

/**
 * End every AI tool's access as the signed-in person (spec 204).
 *
 * The tools' current tokens stop working at once, and they cannot refresh: each must
 * sign in again to connect. The person stays signed in to the app.
 * @summary Disconnect My Ai Tools
 */
export const disconnectMyAiTools = async ( options?: RequestInit): Promise<disconnectMyAiToolsResponse> => {

  return customFetch<disconnectMyAiToolsResponse>(getDisconnectMyAiToolsUrl(),
  {
    ...options,
    method: 'POST'


  }
);}




export const getDisconnectMyAiToolsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectMyAiTools>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disconnectMyAiTools>>, TError,void, TContext> => {

const mutationKey = ['disconnectMyAiTools'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disconnectMyAiTools>>, void> = () => {


          return  disconnectMyAiTools(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisconnectMyAiToolsMutationResult = NonNullable<Awaited<ReturnType<typeof disconnectMyAiTools>>>

    export type DisconnectMyAiToolsMutationError = Error

    /**
 * @summary Disconnect My Ai Tools
 */
export const useDisconnectMyAiTools = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectMyAiTools>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof disconnectMyAiTools>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getDisconnectMyAiToolsMutationOptions(options), queryClient);
    }

export type createWorkspaceResponse201 = {
  data: WorkspaceAggregate
  status: 201
}

export type createWorkspaceResponse401 = {
  data: Error
  status: 401
}

export type createWorkspaceResponse403 = {
  data: Error
  status: 403
}

export type createWorkspaceResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type createWorkspaceResponseSuccess = (createWorkspaceResponse201) & {
  headers: Headers;
};
export type createWorkspaceResponseError = (createWorkspaceResponse401 | createWorkspaceResponse403 | createWorkspaceResponse422) & {
  headers: Headers;
};

export type createWorkspaceResponse = (createWorkspaceResponseSuccess | createWorkspaceResponseError)

export const getCreateWorkspaceUrl = () => {




  return `/api/workspaces`
}

/**
 * @summary Create Workspace
 */
export const createWorkspace = async (createWorkspaceRequest: CreateWorkspaceRequest, options?: RequestInit): Promise<createWorkspaceResponse> => {

  return customFetch<createWorkspaceResponse>(getCreateWorkspaceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createWorkspaceRequest)
  }
);}




export const getCreateWorkspaceMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkspace>>, TError,{data: CreateWorkspaceRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWorkspace>>, TError,{data: CreateWorkspaceRequest}, TContext> => {

const mutationKey = ['createWorkspace'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWorkspace>>, {data: CreateWorkspaceRequest}> = (props) => {
          const {data} = props ?? {};

          return  createWorkspace(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWorkspaceMutationResult = NonNullable<Awaited<ReturnType<typeof createWorkspace>>>
    export type CreateWorkspaceMutationBody = CreateWorkspaceRequest
    export type CreateWorkspaceMutationError = Error | HTTPValidationError

    /**
 * @summary Create Workspace
 */
export const useCreateWorkspace = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkspace>>, TError,{data: CreateWorkspaceRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createWorkspace>>,
        TError,
        {data: CreateWorkspaceRequest},
        TContext
      > => {
      return useMutation(getCreateWorkspaceMutationOptions(options), queryClient);
    }

export type listWorkspacesResponse200 = {
  data: WorkspaceListPage
  status: 200
}

export type listWorkspacesResponse401 = {
  data: Error
  status: 401
}

export type listWorkspacesResponse403 = {
  data: Error
  status: 403
}

export type listWorkspacesResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listWorkspacesResponseSuccess = (listWorkspacesResponse200) & {
  headers: Headers;
};
export type listWorkspacesResponseError = (listWorkspacesResponse401 | listWorkspacesResponse403 | listWorkspacesResponse422) & {
  headers: Headers;
};

export type listWorkspacesResponse = (listWorkspacesResponseSuccess | listWorkspacesResponseError)

export const getListWorkspacesUrl = (params?: ListWorkspacesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces?${stringifiedParams}` : `/api/workspaces`
}

/**
 * @summary List Workspaces
 */
export const listWorkspaces = async (params?: ListWorkspacesParams, options?: RequestInit): Promise<listWorkspacesResponse> => {

  return customFetch<listWorkspacesResponse>(getListWorkspacesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWorkspacesQueryKey = (params?: ListWorkspacesParams,) => {
    return [
    `/api/workspaces`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWorkspacesQueryOptions = <TData = Awaited<ReturnType<typeof listWorkspaces>>, TError = Error | HTTPValidationError>(params?: ListWorkspacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWorkspacesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkspaces>>> = ({ signal }) => listWorkspaces(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListWorkspacesQueryResult = NonNullable<Awaited<ReturnType<typeof listWorkspaces>>>
export type ListWorkspacesQueryError = Error | HTTPValidationError


export function useListWorkspaces<TData = Awaited<ReturnType<typeof listWorkspaces>>, TError = Error | HTTPValidationError>(
 params: undefined |  ListWorkspacesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaces>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaces>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaces<TData = Awaited<ReturnType<typeof listWorkspaces>>, TError = Error | HTTPValidationError>(
 params?: ListWorkspacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaces>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaces>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaces<TData = Awaited<ReturnType<typeof listWorkspaces>>, TError = Error | HTTPValidationError>(
 params?: ListWorkspacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Workspaces
 */

export function useListWorkspaces<TData = Awaited<ReturnType<typeof listWorkspaces>>, TError = Error | HTTPValidationError>(
 params?: ListWorkspacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListWorkspacesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readWorkspaceResponse200 = {
  data: WorkspaceAggregate
  status: 200
}

export type readWorkspaceResponse401 = {
  data: Error
  status: 401
}

export type readWorkspaceResponse403 = {
  data: Error
  status: 403
}

export type readWorkspaceResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type readWorkspaceResponseSuccess = (readWorkspaceResponse200) & {
  headers: Headers;
};
export type readWorkspaceResponseError = (readWorkspaceResponse401 | readWorkspaceResponse403 | readWorkspaceResponse422) & {
  headers: Headers;
};

export type readWorkspaceResponse = (readWorkspaceResponseSuccess | readWorkspaceResponseError)

export const getReadWorkspaceUrl = (workspaceId: string,
    params?: ReadWorkspaceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}?${stringifiedParams}` : `/api/workspaces/${workspaceId}`
}

/**
 * @summary Read Workspace
 */
export const readWorkspace = async (workspaceId: string,
    params?: ReadWorkspaceParams, options?: RequestInit): Promise<readWorkspaceResponse> => {

  return customFetch<readWorkspaceResponse>(getReadWorkspaceUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadWorkspaceQueryKey = (workspaceId: string,
    params?: ReadWorkspaceParams,) => {
    return [
    `/api/workspaces/${workspaceId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadWorkspaceQueryOptions = <TData = Awaited<ReturnType<typeof readWorkspace>>, TError = Error | HTTPValidationError>(workspaceId: string,
    params?: ReadWorkspaceParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspace>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadWorkspaceQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readWorkspace>>> = ({ signal }) => readWorkspace(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readWorkspace>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadWorkspaceQueryResult = NonNullable<Awaited<ReturnType<typeof readWorkspace>>>
export type ReadWorkspaceQueryError = Error | HTTPValidationError


export function useReadWorkspace<TData = Awaited<ReturnType<typeof readWorkspace>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params: undefined |  ReadWorkspaceParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspace>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspace>>,
          TError,
          Awaited<ReturnType<typeof readWorkspace>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspace<TData = Awaited<ReturnType<typeof readWorkspace>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params?: ReadWorkspaceParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspace>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspace>>,
          TError,
          Awaited<ReturnType<typeof readWorkspace>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspace<TData = Awaited<ReturnType<typeof readWorkspace>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params?: ReadWorkspaceParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspace>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Workspace
 */

export function useReadWorkspace<TData = Awaited<ReturnType<typeof readWorkspace>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params?: ReadWorkspaceParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspace>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadWorkspaceQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type updateWorkspaceResponse200 = {
  data: WorkspaceAggregate
  status: 200
}

export type updateWorkspaceResponse401 = {
  data: Error
  status: 401
}

export type updateWorkspaceResponse403 = {
  data: Error
  status: 403
}

export type updateWorkspaceResponse404 = {
  data: Error
  status: 404
}

export type updateWorkspaceResponse422 = {
  data: Error
  status: 422
}

export type updateWorkspaceResponseSuccess = (updateWorkspaceResponse200) & {
  headers: Headers;
};
export type updateWorkspaceResponseError = (updateWorkspaceResponse401 | updateWorkspaceResponse403 | updateWorkspaceResponse404 | updateWorkspaceResponse422) & {
  headers: Headers;
};

export type updateWorkspaceResponse = (updateWorkspaceResponseSuccess | updateWorkspaceResponseError)

export const getUpdateWorkspaceUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}`
}

/**
 * Rename the workspace or change its visibility (spec 153): owners and org admins.
 * @summary Update Workspace
 */
export const updateWorkspace = async (workspaceId: string,
    updateWorkspaceRequest: UpdateWorkspaceRequest, options?: RequestInit): Promise<updateWorkspaceResponse> => {

  return customFetch<updateWorkspaceResponse>(getUpdateWorkspaceUrl(workspaceId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateWorkspaceRequest)
  }
);}




export const getUpdateWorkspaceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspace>>, TError,{workspaceId: string;data: UpdateWorkspaceRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWorkspace>>, TError,{workspaceId: string;data: UpdateWorkspaceRequest}, TContext> => {

const mutationKey = ['updateWorkspace'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWorkspace>>, {workspaceId: string;data: UpdateWorkspaceRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  updateWorkspace(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWorkspaceMutationResult = NonNullable<Awaited<ReturnType<typeof updateWorkspace>>>
    export type UpdateWorkspaceMutationBody = UpdateWorkspaceRequest
    export type UpdateWorkspaceMutationError = Error

    /**
 * @summary Update Workspace
 */
export const useUpdateWorkspace = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspace>>, TError,{workspaceId: string;data: UpdateWorkspaceRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateWorkspace>>,
        TError,
        {workspaceId: string;data: UpdateWorkspaceRequest},
        TContext
      > => {
      return useMutation(getUpdateWorkspaceMutationOptions(options), queryClient);
    }

export type deleteWorkspaceResponse204 = {
  data: void
  status: 204
}

export type deleteWorkspaceResponse401 = {
  data: Error
  status: 401
}

export type deleteWorkspaceResponse403 = {
  data: Error
  status: 403
}

export type deleteWorkspaceResponse404 = {
  data: Error
  status: 404
}

export type deleteWorkspaceResponse409 = {
  data: Error
  status: 409
}

export type deleteWorkspaceResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type deleteWorkspaceResponseSuccess = (deleteWorkspaceResponse204) & {
  headers: Headers;
};
export type deleteWorkspaceResponseError = (deleteWorkspaceResponse401 | deleteWorkspaceResponse403 | deleteWorkspaceResponse404 | deleteWorkspaceResponse409 | deleteWorkspaceResponse422) & {
  headers: Headers;
};

export type deleteWorkspaceResponse = (deleteWorkspaceResponseSuccess | deleteWorkspaceResponseError)

export const getDeleteWorkspaceUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}`
}

/**
 * @summary Delete Workspace
 */
export const deleteWorkspace = async (workspaceId: string, options?: RequestInit): Promise<deleteWorkspaceResponse> => {

  return customFetch<deleteWorkspaceResponse>(getDeleteWorkspaceUrl(workspaceId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteWorkspaceMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWorkspace>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteWorkspace>>, TError,{workspaceId: string}, TContext> => {

const mutationKey = ['deleteWorkspace'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteWorkspace>>, {workspaceId: string}> = (props) => {
          const {workspaceId} = props ?? {};

          return  deleteWorkspace(workspaceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteWorkspaceMutationResult = NonNullable<Awaited<ReturnType<typeof deleteWorkspace>>>

    export type DeleteWorkspaceMutationError = Error | HTTPValidationError

    /**
 * @summary Delete Workspace
 */
export const useDeleteWorkspace = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWorkspace>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteWorkspace>>,
        TError,
        {workspaceId: string},
        TContext
      > => {
      return useMutation(getDeleteWorkspaceMutationOptions(options), queryClient);
    }

export type listWorkspaceMembersResponse200 = {
  data: WorkspaceMembersView
  status: 200
}

export type listWorkspaceMembersResponse401 = {
  data: Error
  status: 401
}

export type listWorkspaceMembersResponse403 = {
  data: Error
  status: 403
}

export type listWorkspaceMembersResponse404 = {
  data: Error
  status: 404
}

export type listWorkspaceMembersResponse409 = {
  data: Error
  status: 409
}

export type listWorkspaceMembersResponse422 = {
  data: Error
  status: 422
}

export type listWorkspaceMembersResponse503 = {
  data: Error
  status: 503
}

export type listWorkspaceMembersResponseSuccess = (listWorkspaceMembersResponse200) & {
  headers: Headers;
};
export type listWorkspaceMembersResponseError = (listWorkspaceMembersResponse401 | listWorkspaceMembersResponse403 | listWorkspaceMembersResponse404 | listWorkspaceMembersResponse409 | listWorkspaceMembersResponse422 | listWorkspaceMembersResponse503) & {
  headers: Headers;
};

export type listWorkspaceMembersResponse = (listWorkspaceMembersResponseSuccess | listWorkspaceMembersResponseError)

export const getListWorkspaceMembersUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/members`
}

/**
 * @summary List Workspace Members
 */
export const listWorkspaceMembers = async (workspaceId: string, options?: RequestInit): Promise<listWorkspaceMembersResponse> => {

  return customFetch<listWorkspaceMembersResponse>(getListWorkspaceMembersUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWorkspaceMembersQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/members`
    ] as const;
    }


export const getListWorkspaceMembersQueryOptions = <TData = Awaited<ReturnType<typeof listWorkspaceMembers>>, TError = Error>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceMembers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWorkspaceMembersQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkspaceMembers>>> = ({ signal }) => listWorkspaceMembers(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceMembers>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListWorkspaceMembersQueryResult = NonNullable<Awaited<ReturnType<typeof listWorkspaceMembers>>>
export type ListWorkspaceMembersQueryError = Error


export function useListWorkspaceMembers<TData = Awaited<ReturnType<typeof listWorkspaceMembers>>, TError = Error>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceMembers>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceMembers>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceMembers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaceMembers<TData = Awaited<ReturnType<typeof listWorkspaceMembers>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceMembers>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceMembers>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceMembers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaceMembers<TData = Awaited<ReturnType<typeof listWorkspaceMembers>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceMembers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Workspace Members
 */

export function useListWorkspaceMembers<TData = Awaited<ReturnType<typeof listWorkspaceMembers>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceMembers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListWorkspaceMembersQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type addWorkspaceMemberResponse201 = {
  data: WorkspaceMembersView
  status: 201
}

export type addWorkspaceMemberResponse401 = {
  data: Error
  status: 401
}

export type addWorkspaceMemberResponse403 = {
  data: Error
  status: 403
}

export type addWorkspaceMemberResponse404 = {
  data: Error
  status: 404
}

export type addWorkspaceMemberResponse409 = {
  data: Error
  status: 409
}

export type addWorkspaceMemberResponse422 = {
  data: Error
  status: 422
}

export type addWorkspaceMemberResponse503 = {
  data: Error
  status: 503
}

export type addWorkspaceMemberResponseSuccess = (addWorkspaceMemberResponse201) & {
  headers: Headers;
};
export type addWorkspaceMemberResponseError = (addWorkspaceMemberResponse401 | addWorkspaceMemberResponse403 | addWorkspaceMemberResponse404 | addWorkspaceMemberResponse409 | addWorkspaceMemberResponse422 | addWorkspaceMemberResponse503) & {
  headers: Headers;
};

export type addWorkspaceMemberResponse = (addWorkspaceMemberResponseSuccess | addWorkspaceMemberResponseError)

export const getAddWorkspaceMemberUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/members`
}

/**
 * @summary Add Workspace Member
 */
export const addWorkspaceMember = async (workspaceId: string,
    addMemberRequest: AddMemberRequest, options?: RequestInit): Promise<addWorkspaceMemberResponse> => {

  return customFetch<addWorkspaceMemberResponse>(getAddWorkspaceMemberUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(addMemberRequest)
  }
);}




export const getAddWorkspaceMemberMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addWorkspaceMember>>, TError,{workspaceId: string;data: AddMemberRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addWorkspaceMember>>, TError,{workspaceId: string;data: AddMemberRequest}, TContext> => {

const mutationKey = ['addWorkspaceMember'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addWorkspaceMember>>, {workspaceId: string;data: AddMemberRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  addWorkspaceMember(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddWorkspaceMemberMutationResult = NonNullable<Awaited<ReturnType<typeof addWorkspaceMember>>>
    export type AddWorkspaceMemberMutationBody = AddMemberRequest
    export type AddWorkspaceMemberMutationError = Error

    /**
 * @summary Add Workspace Member
 */
export const useAddWorkspaceMember = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addWorkspaceMember>>, TError,{workspaceId: string;data: AddMemberRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof addWorkspaceMember>>,
        TError,
        {workspaceId: string;data: AddMemberRequest},
        TContext
      > => {
      return useMutation(getAddWorkspaceMemberMutationOptions(options), queryClient);
    }

export type updateWorkspaceMemberResponse200 = {
  data: WorkspaceMembersView
  status: 200
}

export type updateWorkspaceMemberResponse401 = {
  data: Error
  status: 401
}

export type updateWorkspaceMemberResponse403 = {
  data: Error
  status: 403
}

export type updateWorkspaceMemberResponse404 = {
  data: Error
  status: 404
}

export type updateWorkspaceMemberResponse409 = {
  data: Error
  status: 409
}

export type updateWorkspaceMemberResponse422 = {
  data: Error
  status: 422
}

export type updateWorkspaceMemberResponse503 = {
  data: Error
  status: 503
}

export type updateWorkspaceMemberResponseSuccess = (updateWorkspaceMemberResponse200) & {
  headers: Headers;
};
export type updateWorkspaceMemberResponseError = (updateWorkspaceMemberResponse401 | updateWorkspaceMemberResponse403 | updateWorkspaceMemberResponse404 | updateWorkspaceMemberResponse409 | updateWorkspaceMemberResponse422 | updateWorkspaceMemberResponse503) & {
  headers: Headers;
};

export type updateWorkspaceMemberResponse = (updateWorkspaceMemberResponseSuccess | updateWorkspaceMemberResponseError)

export const getUpdateWorkspaceMemberUrl = (workspaceId: string,
    userId: string,) => {




  return `/api/workspaces/${workspaceId}/members/${userId}`
}

/**
 * @summary Update Workspace Member
 */
export const updateWorkspaceMember = async (workspaceId: string,
    userId: string,
    updateMemberRequest: UpdateMemberRequest, options?: RequestInit): Promise<updateWorkspaceMemberResponse> => {

  return customFetch<updateWorkspaceMemberResponse>(getUpdateWorkspaceMemberUrl(workspaceId,userId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateMemberRequest)
  }
);}




export const getUpdateWorkspaceMemberMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceMember>>, TError,{workspaceId: string;userId: string;data: UpdateMemberRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceMember>>, TError,{workspaceId: string;userId: string;data: UpdateMemberRequest}, TContext> => {

const mutationKey = ['updateWorkspaceMember'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWorkspaceMember>>, {workspaceId: string;userId: string;data: UpdateMemberRequest}> = (props) => {
          const {workspaceId,userId,data} = props ?? {};

          return  updateWorkspaceMember(workspaceId,userId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWorkspaceMemberMutationResult = NonNullable<Awaited<ReturnType<typeof updateWorkspaceMember>>>
    export type UpdateWorkspaceMemberMutationBody = UpdateMemberRequest
    export type UpdateWorkspaceMemberMutationError = Error

    /**
 * @summary Update Workspace Member
 */
export const useUpdateWorkspaceMember = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceMember>>, TError,{workspaceId: string;userId: string;data: UpdateMemberRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateWorkspaceMember>>,
        TError,
        {workspaceId: string;userId: string;data: UpdateMemberRequest},
        TContext
      > => {
      return useMutation(getUpdateWorkspaceMemberMutationOptions(options), queryClient);
    }

export type removeWorkspaceMemberResponse200 = {
  data: MemberRemoval
  status: 200
}

export type removeWorkspaceMemberResponse401 = {
  data: Error
  status: 401
}

export type removeWorkspaceMemberResponse403 = {
  data: Error
  status: 403
}

export type removeWorkspaceMemberResponse404 = {
  data: Error
  status: 404
}

export type removeWorkspaceMemberResponse409 = {
  data: Error
  status: 409
}

export type removeWorkspaceMemberResponse422 = {
  data: Error
  status: 422
}

export type removeWorkspaceMemberResponse503 = {
  data: Error
  status: 503
}

export type removeWorkspaceMemberResponseSuccess = (removeWorkspaceMemberResponse200) & {
  headers: Headers;
};
export type removeWorkspaceMemberResponseError = (removeWorkspaceMemberResponse401 | removeWorkspaceMemberResponse403 | removeWorkspaceMemberResponse404 | removeWorkspaceMemberResponse409 | removeWorkspaceMemberResponse422 | removeWorkspaceMemberResponse503) & {
  headers: Headers;
};

export type removeWorkspaceMemberResponse = (removeWorkspaceMemberResponseSuccess | removeWorkspaceMemberResponseError)

export const getRemoveWorkspaceMemberUrl = (workspaceId: string,
    userId: string,) => {




  return `/api/workspaces/${workspaceId}/members/${userId}`
}

/**
 * Remove someone (owners, org admins) or leave (any member, for themselves).
 * @summary Remove Workspace Member
 */
export const removeWorkspaceMember = async (workspaceId: string,
    userId: string, options?: RequestInit): Promise<removeWorkspaceMemberResponse> => {

  return customFetch<removeWorkspaceMemberResponse>(getRemoveWorkspaceMemberUrl(workspaceId,userId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getRemoveWorkspaceMemberMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeWorkspaceMember>>, TError,{workspaceId: string;userId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeWorkspaceMember>>, TError,{workspaceId: string;userId: string}, TContext> => {

const mutationKey = ['removeWorkspaceMember'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeWorkspaceMember>>, {workspaceId: string;userId: string}> = (props) => {
          const {workspaceId,userId} = props ?? {};

          return  removeWorkspaceMember(workspaceId,userId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveWorkspaceMemberMutationResult = NonNullable<Awaited<ReturnType<typeof removeWorkspaceMember>>>

    export type RemoveWorkspaceMemberMutationError = Error

    /**
 * @summary Remove Workspace Member
 */
export const useRemoveWorkspaceMember = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeWorkspaceMember>>, TError,{workspaceId: string;userId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof removeWorkspaceMember>>,
        TError,
        {workspaceId: string;userId: string},
        TContext
      > => {
      return useMutation(getRemoveWorkspaceMemberMutationOptions(options), queryClient);
    }

export type listWorkspacePeopleResponse200 = {
  data: WorkspacePeople
  status: 200
}

export type listWorkspacePeopleResponse401 = {
  data: Error
  status: 401
}

export type listWorkspacePeopleResponse403 = {
  data: Error
  status: 403
}

export type listWorkspacePeopleResponse404 = {
  data: Error
  status: 404
}

export type listWorkspacePeopleResponse409 = {
  data: Error
  status: 409
}

export type listWorkspacePeopleResponse422 = {
  data: Error
  status: 422
}

export type listWorkspacePeopleResponse503 = {
  data: Error
  status: 503
}

export type listWorkspacePeopleResponseSuccess = (listWorkspacePeopleResponse200) & {
  headers: Headers;
};
export type listWorkspacePeopleResponseError = (listWorkspacePeopleResponse401 | listWorkspacePeopleResponse403 | listWorkspacePeopleResponse404 | listWorkspacePeopleResponse409 | listWorkspacePeopleResponse422 | listWorkspacePeopleResponse503) & {
  headers: Headers;
};

export type listWorkspacePeopleResponse = (listWorkspacePeopleResponseSuccess | listWorkspacePeopleResponseError)

export const getListWorkspacePeopleUrl = (workspaceId: string,
    params?: ListWorkspacePeopleParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/people?${stringifiedParams}` : `/api/workspaces/${workspaceId}/people`
}

/**
 * Who this workspace's gate and mention pickers offer.
 * @summary List Workspace People
 */
export const listWorkspacePeople = async (workspaceId: string,
    params?: ListWorkspacePeopleParams, options?: RequestInit): Promise<listWorkspacePeopleResponse> => {

  return customFetch<listWorkspacePeopleResponse>(getListWorkspacePeopleUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWorkspacePeopleQueryKey = (workspaceId: string,
    params?: ListWorkspacePeopleParams,) => {
    return [
    `/api/workspaces/${workspaceId}/people`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWorkspacePeopleQueryOptions = <TData = Awaited<ReturnType<typeof listWorkspacePeople>>, TError = Error>(workspaceId: string,
    params?: ListWorkspacePeopleParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspacePeople>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWorkspacePeopleQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkspacePeople>>> = ({ signal }) => listWorkspacePeople(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWorkspacePeople>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListWorkspacePeopleQueryResult = NonNullable<Awaited<ReturnType<typeof listWorkspacePeople>>>
export type ListWorkspacePeopleQueryError = Error


export function useListWorkspacePeople<TData = Awaited<ReturnType<typeof listWorkspacePeople>>, TError = Error>(
 workspaceId: string,
    params: undefined |  ListWorkspacePeopleParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspacePeople>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspacePeople>>,
          TError,
          Awaited<ReturnType<typeof listWorkspacePeople>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspacePeople<TData = Awaited<ReturnType<typeof listWorkspacePeople>>, TError = Error>(
 workspaceId: string,
    params?: ListWorkspacePeopleParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspacePeople>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspacePeople>>,
          TError,
          Awaited<ReturnType<typeof listWorkspacePeople>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspacePeople<TData = Awaited<ReturnType<typeof listWorkspacePeople>>, TError = Error>(
 workspaceId: string,
    params?: ListWorkspacePeopleParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspacePeople>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Workspace People
 */

export function useListWorkspacePeople<TData = Awaited<ReturnType<typeof listWorkspacePeople>>, TError = Error>(
 workspaceId: string,
    params?: ListWorkspacePeopleParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspacePeople>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListWorkspacePeopleQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type requestWorkspaceAccessResponse201 = {
  data: MyAccessRequest
  status: 201
}

export type requestWorkspaceAccessResponse401 = {
  data: Error
  status: 401
}

export type requestWorkspaceAccessResponse403 = {
  data: Error
  status: 403
}

export type requestWorkspaceAccessResponse404 = {
  data: Error
  status: 404
}

export type requestWorkspaceAccessResponse409 = {
  data: Error
  status: 409
}

export type requestWorkspaceAccessResponse422 = {
  data: Error
  status: 422
}

export type requestWorkspaceAccessResponse503 = {
  data: Error
  status: 503
}

export type requestWorkspaceAccessResponseSuccess = (requestWorkspaceAccessResponse201) & {
  headers: Headers;
};
export type requestWorkspaceAccessResponseError = (requestWorkspaceAccessResponse401 | requestWorkspaceAccessResponse403 | requestWorkspaceAccessResponse404 | requestWorkspaceAccessResponse409 | requestWorkspaceAccessResponse422 | requestWorkspaceAccessResponse503) & {
  headers: Headers;
};

export type requestWorkspaceAccessResponse = (requestWorkspaceAccessResponseSuccess | requestWorkspaceAccessResponseError)

export const getRequestWorkspaceAccessUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/access-requests`
}

/**
 * Ask to join. Open to anyone who is not in the workspace, including someone who
 * cannot see it; asking again while a request is open changes nothing.
 * @summary Request Workspace Access
 */
export const requestWorkspaceAccess = async (workspaceId: string, options?: RequestInit): Promise<requestWorkspaceAccessResponse> => {

  return customFetch<requestWorkspaceAccessResponse>(getRequestWorkspaceAccessUrl(workspaceId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getRequestWorkspaceAccessMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestWorkspaceAccess>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof requestWorkspaceAccess>>, TError,{workspaceId: string}, TContext> => {

const mutationKey = ['requestWorkspaceAccess'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof requestWorkspaceAccess>>, {workspaceId: string}> = (props) => {
          const {workspaceId} = props ?? {};

          return  requestWorkspaceAccess(workspaceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RequestWorkspaceAccessMutationResult = NonNullable<Awaited<ReturnType<typeof requestWorkspaceAccess>>>

    export type RequestWorkspaceAccessMutationError = Error

    /**
 * @summary Request Workspace Access
 */
export const useRequestWorkspaceAccess = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestWorkspaceAccess>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof requestWorkspaceAccess>>,
        TError,
        {workspaceId: string},
        TContext
      > => {
      return useMutation(getRequestWorkspaceAccessMutationOptions(options), queryClient);
    }

export type listWorkspaceAccessRequestsResponse200 = {
  data: AccessRequests
  status: 200
}

export type listWorkspaceAccessRequestsResponse401 = {
  data: Error
  status: 401
}

export type listWorkspaceAccessRequestsResponse403 = {
  data: Error
  status: 403
}

export type listWorkspaceAccessRequestsResponse404 = {
  data: Error
  status: 404
}

export type listWorkspaceAccessRequestsResponse409 = {
  data: Error
  status: 409
}

export type listWorkspaceAccessRequestsResponse422 = {
  data: Error
  status: 422
}

export type listWorkspaceAccessRequestsResponse503 = {
  data: Error
  status: 503
}

export type listWorkspaceAccessRequestsResponseSuccess = (listWorkspaceAccessRequestsResponse200) & {
  headers: Headers;
};
export type listWorkspaceAccessRequestsResponseError = (listWorkspaceAccessRequestsResponse401 | listWorkspaceAccessRequestsResponse403 | listWorkspaceAccessRequestsResponse404 | listWorkspaceAccessRequestsResponse409 | listWorkspaceAccessRequestsResponse422 | listWorkspaceAccessRequestsResponse503) & {
  headers: Headers;
};

export type listWorkspaceAccessRequestsResponse = (listWorkspaceAccessRequestsResponseSuccess | listWorkspaceAccessRequestsResponseError)

export const getListWorkspaceAccessRequestsUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/access-requests`
}

/**
 * The open requests to join, for its owners and org admins.
 * @summary List Workspace Access Requests
 */
export const listWorkspaceAccessRequests = async (workspaceId: string, options?: RequestInit): Promise<listWorkspaceAccessRequestsResponse> => {

  return customFetch<listWorkspaceAccessRequestsResponse>(getListWorkspaceAccessRequestsUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWorkspaceAccessRequestsQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/access-requests`
    ] as const;
    }


export const getListWorkspaceAccessRequestsQueryOptions = <TData = Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError = Error>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWorkspaceAccessRequestsQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkspaceAccessRequests>>> = ({ signal }) => listWorkspaceAccessRequests(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListWorkspaceAccessRequestsQueryResult = NonNullable<Awaited<ReturnType<typeof listWorkspaceAccessRequests>>>
export type ListWorkspaceAccessRequestsQueryError = Error


export function useListWorkspaceAccessRequests<TData = Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError = Error>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceAccessRequests>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceAccessRequests>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaceAccessRequests<TData = Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceAccessRequests>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceAccessRequests>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaceAccessRequests<TData = Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Workspace Access Requests
 */

export function useListWorkspaceAccessRequests<TData = Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceAccessRequests>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListWorkspaceAccessRequestsQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readMyWorkspaceAccessRequestResponse200 = {
  data: MyAccessRequest
  status: 200
}

export type readMyWorkspaceAccessRequestResponse401 = {
  data: Error
  status: 401
}

export type readMyWorkspaceAccessRequestResponse403 = {
  data: Error
  status: 403
}

export type readMyWorkspaceAccessRequestResponse404 = {
  data: Error
  status: 404
}

export type readMyWorkspaceAccessRequestResponse409 = {
  data: Error
  status: 409
}

export type readMyWorkspaceAccessRequestResponse422 = {
  data: Error
  status: 422
}

export type readMyWorkspaceAccessRequestResponse503 = {
  data: Error
  status: 503
}

export type readMyWorkspaceAccessRequestResponseSuccess = (readMyWorkspaceAccessRequestResponse200) & {
  headers: Headers;
};
export type readMyWorkspaceAccessRequestResponseError = (readMyWorkspaceAccessRequestResponse401 | readMyWorkspaceAccessRequestResponse403 | readMyWorkspaceAccessRequestResponse404 | readMyWorkspaceAccessRequestResponse409 | readMyWorkspaceAccessRequestResponse422 | readMyWorkspaceAccessRequestResponse503) & {
  headers: Headers;
};

export type readMyWorkspaceAccessRequestResponse = (readMyWorkspaceAccessRequestResponseSuccess | readMyWorkspaceAccessRequestResponseError)

export const getReadMyWorkspaceAccessRequestUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/access-requests/mine`
}

/**
 * The caller's own open request, if any — the no-access page's "Request sent".
 * @summary Read My Workspace Access Request
 */
export const readMyWorkspaceAccessRequest = async (workspaceId: string, options?: RequestInit): Promise<readMyWorkspaceAccessRequestResponse> => {

  return customFetch<readMyWorkspaceAccessRequestResponse>(getReadMyWorkspaceAccessRequestUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadMyWorkspaceAccessRequestQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/access-requests/mine`
    ] as const;
    }


export const getReadMyWorkspaceAccessRequestQueryOptions = <TData = Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError = Error>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadMyWorkspaceAccessRequestQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>> = ({ signal }) => readMyWorkspaceAccessRequest(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadMyWorkspaceAccessRequestQueryResult = NonNullable<Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>>
export type ReadMyWorkspaceAccessRequestQueryError = Error


export function useReadMyWorkspaceAccessRequest<TData = Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError = Error>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>,
          TError,
          Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadMyWorkspaceAccessRequest<TData = Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>,
          TError,
          Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadMyWorkspaceAccessRequest<TData = Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read My Workspace Access Request
 */

export function useReadMyWorkspaceAccessRequest<TData = Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readMyWorkspaceAccessRequest>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadMyWorkspaceAccessRequestQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type addFromWorkspaceAccessRequestResponse201 = {
  data: WorkspaceMembersView
  status: 201
}

export type addFromWorkspaceAccessRequestResponse401 = {
  data: Error
  status: 401
}

export type addFromWorkspaceAccessRequestResponse403 = {
  data: Error
  status: 403
}

export type addFromWorkspaceAccessRequestResponse404 = {
  data: Error
  status: 404
}

export type addFromWorkspaceAccessRequestResponse409 = {
  data: Error
  status: 409
}

export type addFromWorkspaceAccessRequestResponse422 = {
  data: Error
  status: 422
}

export type addFromWorkspaceAccessRequestResponse503 = {
  data: Error
  status: 503
}

export type addFromWorkspaceAccessRequestResponseSuccess = (addFromWorkspaceAccessRequestResponse201) & {
  headers: Headers;
};
export type addFromWorkspaceAccessRequestResponseError = (addFromWorkspaceAccessRequestResponse401 | addFromWorkspaceAccessRequestResponse403 | addFromWorkspaceAccessRequestResponse404 | addFromWorkspaceAccessRequestResponse409 | addFromWorkspaceAccessRequestResponse422 | addFromWorkspaceAccessRequestResponse503) & {
  headers: Headers;
};

export type addFromWorkspaceAccessRequestResponse = (addFromWorkspaceAccessRequestResponseSuccess | addFromWorkspaceAccessRequestResponseError)

export const getAddFromWorkspaceAccessRequestUrl = (workspaceId: string,
    requestId: string,) => {




  return `/api/workspaces/${workspaceId}/access-requests/${requestId}/add`
}

/**
 * Grant a request: the person is added, verified with Keycloak, and told.
 * @summary Add From Workspace Access Request
 */
export const addFromWorkspaceAccessRequest = async (workspaceId: string,
    requestId: string,
    addFromRequest: AddFromRequest, options?: RequestInit): Promise<addFromWorkspaceAccessRequestResponse> => {

  return customFetch<addFromWorkspaceAccessRequestResponse>(getAddFromWorkspaceAccessRequestUrl(workspaceId,requestId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(addFromRequest)
  }
);}




export const getAddFromWorkspaceAccessRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addFromWorkspaceAccessRequest>>, TError,{workspaceId: string;requestId: string;data: AddFromRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addFromWorkspaceAccessRequest>>, TError,{workspaceId: string;requestId: string;data: AddFromRequest}, TContext> => {

const mutationKey = ['addFromWorkspaceAccessRequest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addFromWorkspaceAccessRequest>>, {workspaceId: string;requestId: string;data: AddFromRequest}> = (props) => {
          const {workspaceId,requestId,data} = props ?? {};

          return  addFromWorkspaceAccessRequest(workspaceId,requestId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddFromWorkspaceAccessRequestMutationResult = NonNullable<Awaited<ReturnType<typeof addFromWorkspaceAccessRequest>>>
    export type AddFromWorkspaceAccessRequestMutationBody = AddFromRequest
    export type AddFromWorkspaceAccessRequestMutationError = Error

    /**
 * @summary Add From Workspace Access Request
 */
export const useAddFromWorkspaceAccessRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addFromWorkspaceAccessRequest>>, TError,{workspaceId: string;requestId: string;data: AddFromRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof addFromWorkspaceAccessRequest>>,
        TError,
        {workspaceId: string;requestId: string;data: AddFromRequest},
        TContext
      > => {
      return useMutation(getAddFromWorkspaceAccessRequestMutationOptions(options), queryClient);
    }

export type dismissWorkspaceAccessRequestResponse204 = {
  data: void
  status: 204
}

export type dismissWorkspaceAccessRequestResponse401 = {
  data: Error
  status: 401
}

export type dismissWorkspaceAccessRequestResponse403 = {
  data: Error
  status: 403
}

export type dismissWorkspaceAccessRequestResponse404 = {
  data: Error
  status: 404
}

export type dismissWorkspaceAccessRequestResponse409 = {
  data: Error
  status: 409
}

export type dismissWorkspaceAccessRequestResponse422 = {
  data: Error
  status: 422
}

export type dismissWorkspaceAccessRequestResponse503 = {
  data: Error
  status: 503
}

export type dismissWorkspaceAccessRequestResponseSuccess = (dismissWorkspaceAccessRequestResponse204) & {
  headers: Headers;
};
export type dismissWorkspaceAccessRequestResponseError = (dismissWorkspaceAccessRequestResponse401 | dismissWorkspaceAccessRequestResponse403 | dismissWorkspaceAccessRequestResponse404 | dismissWorkspaceAccessRequestResponse409 | dismissWorkspaceAccessRequestResponse422 | dismissWorkspaceAccessRequestResponse503) & {
  headers: Headers;
};

export type dismissWorkspaceAccessRequestResponse = (dismissWorkspaceAccessRequestResponseSuccess | dismissWorkspaceAccessRequestResponseError)

export const getDismissWorkspaceAccessRequestUrl = (workspaceId: string,
    requestId: string,) => {




  return `/api/workspaces/${workspaceId}/access-requests/${requestId}/dismiss`
}

/**
 * Turn a request down. The person is not told.
 * @summary Dismiss Workspace Access Request
 */
export const dismissWorkspaceAccessRequest = async (workspaceId: string,
    requestId: string, options?: RequestInit): Promise<dismissWorkspaceAccessRequestResponse> => {

  return customFetch<dismissWorkspaceAccessRequestResponse>(getDismissWorkspaceAccessRequestUrl(workspaceId,requestId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getDismissWorkspaceAccessRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof dismissWorkspaceAccessRequest>>, TError,{workspaceId: string;requestId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof dismissWorkspaceAccessRequest>>, TError,{workspaceId: string;requestId: string}, TContext> => {

const mutationKey = ['dismissWorkspaceAccessRequest'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof dismissWorkspaceAccessRequest>>, {workspaceId: string;requestId: string}> = (props) => {
          const {workspaceId,requestId} = props ?? {};

          return  dismissWorkspaceAccessRequest(workspaceId,requestId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DismissWorkspaceAccessRequestMutationResult = NonNullable<Awaited<ReturnType<typeof dismissWorkspaceAccessRequest>>>

    export type DismissWorkspaceAccessRequestMutationError = Error

    /**
 * @summary Dismiss Workspace Access Request
 */
export const useDismissWorkspaceAccessRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof dismissWorkspaceAccessRequest>>, TError,{workspaceId: string;requestId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof dismissWorkspaceAccessRequest>>,
        TError,
        {workspaceId: string;requestId: string},
        TContext
      > => {
      return useMutation(getDismissWorkspaceAccessRequestMutationOptions(options), queryClient);
    }

export type readWorkspaceAccessLogResponse200 = {
  data: AccessLogPage
  status: 200
}

export type readWorkspaceAccessLogResponse401 = {
  data: Error
  status: 401
}

export type readWorkspaceAccessLogResponse403 = {
  data: Error
  status: 403
}

export type readWorkspaceAccessLogResponse404 = {
  data: Error
  status: 404
}

export type readWorkspaceAccessLogResponse409 = {
  data: Error
  status: 409
}

export type readWorkspaceAccessLogResponse422 = {
  data: Error
  status: 422
}

export type readWorkspaceAccessLogResponse503 = {
  data: Error
  status: 503
}

export type readWorkspaceAccessLogResponseSuccess = (readWorkspaceAccessLogResponse200) & {
  headers: Headers;
};
export type readWorkspaceAccessLogResponseError = (readWorkspaceAccessLogResponse401 | readWorkspaceAccessLogResponse403 | readWorkspaceAccessLogResponse404 | readWorkspaceAccessLogResponse409 | readWorkspaceAccessLogResponse422 | readWorkspaceAccessLogResponse503) & {
  headers: Headers;
};

export type readWorkspaceAccessLogResponse = (readWorkspaceAccessLogResponseSuccess | readWorkspaceAccessLogResponseError)

export const getReadWorkspaceAccessLogUrl = (workspaceId: string,
    params?: ReadWorkspaceAccessLogParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/access-log?${stringifiedParams}` : `/api/workspaces/${workspaceId}/access-log`
}

/**
 * The workspace's access log, newest first: its owners' and the org admins' (FR-001).
 * @summary Read Workspace Access Log
 */
export const readWorkspaceAccessLog = async (workspaceId: string,
    params?: ReadWorkspaceAccessLogParams, options?: RequestInit): Promise<readWorkspaceAccessLogResponse> => {

  return customFetch<readWorkspaceAccessLogResponse>(getReadWorkspaceAccessLogUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadWorkspaceAccessLogQueryKey = (workspaceId: string,
    params?: ReadWorkspaceAccessLogParams,) => {
    return [
    `/api/workspaces/${workspaceId}/access-log`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadWorkspaceAccessLogQueryOptions = <TData = Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError = Error>(workspaceId: string,
    params?: ReadWorkspaceAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadWorkspaceAccessLogQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readWorkspaceAccessLog>>> = ({ signal }) => readWorkspaceAccessLog(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadWorkspaceAccessLogQueryResult = NonNullable<Awaited<ReturnType<typeof readWorkspaceAccessLog>>>
export type ReadWorkspaceAccessLogQueryError = Error


export function useReadWorkspaceAccessLog<TData = Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError = Error>(
 workspaceId: string,
    params: undefined |  ReadWorkspaceAccessLogParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceAccessLog>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceAccessLog>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceAccessLog<TData = Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceAccessLog>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceAccessLog>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceAccessLog<TData = Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Workspace Access Log
 */

export function useReadWorkspaceAccessLog<TData = Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAccessLog>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadWorkspaceAccessLogQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type createWorkflowResponse201 = {
  data: WorkflowView
  status: 201
}

export type createWorkflowResponse401 = {
  data: Error
  status: 401
}

export type createWorkflowResponse403 = {
  data: Error
  status: 403
}

export type createWorkflowResponse404 = {
  data: Error
  status: 404
}

export type createWorkflowResponse409 = {
  data: ImportRejection
  status: 409
}

export type createWorkflowResponse422 = {
  data: ImportRejection
  status: 422
}

export type createWorkflowResponseSuccess = (createWorkflowResponse201) & {
  headers: Headers;
};
export type createWorkflowResponseError = (createWorkflowResponse401 | createWorkflowResponse403 | createWorkflowResponse404 | createWorkflowResponse409 | createWorkflowResponse422) & {
  headers: Headers;
};

export type createWorkflowResponse = (createWorkflowResponseSuccess | createWorkflowResponseError)

export const getCreateWorkflowUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows`
}

/**
 * @summary Create Workflow
 */
export const createWorkflow = async (workspaceId: string,
    workflowCreate: WorkflowCreate, options?: RequestInit): Promise<createWorkflowResponse> => {

  return customFetch<createWorkflowResponse>(getCreateWorkflowUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(workflowCreate)
  }
);}




export const getCreateWorkflowMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkflow>>, TError,{workspaceId: string;data: WorkflowCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWorkflow>>, TError,{workspaceId: string;data: WorkflowCreate}, TContext> => {

const mutationKey = ['createWorkflow'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWorkflow>>, {workspaceId: string;data: WorkflowCreate}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createWorkflow(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWorkflowMutationResult = NonNullable<Awaited<ReturnType<typeof createWorkflow>>>
    export type CreateWorkflowMutationBody = WorkflowCreate
    export type CreateWorkflowMutationError = Error | ImportRejection

    /**
 * @summary Create Workflow
 */
export const useCreateWorkflow = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkflow>>, TError,{workspaceId: string;data: WorkflowCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createWorkflow>>,
        TError,
        {workspaceId: string;data: WorkflowCreate},
        TContext
      > => {
      return useMutation(getCreateWorkflowMutationOptions(options), queryClient);
    }

export type createWorkflowDraftResponse201 = {
  data: WorkflowView
  status: 201
}

export type createWorkflowDraftResponse401 = {
  data: Error
  status: 401
}

export type createWorkflowDraftResponse403 = {
  data: Error
  status: 403
}

export type createWorkflowDraftResponse404 = {
  data: Error
  status: 404
}

export type createWorkflowDraftResponse409 = {
  data: ImportRejection
  status: 409
}

export type createWorkflowDraftResponse422 = {
  data: ImportRejection
  status: 422
}

export type createWorkflowDraftResponseSuccess = (createWorkflowDraftResponse201) & {
  headers: Headers;
};
export type createWorkflowDraftResponseError = (createWorkflowDraftResponse401 | createWorkflowDraftResponse403 | createWorkflowDraftResponse404 | createWorkflowDraftResponse409 | createWorkflowDraftResponse422) & {
  headers: Headers;
};

export type createWorkflowDraftResponse = (createWorkflowDraftResponseSuccess | createWorkflowDraftResponseError)

export const getCreateWorkflowDraftUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/drafts`
}

/**
 * @summary Create Workflow Draft
 */
export const createWorkflowDraft = async (workspaceId: string,
    workflowDraftCreate: WorkflowDraftCreate, options?: RequestInit): Promise<createWorkflowDraftResponse> => {

  return customFetch<createWorkflowDraftResponse>(getCreateWorkflowDraftUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(workflowDraftCreate)
  }
);}




export const getCreateWorkflowDraftMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkflowDraft>>, TError,{workspaceId: string;data: WorkflowDraftCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWorkflowDraft>>, TError,{workspaceId: string;data: WorkflowDraftCreate}, TContext> => {

const mutationKey = ['createWorkflowDraft'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWorkflowDraft>>, {workspaceId: string;data: WorkflowDraftCreate}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createWorkflowDraft(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWorkflowDraftMutationResult = NonNullable<Awaited<ReturnType<typeof createWorkflowDraft>>>
    export type CreateWorkflowDraftMutationBody = WorkflowDraftCreate
    export type CreateWorkflowDraftMutationError = Error | ImportRejection

    /**
 * @summary Create Workflow Draft
 */
export const useCreateWorkflowDraft = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkflowDraft>>, TError,{workspaceId: string;data: WorkflowDraftCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createWorkflowDraft>>,
        TError,
        {workspaceId: string;data: WorkflowDraftCreate},
        TContext
      > => {
      return useMutation(getCreateWorkflowDraftMutationOptions(options), queryClient);
    }

export type replaceWorkflowResponse200 = {
  data: WorkflowView
  status: 200
}

export type replaceWorkflowResponse401 = {
  data: Error
  status: 401
}

export type replaceWorkflowResponse403 = {
  data: Error
  status: 403
}

export type replaceWorkflowResponse404 = {
  data: Error
  status: 404
}

export type replaceWorkflowResponse409 = {
  data: ImportRejection
  status: 409
}

export type replaceWorkflowResponse422 = {
  data: ImportRejection
  status: 422
}

export type replaceWorkflowResponseSuccess = (replaceWorkflowResponse200) & {
  headers: Headers;
};
export type replaceWorkflowResponseError = (replaceWorkflowResponse401 | replaceWorkflowResponse403 | replaceWorkflowResponse404 | replaceWorkflowResponse409 | replaceWorkflowResponse422) & {
  headers: Headers;
};

export type replaceWorkflowResponse = (replaceWorkflowResponseSuccess | replaceWorkflowResponseError)

export const getReplaceWorkflowUrl = (workspaceId: string,
    workflowId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/${workflowId}`
}

/**
 * @summary Replace Workflow
 */
export const replaceWorkflow = async (workspaceId: string,
    workflowId: string,
    workflowReplace: WorkflowReplace, options?: RequestInit): Promise<replaceWorkflowResponse> => {

  return customFetch<replaceWorkflowResponse>(getReplaceWorkflowUrl(workspaceId,workflowId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(workflowReplace)
  }
);}




export const getReplaceWorkflowMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replaceWorkflow>>, TError,{workspaceId: string;workflowId: string;data: WorkflowReplace}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof replaceWorkflow>>, TError,{workspaceId: string;workflowId: string;data: WorkflowReplace}, TContext> => {

const mutationKey = ['replaceWorkflow'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof replaceWorkflow>>, {workspaceId: string;workflowId: string;data: WorkflowReplace}> = (props) => {
          const {workspaceId,workflowId,data} = props ?? {};

          return  replaceWorkflow(workspaceId,workflowId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReplaceWorkflowMutationResult = NonNullable<Awaited<ReturnType<typeof replaceWorkflow>>>
    export type ReplaceWorkflowMutationBody = WorkflowReplace
    export type ReplaceWorkflowMutationError = Error | ImportRejection

    /**
 * @summary Replace Workflow
 */
export const useReplaceWorkflow = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replaceWorkflow>>, TError,{workspaceId: string;workflowId: string;data: WorkflowReplace}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof replaceWorkflow>>,
        TError,
        {workspaceId: string;workflowId: string;data: WorkflowReplace},
        TContext
      > => {
      return useMutation(getReplaceWorkflowMutationOptions(options), queryClient);
    }

export type renameWorkflowResponse200 = {
  data: WorkflowView
  status: 200
}

export type renameWorkflowResponse401 = {
  data: Error
  status: 401
}

export type renameWorkflowResponse403 = {
  data: Error
  status: 403
}

export type renameWorkflowResponse404 = {
  data: Error
  status: 404
}

export type renameWorkflowResponse422 = {
  data: ImportRejection
  status: 422
}

export type renameWorkflowResponseSuccess = (renameWorkflowResponse200) & {
  headers: Headers;
};
export type renameWorkflowResponseError = (renameWorkflowResponse401 | renameWorkflowResponse403 | renameWorkflowResponse404 | renameWorkflowResponse422) & {
  headers: Headers;
};

export type renameWorkflowResponse = (renameWorkflowResponseSuccess | renameWorkflowResponseError)

export const getRenameWorkflowUrl = (workspaceId: string,
    workflowId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/${workflowId}`
}

/**
 * @summary Rename Workflow
 */
export const renameWorkflow = async (workspaceId: string,
    workflowId: string,
    workflowRename: WorkflowRename, options?: RequestInit): Promise<renameWorkflowResponse> => {

  return customFetch<renameWorkflowResponse>(getRenameWorkflowUrl(workspaceId,workflowId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(workflowRename)
  }
);}




export const getRenameWorkflowMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renameWorkflow>>, TError,{workspaceId: string;workflowId: string;data: WorkflowRename}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof renameWorkflow>>, TError,{workspaceId: string;workflowId: string;data: WorkflowRename}, TContext> => {

const mutationKey = ['renameWorkflow'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof renameWorkflow>>, {workspaceId: string;workflowId: string;data: WorkflowRename}> = (props) => {
          const {workspaceId,workflowId,data} = props ?? {};

          return  renameWorkflow(workspaceId,workflowId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RenameWorkflowMutationResult = NonNullable<Awaited<ReturnType<typeof renameWorkflow>>>
    export type RenameWorkflowMutationBody = WorkflowRename
    export type RenameWorkflowMutationError = Error | ImportRejection

    /**
 * @summary Rename Workflow
 */
export const useRenameWorkflow = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renameWorkflow>>, TError,{workspaceId: string;workflowId: string;data: WorkflowRename}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof renameWorkflow>>,
        TError,
        {workspaceId: string;workflowId: string;data: WorkflowRename},
        TContext
      > => {
      return useMutation(getRenameWorkflowMutationOptions(options), queryClient);
    }

export type deleteWorkflowResponse204 = {
  data: void
  status: 204
}

export type deleteWorkflowResponse401 = {
  data: Error
  status: 401
}

export type deleteWorkflowResponse403 = {
  data: Error
  status: 403
}

export type deleteWorkflowResponse404 = {
  data: Error
  status: 404
}

export type deleteWorkflowResponse409 = {
  data: Error
  status: 409
}

export type deleteWorkflowResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type deleteWorkflowResponseSuccess = (deleteWorkflowResponse204) & {
  headers: Headers;
};
export type deleteWorkflowResponseError = (deleteWorkflowResponse401 | deleteWorkflowResponse403 | deleteWorkflowResponse404 | deleteWorkflowResponse409 | deleteWorkflowResponse422) & {
  headers: Headers;
};

export type deleteWorkflowResponse = (deleteWorkflowResponseSuccess | deleteWorkflowResponseError)

export const getDeleteWorkflowUrl = (workspaceId: string,
    workflowId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/${workflowId}`
}

/**
 * @summary Delete Workflow
 */
export const deleteWorkflow = async (workspaceId: string,
    workflowId: string, options?: RequestInit): Promise<deleteWorkflowResponse> => {

  return customFetch<deleteWorkflowResponse>(getDeleteWorkflowUrl(workspaceId,workflowId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteWorkflowMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWorkflow>>, TError,{workspaceId: string;workflowId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteWorkflow>>, TError,{workspaceId: string;workflowId: string}, TContext> => {

const mutationKey = ['deleteWorkflow'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteWorkflow>>, {workspaceId: string;workflowId: string}> = (props) => {
          const {workspaceId,workflowId} = props ?? {};

          return  deleteWorkflow(workspaceId,workflowId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteWorkflowMutationResult = NonNullable<Awaited<ReturnType<typeof deleteWorkflow>>>

    export type DeleteWorkflowMutationError = Error | HTTPValidationError

    /**
 * @summary Delete Workflow
 */
export const useDeleteWorkflow = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWorkflow>>, TError,{workspaceId: string;workflowId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteWorkflow>>,
        TError,
        {workspaceId: string;workflowId: string},
        TContext
      > => {
      return useMutation(getDeleteWorkflowMutationOptions(options), queryClient);
    }

export type getWorkflowForEditingResponse200 = {
  data: WorkflowEditView
  status: 200
}

export type getWorkflowForEditingResponse401 = {
  data: Error
  status: 401
}

export type getWorkflowForEditingResponse403 = {
  data: Error
  status: 403
}

export type getWorkflowForEditingResponse404 = {
  data: Error
  status: 404
}

export type getWorkflowForEditingResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getWorkflowForEditingResponseSuccess = (getWorkflowForEditingResponse200) & {
  headers: Headers;
};
export type getWorkflowForEditingResponseError = (getWorkflowForEditingResponse401 | getWorkflowForEditingResponse403 | getWorkflowForEditingResponse404 | getWorkflowForEditingResponse422) & {
  headers: Headers;
};

export type getWorkflowForEditingResponse = (getWorkflowForEditingResponseSuccess | getWorkflowForEditingResponseError)

export const getGetWorkflowForEditingUrl = (workspaceId: string,
    workflowId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/${workflowId}/pending-changes`
}

/**
 * @summary Get Workflow For Editing
 */
export const getWorkflowForEditing = async (workspaceId: string,
    workflowId: string, options?: RequestInit): Promise<getWorkflowForEditingResponse> => {

  return customFetch<getWorkflowForEditingResponse>(getGetWorkflowForEditingUrl(workspaceId,workflowId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWorkflowForEditingQueryKey = (workspaceId: string,
    workflowId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/workflows/${workflowId}/pending-changes`
    ] as const;
    }


export const getGetWorkflowForEditingQueryOptions = <TData = Awaited<ReturnType<typeof getWorkflowForEditing>>, TError = Error | HTTPValidationError>(workspaceId: string,
    workflowId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowForEditing>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkflowForEditingQueryKey(workspaceId,workflowId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkflowForEditing>>> = ({ signal }) => getWorkflowForEditing(workspaceId,workflowId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && workflowId !== null && workflowId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkflowForEditing>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetWorkflowForEditingQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkflowForEditing>>>
export type GetWorkflowForEditingQueryError = Error | HTTPValidationError


export function useGetWorkflowForEditing<TData = Awaited<ReturnType<typeof getWorkflowForEditing>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    workflowId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowForEditing>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowForEditing>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowForEditing>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetWorkflowForEditing<TData = Awaited<ReturnType<typeof getWorkflowForEditing>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    workflowId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowForEditing>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowForEditing>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowForEditing>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetWorkflowForEditing<TData = Awaited<ReturnType<typeof getWorkflowForEditing>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    workflowId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowForEditing>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Workflow For Editing
 */

export function useGetWorkflowForEditing<TData = Awaited<ReturnType<typeof getWorkflowForEditing>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    workflowId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowForEditing>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetWorkflowForEditingQueryOptions(workspaceId,workflowId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type saveWorkflowPendingChangesResponse200 = {
  data: WorkflowEditView
  status: 200
}

export type saveWorkflowPendingChangesResponse401 = {
  data: Error
  status: 401
}

export type saveWorkflowPendingChangesResponse403 = {
  data: Error
  status: 403
}

export type saveWorkflowPendingChangesResponse404 = {
  data: Error
  status: 404
}

export type saveWorkflowPendingChangesResponse409 = {
  data: Error
  status: 409
}

export type saveWorkflowPendingChangesResponse422 = {
  data: ImportRejection
  status: 422
}

export type saveWorkflowPendingChangesResponseSuccess = (saveWorkflowPendingChangesResponse200) & {
  headers: Headers;
};
export type saveWorkflowPendingChangesResponseError = (saveWorkflowPendingChangesResponse401 | saveWorkflowPendingChangesResponse403 | saveWorkflowPendingChangesResponse404 | saveWorkflowPendingChangesResponse409 | saveWorkflowPendingChangesResponse422) & {
  headers: Headers;
};

export type saveWorkflowPendingChangesResponse = (saveWorkflowPendingChangesResponseSuccess | saveWorkflowPendingChangesResponseError)

export const getSaveWorkflowPendingChangesUrl = (workspaceId: string,
    workflowId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/${workflowId}/pending-changes`
}

/**
 * @summary Save Workflow Pending Changes
 */
export const saveWorkflowPendingChanges = async (workspaceId: string,
    workflowId: string,
    workflowPendingChangesSave: WorkflowPendingChangesSave, options?: RequestInit): Promise<saveWorkflowPendingChangesResponse> => {

  return customFetch<saveWorkflowPendingChangesResponse>(getSaveWorkflowPendingChangesUrl(workspaceId,workflowId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(workflowPendingChangesSave)
  }
);}




export const getSaveWorkflowPendingChangesMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveWorkflowPendingChanges>>, TError,{workspaceId: string;workflowId: string;data: WorkflowPendingChangesSave}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveWorkflowPendingChanges>>, TError,{workspaceId: string;workflowId: string;data: WorkflowPendingChangesSave}, TContext> => {

const mutationKey = ['saveWorkflowPendingChanges'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveWorkflowPendingChanges>>, {workspaceId: string;workflowId: string;data: WorkflowPendingChangesSave}> = (props) => {
          const {workspaceId,workflowId,data} = props ?? {};

          return  saveWorkflowPendingChanges(workspaceId,workflowId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveWorkflowPendingChangesMutationResult = NonNullable<Awaited<ReturnType<typeof saveWorkflowPendingChanges>>>
    export type SaveWorkflowPendingChangesMutationBody = WorkflowPendingChangesSave
    export type SaveWorkflowPendingChangesMutationError = Error | ImportRejection

    /**
 * @summary Save Workflow Pending Changes
 */
export const useSaveWorkflowPendingChanges = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveWorkflowPendingChanges>>, TError,{workspaceId: string;workflowId: string;data: WorkflowPendingChangesSave}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof saveWorkflowPendingChanges>>,
        TError,
        {workspaceId: string;workflowId: string;data: WorkflowPendingChangesSave},
        TContext
      > => {
      return useMutation(getSaveWorkflowPendingChangesMutationOptions(options), queryClient);
    }

export type discardWorkflowPendingChangesResponse204 = {
  data: void
  status: 204
}

export type discardWorkflowPendingChangesResponse401 = {
  data: Error
  status: 401
}

export type discardWorkflowPendingChangesResponse403 = {
  data: Error
  status: 403
}

export type discardWorkflowPendingChangesResponse404 = {
  data: Error
  status: 404
}

export type discardWorkflowPendingChangesResponse409 = {
  data: Error
  status: 409
}

export type discardWorkflowPendingChangesResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type discardWorkflowPendingChangesResponseSuccess = (discardWorkflowPendingChangesResponse204) & {
  headers: Headers;
};
export type discardWorkflowPendingChangesResponseError = (discardWorkflowPendingChangesResponse401 | discardWorkflowPendingChangesResponse403 | discardWorkflowPendingChangesResponse404 | discardWorkflowPendingChangesResponse409 | discardWorkflowPendingChangesResponse422) & {
  headers: Headers;
};

export type discardWorkflowPendingChangesResponse = (discardWorkflowPendingChangesResponseSuccess | discardWorkflowPendingChangesResponseError)

export const getDiscardWorkflowPendingChangesUrl = (workspaceId: string,
    workflowId: string,
    params: DiscardWorkflowPendingChangesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/workflows/${workflowId}/pending-changes?${stringifiedParams}` : `/api/workspaces/${workspaceId}/workflows/${workflowId}/pending-changes`
}

/**
 * @summary Discard Workflow Pending Changes
 */
export const discardWorkflowPendingChanges = async (workspaceId: string,
    workflowId: string,
    params: DiscardWorkflowPendingChangesParams, options?: RequestInit): Promise<discardWorkflowPendingChangesResponse> => {

  return customFetch<discardWorkflowPendingChangesResponse>(getDiscardWorkflowPendingChangesUrl(workspaceId,workflowId,params),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDiscardWorkflowPendingChangesMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof discardWorkflowPendingChanges>>, TError,{workspaceId: string;workflowId: string;params: DiscardWorkflowPendingChangesParams}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof discardWorkflowPendingChanges>>, TError,{workspaceId: string;workflowId: string;params: DiscardWorkflowPendingChangesParams}, TContext> => {

const mutationKey = ['discardWorkflowPendingChanges'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof discardWorkflowPendingChanges>>, {workspaceId: string;workflowId: string;params: DiscardWorkflowPendingChangesParams}> = (props) => {
          const {workspaceId,workflowId,params} = props ?? {};

          return  discardWorkflowPendingChanges(workspaceId,workflowId,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DiscardWorkflowPendingChangesMutationResult = NonNullable<Awaited<ReturnType<typeof discardWorkflowPendingChanges>>>

    export type DiscardWorkflowPendingChangesMutationError = Error | HTTPValidationError

    /**
 * @summary Discard Workflow Pending Changes
 */
export const useDiscardWorkflowPendingChanges = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof discardWorkflowPendingChanges>>, TError,{workspaceId: string;workflowId: string;params: DiscardWorkflowPendingChangesParams}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof discardWorkflowPendingChanges>>,
        TError,
        {workspaceId: string;workflowId: string;params: DiscardWorkflowPendingChangesParams},
        TContext
      > => {
      return useMutation(getDiscardWorkflowPendingChangesMutationOptions(options), queryClient);
    }

export type publishWorkflowPendingChangesResponse200 = {
  data: WorkflowView
  status: 200
}

export type publishWorkflowPendingChangesResponse401 = {
  data: Error
  status: 401
}

export type publishWorkflowPendingChangesResponse403 = {
  data: Error
  status: 403
}

export type publishWorkflowPendingChangesResponse404 = {
  data: Error
  status: 404
}

export type publishWorkflowPendingChangesResponse409 = {
  data: ImportRejection
  status: 409
}

export type publishWorkflowPendingChangesResponse422 = {
  data: ImportRejection
  status: 422
}

export type publishWorkflowPendingChangesResponseSuccess = (publishWorkflowPendingChangesResponse200) & {
  headers: Headers;
};
export type publishWorkflowPendingChangesResponseError = (publishWorkflowPendingChangesResponse401 | publishWorkflowPendingChangesResponse403 | publishWorkflowPendingChangesResponse404 | publishWorkflowPendingChangesResponse409 | publishWorkflowPendingChangesResponse422) & {
  headers: Headers;
};

export type publishWorkflowPendingChangesResponse = (publishWorkflowPendingChangesResponseSuccess | publishWorkflowPendingChangesResponseError)

export const getPublishWorkflowPendingChangesUrl = (workspaceId: string,
    workflowId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/${workflowId}/pending-changes/publish`
}

/**
 * @summary Publish Workflow Pending Changes
 */
export const publishWorkflowPendingChanges = async (workspaceId: string,
    workflowId: string,
    workflowPendingChangesPublish: WorkflowPendingChangesPublish, options?: RequestInit): Promise<publishWorkflowPendingChangesResponse> => {

  return customFetch<publishWorkflowPendingChangesResponse>(getPublishWorkflowPendingChangesUrl(workspaceId,workflowId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(workflowPendingChangesPublish)
  }
);}




export const getPublishWorkflowPendingChangesMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publishWorkflowPendingChanges>>, TError,{workspaceId: string;workflowId: string;data: WorkflowPendingChangesPublish}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof publishWorkflowPendingChanges>>, TError,{workspaceId: string;workflowId: string;data: WorkflowPendingChangesPublish}, TContext> => {

const mutationKey = ['publishWorkflowPendingChanges'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof publishWorkflowPendingChanges>>, {workspaceId: string;workflowId: string;data: WorkflowPendingChangesPublish}> = (props) => {
          const {workspaceId,workflowId,data} = props ?? {};

          return  publishWorkflowPendingChanges(workspaceId,workflowId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PublishWorkflowPendingChangesMutationResult = NonNullable<Awaited<ReturnType<typeof publishWorkflowPendingChanges>>>
    export type PublishWorkflowPendingChangesMutationBody = WorkflowPendingChangesPublish
    export type PublishWorkflowPendingChangesMutationError = Error | ImportRejection

    /**
 * @summary Publish Workflow Pending Changes
 */
export const usePublishWorkflowPendingChanges = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publishWorkflowPendingChanges>>, TError,{workspaceId: string;workflowId: string;data: WorkflowPendingChangesPublish}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof publishWorkflowPendingChanges>>,
        TError,
        {workspaceId: string;workflowId: string;data: WorkflowPendingChangesPublish},
        TContext
      > => {
      return useMutation(getPublishWorkflowPendingChangesMutationOptions(options), queryClient);
    }

export type importWorkflowResponse201 = {
  data: WorkflowImportResult
  status: 201
}

export type importWorkflowResponse401 = {
  data: Error
  status: 401
}

export type importWorkflowResponse403 = {
  data: Error
  status: 403
}

export type importWorkflowResponse404 = {
  data: Error
  status: 404
}

export type importWorkflowResponse409 = {
  data: ImportRejection
  status: 409
}

export type importWorkflowResponse413 = {
  data: ImportRejection
  status: 413
}

export type importWorkflowResponse422 = {
  data: ImportRejection
  status: 422
}

export type importWorkflowResponseSuccess = (importWorkflowResponse201) & {
  headers: Headers;
};
export type importWorkflowResponseError = (importWorkflowResponse401 | importWorkflowResponse403 | importWorkflowResponse404 | importWorkflowResponse409 | importWorkflowResponse413 | importWorkflowResponse422) & {
  headers: Headers;
};

export type importWorkflowResponse = (importWorkflowResponseSuccess | importWorkflowResponseError)

export const getImportWorkflowUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/import`
}

/**
 * Import one workflow, optionally saying what to use instead (spec 098 US2).
 *
 * ``substitutions`` is a JSON document in a form field rather than a request
 * body, because the file has to travel as multipart and a multipart part is a
 * flat scalar — a map cannot be expressed any other way without inventing an
 * encoding a client has to be told about (research R1). Omitting it is the
 * ordinary case and must behave exactly as this door did before it existed.
 * @summary Import Workflow
 */
export const importWorkflow = async (workspaceId: string,
    bodyImportWorkflow: BodyImportWorkflow, options?: RequestInit): Promise<importWorkflowResponse> => {
    const formData = new FormData();
formData.append(`file`, bodyImportWorkflow.file);
if(bodyImportWorkflow.substitutions !== undefined && bodyImportWorkflow.substitutions !== null) {
 formData.append(`substitutions`, bodyImportWorkflow.substitutions);
 }

  return customFetch<importWorkflowResponse>(getImportWorkflowUrl(workspaceId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}




export const getImportWorkflowMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importWorkflow>>, TError,{workspaceId: string;data: BodyImportWorkflow}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof importWorkflow>>, TError,{workspaceId: string;data: BodyImportWorkflow}, TContext> => {

const mutationKey = ['importWorkflow'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importWorkflow>>, {workspaceId: string;data: BodyImportWorkflow}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  importWorkflow(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportWorkflowMutationResult = NonNullable<Awaited<ReturnType<typeof importWorkflow>>>
    export type ImportWorkflowMutationBody = BodyImportWorkflow
    export type ImportWorkflowMutationError = Error | ImportRejection

    /**
 * @summary Import Workflow
 */
export const useImportWorkflow = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importWorkflow>>, TError,{workspaceId: string;data: BodyImportWorkflow}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importWorkflow>>,
        TError,
        {workspaceId: string;data: BodyImportWorkflow},
        TContext
      > => {
      return useMutation(getImportWorkflowMutationOptions(options), queryClient);
    }

export type preflightWorkflowResponse200 = {
  data: ImportPreflight
  status: 200
}

export type preflightWorkflowResponse401 = {
  data: Error
  status: 401
}

export type preflightWorkflowResponse403 = {
  data: Error
  status: 403
}

export type preflightWorkflowResponse404 = {
  data: Error
  status: 404
}

export type preflightWorkflowResponse413 = {
  data: ImportRejection
  status: 413
}

export type preflightWorkflowResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type preflightWorkflowResponseSuccess = (preflightWorkflowResponse200) & {
  headers: Headers;
};
export type preflightWorkflowResponseError = (preflightWorkflowResponse401 | preflightWorkflowResponse403 | preflightWorkflowResponse404 | preflightWorkflowResponse413 | preflightWorkflowResponse422) & {
  headers: Headers;
};

export type preflightWorkflowResponse = (preflightWorkflowResponseSuccess | preflightWorkflowResponseError)

export const getPreflightWorkflowUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/import/preflight`
}

/**
 * What this file would do here, without doing it (spec 098 US1).
 *
 * 200 for any file that could be read from the request, however broken its
 * contents: the report is the answer, and refusing to describe a bad file is
 * the dead end this door exists to remove. Only the request itself can fail —
 * past the byte limit, or a workspace this caller cannot import into.
 * @summary Preflight Workflow
 */
export const preflightWorkflow = async (workspaceId: string,
    bodyPreflightWorkflow: BodyPreflightWorkflow, options?: RequestInit): Promise<preflightWorkflowResponse> => {
    const formData = new FormData();
formData.append(`file`, bodyPreflightWorkflow.file);

  return customFetch<preflightWorkflowResponse>(getPreflightWorkflowUrl(workspaceId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}




export const getPreflightWorkflowMutationOptions = <TError = Error | ImportRejection | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof preflightWorkflow>>, TError,{workspaceId: string;data: BodyPreflightWorkflow}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof preflightWorkflow>>, TError,{workspaceId: string;data: BodyPreflightWorkflow}, TContext> => {

const mutationKey = ['preflightWorkflow'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof preflightWorkflow>>, {workspaceId: string;data: BodyPreflightWorkflow}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  preflightWorkflow(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreflightWorkflowMutationResult = NonNullable<Awaited<ReturnType<typeof preflightWorkflow>>>
    export type PreflightWorkflowMutationBody = BodyPreflightWorkflow
    export type PreflightWorkflowMutationError = Error | ImportRejection | HTTPValidationError

    /**
 * @summary Preflight Workflow
 */
export const usePreflightWorkflow = <TError = Error | ImportRejection | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof preflightWorkflow>>, TError,{workspaceId: string;data: BodyPreflightWorkflow}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof preflightWorkflow>>,
        TError,
        {workspaceId: string;data: BodyPreflightWorkflow},
        TContext
      > => {
      return useMutation(getPreflightWorkflowMutationOptions(options), queryClient);
    }

export type createTaskResponse201 = {
  data: TaskView
  status: 201
}

export type createTaskResponse401 = {
  data: Error
  status: 401
}

export type createTaskResponse403 = {
  data: Error
  status: 403
}

export type createTaskResponse404 = {
  data: Error
  status: 404
}

export type createTaskResponse422 = {
  data: Error
  status: 422
}

export type createTaskResponseSuccess = (createTaskResponse201) & {
  headers: Headers;
};
export type createTaskResponseError = (createTaskResponse401 | createTaskResponse403 | createTaskResponse404 | createTaskResponse422) & {
  headers: Headers;
};

export type createTaskResponse = (createTaskResponseSuccess | createTaskResponseError)

export const getCreateTaskUrl = (workspaceId: string,
    workflowId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/${workflowId}/tasks`
}

/**
 * @summary Create Task
 */
export const createTask = async (workspaceId: string,
    workflowId: string,
    taskCreate: TaskCreate, options?: RequestInit): Promise<createTaskResponse> => {

  return customFetch<createTaskResponse>(getCreateTaskUrl(workspaceId,workflowId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(taskCreate)
  }
);}




export const getCreateTaskMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTask>>, TError,{workspaceId: string;workflowId: string;data: TaskCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTask>>, TError,{workspaceId: string;workflowId: string;data: TaskCreate}, TContext> => {

const mutationKey = ['createTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTask>>, {workspaceId: string;workflowId: string;data: TaskCreate}> = (props) => {
          const {workspaceId,workflowId,data} = props ?? {};

          return  createTask(workspaceId,workflowId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTaskMutationResult = NonNullable<Awaited<ReturnType<typeof createTask>>>
    export type CreateTaskMutationBody = TaskCreate
    export type CreateTaskMutationError = Error

    /**
 * @summary Create Task
 */
export const useCreateTask = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTask>>, TError,{workspaceId: string;workflowId: string;data: TaskCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createTask>>,
        TError,
        {workspaceId: string;workflowId: string;data: TaskCreate},
        TContext
      > => {
      return useMutation(getCreateTaskMutationOptions(options), queryClient);
    }

export type stopTaskResponse200 = {
  data: unknown
  status: 200
}

export type stopTaskResponse401 = {
  data: Error
  status: 401
}

export type stopTaskResponse403 = {
  data: Error
  status: 403
}

export type stopTaskResponse404 = {
  data: Error
  status: 404
}

export type stopTaskResponse409 = {
  data: Error
  status: 409
}

export type stopTaskResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type stopTaskResponse504 = {
  data: Error
  status: 504
}

export type stopTaskResponseSuccess = (stopTaskResponse200) & {
  headers: Headers;
};
export type stopTaskResponseError = (stopTaskResponse401 | stopTaskResponse403 | stopTaskResponse404 | stopTaskResponse409 | stopTaskResponse422 | stopTaskResponse504) & {
  headers: Headers;
};

export type stopTaskResponse = (stopTaskResponseSuccess | stopTaskResponseError)

export const getStopTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/stop`
}

/**
 * Interrupt the task's running execution, and wait until it has stopped (078 US1).
 *
 * This request is deliberately slow: it does not answer until the run is confirmed
 * ended, because a stop that reports success while the run continues spends a
 * workspace's budget on the strength of a lie (FR-003). A run that cannot be confirmed
 * stopped answers 504, never 200.
 * @summary Stop Task
 */
export const stopTask = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<stopTaskResponse> => {

  return customFetch<stopTaskResponse>(getStopTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getStopTaskMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof stopTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof stopTask>>, TError,{workspaceId: string;taskId: string}, TContext> => {

const mutationKey = ['stopTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof stopTask>>, {workspaceId: string;taskId: string}> = (props) => {
          const {workspaceId,taskId} = props ?? {};

          return  stopTask(workspaceId,taskId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StopTaskMutationResult = NonNullable<Awaited<ReturnType<typeof stopTask>>>

    export type StopTaskMutationError = Error | HTTPValidationError

    /**
 * @summary Stop Task
 */
export const useStopTask = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof stopTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof stopTask>>,
        TError,
        {workspaceId: string;taskId: string},
        TContext
      > => {
      return useMutation(getStopTaskMutationOptions(options), queryClient);
    }

export type rerunTaskResponse202 = {
  data: unknown
  status: 202
}

export type rerunTaskResponse401 = {
  data: Error
  status: 401
}

export type rerunTaskResponse403 = {
  data: Error
  status: 403
}

export type rerunTaskResponse404 = {
  data: Error
  status: 404
}

export type rerunTaskResponse409 = {
  data: Error
  status: 409
}

export type rerunTaskResponse422 = {
  data: void
  status: 422
}

export type rerunTaskResponse504 = {
  data: Error
  status: 504
}

export type rerunTaskResponseSuccess = (rerunTaskResponse202) & {
  headers: Headers;
};
export type rerunTaskResponseError = (rerunTaskResponse401 | rerunTaskResponse403 | rerunTaskResponse404 | rerunTaskResponse409 | rerunTaskResponse422 | rerunTaskResponse504) & {
  headers: Headers;
};

export type rerunTaskResponse = (rerunTaskResponseSuccess | rerunTaskResponseError)

export const getRerunTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/rerun`
}

/**
 * Run the task's most recent execution again, on the same step (078 US2).
 *
 * 202 rather than 200: the run is dispatched, not finished. Dispatch happens after the
 * new execution is committed, matching every other dispatch here, so a rerun that fails
 * to start leaves no half-created execution behind.
 * @summary Rerun Task
 */
export const rerunTask = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<rerunTaskResponse> => {

  return customFetch<rerunTaskResponse>(getRerunTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getRerunTaskMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rerunTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rerunTask>>, TError,{workspaceId: string;taskId: string}, TContext> => {

const mutationKey = ['rerunTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rerunTask>>, {workspaceId: string;taskId: string}> = (props) => {
          const {workspaceId,taskId} = props ?? {};

          return  rerunTask(workspaceId,taskId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RerunTaskMutationResult = NonNullable<Awaited<ReturnType<typeof rerunTask>>>

    export type RerunTaskMutationError = Error | void

    /**
 * @summary Rerun Task
 */
export const useRerunTask = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rerunTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof rerunTask>>,
        TError,
        {workspaceId: string;taskId: string},
        TContext
      > => {
      return useMutation(getRerunTaskMutationOptions(options), queryClient);
    }

export type hideTaskResponse200 = {
  data: unknown
  status: 200
}

export type hideTaskResponse401 = {
  data: Error
  status: 401
}

export type hideTaskResponse403 = {
  data: Error
  status: 403
}

export type hideTaskResponse404 = {
  data: Error
  status: 404
}

export type hideTaskResponse409 = {
  data: Error
  status: 409
}

export type hideTaskResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type hideTaskResponse504 = {
  data: Error
  status: 504
}

export type hideTaskResponseSuccess = (hideTaskResponse200) & {
  headers: Headers;
};
export type hideTaskResponseError = (hideTaskResponse401 | hideTaskResponse403 | hideTaskResponse404 | hideTaskResponse409 | hideTaskResponse422 | hideTaskResponse504) & {
  headers: Headers;
};

export type hideTaskResponse = (hideTaskResponseSuccess | hideTaskResponseError)

export const getHideTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/hide`
}

/**
 * Remove a task from the board without deleting anything (078 US3).
 *
 * Refused while a run is in flight, so a task cannot go on spending a workspace's
 * budget with nothing on the board to explain it (FR-015). Nothing is deleted: the
 * task's runs, files and consumption records are untouched, and no spend figure moves.
 * @summary Hide Task
 */
export const hideTask = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<hideTaskResponse> => {

  return customFetch<hideTaskResponse>(getHideTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getHideTaskMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof hideTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof hideTask>>, TError,{workspaceId: string;taskId: string}, TContext> => {

const mutationKey = ['hideTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof hideTask>>, {workspaceId: string;taskId: string}> = (props) => {
          const {workspaceId,taskId} = props ?? {};

          return  hideTask(workspaceId,taskId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type HideTaskMutationResult = NonNullable<Awaited<ReturnType<typeof hideTask>>>

    export type HideTaskMutationError = Error | HTTPValidationError

    /**
 * @summary Hide Task
 */
export const useHideTask = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof hideTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof hideTask>>,
        TError,
        {workspaceId: string;taskId: string},
        TContext
      > => {
      return useMutation(getHideTaskMutationOptions(options), queryClient);
    }

export type unhideTaskResponse200 = {
  data: unknown
  status: 200
}

export type unhideTaskResponse401 = {
  data: Error
  status: 401
}

export type unhideTaskResponse403 = {
  data: Error
  status: 403
}

export type unhideTaskResponse404 = {
  data: Error
  status: 404
}

export type unhideTaskResponse409 = {
  data: Error
  status: 409
}

export type unhideTaskResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type unhideTaskResponse504 = {
  data: Error
  status: 504
}

export type unhideTaskResponseSuccess = (unhideTaskResponse200) & {
  headers: Headers;
};
export type unhideTaskResponseError = (unhideTaskResponse401 | unhideTaskResponse403 | unhideTaskResponse404 | unhideTaskResponse409 | unhideTaskResponse422 | unhideTaskResponse504) & {
  headers: Headers;
};

export type unhideTaskResponse = (unhideTaskResponseSuccess | unhideTaskResponseError)

export const getUnhideTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/unhide`
}

/**
 * Bring a hidden task back to the board (078 FR-017).
 *
 * Never refused. There is no state in which restoring visibility is the wrong thing.
 * @summary Unhide Task
 */
export const unhideTask = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<unhideTaskResponse> => {

  return customFetch<unhideTaskResponse>(getUnhideTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getUnhideTaskMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unhideTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unhideTask>>, TError,{workspaceId: string;taskId: string}, TContext> => {

const mutationKey = ['unhideTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unhideTask>>, {workspaceId: string;taskId: string}> = (props) => {
          const {workspaceId,taskId} = props ?? {};

          return  unhideTask(workspaceId,taskId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnhideTaskMutationResult = NonNullable<Awaited<ReturnType<typeof unhideTask>>>

    export type UnhideTaskMutationError = Error | HTTPValidationError

    /**
 * @summary Unhide Task
 */
export const useUnhideTask = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unhideTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof unhideTask>>,
        TError,
        {workspaceId: string;taskId: string},
        TContext
      > => {
      return useMutation(getUnhideTaskMutationOptions(options), queryClient);
    }

export type moveTaskResponse200 = {
  data: MoveTaskResponse
  status: 200
}

export type moveTaskResponse401 = {
  data: Error
  status: 401
}

export type moveTaskResponse403 = {
  data: Error
  status: 403
}

export type moveTaskResponse404 = {
  data: Error
  status: 404
}

export type moveTaskResponse409 = {
  data: Error
  status: 409
}

export type moveTaskResponse422 = {
  data: Error
  status: 422
}

export type moveTaskResponseSuccess = (moveTaskResponse200) & {
  headers: Headers;
};
export type moveTaskResponseError = (moveTaskResponse401 | moveTaskResponse403 | moveTaskResponse404 | moveTaskResponse409 | moveTaskResponse422) & {
  headers: Headers;
};

export type moveTaskResponse = (moveTaskResponseSuccess | moveTaskResponseError)

export const getMoveTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/move`
}

/**
 * @summary Move Task
 */
export const moveTask = async (workspaceId: string,
    taskId: string,
    moveTaskRequest: MoveTaskRequest, options?: RequestInit): Promise<moveTaskResponse> => {

  return customFetch<moveTaskResponse>(getMoveTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(moveTaskRequest)
  }
);}




export const getMoveTaskMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof moveTask>>, TError,{workspaceId: string;taskId: string;data: MoveTaskRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof moveTask>>, TError,{workspaceId: string;taskId: string;data: MoveTaskRequest}, TContext> => {

const mutationKey = ['moveTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof moveTask>>, {workspaceId: string;taskId: string;data: MoveTaskRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  moveTask(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MoveTaskMutationResult = NonNullable<Awaited<ReturnType<typeof moveTask>>>
    export type MoveTaskMutationBody = MoveTaskRequest
    export type MoveTaskMutationError = Error

    /**
 * @summary Move Task
 */
export const useMoveTask = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof moveTask>>, TError,{workspaceId: string;taskId: string;data: MoveTaskRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof moveTask>>,
        TError,
        {workspaceId: string;taskId: string;data: MoveTaskRequest},
        TContext
      > => {
      return useMutation(getMoveTaskMutationOptions(options), queryClient);
    }

export type startLinkedTasksResponse200 = {
  data: StartLinkedTasksResponse
  status: 200
}

export type startLinkedTasksResponse401 = {
  data: Error
  status: 401
}

export type startLinkedTasksResponse403 = {
  data: Error
  status: 403
}

export type startLinkedTasksResponse404 = {
  data: Error
  status: 404
}

export type startLinkedTasksResponse409 = {
  data: Error
  status: 409
}

export type startLinkedTasksResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type startLinkedTasksResponseSuccess = (startLinkedTasksResponse200) & {
  headers: Headers;
};
export type startLinkedTasksResponseError = (startLinkedTasksResponse401 | startLinkedTasksResponse403 | startLinkedTasksResponse404 | startLinkedTasksResponse409 | startLinkedTasksResponse422) & {
  headers: Headers;
};

export type startLinkedTasksResponse = (startLinkedTasksResponseSuccess | startLinkedTasksResponseError)

export const getStartLinkedTasksUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/linked-tasks/start`
}

/**
 * Start every task this task's sub-workflow step created that still waits in
 * Backlog, each as the caller's own Start would (spec 125 US3).
 *
 * Each start is its own move and its own transaction, so one refused (a spend gate,
 * a step with no model) leaves the others started; the answer names which. Nothing
 * waiting is an empty answer, not an error: a second click must be harmless.
 * @summary Start Linked Tasks
 */
export const startLinkedTasks = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<startLinkedTasksResponse> => {

  return customFetch<startLinkedTasksResponse>(getStartLinkedTasksUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getStartLinkedTasksMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startLinkedTasks>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startLinkedTasks>>, TError,{workspaceId: string;taskId: string}, TContext> => {

const mutationKey = ['startLinkedTasks'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startLinkedTasks>>, {workspaceId: string;taskId: string}> = (props) => {
          const {workspaceId,taskId} = props ?? {};

          return  startLinkedTasks(workspaceId,taskId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartLinkedTasksMutationResult = NonNullable<Awaited<ReturnType<typeof startLinkedTasks>>>

    export type StartLinkedTasksMutationError = Error | HTTPValidationError

    /**
 * @summary Start Linked Tasks
 */
export const useStartLinkedTasks = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startLinkedTasks>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof startLinkedTasks>>,
        TError,
        {workspaceId: string;taskId: string},
        TContext
      > => {
      return useMutation(getStartLinkedTasksMutationOptions(options), queryClient);
    }

export type splitTaskResponse201 = {
  data: SplitTaskResponse
  status: 201
}

export type splitTaskResponse401 = {
  data: Error
  status: 401
}

export type splitTaskResponse403 = {
  data: Error
  status: 403
}

export type splitTaskResponse404 = {
  data: Error
  status: 404
}

export type splitTaskResponse409 = {
  data: Error
  status: 409
}

export type splitTaskResponse422 = {
  data: Error
  status: 422
}

export type splitTaskResponseSuccess = (splitTaskResponse201) & {
  headers: Headers;
};
export type splitTaskResponseError = (splitTaskResponse401 | splitTaskResponse403 | splitTaskResponse404 | splitTaskResponse409 | splitTaskResponse422) & {
  headers: Headers;
};

export type splitTaskResponse = (splitTaskResponseSuccess | splitTaskResponseError)

export const getSplitTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/split`
}

/**
 * Move some of a waiting task's subtasks to a new task beside it (spec 119 US6).
 *
 * Only the waiting step's batch can be split (spec 132): a subtask an earlier
 * sub-workflow step created is refused as not a child.
 * @summary Split Task
 */
export const splitTask = async (workspaceId: string,
    taskId: string,
    splitTaskRequest: SplitTaskRequest, options?: RequestInit): Promise<splitTaskResponse> => {

  return customFetch<splitTaskResponse>(getSplitTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(splitTaskRequest)
  }
);}




export const getSplitTaskMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof splitTask>>, TError,{workspaceId: string;taskId: string;data: SplitTaskRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof splitTask>>, TError,{workspaceId: string;taskId: string;data: SplitTaskRequest}, TContext> => {

const mutationKey = ['splitTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof splitTask>>, {workspaceId: string;taskId: string;data: SplitTaskRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  splitTask(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SplitTaskMutationResult = NonNullable<Awaited<ReturnType<typeof splitTask>>>
    export type SplitTaskMutationBody = SplitTaskRequest
    export type SplitTaskMutationError = Error

    /**
 * @summary Split Task
 */
export const useSplitTask = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof splitTask>>, TError,{workspaceId: string;taskId: string;data: SplitTaskRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof splitTask>>,
        TError,
        {workspaceId: string;taskId: string;data: SplitTaskRequest},
        TContext
      > => {
      return useMutation(getSplitTaskMutationOptions(options), queryClient);
    }

export type setTaskStepAssigneeResponse200 = {
  data: TaskView
  status: 200
}

export type setTaskStepAssigneeResponse401 = {
  data: Error
  status: 401
}

export type setTaskStepAssigneeResponse403 = {
  data: Error
  status: 403
}

export type setTaskStepAssigneeResponse404 = {
  data: Error
  status: 404
}

export type setTaskStepAssigneeResponse409 = {
  data: Error
  status: 409
}

export type setTaskStepAssigneeResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type setTaskStepAssigneeResponseSuccess = (setTaskStepAssigneeResponse200) & {
  headers: Headers;
};
export type setTaskStepAssigneeResponseError = (setTaskStepAssigneeResponse401 | setTaskStepAssigneeResponse403 | setTaskStepAssigneeResponse404 | setTaskStepAssigneeResponse409 | setTaskStepAssigneeResponse422) & {
  headers: Headers;
};

export type setTaskStepAssigneeResponse = (setTaskStepAssigneeResponseSuccess | setTaskStepAssigneeResponseError)

export const getSetTaskStepAssigneeUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/step-assignee`
}

/**
 * Route one gate on this task to a person, leaving the workflow untouched.
 * @summary Set Task Step Assignee
 */
export const setTaskStepAssignee = async (workspaceId: string,
    taskId: string,
    setStepAssigneeRequest: SetStepAssigneeRequest, options?: RequestInit): Promise<setTaskStepAssigneeResponse> => {

  return customFetch<setTaskStepAssigneeResponse>(getSetTaskStepAssigneeUrl(workspaceId,taskId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setStepAssigneeRequest)
  }
);}




export const getSetTaskStepAssigneeMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setTaskStepAssignee>>, TError,{workspaceId: string;taskId: string;data: SetStepAssigneeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setTaskStepAssignee>>, TError,{workspaceId: string;taskId: string;data: SetStepAssigneeRequest}, TContext> => {

const mutationKey = ['setTaskStepAssignee'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setTaskStepAssignee>>, {workspaceId: string;taskId: string;data: SetStepAssigneeRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  setTaskStepAssignee(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetTaskStepAssigneeMutationResult = NonNullable<Awaited<ReturnType<typeof setTaskStepAssignee>>>
    export type SetTaskStepAssigneeMutationBody = SetStepAssigneeRequest
    export type SetTaskStepAssigneeMutationError = Error | HTTPValidationError

    /**
 * @summary Set Task Step Assignee
 */
export const useSetTaskStepAssignee = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setTaskStepAssignee>>, TError,{workspaceId: string;taskId: string;data: SetStepAssigneeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setTaskStepAssignee>>,
        TError,
        {workspaceId: string;taskId: string;data: SetStepAssigneeRequest},
        TContext
      > => {
      return useMutation(getSetTaskStepAssigneeMutationOptions(options), queryClient);
    }

export type clearTaskStepAssigneeResponse200 = {
  data: TaskView
  status: 200
}

export type clearTaskStepAssigneeResponse401 = {
  data: Error
  status: 401
}

export type clearTaskStepAssigneeResponse403 = {
  data: Error
  status: 403
}

export type clearTaskStepAssigneeResponse404 = {
  data: Error
  status: 404
}

export type clearTaskStepAssigneeResponse409 = {
  data: Error
  status: 409
}

export type clearTaskStepAssigneeResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type clearTaskStepAssigneeResponseSuccess = (clearTaskStepAssigneeResponse200) & {
  headers: Headers;
};
export type clearTaskStepAssigneeResponseError = (clearTaskStepAssigneeResponse401 | clearTaskStepAssigneeResponse403 | clearTaskStepAssigneeResponse404 | clearTaskStepAssigneeResponse409 | clearTaskStepAssigneeResponse422) & {
  headers: Headers;
};

export type clearTaskStepAssigneeResponse = (clearTaskStepAssigneeResponseSuccess | clearTaskStepAssigneeResponseError)

export const getClearTaskStepAssigneeUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/step-assignee`
}

/**
 * Return one gate on this task to the workflow's assignee. Idempotent.
 * @summary Clear Task Step Assignee
 */
export const clearTaskStepAssignee = async (workspaceId: string,
    taskId: string,
    clearStepAssigneeRequest: ClearStepAssigneeRequest, options?: RequestInit): Promise<clearTaskStepAssigneeResponse> => {

  return customFetch<clearTaskStepAssigneeResponse>(getClearTaskStepAssigneeUrl(workspaceId,taskId),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(clearStepAssigneeRequest)
  }
);}




export const getClearTaskStepAssigneeMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearTaskStepAssignee>>, TError,{workspaceId: string;taskId: string;data: ClearStepAssigneeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearTaskStepAssignee>>, TError,{workspaceId: string;taskId: string;data: ClearStepAssigneeRequest}, TContext> => {

const mutationKey = ['clearTaskStepAssignee'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearTaskStepAssignee>>, {workspaceId: string;taskId: string;data: ClearStepAssigneeRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  clearTaskStepAssignee(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearTaskStepAssigneeMutationResult = NonNullable<Awaited<ReturnType<typeof clearTaskStepAssignee>>>
    export type ClearTaskStepAssigneeMutationBody = ClearStepAssigneeRequest
    export type ClearTaskStepAssigneeMutationError = Error | HTTPValidationError

    /**
 * @summary Clear Task Step Assignee
 */
export const useClearTaskStepAssignee = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearTaskStepAssignee>>, TError,{workspaceId: string;taskId: string;data: ClearStepAssigneeRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clearTaskStepAssignee>>,
        TError,
        {workspaceId: string;taskId: string;data: ClearStepAssigneeRequest},
        TContext
      > => {
      return useMutation(getClearTaskStepAssigneeMutationOptions(options), queryClient);
    }

export type renameTaskResponse200 = {
  data: TaskView
  status: 200
}

export type renameTaskResponse401 = {
  data: Error
  status: 401
}

export type renameTaskResponse403 = {
  data: Error
  status: 403
}

export type renameTaskResponse404 = {
  data: Error
  status: 404
}

export type renameTaskResponse422 = {
  data: Error
  status: 422
}

export type renameTaskResponseSuccess = (renameTaskResponse200) & {
  headers: Headers;
};
export type renameTaskResponseError = (renameTaskResponse401 | renameTaskResponse403 | renameTaskResponse404 | renameTaskResponse422) & {
  headers: Headers;
};

export type renameTaskResponse = (renameTaskResponseSuccess | renameTaskResponseError)

export const getRenameTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/title`
}

/**
 * Change a task's title.
 *
 * Its own route rather than a field of a partial task ``PATCH``: the board edits
 * a title and a description from two separate controls that never fire together,
 * and one body carrying both would have to tell an omitted field from a null one
 * to say "clear the description" — a distinction nothing else here draws.
 * @summary Rename Task
 */
export const renameTask = async (workspaceId: string,
    taskId: string,
    renameTaskRequest: RenameTaskRequest, options?: RequestInit): Promise<renameTaskResponse> => {

  return customFetch<renameTaskResponse>(getRenameTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(renameTaskRequest)
  }
);}




export const getRenameTaskMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renameTask>>, TError,{workspaceId: string;taskId: string;data: RenameTaskRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof renameTask>>, TError,{workspaceId: string;taskId: string;data: RenameTaskRequest}, TContext> => {

const mutationKey = ['renameTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof renameTask>>, {workspaceId: string;taskId: string;data: RenameTaskRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  renameTask(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RenameTaskMutationResult = NonNullable<Awaited<ReturnType<typeof renameTask>>>
    export type RenameTaskMutationBody = RenameTaskRequest
    export type RenameTaskMutationError = Error

    /**
 * @summary Rename Task
 */
export const useRenameTask = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renameTask>>, TError,{workspaceId: string;taskId: string;data: RenameTaskRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof renameTask>>,
        TError,
        {workspaceId: string;taskId: string;data: RenameTaskRequest},
        TContext
      > => {
      return useMutation(getRenameTaskMutationOptions(options), queryClient);
    }

export type setTaskDescriptionResponse200 = {
  data: TaskView
  status: 200
}

export type setTaskDescriptionResponse401 = {
  data: Error
  status: 401
}

export type setTaskDescriptionResponse403 = {
  data: Error
  status: 403
}

export type setTaskDescriptionResponse404 = {
  data: Error
  status: 404
}

export type setTaskDescriptionResponse422 = {
  data: Error
  status: 422
}

export type setTaskDescriptionResponseSuccess = (setTaskDescriptionResponse200) & {
  headers: Headers;
};
export type setTaskDescriptionResponseError = (setTaskDescriptionResponse401 | setTaskDescriptionResponse403 | setTaskDescriptionResponse404 | setTaskDescriptionResponse422) & {
  headers: Headers;
};

export type setTaskDescriptionResponse = (setTaskDescriptionResponseSuccess | setTaskDescriptionResponseError)

export const getSetTaskDescriptionUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/description`
}

/**
 * Replace a task's description, or clear it by sending ``null``.
 *
 * A task in another workspace answers 404, not 403, for the reason
 * :func:`replace_task_labels` gives: this product has no authorization, so
 * "forbidden" would be a claim it cannot make.
 * @summary Set Task Description
 */
export const setTaskDescription = async (workspaceId: string,
    taskId: string,
    setTaskDescriptionRequest: SetTaskDescriptionRequest, options?: RequestInit): Promise<setTaskDescriptionResponse> => {

  return customFetch<setTaskDescriptionResponse>(getSetTaskDescriptionUrl(workspaceId,taskId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setTaskDescriptionRequest)
  }
);}




export const getSetTaskDescriptionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setTaskDescription>>, TError,{workspaceId: string;taskId: string;data: SetTaskDescriptionRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setTaskDescription>>, TError,{workspaceId: string;taskId: string;data: SetTaskDescriptionRequest}, TContext> => {

const mutationKey = ['setTaskDescription'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setTaskDescription>>, {workspaceId: string;taskId: string;data: SetTaskDescriptionRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  setTaskDescription(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetTaskDescriptionMutationResult = NonNullable<Awaited<ReturnType<typeof setTaskDescription>>>
    export type SetTaskDescriptionMutationBody = SetTaskDescriptionRequest
    export type SetTaskDescriptionMutationError = Error

    /**
 * @summary Set Task Description
 */
export const useSetTaskDescription = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setTaskDescription>>, TError,{workspaceId: string;taskId: string;data: SetTaskDescriptionRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setTaskDescription>>,
        TError,
        {workspaceId: string;taskId: string;data: SetTaskDescriptionRequest},
        TContext
      > => {
      return useMutation(getSetTaskDescriptionMutationOptions(options), queryClient);
    }

export type addTaskCommentResponse201 = {
  data: TaskCommentView
  status: 201
}

export type addTaskCommentResponse401 = {
  data: Error
  status: 401
}

export type addTaskCommentResponse403 = {
  data: Error
  status: 403
}

export type addTaskCommentResponse404 = {
  data: Error
  status: 404
}

export type addTaskCommentResponse422 = {
  data: Error
  status: 422
}

export type addTaskCommentResponseSuccess = (addTaskCommentResponse201) & {
  headers: Headers;
};
export type addTaskCommentResponseError = (addTaskCommentResponse401 | addTaskCommentResponse403 | addTaskCommentResponse404 | addTaskCommentResponse422) & {
  headers: Headers;
};

export type addTaskCommentResponse = (addTaskCommentResponseSuccess | addTaskCommentResponseError)

export const getAddTaskCommentUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/comments`
}

/**
 * @summary Add Task Comment
 */
export const addTaskComment = async (workspaceId: string,
    taskId: string,
    createTaskCommentRequest: CreateTaskCommentRequest, options?: RequestInit): Promise<addTaskCommentResponse> => {

  return customFetch<addTaskCommentResponse>(getAddTaskCommentUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createTaskCommentRequest)
  }
);}




export const getAddTaskCommentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addTaskComment>>, TError,{workspaceId: string;taskId: string;data: CreateTaskCommentRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addTaskComment>>, TError,{workspaceId: string;taskId: string;data: CreateTaskCommentRequest}, TContext> => {

const mutationKey = ['addTaskComment'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addTaskComment>>, {workspaceId: string;taskId: string;data: CreateTaskCommentRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  addTaskComment(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddTaskCommentMutationResult = NonNullable<Awaited<ReturnType<typeof addTaskComment>>>
    export type AddTaskCommentMutationBody = CreateTaskCommentRequest
    export type AddTaskCommentMutationError = Error

    /**
 * @summary Add Task Comment
 */
export const useAddTaskComment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addTaskComment>>, TError,{workspaceId: string;taskId: string;data: CreateTaskCommentRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof addTaskComment>>,
        TError,
        {workspaceId: string;taskId: string;data: CreateTaskCommentRequest},
        TContext
      > => {
      return useMutation(getAddTaskCommentMutationOptions(options), queryClient);
    }

export type listTaskCommentsResponse200 = {
  data: TaskCommentList
  status: 200
}

export type listTaskCommentsResponse401 = {
  data: Error
  status: 401
}

export type listTaskCommentsResponse403 = {
  data: Error
  status: 403
}

export type listTaskCommentsResponse404 = {
  data: Error
  status: 404
}

export type listTaskCommentsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listTaskCommentsResponseSuccess = (listTaskCommentsResponse200) & {
  headers: Headers;
};
export type listTaskCommentsResponseError = (listTaskCommentsResponse401 | listTaskCommentsResponse403 | listTaskCommentsResponse404 | listTaskCommentsResponse422) & {
  headers: Headers;
};

export type listTaskCommentsResponse = (listTaskCommentsResponseSuccess | listTaskCommentsResponseError)

export const getListTaskCommentsUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/comments`
}

/**
 * @summary List Task Comments
 */
export const listTaskComments = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<listTaskCommentsResponse> => {

  return customFetch<listTaskCommentsResponse>(getListTaskCommentsUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTaskCommentsQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/comments`
    ] as const;
    }


export const getListTaskCommentsQueryOptions = <TData = Awaited<ReturnType<typeof listTaskComments>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskComments>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTaskCommentsQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTaskComments>>> = ({ signal }) => listTaskComments(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTaskComments>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTaskCommentsQueryResult = NonNullable<Awaited<ReturnType<typeof listTaskComments>>>
export type ListTaskCommentsQueryError = Error | HTTPValidationError


export function useListTaskComments<TData = Awaited<ReturnType<typeof listTaskComments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskComments>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTaskComments>>,
          TError,
          Awaited<ReturnType<typeof listTaskComments>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTaskComments<TData = Awaited<ReturnType<typeof listTaskComments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskComments>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTaskComments>>,
          TError,
          Awaited<ReturnType<typeof listTaskComments>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTaskComments<TData = Awaited<ReturnType<typeof listTaskComments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskComments>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Task Comments
 */

export function useListTaskComments<TData = Awaited<ReturnType<typeof listTaskComments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskComments>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTaskCommentsQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listTaskMovesResponse200 = {
  data: TaskMoveList
  status: 200
}

export type listTaskMovesResponse401 = {
  data: Error
  status: 401
}

export type listTaskMovesResponse403 = {
  data: Error
  status: 403
}

export type listTaskMovesResponse404 = {
  data: Error
  status: 404
}

export type listTaskMovesResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listTaskMovesResponseSuccess = (listTaskMovesResponse200) & {
  headers: Headers;
};
export type listTaskMovesResponseError = (listTaskMovesResponse401 | listTaskMovesResponse403 | listTaskMovesResponse404 | listTaskMovesResponse422) & {
  headers: Headers;
};

export type listTaskMovesResponse = (listTaskMovesResponseSuccess | listTaskMovesResponseError)

export const getListTaskMovesUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/moves`
}

/**
 * @summary List Task Moves
 */
export const listTaskMoves = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<listTaskMovesResponse> => {

  return customFetch<listTaskMovesResponse>(getListTaskMovesUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTaskMovesQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/moves`
    ] as const;
    }


export const getListTaskMovesQueryOptions = <TData = Awaited<ReturnType<typeof listTaskMoves>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskMoves>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTaskMovesQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTaskMoves>>> = ({ signal }) => listTaskMoves(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTaskMoves>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTaskMovesQueryResult = NonNullable<Awaited<ReturnType<typeof listTaskMoves>>>
export type ListTaskMovesQueryError = Error | HTTPValidationError


export function useListTaskMoves<TData = Awaited<ReturnType<typeof listTaskMoves>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskMoves>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTaskMoves>>,
          TError,
          Awaited<ReturnType<typeof listTaskMoves>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTaskMoves<TData = Awaited<ReturnType<typeof listTaskMoves>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskMoves>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTaskMoves>>,
          TError,
          Awaited<ReturnType<typeof listTaskMoves>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTaskMoves<TData = Awaited<ReturnType<typeof listTaskMoves>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskMoves>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Task Moves
 */

export function useListTaskMoves<TData = Awaited<ReturnType<typeof listTaskMoves>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskMoves>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTaskMovesQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listRunActivityResponse200 = {
  data: RunActivityList
  status: 200
}

export type listRunActivityResponse401 = {
  data: Error
  status: 401
}

export type listRunActivityResponse403 = {
  data: Error
  status: 403
}

export type listRunActivityResponse404 = {
  data: Error
  status: 404
}

export type listRunActivityResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listRunActivityResponseSuccess = (listRunActivityResponse200) & {
  headers: Headers;
};
export type listRunActivityResponseError = (listRunActivityResponse401 | listRunActivityResponse403 | listRunActivityResponse404 | listRunActivityResponse422) & {
  headers: Headers;
};

export type listRunActivityResponse = (listRunActivityResponseSuccess | listRunActivityResponseError)

export const getListRunActivityUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/activity`
}

/**
 * What happened to this run's subtasks and who did it, newest first (spec 131).
 * @summary List Run Activity
 */
export const listRunActivity = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<listRunActivityResponse> => {

  return customFetch<listRunActivityResponse>(getListRunActivityUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRunActivityQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/activity`
    ] as const;
    }


export const getListRunActivityQueryOptions = <TData = Awaited<ReturnType<typeof listRunActivity>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunActivity>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRunActivityQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRunActivity>>> = ({ signal }) => listRunActivity(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRunActivity>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListRunActivityQueryResult = NonNullable<Awaited<ReturnType<typeof listRunActivity>>>
export type ListRunActivityQueryError = Error | HTTPValidationError


export function useListRunActivity<TData = Awaited<ReturnType<typeof listRunActivity>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunActivity>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRunActivity>>,
          TError,
          Awaited<ReturnType<typeof listRunActivity>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListRunActivity<TData = Awaited<ReturnType<typeof listRunActivity>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunActivity>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRunActivity>>,
          TError,
          Awaited<ReturnType<typeof listRunActivity>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListRunActivity<TData = Awaited<ReturnType<typeof listRunActivity>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunActivity>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Run Activity
 */

export function useListRunActivity<TData = Awaited<ReturnType<typeof listRunActivity>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunActivity>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListRunActivityQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type addSchedulerNoteResponse201 = {
  data: SchedulerNoteEntry
  status: 201
}

export type addSchedulerNoteResponse401 = {
  data: Error
  status: 401
}

export type addSchedulerNoteResponse403 = {
  data: Error
  status: 403
}

export type addSchedulerNoteResponse404 = {
  data: Error
  status: 404
}

export type addSchedulerNoteResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type addSchedulerNoteResponseSuccess = (addSchedulerNoteResponse201) & {
  headers: Headers;
};
export type addSchedulerNoteResponseError = (addSchedulerNoteResponse401 | addSchedulerNoteResponse403 | addSchedulerNoteResponse404 | addSchedulerNoteResponse422) & {
  headers: Headers;
};

export type addSchedulerNoteResponse = (addSchedulerNoteResponseSuccess | addSchedulerNoteResponseError)

export const getAddSchedulerNoteUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/scheduler-notes`
}

/**
 * A message to this run's scheduler; its next decision treats it as the main instruction.
 * @summary Add Scheduler Note
 */
export const addSchedulerNote = async (workspaceId: string,
    taskId: string,
    createSchedulerNoteRequest: CreateSchedulerNoteRequest, options?: RequestInit): Promise<addSchedulerNoteResponse> => {

  return customFetch<addSchedulerNoteResponse>(getAddSchedulerNoteUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createSchedulerNoteRequest)
  }
);}




export const getAddSchedulerNoteMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addSchedulerNote>>, TError,{workspaceId: string;taskId: string;data: CreateSchedulerNoteRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addSchedulerNote>>, TError,{workspaceId: string;taskId: string;data: CreateSchedulerNoteRequest}, TContext> => {

const mutationKey = ['addSchedulerNote'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addSchedulerNote>>, {workspaceId: string;taskId: string;data: CreateSchedulerNoteRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  addSchedulerNote(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddSchedulerNoteMutationResult = NonNullable<Awaited<ReturnType<typeof addSchedulerNote>>>
    export type AddSchedulerNoteMutationBody = CreateSchedulerNoteRequest
    export type AddSchedulerNoteMutationError = Error | HTTPValidationError

    /**
 * @summary Add Scheduler Note
 */
export const useAddSchedulerNote = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addSchedulerNote>>, TError,{workspaceId: string;taskId: string;data: CreateSchedulerNoteRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof addSchedulerNote>>,
        TError,
        {workspaceId: string;taskId: string;data: CreateSchedulerNoteRequest},
        TContext
      > => {
      return useMutation(getAddSchedulerNoteMutationOptions(options), queryClient);
    }

export type getExecutionTranscriptResponse200 = {
  data: ExecutionTranscriptView
  status: 200
}

export type getExecutionTranscriptResponse401 = {
  data: Error
  status: 401
}

export type getExecutionTranscriptResponse403 = {
  data: Error
  status: 403
}

export type getExecutionTranscriptResponse404 = {
  data: Error
  status: 404
}

export type getExecutionTranscriptResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getExecutionTranscriptResponseSuccess = (getExecutionTranscriptResponse200) & {
  headers: Headers;
};
export type getExecutionTranscriptResponseError = (getExecutionTranscriptResponse401 | getExecutionTranscriptResponse403 | getExecutionTranscriptResponse404 | getExecutionTranscriptResponse422) & {
  headers: Headers;
};

export type getExecutionTranscriptResponse = (getExecutionTranscriptResponseSuccess | getExecutionTranscriptResponseError)

export const getGetExecutionTranscriptUrl = (workspaceId: string,
    taskId: string,
    executionId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/transcript`
}

/**
 * One run's activity record, off the two-second poll and onto a deliberate read (081).
 * @summary Get Execution Transcript
 */
export const getExecutionTranscript = async (workspaceId: string,
    taskId: string,
    executionId: string, options?: RequestInit): Promise<getExecutionTranscriptResponse> => {

  return customFetch<getExecutionTranscriptResponse>(getGetExecutionTranscriptUrl(workspaceId,taskId,executionId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetExecutionTranscriptQueryKey = (workspaceId: string,
    taskId: string,
    executionId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/transcript`
    ] as const;
    }


export const getGetExecutionTranscriptQueryOptions = <TData = Awaited<ReturnType<typeof getExecutionTranscript>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionTranscript>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetExecutionTranscriptQueryKey(workspaceId,taskId,executionId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getExecutionTranscript>>> = ({ signal }) => getExecutionTranscript(workspaceId,taskId,executionId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined && executionId !== null && executionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getExecutionTranscript>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetExecutionTranscriptQueryResult = NonNullable<Awaited<ReturnType<typeof getExecutionTranscript>>>
export type GetExecutionTranscriptQueryError = Error | HTTPValidationError


export function useGetExecutionTranscript<TData = Awaited<ReturnType<typeof getExecutionTranscript>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionTranscript>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getExecutionTranscript>>,
          TError,
          Awaited<ReturnType<typeof getExecutionTranscript>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetExecutionTranscript<TData = Awaited<ReturnType<typeof getExecutionTranscript>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionTranscript>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getExecutionTranscript>>,
          TError,
          Awaited<ReturnType<typeof getExecutionTranscript>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetExecutionTranscript<TData = Awaited<ReturnType<typeof getExecutionTranscript>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionTranscript>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Execution Transcript
 */

export function useGetExecutionTranscript<TData = Awaited<ReturnType<typeof getExecutionTranscript>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionTranscript>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetExecutionTranscriptQueryOptions(workspaceId,taskId,executionId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type createTaskRelationResponse201 = {
  data: TaskRelationView
  status: 201
}

export type createTaskRelationResponse401 = {
  data: Error
  status: 401
}

export type createTaskRelationResponse403 = {
  data: Error
  status: 403
}

export type createTaskRelationResponse404 = {
  data: Error
  status: 404
}

export type createTaskRelationResponse409 = {
  data: Error
  status: 409
}

export type createTaskRelationResponse422 = {
  data: Error
  status: 422
}

export type createTaskRelationResponseSuccess = (createTaskRelationResponse201) & {
  headers: Headers;
};
export type createTaskRelationResponseError = (createTaskRelationResponse401 | createTaskRelationResponse403 | createTaskRelationResponse404 | createTaskRelationResponse409 | createTaskRelationResponse422) & {
  headers: Headers;
};

export type createTaskRelationResponse = (createTaskRelationResponseSuccess | createTaskRelationResponseError)

export const getCreateTaskRelationUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/task-relations`
}

/**
 * @summary Create Task Relation
 */
export const createTaskRelation = async (workspaceId: string,
    createTaskRelationRequest: CreateTaskRelationRequest, options?: RequestInit): Promise<createTaskRelationResponse> => {

  return customFetch<createTaskRelationResponse>(getCreateTaskRelationUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createTaskRelationRequest)
  }
);}




export const getCreateTaskRelationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTaskRelation>>, TError,{workspaceId: string;data: CreateTaskRelationRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTaskRelation>>, TError,{workspaceId: string;data: CreateTaskRelationRequest}, TContext> => {

const mutationKey = ['createTaskRelation'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTaskRelation>>, {workspaceId: string;data: CreateTaskRelationRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createTaskRelation(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTaskRelationMutationResult = NonNullable<Awaited<ReturnType<typeof createTaskRelation>>>
    export type CreateTaskRelationMutationBody = CreateTaskRelationRequest
    export type CreateTaskRelationMutationError = Error

    /**
 * @summary Create Task Relation
 */
export const useCreateTaskRelation = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTaskRelation>>, TError,{workspaceId: string;data: CreateTaskRelationRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createTaskRelation>>,
        TError,
        {workspaceId: string;data: CreateTaskRelationRequest},
        TContext
      > => {
      return useMutation(getCreateTaskRelationMutationOptions(options), queryClient);
    }

export type deleteTaskRelationResponse204 = {
  data: void
  status: 204
}

export type deleteTaskRelationResponse401 = {
  data: Error
  status: 401
}

export type deleteTaskRelationResponse403 = {
  data: Error
  status: 403
}

export type deleteTaskRelationResponse404 = {
  data: Error
  status: 404
}

export type deleteTaskRelationResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type deleteTaskRelationResponseSuccess = (deleteTaskRelationResponse204) & {
  headers: Headers;
};
export type deleteTaskRelationResponseError = (deleteTaskRelationResponse401 | deleteTaskRelationResponse403 | deleteTaskRelationResponse404 | deleteTaskRelationResponse422) & {
  headers: Headers;
};

export type deleteTaskRelationResponse = (deleteTaskRelationResponseSuccess | deleteTaskRelationResponseError)

export const getDeleteTaskRelationUrl = (workspaceId: string,
    relationId: string,) => {




  return `/api/workspaces/${workspaceId}/task-relations/${relationId}`
}

/**
 * @summary Delete Task Relation
 */
export const deleteTaskRelation = async (workspaceId: string,
    relationId: string, options?: RequestInit): Promise<deleteTaskRelationResponse> => {

  return customFetch<deleteTaskRelationResponse>(getDeleteTaskRelationUrl(workspaceId,relationId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteTaskRelationMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTaskRelation>>, TError,{workspaceId: string;relationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTaskRelation>>, TError,{workspaceId: string;relationId: string}, TContext> => {

const mutationKey = ['deleteTaskRelation'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTaskRelation>>, {workspaceId: string;relationId: string}> = (props) => {
          const {workspaceId,relationId} = props ?? {};

          return  deleteTaskRelation(workspaceId,relationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTaskRelationMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTaskRelation>>>

    export type DeleteTaskRelationMutationError = Error | HTTPValidationError

    /**
 * @summary Delete Task Relation
 */
export const useDeleteTaskRelation = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTaskRelation>>, TError,{workspaceId: string;relationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteTaskRelation>>,
        TError,
        {workspaceId: string;relationId: string},
        TContext
      > => {
      return useMutation(getDeleteTaskRelationMutationOptions(options), queryClient);
    }

export type getDirectTaskRelationsResponse200 = {
  data: DirectTaskRelationsResponse
  status: 200
}

export type getDirectTaskRelationsResponse401 = {
  data: Error
  status: 401
}

export type getDirectTaskRelationsResponse403 = {
  data: Error
  status: 403
}

export type getDirectTaskRelationsResponse404 = {
  data: Error
  status: 404
}

export type getDirectTaskRelationsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getDirectTaskRelationsResponseSuccess = (getDirectTaskRelationsResponse200) & {
  headers: Headers;
};
export type getDirectTaskRelationsResponseError = (getDirectTaskRelationsResponse401 | getDirectTaskRelationsResponse403 | getDirectTaskRelationsResponse404 | getDirectTaskRelationsResponse422) & {
  headers: Headers;
};

export type getDirectTaskRelationsResponse = (getDirectTaskRelationsResponseSuccess | getDirectTaskRelationsResponseError)

export const getGetDirectTaskRelationsUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/task-relations`
}

/**
 * @summary Get Direct Task Relations
 */
export const getDirectTaskRelations = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<getDirectTaskRelationsResponse> => {

  return customFetch<getDirectTaskRelationsResponse>(getGetDirectTaskRelationsUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDirectTaskRelationsQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/task-relations`
    ] as const;
    }


export const getGetDirectTaskRelationsQueryOptions = <TData = Awaited<ReturnType<typeof getDirectTaskRelations>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDirectTaskRelations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDirectTaskRelationsQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDirectTaskRelations>>> = ({ signal }) => getDirectTaskRelations(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDirectTaskRelations>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetDirectTaskRelationsQueryResult = NonNullable<Awaited<ReturnType<typeof getDirectTaskRelations>>>
export type GetDirectTaskRelationsQueryError = Error | HTTPValidationError


export function useGetDirectTaskRelations<TData = Awaited<ReturnType<typeof getDirectTaskRelations>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDirectTaskRelations>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDirectTaskRelations>>,
          TError,
          Awaited<ReturnType<typeof getDirectTaskRelations>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetDirectTaskRelations<TData = Awaited<ReturnType<typeof getDirectTaskRelations>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDirectTaskRelations>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDirectTaskRelations>>,
          TError,
          Awaited<ReturnType<typeof getDirectTaskRelations>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetDirectTaskRelations<TData = Awaited<ReturnType<typeof getDirectTaskRelations>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDirectTaskRelations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Direct Task Relations
 */

export function useGetDirectTaskRelations<TData = Awaited<ReturnType<typeof getDirectTaskRelations>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDirectTaskRelations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetDirectTaskRelationsQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type getRelatedTasksResponse200 = {
  data: RelatedTasksResponse
  status: 200
}

export type getRelatedTasksResponse401 = {
  data: Error
  status: 401
}

export type getRelatedTasksResponse403 = {
  data: Error
  status: 403
}

export type getRelatedTasksResponse404 = {
  data: Error
  status: 404
}

export type getRelatedTasksResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getRelatedTasksResponseSuccess = (getRelatedTasksResponse200) & {
  headers: Headers;
};
export type getRelatedTasksResponseError = (getRelatedTasksResponse401 | getRelatedTasksResponse403 | getRelatedTasksResponse404 | getRelatedTasksResponse422) & {
  headers: Headers;
};

export type getRelatedTasksResponse = (getRelatedTasksResponseSuccess | getRelatedTasksResponseError)

export const getGetRelatedTasksUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/related-tasks`
}

/**
 * @summary Get Related Tasks
 */
export const getRelatedTasks = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<getRelatedTasksResponse> => {

  return customFetch<getRelatedTasksResponse>(getGetRelatedTasksUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetRelatedTasksQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/related-tasks`
    ] as const;
    }


export const getGetRelatedTasksQueryOptions = <TData = Awaited<ReturnType<typeof getRelatedTasks>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedTasks>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRelatedTasksQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRelatedTasks>>> = ({ signal }) => getRelatedTasks(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRelatedTasks>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetRelatedTasksQueryResult = NonNullable<Awaited<ReturnType<typeof getRelatedTasks>>>
export type GetRelatedTasksQueryError = Error | HTTPValidationError


export function useGetRelatedTasks<TData = Awaited<ReturnType<typeof getRelatedTasks>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedTasks>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRelatedTasks>>,
          TError,
          Awaited<ReturnType<typeof getRelatedTasks>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetRelatedTasks<TData = Awaited<ReturnType<typeof getRelatedTasks>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedTasks>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRelatedTasks>>,
          TError,
          Awaited<ReturnType<typeof getRelatedTasks>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetRelatedTasks<TData = Awaited<ReturnType<typeof getRelatedTasks>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedTasks>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Related Tasks
 */

export function useGetRelatedTasks<TData = Awaited<ReturnType<typeof getRelatedTasks>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getRelatedTasks>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetRelatedTasksQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type createRelatedTaskResponse201 = {
  data: CreateRelatedTaskResponse
  status: 201
}

export type createRelatedTaskResponse403 = {
  data: Error
  status: 403
}

export type createRelatedTaskResponse404 = {
  data: Error
  status: 404
}

export type createRelatedTaskResponse422 = {
  data: Error
  status: 422
}

export type createRelatedTaskResponseSuccess = (createRelatedTaskResponse201) & {
  headers: Headers;
};
export type createRelatedTaskResponseError = (createRelatedTaskResponse403 | createRelatedTaskResponse404 | createRelatedTaskResponse422) & {
  headers: Headers;
};

export type createRelatedTaskResponse = (createRelatedTaskResponseSuccess | createRelatedTaskResponseError)

export const getCreateRelatedTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/related-tasks`
}

/**
 * @summary Create Related Task
 */
export const createRelatedTask = async (workspaceId: string,
    taskId: string,
    createRelatedTaskRequest: CreateRelatedTaskRequest, options?: RequestInit): Promise<createRelatedTaskResponse> => {

  return customFetch<createRelatedTaskResponse>(getCreateRelatedTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createRelatedTaskRequest)
  }
);}




export const getCreateRelatedTaskMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createRelatedTask>>, TError,{workspaceId: string;taskId: string;data: CreateRelatedTaskRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createRelatedTask>>, TError,{workspaceId: string;taskId: string;data: CreateRelatedTaskRequest}, TContext> => {

const mutationKey = ['createRelatedTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createRelatedTask>>, {workspaceId: string;taskId: string;data: CreateRelatedTaskRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  createRelatedTask(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateRelatedTaskMutationResult = NonNullable<Awaited<ReturnType<typeof createRelatedTask>>>
    export type CreateRelatedTaskMutationBody = CreateRelatedTaskRequest
    export type CreateRelatedTaskMutationError = Error

    /**
 * @summary Create Related Task
 */
export const useCreateRelatedTask = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createRelatedTask>>, TError,{workspaceId: string;taskId: string;data: CreateRelatedTaskRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createRelatedTask>>,
        TError,
        {workspaceId: string;taskId: string;data: CreateRelatedTaskRequest},
        TContext
      > => {
      return useMutation(getCreateRelatedTaskMutationOptions(options), queryClient);
    }

export type getTaskSiblingsResponse200 = {
  data: SiblingsResponse
  status: 200
}

export type getTaskSiblingsResponse401 = {
  data: Error
  status: 401
}

export type getTaskSiblingsResponse403 = {
  data: Error
  status: 403
}

export type getTaskSiblingsResponse404 = {
  data: Error
  status: 404
}

export type getTaskSiblingsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getTaskSiblingsResponseSuccess = (getTaskSiblingsResponse200) & {
  headers: Headers;
};
export type getTaskSiblingsResponseError = (getTaskSiblingsResponse401 | getTaskSiblingsResponse403 | getTaskSiblingsResponse404 | getTaskSiblingsResponse422) & {
  headers: Headers;
};

export type getTaskSiblingsResponse = (getTaskSiblingsResponseSuccess | getTaskSiblingsResponseError)

export const getGetTaskSiblingsUrl = (workspaceId: string,
    taskId: string,
    params: GetTaskSiblingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/tasks/${taskId}/related-tasks/siblings?${stringifiedParams}` : `/api/workspaces/${workspaceId}/tasks/${taskId}/related-tasks/siblings`
}

/**
 * @summary Get Task Siblings
 */
export const getTaskSiblings = async (workspaceId: string,
    taskId: string,
    params: GetTaskSiblingsParams, options?: RequestInit): Promise<getTaskSiblingsResponse> => {

  return customFetch<getTaskSiblingsResponse>(getGetTaskSiblingsUrl(workspaceId,taskId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTaskSiblingsQueryKey = (workspaceId: string,
    taskId: string,
    params?: GetTaskSiblingsParams,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/related-tasks/siblings`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetTaskSiblingsQueryOptions = <TData = Awaited<ReturnType<typeof getTaskSiblings>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string,
    params: GetTaskSiblingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskSiblings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTaskSiblingsQueryKey(workspaceId,taskId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTaskSiblings>>> = ({ signal }) => getTaskSiblings(workspaceId,taskId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTaskSiblings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetTaskSiblingsQueryResult = NonNullable<Awaited<ReturnType<typeof getTaskSiblings>>>
export type GetTaskSiblingsQueryError = Error | HTTPValidationError


export function useGetTaskSiblings<TData = Awaited<ReturnType<typeof getTaskSiblings>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    params: GetTaskSiblingsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskSiblings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskSiblings>>,
          TError,
          Awaited<ReturnType<typeof getTaskSiblings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTaskSiblings<TData = Awaited<ReturnType<typeof getTaskSiblings>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    params: GetTaskSiblingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskSiblings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskSiblings>>,
          TError,
          Awaited<ReturnType<typeof getTaskSiblings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTaskSiblings<TData = Awaited<ReturnType<typeof getTaskSiblings>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    params: GetTaskSiblingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskSiblings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Task Siblings
 */

export function useGetTaskSiblings<TData = Awaited<ReturnType<typeof getTaskSiblings>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    params: GetTaskSiblingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskSiblings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetTaskSiblingsQueryOptions(workspaceId,taskId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readTaskFollowResponse200 = {
  data: TaskFollowState
  status: 200
}

export type readTaskFollowResponse401 = {
  data: Error
  status: 401
}

export type readTaskFollowResponse403 = {
  data: Error
  status: 403
}

export type readTaskFollowResponse404 = {
  data: Error
  status: 404
}

export type readTaskFollowResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type readTaskFollowResponseSuccess = (readTaskFollowResponse200) & {
  headers: Headers;
};
export type readTaskFollowResponseError = (readTaskFollowResponse401 | readTaskFollowResponse403 | readTaskFollowResponse404 | readTaskFollowResponse422) & {
  headers: Headers;
};

export type readTaskFollowResponse = (readTaskFollowResponseSuccess | readTaskFollowResponseError)

export const getReadTaskFollowUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/follow`
}

/**
 * Whether the signed-in person follows this task (spec 116 FR-006).
 *
 * On the tasks router although the table belongs to notifications, for the reason
 * :func:`replace_task_labels` gives: "is this task in this workspace" is a tasks
 * rule, and serving it from the notifications router would close a cycle.
 * @summary Read Task Follow
 */
export const readTaskFollow = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<readTaskFollowResponse> => {

  return customFetch<readTaskFollowResponse>(getReadTaskFollowUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadTaskFollowQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/follow`
    ] as const;
    }


export const getReadTaskFollowQueryOptions = <TData = Awaited<ReturnType<typeof readTaskFollow>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readTaskFollow>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadTaskFollowQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readTaskFollow>>> = ({ signal }) => readTaskFollow(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readTaskFollow>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadTaskFollowQueryResult = NonNullable<Awaited<ReturnType<typeof readTaskFollow>>>
export type ReadTaskFollowQueryError = Error | HTTPValidationError


export function useReadTaskFollow<TData = Awaited<ReturnType<typeof readTaskFollow>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readTaskFollow>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readTaskFollow>>,
          TError,
          Awaited<ReturnType<typeof readTaskFollow>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadTaskFollow<TData = Awaited<ReturnType<typeof readTaskFollow>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readTaskFollow>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readTaskFollow>>,
          TError,
          Awaited<ReturnType<typeof readTaskFollow>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadTaskFollow<TData = Awaited<ReturnType<typeof readTaskFollow>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readTaskFollow>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Task Follow
 */

export function useReadTaskFollow<TData = Awaited<ReturnType<typeof readTaskFollow>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readTaskFollow>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadTaskFollowQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type followTaskResponse200 = {
  data: TaskFollowState
  status: 200
}

export type followTaskResponse401 = {
  data: Error
  status: 401
}

export type followTaskResponse403 = {
  data: Error
  status: 403
}

export type followTaskResponse404 = {
  data: Error
  status: 404
}

export type followTaskResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type followTaskResponseSuccess = (followTaskResponse200) & {
  headers: Headers;
};
export type followTaskResponseError = (followTaskResponse401 | followTaskResponse403 | followTaskResponse404 | followTaskResponse422) & {
  headers: Headers;
};

export type followTaskResponse = (followTaskResponseSuccess | followTaskResponseError)

export const getFollowTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/follow`
}

/**
 * @summary Follow Task
 */
export const followTask = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<followTaskResponse> => {

  return customFetch<followTaskResponse>(getFollowTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'PUT'


  }
);}




export const getFollowTaskMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof followTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof followTask>>, TError,{workspaceId: string;taskId: string}, TContext> => {

const mutationKey = ['followTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof followTask>>, {workspaceId: string;taskId: string}> = (props) => {
          const {workspaceId,taskId} = props ?? {};

          return  followTask(workspaceId,taskId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type FollowTaskMutationResult = NonNullable<Awaited<ReturnType<typeof followTask>>>

    export type FollowTaskMutationError = Error | HTTPValidationError

    /**
 * @summary Follow Task
 */
export const useFollowTask = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof followTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof followTask>>,
        TError,
        {workspaceId: string;taskId: string},
        TContext
      > => {
      return useMutation(getFollowTaskMutationOptions(options), queryClient);
    }

export type unfollowTaskResponse200 = {
  data: TaskFollowState
  status: 200
}

export type unfollowTaskResponse401 = {
  data: Error
  status: 401
}

export type unfollowTaskResponse403 = {
  data: Error
  status: 403
}

export type unfollowTaskResponse404 = {
  data: Error
  status: 404
}

export type unfollowTaskResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type unfollowTaskResponseSuccess = (unfollowTaskResponse200) & {
  headers: Headers;
};
export type unfollowTaskResponseError = (unfollowTaskResponse401 | unfollowTaskResponse403 | unfollowTaskResponse404 | unfollowTaskResponse422) & {
  headers: Headers;
};

export type unfollowTaskResponse = (unfollowTaskResponseSuccess | unfollowTaskResponseError)

export const getUnfollowTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/follow`
}

/**
 * Stop following. It persists: commenting later does not re-follow (FR-002).
 * @summary Unfollow Task
 */
export const unfollowTask = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<unfollowTaskResponse> => {

  return customFetch<unfollowTaskResponse>(getUnfollowTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getUnfollowTaskMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unfollowTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unfollowTask>>, TError,{workspaceId: string;taskId: string}, TContext> => {

const mutationKey = ['unfollowTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unfollowTask>>, {workspaceId: string;taskId: string}> = (props) => {
          const {workspaceId,taskId} = props ?? {};

          return  unfollowTask(workspaceId,taskId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnfollowTaskMutationResult = NonNullable<Awaited<ReturnType<typeof unfollowTask>>>

    export type UnfollowTaskMutationError = Error | HTTPValidationError

    /**
 * @summary Unfollow Task
 */
export const useUnfollowTask = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unfollowTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof unfollowTask>>,
        TError,
        {workspaceId: string;taskId: string},
        TContext
      > => {
      return useMutation(getUnfollowTaskMutationOptions(options), queryClient);
    }

export type replaceTaskLabelsResponse200 = {
  data: TaskLabelList
  status: 200
}

export type replaceTaskLabelsResponse401 = {
  data: Error
  status: 401
}

export type replaceTaskLabelsResponse403 = {
  data: Error
  status: 403
}

export type replaceTaskLabelsResponse404 = {
  data: Error
  status: 404
}

export type replaceTaskLabelsResponse422 = {
  data: Error
  status: 422
}

export type replaceTaskLabelsResponseSuccess = (replaceTaskLabelsResponse200) & {
  headers: Headers;
};
export type replaceTaskLabelsResponseError = (replaceTaskLabelsResponse401 | replaceTaskLabelsResponse403 | replaceTaskLabelsResponse404 | replaceTaskLabelsResponse422) & {
  headers: Headers;
};

export type replaceTaskLabelsResponse = (replaceTaskLabelsResponseSuccess | replaceTaskLabelsResponseError)

export const getReplaceTaskLabelsUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/labels`
}

/**
 * Replace the whole set of labels on a task (spec 063 FR-010).
 *
 * On the tasks router although the tables belong to the labels feature: the
 * first thing this does is establish that the task is in this workspace, which
 * is a tasks rule. Serving it from the labels router would make that feature
 * depend on tasks while tasks already depends on it, closing a cycle.
 *
 * A label identifier from another workspace answers 404, not 403 — this product
 * has no authorization, so "forbidden" would be a claim it cannot make.
 * @summary Replace Task Labels
 */
export const replaceTaskLabels = async (workspaceId: string,
    taskId: string,
    replaceTaskLabelsRequest: ReplaceTaskLabelsRequest, options?: RequestInit): Promise<replaceTaskLabelsResponse> => {

  return customFetch<replaceTaskLabelsResponse>(getReplaceTaskLabelsUrl(workspaceId,taskId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(replaceTaskLabelsRequest)
  }
);}




export const getReplaceTaskLabelsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replaceTaskLabels>>, TError,{workspaceId: string;taskId: string;data: ReplaceTaskLabelsRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof replaceTaskLabels>>, TError,{workspaceId: string;taskId: string;data: ReplaceTaskLabelsRequest}, TContext> => {

const mutationKey = ['replaceTaskLabels'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof replaceTaskLabels>>, {workspaceId: string;taskId: string;data: ReplaceTaskLabelsRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  replaceTaskLabels(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReplaceTaskLabelsMutationResult = NonNullable<Awaited<ReturnType<typeof replaceTaskLabels>>>
    export type ReplaceTaskLabelsMutationBody = ReplaceTaskLabelsRequest
    export type ReplaceTaskLabelsMutationError = Error

    /**
 * @summary Replace Task Labels
 */
export const useReplaceTaskLabels = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replaceTaskLabels>>, TError,{workspaceId: string;taskId: string;data: ReplaceTaskLabelsRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof replaceTaskLabels>>,
        TError,
        {workspaceId: string;taskId: string;data: ReplaceTaskLabelsRequest},
        TContext
      > => {
      return useMutation(getReplaceTaskLabelsMutationOptions(options), queryClient);
    }

export type deleteTaskResponse204 = {
  data: void
  status: 204
}

export type deleteTaskResponse401 = {
  data: Error
  status: 401
}

export type deleteTaskResponse403 = {
  data: Error
  status: 403
}

export type deleteTaskResponse404 = {
  data: Error
  status: 404
}

export type deleteTaskResponse409 = {
  data: Error
  status: 409
}

export type deleteTaskResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type deleteTaskResponse504 = {
  data: Error
  status: 504
}

export type deleteTaskResponseSuccess = (deleteTaskResponse204) & {
  headers: Headers;
};
export type deleteTaskResponseError = (deleteTaskResponse401 | deleteTaskResponse403 | deleteTaskResponse404 | deleteTaskResponse409 | deleteTaskResponse422 | deleteTaskResponse504) & {
  headers: Headers;
};

export type deleteTaskResponse = (deleteTaskResponseSuccess | deleteTaskResponseError)

export const getDeleteTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}`
}

/**
 * Permanently delete the task and every descendant; running work is stopped first.
 *
 * A stop that cannot be confirmed answers 504 and deletes nothing. Spend is kept:
 * the ledger rows outlive the executions they came from.
 * @summary Delete Task
 */
export const deleteTask = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<deleteTaskResponse> => {

  return customFetch<deleteTaskResponse>(getDeleteTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteTaskMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTask>>, TError,{workspaceId: string;taskId: string}, TContext> => {

const mutationKey = ['deleteTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTask>>, {workspaceId: string;taskId: string}> = (props) => {
          const {workspaceId,taskId} = props ?? {};

          return  deleteTask(workspaceId,taskId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTaskMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTask>>>

    export type DeleteTaskMutationError = Error | HTTPValidationError

    /**
 * @summary Delete Task
 */
export const useDeleteTask = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTask>>, TError,{workspaceId: string;taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteTask>>,
        TError,
        {workspaceId: string;taskId: string},
        TContext
      > => {
      return useMutation(getDeleteTaskMutationOptions(options), queryClient);
    }

export type getTaskResultsResponse200 = {
  data: TaskResultsView
  status: 200
}

export type getTaskResultsResponse401 = {
  data: Error
  status: 401
}

export type getTaskResultsResponse403 = {
  data: Error
  status: 403
}

export type getTaskResultsResponse404 = {
  data: Error
  status: 404
}

export type getTaskResultsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getTaskResultsResponseSuccess = (getTaskResultsResponse200) & {
  headers: Headers;
};
export type getTaskResultsResponseError = (getTaskResultsResponse401 | getTaskResultsResponse403 | getTaskResultsResponse404 | getTaskResultsResponse422) & {
  headers: Headers;
};

export type getTaskResultsResponse = (getTaskResultsResponseSuccess | getTaskResultsResponseError)

export const getGetTaskResultsUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/results`
}

/**
 * @summary Get Task Results
 */
export const getTaskResults = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<getTaskResultsResponse> => {

  return customFetch<getTaskResultsResponse>(getGetTaskResultsUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTaskResultsQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/results`
    ] as const;
    }


export const getGetTaskResultsQueryOptions = <TData = Awaited<ReturnType<typeof getTaskResults>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskResults>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTaskResultsQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTaskResults>>> = ({ signal }) => getTaskResults(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTaskResults>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetTaskResultsQueryResult = NonNullable<Awaited<ReturnType<typeof getTaskResults>>>
export type GetTaskResultsQueryError = Error | HTTPValidationError


export function useGetTaskResults<TData = Awaited<ReturnType<typeof getTaskResults>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskResults>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskResults>>,
          TError,
          Awaited<ReturnType<typeof getTaskResults>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTaskResults<TData = Awaited<ReturnType<typeof getTaskResults>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskResults>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskResults>>,
          TError,
          Awaited<ReturnType<typeof getTaskResults>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTaskResults<TData = Awaited<ReturnType<typeof getTaskResults>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskResults>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Task Results
 */

export function useGetTaskResults<TData = Awaited<ReturnType<typeof getTaskResults>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskResults>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetTaskResultsQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type getTaskArtifactsResponse200 = {
  data: TaskArtifactsView
  status: 200
}

export type getTaskArtifactsResponse401 = {
  data: Error
  status: 401
}

export type getTaskArtifactsResponse403 = {
  data: Error
  status: 403
}

export type getTaskArtifactsResponse404 = {
  data: Error
  status: 404
}

export type getTaskArtifactsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getTaskArtifactsResponseSuccess = (getTaskArtifactsResponse200) & {
  headers: Headers;
};
export type getTaskArtifactsResponseError = (getTaskArtifactsResponse401 | getTaskArtifactsResponse403 | getTaskArtifactsResponse404 | getTaskArtifactsResponse422) & {
  headers: Headers;
};

export type getTaskArtifactsResponse = (getTaskArtifactsResponseSuccess | getTaskArtifactsResponseError)

export const getGetTaskArtifactsUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/artifacts`
}

/**
 * @summary Get Task Artifacts
 */
export const getTaskArtifacts = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<getTaskArtifactsResponse> => {

  return customFetch<getTaskArtifactsResponse>(getGetTaskArtifactsUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTaskArtifactsQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/artifacts`
    ] as const;
    }


export const getGetTaskArtifactsQueryOptions = <TData = Awaited<ReturnType<typeof getTaskArtifacts>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskArtifacts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTaskArtifactsQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTaskArtifacts>>> = ({ signal }) => getTaskArtifacts(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTaskArtifacts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetTaskArtifactsQueryResult = NonNullable<Awaited<ReturnType<typeof getTaskArtifacts>>>
export type GetTaskArtifactsQueryError = Error | HTTPValidationError


export function useGetTaskArtifacts<TData = Awaited<ReturnType<typeof getTaskArtifacts>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskArtifacts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskArtifacts>>,
          TError,
          Awaited<ReturnType<typeof getTaskArtifacts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTaskArtifacts<TData = Awaited<ReturnType<typeof getTaskArtifacts>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskArtifacts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskArtifacts>>,
          TError,
          Awaited<ReturnType<typeof getTaskArtifacts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTaskArtifacts<TData = Awaited<ReturnType<typeof getTaskArtifacts>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskArtifacts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Task Artifacts
 */

export function useGetTaskArtifacts<TData = Awaited<ReturnType<typeof getTaskArtifacts>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskArtifacts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetTaskArtifactsQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type postRunThreadMessageResponse201 = {
  data: ThreadMessageView
  status: 201
}

export type postRunThreadMessageResponse401 = {
  data: Error
  status: 401
}

export type postRunThreadMessageResponse403 = {
  data: Error
  status: 403
}

export type postRunThreadMessageResponse404 = {
  data: Error
  status: 404
}

export type postRunThreadMessageResponse409 = {
  data: Error
  status: 409
}

export type postRunThreadMessageResponse422 = {
  data: Error
  status: 422
}

export type postRunThreadMessageResponse429 = {
  data: Error
  status: 429
}

export type postRunThreadMessageResponseSuccess = (postRunThreadMessageResponse201) & {
  headers: Headers;
};
export type postRunThreadMessageResponseError = (postRunThreadMessageResponse401 | postRunThreadMessageResponse403 | postRunThreadMessageResponse404 | postRunThreadMessageResponse409 | postRunThreadMessageResponse422 | postRunThreadMessageResponse429) & {
  headers: Headers;
};

export type postRunThreadMessageResponse = (postRunThreadMessageResponseSuccess | postRunThreadMessageResponseError)

export const getPostRunThreadMessageUrl = (workspaceId: string,
    taskId: string,
    executionId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/thread/messages`
}

/**
 * Post a message on a finished run's thread (spec 131).
 * @summary Post Run Thread Message
 */
export const postRunThreadMessage = async (workspaceId: string,
    taskId: string,
    executionId: string,
    postThreadMessageRequest: PostThreadMessageRequest, options?: RequestInit): Promise<postRunThreadMessageResponse> => {

  return customFetch<postRunThreadMessageResponse>(getPostRunThreadMessageUrl(workspaceId,taskId,executionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(postThreadMessageRequest)
  }
);}




export const getPostRunThreadMessageMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postRunThreadMessage>>, TError,{workspaceId: string;taskId: string;executionId: string;data: PostThreadMessageRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postRunThreadMessage>>, TError,{workspaceId: string;taskId: string;executionId: string;data: PostThreadMessageRequest}, TContext> => {

const mutationKey = ['postRunThreadMessage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postRunThreadMessage>>, {workspaceId: string;taskId: string;executionId: string;data: PostThreadMessageRequest}> = (props) => {
          const {workspaceId,taskId,executionId,data} = props ?? {};

          return  postRunThreadMessage(workspaceId,taskId,executionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostRunThreadMessageMutationResult = NonNullable<Awaited<ReturnType<typeof postRunThreadMessage>>>
    export type PostRunThreadMessageMutationBody = PostThreadMessageRequest
    export type PostRunThreadMessageMutationError = Error

    /**
 * @summary Post Run Thread Message
 */
export const usePostRunThreadMessage = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postRunThreadMessage>>, TError,{workspaceId: string;taskId: string;executionId: string;data: PostThreadMessageRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postRunThreadMessage>>,
        TError,
        {workspaceId: string;taskId: string;executionId: string;data: PostThreadMessageRequest},
        TContext
      > => {
      return useMutation(getPostRunThreadMessageMutationOptions(options), queryClient);
    }

export type listRunThreadMessagesResponse200 = {
  data: ThreadMessagePage
  status: 200
}

export type listRunThreadMessagesResponse401 = {
  data: Error
  status: 401
}

export type listRunThreadMessagesResponse403 = {
  data: Error
  status: 403
}

export type listRunThreadMessagesResponse404 = {
  data: Error
  status: 404
}

export type listRunThreadMessagesResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listRunThreadMessagesResponseSuccess = (listRunThreadMessagesResponse200) & {
  headers: Headers;
};
export type listRunThreadMessagesResponseError = (listRunThreadMessagesResponse401 | listRunThreadMessagesResponse403 | listRunThreadMessagesResponse404 | listRunThreadMessagesResponse422) & {
  headers: Headers;
};

export type listRunThreadMessagesResponse = (listRunThreadMessagesResponseSuccess | listRunThreadMessagesResponseError)

export const getListRunThreadMessagesUrl = (workspaceId: string,
    taskId: string,
    executionId: string,
    params?: ListRunThreadMessagesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/thread/messages?${stringifiedParams}` : `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/thread/messages`
}

/**
 * A page of a run's thread, oldest first; the first page is the newest messages.
 * @summary List Run Thread Messages
 */
export const listRunThreadMessages = async (workspaceId: string,
    taskId: string,
    executionId: string,
    params?: ListRunThreadMessagesParams, options?: RequestInit): Promise<listRunThreadMessagesResponse> => {

  return customFetch<listRunThreadMessagesResponse>(getListRunThreadMessagesUrl(workspaceId,taskId,executionId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRunThreadMessagesQueryKey = (workspaceId: string,
    taskId: string,
    executionId: string,
    params?: ListRunThreadMessagesParams,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/thread/messages`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRunThreadMessagesQueryOptions = <TData = Awaited<ReturnType<typeof listRunThreadMessages>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string,
    executionId: string,
    params?: ListRunThreadMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunThreadMessages>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRunThreadMessagesQueryKey(workspaceId,taskId,executionId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRunThreadMessages>>> = ({ signal }) => listRunThreadMessages(workspaceId,taskId,executionId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined && executionId !== null && executionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRunThreadMessages>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListRunThreadMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof listRunThreadMessages>>>
export type ListRunThreadMessagesQueryError = Error | HTTPValidationError


export function useListRunThreadMessages<TData = Awaited<ReturnType<typeof listRunThreadMessages>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string,
    params: undefined |  ListRunThreadMessagesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunThreadMessages>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRunThreadMessages>>,
          TError,
          Awaited<ReturnType<typeof listRunThreadMessages>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListRunThreadMessages<TData = Awaited<ReturnType<typeof listRunThreadMessages>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string,
    params?: ListRunThreadMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunThreadMessages>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRunThreadMessages>>,
          TError,
          Awaited<ReturnType<typeof listRunThreadMessages>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListRunThreadMessages<TData = Awaited<ReturnType<typeof listRunThreadMessages>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string,
    params?: ListRunThreadMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunThreadMessages>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Run Thread Messages
 */

export function useListRunThreadMessages<TData = Awaited<ReturnType<typeof listRunThreadMessages>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string,
    params?: ListRunThreadMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRunThreadMessages>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListRunThreadMessagesQueryOptions(workspaceId,taskId,executionId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type retryRunThreadReplyResponse202 = {
  data: ThreadMessageView
  status: 202
}

export type retryRunThreadReplyResponse401 = {
  data: Error
  status: 401
}

export type retryRunThreadReplyResponse403 = {
  data: Error
  status: 403
}

export type retryRunThreadReplyResponse404 = {
  data: Error
  status: 404
}

export type retryRunThreadReplyResponse409 = {
  data: Error
  status: 409
}

export type retryRunThreadReplyResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type retryRunThreadReplyResponse429 = {
  data: Error
  status: 429
}

export type retryRunThreadReplyResponseSuccess = (retryRunThreadReplyResponse202) & {
  headers: Headers;
};
export type retryRunThreadReplyResponseError = (retryRunThreadReplyResponse401 | retryRunThreadReplyResponse403 | retryRunThreadReplyResponse404 | retryRunThreadReplyResponse409 | retryRunThreadReplyResponse422 | retryRunThreadReplyResponse429) & {
  headers: Headers;
};

export type retryRunThreadReplyResponse = (retryRunThreadReplyResponseSuccess | retryRunThreadReplyResponseError)

export const getRetryRunThreadReplyUrl = (workspaceId: string,
    taskId: string,
    executionId: string,
    messageId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/thread/messages/${messageId}/retry`
}

/**
 * Ask again for the reply to a message whose reply failed or was refused (spec 131).
 * @summary Retry Run Thread Reply
 */
export const retryRunThreadReply = async (workspaceId: string,
    taskId: string,
    executionId: string,
    messageId: string, options?: RequestInit): Promise<retryRunThreadReplyResponse> => {

  return customFetch<retryRunThreadReplyResponse>(getRetryRunThreadReplyUrl(workspaceId,taskId,executionId,messageId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getRetryRunThreadReplyMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryRunThreadReply>>, TError,{workspaceId: string;taskId: string;executionId: string;messageId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retryRunThreadReply>>, TError,{workspaceId: string;taskId: string;executionId: string;messageId: string}, TContext> => {

const mutationKey = ['retryRunThreadReply'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retryRunThreadReply>>, {workspaceId: string;taskId: string;executionId: string;messageId: string}> = (props) => {
          const {workspaceId,taskId,executionId,messageId} = props ?? {};

          return  retryRunThreadReply(workspaceId,taskId,executionId,messageId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetryRunThreadReplyMutationResult = NonNullable<Awaited<ReturnType<typeof retryRunThreadReply>>>

    export type RetryRunThreadReplyMutationError = Error | HTTPValidationError

    /**
 * @summary Retry Run Thread Reply
 */
export const useRetryRunThreadReply = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryRunThreadReply>>, TError,{workspaceId: string;taskId: string;executionId: string;messageId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof retryRunThreadReply>>,
        TError,
        {workspaceId: string;taskId: string;executionId: string;messageId: string},
        TContext
      > => {
      return useMutation(getRetryRunThreadReplyMutationOptions(options), queryClient);
    }

export type addRunThreadMessageToTaskContextResponse201 = {
  data: AddedToTaskContextView
  status: 201
}

export type addRunThreadMessageToTaskContextResponse401 = {
  data: Error
  status: 401
}

export type addRunThreadMessageToTaskContextResponse403 = {
  data: Error
  status: 403
}

export type addRunThreadMessageToTaskContextResponse404 = {
  data: Error
  status: 404
}

export type addRunThreadMessageToTaskContextResponse409 = {
  data: Error
  status: 409
}

export type addRunThreadMessageToTaskContextResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type addRunThreadMessageToTaskContextResponseSuccess = (addRunThreadMessageToTaskContextResponse201) & {
  headers: Headers;
};
export type addRunThreadMessageToTaskContextResponseError = (addRunThreadMessageToTaskContextResponse401 | addRunThreadMessageToTaskContextResponse403 | addRunThreadMessageToTaskContextResponse404 | addRunThreadMessageToTaskContextResponse409 | addRunThreadMessageToTaskContextResponse422) & {
  headers: Headers;
};

export type addRunThreadMessageToTaskContextResponse = (addRunThreadMessageToTaskContextResponseSuccess | addRunThreadMessageToTaskContextResponseError)

export const getAddRunThreadMessageToTaskContextUrl = (workspaceId: string,
    taskId: string,
    executionId: string,
    messageId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/thread/messages/${messageId}/add-to-task-context`
}

/**
 * Copy a thread message into the task's comments, as the caller's (spec 131 FR-009).
 *
 * Later runs read every comment, so this is how a thread's conclusion reaches them.
 * @summary Add Run Thread Message To Task Context
 */
export const addRunThreadMessageToTaskContext = async (workspaceId: string,
    taskId: string,
    executionId: string,
    messageId: string, options?: RequestInit): Promise<addRunThreadMessageToTaskContextResponse> => {

  return customFetch<addRunThreadMessageToTaskContextResponse>(getAddRunThreadMessageToTaskContextUrl(workspaceId,taskId,executionId,messageId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getAddRunThreadMessageToTaskContextMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addRunThreadMessageToTaskContext>>, TError,{workspaceId: string;taskId: string;executionId: string;messageId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addRunThreadMessageToTaskContext>>, TError,{workspaceId: string;taskId: string;executionId: string;messageId: string}, TContext> => {

const mutationKey = ['addRunThreadMessageToTaskContext'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addRunThreadMessageToTaskContext>>, {workspaceId: string;taskId: string;executionId: string;messageId: string}> = (props) => {
          const {workspaceId,taskId,executionId,messageId} = props ?? {};

          return  addRunThreadMessageToTaskContext(workspaceId,taskId,executionId,messageId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddRunThreadMessageToTaskContextMutationResult = NonNullable<Awaited<ReturnType<typeof addRunThreadMessageToTaskContext>>>

    export type AddRunThreadMessageToTaskContextMutationError = Error | HTTPValidationError

    /**
 * @summary Add Run Thread Message To Task Context
 */
export const useAddRunThreadMessageToTaskContext = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addRunThreadMessageToTaskContext>>, TError,{workspaceId: string;taskId: string;executionId: string;messageId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof addRunThreadMessageToTaskContext>>,
        TError,
        {workspaceId: string;taskId: string;executionId: string;messageId: string},
        TContext
      > => {
      return useMutation(getAddRunThreadMessageToTaskContextMutationOptions(options), queryClient);
    }

export type attachFileToTaskResponse201 = {
  data: AttachmentView
  status: 201
}

export type attachFileToTaskResponse401 = {
  data: Error
  status: 401
}

export type attachFileToTaskResponse403 = {
  data: Error
  status: 403
}

export type attachFileToTaskResponse404 = {
  data: Error
  status: 404
}

export type attachFileToTaskResponse413 = {
  data: Error
  status: 413
}

export type attachFileToTaskResponse422 = {
  data: Error
  status: 422
}

export type attachFileToTaskResponseSuccess = (attachFileToTaskResponse201) & {
  headers: Headers;
};
export type attachFileToTaskResponseError = (attachFileToTaskResponse401 | attachFileToTaskResponse403 | attachFileToTaskResponse404 | attachFileToTaskResponse413 | attachFileToTaskResponse422) & {
  headers: Headers;
};

export type attachFileToTaskResponse = (attachFileToTaskResponseSuccess | attachFileToTaskResponseError)

export const getAttachFileToTaskUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/attachments`
}

/**
 * @summary Attach File To Task
 */
export const attachFileToTask = async (workspaceId: string,
    taskId: string,
    bodyAttachFileToTask: BodyAttachFileToTask, options?: RequestInit): Promise<attachFileToTaskResponse> => {
    const formData = new FormData();
formData.append(`file`, bodyAttachFileToTask.file);
if(bodyAttachFileToTask.replace !== undefined) {
 formData.append(`replace`, bodyAttachFileToTask.replace.toString())
 }

  return customFetch<attachFileToTaskResponse>(getAttachFileToTaskUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}




export const getAttachFileToTaskMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof attachFileToTask>>, TError,{workspaceId: string;taskId: string;data: BodyAttachFileToTask}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof attachFileToTask>>, TError,{workspaceId: string;taskId: string;data: BodyAttachFileToTask}, TContext> => {

const mutationKey = ['attachFileToTask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof attachFileToTask>>, {workspaceId: string;taskId: string;data: BodyAttachFileToTask}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  attachFileToTask(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AttachFileToTaskMutationResult = NonNullable<Awaited<ReturnType<typeof attachFileToTask>>>
    export type AttachFileToTaskMutationBody = BodyAttachFileToTask
    export type AttachFileToTaskMutationError = Error

    /**
 * @summary Attach File To Task
 */
export const useAttachFileToTask = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof attachFileToTask>>, TError,{workspaceId: string;taskId: string;data: BodyAttachFileToTask}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof attachFileToTask>>,
        TError,
        {workspaceId: string;taskId: string;data: BodyAttachFileToTask},
        TContext
      > => {
      return useMutation(getAttachFileToTaskMutationOptions(options), queryClient);
    }

export type listTaskAttachmentsResponse200 = {
  data: TaskAttachmentsView
  status: 200
}

export type listTaskAttachmentsResponse401 = {
  data: Error
  status: 401
}

export type listTaskAttachmentsResponse403 = {
  data: Error
  status: 403
}

export type listTaskAttachmentsResponse404 = {
  data: Error
  status: 404
}

export type listTaskAttachmentsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listTaskAttachmentsResponseSuccess = (listTaskAttachmentsResponse200) & {
  headers: Headers;
};
export type listTaskAttachmentsResponseError = (listTaskAttachmentsResponse401 | listTaskAttachmentsResponse403 | listTaskAttachmentsResponse404 | listTaskAttachmentsResponse422) & {
  headers: Headers;
};

export type listTaskAttachmentsResponse = (listTaskAttachmentsResponseSuccess | listTaskAttachmentsResponseError)

export const getListTaskAttachmentsUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/attachments`
}

/**
 * @summary List Task Attachments
 */
export const listTaskAttachments = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<listTaskAttachmentsResponse> => {

  return customFetch<listTaskAttachmentsResponse>(getListTaskAttachmentsUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTaskAttachmentsQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/attachments`
    ] as const;
    }


export const getListTaskAttachmentsQueryOptions = <TData = Awaited<ReturnType<typeof listTaskAttachments>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskAttachments>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTaskAttachmentsQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTaskAttachments>>> = ({ signal }) => listTaskAttachments(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTaskAttachments>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTaskAttachmentsQueryResult = NonNullable<Awaited<ReturnType<typeof listTaskAttachments>>>
export type ListTaskAttachmentsQueryError = Error | HTTPValidationError


export function useListTaskAttachments<TData = Awaited<ReturnType<typeof listTaskAttachments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskAttachments>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTaskAttachments>>,
          TError,
          Awaited<ReturnType<typeof listTaskAttachments>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTaskAttachments<TData = Awaited<ReturnType<typeof listTaskAttachments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskAttachments>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTaskAttachments>>,
          TError,
          Awaited<ReturnType<typeof listTaskAttachments>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTaskAttachments<TData = Awaited<ReturnType<typeof listTaskAttachments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskAttachments>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Task Attachments
 */

export function useListTaskAttachments<TData = Awaited<ReturnType<typeof listTaskAttachments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTaskAttachments>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTaskAttachmentsQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type removeTaskAttachmentResponse204 = {
  data: void
  status: 204
}

export type removeTaskAttachmentResponse401 = {
  data: Error
  status: 401
}

export type removeTaskAttachmentResponse403 = {
  data: Error
  status: 403
}

export type removeTaskAttachmentResponse404 = {
  data: Error
  status: 404
}

export type removeTaskAttachmentResponse422 = {
  data: Error
  status: 422
}

export type removeTaskAttachmentResponseSuccess = (removeTaskAttachmentResponse204) & {
  headers: Headers;
};
export type removeTaskAttachmentResponseError = (removeTaskAttachmentResponse401 | removeTaskAttachmentResponse403 | removeTaskAttachmentResponse404 | removeTaskAttachmentResponse422) & {
  headers: Headers;
};

export type removeTaskAttachmentResponse = (removeTaskAttachmentResponseSuccess | removeTaskAttachmentResponseError)

export const getRemoveTaskAttachmentUrl = (workspaceId: string,
    taskId: string,
    name: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/attachments/${name}`
}

/**
 * @summary Remove Task Attachment
 */
export const removeTaskAttachment = async (workspaceId: string,
    taskId: string,
    name: string, options?: RequestInit): Promise<removeTaskAttachmentResponse> => {

  return customFetch<removeTaskAttachmentResponse>(getRemoveTaskAttachmentUrl(workspaceId,taskId,name),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getRemoveTaskAttachmentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeTaskAttachment>>, TError,{workspaceId: string;taskId: string;name: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeTaskAttachment>>, TError,{workspaceId: string;taskId: string;name: string}, TContext> => {

const mutationKey = ['removeTaskAttachment'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeTaskAttachment>>, {workspaceId: string;taskId: string;name: string}> = (props) => {
          const {workspaceId,taskId,name} = props ?? {};

          return  removeTaskAttachment(workspaceId,taskId,name,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveTaskAttachmentMutationResult = NonNullable<Awaited<ReturnType<typeof removeTaskAttachment>>>

    export type RemoveTaskAttachmentMutationError = Error

    /**
 * @summary Remove Task Attachment
 */
export const useRemoveTaskAttachment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeTaskAttachment>>, TError,{workspaceId: string;taskId: string;name: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof removeTaskAttachment>>,
        TError,
        {workspaceId: string;taskId: string;name: string},
        TContext
      > => {
      return useMutation(getRemoveTaskAttachmentMutationOptions(options), queryClient);
    }

export type listWorkspaceFilesResponse200 = {
  data: FileListing
  status: 200
}

export type listWorkspaceFilesResponse401 = {
  data: Error
  status: 401
}

export type listWorkspaceFilesResponse403 = {
  data: Error
  status: 403
}

export type listWorkspaceFilesResponse404 = {
  data: Error
  status: 404
}

export type listWorkspaceFilesResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listWorkspaceFilesResponseSuccess = (listWorkspaceFilesResponse200) & {
  headers: Headers;
};
export type listWorkspaceFilesResponseError = (listWorkspaceFilesResponse401 | listWorkspaceFilesResponse403 | listWorkspaceFilesResponse404 | listWorkspaceFilesResponse422) & {
  headers: Headers;
};

export type listWorkspaceFilesResponse = (listWorkspaceFilesResponseSuccess | listWorkspaceFilesResponseError)

export const getListWorkspaceFilesUrl = (workspaceId: string,
    params?: ListWorkspaceFilesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/files?${stringifiedParams}` : `/api/workspaces/${workspaceId}/files`
}

/**
 * @summary List Workspace Files
 */
export const listWorkspaceFiles = async (workspaceId: string,
    params?: ListWorkspaceFilesParams, options?: RequestInit): Promise<listWorkspaceFilesResponse> => {

  return customFetch<listWorkspaceFilesResponse>(getListWorkspaceFilesUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWorkspaceFilesQueryKey = (workspaceId: string,
    params?: ListWorkspaceFilesParams,) => {
    return [
    `/api/workspaces/${workspaceId}/files`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWorkspaceFilesQueryOptions = <TData = Awaited<ReturnType<typeof listWorkspaceFiles>>, TError = Error | HTTPValidationError>(workspaceId: string,
    params?: ListWorkspaceFilesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWorkspaceFilesQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkspaceFiles>>> = ({ signal }) => listWorkspaceFiles(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFiles>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListWorkspaceFilesQueryResult = NonNullable<Awaited<ReturnType<typeof listWorkspaceFiles>>>
export type ListWorkspaceFilesQueryError = Error | HTTPValidationError


export function useListWorkspaceFiles<TData = Awaited<ReturnType<typeof listWorkspaceFiles>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params: undefined |  ListWorkspaceFilesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFiles>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceFiles>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceFiles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaceFiles<TData = Awaited<ReturnType<typeof listWorkspaceFiles>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params?: ListWorkspaceFilesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFiles>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceFiles>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceFiles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaceFiles<TData = Awaited<ReturnType<typeof listWorkspaceFiles>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params?: ListWorkspaceFilesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Workspace Files
 */

export function useListWorkspaceFiles<TData = Awaited<ReturnType<typeof listWorkspaceFiles>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params?: ListWorkspaceFilesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListWorkspaceFilesQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type deleteWorkspaceFileResponse204 = {
  data: void
  status: 204
}

export type deleteWorkspaceFileResponse401 = {
  data: Error
  status: 401
}

export type deleteWorkspaceFileResponse403 = {
  data: Error
  status: 403
}

export type deleteWorkspaceFileResponse404 = {
  data: Error
  status: 404
}

export type deleteWorkspaceFileResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type deleteWorkspaceFileResponseSuccess = (deleteWorkspaceFileResponse204) & {
  headers: Headers;
};
export type deleteWorkspaceFileResponseError = (deleteWorkspaceFileResponse401 | deleteWorkspaceFileResponse403 | deleteWorkspaceFileResponse404 | deleteWorkspaceFileResponse422) & {
  headers: Headers;
};

export type deleteWorkspaceFileResponse = (deleteWorkspaceFileResponseSuccess | deleteWorkspaceFileResponseError)

export const getDeleteWorkspaceFileUrl = (workspaceId: string,
    params: DeleteWorkspaceFileParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/files?${stringifiedParams}` : `/api/workspaces/${workspaceId}/files`
}

/**
 * @summary Delete Workspace File
 */
export const deleteWorkspaceFile = async (workspaceId: string,
    params: DeleteWorkspaceFileParams, options?: RequestInit): Promise<deleteWorkspaceFileResponse> => {

  return customFetch<deleteWorkspaceFileResponse>(getDeleteWorkspaceFileUrl(workspaceId,params),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteWorkspaceFileMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWorkspaceFile>>, TError,{workspaceId: string;params: DeleteWorkspaceFileParams}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteWorkspaceFile>>, TError,{workspaceId: string;params: DeleteWorkspaceFileParams}, TContext> => {

const mutationKey = ['deleteWorkspaceFile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteWorkspaceFile>>, {workspaceId: string;params: DeleteWorkspaceFileParams}> = (props) => {
          const {workspaceId,params} = props ?? {};

          return  deleteWorkspaceFile(workspaceId,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteWorkspaceFileMutationResult = NonNullable<Awaited<ReturnType<typeof deleteWorkspaceFile>>>

    export type DeleteWorkspaceFileMutationError = Error | HTTPValidationError

    /**
 * @summary Delete Workspace File
 */
export const useDeleteWorkspaceFile = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWorkspaceFile>>, TError,{workspaceId: string;params: DeleteWorkspaceFileParams}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteWorkspaceFile>>,
        TError,
        {workspaceId: string;params: DeleteWorkspaceFileParams},
        TContext
      > => {
      return useMutation(getDeleteWorkspaceFileMutationOptions(options), queryClient);
    }

export type readWorkspaceFileContentResponse200ApplicationJson = {
  data: unknown
  status: 200
}

export type readWorkspaceFileContentResponse200ApplicationOctetStream = {
  data: Blob
  status: 200
}

export type readWorkspaceFileContentResponse401 = {
  data: Error
  status: 401
}

export type readWorkspaceFileContentResponse403 = {
  data: Error
  status: 403
}

export type readWorkspaceFileContentResponse404 = {
  data: Error
  status: 404
}

export type readWorkspaceFileContentResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type readWorkspaceFileContentResponseSuccess = (readWorkspaceFileContentResponse200ApplicationJson | readWorkspaceFileContentResponse200ApplicationOctetStream) & {
  headers: Headers;
};
export type readWorkspaceFileContentResponseError = (readWorkspaceFileContentResponse401 | readWorkspaceFileContentResponse403 | readWorkspaceFileContentResponse404 | readWorkspaceFileContentResponse422) & {
  headers: Headers;
};

export type readWorkspaceFileContentResponse = (readWorkspaceFileContentResponseSuccess | readWorkspaceFileContentResponseError)

export const getReadWorkspaceFileContentUrl = (workspaceId: string,
    params: ReadWorkspaceFileContentParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/files/content?${stringifiedParams}` : `/api/workspaces/${workspaceId}/files/content`
}

/**
 * @summary Read Workspace File Content
 */
export const readWorkspaceFileContent = async (workspaceId: string,
    params: ReadWorkspaceFileContentParams, options?: RequestInit): Promise<readWorkspaceFileContentResponse> => {

  return customFetch<readWorkspaceFileContentResponse>(getReadWorkspaceFileContentUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadWorkspaceFileContentQueryKey = (workspaceId: string,
    params?: ReadWorkspaceFileContentParams,) => {
    return [
    `/api/workspaces/${workspaceId}/files/content`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadWorkspaceFileContentQueryOptions = <TData = Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError = Error | HTTPValidationError>(workspaceId: string,
    params: ReadWorkspaceFileContentParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadWorkspaceFileContentQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readWorkspaceFileContent>>> = ({ signal }) => readWorkspaceFileContent(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadWorkspaceFileContentQueryResult = NonNullable<Awaited<ReturnType<typeof readWorkspaceFileContent>>>
export type ReadWorkspaceFileContentQueryError = Error | HTTPValidationError


export function useReadWorkspaceFileContent<TData = Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params: ReadWorkspaceFileContentParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceFileContent>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceFileContent>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceFileContent<TData = Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params: ReadWorkspaceFileContentParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceFileContent>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceFileContent>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceFileContent<TData = Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params: ReadWorkspaceFileContentParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Workspace File Content
 */

export function useReadWorkspaceFileContent<TData = Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params: ReadWorkspaceFileContentParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceFileContent>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadWorkspaceFileContentQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type uploadWorkspaceFileResponse200 = {
  data: FileChangeEventView
  status: 200
}

export type uploadWorkspaceFileResponse401 = {
  data: Error
  status: 401
}

export type uploadWorkspaceFileResponse403 = {
  data: Error
  status: 403
}

export type uploadWorkspaceFileResponse404 = {
  data: Error
  status: 404
}

export type uploadWorkspaceFileResponse413 = {
  data: Error
  status: 413
}

export type uploadWorkspaceFileResponse422 = {
  data: Error
  status: 422
}

export type uploadWorkspaceFileResponseSuccess = (uploadWorkspaceFileResponse200) & {
  headers: Headers;
};
export type uploadWorkspaceFileResponseError = (uploadWorkspaceFileResponse401 | uploadWorkspaceFileResponse403 | uploadWorkspaceFileResponse404 | uploadWorkspaceFileResponse413 | uploadWorkspaceFileResponse422) & {
  headers: Headers;
};

export type uploadWorkspaceFileResponse = (uploadWorkspaceFileResponseSuccess | uploadWorkspaceFileResponseError)

export const getUploadWorkspaceFileUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/files/content`
}

/**
 * @summary Upload Workspace File
 */
export const uploadWorkspaceFile = async (workspaceId: string,
    bodyUploadWorkspaceFile: BodyUploadWorkspaceFile, options?: RequestInit): Promise<uploadWorkspaceFileResponse> => {
    const formData = new FormData();
formData.append(`path`, bodyUploadWorkspaceFile.path);
formData.append(`file`, bodyUploadWorkspaceFile.file);
if(bodyUploadWorkspaceFile.task_id !== undefined && bodyUploadWorkspaceFile.task_id !== null) {
 formData.append(`task_id`, bodyUploadWorkspaceFile.task_id);
 }
if(bodyUploadWorkspaceFile.step_id !== undefined && bodyUploadWorkspaceFile.step_id !== null) {
 formData.append(`step_id`, bodyUploadWorkspaceFile.step_id);
 }

  return customFetch<uploadWorkspaceFileResponse>(getUploadWorkspaceFileUrl(workspaceId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}




export const getUploadWorkspaceFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadWorkspaceFile>>, TError,{workspaceId: string;data: BodyUploadWorkspaceFile}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadWorkspaceFile>>, TError,{workspaceId: string;data: BodyUploadWorkspaceFile}, TContext> => {

const mutationKey = ['uploadWorkspaceFile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadWorkspaceFile>>, {workspaceId: string;data: BodyUploadWorkspaceFile}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  uploadWorkspaceFile(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadWorkspaceFileMutationResult = NonNullable<Awaited<ReturnType<typeof uploadWorkspaceFile>>>
    export type UploadWorkspaceFileMutationBody = BodyUploadWorkspaceFile
    export type UploadWorkspaceFileMutationError = Error

    /**
 * @summary Upload Workspace File
 */
export const useUploadWorkspaceFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadWorkspaceFile>>, TError,{workspaceId: string;data: BodyUploadWorkspaceFile}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof uploadWorkspaceFile>>,
        TError,
        {workspaceId: string;data: BodyUploadWorkspaceFile},
        TContext
      > => {
      return useMutation(getUploadWorkspaceFileMutationOptions(options), queryClient);
    }

export type moveWorkspaceFileResponse200 = {
  data: MoveFileResult
  status: 200
}

export type moveWorkspaceFileResponse401 = {
  data: Error
  status: 401
}

export type moveWorkspaceFileResponse403 = {
  data: Error
  status: 403
}

export type moveWorkspaceFileResponse404 = {
  data: Error
  status: 404
}

export type moveWorkspaceFileResponse409 = {
  data: Error
  status: 409
}

export type moveWorkspaceFileResponse422 = {
  data: Error
  status: 422
}

export type moveWorkspaceFileResponseSuccess = (moveWorkspaceFileResponse200) & {
  headers: Headers;
};
export type moveWorkspaceFileResponseError = (moveWorkspaceFileResponse401 | moveWorkspaceFileResponse403 | moveWorkspaceFileResponse404 | moveWorkspaceFileResponse409 | moveWorkspaceFileResponse422) & {
  headers: Headers;
};

export type moveWorkspaceFileResponse = (moveWorkspaceFileResponseSuccess | moveWorkspaceFileResponseError)

export const getMoveWorkspaceFileUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/files/move`
}

/**
 * @summary Move Workspace File
 */
export const moveWorkspaceFile = async (workspaceId: string,
    moveFileRequest: MoveFileRequest, options?: RequestInit): Promise<moveWorkspaceFileResponse> => {

  return customFetch<moveWorkspaceFileResponse>(getMoveWorkspaceFileUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(moveFileRequest)
  }
);}




export const getMoveWorkspaceFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof moveWorkspaceFile>>, TError,{workspaceId: string;data: MoveFileRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof moveWorkspaceFile>>, TError,{workspaceId: string;data: MoveFileRequest}, TContext> => {

const mutationKey = ['moveWorkspaceFile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof moveWorkspaceFile>>, {workspaceId: string;data: MoveFileRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  moveWorkspaceFile(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MoveWorkspaceFileMutationResult = NonNullable<Awaited<ReturnType<typeof moveWorkspaceFile>>>
    export type MoveWorkspaceFileMutationBody = MoveFileRequest
    export type MoveWorkspaceFileMutationError = Error

    /**
 * @summary Move Workspace File
 */
export const useMoveWorkspaceFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof moveWorkspaceFile>>, TError,{workspaceId: string;data: MoveFileRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof moveWorkspaceFile>>,
        TError,
        {workspaceId: string;data: MoveFileRequest},
        TContext
      > => {
      return useMutation(getMoveWorkspaceFileMutationOptions(options), queryClient);
    }

export type getWorkspaceFileHistoryResponse200 = {
  data: FileHistoryPage
  status: 200
}

export type getWorkspaceFileHistoryResponse401 = {
  data: Error
  status: 401
}

export type getWorkspaceFileHistoryResponse403 = {
  data: Error
  status: 403
}

export type getWorkspaceFileHistoryResponse404 = {
  data: Error
  status: 404
}

export type getWorkspaceFileHistoryResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getWorkspaceFileHistoryResponseSuccess = (getWorkspaceFileHistoryResponse200) & {
  headers: Headers;
};
export type getWorkspaceFileHistoryResponseError = (getWorkspaceFileHistoryResponse401 | getWorkspaceFileHistoryResponse403 | getWorkspaceFileHistoryResponse404 | getWorkspaceFileHistoryResponse422) & {
  headers: Headers;
};

export type getWorkspaceFileHistoryResponse = (getWorkspaceFileHistoryResponseSuccess | getWorkspaceFileHistoryResponseError)

export const getGetWorkspaceFileHistoryUrl = (workspaceId: string,
    params?: GetWorkspaceFileHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/files/history?${stringifiedParams}` : `/api/workspaces/${workspaceId}/files/history`
}

/**
 * @summary Get Workspace File History
 */
export const getWorkspaceFileHistory = async (workspaceId: string,
    params?: GetWorkspaceFileHistoryParams, options?: RequestInit): Promise<getWorkspaceFileHistoryResponse> => {

  return customFetch<getWorkspaceFileHistoryResponse>(getGetWorkspaceFileHistoryUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWorkspaceFileHistoryQueryKey = (workspaceId: string,
    params?: GetWorkspaceFileHistoryParams,) => {
    return [
    `/api/workspaces/${workspaceId}/files/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetWorkspaceFileHistoryQueryOptions = <TData = Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError = Error | HTTPValidationError>(workspaceId: string,
    params?: GetWorkspaceFileHistoryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkspaceFileHistoryQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkspaceFileHistory>>> = ({ signal }) => getWorkspaceFileHistory(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetWorkspaceFileHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkspaceFileHistory>>>
export type GetWorkspaceFileHistoryQueryError = Error | HTTPValidationError


export function useGetWorkspaceFileHistory<TData = Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params: undefined |  GetWorkspaceFileHistoryParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkspaceFileHistory>>,
          TError,
          Awaited<ReturnType<typeof getWorkspaceFileHistory>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetWorkspaceFileHistory<TData = Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params?: GetWorkspaceFileHistoryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkspaceFileHistory>>,
          TError,
          Awaited<ReturnType<typeof getWorkspaceFileHistory>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetWorkspaceFileHistory<TData = Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params?: GetWorkspaceFileHistoryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Workspace File History
 */

export function useGetWorkspaceFileHistory<TData = Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    params?: GetWorkspaceFileHistoryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkspaceFileHistory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetWorkspaceFileHistoryQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listWorkspaceSecretsResponse200 = {
  data: WorkspaceSecretList
  status: 200
}

export type listWorkspaceSecretsResponse401 = {
  data: Error
  status: 401
}

export type listWorkspaceSecretsResponse403 = {
  data: Error
  status: 403
}

export type listWorkspaceSecretsResponse404 = {
  data: Error
  status: 404
}

export type listWorkspaceSecretsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listWorkspaceSecretsResponseSuccess = (listWorkspaceSecretsResponse200) & {
  headers: Headers;
};
export type listWorkspaceSecretsResponseError = (listWorkspaceSecretsResponse401 | listWorkspaceSecretsResponse403 | listWorkspaceSecretsResponse404 | listWorkspaceSecretsResponse422) & {
  headers: Headers;
};

export type listWorkspaceSecretsResponse = (listWorkspaceSecretsResponseSuccess | listWorkspaceSecretsResponseError)

export const getListWorkspaceSecretsUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/secrets`
}

/**
 * @summary List Workspace Secrets
 */
export const listWorkspaceSecrets = async (workspaceId: string, options?: RequestInit): Promise<listWorkspaceSecretsResponse> => {

  return customFetch<listWorkspaceSecretsResponse>(getListWorkspaceSecretsUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWorkspaceSecretsQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/secrets`
    ] as const;
    }


export const getListWorkspaceSecretsQueryOptions = <TData = Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError = Error | HTTPValidationError>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWorkspaceSecretsQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkspaceSecrets>>> = ({ signal }) => listWorkspaceSecrets(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListWorkspaceSecretsQueryResult = NonNullable<Awaited<ReturnType<typeof listWorkspaceSecrets>>>
export type ListWorkspaceSecretsQueryError = Error | HTTPValidationError


export function useListWorkspaceSecrets<TData = Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceSecrets>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceSecrets>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaceSecrets<TData = Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceSecrets>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceSecrets>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaceSecrets<TData = Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Workspace Secrets
 */

export function useListWorkspaceSecrets<TData = Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceSecrets>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListWorkspaceSecretsQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type createWorkspaceSecretResponse201 = {
  data: WorkspaceSecretView
  status: 201
}

export type createWorkspaceSecretResponse401 = {
  data: Error
  status: 401
}

export type createWorkspaceSecretResponse403 = {
  data: Error
  status: 403
}

export type createWorkspaceSecretResponse404 = {
  data: Error
  status: 404
}

export type createWorkspaceSecretResponse409 = {
  data: Error
  status: 409
}

export type createWorkspaceSecretResponse413 = {
  data: Error
  status: 413
}

export type createWorkspaceSecretResponse422 = {
  data: Error
  status: 422
}

export type createWorkspaceSecretResponse500 = {
  data: Error
  status: 500
}

export type createWorkspaceSecretResponseSuccess = (createWorkspaceSecretResponse201) & {
  headers: Headers;
};
export type createWorkspaceSecretResponseError = (createWorkspaceSecretResponse401 | createWorkspaceSecretResponse403 | createWorkspaceSecretResponse404 | createWorkspaceSecretResponse409 | createWorkspaceSecretResponse413 | createWorkspaceSecretResponse422 | createWorkspaceSecretResponse500) & {
  headers: Headers;
};

export type createWorkspaceSecretResponse = (createWorkspaceSecretResponseSuccess | createWorkspaceSecretResponseError)

export const getCreateWorkspaceSecretUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/secrets`
}

/**
 * @summary Create Workspace Secret
 */
export const createWorkspaceSecret = async (workspaceId: string,
    createWorkspaceSecretRequest: CreateWorkspaceSecretRequest, options?: RequestInit): Promise<createWorkspaceSecretResponse> => {

  return customFetch<createWorkspaceSecretResponse>(getCreateWorkspaceSecretUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createWorkspaceSecretRequest)
  }
);}




export const getCreateWorkspaceSecretMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkspaceSecret>>, TError,{workspaceId: string;data: CreateWorkspaceSecretRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWorkspaceSecret>>, TError,{workspaceId: string;data: CreateWorkspaceSecretRequest}, TContext> => {

const mutationKey = ['createWorkspaceSecret'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWorkspaceSecret>>, {workspaceId: string;data: CreateWorkspaceSecretRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createWorkspaceSecret(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWorkspaceSecretMutationResult = NonNullable<Awaited<ReturnType<typeof createWorkspaceSecret>>>
    export type CreateWorkspaceSecretMutationBody = CreateWorkspaceSecretRequest
    export type CreateWorkspaceSecretMutationError = Error

    /**
 * @summary Create Workspace Secret
 */
export const useCreateWorkspaceSecret = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkspaceSecret>>, TError,{workspaceId: string;data: CreateWorkspaceSecretRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createWorkspaceSecret>>,
        TError,
        {workspaceId: string;data: CreateWorkspaceSecretRequest},
        TContext
      > => {
      return useMutation(getCreateWorkspaceSecretMutationOptions(options), queryClient);
    }

export type replaceWorkspaceSecretValueResponse200 = {
  data: WorkspaceSecretView
  status: 200
}

export type replaceWorkspaceSecretValueResponse401 = {
  data: Error
  status: 401
}

export type replaceWorkspaceSecretValueResponse403 = {
  data: Error
  status: 403
}

export type replaceWorkspaceSecretValueResponse404 = {
  data: Error
  status: 404
}

export type replaceWorkspaceSecretValueResponse409 = {
  data: Error
  status: 409
}

export type replaceWorkspaceSecretValueResponse413 = {
  data: Error
  status: 413
}

export type replaceWorkspaceSecretValueResponse422 = {
  data: Error
  status: 422
}

export type replaceWorkspaceSecretValueResponse500 = {
  data: Error
  status: 500
}

export type replaceWorkspaceSecretValueResponseSuccess = (replaceWorkspaceSecretValueResponse200) & {
  headers: Headers;
};
export type replaceWorkspaceSecretValueResponseError = (replaceWorkspaceSecretValueResponse401 | replaceWorkspaceSecretValueResponse403 | replaceWorkspaceSecretValueResponse404 | replaceWorkspaceSecretValueResponse409 | replaceWorkspaceSecretValueResponse413 | replaceWorkspaceSecretValueResponse422 | replaceWorkspaceSecretValueResponse500) & {
  headers: Headers;
};

export type replaceWorkspaceSecretValueResponse = (replaceWorkspaceSecretValueResponseSuccess | replaceWorkspaceSecretValueResponseError)

export const getReplaceWorkspaceSecretValueUrl = (workspaceId: string,
    key: string,) => {




  return `/api/workspaces/${workspaceId}/secrets/${key}`
}

/**
 * @summary Replace Workspace Secret Value
 */
export const replaceWorkspaceSecretValue = async (workspaceId: string,
    key: string,
    replaceWorkspaceSecretValueRequest: ReplaceWorkspaceSecretValueRequest, options?: RequestInit): Promise<replaceWorkspaceSecretValueResponse> => {

  return customFetch<replaceWorkspaceSecretValueResponse>(getReplaceWorkspaceSecretValueUrl(workspaceId,key),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(replaceWorkspaceSecretValueRequest)
  }
);}




export const getReplaceWorkspaceSecretValueMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replaceWorkspaceSecretValue>>, TError,{workspaceId: string;key: string;data: ReplaceWorkspaceSecretValueRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof replaceWorkspaceSecretValue>>, TError,{workspaceId: string;key: string;data: ReplaceWorkspaceSecretValueRequest}, TContext> => {

const mutationKey = ['replaceWorkspaceSecretValue'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof replaceWorkspaceSecretValue>>, {workspaceId: string;key: string;data: ReplaceWorkspaceSecretValueRequest}> = (props) => {
          const {workspaceId,key,data} = props ?? {};

          return  replaceWorkspaceSecretValue(workspaceId,key,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReplaceWorkspaceSecretValueMutationResult = NonNullable<Awaited<ReturnType<typeof replaceWorkspaceSecretValue>>>
    export type ReplaceWorkspaceSecretValueMutationBody = ReplaceWorkspaceSecretValueRequest
    export type ReplaceWorkspaceSecretValueMutationError = Error

    /**
 * @summary Replace Workspace Secret Value
 */
export const useReplaceWorkspaceSecretValue = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replaceWorkspaceSecretValue>>, TError,{workspaceId: string;key: string;data: ReplaceWorkspaceSecretValueRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof replaceWorkspaceSecretValue>>,
        TError,
        {workspaceId: string;key: string;data: ReplaceWorkspaceSecretValueRequest},
        TContext
      > => {
      return useMutation(getReplaceWorkspaceSecretValueMutationOptions(options), queryClient);
    }

export type updateWorkspaceSecretDescriptionResponse200 = {
  data: WorkspaceSecretView
  status: 200
}

export type updateWorkspaceSecretDescriptionResponse401 = {
  data: Error
  status: 401
}

export type updateWorkspaceSecretDescriptionResponse403 = {
  data: Error
  status: 403
}

export type updateWorkspaceSecretDescriptionResponse404 = {
  data: Error
  status: 404
}

export type updateWorkspaceSecretDescriptionResponse409 = {
  data: Error
  status: 409
}

export type updateWorkspaceSecretDescriptionResponse413 = {
  data: Error
  status: 413
}

export type updateWorkspaceSecretDescriptionResponse422 = {
  data: Error
  status: 422
}

export type updateWorkspaceSecretDescriptionResponse500 = {
  data: Error
  status: 500
}

export type updateWorkspaceSecretDescriptionResponseSuccess = (updateWorkspaceSecretDescriptionResponse200) & {
  headers: Headers;
};
export type updateWorkspaceSecretDescriptionResponseError = (updateWorkspaceSecretDescriptionResponse401 | updateWorkspaceSecretDescriptionResponse403 | updateWorkspaceSecretDescriptionResponse404 | updateWorkspaceSecretDescriptionResponse409 | updateWorkspaceSecretDescriptionResponse413 | updateWorkspaceSecretDescriptionResponse422 | updateWorkspaceSecretDescriptionResponse500) & {
  headers: Headers;
};

export type updateWorkspaceSecretDescriptionResponse = (updateWorkspaceSecretDescriptionResponseSuccess | updateWorkspaceSecretDescriptionResponseError)

export const getUpdateWorkspaceSecretDescriptionUrl = (workspaceId: string,
    key: string,) => {




  return `/api/workspaces/${workspaceId}/secrets/${key}`
}

/**
 * Edit the description alone — no value required, and none accepted (060 FR-019).
 *
 * Separate from ``PUT`` because these are separate acts with different consequences:
 * one is a correction, the other is irreversible for a secret. A single route that
 * sometimes did both would invite the second by accident.
 * @summary Update Workspace Secret Description
 */
export const updateWorkspaceSecretDescription = async (workspaceId: string,
    key: string,
    updateWorkspaceSecretDescriptionRequest: UpdateWorkspaceSecretDescriptionRequest, options?: RequestInit): Promise<updateWorkspaceSecretDescriptionResponse> => {

  return customFetch<updateWorkspaceSecretDescriptionResponse>(getUpdateWorkspaceSecretDescriptionUrl(workspaceId,key),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateWorkspaceSecretDescriptionRequest)
  }
);}




export const getUpdateWorkspaceSecretDescriptionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceSecretDescription>>, TError,{workspaceId: string;key: string;data: UpdateWorkspaceSecretDescriptionRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceSecretDescription>>, TError,{workspaceId: string;key: string;data: UpdateWorkspaceSecretDescriptionRequest}, TContext> => {

const mutationKey = ['updateWorkspaceSecretDescription'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWorkspaceSecretDescription>>, {workspaceId: string;key: string;data: UpdateWorkspaceSecretDescriptionRequest}> = (props) => {
          const {workspaceId,key,data} = props ?? {};

          return  updateWorkspaceSecretDescription(workspaceId,key,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWorkspaceSecretDescriptionMutationResult = NonNullable<Awaited<ReturnType<typeof updateWorkspaceSecretDescription>>>
    export type UpdateWorkspaceSecretDescriptionMutationBody = UpdateWorkspaceSecretDescriptionRequest
    export type UpdateWorkspaceSecretDescriptionMutationError = Error

    /**
 * @summary Update Workspace Secret Description
 */
export const useUpdateWorkspaceSecretDescription = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkspaceSecretDescription>>, TError,{workspaceId: string;key: string;data: UpdateWorkspaceSecretDescriptionRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateWorkspaceSecretDescription>>,
        TError,
        {workspaceId: string;key: string;data: UpdateWorkspaceSecretDescriptionRequest},
        TContext
      > => {
      return useMutation(getUpdateWorkspaceSecretDescriptionMutationOptions(options), queryClient);
    }

export type deleteWorkspaceSecretResponse204 = {
  data: void
  status: 204
}

export type deleteWorkspaceSecretResponse401 = {
  data: Error
  status: 401
}

export type deleteWorkspaceSecretResponse403 = {
  data: Error
  status: 403
}

export type deleteWorkspaceSecretResponse404 = {
  data: Error
  status: 404
}

export type deleteWorkspaceSecretResponse409 = {
  data: Error
  status: 409
}

export type deleteWorkspaceSecretResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type deleteWorkspaceSecretResponseSuccess = (deleteWorkspaceSecretResponse204) & {
  headers: Headers;
};
export type deleteWorkspaceSecretResponseError = (deleteWorkspaceSecretResponse401 | deleteWorkspaceSecretResponse403 | deleteWorkspaceSecretResponse404 | deleteWorkspaceSecretResponse409 | deleteWorkspaceSecretResponse422) & {
  headers: Headers;
};

export type deleteWorkspaceSecretResponse = (deleteWorkspaceSecretResponseSuccess | deleteWorkspaceSecretResponseError)

export const getDeleteWorkspaceSecretUrl = (workspaceId: string,
    key: string,) => {




  return `/api/workspaces/${workspaceId}/secrets/${key}`
}

/**
 * @summary Delete Workspace Secret
 */
export const deleteWorkspaceSecret = async (workspaceId: string,
    key: string, options?: RequestInit): Promise<deleteWorkspaceSecretResponse> => {

  return customFetch<deleteWorkspaceSecretResponse>(getDeleteWorkspaceSecretUrl(workspaceId,key),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteWorkspaceSecretMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWorkspaceSecret>>, TError,{workspaceId: string;key: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteWorkspaceSecret>>, TError,{workspaceId: string;key: string}, TContext> => {

const mutationKey = ['deleteWorkspaceSecret'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteWorkspaceSecret>>, {workspaceId: string;key: string}> = (props) => {
          const {workspaceId,key} = props ?? {};

          return  deleteWorkspaceSecret(workspaceId,key,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteWorkspaceSecretMutationResult = NonNullable<Awaited<ReturnType<typeof deleteWorkspaceSecret>>>

    export type DeleteWorkspaceSecretMutationError = Error | HTTPValidationError

    /**
 * @summary Delete Workspace Secret
 */
export const useDeleteWorkspaceSecret = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWorkspaceSecret>>, TError,{workspaceId: string;key: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteWorkspaceSecret>>,
        TError,
        {workspaceId: string;key: string},
        TContext
      > => {
      return useMutation(getDeleteWorkspaceSecretMutationOptions(options), queryClient);
    }

export type createTriggerProfileResponse201 = {
  data: TriggerProfileView
  status: 201
}

export type createTriggerProfileResponse401 = {
  data: Error
  status: 401
}

export type createTriggerProfileResponse403 = {
  data: Error
  status: 403
}

export type createTriggerProfileResponse404 = {
  data: Error
  status: 404
}

export type createTriggerProfileResponse422 = {
  data: Error
  status: 422
}

export type createTriggerProfileResponseSuccess = (createTriggerProfileResponse201) & {
  headers: Headers;
};
export type createTriggerProfileResponseError = (createTriggerProfileResponse401 | createTriggerProfileResponse403 | createTriggerProfileResponse404 | createTriggerProfileResponse422) & {
  headers: Headers;
};

export type createTriggerProfileResponse = (createTriggerProfileResponseSuccess | createTriggerProfileResponseError)

export const getCreateTriggerProfileUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/trigger-profiles`
}

/**
 * `workspace_task_change` (Spec 1) is unchanged: `payload`'s schedule fields
 * stay at their contract defaults for it, and `TriggerService.create_profile`
 * already ignores them for that type. For `github`/`gitlab` (spec 065 US2),
 * :func:`validate_scheduled_profile` (T044) runs first — dispatch through
 * `ConnectionService`, never `ConnectionRepository` directly — so a caller sees
 * `404`/`422` for a bad `connection_id`/cadence/`config.repositories` before any
 * row is written or Temporal is ever called.
 * @summary Create Trigger Profile
 */
export const createTriggerProfile = async (workspaceId: string,
    triggerProfileCreate: TriggerProfileCreate, options?: RequestInit): Promise<createTriggerProfileResponse> => {

  return customFetch<createTriggerProfileResponse>(getCreateTriggerProfileUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(triggerProfileCreate)
  }
);}




export const getCreateTriggerProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTriggerProfile>>, TError,{workspaceId: string;data: TriggerProfileCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTriggerProfile>>, TError,{workspaceId: string;data: TriggerProfileCreate}, TContext> => {

const mutationKey = ['createTriggerProfile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTriggerProfile>>, {workspaceId: string;data: TriggerProfileCreate}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createTriggerProfile(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTriggerProfileMutationResult = NonNullable<Awaited<ReturnType<typeof createTriggerProfile>>>
    export type CreateTriggerProfileMutationBody = TriggerProfileCreate
    export type CreateTriggerProfileMutationError = Error

    /**
 * @summary Create Trigger Profile
 */
export const useCreateTriggerProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTriggerProfile>>, TError,{workspaceId: string;data: TriggerProfileCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createTriggerProfile>>,
        TError,
        {workspaceId: string;data: TriggerProfileCreate},
        TContext
      > => {
      return useMutation(getCreateTriggerProfileMutationOptions(options), queryClient);
    }

export type listTriggerProfilesResponse200 = {
  data: TriggerProfileView[]
  status: 200
}

export type listTriggerProfilesResponse401 = {
  data: Error
  status: 401
}

export type listTriggerProfilesResponse403 = {
  data: Error
  status: 403
}

export type listTriggerProfilesResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listTriggerProfilesResponseSuccess = (listTriggerProfilesResponse200) & {
  headers: Headers;
};
export type listTriggerProfilesResponseError = (listTriggerProfilesResponse401 | listTriggerProfilesResponse403 | listTriggerProfilesResponse422) & {
  headers: Headers;
};

export type listTriggerProfilesResponse = (listTriggerProfilesResponseSuccess | listTriggerProfilesResponseError)

export const getListTriggerProfilesUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/trigger-profiles`
}

/**
 * @summary List Trigger Profiles
 */
export const listTriggerProfiles = async (workspaceId: string, options?: RequestInit): Promise<listTriggerProfilesResponse> => {

  return customFetch<listTriggerProfilesResponse>(getListTriggerProfilesUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTriggerProfilesQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/trigger-profiles`
    ] as const;
    }


export const getListTriggerProfilesQueryOptions = <TData = Awaited<ReturnType<typeof listTriggerProfiles>>, TError = Error | HTTPValidationError>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTriggerProfilesQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTriggerProfiles>>> = ({ signal }) => listTriggerProfiles(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfiles>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTriggerProfilesQueryResult = NonNullable<Awaited<ReturnType<typeof listTriggerProfiles>>>
export type ListTriggerProfilesQueryError = Error | HTTPValidationError


export function useListTriggerProfiles<TData = Awaited<ReturnType<typeof listTriggerProfiles>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfiles>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTriggerProfiles>>,
          TError,
          Awaited<ReturnType<typeof listTriggerProfiles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTriggerProfiles<TData = Awaited<ReturnType<typeof listTriggerProfiles>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfiles>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTriggerProfiles>>,
          TError,
          Awaited<ReturnType<typeof listTriggerProfiles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTriggerProfiles<TData = Awaited<ReturnType<typeof listTriggerProfiles>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Trigger Profiles
 */

export function useListTriggerProfiles<TData = Awaited<ReturnType<typeof listTriggerProfiles>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTriggerProfilesQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type getTriggerProfileResponse200 = {
  data: TriggerProfileView
  status: 200
}

export type getTriggerProfileResponse401 = {
  data: Error
  status: 401
}

export type getTriggerProfileResponse403 = {
  data: Error
  status: 403
}

export type getTriggerProfileResponse404 = {
  data: Error
  status: 404
}

export type getTriggerProfileResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getTriggerProfileResponseSuccess = (getTriggerProfileResponse200) & {
  headers: Headers;
};
export type getTriggerProfileResponseError = (getTriggerProfileResponse401 | getTriggerProfileResponse403 | getTriggerProfileResponse404 | getTriggerProfileResponse422) & {
  headers: Headers;
};

export type getTriggerProfileResponse = (getTriggerProfileResponseSuccess | getTriggerProfileResponseError)

export const getGetTriggerProfileUrl = (workspaceId: string,
    triggerProfileId: string,) => {




  return `/api/workspaces/${workspaceId}/trigger-profiles/${triggerProfileId}`
}

/**
 * @summary Get Trigger Profile
 */
export const getTriggerProfile = async (workspaceId: string,
    triggerProfileId: string, options?: RequestInit): Promise<getTriggerProfileResponse> => {

  return customFetch<getTriggerProfileResponse>(getGetTriggerProfileUrl(workspaceId,triggerProfileId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTriggerProfileQueryKey = (workspaceId: string,
    triggerProfileId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/trigger-profiles/${triggerProfileId}`
    ] as const;
    }


export const getGetTriggerProfileQueryOptions = <TData = Awaited<ReturnType<typeof getTriggerProfile>>, TError = Error | HTTPValidationError>(workspaceId: string,
    triggerProfileId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTriggerProfile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTriggerProfileQueryKey(workspaceId,triggerProfileId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTriggerProfile>>> = ({ signal }) => getTriggerProfile(workspaceId,triggerProfileId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && triggerProfileId !== null && triggerProfileId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTriggerProfile>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetTriggerProfileQueryResult = NonNullable<Awaited<ReturnType<typeof getTriggerProfile>>>
export type GetTriggerProfileQueryError = Error | HTTPValidationError


export function useGetTriggerProfile<TData = Awaited<ReturnType<typeof getTriggerProfile>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTriggerProfile>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTriggerProfile>>,
          TError,
          Awaited<ReturnType<typeof getTriggerProfile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTriggerProfile<TData = Awaited<ReturnType<typeof getTriggerProfile>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTriggerProfile>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTriggerProfile>>,
          TError,
          Awaited<ReturnType<typeof getTriggerProfile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTriggerProfile<TData = Awaited<ReturnType<typeof getTriggerProfile>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTriggerProfile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Trigger Profile
 */

export function useGetTriggerProfile<TData = Awaited<ReturnType<typeof getTriggerProfile>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTriggerProfile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetTriggerProfileQueryOptions(workspaceId,triggerProfileId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type updateTriggerProfileResponse200 = {
  data: TriggerProfileView
  status: 200
}

export type updateTriggerProfileResponse401 = {
  data: Error
  status: 401
}

export type updateTriggerProfileResponse403 = {
  data: Error
  status: 403
}

export type updateTriggerProfileResponse404 = {
  data: Error
  status: 404
}

export type updateTriggerProfileResponse422 = {
  data: Error
  status: 422
}

export type updateTriggerProfileResponseSuccess = (updateTriggerProfileResponse200) & {
  headers: Headers;
};
export type updateTriggerProfileResponseError = (updateTriggerProfileResponse401 | updateTriggerProfileResponse403 | updateTriggerProfileResponse404 | updateTriggerProfileResponse422) & {
  headers: Headers;
};

export type updateTriggerProfileResponse = (updateTriggerProfileResponseSuccess | updateTriggerProfileResponseError)

export const getUpdateTriggerProfileUrl = (workspaceId: string,
    triggerProfileId: string,) => {




  return `/api/workspaces/${workspaceId}/trigger-profiles/${triggerProfileId}`
}

/**
 * Three narrow update intents share this one route (contracts/trigger-
 * profile-lifecycle-api.md, contracts/scheduled-trigger-profiles-api.md) —
 * `extra="forbid"` on all three request models is what makes dispatching on
 * the parsed type, rather than a raw body inspection, reliable.
 * `SetTriggerProfileEnabledRequest` (Spec 1, unchanged) works for every
 * profile type; `TriggerProfileUpdate` (spec 074, US1) is a general partial
 * edit for every profile type; `UpdateTriggerScheduleRequest` (US2 AC5) only
 * makes sense for a scheduled (`github`/`gitlab`) profile.
 * @summary Update Trigger Profile
 */
export const updateTriggerProfile = async (workspaceId: string,
    triggerProfileId: string,
    setTriggerProfileEnabledRequestTriggerProfileUpdateUpdateTriggerScheduleRequest: SetTriggerProfileEnabledRequest | TriggerProfileUpdate | UpdateTriggerScheduleRequest, options?: RequestInit): Promise<updateTriggerProfileResponse> => {

  return customFetch<updateTriggerProfileResponse>(getUpdateTriggerProfileUrl(workspaceId,triggerProfileId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setTriggerProfileEnabledRequestTriggerProfileUpdateUpdateTriggerScheduleRequest)
  }
);}




export const getUpdateTriggerProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTriggerProfile>>, TError,{workspaceId: string;triggerProfileId: string;data: SetTriggerProfileEnabledRequest | TriggerProfileUpdate | UpdateTriggerScheduleRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTriggerProfile>>, TError,{workspaceId: string;triggerProfileId: string;data: SetTriggerProfileEnabledRequest | TriggerProfileUpdate | UpdateTriggerScheduleRequest}, TContext> => {

const mutationKey = ['updateTriggerProfile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTriggerProfile>>, {workspaceId: string;triggerProfileId: string;data: SetTriggerProfileEnabledRequest | TriggerProfileUpdate | UpdateTriggerScheduleRequest}> = (props) => {
          const {workspaceId,triggerProfileId,data} = props ?? {};

          return  updateTriggerProfile(workspaceId,triggerProfileId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTriggerProfileMutationResult = NonNullable<Awaited<ReturnType<typeof updateTriggerProfile>>>
    export type UpdateTriggerProfileMutationBody = SetTriggerProfileEnabledRequest | TriggerProfileUpdate | UpdateTriggerScheduleRequest
    export type UpdateTriggerProfileMutationError = Error

    /**
 * @summary Update Trigger Profile
 */
export const useUpdateTriggerProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTriggerProfile>>, TError,{workspaceId: string;triggerProfileId: string;data: SetTriggerProfileEnabledRequest | TriggerProfileUpdate | UpdateTriggerScheduleRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateTriggerProfile>>,
        TError,
        {workspaceId: string;triggerProfileId: string;data: SetTriggerProfileEnabledRequest | TriggerProfileUpdate | UpdateTriggerScheduleRequest},
        TContext
      > => {
      return useMutation(getUpdateTriggerProfileMutationOptions(options), queryClient);
    }

export type deleteTriggerProfileResponse204 = {
  data: void
  status: 204
}

export type deleteTriggerProfileResponse401 = {
  data: Error
  status: 401
}

export type deleteTriggerProfileResponse403 = {
  data: Error
  status: 403
}

export type deleteTriggerProfileResponse404 = {
  data: Error
  status: 404
}

export type deleteTriggerProfileResponse422 = {
  data: Error
  status: 422
}

export type deleteTriggerProfileResponseSuccess = (deleteTriggerProfileResponse204) & {
  headers: Headers;
};
export type deleteTriggerProfileResponseError = (deleteTriggerProfileResponse401 | deleteTriggerProfileResponse403 | deleteTriggerProfileResponse404 | deleteTriggerProfileResponse422) & {
  headers: Headers;
};

export type deleteTriggerProfileResponse = (deleteTriggerProfileResponseSuccess | deleteTriggerProfileResponseError)

export const getDeleteTriggerProfileUrl = (workspaceId: string,
    triggerProfileId: string,) => {




  return `/api/workspaces/${workspaceId}/trigger-profiles/${triggerProfileId}`
}

/**
 * Soft-delete (spec 074, US2) — not reversible; run/signal history stays
 * readable via the unchanged `GET .../runs`/`.../signals` routes below.
 * @summary Delete Trigger Profile
 */
export const deleteTriggerProfile = async (workspaceId: string,
    triggerProfileId: string, options?: RequestInit): Promise<deleteTriggerProfileResponse> => {

  return customFetch<deleteTriggerProfileResponse>(getDeleteTriggerProfileUrl(workspaceId,triggerProfileId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteTriggerProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTriggerProfile>>, TError,{workspaceId: string;triggerProfileId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTriggerProfile>>, TError,{workspaceId: string;triggerProfileId: string}, TContext> => {

const mutationKey = ['deleteTriggerProfile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTriggerProfile>>, {workspaceId: string;triggerProfileId: string}> = (props) => {
          const {workspaceId,triggerProfileId} = props ?? {};

          return  deleteTriggerProfile(workspaceId,triggerProfileId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTriggerProfileMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTriggerProfile>>>

    export type DeleteTriggerProfileMutationError = Error

    /**
 * @summary Delete Trigger Profile
 */
export const useDeleteTriggerProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTriggerProfile>>, TError,{workspaceId: string;triggerProfileId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteTriggerProfile>>,
        TError,
        {workspaceId: string;triggerProfileId: string},
        TContext
      > => {
      return useMutation(getDeleteTriggerProfileMutationOptions(options), queryClient);
    }

export type listTriggerProfileRunsResponse200 = {
  data: TriggerProfileRunView[]
  status: 200
}

export type listTriggerProfileRunsResponse401 = {
  data: Error
  status: 401
}

export type listTriggerProfileRunsResponse403 = {
  data: Error
  status: 403
}

export type listTriggerProfileRunsResponse404 = {
  data: Error
  status: 404
}

export type listTriggerProfileRunsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listTriggerProfileRunsResponseSuccess = (listTriggerProfileRunsResponse200) & {
  headers: Headers;
};
export type listTriggerProfileRunsResponseError = (listTriggerProfileRunsResponse401 | listTriggerProfileRunsResponse403 | listTriggerProfileRunsResponse404 | listTriggerProfileRunsResponse422) & {
  headers: Headers;
};

export type listTriggerProfileRunsResponse = (listTriggerProfileRunsResponseSuccess | listTriggerProfileRunsResponseError)

export const getListTriggerProfileRunsUrl = (workspaceId: string,
    triggerProfileId: string,) => {




  return `/api/workspaces/${workspaceId}/trigger-profiles/${triggerProfileId}/runs`
}

/**
 * @summary List Trigger Profile Runs
 */
export const listTriggerProfileRuns = async (workspaceId: string,
    triggerProfileId: string, options?: RequestInit): Promise<listTriggerProfileRunsResponse> => {

  return customFetch<listTriggerProfileRunsResponse>(getListTriggerProfileRunsUrl(workspaceId,triggerProfileId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTriggerProfileRunsQueryKey = (workspaceId: string,
    triggerProfileId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/trigger-profiles/${triggerProfileId}/runs`
    ] as const;
    }


export const getListTriggerProfileRunsQueryOptions = <TData = Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError = Error | HTTPValidationError>(workspaceId: string,
    triggerProfileId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTriggerProfileRunsQueryKey(workspaceId,triggerProfileId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTriggerProfileRuns>>> = ({ signal }) => listTriggerProfileRuns(workspaceId,triggerProfileId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && triggerProfileId !== null && triggerProfileId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTriggerProfileRunsQueryResult = NonNullable<Awaited<ReturnType<typeof listTriggerProfileRuns>>>
export type ListTriggerProfileRunsQueryError = Error | HTTPValidationError


export function useListTriggerProfileRuns<TData = Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTriggerProfileRuns>>,
          TError,
          Awaited<ReturnType<typeof listTriggerProfileRuns>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTriggerProfileRuns<TData = Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTriggerProfileRuns>>,
          TError,
          Awaited<ReturnType<typeof listTriggerProfileRuns>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTriggerProfileRuns<TData = Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Trigger Profile Runs
 */

export function useListTriggerProfileRuns<TData = Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileRuns>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTriggerProfileRunsQueryOptions(workspaceId,triggerProfileId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listTriggerProfileSignalsResponse200 = {
  data: SignalListPage
  status: 200
}

export type listTriggerProfileSignalsResponse401 = {
  data: Error
  status: 401
}

export type listTriggerProfileSignalsResponse403 = {
  data: Error
  status: 403
}

export type listTriggerProfileSignalsResponse404 = {
  data: Error
  status: 404
}

export type listTriggerProfileSignalsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listTriggerProfileSignalsResponseSuccess = (listTriggerProfileSignalsResponse200) & {
  headers: Headers;
};
export type listTriggerProfileSignalsResponseError = (listTriggerProfileSignalsResponse401 | listTriggerProfileSignalsResponse403 | listTriggerProfileSignalsResponse404 | listTriggerProfileSignalsResponse422) & {
  headers: Headers;
};

export type listTriggerProfileSignalsResponse = (listTriggerProfileSignalsResponseSuccess | listTriggerProfileSignalsResponseError)

export const getListTriggerProfileSignalsUrl = (workspaceId: string,
    triggerProfileId: string,
    params?: ListTriggerProfileSignalsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/trigger-profiles/${triggerProfileId}/signals?${stringifiedParams}` : `/api/workspaces/${workspaceId}/trigger-profiles/${triggerProfileId}/signals`
}

/**
 * A bounded page of what arrived, newest first (spec 090).
 *
 * Out-of-range ``limit``/``offset`` are rejected as 422 rather than clamped,
 * matching ``workspaces``' own listing: a silently clamped page tells the caller
 * it got what it asked for.
 * @summary List Trigger Profile Signals
 */
export const listTriggerProfileSignals = async (workspaceId: string,
    triggerProfileId: string,
    params?: ListTriggerProfileSignalsParams, options?: RequestInit): Promise<listTriggerProfileSignalsResponse> => {

  return customFetch<listTriggerProfileSignalsResponse>(getListTriggerProfileSignalsUrl(workspaceId,triggerProfileId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTriggerProfileSignalsQueryKey = (workspaceId: string,
    triggerProfileId: string,
    params?: ListTriggerProfileSignalsParams,) => {
    return [
    `/api/workspaces/${workspaceId}/trigger-profiles/${triggerProfileId}/signals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTriggerProfileSignalsQueryOptions = <TData = Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError = Error | HTTPValidationError>(workspaceId: string,
    triggerProfileId: string,
    params?: ListTriggerProfileSignalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTriggerProfileSignalsQueryKey(workspaceId,triggerProfileId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTriggerProfileSignals>>> = ({ signal }) => listTriggerProfileSignals(workspaceId,triggerProfileId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && triggerProfileId !== null && triggerProfileId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTriggerProfileSignalsQueryResult = NonNullable<Awaited<ReturnType<typeof listTriggerProfileSignals>>>
export type ListTriggerProfileSignalsQueryError = Error | HTTPValidationError


export function useListTriggerProfileSignals<TData = Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string,
    params: undefined |  ListTriggerProfileSignalsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTriggerProfileSignals>>,
          TError,
          Awaited<ReturnType<typeof listTriggerProfileSignals>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTriggerProfileSignals<TData = Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string,
    params?: ListTriggerProfileSignalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTriggerProfileSignals>>,
          TError,
          Awaited<ReturnType<typeof listTriggerProfileSignals>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTriggerProfileSignals<TData = Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string,
    params?: ListTriggerProfileSignalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Trigger Profile Signals
 */

export function useListTriggerProfileSignals<TData = Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    triggerProfileId: string,
    params?: ListTriggerProfileSignalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTriggerProfileSignals>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTriggerProfileSignalsQueryOptions(workspaceId,triggerProfileId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type getVariableManifestResponse200 = {
  data: VariableManifest
  status: 200
}

export type getVariableManifestResponse401 = {
  data: Error
  status: 401
}

export type getVariableManifestResponse403 = {
  data: Error
  status: 403
}

export type getVariableManifestResponse404 = {
  data: Error
  status: 404
}

export type getVariableManifestResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getVariableManifestResponseSuccess = (getVariableManifestResponse200) & {
  headers: Headers;
};
export type getVariableManifestResponseError = (getVariableManifestResponse401 | getVariableManifestResponse403 | getVariableManifestResponse404 | getVariableManifestResponse422) & {
  headers: Headers;
};

export type getVariableManifestResponse = (getVariableManifestResponseSuccess | getVariableManifestResponseError)

export const getGetVariableManifestUrl = (workspaceId: string,
    type: string,) => {




  return `/api/workspaces/${workspaceId}/trigger-profile-types/${type}/variable-manifest`
}

/**
 * Static, driver-owned template-autocomplete data (spec 074, US3, FR-009,
 * research.md D-MAN1) — a direct `VARIABLE_MANIFESTS` lookup, never a
 * `TriggerService` round trip: this content is fixed per driver `type` and
 * does not vary by workspace or by any stored `TriggerProfile` row.
 * `workspace_id` is unused beyond routing/auth prefix consistency.
 * @summary Get Variable Manifest
 */
export const getVariableManifest = async (workspaceId: string,
    type: string, options?: RequestInit): Promise<getVariableManifestResponse> => {

  return customFetch<getVariableManifestResponse>(getGetVariableManifestUrl(workspaceId,type),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVariableManifestQueryKey = (workspaceId: string,
    type: string,) => {
    return [
    `/api/workspaces/${workspaceId}/trigger-profile-types/${type}/variable-manifest`
    ] as const;
    }


export const getGetVariableManifestQueryOptions = <TData = Awaited<ReturnType<typeof getVariableManifest>>, TError = Error | HTTPValidationError>(workspaceId: string,
    type: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVariableManifest>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVariableManifestQueryKey(workspaceId,type);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVariableManifest>>> = ({ signal }) => getVariableManifest(workspaceId,type, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && type !== null && type !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVariableManifest>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetVariableManifestQueryResult = NonNullable<Awaited<ReturnType<typeof getVariableManifest>>>
export type GetVariableManifestQueryError = Error | HTTPValidationError


export function useGetVariableManifest<TData = Awaited<ReturnType<typeof getVariableManifest>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    type: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVariableManifest>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVariableManifest>>,
          TError,
          Awaited<ReturnType<typeof getVariableManifest>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetVariableManifest<TData = Awaited<ReturnType<typeof getVariableManifest>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    type: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVariableManifest>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVariableManifest>>,
          TError,
          Awaited<ReturnType<typeof getVariableManifest>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetVariableManifest<TData = Awaited<ReturnType<typeof getVariableManifest>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    type: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVariableManifest>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Variable Manifest
 */

export function useGetVariableManifest<TData = Awaited<ReturnType<typeof getVariableManifest>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    type: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVariableManifest>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetVariableManifestQueryOptions(workspaceId,type,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type createLabelResponse201 = {
  data: LabelView
  status: 201
}

export type createLabelResponse401 = {
  data: Error
  status: 401
}

export type createLabelResponse403 = {
  data: Error
  status: 403
}

export type createLabelResponse404 = {
  data: Error
  status: 404
}

export type createLabelResponse409 = {
  data: Error
  status: 409
}

export type createLabelResponse422 = {
  data: Error
  status: 422
}

export type createLabelResponseSuccess = (createLabelResponse201) & {
  headers: Headers;
};
export type createLabelResponseError = (createLabelResponse401 | createLabelResponse403 | createLabelResponse404 | createLabelResponse409 | createLabelResponse422) & {
  headers: Headers;
};

export type createLabelResponse = (createLabelResponseSuccess | createLabelResponseError)

export const getCreateLabelUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/labels`
}

/**
 * @summary Create Label
 */
export const createLabel = async (workspaceId: string,
    createLabelRequest: CreateLabelRequest, options?: RequestInit): Promise<createLabelResponse> => {

  return customFetch<createLabelResponse>(getCreateLabelUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createLabelRequest)
  }
);}




export const getCreateLabelMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLabel>>, TError,{workspaceId: string;data: CreateLabelRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createLabel>>, TError,{workspaceId: string;data: CreateLabelRequest}, TContext> => {

const mutationKey = ['createLabel'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createLabel>>, {workspaceId: string;data: CreateLabelRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createLabel(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateLabelMutationResult = NonNullable<Awaited<ReturnType<typeof createLabel>>>
    export type CreateLabelMutationBody = CreateLabelRequest
    export type CreateLabelMutationError = Error

    /**
 * @summary Create Label
 */
export const useCreateLabel = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLabel>>, TError,{workspaceId: string;data: CreateLabelRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createLabel>>,
        TError,
        {workspaceId: string;data: CreateLabelRequest},
        TContext
      > => {
      return useMutation(getCreateLabelMutationOptions(options), queryClient);
    }

export type updateLabelResponse200 = {
  data: LabelView
  status: 200
}

export type updateLabelResponse401 = {
  data: Error
  status: 401
}

export type updateLabelResponse403 = {
  data: Error
  status: 403
}

export type updateLabelResponse404 = {
  data: Error
  status: 404
}

export type updateLabelResponse409 = {
  data: Error
  status: 409
}

export type updateLabelResponse422 = {
  data: Error
  status: 422
}

export type updateLabelResponseSuccess = (updateLabelResponse200) & {
  headers: Headers;
};
export type updateLabelResponseError = (updateLabelResponse401 | updateLabelResponse403 | updateLabelResponse404 | updateLabelResponse409 | updateLabelResponse422) & {
  headers: Headers;
};

export type updateLabelResponse = (updateLabelResponseSuccess | updateLabelResponseError)

export const getUpdateLabelUrl = (workspaceId: string,
    labelId: string,) => {




  return `/api/workspaces/${workspaceId}/labels/${labelId}`
}

/**
 * @summary Update Label
 */
export const updateLabel = async (workspaceId: string,
    labelId: string,
    updateLabelRequest: UpdateLabelRequest, options?: RequestInit): Promise<updateLabelResponse> => {

  return customFetch<updateLabelResponse>(getUpdateLabelUrl(workspaceId,labelId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateLabelRequest)
  }
);}




export const getUpdateLabelMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateLabel>>, TError,{workspaceId: string;labelId: string;data: UpdateLabelRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateLabel>>, TError,{workspaceId: string;labelId: string;data: UpdateLabelRequest}, TContext> => {

const mutationKey = ['updateLabel'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateLabel>>, {workspaceId: string;labelId: string;data: UpdateLabelRequest}> = (props) => {
          const {workspaceId,labelId,data} = props ?? {};

          return  updateLabel(workspaceId,labelId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateLabelMutationResult = NonNullable<Awaited<ReturnType<typeof updateLabel>>>
    export type UpdateLabelMutationBody = UpdateLabelRequest
    export type UpdateLabelMutationError = Error

    /**
 * @summary Update Label
 */
export const useUpdateLabel = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateLabel>>, TError,{workspaceId: string;labelId: string;data: UpdateLabelRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateLabel>>,
        TError,
        {workspaceId: string;labelId: string;data: UpdateLabelRequest},
        TContext
      > => {
      return useMutation(getUpdateLabelMutationOptions(options), queryClient);
    }

export type deleteLabelResponse204 = {
  data: void
  status: 204
}

export type deleteLabelResponse401 = {
  data: Error
  status: 401
}

export type deleteLabelResponse403 = {
  data: Error
  status: 403
}

export type deleteLabelResponse404 = {
  data: Error
  status: 404
}

export type deleteLabelResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type deleteLabelResponseSuccess = (deleteLabelResponse204) & {
  headers: Headers;
};
export type deleteLabelResponseError = (deleteLabelResponse401 | deleteLabelResponse403 | deleteLabelResponse404 | deleteLabelResponse422) & {
  headers: Headers;
};

export type deleteLabelResponse = (deleteLabelResponseSuccess | deleteLabelResponseError)

export const getDeleteLabelUrl = (workspaceId: string,
    labelId: string,) => {




  return `/api/workspaces/${workspaceId}/labels/${labelId}`
}

/**
 * @summary Delete Label
 */
export const deleteLabel = async (workspaceId: string,
    labelId: string, options?: RequestInit): Promise<deleteLabelResponse> => {

  return customFetch<deleteLabelResponse>(getDeleteLabelUrl(workspaceId,labelId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteLabelMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteLabel>>, TError,{workspaceId: string;labelId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteLabel>>, TError,{workspaceId: string;labelId: string}, TContext> => {

const mutationKey = ['deleteLabel'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteLabel>>, {workspaceId: string;labelId: string}> = (props) => {
          const {workspaceId,labelId} = props ?? {};

          return  deleteLabel(workspaceId,labelId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteLabelMutationResult = NonNullable<Awaited<ReturnType<typeof deleteLabel>>>

    export type DeleteLabelMutationError = Error | HTTPValidationError

    /**
 * @summary Delete Label
 */
export const useDeleteLabel = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteLabel>>, TError,{workspaceId: string;labelId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteLabel>>,
        TError,
        {workspaceId: string;labelId: string},
        TContext
      > => {
      return useMutation(getDeleteLabelMutationOptions(options), queryClient);
    }

export type readWorkspaceUsageResponse200 = {
  data: WorkspaceUsageSummary
  status: 200
}

export type readWorkspaceUsageResponse401 = {
  data: Error
  status: 401
}

export type readWorkspaceUsageResponse403 = {
  data: Error
  status: 403
}

export type readWorkspaceUsageResponse404 = {
  data: Error
  status: 404
}

export type readWorkspaceUsageResponse422 = {
  data: Error
  status: 422
}

export type readWorkspaceUsageResponseSuccess = (readWorkspaceUsageResponse200) & {
  headers: Headers;
};
export type readWorkspaceUsageResponseError = (readWorkspaceUsageResponse401 | readWorkspaceUsageResponse403 | readWorkspaceUsageResponse404 | readWorkspaceUsageResponse422) & {
  headers: Headers;
};

export type readWorkspaceUsageResponse = (readWorkspaceUsageResponseSuccess | readWorkspaceUsageResponseError)

export const getReadWorkspaceUsageUrl = (workspaceId: string,
    params?: ReadWorkspaceUsageParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/usage?${stringifiedParams}` : `/api/workspaces/${workspaceId}/usage`
}

/**
 * @summary Read Workspace Usage
 */
export const readWorkspaceUsage = async (workspaceId: string,
    params?: ReadWorkspaceUsageParams, options?: RequestInit): Promise<readWorkspaceUsageResponse> => {

  return customFetch<readWorkspaceUsageResponse>(getReadWorkspaceUsageUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadWorkspaceUsageQueryKey = (workspaceId: string,
    params?: ReadWorkspaceUsageParams,) => {
    return [
    `/api/workspaces/${workspaceId}/usage`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadWorkspaceUsageQueryOptions = <TData = Awaited<ReturnType<typeof readWorkspaceUsage>>, TError = Error>(workspaceId: string,
    params?: ReadWorkspaceUsageParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceUsage>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadWorkspaceUsageQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readWorkspaceUsage>>> = ({ signal }) => readWorkspaceUsage(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceUsage>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadWorkspaceUsageQueryResult = NonNullable<Awaited<ReturnType<typeof readWorkspaceUsage>>>
export type ReadWorkspaceUsageQueryError = Error


export function useReadWorkspaceUsage<TData = Awaited<ReturnType<typeof readWorkspaceUsage>>, TError = Error>(
 workspaceId: string,
    params: undefined |  ReadWorkspaceUsageParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceUsage>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceUsage>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceUsage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceUsage<TData = Awaited<ReturnType<typeof readWorkspaceUsage>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceUsageParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceUsage>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceUsage>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceUsage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceUsage<TData = Awaited<ReturnType<typeof readWorkspaceUsage>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceUsageParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceUsage>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Workspace Usage
 */

export function useReadWorkspaceUsage<TData = Awaited<ReturnType<typeof readWorkspaceUsage>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceUsageParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceUsage>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadWorkspaceUsageQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readWorkspaceAiModelsResponse200 = {
  data: WorkspaceAIModels
  status: 200
}

export type readWorkspaceAiModelsResponse401 = {
  data: Error
  status: 401
}

export type readWorkspaceAiModelsResponse403 = {
  data: Error
  status: 403
}

export type readWorkspaceAiModelsResponse404 = {
  data: Error
  status: 404
}

export type readWorkspaceAiModelsResponse422 = {
  data: Error
  status: 422
}

export type readWorkspaceAiModelsResponseSuccess = (readWorkspaceAiModelsResponse200) & {
  headers: Headers;
};
export type readWorkspaceAiModelsResponseError = (readWorkspaceAiModelsResponse401 | readWorkspaceAiModelsResponse403 | readWorkspaceAiModelsResponse404 | readWorkspaceAiModelsResponse422) & {
  headers: Headers;
};

export type readWorkspaceAiModelsResponse = (readWorkspaceAiModelsResponseSuccess | readWorkspaceAiModelsResponseError)

export const getReadWorkspaceAiModelsUrl = (workspaceId: string,
    params?: ReadWorkspaceAiModelsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/ai-models?${stringifiedParams}` : `/api/workspaces/${workspaceId}/ai-models`
}

/**
 * Each LLM profile the workspace uses, where, and the period's runs and tokens.
 * @summary Read Workspace Ai Models
 */
export const readWorkspaceAiModels = async (workspaceId: string,
    params?: ReadWorkspaceAiModelsParams, options?: RequestInit): Promise<readWorkspaceAiModelsResponse> => {

  return customFetch<readWorkspaceAiModelsResponse>(getReadWorkspaceAiModelsUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadWorkspaceAiModelsQueryKey = (workspaceId: string,
    params?: ReadWorkspaceAiModelsParams,) => {
    return [
    `/api/workspaces/${workspaceId}/ai-models`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadWorkspaceAiModelsQueryOptions = <TData = Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError = Error>(workspaceId: string,
    params?: ReadWorkspaceAiModelsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadWorkspaceAiModelsQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readWorkspaceAiModels>>> = ({ signal }) => readWorkspaceAiModels(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadWorkspaceAiModelsQueryResult = NonNullable<Awaited<ReturnType<typeof readWorkspaceAiModels>>>
export type ReadWorkspaceAiModelsQueryError = Error


export function useReadWorkspaceAiModels<TData = Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError = Error>(
 workspaceId: string,
    params: undefined |  ReadWorkspaceAiModelsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceAiModels>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceAiModels>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceAiModels<TData = Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceAiModelsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceAiModels>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceAiModels>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceAiModels<TData = Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceAiModelsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Workspace Ai Models
 */

export function useReadWorkspaceAiModels<TData = Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceAiModelsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModels>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadWorkspaceAiModelsQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readWorkspaceAiModelSpendResponse200 = {
  data: WorkspaceAIModelSpend
  status: 200
}

export type readWorkspaceAiModelSpendResponse401 = {
  data: Error
  status: 401
}

export type readWorkspaceAiModelSpendResponse403 = {
  data: Error
  status: 403
}

export type readWorkspaceAiModelSpendResponse404 = {
  data: Error
  status: 404
}

export type readWorkspaceAiModelSpendResponse422 = {
  data: Error
  status: 422
}

export type readWorkspaceAiModelSpendResponseSuccess = (readWorkspaceAiModelSpendResponse200) & {
  headers: Headers;
};
export type readWorkspaceAiModelSpendResponseError = (readWorkspaceAiModelSpendResponse401 | readWorkspaceAiModelSpendResponse403 | readWorkspaceAiModelSpendResponse404 | readWorkspaceAiModelSpendResponse422) & {
  headers: Headers;
};

export type readWorkspaceAiModelSpendResponse = (readWorkspaceAiModelSpendResponseSuccess | readWorkspaceAiModelSpendResponseError)

export const getReadWorkspaceAiModelSpendUrl = (workspaceId: string,
    params?: ReadWorkspaceAiModelSpendParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/ai-models/spend?${stringifiedParams}` : `/api/workspaces/${workspaceId}/ai-models/spend`
}

/**
 * The period's spend by workflow, profile and connection, each with what it is
 * made of. Owners and organization administrators only.
 * @summary Read Workspace Ai Model Spend
 */
export const readWorkspaceAiModelSpend = async (workspaceId: string,
    params?: ReadWorkspaceAiModelSpendParams, options?: RequestInit): Promise<readWorkspaceAiModelSpendResponse> => {

  return customFetch<readWorkspaceAiModelSpendResponse>(getReadWorkspaceAiModelSpendUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadWorkspaceAiModelSpendQueryKey = (workspaceId: string,
    params?: ReadWorkspaceAiModelSpendParams,) => {
    return [
    `/api/workspaces/${workspaceId}/ai-models/spend`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadWorkspaceAiModelSpendQueryOptions = <TData = Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError = Error>(workspaceId: string,
    params?: ReadWorkspaceAiModelSpendParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadWorkspaceAiModelSpendQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>> = ({ signal }) => readWorkspaceAiModelSpend(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadWorkspaceAiModelSpendQueryResult = NonNullable<Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>>
export type ReadWorkspaceAiModelSpendQueryError = Error


export function useReadWorkspaceAiModelSpend<TData = Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError = Error>(
 workspaceId: string,
    params: undefined |  ReadWorkspaceAiModelSpendParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceAiModelSpend<TData = Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceAiModelSpendParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceAiModelSpend<TData = Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceAiModelSpendParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Workspace Ai Model Spend
 */

export function useReadWorkspaceAiModelSpend<TData = Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError = Error>(
 workspaceId: string,
    params?: ReadWorkspaceAiModelSpendParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceAiModelSpend>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadWorkspaceAiModelSpendQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readOrganizationUsageResponse200 = {
  data: OrganizationUsageSummary
  status: 200
}

export type readOrganizationUsageResponse401 = {
  data: Error
  status: 401
}

export type readOrganizationUsageResponse403 = {
  data: Error
  status: 403
}

export type readOrganizationUsageResponse404 = {
  data: Error
  status: 404
}

export type readOrganizationUsageResponse422 = {
  data: Error
  status: 422
}

export type readOrganizationUsageResponseSuccess = (readOrganizationUsageResponse200) & {
  headers: Headers;
};
export type readOrganizationUsageResponseError = (readOrganizationUsageResponse401 | readOrganizationUsageResponse403 | readOrganizationUsageResponse404 | readOrganizationUsageResponse422) & {
  headers: Headers;
};

export type readOrganizationUsageResponse = (readOrganizationUsageResponseSuccess | readOrganizationUsageResponseError)

export const getReadOrganizationUsageUrl = (params?: ReadOrganizationUsageParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/organization/usage?${stringifiedParams}` : `/api/organization/usage`
}

/**
 * @summary Read Organization Usage
 */
export const readOrganizationUsage = async (params?: ReadOrganizationUsageParams, options?: RequestInit): Promise<readOrganizationUsageResponse> => {

  return customFetch<readOrganizationUsageResponse>(getReadOrganizationUsageUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadOrganizationUsageQueryKey = (params?: ReadOrganizationUsageParams,) => {
    return [
    `/api/organization/usage`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadOrganizationUsageQueryOptions = <TData = Awaited<ReturnType<typeof readOrganizationUsage>>, TError = Error>(params?: ReadOrganizationUsageParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationUsage>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadOrganizationUsageQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readOrganizationUsage>>> = ({ signal }) => readOrganizationUsage(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readOrganizationUsage>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadOrganizationUsageQueryResult = NonNullable<Awaited<ReturnType<typeof readOrganizationUsage>>>
export type ReadOrganizationUsageQueryError = Error


export function useReadOrganizationUsage<TData = Awaited<ReturnType<typeof readOrganizationUsage>>, TError = Error>(
 params: undefined |  ReadOrganizationUsageParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationUsage>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readOrganizationUsage>>,
          TError,
          Awaited<ReturnType<typeof readOrganizationUsage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadOrganizationUsage<TData = Awaited<ReturnType<typeof readOrganizationUsage>>, TError = Error>(
 params?: ReadOrganizationUsageParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationUsage>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readOrganizationUsage>>,
          TError,
          Awaited<ReturnType<typeof readOrganizationUsage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadOrganizationUsage<TData = Awaited<ReturnType<typeof readOrganizationUsage>>, TError = Error>(
 params?: ReadOrganizationUsageParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationUsage>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Organization Usage
 */

export function useReadOrganizationUsage<TData = Awaited<ReturnType<typeof readOrganizationUsage>>, TError = Error>(
 params?: ReadOrganizationUsageParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationUsage>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadOrganizationUsageQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type setModelPriceResponse201 = {
  data: ModelPriceView
  status: 201
}

export type setModelPriceResponse401 = {
  data: Error
  status: 401
}

export type setModelPriceResponse403 = {
  data: Error
  status: 403
}

export type setModelPriceResponse404 = {
  data: Error
  status: 404
}

export type setModelPriceResponse422 = {
  data: Error
  status: 422
}

export type setModelPriceResponseSuccess = (setModelPriceResponse201) & {
  headers: Headers;
};
export type setModelPriceResponseError = (setModelPriceResponse401 | setModelPriceResponse403 | setModelPriceResponse404 | setModelPriceResponse422) & {
  headers: Headers;
};

export type setModelPriceResponse = (setModelPriceResponseSuccess | setModelPriceResponseError)

export const getSetModelPriceUrl = () => {




  return `/api/organization/usage/prices`
}

/**
 * Record a rate for a model, from an instant (spec 077 US2).
 *
 * ``POST`` and 201, not ``PUT``: this creates a new rate sheet and never replaces one.
 * A ``PUT`` would imply the resource is addressable and overwritable, which is exactly
 * the operation the append-only rule forbids.
 * @summary Set Model Price
 */
export const setModelPrice = async (setModelPriceRequest: SetModelPriceRequest, options?: RequestInit): Promise<setModelPriceResponse> => {

  return customFetch<setModelPriceResponse>(getSetModelPriceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setModelPriceRequest)
  }
);}




export const getSetModelPriceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setModelPrice>>, TError,{data: SetModelPriceRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setModelPrice>>, TError,{data: SetModelPriceRequest}, TContext> => {

const mutationKey = ['setModelPrice'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setModelPrice>>, {data: SetModelPriceRequest}> = (props) => {
          const {data} = props ?? {};

          return  setModelPrice(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetModelPriceMutationResult = NonNullable<Awaited<ReturnType<typeof setModelPrice>>>
    export type SetModelPriceMutationBody = SetModelPriceRequest
    export type SetModelPriceMutationError = Error

    /**
 * @summary Set Model Price
 */
export const useSetModelPrice = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setModelPrice>>, TError,{data: SetModelPriceRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setModelPrice>>,
        TError,
        {data: SetModelPriceRequest},
        TContext
      > => {
      return useMutation(getSetModelPriceMutationOptions(options), queryClient);
    }

export type readUnpricedModelsResponse200 = {
  data: UnpricedModelsView
  status: 200
}

export type readUnpricedModelsResponse401 = {
  data: Error
  status: 401
}

export type readUnpricedModelsResponse403 = {
  data: Error
  status: 403
}

export type readUnpricedModelsResponse404 = {
  data: Error
  status: 404
}

export type readUnpricedModelsResponse422 = {
  data: Error
  status: 422
}

export type readUnpricedModelsResponseSuccess = (readUnpricedModelsResponse200) & {
  headers: Headers;
};
export type readUnpricedModelsResponseError = (readUnpricedModelsResponse401 | readUnpricedModelsResponse403 | readUnpricedModelsResponse404 | readUnpricedModelsResponse422) & {
  headers: Headers;
};

export type readUnpricedModelsResponse = (readUnpricedModelsResponseSuccess | readUnpricedModelsResponseError)

export const getReadUnpricedModelsUrl = (params?: ReadUnpricedModelsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/organization/usage/unpriced-models?${stringifiedParams}` : `/api/organization/usage/unpriced-models`
}

/**
 * Models whose runs no price covers, so an administrator can act (spec 077 US1).
 * @summary Read Unpriced Models
 */
export const readUnpricedModels = async (params?: ReadUnpricedModelsParams, options?: RequestInit): Promise<readUnpricedModelsResponse> => {

  return customFetch<readUnpricedModelsResponse>(getReadUnpricedModelsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadUnpricedModelsQueryKey = (params?: ReadUnpricedModelsParams,) => {
    return [
    `/api/organization/usage/unpriced-models`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadUnpricedModelsQueryOptions = <TData = Awaited<ReturnType<typeof readUnpricedModels>>, TError = Error>(params?: ReadUnpricedModelsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readUnpricedModels>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadUnpricedModelsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readUnpricedModels>>> = ({ signal }) => readUnpricedModels(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readUnpricedModels>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadUnpricedModelsQueryResult = NonNullable<Awaited<ReturnType<typeof readUnpricedModels>>>
export type ReadUnpricedModelsQueryError = Error


export function useReadUnpricedModels<TData = Awaited<ReturnType<typeof readUnpricedModels>>, TError = Error>(
 params: undefined |  ReadUnpricedModelsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readUnpricedModels>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readUnpricedModels>>,
          TError,
          Awaited<ReturnType<typeof readUnpricedModels>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadUnpricedModels<TData = Awaited<ReturnType<typeof readUnpricedModels>>, TError = Error>(
 params?: ReadUnpricedModelsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readUnpricedModels>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readUnpricedModels>>,
          TError,
          Awaited<ReturnType<typeof readUnpricedModels>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadUnpricedModels<TData = Awaited<ReturnType<typeof readUnpricedModels>>, TError = Error>(
 params?: ReadUnpricedModelsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readUnpricedModels>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Unpriced Models
 */

export function useReadUnpricedModels<TData = Awaited<ReturnType<typeof readUnpricedModels>>, TError = Error>(
 params?: ReadUnpricedModelsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readUnpricedModels>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadUnpricedModelsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readModelPricesResponse200 = {
  data: ModelPricesView
  status: 200
}

export type readModelPricesResponse401 = {
  data: Error
  status: 401
}

export type readModelPricesResponse403 = {
  data: Error
  status: 403
}

export type readModelPricesResponse404 = {
  data: Error
  status: 404
}

export type readModelPricesResponse422 = {
  data: Error
  status: 422
}

export type readModelPricesResponseSuccess = (readModelPricesResponse200) & {
  headers: Headers;
};
export type readModelPricesResponseError = (readModelPricesResponse401 | readModelPricesResponse403 | readModelPricesResponse404 | readModelPricesResponse422) & {
  headers: Headers;
};

export type readModelPricesResponse = (readModelPricesResponseSuccess | readModelPricesResponseError)

export const getReadModelPricesUrl = () => {




  return `/api/organization/usage/model-prices`
}

/**
 * Every model's currently-effective price, independent of any period (spec 084).
 * @summary Read Model Prices
 */
export const readModelPrices = async ( options?: RequestInit): Promise<readModelPricesResponse> => {

  return customFetch<readModelPricesResponse>(getReadModelPricesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadModelPricesQueryKey = () => {
    return [
    `/api/organization/usage/model-prices`
    ] as const;
    }


export const getReadModelPricesQueryOptions = <TData = Awaited<ReturnType<typeof readModelPrices>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readModelPrices>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadModelPricesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readModelPrices>>> = ({ signal }) => readModelPrices({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readModelPrices>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadModelPricesQueryResult = NonNullable<Awaited<ReturnType<typeof readModelPrices>>>
export type ReadModelPricesQueryError = Error


export function useReadModelPrices<TData = Awaited<ReturnType<typeof readModelPrices>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readModelPrices>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readModelPrices>>,
          TError,
          Awaited<ReturnType<typeof readModelPrices>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadModelPrices<TData = Awaited<ReturnType<typeof readModelPrices>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readModelPrices>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readModelPrices>>,
          TError,
          Awaited<ReturnType<typeof readModelPrices>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadModelPrices<TData = Awaited<ReturnType<typeof readModelPrices>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readModelPrices>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Model Prices
 */

export function useReadModelPrices<TData = Awaited<ReturnType<typeof readModelPrices>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readModelPrices>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadModelPricesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readWorkflowLimitsResponse200 = {
  data: WorkflowLimitRow[]
  status: 200
}

export type readWorkflowLimitsResponse401 = {
  data: Error
  status: 401
}

export type readWorkflowLimitsResponse403 = {
  data: Error
  status: 403
}

export type readWorkflowLimitsResponse404 = {
  data: Error
  status: 404
}

export type readWorkflowLimitsResponse422 = {
  data: Error
  status: 422
}

export type readWorkflowLimitsResponseSuccess = (readWorkflowLimitsResponse200) & {
  headers: Headers;
};
export type readWorkflowLimitsResponseError = (readWorkflowLimitsResponse401 | readWorkflowLimitsResponse403 | readWorkflowLimitsResponse404 | readWorkflowLimitsResponse422) & {
  headers: Headers;
};

export type readWorkflowLimitsResponse = (readWorkflowLimitsResponseSuccess | readWorkflowLimitsResponseError)

export const getReadWorkflowLimitsUrl = (params?: ReadWorkflowLimitsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/organization/usage/workflow-limits?${stringifiedParams}` : `/api/organization/usage/workflow-limits`
}

/**
 * Every workflow that has a spend limit, with its period cost (spec 080 US1).
 *
 * Lists only capped workflows. Limits are opt-in, so an empty list means nobody has
 * capped anything — not that the question was not asked.
 * @summary Read Workflow Limits
 */
export const readWorkflowLimits = async (params?: ReadWorkflowLimitsParams, options?: RequestInit): Promise<readWorkflowLimitsResponse> => {

  return customFetch<readWorkflowLimitsResponse>(getReadWorkflowLimitsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadWorkflowLimitsQueryKey = (params?: ReadWorkflowLimitsParams,) => {
    return [
    `/api/organization/usage/workflow-limits`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadWorkflowLimitsQueryOptions = <TData = Awaited<ReturnType<typeof readWorkflowLimits>>, TError = Error>(params?: ReadWorkflowLimitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkflowLimits>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadWorkflowLimitsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readWorkflowLimits>>> = ({ signal }) => readWorkflowLimits(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readWorkflowLimits>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadWorkflowLimitsQueryResult = NonNullable<Awaited<ReturnType<typeof readWorkflowLimits>>>
export type ReadWorkflowLimitsQueryError = Error


export function useReadWorkflowLimits<TData = Awaited<ReturnType<typeof readWorkflowLimits>>, TError = Error>(
 params: undefined |  ReadWorkflowLimitsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkflowLimits>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkflowLimits>>,
          TError,
          Awaited<ReturnType<typeof readWorkflowLimits>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkflowLimits<TData = Awaited<ReturnType<typeof readWorkflowLimits>>, TError = Error>(
 params?: ReadWorkflowLimitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkflowLimits>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkflowLimits>>,
          TError,
          Awaited<ReturnType<typeof readWorkflowLimits>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkflowLimits<TData = Awaited<ReturnType<typeof readWorkflowLimits>>, TError = Error>(
 params?: ReadWorkflowLimitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkflowLimits>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Workflow Limits
 */

export function useReadWorkflowLimits<TData = Awaited<ReturnType<typeof readWorkflowLimits>>, TError = Error>(
 params?: ReadWorkflowLimitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkflowLimits>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadWorkflowLimitsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type setWorkflowLimitResponse200 = {
  data: WorkflowLimitView
  status: 200
}

export type setWorkflowLimitResponse401 = {
  data: Error
  status: 401
}

export type setWorkflowLimitResponse403 = {
  data: Error
  status: 403
}

export type setWorkflowLimitResponse404 = {
  data: Error
  status: 404
}

export type setWorkflowLimitResponse422 = {
  data: Error
  status: 422
}

export type setWorkflowLimitResponseSuccess = (setWorkflowLimitResponse200) & {
  headers: Headers;
};
export type setWorkflowLimitResponseError = (setWorkflowLimitResponse401 | setWorkflowLimitResponse403 | setWorkflowLimitResponse404 | setWorkflowLimitResponse422) & {
  headers: Headers;
};

export type setWorkflowLimitResponse = (setWorkflowLimitResponseSuccess | setWorkflowLimitResponseError)

export const getSetWorkflowLimitUrl = (workflowId: string,) => {




  return `/api/organization/usage/workflow-limit/${workflowId}`
}

/**
 * @summary Set Workflow Limit
 */
export const setWorkflowLimit = async (workflowId: string,
    setLimitRequest: SetLimitRequest, options?: RequestInit): Promise<setWorkflowLimitResponse> => {

  return customFetch<setWorkflowLimitResponse>(getSetWorkflowLimitUrl(workflowId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setLimitRequest)
  }
);}




export const getSetWorkflowLimitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setWorkflowLimit>>, TError,{workflowId: string;data: SetLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setWorkflowLimit>>, TError,{workflowId: string;data: SetLimitRequest}, TContext> => {

const mutationKey = ['setWorkflowLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setWorkflowLimit>>, {workflowId: string;data: SetLimitRequest}> = (props) => {
          const {workflowId,data} = props ?? {};

          return  setWorkflowLimit(workflowId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetWorkflowLimitMutationResult = NonNullable<Awaited<ReturnType<typeof setWorkflowLimit>>>
    export type SetWorkflowLimitMutationBody = SetLimitRequest
    export type SetWorkflowLimitMutationError = Error

    /**
 * @summary Set Workflow Limit
 */
export const useSetWorkflowLimit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setWorkflowLimit>>, TError,{workflowId: string;data: SetLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setWorkflowLimit>>,
        TError,
        {workflowId: string;data: SetLimitRequest},
        TContext
      > => {
      return useMutation(getSetWorkflowLimitMutationOptions(options), queryClient);
    }

export type clearWorkflowLimitResponse204 = {
  data: void
  status: 204
}

export type clearWorkflowLimitResponse401 = {
  data: Error
  status: 401
}

export type clearWorkflowLimitResponse403 = {
  data: Error
  status: 403
}

export type clearWorkflowLimitResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type clearWorkflowLimitResponseSuccess = (clearWorkflowLimitResponse204) & {
  headers: Headers;
};
export type clearWorkflowLimitResponseError = (clearWorkflowLimitResponse401 | clearWorkflowLimitResponse403 | clearWorkflowLimitResponse422) & {
  headers: Headers;
};

export type clearWorkflowLimitResponse = (clearWorkflowLimitResponseSuccess | clearWorkflowLimitResponseError)

export const getClearWorkflowLimitUrl = (workflowId: string,) => {




  return `/api/organization/usage/workflow-limit/${workflowId}`
}

/**
 * @summary Clear Workflow Limit
 */
export const clearWorkflowLimit = async (workflowId: string, options?: RequestInit): Promise<clearWorkflowLimitResponse> => {

  return customFetch<clearWorkflowLimitResponse>(getClearWorkflowLimitUrl(workflowId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getClearWorkflowLimitMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearWorkflowLimit>>, TError,{workflowId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearWorkflowLimit>>, TError,{workflowId: string}, TContext> => {

const mutationKey = ['clearWorkflowLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearWorkflowLimit>>, {workflowId: string}> = (props) => {
          const {workflowId} = props ?? {};

          return  clearWorkflowLimit(workflowId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearWorkflowLimitMutationResult = NonNullable<Awaited<ReturnType<typeof clearWorkflowLimit>>>

    export type ClearWorkflowLimitMutationError = Error | HTTPValidationError

    /**
 * @summary Clear Workflow Limit
 */
export const useClearWorkflowLimit = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearWorkflowLimit>>, TError,{workflowId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clearWorkflowLimit>>,
        TError,
        {workflowId: string},
        TContext
      > => {
      return useMutation(getClearWorkflowLimitMutationOptions(options), queryClient);
    }

export type setDefaultLimitResponse200 = {
  data: LimitView
  status: 200
}

export type setDefaultLimitResponse401 = {
  data: Error
  status: 401
}

export type setDefaultLimitResponse403 = {
  data: Error
  status: 403
}

export type setDefaultLimitResponse404 = {
  data: Error
  status: 404
}

export type setDefaultLimitResponse422 = {
  data: Error
  status: 422
}

export type setDefaultLimitResponseSuccess = (setDefaultLimitResponse200) & {
  headers: Headers;
};
export type setDefaultLimitResponseError = (setDefaultLimitResponse401 | setDefaultLimitResponse403 | setDefaultLimitResponse404 | setDefaultLimitResponse422) & {
  headers: Headers;
};

export type setDefaultLimitResponse = (setDefaultLimitResponseSuccess | setDefaultLimitResponseError)

export const getSetDefaultLimitUrl = () => {




  return `/api/organization/usage/limit`
}

/**
 * @summary Set Default Limit
 */
export const setDefaultLimit = async (setLimitRequest: SetLimitRequest, options?: RequestInit): Promise<setDefaultLimitResponse> => {

  return customFetch<setDefaultLimitResponse>(getSetDefaultLimitUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setLimitRequest)
  }
);}




export const getSetDefaultLimitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultLimit>>, TError,{data: SetLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setDefaultLimit>>, TError,{data: SetLimitRequest}, TContext> => {

const mutationKey = ['setDefaultLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setDefaultLimit>>, {data: SetLimitRequest}> = (props) => {
          const {data} = props ?? {};

          return  setDefaultLimit(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetDefaultLimitMutationResult = NonNullable<Awaited<ReturnType<typeof setDefaultLimit>>>
    export type SetDefaultLimitMutationBody = SetLimitRequest
    export type SetDefaultLimitMutationError = Error

    /**
 * @summary Set Default Limit
 */
export const useSetDefaultLimit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultLimit>>, TError,{data: SetLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setDefaultLimit>>,
        TError,
        {data: SetLimitRequest},
        TContext
      > => {
      return useMutation(getSetDefaultLimitMutationOptions(options), queryClient);
    }

export type setWorkspaceLimitResponse200 = {
  data: LimitView
  status: 200
}

export type setWorkspaceLimitResponse401 = {
  data: Error
  status: 401
}

export type setWorkspaceLimitResponse403 = {
  data: Error
  status: 403
}

export type setWorkspaceLimitResponse404 = {
  data: Error
  status: 404
}

export type setWorkspaceLimitResponse422 = {
  data: Error
  status: 422
}

export type setWorkspaceLimitResponseSuccess = (setWorkspaceLimitResponse200) & {
  headers: Headers;
};
export type setWorkspaceLimitResponseError = (setWorkspaceLimitResponse401 | setWorkspaceLimitResponse403 | setWorkspaceLimitResponse404 | setWorkspaceLimitResponse422) & {
  headers: Headers;
};

export type setWorkspaceLimitResponse = (setWorkspaceLimitResponseSuccess | setWorkspaceLimitResponseError)

export const getSetWorkspaceLimitUrl = (workspaceId: string,) => {




  return `/api/organization/usage/limit/${workspaceId}`
}

/**
 * @summary Set Workspace Limit
 */
export const setWorkspaceLimit = async (workspaceId: string,
    setLimitRequest: SetLimitRequest, options?: RequestInit): Promise<setWorkspaceLimitResponse> => {

  return customFetch<setWorkspaceLimitResponse>(getSetWorkspaceLimitUrl(workspaceId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setLimitRequest)
  }
);}




export const getSetWorkspaceLimitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setWorkspaceLimit>>, TError,{workspaceId: string;data: SetLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setWorkspaceLimit>>, TError,{workspaceId: string;data: SetLimitRequest}, TContext> => {

const mutationKey = ['setWorkspaceLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setWorkspaceLimit>>, {workspaceId: string;data: SetLimitRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  setWorkspaceLimit(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetWorkspaceLimitMutationResult = NonNullable<Awaited<ReturnType<typeof setWorkspaceLimit>>>
    export type SetWorkspaceLimitMutationBody = SetLimitRequest
    export type SetWorkspaceLimitMutationError = Error

    /**
 * @summary Set Workspace Limit
 */
export const useSetWorkspaceLimit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setWorkspaceLimit>>, TError,{workspaceId: string;data: SetLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setWorkspaceLimit>>,
        TError,
        {workspaceId: string;data: SetLimitRequest},
        TContext
      > => {
      return useMutation(getSetWorkspaceLimitMutationOptions(options), queryClient);
    }

export type clearWorkspaceLimitResponse204 = {
  data: void
  status: 204
}

export type clearWorkspaceLimitResponse401 = {
  data: Error
  status: 401
}

export type clearWorkspaceLimitResponse403 = {
  data: Error
  status: 403
}

export type clearWorkspaceLimitResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type clearWorkspaceLimitResponseSuccess = (clearWorkspaceLimitResponse204) & {
  headers: Headers;
};
export type clearWorkspaceLimitResponseError = (clearWorkspaceLimitResponse401 | clearWorkspaceLimitResponse403 | clearWorkspaceLimitResponse422) & {
  headers: Headers;
};

export type clearWorkspaceLimitResponse = (clearWorkspaceLimitResponseSuccess | clearWorkspaceLimitResponseError)

export const getClearWorkspaceLimitUrl = (workspaceId: string,) => {




  return `/api/organization/usage/limit/${workspaceId}`
}

/**
 * @summary Clear Workspace Limit
 */
export const clearWorkspaceLimit = async (workspaceId: string, options?: RequestInit): Promise<clearWorkspaceLimitResponse> => {

  return customFetch<clearWorkspaceLimitResponse>(getClearWorkspaceLimitUrl(workspaceId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getClearWorkspaceLimitMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearWorkspaceLimit>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearWorkspaceLimit>>, TError,{workspaceId: string}, TContext> => {

const mutationKey = ['clearWorkspaceLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearWorkspaceLimit>>, {workspaceId: string}> = (props) => {
          const {workspaceId} = props ?? {};

          return  clearWorkspaceLimit(workspaceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearWorkspaceLimitMutationResult = NonNullable<Awaited<ReturnType<typeof clearWorkspaceLimit>>>

    export type ClearWorkspaceLimitMutationError = Error | HTTPValidationError

    /**
 * @summary Clear Workspace Limit
 */
export const useClearWorkspaceLimit = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearWorkspaceLimit>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clearWorkspaceLimit>>,
        TError,
        {workspaceId: string},
        TContext
      > => {
      return useMutation(getClearWorkspaceLimitMutationOptions(options), queryClient);
    }

export type readWorkspaceConcurrencyResponse200 = {
  data: WorkspaceConcurrencyView
  status: 200
}

export type readWorkspaceConcurrencyResponse401 = {
  data: Error
  status: 401
}

export type readWorkspaceConcurrencyResponse403 = {
  data: Error
  status: 403
}

export type readWorkspaceConcurrencyResponse404 = {
  data: Error
  status: 404
}

export type readWorkspaceConcurrencyResponse422 = {
  data: Error
  status: 422
}

export type readWorkspaceConcurrencyResponseSuccess = (readWorkspaceConcurrencyResponse200) & {
  headers: Headers;
};
export type readWorkspaceConcurrencyResponseError = (readWorkspaceConcurrencyResponse401 | readWorkspaceConcurrencyResponse403 | readWorkspaceConcurrencyResponse404 | readWorkspaceConcurrencyResponse422) & {
  headers: Headers;
};

export type readWorkspaceConcurrencyResponse = (readWorkspaceConcurrencyResponseSuccess | readWorkspaceConcurrencyResponseError)

export const getReadWorkspaceConcurrencyUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/concurrency`
}

/**
 * @summary Read Workspace Concurrency
 */
export const readWorkspaceConcurrency = async (workspaceId: string, options?: RequestInit): Promise<readWorkspaceConcurrencyResponse> => {

  return customFetch<readWorkspaceConcurrencyResponse>(getReadWorkspaceConcurrencyUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadWorkspaceConcurrencyQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/concurrency`
    ] as const;
    }


export const getReadWorkspaceConcurrencyQueryOptions = <TData = Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError = Error>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadWorkspaceConcurrencyQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readWorkspaceConcurrency>>> = ({ signal }) => readWorkspaceConcurrency(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadWorkspaceConcurrencyQueryResult = NonNullable<Awaited<ReturnType<typeof readWorkspaceConcurrency>>>
export type ReadWorkspaceConcurrencyQueryError = Error


export function useReadWorkspaceConcurrency<TData = Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError = Error>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceConcurrency>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceConcurrency>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceConcurrency<TData = Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceConcurrency>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceConcurrency>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceConcurrency<TData = Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Workspace Concurrency
 */

export function useReadWorkspaceConcurrency<TData = Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceConcurrency>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadWorkspaceConcurrencyQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readOrganizationConcurrencyResponse200 = {
  data: OrganizationConcurrencyView
  status: 200
}

export type readOrganizationConcurrencyResponse401 = {
  data: Error
  status: 401
}

export type readOrganizationConcurrencyResponse403 = {
  data: Error
  status: 403
}

export type readOrganizationConcurrencyResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type readOrganizationConcurrencyResponseSuccess = (readOrganizationConcurrencyResponse200) & {
  headers: Headers;
};
export type readOrganizationConcurrencyResponseError = (readOrganizationConcurrencyResponse401 | readOrganizationConcurrencyResponse403 | readOrganizationConcurrencyResponse422) & {
  headers: Headers;
};

export type readOrganizationConcurrencyResponse = (readOrganizationConcurrencyResponseSuccess | readOrganizationConcurrencyResponseError)

export const getReadOrganizationConcurrencyUrl = () => {




  return `/api/organization/concurrency`
}

/**
 * @summary Read Organization Concurrency
 */
export const readOrganizationConcurrency = async ( options?: RequestInit): Promise<readOrganizationConcurrencyResponse> => {

  return customFetch<readOrganizationConcurrencyResponse>(getReadOrganizationConcurrencyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadOrganizationConcurrencyQueryKey = () => {
    return [
    `/api/organization/concurrency`
    ] as const;
    }


export const getReadOrganizationConcurrencyQueryOptions = <TData = Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError = Error | HTTPValidationError>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadOrganizationConcurrencyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readOrganizationConcurrency>>> = ({ signal }) => readOrganizationConcurrency({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadOrganizationConcurrencyQueryResult = NonNullable<Awaited<ReturnType<typeof readOrganizationConcurrency>>>
export type ReadOrganizationConcurrencyQueryError = Error | HTTPValidationError


export function useReadOrganizationConcurrency<TData = Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError = Error | HTTPValidationError>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readOrganizationConcurrency>>,
          TError,
          Awaited<ReturnType<typeof readOrganizationConcurrency>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadOrganizationConcurrency<TData = Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readOrganizationConcurrency>>,
          TError,
          Awaited<ReturnType<typeof readOrganizationConcurrency>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadOrganizationConcurrency<TData = Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Organization Concurrency
 */

export function useReadOrganizationConcurrency<TData = Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationConcurrency>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadOrganizationConcurrencyQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type setDefaultConcurrencyLimitResponse200 = {
  data: ConcurrencyLimitView
  status: 200
}

export type setDefaultConcurrencyLimitResponse401 = {
  data: Error
  status: 401
}

export type setDefaultConcurrencyLimitResponse403 = {
  data: Error
  status: 403
}

export type setDefaultConcurrencyLimitResponse404 = {
  data: Error
  status: 404
}

export type setDefaultConcurrencyLimitResponse422 = {
  data: Error
  status: 422
}

export type setDefaultConcurrencyLimitResponseSuccess = (setDefaultConcurrencyLimitResponse200) & {
  headers: Headers;
};
export type setDefaultConcurrencyLimitResponseError = (setDefaultConcurrencyLimitResponse401 | setDefaultConcurrencyLimitResponse403 | setDefaultConcurrencyLimitResponse404 | setDefaultConcurrencyLimitResponse422) & {
  headers: Headers;
};

export type setDefaultConcurrencyLimitResponse = (setDefaultConcurrencyLimitResponseSuccess | setDefaultConcurrencyLimitResponseError)

export const getSetDefaultConcurrencyLimitUrl = () => {




  return `/api/organization/concurrency/limit`
}

/**
 * @summary Set Default Concurrency Limit
 */
export const setDefaultConcurrencyLimit = async (setConcurrencyLimitRequest: SetConcurrencyLimitRequest, options?: RequestInit): Promise<setDefaultConcurrencyLimitResponse> => {

  return customFetch<setDefaultConcurrencyLimitResponse>(getSetDefaultConcurrencyLimitUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setConcurrencyLimitRequest)
  }
);}




export const getSetDefaultConcurrencyLimitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultConcurrencyLimit>>, TError,{data: SetConcurrencyLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setDefaultConcurrencyLimit>>, TError,{data: SetConcurrencyLimitRequest}, TContext> => {

const mutationKey = ['setDefaultConcurrencyLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setDefaultConcurrencyLimit>>, {data: SetConcurrencyLimitRequest}> = (props) => {
          const {data} = props ?? {};

          return  setDefaultConcurrencyLimit(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetDefaultConcurrencyLimitMutationResult = NonNullable<Awaited<ReturnType<typeof setDefaultConcurrencyLimit>>>
    export type SetDefaultConcurrencyLimitMutationBody = SetConcurrencyLimitRequest
    export type SetDefaultConcurrencyLimitMutationError = Error

    /**
 * @summary Set Default Concurrency Limit
 */
export const useSetDefaultConcurrencyLimit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultConcurrencyLimit>>, TError,{data: SetConcurrencyLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setDefaultConcurrencyLimit>>,
        TError,
        {data: SetConcurrencyLimitRequest},
        TContext
      > => {
      return useMutation(getSetDefaultConcurrencyLimitMutationOptions(options), queryClient);
    }

export type clearDefaultConcurrencyLimitResponse204 = {
  data: void
  status: 204
}

export type clearDefaultConcurrencyLimitResponse401 = {
  data: Error
  status: 401
}

export type clearDefaultConcurrencyLimitResponse403 = {
  data: Error
  status: 403
}

export type clearDefaultConcurrencyLimitResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type clearDefaultConcurrencyLimitResponseSuccess = (clearDefaultConcurrencyLimitResponse204) & {
  headers: Headers;
};
export type clearDefaultConcurrencyLimitResponseError = (clearDefaultConcurrencyLimitResponse401 | clearDefaultConcurrencyLimitResponse403 | clearDefaultConcurrencyLimitResponse422) & {
  headers: Headers;
};

export type clearDefaultConcurrencyLimitResponse = (clearDefaultConcurrencyLimitResponseSuccess | clearDefaultConcurrencyLimitResponseError)

export const getClearDefaultConcurrencyLimitUrl = () => {




  return `/api/organization/concurrency/limit`
}

/**
 * @summary Clear Default Concurrency Limit
 */
export const clearDefaultConcurrencyLimit = async ( options?: RequestInit): Promise<clearDefaultConcurrencyLimitResponse> => {

  return customFetch<clearDefaultConcurrencyLimitResponse>(getClearDefaultConcurrencyLimitUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getClearDefaultConcurrencyLimitMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearDefaultConcurrencyLimit>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearDefaultConcurrencyLimit>>, TError,void, TContext> => {

const mutationKey = ['clearDefaultConcurrencyLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearDefaultConcurrencyLimit>>, void> = () => {


          return  clearDefaultConcurrencyLimit(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearDefaultConcurrencyLimitMutationResult = NonNullable<Awaited<ReturnType<typeof clearDefaultConcurrencyLimit>>>

    export type ClearDefaultConcurrencyLimitMutationError = Error | HTTPValidationError

    /**
 * @summary Clear Default Concurrency Limit
 */
export const useClearDefaultConcurrencyLimit = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearDefaultConcurrencyLimit>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clearDefaultConcurrencyLimit>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getClearDefaultConcurrencyLimitMutationOptions(options), queryClient);
    }

export type setWorkspaceConcurrencyLimitResponse200 = {
  data: ConcurrencyLimitView
  status: 200
}

export type setWorkspaceConcurrencyLimitResponse401 = {
  data: Error
  status: 401
}

export type setWorkspaceConcurrencyLimitResponse403 = {
  data: Error
  status: 403
}

export type setWorkspaceConcurrencyLimitResponse404 = {
  data: Error
  status: 404
}

export type setWorkspaceConcurrencyLimitResponse422 = {
  data: Error
  status: 422
}

export type setWorkspaceConcurrencyLimitResponseSuccess = (setWorkspaceConcurrencyLimitResponse200) & {
  headers: Headers;
};
export type setWorkspaceConcurrencyLimitResponseError = (setWorkspaceConcurrencyLimitResponse401 | setWorkspaceConcurrencyLimitResponse403 | setWorkspaceConcurrencyLimitResponse404 | setWorkspaceConcurrencyLimitResponse422) & {
  headers: Headers;
};

export type setWorkspaceConcurrencyLimitResponse = (setWorkspaceConcurrencyLimitResponseSuccess | setWorkspaceConcurrencyLimitResponseError)

export const getSetWorkspaceConcurrencyLimitUrl = (workspaceId: string,) => {




  return `/api/organization/concurrency/limit/${workspaceId}`
}

/**
 * @summary Set Workspace Concurrency Limit
 */
export const setWorkspaceConcurrencyLimit = async (workspaceId: string,
    setConcurrencyLimitRequest: SetConcurrencyLimitRequest, options?: RequestInit): Promise<setWorkspaceConcurrencyLimitResponse> => {

  return customFetch<setWorkspaceConcurrencyLimitResponse>(getSetWorkspaceConcurrencyLimitUrl(workspaceId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setConcurrencyLimitRequest)
  }
);}




export const getSetWorkspaceConcurrencyLimitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setWorkspaceConcurrencyLimit>>, TError,{workspaceId: string;data: SetConcurrencyLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setWorkspaceConcurrencyLimit>>, TError,{workspaceId: string;data: SetConcurrencyLimitRequest}, TContext> => {

const mutationKey = ['setWorkspaceConcurrencyLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setWorkspaceConcurrencyLimit>>, {workspaceId: string;data: SetConcurrencyLimitRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  setWorkspaceConcurrencyLimit(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetWorkspaceConcurrencyLimitMutationResult = NonNullable<Awaited<ReturnType<typeof setWorkspaceConcurrencyLimit>>>
    export type SetWorkspaceConcurrencyLimitMutationBody = SetConcurrencyLimitRequest
    export type SetWorkspaceConcurrencyLimitMutationError = Error

    /**
 * @summary Set Workspace Concurrency Limit
 */
export const useSetWorkspaceConcurrencyLimit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setWorkspaceConcurrencyLimit>>, TError,{workspaceId: string;data: SetConcurrencyLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setWorkspaceConcurrencyLimit>>,
        TError,
        {workspaceId: string;data: SetConcurrencyLimitRequest},
        TContext
      > => {
      return useMutation(getSetWorkspaceConcurrencyLimitMutationOptions(options), queryClient);
    }

export type clearWorkspaceConcurrencyLimitResponse204 = {
  data: void
  status: 204
}

export type clearWorkspaceConcurrencyLimitResponse401 = {
  data: Error
  status: 401
}

export type clearWorkspaceConcurrencyLimitResponse403 = {
  data: Error
  status: 403
}

export type clearWorkspaceConcurrencyLimitResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type clearWorkspaceConcurrencyLimitResponseSuccess = (clearWorkspaceConcurrencyLimitResponse204) & {
  headers: Headers;
};
export type clearWorkspaceConcurrencyLimitResponseError = (clearWorkspaceConcurrencyLimitResponse401 | clearWorkspaceConcurrencyLimitResponse403 | clearWorkspaceConcurrencyLimitResponse422) & {
  headers: Headers;
};

export type clearWorkspaceConcurrencyLimitResponse = (clearWorkspaceConcurrencyLimitResponseSuccess | clearWorkspaceConcurrencyLimitResponseError)

export const getClearWorkspaceConcurrencyLimitUrl = (workspaceId: string,) => {




  return `/api/organization/concurrency/limit/${workspaceId}`
}

/**
 * @summary Clear Workspace Concurrency Limit
 */
export const clearWorkspaceConcurrencyLimit = async (workspaceId: string, options?: RequestInit): Promise<clearWorkspaceConcurrencyLimitResponse> => {

  return customFetch<clearWorkspaceConcurrencyLimitResponse>(getClearWorkspaceConcurrencyLimitUrl(workspaceId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getClearWorkspaceConcurrencyLimitMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearWorkspaceConcurrencyLimit>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearWorkspaceConcurrencyLimit>>, TError,{workspaceId: string}, TContext> => {

const mutationKey = ['clearWorkspaceConcurrencyLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearWorkspaceConcurrencyLimit>>, {workspaceId: string}> = (props) => {
          const {workspaceId} = props ?? {};

          return  clearWorkspaceConcurrencyLimit(workspaceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearWorkspaceConcurrencyLimitMutationResult = NonNullable<Awaited<ReturnType<typeof clearWorkspaceConcurrencyLimit>>>

    export type ClearWorkspaceConcurrencyLimitMutationError = Error | HTTPValidationError

    /**
 * @summary Clear Workspace Concurrency Limit
 */
export const useClearWorkspaceConcurrencyLimit = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearWorkspaceConcurrencyLimit>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clearWorkspaceConcurrencyLimit>>,
        TError,
        {workspaceId: string},
        TContext
      > => {
      return useMutation(getClearWorkspaceConcurrencyLimitMutationOptions(options), queryClient);
    }

export type readWorkspaceMemoryResponse200 = {
  data: WorkspaceMemoryView
  status: 200
}

export type readWorkspaceMemoryResponse401 = {
  data: Error
  status: 401
}

export type readWorkspaceMemoryResponse403 = {
  data: Error
  status: 403
}

export type readWorkspaceMemoryResponse404 = {
  data: Error
  status: 404
}

export type readWorkspaceMemoryResponse422 = {
  data: Error
  status: 422
}

export type readWorkspaceMemoryResponseSuccess = (readWorkspaceMemoryResponse200) & {
  headers: Headers;
};
export type readWorkspaceMemoryResponseError = (readWorkspaceMemoryResponse401 | readWorkspaceMemoryResponse403 | readWorkspaceMemoryResponse404 | readWorkspaceMemoryResponse422) & {
  headers: Headers;
};

export type readWorkspaceMemoryResponse = (readWorkspaceMemoryResponseSuccess | readWorkspaceMemoryResponseError)

export const getReadWorkspaceMemoryUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/memory`
}

/**
 * @summary Read Workspace Memory
 */
export const readWorkspaceMemory = async (workspaceId: string, options?: RequestInit): Promise<readWorkspaceMemoryResponse> => {

  return customFetch<readWorkspaceMemoryResponse>(getReadWorkspaceMemoryUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadWorkspaceMemoryQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/memory`
    ] as const;
    }


export const getReadWorkspaceMemoryQueryOptions = <TData = Awaited<ReturnType<typeof readWorkspaceMemory>>, TError = Error>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceMemory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadWorkspaceMemoryQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readWorkspaceMemory>>> = ({ signal }) => readWorkspaceMemory(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceMemory>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadWorkspaceMemoryQueryResult = NonNullable<Awaited<ReturnType<typeof readWorkspaceMemory>>>
export type ReadWorkspaceMemoryQueryError = Error


export function useReadWorkspaceMemory<TData = Awaited<ReturnType<typeof readWorkspaceMemory>>, TError = Error>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceMemory>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceMemory>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceMemory>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceMemory<TData = Awaited<ReturnType<typeof readWorkspaceMemory>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceMemory>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readWorkspaceMemory>>,
          TError,
          Awaited<ReturnType<typeof readWorkspaceMemory>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadWorkspaceMemory<TData = Awaited<ReturnType<typeof readWorkspaceMemory>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceMemory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Workspace Memory
 */

export function useReadWorkspaceMemory<TData = Awaited<ReturnType<typeof readWorkspaceMemory>>, TError = Error>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readWorkspaceMemory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadWorkspaceMemoryQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readOrganizationMemoryResponse200 = {
  data: OrganizationMemoryView
  status: 200
}

export type readOrganizationMemoryResponse401 = {
  data: Error
  status: 401
}

export type readOrganizationMemoryResponse403 = {
  data: Error
  status: 403
}

export type readOrganizationMemoryResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type readOrganizationMemoryResponseSuccess = (readOrganizationMemoryResponse200) & {
  headers: Headers;
};
export type readOrganizationMemoryResponseError = (readOrganizationMemoryResponse401 | readOrganizationMemoryResponse403 | readOrganizationMemoryResponse422) & {
  headers: Headers;
};

export type readOrganizationMemoryResponse = (readOrganizationMemoryResponseSuccess | readOrganizationMemoryResponseError)

export const getReadOrganizationMemoryUrl = () => {




  return `/api/organization/memory`
}

/**
 * @summary Read Organization Memory
 */
export const readOrganizationMemory = async ( options?: RequestInit): Promise<readOrganizationMemoryResponse> => {

  return customFetch<readOrganizationMemoryResponse>(getReadOrganizationMemoryUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadOrganizationMemoryQueryKey = () => {
    return [
    `/api/organization/memory`
    ] as const;
    }


export const getReadOrganizationMemoryQueryOptions = <TData = Awaited<ReturnType<typeof readOrganizationMemory>>, TError = Error | HTTPValidationError>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationMemory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadOrganizationMemoryQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readOrganizationMemory>>> = ({ signal }) => readOrganizationMemory({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readOrganizationMemory>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadOrganizationMemoryQueryResult = NonNullable<Awaited<ReturnType<typeof readOrganizationMemory>>>
export type ReadOrganizationMemoryQueryError = Error | HTTPValidationError


export function useReadOrganizationMemory<TData = Awaited<ReturnType<typeof readOrganizationMemory>>, TError = Error | HTTPValidationError>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationMemory>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readOrganizationMemory>>,
          TError,
          Awaited<ReturnType<typeof readOrganizationMemory>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadOrganizationMemory<TData = Awaited<ReturnType<typeof readOrganizationMemory>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationMemory>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readOrganizationMemory>>,
          TError,
          Awaited<ReturnType<typeof readOrganizationMemory>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadOrganizationMemory<TData = Awaited<ReturnType<typeof readOrganizationMemory>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationMemory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Organization Memory
 */

export function useReadOrganizationMemory<TData = Awaited<ReturnType<typeof readOrganizationMemory>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationMemory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadOrganizationMemoryQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type setDefaultMemoryLimitResponse200 = {
  data: MemoryLimitView
  status: 200
}

export type setDefaultMemoryLimitResponse401 = {
  data: Error
  status: 401
}

export type setDefaultMemoryLimitResponse403 = {
  data: Error
  status: 403
}

export type setDefaultMemoryLimitResponse404 = {
  data: Error
  status: 404
}

export type setDefaultMemoryLimitResponse422 = {
  data: Error
  status: 422
}

export type setDefaultMemoryLimitResponseSuccess = (setDefaultMemoryLimitResponse200) & {
  headers: Headers;
};
export type setDefaultMemoryLimitResponseError = (setDefaultMemoryLimitResponse401 | setDefaultMemoryLimitResponse403 | setDefaultMemoryLimitResponse404 | setDefaultMemoryLimitResponse422) & {
  headers: Headers;
};

export type setDefaultMemoryLimitResponse = (setDefaultMemoryLimitResponseSuccess | setDefaultMemoryLimitResponseError)

export const getSetDefaultMemoryLimitUrl = () => {




  return `/api/organization/memory/limit`
}

/**
 * @summary Set Default Memory Limit
 */
export const setDefaultMemoryLimit = async (setMemoryLimitRequest: SetMemoryLimitRequest, options?: RequestInit): Promise<setDefaultMemoryLimitResponse> => {

  return customFetch<setDefaultMemoryLimitResponse>(getSetDefaultMemoryLimitUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setMemoryLimitRequest)
  }
);}




export const getSetDefaultMemoryLimitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultMemoryLimit>>, TError,{data: SetMemoryLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setDefaultMemoryLimit>>, TError,{data: SetMemoryLimitRequest}, TContext> => {

const mutationKey = ['setDefaultMemoryLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setDefaultMemoryLimit>>, {data: SetMemoryLimitRequest}> = (props) => {
          const {data} = props ?? {};

          return  setDefaultMemoryLimit(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetDefaultMemoryLimitMutationResult = NonNullable<Awaited<ReturnType<typeof setDefaultMemoryLimit>>>
    export type SetDefaultMemoryLimitMutationBody = SetMemoryLimitRequest
    export type SetDefaultMemoryLimitMutationError = Error

    /**
 * @summary Set Default Memory Limit
 */
export const useSetDefaultMemoryLimit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultMemoryLimit>>, TError,{data: SetMemoryLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setDefaultMemoryLimit>>,
        TError,
        {data: SetMemoryLimitRequest},
        TContext
      > => {
      return useMutation(getSetDefaultMemoryLimitMutationOptions(options), queryClient);
    }

export type clearDefaultMemoryLimitResponse204 = {
  data: void
  status: 204
}

export type clearDefaultMemoryLimitResponse401 = {
  data: Error
  status: 401
}

export type clearDefaultMemoryLimitResponse403 = {
  data: Error
  status: 403
}

export type clearDefaultMemoryLimitResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type clearDefaultMemoryLimitResponseSuccess = (clearDefaultMemoryLimitResponse204) & {
  headers: Headers;
};
export type clearDefaultMemoryLimitResponseError = (clearDefaultMemoryLimitResponse401 | clearDefaultMemoryLimitResponse403 | clearDefaultMemoryLimitResponse422) & {
  headers: Headers;
};

export type clearDefaultMemoryLimitResponse = (clearDefaultMemoryLimitResponseSuccess | clearDefaultMemoryLimitResponseError)

export const getClearDefaultMemoryLimitUrl = () => {




  return `/api/organization/memory/limit`
}

/**
 * @summary Clear Default Memory Limit
 */
export const clearDefaultMemoryLimit = async ( options?: RequestInit): Promise<clearDefaultMemoryLimitResponse> => {

  return customFetch<clearDefaultMemoryLimitResponse>(getClearDefaultMemoryLimitUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getClearDefaultMemoryLimitMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearDefaultMemoryLimit>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearDefaultMemoryLimit>>, TError,void, TContext> => {

const mutationKey = ['clearDefaultMemoryLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearDefaultMemoryLimit>>, void> = () => {


          return  clearDefaultMemoryLimit(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearDefaultMemoryLimitMutationResult = NonNullable<Awaited<ReturnType<typeof clearDefaultMemoryLimit>>>

    export type ClearDefaultMemoryLimitMutationError = Error | HTTPValidationError

    /**
 * @summary Clear Default Memory Limit
 */
export const useClearDefaultMemoryLimit = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearDefaultMemoryLimit>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clearDefaultMemoryLimit>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getClearDefaultMemoryLimitMutationOptions(options), queryClient);
    }

export type setWorkspaceMemoryLimitResponse200 = {
  data: MemoryLimitView
  status: 200
}

export type setWorkspaceMemoryLimitResponse401 = {
  data: Error
  status: 401
}

export type setWorkspaceMemoryLimitResponse403 = {
  data: Error
  status: 403
}

export type setWorkspaceMemoryLimitResponse404 = {
  data: Error
  status: 404
}

export type setWorkspaceMemoryLimitResponse422 = {
  data: Error
  status: 422
}

export type setWorkspaceMemoryLimitResponseSuccess = (setWorkspaceMemoryLimitResponse200) & {
  headers: Headers;
};
export type setWorkspaceMemoryLimitResponseError = (setWorkspaceMemoryLimitResponse401 | setWorkspaceMemoryLimitResponse403 | setWorkspaceMemoryLimitResponse404 | setWorkspaceMemoryLimitResponse422) & {
  headers: Headers;
};

export type setWorkspaceMemoryLimitResponse = (setWorkspaceMemoryLimitResponseSuccess | setWorkspaceMemoryLimitResponseError)

export const getSetWorkspaceMemoryLimitUrl = (workspaceId: string,) => {




  return `/api/organization/memory/limit/${workspaceId}`
}

/**
 * @summary Set Workspace Memory Limit
 */
export const setWorkspaceMemoryLimit = async (workspaceId: string,
    setMemoryLimitRequest: SetMemoryLimitRequest, options?: RequestInit): Promise<setWorkspaceMemoryLimitResponse> => {

  return customFetch<setWorkspaceMemoryLimitResponse>(getSetWorkspaceMemoryLimitUrl(workspaceId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(setMemoryLimitRequest)
  }
);}




export const getSetWorkspaceMemoryLimitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setWorkspaceMemoryLimit>>, TError,{workspaceId: string;data: SetMemoryLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setWorkspaceMemoryLimit>>, TError,{workspaceId: string;data: SetMemoryLimitRequest}, TContext> => {

const mutationKey = ['setWorkspaceMemoryLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setWorkspaceMemoryLimit>>, {workspaceId: string;data: SetMemoryLimitRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  setWorkspaceMemoryLimit(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetWorkspaceMemoryLimitMutationResult = NonNullable<Awaited<ReturnType<typeof setWorkspaceMemoryLimit>>>
    export type SetWorkspaceMemoryLimitMutationBody = SetMemoryLimitRequest
    export type SetWorkspaceMemoryLimitMutationError = Error

    /**
 * @summary Set Workspace Memory Limit
 */
export const useSetWorkspaceMemoryLimit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setWorkspaceMemoryLimit>>, TError,{workspaceId: string;data: SetMemoryLimitRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setWorkspaceMemoryLimit>>,
        TError,
        {workspaceId: string;data: SetMemoryLimitRequest},
        TContext
      > => {
      return useMutation(getSetWorkspaceMemoryLimitMutationOptions(options), queryClient);
    }

export type clearWorkspaceMemoryLimitResponse204 = {
  data: void
  status: 204
}

export type clearWorkspaceMemoryLimitResponse401 = {
  data: Error
  status: 401
}

export type clearWorkspaceMemoryLimitResponse403 = {
  data: Error
  status: 403
}

export type clearWorkspaceMemoryLimitResponse404 = {
  data: Error
  status: 404
}

export type clearWorkspaceMemoryLimitResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type clearWorkspaceMemoryLimitResponseSuccess = (clearWorkspaceMemoryLimitResponse204) & {
  headers: Headers;
};
export type clearWorkspaceMemoryLimitResponseError = (clearWorkspaceMemoryLimitResponse401 | clearWorkspaceMemoryLimitResponse403 | clearWorkspaceMemoryLimitResponse404 | clearWorkspaceMemoryLimitResponse422) & {
  headers: Headers;
};

export type clearWorkspaceMemoryLimitResponse = (clearWorkspaceMemoryLimitResponseSuccess | clearWorkspaceMemoryLimitResponseError)

export const getClearWorkspaceMemoryLimitUrl = (workspaceId: string,) => {




  return `/api/organization/memory/limit/${workspaceId}`
}

/**
 * @summary Clear Workspace Memory Limit
 */
export const clearWorkspaceMemoryLimit = async (workspaceId: string, options?: RequestInit): Promise<clearWorkspaceMemoryLimitResponse> => {

  return customFetch<clearWorkspaceMemoryLimitResponse>(getClearWorkspaceMemoryLimitUrl(workspaceId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getClearWorkspaceMemoryLimitMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearWorkspaceMemoryLimit>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearWorkspaceMemoryLimit>>, TError,{workspaceId: string}, TContext> => {

const mutationKey = ['clearWorkspaceMemoryLimit'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearWorkspaceMemoryLimit>>, {workspaceId: string}> = (props) => {
          const {workspaceId} = props ?? {};

          return  clearWorkspaceMemoryLimit(workspaceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearWorkspaceMemoryLimitMutationResult = NonNullable<Awaited<ReturnType<typeof clearWorkspaceMemoryLimit>>>

    export type ClearWorkspaceMemoryLimitMutationError = Error | HTTPValidationError

    /**
 * @summary Clear Workspace Memory Limit
 */
export const useClearWorkspaceMemoryLimit = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearWorkspaceMemoryLimit>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clearWorkspaceMemoryLimit>>,
        TError,
        {workspaceId: string},
        TContext
      > => {
      return useMutation(getClearWorkspaceMemoryLimitMutationOptions(options), queryClient);
    }

export type readOrganizationResponse200 = {
  data: OrganizationView
  status: 200
}

export type readOrganizationResponse401 = {
  data: Error
  status: 401
}

export type readOrganizationResponseSuccess = (readOrganizationResponse200) & {
  headers: Headers;
};
export type readOrganizationResponseError = (readOrganizationResponse401) & {
  headers: Headers;
};

export type readOrganizationResponse = (readOrganizationResponseSuccess | readOrganizationResponseError)

export const getReadOrganizationUrl = () => {




  return `/api/organization`
}

/**
 * Readable by any signed-in caller (FR-004) — it names the product they are in.
 * @summary Read Organization
 */
export const readOrganization = async ( options?: RequestInit): Promise<readOrganizationResponse> => {

  return customFetch<readOrganizationResponse>(getReadOrganizationUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadOrganizationQueryKey = () => {
    return [
    `/api/organization`
    ] as const;
    }


export const getReadOrganizationQueryOptions = <TData = Awaited<ReturnType<typeof readOrganization>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganization>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadOrganizationQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readOrganization>>> = ({ signal }) => readOrganization({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readOrganization>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadOrganizationQueryResult = NonNullable<Awaited<ReturnType<typeof readOrganization>>>
export type ReadOrganizationQueryError = Error


export function useReadOrganization<TData = Awaited<ReturnType<typeof readOrganization>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganization>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readOrganization>>,
          TError,
          Awaited<ReturnType<typeof readOrganization>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadOrganization<TData = Awaited<ReturnType<typeof readOrganization>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganization>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readOrganization>>,
          TError,
          Awaited<ReturnType<typeof readOrganization>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadOrganization<TData = Awaited<ReturnType<typeof readOrganization>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganization>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Organization
 */

export function useReadOrganization<TData = Awaited<ReturnType<typeof readOrganization>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganization>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadOrganizationQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type updateOrganizationResponse200 = {
  data: OrganizationView
  status: 200
}

export type updateOrganizationResponse401 = {
  data: Error
  status: 401
}

export type updateOrganizationResponse403 = {
  data: Error
  status: 403
}

export type updateOrganizationResponse422 = {
  data: Error
  status: 422
}

export type updateOrganizationResponseSuccess = (updateOrganizationResponse200) & {
  headers: Headers;
};
export type updateOrganizationResponseError = (updateOrganizationResponse401 | updateOrganizationResponse403 | updateOrganizationResponse422) & {
  headers: Headers;
};

export type updateOrganizationResponse = (updateOrganizationResponseSuccess | updateOrganizationResponseError)

export const getUpdateOrganizationUrl = () => {




  return `/api/organization`
}

/**
 * @summary Update Organization
 */
export const updateOrganization = async (updateOrganizationRequest: UpdateOrganizationRequest, options?: RequestInit): Promise<updateOrganizationResponse> => {

  return customFetch<updateOrganizationResponse>(getUpdateOrganizationUrl(),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateOrganizationRequest)
  }
);}




export const getUpdateOrganizationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateOrganization>>, TError,{data: UpdateOrganizationRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateOrganization>>, TError,{data: UpdateOrganizationRequest}, TContext> => {

const mutationKey = ['updateOrganization'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateOrganization>>, {data: UpdateOrganizationRequest}> = (props) => {
          const {data} = props ?? {};

          return  updateOrganization(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateOrganizationMutationResult = NonNullable<Awaited<ReturnType<typeof updateOrganization>>>
    export type UpdateOrganizationMutationBody = UpdateOrganizationRequest
    export type UpdateOrganizationMutationError = Error

    /**
 * @summary Update Organization
 */
export const useUpdateOrganization = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateOrganization>>, TError,{data: UpdateOrganizationRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateOrganization>>,
        TError,
        {data: UpdateOrganizationRequest},
        TContext
      > => {
      return useMutation(getUpdateOrganizationMutationOptions(options), queryClient);
    }

export type listOrganizationWorkspacesResponse200 = {
  data: AdminWorkspaces
  status: 200
}

export type listOrganizationWorkspacesResponse401 = {
  data: Error
  status: 401
}

export type listOrganizationWorkspacesResponse403 = {
  data: Error
  status: 403
}

export type listOrganizationWorkspacesResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listOrganizationWorkspacesResponseSuccess = (listOrganizationWorkspacesResponse200) & {
  headers: Headers;
};
export type listOrganizationWorkspacesResponseError = (listOrganizationWorkspacesResponse401 | listOrganizationWorkspacesResponse403 | listOrganizationWorkspacesResponse422) & {
  headers: Headers;
};

export type listOrganizationWorkspacesResponse = (listOrganizationWorkspacesResponseSuccess | listOrganizationWorkspacesResponseError)

export const getListOrganizationWorkspacesUrl = (params?: ListOrganizationWorkspacesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/organization/workspaces?${stringifiedParams}` : `/api/organization/workspaces`
}

/**
 * Every workspace: owners, members, visibility, last activity (FR-004).
 * @summary List Organization Workspaces
 */
export const listOrganizationWorkspaces = async (params?: ListOrganizationWorkspacesParams, options?: RequestInit): Promise<listOrganizationWorkspacesResponse> => {

  return customFetch<listOrganizationWorkspacesResponse>(getListOrganizationWorkspacesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOrganizationWorkspacesQueryKey = (params?: ListOrganizationWorkspacesParams,) => {
    return [
    `/api/organization/workspaces`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOrganizationWorkspacesQueryOptions = <TData = Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError = Error | HTTPValidationError>(params?: ListOrganizationWorkspacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOrganizationWorkspacesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOrganizationWorkspaces>>> = ({ signal }) => listOrganizationWorkspaces(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListOrganizationWorkspacesQueryResult = NonNullable<Awaited<ReturnType<typeof listOrganizationWorkspaces>>>
export type ListOrganizationWorkspacesQueryError = Error | HTTPValidationError


export function useListOrganizationWorkspaces<TData = Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError = Error | HTTPValidationError>(
 params: undefined |  ListOrganizationWorkspacesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganizationWorkspaces>>,
          TError,
          Awaited<ReturnType<typeof listOrganizationWorkspaces>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListOrganizationWorkspaces<TData = Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError = Error | HTTPValidationError>(
 params?: ListOrganizationWorkspacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganizationWorkspaces>>,
          TError,
          Awaited<ReturnType<typeof listOrganizationWorkspaces>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListOrganizationWorkspaces<TData = Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError = Error | HTTPValidationError>(
 params?: ListOrganizationWorkspacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Organization Workspaces
 */

export function useListOrganizationWorkspaces<TData = Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError = Error | HTTPValidationError>(
 params?: ListOrganizationWorkspacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganizationWorkspaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListOrganizationWorkspacesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type exportOrganizationWorkspacesResponse200 = {
  data: string
  status: 200
}

export type exportOrganizationWorkspacesResponse401 = {
  data: Error
  status: 401
}

export type exportOrganizationWorkspacesResponse403 = {
  data: Error
  status: 403
}

export type exportOrganizationWorkspacesResponseSuccess = (exportOrganizationWorkspacesResponse200) & {
  headers: Headers;
};
export type exportOrganizationWorkspacesResponseError = (exportOrganizationWorkspacesResponse401 | exportOrganizationWorkspacesResponse403) & {
  headers: Headers;
};

export type exportOrganizationWorkspacesResponse = (exportOrganizationWorkspacesResponseSuccess | exportOrganizationWorkspacesResponseError)

export const getExportOrganizationWorkspacesUrl = () => {




  return `/api/organization/workspaces.csv`
}

/**
 * Every workspace as CSV (FR-005).
 * @summary Export Organization Workspaces
 */
export const exportOrganizationWorkspaces = async ( options?: RequestInit): Promise<exportOrganizationWorkspacesResponse> => {

  return customFetch<exportOrganizationWorkspacesResponse>(getExportOrganizationWorkspacesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportOrganizationWorkspacesQueryKey = () => {
    return [
    `/api/organization/workspaces.csv`
    ] as const;
    }


export const getExportOrganizationWorkspacesQueryOptions = <TData = Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportOrganizationWorkspacesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportOrganizationWorkspaces>>> = ({ signal }) => exportOrganizationWorkspaces({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ExportOrganizationWorkspacesQueryResult = NonNullable<Awaited<ReturnType<typeof exportOrganizationWorkspaces>>>
export type ExportOrganizationWorkspacesQueryError = Error


export function useExportOrganizationWorkspaces<TData = Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportOrganizationWorkspaces>>,
          TError,
          Awaited<ReturnType<typeof exportOrganizationWorkspaces>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportOrganizationWorkspaces<TData = Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportOrganizationWorkspaces>>,
          TError,
          Awaited<ReturnType<typeof exportOrganizationWorkspaces>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportOrganizationWorkspaces<TData = Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Export Organization Workspaces
 */

export function useExportOrganizationWorkspaces<TData = Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationWorkspaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getExportOrganizationWorkspacesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readOrganizationAccessLogResponse200 = {
  data: AccessLogPage
  status: 200
}

export type readOrganizationAccessLogResponse401 = {
  data: Error
  status: 401
}

export type readOrganizationAccessLogResponse403 = {
  data: Error
  status: 403
}

export type readOrganizationAccessLogResponse422 = {
  data: Error
  status: 422
}

export type readOrganizationAccessLogResponseSuccess = (readOrganizationAccessLogResponse200) & {
  headers: Headers;
};
export type readOrganizationAccessLogResponseError = (readOrganizationAccessLogResponse401 | readOrganizationAccessLogResponse403 | readOrganizationAccessLogResponse422) & {
  headers: Headers;
};

export type readOrganizationAccessLogResponse = (readOrganizationAccessLogResponseSuccess | readOrganizationAccessLogResponseError)

export const getReadOrganizationAccessLogUrl = (params?: ReadOrganizationAccessLogParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/organization/workspace-access-log?${stringifiedParams}` : `/api/organization/workspace-access-log`
}

/**
 * Every workspace's access log, newest first (FR-006).
 * @summary Read Organization Access Log
 */
export const readOrganizationAccessLog = async (params?: ReadOrganizationAccessLogParams, options?: RequestInit): Promise<readOrganizationAccessLogResponse> => {

  return customFetch<readOrganizationAccessLogResponse>(getReadOrganizationAccessLogUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadOrganizationAccessLogQueryKey = (params?: ReadOrganizationAccessLogParams,) => {
    return [
    `/api/organization/workspace-access-log`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadOrganizationAccessLogQueryOptions = <TData = Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError = Error>(params?: ReadOrganizationAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadOrganizationAccessLogQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readOrganizationAccessLog>>> = ({ signal }) => readOrganizationAccessLog(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadOrganizationAccessLogQueryResult = NonNullable<Awaited<ReturnType<typeof readOrganizationAccessLog>>>
export type ReadOrganizationAccessLogQueryError = Error


export function useReadOrganizationAccessLog<TData = Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError = Error>(
 params: undefined |  ReadOrganizationAccessLogParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readOrganizationAccessLog>>,
          TError,
          Awaited<ReturnType<typeof readOrganizationAccessLog>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadOrganizationAccessLog<TData = Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError = Error>(
 params?: ReadOrganizationAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readOrganizationAccessLog>>,
          TError,
          Awaited<ReturnType<typeof readOrganizationAccessLog>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadOrganizationAccessLog<TData = Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError = Error>(
 params?: ReadOrganizationAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Organization Access Log
 */

export function useReadOrganizationAccessLog<TData = Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError = Error>(
 params?: ReadOrganizationAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readOrganizationAccessLog>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadOrganizationAccessLogQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type exportOrganizationAccessLogResponse200 = {
  data: string
  status: 200
}

export type exportOrganizationAccessLogResponse401 = {
  data: Error
  status: 401
}

export type exportOrganizationAccessLogResponse403 = {
  data: Error
  status: 403
}

export type exportOrganizationAccessLogResponse422 = {
  data: Error
  status: 422
}

export type exportOrganizationAccessLogResponseSuccess = (exportOrganizationAccessLogResponse200) & {
  headers: Headers;
};
export type exportOrganizationAccessLogResponseError = (exportOrganizationAccessLogResponse401 | exportOrganizationAccessLogResponse403 | exportOrganizationAccessLogResponse422) & {
  headers: Headers;
};

export type exportOrganizationAccessLogResponse = (exportOrganizationAccessLogResponseSuccess | exportOrganizationAccessLogResponseError)

export const getExportOrganizationAccessLogUrl = (params: ExportOrganizationAccessLogParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/organization/workspace-access-log.csv?${stringifiedParams}` : `/api/organization/workspace-access-log.csv`
}

/**
 * The access log for a date range as CSV, oldest first (FR-006).
 * @summary Export Organization Access Log
 */
export const exportOrganizationAccessLog = async (params: ExportOrganizationAccessLogParams, options?: RequestInit): Promise<exportOrganizationAccessLogResponse> => {

  return customFetch<exportOrganizationAccessLogResponse>(getExportOrganizationAccessLogUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportOrganizationAccessLogQueryKey = (params?: ExportOrganizationAccessLogParams,) => {
    return [
    `/api/organization/workspace-access-log.csv`, ...(params ? [params] : [])
    ] as const;
    }


export const getExportOrganizationAccessLogQueryOptions = <TData = Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError = Error>(params: ExportOrganizationAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportOrganizationAccessLogQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportOrganizationAccessLog>>> = ({ signal }) => exportOrganizationAccessLog(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ExportOrganizationAccessLogQueryResult = NonNullable<Awaited<ReturnType<typeof exportOrganizationAccessLog>>>
export type ExportOrganizationAccessLogQueryError = Error


export function useExportOrganizationAccessLog<TData = Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError = Error>(
 params: ExportOrganizationAccessLogParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportOrganizationAccessLog>>,
          TError,
          Awaited<ReturnType<typeof exportOrganizationAccessLog>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportOrganizationAccessLog<TData = Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError = Error>(
 params: ExportOrganizationAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportOrganizationAccessLog>>,
          TError,
          Awaited<ReturnType<typeof exportOrganizationAccessLog>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportOrganizationAccessLog<TData = Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError = Error>(
 params: ExportOrganizationAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Export Organization Access Log
 */

export function useExportOrganizationAccessLog<TData = Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError = Error>(
 params: ExportOrganizationAccessLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportOrganizationAccessLog>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getExportOrganizationAccessLogQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listLlmProfilesResponse200 = {
  data: LLMProfileList
  status: 200
}

export type listLlmProfilesResponse401 = {
  data: Error
  status: 401
}

export type listLlmProfilesResponse404 = {
  data: Error
  status: 404
}

export type listLlmProfilesResponseSuccess = (listLlmProfilesResponse200) & {
  headers: Headers;
};
export type listLlmProfilesResponseError = (listLlmProfilesResponse401 | listLlmProfilesResponse404) & {
  headers: Headers;
};

export type listLlmProfilesResponse = (listLlmProfilesResponseSuccess | listLlmProfilesResponseError)

export const getListLlmProfilesUrl = () => {




  return `/api/llm-profiles`
}

/**
 * Readable by any signed-in caller (spec 074 FR-005) — no workspace, no
 * ``org-admin`` requirement.
 * @summary List Llm Profiles
 */
export const listLlmProfiles = async ( options?: RequestInit): Promise<listLlmProfilesResponse> => {

  return customFetch<listLlmProfilesResponse>(getListLlmProfilesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListLlmProfilesQueryKey = () => {
    return [
    `/api/llm-profiles`
    ] as const;
    }


export const getListLlmProfilesQueryOptions = <TData = Awaited<ReturnType<typeof listLlmProfiles>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmProfiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListLlmProfilesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listLlmProfiles>>> = ({ signal }) => listLlmProfiles({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listLlmProfiles>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListLlmProfilesQueryResult = NonNullable<Awaited<ReturnType<typeof listLlmProfiles>>>
export type ListLlmProfilesQueryError = Error


export function useListLlmProfiles<TData = Awaited<ReturnType<typeof listLlmProfiles>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmProfiles>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listLlmProfiles>>,
          TError,
          Awaited<ReturnType<typeof listLlmProfiles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListLlmProfiles<TData = Awaited<ReturnType<typeof listLlmProfiles>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmProfiles>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listLlmProfiles>>,
          TError,
          Awaited<ReturnType<typeof listLlmProfiles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListLlmProfiles<TData = Awaited<ReturnType<typeof listLlmProfiles>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmProfiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Llm Profiles
 */

export function useListLlmProfiles<TData = Awaited<ReturnType<typeof listLlmProfiles>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLlmProfiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListLlmProfilesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type createLlmProfileResponse201 = {
  data: LLMProfileView
  status: 201
}

export type createLlmProfileResponse401 = {
  data: Error
  status: 401
}

export type createLlmProfileResponse403 = {
  data: Error
  status: 403
}

export type createLlmProfileResponse404 = {
  data: Error
  status: 404
}

export type createLlmProfileResponse409 = {
  data: Error
  status: 409
}

export type createLlmProfileResponse422 = {
  data: Error
  status: 422
}

export type createLlmProfileResponseSuccess = (createLlmProfileResponse201) & {
  headers: Headers;
};
export type createLlmProfileResponseError = (createLlmProfileResponse401 | createLlmProfileResponse403 | createLlmProfileResponse404 | createLlmProfileResponse409 | createLlmProfileResponse422) & {
  headers: Headers;
};

export type createLlmProfileResponse = (createLlmProfileResponseSuccess | createLlmProfileResponseError)

export const getCreateLlmProfileUrl = () => {




  return `/api/llm-profiles`
}

/**
 * @summary Create Llm Profile
 */
export const createLlmProfile = async (createLLMProfileRequest: CreateLLMProfileRequest, options?: RequestInit): Promise<createLlmProfileResponse> => {

  return customFetch<createLlmProfileResponse>(getCreateLlmProfileUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createLLMProfileRequest)
  }
);}




export const getCreateLlmProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLlmProfile>>, TError,{data: CreateLLMProfileRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createLlmProfile>>, TError,{data: CreateLLMProfileRequest}, TContext> => {

const mutationKey = ['createLlmProfile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createLlmProfile>>, {data: CreateLLMProfileRequest}> = (props) => {
          const {data} = props ?? {};

          return  createLlmProfile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateLlmProfileMutationResult = NonNullable<Awaited<ReturnType<typeof createLlmProfile>>>
    export type CreateLlmProfileMutationBody = CreateLLMProfileRequest
    export type CreateLlmProfileMutationError = Error

    /**
 * @summary Create Llm Profile
 */
export const useCreateLlmProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLlmProfile>>, TError,{data: CreateLLMProfileRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createLlmProfile>>,
        TError,
        {data: CreateLLMProfileRequest},
        TContext
      > => {
      return useMutation(getCreateLlmProfileMutationOptions(options), queryClient);
    }

export type readLlmProfilesAdminOverviewResponse200 = {
  data: LLMProfileAdminOverview
  status: 200
}

export type readLlmProfilesAdminOverviewResponse401 = {
  data: Error
  status: 401
}

export type readLlmProfilesAdminOverviewResponse403 = {
  data: Error
  status: 403
}

export type readLlmProfilesAdminOverviewResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type readLlmProfilesAdminOverviewResponseSuccess = (readLlmProfilesAdminOverviewResponse200) & {
  headers: Headers;
};
export type readLlmProfilesAdminOverviewResponseError = (readLlmProfilesAdminOverviewResponse401 | readLlmProfilesAdminOverviewResponse403 | readLlmProfilesAdminOverviewResponse422) & {
  headers: Headers;
};

export type readLlmProfilesAdminOverviewResponse = (readLlmProfilesAdminOverviewResponseSuccess | readLlmProfilesAdminOverviewResponseError)

export const getReadLlmProfilesAdminOverviewUrl = () => {




  return `/api/llm-profiles/admin-overview`
}

/**
 * Every profile with its connection, spend and usage — organization admins only.
 *
 * **Declared before the ``/{slug}`` routes deliberately.** There is no ``GET
 * /{slug}`` on this router today, so nothing can shadow this path right now; the
 * ordering is what keeps that true if one is ever added, since a path parameter would
 * otherwise match ``admin-overview`` and answer "no such profile".
 *
 * No query parameters at all (FR-009): the figures are all-time and organization-wide
 * by design, so there is no period to narrow and ``?period=...`` is simply ignored
 * rather than silently changing the answer.
 *
 * The admin gate is the service's first line, not a dependency here — this router
 * parses nothing, branches not at all and raises no ``HTTPException`` (§2.1).
 * @summary Read Llm Profiles Admin Overview
 */
export const readLlmProfilesAdminOverview = async ( options?: RequestInit): Promise<readLlmProfilesAdminOverviewResponse> => {

  return customFetch<readLlmProfilesAdminOverviewResponse>(getReadLlmProfilesAdminOverviewUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadLlmProfilesAdminOverviewQueryKey = () => {
    return [
    `/api/llm-profiles/admin-overview`
    ] as const;
    }


export const getReadLlmProfilesAdminOverviewQueryOptions = <TData = Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError = Error | HTTPValidationError>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadLlmProfilesAdminOverviewQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>> = ({ signal }) => readLlmProfilesAdminOverview({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadLlmProfilesAdminOverviewQueryResult = NonNullable<Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>>
export type ReadLlmProfilesAdminOverviewQueryError = Error | HTTPValidationError


export function useReadLlmProfilesAdminOverview<TData = Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError = Error | HTTPValidationError>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>,
          TError,
          Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadLlmProfilesAdminOverview<TData = Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>,
          TError,
          Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadLlmProfilesAdminOverview<TData = Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Llm Profiles Admin Overview
 */

export function useReadLlmProfilesAdminOverview<TData = Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError = Error | HTTPValidationError>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readLlmProfilesAdminOverview>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadLlmProfilesAdminOverviewQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type updateLlmProfileResponse200 = {
  data: LLMProfileView
  status: 200
}

export type updateLlmProfileResponse401 = {
  data: Error
  status: 401
}

export type updateLlmProfileResponse403 = {
  data: Error
  status: 403
}

export type updateLlmProfileResponse404 = {
  data: Error
  status: 404
}

export type updateLlmProfileResponse409 = {
  data: Error
  status: 409
}

export type updateLlmProfileResponse422 = {
  data: Error
  status: 422
}

export type updateLlmProfileResponseSuccess = (updateLlmProfileResponse200) & {
  headers: Headers;
};
export type updateLlmProfileResponseError = (updateLlmProfileResponse401 | updateLlmProfileResponse403 | updateLlmProfileResponse404 | updateLlmProfileResponse409 | updateLlmProfileResponse422) & {
  headers: Headers;
};

export type updateLlmProfileResponse = (updateLlmProfileResponseSuccess | updateLlmProfileResponseError)

export const getUpdateLlmProfileUrl = (slug: string,) => {




  return `/api/llm-profiles/${slug}`
}

/**
 * @summary Update Llm Profile
 */
export const updateLlmProfile = async (slug: string,
    updateLLMProfileRequest: UpdateLLMProfileRequest, options?: RequestInit): Promise<updateLlmProfileResponse> => {

  return customFetch<updateLlmProfileResponse>(getUpdateLlmProfileUrl(slug),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateLLMProfileRequest)
  }
);}




export const getUpdateLlmProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateLlmProfile>>, TError,{slug: string;data: UpdateLLMProfileRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateLlmProfile>>, TError,{slug: string;data: UpdateLLMProfileRequest}, TContext> => {

const mutationKey = ['updateLlmProfile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateLlmProfile>>, {slug: string;data: UpdateLLMProfileRequest}> = (props) => {
          const {slug,data} = props ?? {};

          return  updateLlmProfile(slug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateLlmProfileMutationResult = NonNullable<Awaited<ReturnType<typeof updateLlmProfile>>>
    export type UpdateLlmProfileMutationBody = UpdateLLMProfileRequest
    export type UpdateLlmProfileMutationError = Error

    /**
 * @summary Update Llm Profile
 */
export const useUpdateLlmProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateLlmProfile>>, TError,{slug: string;data: UpdateLLMProfileRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateLlmProfile>>,
        TError,
        {slug: string;data: UpdateLLMProfileRequest},
        TContext
      > => {
      return useMutation(getUpdateLlmProfileMutationOptions(options), queryClient);
    }

export type deleteLlmProfileResponse204 = {
  data: void
  status: 204
}

export type deleteLlmProfileResponse401 = {
  data: Error
  status: 401
}

export type deleteLlmProfileResponse403 = {
  data: Error
  status: 403
}

export type deleteLlmProfileResponse404 = {
  data: Error
  status: 404
}

export type deleteLlmProfileResponse409 = {
  data: Error
  status: 409
}

export type deleteLlmProfileResponse422 = {
  data: Error
  status: 422
}

export type deleteLlmProfileResponseSuccess = (deleteLlmProfileResponse204) & {
  headers: Headers;
};
export type deleteLlmProfileResponseError = (deleteLlmProfileResponse401 | deleteLlmProfileResponse403 | deleteLlmProfileResponse404 | deleteLlmProfileResponse409 | deleteLlmProfileResponse422) & {
  headers: Headers;
};

export type deleteLlmProfileResponse = (deleteLlmProfileResponseSuccess | deleteLlmProfileResponseError)

export const getDeleteLlmProfileUrl = (slug: string,) => {




  return `/api/llm-profiles/${slug}`
}

/**
 * @summary Delete Llm Profile
 */
export const deleteLlmProfile = async (slug: string, options?: RequestInit): Promise<deleteLlmProfileResponse> => {

  return customFetch<deleteLlmProfileResponse>(getDeleteLlmProfileUrl(slug),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteLlmProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteLlmProfile>>, TError,{slug: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteLlmProfile>>, TError,{slug: string}, TContext> => {

const mutationKey = ['deleteLlmProfile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteLlmProfile>>, {slug: string}> = (props) => {
          const {slug} = props ?? {};

          return  deleteLlmProfile(slug,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteLlmProfileMutationResult = NonNullable<Awaited<ReturnType<typeof deleteLlmProfile>>>

    export type DeleteLlmProfileMutationError = Error

    /**
 * @summary Delete Llm Profile
 */
export const useDeleteLlmProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteLlmProfile>>, TError,{slug: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteLlmProfile>>,
        TError,
        {slug: string},
        TContext
      > => {
      return useMutation(getDeleteLlmProfileMutationOptions(options), queryClient);
    }

export type setDefaultLlmProfileResponse204 = {
  data: void
  status: 204
}

export type setDefaultLlmProfileResponse401 = {
  data: Error
  status: 401
}

export type setDefaultLlmProfileResponse403 = {
  data: Error
  status: 403
}

export type setDefaultLlmProfileResponse404 = {
  data: Error
  status: 404
}

export type setDefaultLlmProfileResponse409 = {
  data: Error
  status: 409
}

export type setDefaultLlmProfileResponse422 = {
  data: Error
  status: 422
}

export type setDefaultLlmProfileResponseSuccess = (setDefaultLlmProfileResponse204) & {
  headers: Headers;
};
export type setDefaultLlmProfileResponseError = (setDefaultLlmProfileResponse401 | setDefaultLlmProfileResponse403 | setDefaultLlmProfileResponse404 | setDefaultLlmProfileResponse409 | setDefaultLlmProfileResponse422) & {
  headers: Headers;
};

export type setDefaultLlmProfileResponse = (setDefaultLlmProfileResponseSuccess | setDefaultLlmProfileResponseError)

export const getSetDefaultLlmProfileUrl = (slug: string,) => {




  return `/api/llm-profiles/${slug}/default`
}

/**
 * @summary Set Default Llm Profile
 */
export const setDefaultLlmProfile = async (slug: string, options?: RequestInit): Promise<setDefaultLlmProfileResponse> => {

  return customFetch<setDefaultLlmProfileResponse>(getSetDefaultLlmProfileUrl(slug),
  {
    ...options,
    method: 'POST'


  }
);}




export const getSetDefaultLlmProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultLlmProfile>>, TError,{slug: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setDefaultLlmProfile>>, TError,{slug: string}, TContext> => {

const mutationKey = ['setDefaultLlmProfile'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setDefaultLlmProfile>>, {slug: string}> = (props) => {
          const {slug} = props ?? {};

          return  setDefaultLlmProfile(slug,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetDefaultLlmProfileMutationResult = NonNullable<Awaited<ReturnType<typeof setDefaultLlmProfile>>>

    export type SetDefaultLlmProfileMutationError = Error

    /**
 * @summary Set Default Llm Profile
 */
export const useSetDefaultLlmProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultLlmProfile>>, TError,{slug: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setDefaultLlmProfile>>,
        TError,
        {slug: string},
        TContext
      > => {
      return useMutation(getSetDefaultLlmProfileMutationOptions(options), queryClient);
    }

export type listAiProvidersResponse200 = {
  data: AIProviderList
  status: 200
}

export type listAiProvidersResponse401 = {
  data: Error
  status: 401
}

export type listAiProvidersResponse404 = {
  data: Error
  status: 404
}

export type listAiProvidersResponseSuccess = (listAiProvidersResponse200) & {
  headers: Headers;
};
export type listAiProvidersResponseError = (listAiProvidersResponse401 | listAiProvidersResponse404) & {
  headers: Headers;
};

export type listAiProvidersResponse = (listAiProvidersResponseSuccess | listAiProvidersResponseError)

export const getListAiProvidersUrl = () => {




  return `/api/ai-providers`
}

/**
 * Readable by any signed-in caller, mirroring ``llm_profiles``' own precedent
 * for its list endpoint.
 * @summary List Ai Providers
 */
export const listAiProviders = async ( options?: RequestInit): Promise<listAiProvidersResponse> => {

  return customFetch<listAiProvidersResponse>(getListAiProvidersUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAiProvidersQueryKey = () => {
    return [
    `/api/ai-providers`
    ] as const;
    }


export const getListAiProvidersQueryOptions = <TData = Awaited<ReturnType<typeof listAiProviders>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAiProviders>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAiProvidersQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAiProviders>>> = ({ signal }) => listAiProviders({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAiProviders>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListAiProvidersQueryResult = NonNullable<Awaited<ReturnType<typeof listAiProviders>>>
export type ListAiProvidersQueryError = Error


export function useListAiProviders<TData = Awaited<ReturnType<typeof listAiProviders>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAiProviders>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAiProviders>>,
          TError,
          Awaited<ReturnType<typeof listAiProviders>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAiProviders<TData = Awaited<ReturnType<typeof listAiProviders>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAiProviders>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAiProviders>>,
          TError,
          Awaited<ReturnType<typeof listAiProviders>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAiProviders<TData = Awaited<ReturnType<typeof listAiProviders>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAiProviders>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Ai Providers
 */

export function useListAiProviders<TData = Awaited<ReturnType<typeof listAiProviders>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAiProviders>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListAiProvidersQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type createAiProviderResponse201 = {
  data: AIProviderView
  status: 201
}

export type createAiProviderResponse401 = {
  data: Error
  status: 401
}

export type createAiProviderResponse403 = {
  data: Error
  status: 403
}

export type createAiProviderResponse404 = {
  data: Error
  status: 404
}

export type createAiProviderResponse409 = {
  data: Error
  status: 409
}

export type createAiProviderResponse422 = {
  data: Error
  status: 422
}

export type createAiProviderResponseSuccess = (createAiProviderResponse201) & {
  headers: Headers;
};
export type createAiProviderResponseError = (createAiProviderResponse401 | createAiProviderResponse403 | createAiProviderResponse404 | createAiProviderResponse409 | createAiProviderResponse422) & {
  headers: Headers;
};

export type createAiProviderResponse = (createAiProviderResponseSuccess | createAiProviderResponseError)

export const getCreateAiProviderUrl = () => {




  return `/api/ai-providers`
}

/**
 * @summary Create Ai Provider
 */
export const createAiProvider = async (createAIProviderRequest: CreateAIProviderRequest, options?: RequestInit): Promise<createAiProviderResponse> => {

  return customFetch<createAiProviderResponse>(getCreateAiProviderUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createAIProviderRequest)
  }
);}




export const getCreateAiProviderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAiProvider>>, TError,{data: CreateAIProviderRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAiProvider>>, TError,{data: CreateAIProviderRequest}, TContext> => {

const mutationKey = ['createAiProvider'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAiProvider>>, {data: CreateAIProviderRequest}> = (props) => {
          const {data} = props ?? {};

          return  createAiProvider(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAiProviderMutationResult = NonNullable<Awaited<ReturnType<typeof createAiProvider>>>
    export type CreateAiProviderMutationBody = CreateAIProviderRequest
    export type CreateAiProviderMutationError = Error

    /**
 * @summary Create Ai Provider
 */
export const useCreateAiProvider = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAiProvider>>, TError,{data: CreateAIProviderRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createAiProvider>>,
        TError,
        {data: CreateAIProviderRequest},
        TContext
      > => {
      return useMutation(getCreateAiProviderMutationOptions(options), queryClient);
    }

export type getAiProviderResponse200 = {
  data: AIProviderView
  status: 200
}

export type getAiProviderResponse401 = {
  data: Error
  status: 401
}

export type getAiProviderResponse404 = {
  data: Error
  status: 404
}

export type getAiProviderResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getAiProviderResponseSuccess = (getAiProviderResponse200) & {
  headers: Headers;
};
export type getAiProviderResponseError = (getAiProviderResponse401 | getAiProviderResponse404 | getAiProviderResponse422) & {
  headers: Headers;
};

export type getAiProviderResponse = (getAiProviderResponseSuccess | getAiProviderResponseError)

export const getGetAiProviderUrl = (providerId: string,) => {




  return `/api/ai-providers/${providerId}`
}

/**
 * @summary Get Ai Provider
 */
export const getAiProvider = async (providerId: string, options?: RequestInit): Promise<getAiProviderResponse> => {

  return customFetch<getAiProviderResponse>(getGetAiProviderUrl(providerId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAiProviderQueryKey = (providerId: string,) => {
    return [
    `/api/ai-providers/${providerId}`
    ] as const;
    }


export const getGetAiProviderQueryOptions = <TData = Awaited<ReturnType<typeof getAiProvider>>, TError = Error | HTTPValidationError>(providerId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAiProvider>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAiProviderQueryKey(providerId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAiProvider>>> = ({ signal }) => getAiProvider(providerId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: providerId !== null && providerId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAiProvider>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAiProviderQueryResult = NonNullable<Awaited<ReturnType<typeof getAiProvider>>>
export type GetAiProviderQueryError = Error | HTTPValidationError


export function useGetAiProvider<TData = Awaited<ReturnType<typeof getAiProvider>>, TError = Error | HTTPValidationError>(
 providerId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAiProvider>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAiProvider>>,
          TError,
          Awaited<ReturnType<typeof getAiProvider>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAiProvider<TData = Awaited<ReturnType<typeof getAiProvider>>, TError = Error | HTTPValidationError>(
 providerId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAiProvider>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAiProvider>>,
          TError,
          Awaited<ReturnType<typeof getAiProvider>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAiProvider<TData = Awaited<ReturnType<typeof getAiProvider>>, TError = Error | HTTPValidationError>(
 providerId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAiProvider>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Ai Provider
 */

export function useGetAiProvider<TData = Awaited<ReturnType<typeof getAiProvider>>, TError = Error | HTTPValidationError>(
 providerId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAiProvider>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetAiProviderQueryOptions(providerId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type updateAiProviderResponse200 = {
  data: AIProviderView
  status: 200
}

export type updateAiProviderResponse401 = {
  data: Error
  status: 401
}

export type updateAiProviderResponse403 = {
  data: Error
  status: 403
}

export type updateAiProviderResponse404 = {
  data: Error
  status: 404
}

export type updateAiProviderResponse409 = {
  data: Error
  status: 409
}

export type updateAiProviderResponse422 = {
  data: Error
  status: 422
}

export type updateAiProviderResponseSuccess = (updateAiProviderResponse200) & {
  headers: Headers;
};
export type updateAiProviderResponseError = (updateAiProviderResponse401 | updateAiProviderResponse403 | updateAiProviderResponse404 | updateAiProviderResponse409 | updateAiProviderResponse422) & {
  headers: Headers;
};

export type updateAiProviderResponse = (updateAiProviderResponseSuccess | updateAiProviderResponseError)

export const getUpdateAiProviderUrl = (providerId: string,) => {




  return `/api/ai-providers/${providerId}`
}

/**
 * @summary Update Ai Provider
 */
export const updateAiProvider = async (providerId: string,
    updateAIProviderRequest: UpdateAIProviderRequest, options?: RequestInit): Promise<updateAiProviderResponse> => {

  return customFetch<updateAiProviderResponse>(getUpdateAiProviderUrl(providerId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(updateAIProviderRequest)
  }
);}




export const getUpdateAiProviderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAiProvider>>, TError,{providerId: string;data: UpdateAIProviderRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAiProvider>>, TError,{providerId: string;data: UpdateAIProviderRequest}, TContext> => {

const mutationKey = ['updateAiProvider'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAiProvider>>, {providerId: string;data: UpdateAIProviderRequest}> = (props) => {
          const {providerId,data} = props ?? {};

          return  updateAiProvider(providerId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAiProviderMutationResult = NonNullable<Awaited<ReturnType<typeof updateAiProvider>>>
    export type UpdateAiProviderMutationBody = UpdateAIProviderRequest
    export type UpdateAiProviderMutationError = Error

    /**
 * @summary Update Ai Provider
 */
export const useUpdateAiProvider = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAiProvider>>, TError,{providerId: string;data: UpdateAIProviderRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateAiProvider>>,
        TError,
        {providerId: string;data: UpdateAIProviderRequest},
        TContext
      > => {
      return useMutation(getUpdateAiProviderMutationOptions(options), queryClient);
    }

export type deleteAiProviderResponse204 = {
  data: void
  status: 204
}

export type deleteAiProviderResponse401 = {
  data: Error
  status: 401
}

export type deleteAiProviderResponse403 = {
  data: Error
  status: 403
}

export type deleteAiProviderResponse404 = {
  data: Error
  status: 404
}

export type deleteAiProviderResponse409 = {
  data: Error
  status: 409
}

export type deleteAiProviderResponse422 = {
  data: Error
  status: 422
}

export type deleteAiProviderResponseSuccess = (deleteAiProviderResponse204) & {
  headers: Headers;
};
export type deleteAiProviderResponseError = (deleteAiProviderResponse401 | deleteAiProviderResponse403 | deleteAiProviderResponse404 | deleteAiProviderResponse409 | deleteAiProviderResponse422) & {
  headers: Headers;
};

export type deleteAiProviderResponse = (deleteAiProviderResponseSuccess | deleteAiProviderResponseError)

export const getDeleteAiProviderUrl = (providerId: string,) => {




  return `/api/ai-providers/${providerId}`
}

/**
 * @summary Delete Ai Provider
 */
export const deleteAiProvider = async (providerId: string, options?: RequestInit): Promise<deleteAiProviderResponse> => {

  return customFetch<deleteAiProviderResponse>(getDeleteAiProviderUrl(providerId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteAiProviderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAiProvider>>, TError,{providerId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteAiProvider>>, TError,{providerId: string}, TContext> => {

const mutationKey = ['deleteAiProvider'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAiProvider>>, {providerId: string}> = (props) => {
          const {providerId} = props ?? {};

          return  deleteAiProvider(providerId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAiProviderMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAiProvider>>>

    export type DeleteAiProviderMutationError = Error

    /**
 * @summary Delete Ai Provider
 */
export const useDeleteAiProvider = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAiProvider>>, TError,{providerId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteAiProvider>>,
        TError,
        {providerId: string},
        TContext
      > => {
      return useMutation(getDeleteAiProviderMutationOptions(options), queryClient);
    }

export type setDefaultAiProviderResponse204 = {
  data: void
  status: 204
}

export type setDefaultAiProviderResponse401 = {
  data: Error
  status: 401
}

export type setDefaultAiProviderResponse403 = {
  data: Error
  status: 403
}

export type setDefaultAiProviderResponse404 = {
  data: Error
  status: 404
}

export type setDefaultAiProviderResponse409 = {
  data: Error
  status: 409
}

export type setDefaultAiProviderResponse422 = {
  data: Error
  status: 422
}

export type setDefaultAiProviderResponseSuccess = (setDefaultAiProviderResponse204) & {
  headers: Headers;
};
export type setDefaultAiProviderResponseError = (setDefaultAiProviderResponse401 | setDefaultAiProviderResponse403 | setDefaultAiProviderResponse404 | setDefaultAiProviderResponse409 | setDefaultAiProviderResponse422) & {
  headers: Headers;
};

export type setDefaultAiProviderResponse = (setDefaultAiProviderResponseSuccess | setDefaultAiProviderResponseError)

export const getSetDefaultAiProviderUrl = (providerId: string,) => {




  return `/api/ai-providers/${providerId}/default`
}

/**
 * @summary Set Default Ai Provider
 */
export const setDefaultAiProvider = async (providerId: string, options?: RequestInit): Promise<setDefaultAiProviderResponse> => {

  return customFetch<setDefaultAiProviderResponse>(getSetDefaultAiProviderUrl(providerId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getSetDefaultAiProviderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultAiProvider>>, TError,{providerId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setDefaultAiProvider>>, TError,{providerId: string}, TContext> => {

const mutationKey = ['setDefaultAiProvider'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setDefaultAiProvider>>, {providerId: string}> = (props) => {
          const {providerId} = props ?? {};

          return  setDefaultAiProvider(providerId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetDefaultAiProviderMutationResult = NonNullable<Awaited<ReturnType<typeof setDefaultAiProvider>>>

    export type SetDefaultAiProviderMutationError = Error

    /**
 * @summary Set Default Ai Provider
 */
export const useSetDefaultAiProvider = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultAiProvider>>, TError,{providerId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setDefaultAiProvider>>,
        TError,
        {providerId: string},
        TContext
      > => {
      return useMutation(getSetDefaultAiProviderMutationOptions(options), queryClient);
    }

export type testAiProviderConnectionResponse200 = {
  data: AIProviderConnectionTestResult
  status: 200
}

export type testAiProviderConnectionResponse401 = {
  data: Error
  status: 401
}

export type testAiProviderConnectionResponse403 = {
  data: Error
  status: 403
}

export type testAiProviderConnectionResponse404 = {
  data: Error
  status: 404
}

export type testAiProviderConnectionResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type testAiProviderConnectionResponseSuccess = (testAiProviderConnectionResponse200) & {
  headers: Headers;
};
export type testAiProviderConnectionResponseError = (testAiProviderConnectionResponse401 | testAiProviderConnectionResponse403 | testAiProviderConnectionResponse404 | testAiProviderConnectionResponse422) & {
  headers: Headers;
};

export type testAiProviderConnectionResponse = (testAiProviderConnectionResponseSuccess | testAiProviderConnectionResponseError)

export const getTestAiProviderConnectionUrl = (providerId: string,) => {




  return `/api/ai-providers/${providerId}/test`
}

/**
 * Fast, direct check — never the Argo/sandbox execution path, mirroring
 * ``llm_profiles/router.py``'s own test endpoint.
 *
 * A failed check (bad token, unreachable host, timeout) is a normal ``200`` with
 * ``ok=False``, never a ``5xx``.
 *
 * The body is optional: sent, it names candidate base_url/token values an admin
 * has typed but not saved, so the form can be verified before it is committed;
 * omitted, this tests the saved provider exactly as it always has.
 * @summary Test Ai Provider Connection
 */
export const testAiProviderConnection = async (providerId: string,
    aIProviderConnectionTestRequestNull?: AIProviderConnectionTestRequest | null, options?: RequestInit): Promise<testAiProviderConnectionResponse> => {

  return customFetch<testAiProviderConnectionResponse>(getTestAiProviderConnectionUrl(providerId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(aIProviderConnectionTestRequestNull)
  }
);}




export const getTestAiProviderConnectionMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testAiProviderConnection>>, TError,{providerId: string;data?: AIProviderConnectionTestRequest | null}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof testAiProviderConnection>>, TError,{providerId: string;data?: AIProviderConnectionTestRequest | null}, TContext> => {

const mutationKey = ['testAiProviderConnection'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof testAiProviderConnection>>, {providerId: string;data?: AIProviderConnectionTestRequest | null}> = (props) => {
          const {providerId,data} = props ?? {};

          return  testAiProviderConnection(providerId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TestAiProviderConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof testAiProviderConnection>>>
    export type TestAiProviderConnectionMutationBody = AIProviderConnectionTestRequest | null | undefined
    export type TestAiProviderConnectionMutationError = Error | HTTPValidationError

    /**
 * @summary Test Ai Provider Connection
 */
export const useTestAiProviderConnection = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testAiProviderConnection>>, TError,{providerId: string;data?: AIProviderConnectionTestRequest | null}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof testAiProviderConnection>>,
        TError,
        {providerId: string;data?: AIProviderConnectionTestRequest | null},
        TContext
      > => {
      return useMutation(getTestAiProviderConnectionMutationOptions(options), queryClient);
    }

export type createConnectionResponse201 = {
  data: ConnectionView
  status: 201
}

export type createConnectionResponse401 = {
  data: Error
  status: 401
}

export type createConnectionResponse403 = {
  data: Error
  status: 403
}

export type createConnectionResponse404 = {
  data: Error
  status: 404
}

export type createConnectionResponse409 = {
  data: Error
  status: 409
}

export type createConnectionResponse422 = {
  data: Error
  status: 422
}

export type createConnectionResponseSuccess = (createConnectionResponse201) & {
  headers: Headers;
};
export type createConnectionResponseError = (createConnectionResponse401 | createConnectionResponse403 | createConnectionResponse404 | createConnectionResponse409 | createConnectionResponse422) & {
  headers: Headers;
};

export type createConnectionResponse = (createConnectionResponseSuccess | createConnectionResponseError)

export const getCreateConnectionUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/connections`
}

/**
 * @summary Create Connection
 */
export const createConnection = async (workspaceId: string,
    connectionCreate: ConnectionCreate, options?: RequestInit): Promise<createConnectionResponse> => {

  return customFetch<createConnectionResponse>(getCreateConnectionUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(connectionCreate)
  }
);}




export const getCreateConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createConnection>>, TError,{workspaceId: string;data: ConnectionCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createConnection>>, TError,{workspaceId: string;data: ConnectionCreate}, TContext> => {

const mutationKey = ['createConnection'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createConnection>>, {workspaceId: string;data: ConnectionCreate}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createConnection(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof createConnection>>>
    export type CreateConnectionMutationBody = ConnectionCreate
    export type CreateConnectionMutationError = Error

    /**
 * @summary Create Connection
 */
export const useCreateConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createConnection>>, TError,{workspaceId: string;data: ConnectionCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createConnection>>,
        TError,
        {workspaceId: string;data: ConnectionCreate},
        TContext
      > => {
      return useMutation(getCreateConnectionMutationOptions(options), queryClient);
    }

export type listConnectionsResponse200 = {
  data: ConnectionView[]
  status: 200
}

export type listConnectionsResponse401 = {
  data: Error
  status: 401
}

export type listConnectionsResponse403 = {
  data: Error
  status: 403
}

export type listConnectionsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listConnectionsResponseSuccess = (listConnectionsResponse200) & {
  headers: Headers;
};
export type listConnectionsResponseError = (listConnectionsResponse401 | listConnectionsResponse403 | listConnectionsResponse422) & {
  headers: Headers;
};

export type listConnectionsResponse = (listConnectionsResponseSuccess | listConnectionsResponseError)

export const getListConnectionsUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/connections`
}

/**
 * @summary List Connections
 */
export const listConnections = async (workspaceId: string, options?: RequestInit): Promise<listConnectionsResponse> => {

  return customFetch<listConnectionsResponse>(getListConnectionsUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListConnectionsQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/connections`
    ] as const;
    }


export const getListConnectionsQueryOptions = <TData = Awaited<ReturnType<typeof listConnections>>, TError = Error | HTTPValidationError>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listConnections>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListConnectionsQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listConnections>>> = ({ signal }) => listConnections(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listConnections>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListConnectionsQueryResult = NonNullable<Awaited<ReturnType<typeof listConnections>>>
export type ListConnectionsQueryError = Error | HTTPValidationError


export function useListConnections<TData = Awaited<ReturnType<typeof listConnections>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listConnections>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listConnections>>,
          TError,
          Awaited<ReturnType<typeof listConnections>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListConnections<TData = Awaited<ReturnType<typeof listConnections>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listConnections>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listConnections>>,
          TError,
          Awaited<ReturnType<typeof listConnections>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListConnections<TData = Awaited<ReturnType<typeof listConnections>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listConnections>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Connections
 */

export function useListConnections<TData = Awaited<ReturnType<typeof listConnections>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listConnections>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListConnectionsQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type listConnectionProvidersResponse200 = {
  data: ConnectionProviderView[]
  status: 200
}

export type listConnectionProvidersResponse401 = {
  data: Error
  status: 401
}

export type listConnectionProvidersResponse403 = {
  data: Error
  status: 403
}

export type listConnectionProvidersResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listConnectionProvidersResponseSuccess = (listConnectionProvidersResponse200) & {
  headers: Headers;
};
export type listConnectionProvidersResponseError = (listConnectionProvidersResponse401 | listConnectionProvidersResponse403 | listConnectionProvidersResponse422) & {
  headers: Headers;
};

export type listConnectionProvidersResponse = (listConnectionProvidersResponseSuccess | listConnectionProvidersResponseError)

export const getListConnectionProvidersUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/connections/catalog`
}

/**
 * The provider catalog (connections rework). Workspace-scoped in path only, to
 * sit beside the connections it describes; the catalog itself is global.
 * @summary List Connection Providers
 */
export const listConnectionProviders = async (workspaceId: string, options?: RequestInit): Promise<listConnectionProvidersResponse> => {

  return customFetch<listConnectionProvidersResponse>(getListConnectionProvidersUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListConnectionProvidersQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/connections/catalog`
    ] as const;
    }


export const getListConnectionProvidersQueryOptions = <TData = Awaited<ReturnType<typeof listConnectionProviders>>, TError = Error | HTTPValidationError>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listConnectionProviders>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListConnectionProvidersQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listConnectionProviders>>> = ({ signal }) => listConnectionProviders(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listConnectionProviders>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListConnectionProvidersQueryResult = NonNullable<Awaited<ReturnType<typeof listConnectionProviders>>>
export type ListConnectionProvidersQueryError = Error | HTTPValidationError


export function useListConnectionProviders<TData = Awaited<ReturnType<typeof listConnectionProviders>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listConnectionProviders>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listConnectionProviders>>,
          TError,
          Awaited<ReturnType<typeof listConnectionProviders>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListConnectionProviders<TData = Awaited<ReturnType<typeof listConnectionProviders>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listConnectionProviders>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listConnectionProviders>>,
          TError,
          Awaited<ReturnType<typeof listConnectionProviders>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListConnectionProviders<TData = Awaited<ReturnType<typeof listConnectionProviders>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listConnectionProviders>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Connection Providers
 */

export function useListConnectionProviders<TData = Awaited<ReturnType<typeof listConnectionProviders>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listConnectionProviders>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListConnectionProvidersQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type getConnectionResponse200 = {
  data: ConnectionView
  status: 200
}

export type getConnectionResponse401 = {
  data: Error
  status: 401
}

export type getConnectionResponse403 = {
  data: Error
  status: 403
}

export type getConnectionResponse404 = {
  data: Error
  status: 404
}

export type getConnectionResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getConnectionResponseSuccess = (getConnectionResponse200) & {
  headers: Headers;
};
export type getConnectionResponseError = (getConnectionResponse401 | getConnectionResponse403 | getConnectionResponse404 | getConnectionResponse422) & {
  headers: Headers;
};

export type getConnectionResponse = (getConnectionResponseSuccess | getConnectionResponseError)

export const getGetConnectionUrl = (workspaceId: string,
    connectionId: string,) => {




  return `/api/workspaces/${workspaceId}/connections/${connectionId}`
}

/**
 * @summary Get Connection
 */
export const getConnection = async (workspaceId: string,
    connectionId: string, options?: RequestInit): Promise<getConnectionResponse> => {

  return customFetch<getConnectionResponse>(getGetConnectionUrl(workspaceId,connectionId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetConnectionQueryKey = (workspaceId: string,
    connectionId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/connections/${connectionId}`
    ] as const;
    }


export const getGetConnectionQueryOptions = <TData = Awaited<ReturnType<typeof getConnection>>, TError = Error | HTTPValidationError>(workspaceId: string,
    connectionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnection>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetConnectionQueryKey(workspaceId,connectionId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getConnection>>> = ({ signal }) => getConnection(workspaceId,connectionId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && connectionId !== null && connectionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getConnection>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof getConnection>>>
export type GetConnectionQueryError = Error | HTTPValidationError


export function useGetConnection<TData = Awaited<ReturnType<typeof getConnection>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    connectionId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnection>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnection>>,
          TError,
          Awaited<ReturnType<typeof getConnection>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetConnection<TData = Awaited<ReturnType<typeof getConnection>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    connectionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnection>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnection>>,
          TError,
          Awaited<ReturnType<typeof getConnection>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetConnection<TData = Awaited<ReturnType<typeof getConnection>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    connectionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnection>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Connection
 */

export function useGetConnection<TData = Awaited<ReturnType<typeof getConnection>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    connectionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnection>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetConnectionQueryOptions(workspaceId,connectionId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type updateConnectionResponse200 = {
  data: ConnectionView
  status: 200
}

export type updateConnectionResponse401 = {
  data: Error
  status: 401
}

export type updateConnectionResponse403 = {
  data: Error
  status: 403
}

export type updateConnectionResponse404 = {
  data: Error
  status: 404
}

export type updateConnectionResponse409 = {
  data: Error
  status: 409
}

export type updateConnectionResponse422 = {
  data: Error
  status: 422
}

export type updateConnectionResponseSuccess = (updateConnectionResponse200) & {
  headers: Headers;
};
export type updateConnectionResponseError = (updateConnectionResponse401 | updateConnectionResponse403 | updateConnectionResponse404 | updateConnectionResponse409 | updateConnectionResponse422) & {
  headers: Headers;
};

export type updateConnectionResponse = (updateConnectionResponseSuccess | updateConnectionResponseError)

export const getUpdateConnectionUrl = (workspaceId: string,
    connectionId: string,) => {




  return `/api/workspaces/${workspaceId}/connections/${connectionId}`
}

/**
 * @summary Update Connection
 */
export const updateConnection = async (workspaceId: string,
    connectionId: string,
    connectionUpdate: ConnectionUpdate, options?: RequestInit): Promise<updateConnectionResponse> => {

  return customFetch<updateConnectionResponse>(getUpdateConnectionUrl(workspaceId,connectionId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(connectionUpdate)
  }
);}




export const getUpdateConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateConnection>>, TError,{workspaceId: string;connectionId: string;data: ConnectionUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateConnection>>, TError,{workspaceId: string;connectionId: string;data: ConnectionUpdate}, TContext> => {

const mutationKey = ['updateConnection'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateConnection>>, {workspaceId: string;connectionId: string;data: ConnectionUpdate}> = (props) => {
          const {workspaceId,connectionId,data} = props ?? {};

          return  updateConnection(workspaceId,connectionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof updateConnection>>>
    export type UpdateConnectionMutationBody = ConnectionUpdate
    export type UpdateConnectionMutationError = Error

    /**
 * @summary Update Connection
 */
export const useUpdateConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateConnection>>, TError,{workspaceId: string;connectionId: string;data: ConnectionUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateConnection>>,
        TError,
        {workspaceId: string;connectionId: string;data: ConnectionUpdate},
        TContext
      > => {
      return useMutation(getUpdateConnectionMutationOptions(options), queryClient);
    }

export type deleteConnectionResponse204 = {
  data: void
  status: 204
}

export type deleteConnectionResponse401 = {
  data: Error
  status: 401
}

export type deleteConnectionResponse403 = {
  data: Error
  status: 403
}

export type deleteConnectionResponse404 = {
  data: Error
  status: 404
}

export type deleteConnectionResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type deleteConnectionResponseSuccess = (deleteConnectionResponse204) & {
  headers: Headers;
};
export type deleteConnectionResponseError = (deleteConnectionResponse401 | deleteConnectionResponse403 | deleteConnectionResponse404 | deleteConnectionResponse422) & {
  headers: Headers;
};

export type deleteConnectionResponse = (deleteConnectionResponseSuccess | deleteConnectionResponseError)

export const getDeleteConnectionUrl = (workspaceId: string,
    connectionId: string,) => {




  return `/api/workspaces/${workspaceId}/connections/${connectionId}`
}

/**
 * @summary Delete Connection
 */
export const deleteConnection = async (workspaceId: string,
    connectionId: string, options?: RequestInit): Promise<deleteConnectionResponse> => {

  return customFetch<deleteConnectionResponse>(getDeleteConnectionUrl(workspaceId,connectionId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteConnectionMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteConnection>>, TError,{workspaceId: string;connectionId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteConnection>>, TError,{workspaceId: string;connectionId: string}, TContext> => {

const mutationKey = ['deleteConnection'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteConnection>>, {workspaceId: string;connectionId: string}> = (props) => {
          const {workspaceId,connectionId} = props ?? {};

          return  deleteConnection(workspaceId,connectionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof deleteConnection>>>

    export type DeleteConnectionMutationError = Error | HTTPValidationError

    /**
 * @summary Delete Connection
 */
export const useDeleteConnection = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteConnection>>, TError,{workspaceId: string;connectionId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteConnection>>,
        TError,
        {workspaceId: string;connectionId: string},
        TContext
      > => {
      return useMutation(getDeleteConnectionMutationOptions(options), queryClient);
    }

export type testConnectionResponse200 = {
  data: ConnectionTestResult
  status: 200
}

export type testConnectionResponse401 = {
  data: Error
  status: 401
}

export type testConnectionResponse403 = {
  data: Error
  status: 403
}

export type testConnectionResponse404 = {
  data: Error
  status: 404
}

export type testConnectionResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type testConnectionResponse500 = {
  data: Error
  status: 500
}

export type testConnectionResponseSuccess = (testConnectionResponse200) & {
  headers: Headers;
};
export type testConnectionResponseError = (testConnectionResponse401 | testConnectionResponse403 | testConnectionResponse404 | testConnectionResponse422 | testConnectionResponse500) & {
  headers: Headers;
};

export type testConnectionResponse = (testConnectionResponseSuccess | testConnectionResponseError)

export const getTestConnectionUrl = (workspaceId: string,
    connectionId: string,) => {




  return `/api/workspaces/${workspaceId}/connections/${connectionId}/test`
}

/**
 * @summary Test Connection
 */
export const testConnection = async (workspaceId: string,
    connectionId: string, options?: RequestInit): Promise<testConnectionResponse> => {

  return customFetch<testConnectionResponse>(getTestConnectionUrl(workspaceId,connectionId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getTestConnectionMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testConnection>>, TError,{workspaceId: string;connectionId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof testConnection>>, TError,{workspaceId: string;connectionId: string}, TContext> => {

const mutationKey = ['testConnection'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof testConnection>>, {workspaceId: string;connectionId: string}> = (props) => {
          const {workspaceId,connectionId} = props ?? {};

          return  testConnection(workspaceId,connectionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TestConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof testConnection>>>

    export type TestConnectionMutationError = Error | HTTPValidationError

    /**
 * @summary Test Connection
 */
export const useTestConnection = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testConnection>>, TError,{workspaceId: string;connectionId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof testConnection>>,
        TError,
        {workspaceId: string;connectionId: string},
        TContext
      > => {
      return useMutation(getTestConnectionMutationOptions(options), queryClient);
    }

export type createExecutionImageResponse201 = {
  data: ExecutionImageView
  status: 201
}

export type createExecutionImageResponse401 = {
  data: Error
  status: 401
}

export type createExecutionImageResponse403 = {
  data: Error
  status: 403
}

export type createExecutionImageResponse404 = {
  data: Error
  status: 404
}

export type createExecutionImageResponse422 = {
  data: Error
  status: 422
}

export type createExecutionImageResponseSuccess = (createExecutionImageResponse201) & {
  headers: Headers;
};
export type createExecutionImageResponseError = (createExecutionImageResponse401 | createExecutionImageResponse403 | createExecutionImageResponse404 | createExecutionImageResponse422) & {
  headers: Headers;
};

export type createExecutionImageResponse = (createExecutionImageResponseSuccess | createExecutionImageResponseError)

export const getCreateExecutionImageUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/execution-images`
}

/**
 * @summary Create Execution Image
 */
export const createExecutionImage = async (workspaceId: string,
    executionImageCreate: ExecutionImageCreate, options?: RequestInit): Promise<createExecutionImageResponse> => {

  return customFetch<createExecutionImageResponse>(getCreateExecutionImageUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(executionImageCreate)
  }
);}




export const getCreateExecutionImageMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createExecutionImage>>, TError,{workspaceId: string;data: ExecutionImageCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createExecutionImage>>, TError,{workspaceId: string;data: ExecutionImageCreate}, TContext> => {

const mutationKey = ['createExecutionImage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createExecutionImage>>, {workspaceId: string;data: ExecutionImageCreate}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createExecutionImage(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateExecutionImageMutationResult = NonNullable<Awaited<ReturnType<typeof createExecutionImage>>>
    export type CreateExecutionImageMutationBody = ExecutionImageCreate
    export type CreateExecutionImageMutationError = Error

    /**
 * @summary Create Execution Image
 */
export const useCreateExecutionImage = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createExecutionImage>>, TError,{workspaceId: string;data: ExecutionImageCreate}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createExecutionImage>>,
        TError,
        {workspaceId: string;data: ExecutionImageCreate},
        TContext
      > => {
      return useMutation(getCreateExecutionImageMutationOptions(options), queryClient);
    }

export type listExecutionImagesResponse200 = {
  data: ExecutionImageView[]
  status: 200
}

export type listExecutionImagesResponse401 = {
  data: Error
  status: 401
}

export type listExecutionImagesResponse403 = {
  data: Error
  status: 403
}

export type listExecutionImagesResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listExecutionImagesResponseSuccess = (listExecutionImagesResponse200) & {
  headers: Headers;
};
export type listExecutionImagesResponseError = (listExecutionImagesResponse401 | listExecutionImagesResponse403 | listExecutionImagesResponse422) & {
  headers: Headers;
};

export type listExecutionImagesResponse = (listExecutionImagesResponseSuccess | listExecutionImagesResponseError)

export const getListExecutionImagesUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/execution-images`
}

/**
 * @summary List Execution Images
 */
export const listExecutionImages = async (workspaceId: string, options?: RequestInit): Promise<listExecutionImagesResponse> => {

  return customFetch<listExecutionImagesResponse>(getListExecutionImagesUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListExecutionImagesQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/execution-images`
    ] as const;
    }


export const getListExecutionImagesQueryOptions = <TData = Awaited<ReturnType<typeof listExecutionImages>>, TError = Error | HTTPValidationError>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutionImages>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListExecutionImagesQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listExecutionImages>>> = ({ signal }) => listExecutionImages(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listExecutionImages>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListExecutionImagesQueryResult = NonNullable<Awaited<ReturnType<typeof listExecutionImages>>>
export type ListExecutionImagesQueryError = Error | HTTPValidationError


export function useListExecutionImages<TData = Awaited<ReturnType<typeof listExecutionImages>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutionImages>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listExecutionImages>>,
          TError,
          Awaited<ReturnType<typeof listExecutionImages>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListExecutionImages<TData = Awaited<ReturnType<typeof listExecutionImages>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutionImages>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listExecutionImages>>,
          TError,
          Awaited<ReturnType<typeof listExecutionImages>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListExecutionImages<TData = Awaited<ReturnType<typeof listExecutionImages>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutionImages>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Execution Images
 */

export function useListExecutionImages<TData = Awaited<ReturnType<typeof listExecutionImages>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutionImages>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListExecutionImagesQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type getExecutionImageResponse200 = {
  data: ExecutionImageView
  status: 200
}

export type getExecutionImageResponse401 = {
  data: Error
  status: 401
}

export type getExecutionImageResponse403 = {
  data: Error
  status: 403
}

export type getExecutionImageResponse404 = {
  data: Error
  status: 404
}

export type getExecutionImageResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getExecutionImageResponseSuccess = (getExecutionImageResponse200) & {
  headers: Headers;
};
export type getExecutionImageResponseError = (getExecutionImageResponse401 | getExecutionImageResponse403 | getExecutionImageResponse404 | getExecutionImageResponse422) & {
  headers: Headers;
};

export type getExecutionImageResponse = (getExecutionImageResponseSuccess | getExecutionImageResponseError)

export const getGetExecutionImageUrl = (workspaceId: string,
    imageId: string,) => {




  return `/api/workspaces/${workspaceId}/execution-images/${imageId}`
}

/**
 * @summary Get Execution Image
 */
export const getExecutionImage = async (workspaceId: string,
    imageId: string, options?: RequestInit): Promise<getExecutionImageResponse> => {

  return customFetch<getExecutionImageResponse>(getGetExecutionImageUrl(workspaceId,imageId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetExecutionImageQueryKey = (workspaceId: string,
    imageId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/execution-images/${imageId}`
    ] as const;
    }


export const getGetExecutionImageQueryOptions = <TData = Awaited<ReturnType<typeof getExecutionImage>>, TError = Error | HTTPValidationError>(workspaceId: string,
    imageId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionImage>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetExecutionImageQueryKey(workspaceId,imageId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getExecutionImage>>> = ({ signal }) => getExecutionImage(workspaceId,imageId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && imageId !== null && imageId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getExecutionImage>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetExecutionImageQueryResult = NonNullable<Awaited<ReturnType<typeof getExecutionImage>>>
export type GetExecutionImageQueryError = Error | HTTPValidationError


export function useGetExecutionImage<TData = Awaited<ReturnType<typeof getExecutionImage>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    imageId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionImage>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getExecutionImage>>,
          TError,
          Awaited<ReturnType<typeof getExecutionImage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetExecutionImage<TData = Awaited<ReturnType<typeof getExecutionImage>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    imageId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionImage>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getExecutionImage>>,
          TError,
          Awaited<ReturnType<typeof getExecutionImage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetExecutionImage<TData = Awaited<ReturnType<typeof getExecutionImage>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    imageId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionImage>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Execution Image
 */

export function useGetExecutionImage<TData = Awaited<ReturnType<typeof getExecutionImage>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    imageId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionImage>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetExecutionImageQueryOptions(workspaceId,imageId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type updateExecutionImageResponse200 = {
  data: ExecutionImageView
  status: 200
}

export type updateExecutionImageResponse401 = {
  data: Error
  status: 401
}

export type updateExecutionImageResponse403 = {
  data: Error
  status: 403
}

export type updateExecutionImageResponse404 = {
  data: Error
  status: 404
}

export type updateExecutionImageResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type updateExecutionImageResponseSuccess = (updateExecutionImageResponse200) & {
  headers: Headers;
};
export type updateExecutionImageResponseError = (updateExecutionImageResponse401 | updateExecutionImageResponse403 | updateExecutionImageResponse404 | updateExecutionImageResponse422) & {
  headers: Headers;
};

export type updateExecutionImageResponse = (updateExecutionImageResponseSuccess | updateExecutionImageResponseError)

export const getUpdateExecutionImageUrl = (workspaceId: string,
    imageId: string,) => {




  return `/api/workspaces/${workspaceId}/execution-images/${imageId}`
}

/**
 * @summary Update Execution Image
 */
export const updateExecutionImage = async (workspaceId: string,
    imageId: string,
    executionImageUpdate: ExecutionImageUpdate, options?: RequestInit): Promise<updateExecutionImageResponse> => {

  return customFetch<updateExecutionImageResponse>(getUpdateExecutionImageUrl(workspaceId,imageId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(executionImageUpdate)
  }
);}




export const getUpdateExecutionImageMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateExecutionImage>>, TError,{workspaceId: string;imageId: string;data: ExecutionImageUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateExecutionImage>>, TError,{workspaceId: string;imageId: string;data: ExecutionImageUpdate}, TContext> => {

const mutationKey = ['updateExecutionImage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateExecutionImage>>, {workspaceId: string;imageId: string;data: ExecutionImageUpdate}> = (props) => {
          const {workspaceId,imageId,data} = props ?? {};

          return  updateExecutionImage(workspaceId,imageId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateExecutionImageMutationResult = NonNullable<Awaited<ReturnType<typeof updateExecutionImage>>>
    export type UpdateExecutionImageMutationBody = ExecutionImageUpdate
    export type UpdateExecutionImageMutationError = Error | HTTPValidationError

    /**
 * @summary Update Execution Image
 */
export const useUpdateExecutionImage = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateExecutionImage>>, TError,{workspaceId: string;imageId: string;data: ExecutionImageUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateExecutionImage>>,
        TError,
        {workspaceId: string;imageId: string;data: ExecutionImageUpdate},
        TContext
      > => {
      return useMutation(getUpdateExecutionImageMutationOptions(options), queryClient);
    }

export type deleteExecutionImageResponse204 = {
  data: void
  status: 204
}

export type deleteExecutionImageResponse401 = {
  data: Error
  status: 401
}

export type deleteExecutionImageResponse403 = {
  data: Error
  status: 403
}

export type deleteExecutionImageResponse404 = {
  data: Error
  status: 404
}

export type deleteExecutionImageResponse422 = {
  data: Error
  status: 422
}

export type deleteExecutionImageResponseSuccess = (deleteExecutionImageResponse204) & {
  headers: Headers;
};
export type deleteExecutionImageResponseError = (deleteExecutionImageResponse401 | deleteExecutionImageResponse403 | deleteExecutionImageResponse404 | deleteExecutionImageResponse422) & {
  headers: Headers;
};

export type deleteExecutionImageResponse = (deleteExecutionImageResponseSuccess | deleteExecutionImageResponseError)

export const getDeleteExecutionImageUrl = (workspaceId: string,
    imageId: string,) => {




  return `/api/workspaces/${workspaceId}/execution-images/${imageId}`
}

/**
 * @summary Delete Execution Image
 */
export const deleteExecutionImage = async (workspaceId: string,
    imageId: string, options?: RequestInit): Promise<deleteExecutionImageResponse> => {

  return customFetch<deleteExecutionImageResponse>(getDeleteExecutionImageUrl(workspaceId,imageId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteExecutionImageMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteExecutionImage>>, TError,{workspaceId: string;imageId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteExecutionImage>>, TError,{workspaceId: string;imageId: string}, TContext> => {

const mutationKey = ['deleteExecutionImage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteExecutionImage>>, {workspaceId: string;imageId: string}> = (props) => {
          const {workspaceId,imageId} = props ?? {};

          return  deleteExecutionImage(workspaceId,imageId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteExecutionImageMutationResult = NonNullable<Awaited<ReturnType<typeof deleteExecutionImage>>>

    export type DeleteExecutionImageMutationError = Error

    /**
 * @summary Delete Execution Image
 */
export const useDeleteExecutionImage = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteExecutionImage>>, TError,{workspaceId: string;imageId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteExecutionImage>>,
        TError,
        {workspaceId: string;imageId: string},
        TContext
      > => {
      return useMutation(getDeleteExecutionImageMutationOptions(options), queryClient);
    }

export type setDefaultExecutionImageResponse200 = {
  data: ExecutionImageView
  status: 200
}

export type setDefaultExecutionImageResponse401 = {
  data: Error
  status: 401
}

export type setDefaultExecutionImageResponse403 = {
  data: Error
  status: 403
}

export type setDefaultExecutionImageResponse404 = {
  data: Error
  status: 404
}

export type setDefaultExecutionImageResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type setDefaultExecutionImageResponseSuccess = (setDefaultExecutionImageResponse200) & {
  headers: Headers;
};
export type setDefaultExecutionImageResponseError = (setDefaultExecutionImageResponse401 | setDefaultExecutionImageResponse403 | setDefaultExecutionImageResponse404 | setDefaultExecutionImageResponse422) & {
  headers: Headers;
};

export type setDefaultExecutionImageResponse = (setDefaultExecutionImageResponseSuccess | setDefaultExecutionImageResponseError)

export const getSetDefaultExecutionImageUrl = (workspaceId: string,
    imageId: string,) => {




  return `/api/workspaces/${workspaceId}/execution-images/${imageId}/set-default`
}

/**
 * @summary Set Default Execution Image
 */
export const setDefaultExecutionImage = async (workspaceId: string,
    imageId: string, options?: RequestInit): Promise<setDefaultExecutionImageResponse> => {

  return customFetch<setDefaultExecutionImageResponse>(getSetDefaultExecutionImageUrl(workspaceId,imageId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getSetDefaultExecutionImageMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultExecutionImage>>, TError,{workspaceId: string;imageId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setDefaultExecutionImage>>, TError,{workspaceId: string;imageId: string}, TContext> => {

const mutationKey = ['setDefaultExecutionImage'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setDefaultExecutionImage>>, {workspaceId: string;imageId: string}> = (props) => {
          const {workspaceId,imageId} = props ?? {};

          return  setDefaultExecutionImage(workspaceId,imageId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetDefaultExecutionImageMutationResult = NonNullable<Awaited<ReturnType<typeof setDefaultExecutionImage>>>

    export type SetDefaultExecutionImageMutationError = Error | HTTPValidationError

    /**
 * @summary Set Default Execution Image
 */
export const useSetDefaultExecutionImage = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultExecutionImage>>, TError,{workspaceId: string;imageId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setDefaultExecutionImage>>,
        TError,
        {workspaceId: string;imageId: string},
        TContext
      > => {
      return useMutation(getSetDefaultExecutionImageMutationOptions(options), queryClient);
    }

export type listLibraryItemsResponse200 = {
  data: LibraryCatalogView
  status: 200
}

export type listLibraryItemsResponse401 = {
  data: Error
  status: 401
}

export type listLibraryItemsResponse403 = {
  data: Error
  status: 403
}

export type listLibraryItemsResponse404 = {
  data: Error
  status: 404
}

export type listLibraryItemsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listLibraryItemsResponse503 = {
  data: Error
  status: 503
}

export type listLibraryItemsResponseSuccess = (listLibraryItemsResponse200) & {
  headers: Headers;
};
export type listLibraryItemsResponseError = (listLibraryItemsResponse401 | listLibraryItemsResponse403 | listLibraryItemsResponse404 | listLibraryItemsResponse422 | listLibraryItemsResponse503) & {
  headers: Headers;
};

export type listLibraryItemsResponse = (listLibraryItemsResponseSuccess | listLibraryItemsResponseError)

export const getListLibraryItemsUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/library/items`
}

/**
 * Every library item, with where this workspace stands against it.
 * @summary List Library Items
 */
export const listLibraryItems = async (workspaceId: string, options?: RequestInit): Promise<listLibraryItemsResponse> => {

  return customFetch<listLibraryItemsResponse>(getListLibraryItemsUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListLibraryItemsQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/library/items`
    ] as const;
    }


export const getListLibraryItemsQueryOptions = <TData = Awaited<ReturnType<typeof listLibraryItems>>, TError = Error | HTTPValidationError>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLibraryItems>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListLibraryItemsQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listLibraryItems>>> = ({ signal }) => listLibraryItems(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listLibraryItems>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListLibraryItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listLibraryItems>>>
export type ListLibraryItemsQueryError = Error | HTTPValidationError


export function useListLibraryItems<TData = Awaited<ReturnType<typeof listLibraryItems>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLibraryItems>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listLibraryItems>>,
          TError,
          Awaited<ReturnType<typeof listLibraryItems>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListLibraryItems<TData = Awaited<ReturnType<typeof listLibraryItems>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLibraryItems>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listLibraryItems>>,
          TError,
          Awaited<ReturnType<typeof listLibraryItems>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListLibraryItems<TData = Awaited<ReturnType<typeof listLibraryItems>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLibraryItems>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Library Items
 */

export function useListLibraryItems<TData = Awaited<ReturnType<typeof listLibraryItems>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLibraryItems>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListLibraryItemsQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readLibraryItemFileResponse200 = {
  data: LibraryFileView
  status: 200
}

export type readLibraryItemFileResponse401 = {
  data: Error
  status: 401
}

export type readLibraryItemFileResponse403 = {
  data: Error
  status: 403
}

export type readLibraryItemFileResponse404 = {
  data: Error
  status: 404
}

export type readLibraryItemFileResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type readLibraryItemFileResponse503 = {
  data: Error
  status: 503
}

export type readLibraryItemFileResponseSuccess = (readLibraryItemFileResponse200) & {
  headers: Headers;
};
export type readLibraryItemFileResponseError = (readLibraryItemFileResponse401 | readLibraryItemFileResponse403 | readLibraryItemFileResponse404 | readLibraryItemFileResponse422 | readLibraryItemFileResponse503) & {
  headers: Headers;
};

export type readLibraryItemFileResponse = (readLibraryItemFileResponseSuccess | readLibraryItemFileResponseError)

export const getReadLibraryItemFileUrl = (workspaceId: string,
    kind: LibraryItemKind,
    key: string,
    params: ReadLibraryItemFileParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/library/items/${kind}/${key}/file?${stringifiedParams}` : `/api/workspaces/${workspaceId}/library/items/${kind}/${key}/file`
}

/**
 * One file of one library item, so it can be read before it is installed.
 *
 * Returns what the library holds and interprets none of it: front matter is
 * not stripped, a workflow is not outlined, markdown is not rendered. That is
 * what lets one door serve all three kinds.
 * @summary Read Library Item File
 */
export const readLibraryItemFile = async (workspaceId: string,
    kind: LibraryItemKind,
    key: string,
    params: ReadLibraryItemFileParams, options?: RequestInit): Promise<readLibraryItemFileResponse> => {

  return customFetch<readLibraryItemFileResponse>(getReadLibraryItemFileUrl(workspaceId,kind,key,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadLibraryItemFileQueryKey = (workspaceId: string,
    kind: LibraryItemKind,
    key: string,
    params?: ReadLibraryItemFileParams,) => {
    return [
    `/api/workspaces/${workspaceId}/library/items/${kind}/${key}/file`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadLibraryItemFileQueryOptions = <TData = Awaited<ReturnType<typeof readLibraryItemFile>>, TError = Error | HTTPValidationError>(workspaceId: string,
    kind: LibraryItemKind,
    key: string,
    params: ReadLibraryItemFileParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readLibraryItemFile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadLibraryItemFileQueryKey(workspaceId,kind,key,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readLibraryItemFile>>> = ({ signal }) => readLibraryItemFile(workspaceId,kind,key,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && kind !== null && kind !== undefined && key !== null && key !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readLibraryItemFile>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadLibraryItemFileQueryResult = NonNullable<Awaited<ReturnType<typeof readLibraryItemFile>>>
export type ReadLibraryItemFileQueryError = Error | HTTPValidationError


export function useReadLibraryItemFile<TData = Awaited<ReturnType<typeof readLibraryItemFile>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    kind: LibraryItemKind,
    key: string,
    params: ReadLibraryItemFileParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readLibraryItemFile>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readLibraryItemFile>>,
          TError,
          Awaited<ReturnType<typeof readLibraryItemFile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadLibraryItemFile<TData = Awaited<ReturnType<typeof readLibraryItemFile>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    kind: LibraryItemKind,
    key: string,
    params: ReadLibraryItemFileParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readLibraryItemFile>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readLibraryItemFile>>,
          TError,
          Awaited<ReturnType<typeof readLibraryItemFile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadLibraryItemFile<TData = Awaited<ReturnType<typeof readLibraryItemFile>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    kind: LibraryItemKind,
    key: string,
    params: ReadLibraryItemFileParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readLibraryItemFile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Library Item File
 */

export function useReadLibraryItemFile<TData = Awaited<ReturnType<typeof readLibraryItemFile>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    kind: LibraryItemKind,
    key: string,
    params: ReadLibraryItemFileParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readLibraryItemFile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadLibraryItemFileQueryOptions(workspaceId,kind,key,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type uninstallLibraryItemResponse204 = {
  data: void
  status: 204
}

export type uninstallLibraryItemResponse401 = {
  data: Error
  status: 401
}

export type uninstallLibraryItemResponse403 = {
  data: Error
  status: 403
}

export type uninstallLibraryItemResponse404 = {
  data: Error
  status: 404
}

export type uninstallLibraryItemResponse422 = {
  data: Error
  status: 422
}

export type uninstallLibraryItemResponse503 = {
  data: Error
  status: 503
}

export type uninstallLibraryItemResponseSuccess = (uninstallLibraryItemResponse204) & {
  headers: Headers;
};
export type uninstallLibraryItemResponseError = (uninstallLibraryItemResponse401 | uninstallLibraryItemResponse403 | uninstallLibraryItemResponse404 | uninstallLibraryItemResponse422 | uninstallLibraryItemResponse503) & {
  headers: Headers;
};

export type uninstallLibraryItemResponse = (uninstallLibraryItemResponseSuccess | uninstallLibraryItemResponseError)

export const getUninstallLibraryItemUrl = (workspaceId: string,
    kind: LibraryItemKind,
    key: string,) => {




  return `/api/workspaces/${workspaceId}/library/items/${kind}/${key}`
}

/**
 * Take an installed agent or skill back out; the catalog says so afterwards.
 * @summary Uninstall Library Item
 */
export const uninstallLibraryItem = async (workspaceId: string,
    kind: LibraryItemKind,
    key: string, options?: RequestInit): Promise<uninstallLibraryItemResponse> => {

  return customFetch<uninstallLibraryItemResponse>(getUninstallLibraryItemUrl(workspaceId,kind,key),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getUninstallLibraryItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uninstallLibraryItem>>, TError,{workspaceId: string;kind: LibraryItemKind;key: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uninstallLibraryItem>>, TError,{workspaceId: string;kind: LibraryItemKind;key: string}, TContext> => {

const mutationKey = ['uninstallLibraryItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uninstallLibraryItem>>, {workspaceId: string;kind: LibraryItemKind;key: string}> = (props) => {
          const {workspaceId,kind,key} = props ?? {};

          return  uninstallLibraryItem(workspaceId,kind,key,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UninstallLibraryItemMutationResult = NonNullable<Awaited<ReturnType<typeof uninstallLibraryItem>>>

    export type UninstallLibraryItemMutationError = Error

    /**
 * @summary Uninstall Library Item
 */
export const useUninstallLibraryItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uninstallLibraryItem>>, TError,{workspaceId: string;kind: LibraryItemKind;key: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof uninstallLibraryItem>>,
        TError,
        {workspaceId: string;kind: LibraryItemKind;key: string},
        TContext
      > => {
      return useMutation(getUninstallLibraryItemMutationOptions(options), queryClient);
    }

export type installLibraryItemResponse200 = {
  data: LibraryInstallResult
  status: 200
}

export type installLibraryItemResponse401 = {
  data: Error
  status: 401
}

export type installLibraryItemResponse403 = {
  data: Error
  status: 403
}

export type installLibraryItemResponse404 = {
  data: Error
  status: 404
}

export type installLibraryItemResponse422 = {
  data: ImportRejection
  status: 422
}

export type installLibraryItemResponse503 = {
  data: Error
  status: 503
}

export type installLibraryItemResponseSuccess = (installLibraryItemResponse200) & {
  headers: Headers;
};
export type installLibraryItemResponseError = (installLibraryItemResponse401 | installLibraryItemResponse403 | installLibraryItemResponse404 | installLibraryItemResponse422 | installLibraryItemResponse503) & {
  headers: Headers;
};

export type installLibraryItemResponse = (installLibraryItemResponseSuccess | installLibraryItemResponseError)

export const getInstallLibraryItemUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/library/install`
}

/**
 * Install an item and every dependency the workspace lacks; report each by name.
 * @summary Install Library Item
 */
export const installLibraryItem = async (workspaceId: string,
    libraryInstallRequest: LibraryInstallRequest, options?: RequestInit): Promise<installLibraryItemResponse> => {

  return customFetch<installLibraryItemResponse>(getInstallLibraryItemUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(libraryInstallRequest)
  }
);}




export const getInstallLibraryItemMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof installLibraryItem>>, TError,{workspaceId: string;data: LibraryInstallRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof installLibraryItem>>, TError,{workspaceId: string;data: LibraryInstallRequest}, TContext> => {

const mutationKey = ['installLibraryItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof installLibraryItem>>, {workspaceId: string;data: LibraryInstallRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  installLibraryItem(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InstallLibraryItemMutationResult = NonNullable<Awaited<ReturnType<typeof installLibraryItem>>>
    export type InstallLibraryItemMutationBody = LibraryInstallRequest
    export type InstallLibraryItemMutationError = Error | ImportRejection

    /**
 * @summary Install Library Item
 */
export const useInstallLibraryItem = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof installLibraryItem>>, TError,{workspaceId: string;data: LibraryInstallRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof installLibraryItem>>,
        TError,
        {workspaceId: string;data: LibraryInstallRequest},
        TContext
      > => {
      return useMutation(getInstallLibraryItemMutationOptions(options), queryClient);
    }

export type updateLibraryItemResponse200 = {
  data: LibraryInstallResult
  status: 200
}

export type updateLibraryItemResponse401 = {
  data: Error
  status: 401
}

export type updateLibraryItemResponse403 = {
  data: Error
  status: 403
}

export type updateLibraryItemResponse404 = {
  data: Error
  status: 404
}

export type updateLibraryItemResponse422 = {
  data: ImportRejection
  status: 422
}

export type updateLibraryItemResponse503 = {
  data: Error
  status: 503
}

export type updateLibraryItemResponseSuccess = (updateLibraryItemResponse200) & {
  headers: Headers;
};
export type updateLibraryItemResponseError = (updateLibraryItemResponse401 | updateLibraryItemResponse403 | updateLibraryItemResponse404 | updateLibraryItemResponse422 | updateLibraryItemResponse503) & {
  headers: Headers;
};

export type updateLibraryItemResponse = (updateLibraryItemResponseSuccess | updateLibraryItemResponseError)

export const getUpdateLibraryItemUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/library/update`
}

/**
 * Replace an unmodified workspace copy of an agent or skill with the library's.
 * @summary Update Library Item
 */
export const updateLibraryItem = async (workspaceId: string,
    libraryUpdateRequest: LibraryUpdateRequest, options?: RequestInit): Promise<updateLibraryItemResponse> => {

  return customFetch<updateLibraryItemResponse>(getUpdateLibraryItemUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(libraryUpdateRequest)
  }
);}




export const getUpdateLibraryItemMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateLibraryItem>>, TError,{workspaceId: string;data: LibraryUpdateRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateLibraryItem>>, TError,{workspaceId: string;data: LibraryUpdateRequest}, TContext> => {

const mutationKey = ['updateLibraryItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateLibraryItem>>, {workspaceId: string;data: LibraryUpdateRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  updateLibraryItem(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateLibraryItemMutationResult = NonNullable<Awaited<ReturnType<typeof updateLibraryItem>>>
    export type UpdateLibraryItemMutationBody = LibraryUpdateRequest
    export type UpdateLibraryItemMutationError = Error | ImportRejection

    /**
 * @summary Update Library Item
 */
export const useUpdateLibraryItem = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateLibraryItem>>, TError,{workspaceId: string;data: LibraryUpdateRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateLibraryItem>>,
        TError,
        {workspaceId: string;data: LibraryUpdateRequest},
        TContext
      > => {
      return useMutation(getUpdateLibraryItemMutationOptions(options), queryClient);
    }

export type previewWorkflowBundleResponse200 = {
  data: BundlePreview
  status: 200
}

export type previewWorkflowBundleResponse401 = {
  data: Error
  status: 401
}

export type previewWorkflowBundleResponse403 = {
  data: Error
  status: 403
}

export type previewWorkflowBundleResponse404 = {
  data: Error
  status: 404
}

export type previewWorkflowBundleResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type previewWorkflowBundleResponseSuccess = (previewWorkflowBundleResponse200) & {
  headers: Headers;
};
export type previewWorkflowBundleResponseError = (previewWorkflowBundleResponse401 | previewWorkflowBundleResponse403 | previewWorkflowBundleResponse404 | previewWorkflowBundleResponse422) & {
  headers: Headers;
};

export type previewWorkflowBundleResponse = (previewWorkflowBundleResponseSuccess | previewWorkflowBundleResponseError)

export const getPreviewWorkflowBundleUrl = (workspaceId: string,
    workflowId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/${workflowId}/bundle/preview`
}

/**
 * What exporting this workflow would carry, and what stops it (US4).
 * @summary Preview Workflow Bundle
 */
export const previewWorkflowBundle = async (workspaceId: string,
    workflowId: string, options?: RequestInit): Promise<previewWorkflowBundleResponse> => {

  return customFetch<previewWorkflowBundleResponse>(getPreviewWorkflowBundleUrl(workspaceId,workflowId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPreviewWorkflowBundleQueryKey = (workspaceId: string,
    workflowId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/workflows/${workflowId}/bundle/preview`
    ] as const;
    }


export const getPreviewWorkflowBundleQueryOptions = <TData = Awaited<ReturnType<typeof previewWorkflowBundle>>, TError = Error | HTTPValidationError>(workspaceId: string,
    workflowId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof previewWorkflowBundle>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPreviewWorkflowBundleQueryKey(workspaceId,workflowId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof previewWorkflowBundle>>> = ({ signal }) => previewWorkflowBundle(workspaceId,workflowId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && workflowId !== null && workflowId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof previewWorkflowBundle>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PreviewWorkflowBundleQueryResult = NonNullable<Awaited<ReturnType<typeof previewWorkflowBundle>>>
export type PreviewWorkflowBundleQueryError = Error | HTTPValidationError


export function usePreviewWorkflowBundle<TData = Awaited<ReturnType<typeof previewWorkflowBundle>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    workflowId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof previewWorkflowBundle>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewWorkflowBundle>>,
          TError,
          Awaited<ReturnType<typeof previewWorkflowBundle>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePreviewWorkflowBundle<TData = Awaited<ReturnType<typeof previewWorkflowBundle>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    workflowId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof previewWorkflowBundle>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewWorkflowBundle>>,
          TError,
          Awaited<ReturnType<typeof previewWorkflowBundle>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePreviewWorkflowBundle<TData = Awaited<ReturnType<typeof previewWorkflowBundle>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    workflowId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof previewWorkflowBundle>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Preview Workflow Bundle
 */

export function usePreviewWorkflowBundle<TData = Awaited<ReturnType<typeof previewWorkflowBundle>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    workflowId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof previewWorkflowBundle>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPreviewWorkflowBundleQueryOptions(workspaceId,workflowId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type exportWorkflowBundleResponse200 = {
  data: Blob
  status: 200
}

export type exportWorkflowBundleResponse401 = {
  data: Error
  status: 401
}

export type exportWorkflowBundleResponse403 = {
  data: Error
  status: 403
}

export type exportWorkflowBundleResponse404 = {
  data: Error
  status: 404
}

export type exportWorkflowBundleResponse409 = {
  data: Error
  status: 409
}

export type exportWorkflowBundleResponse413 = {
  data: Error
  status: 413
}

export type exportWorkflowBundleResponse422 = {
  data: Error
  status: 422
}

export type exportWorkflowBundleResponseSuccess = (exportWorkflowBundleResponse200) & {
  headers: Headers;
};
export type exportWorkflowBundleResponseError = (exportWorkflowBundleResponse401 | exportWorkflowBundleResponse403 | exportWorkflowBundleResponse404 | exportWorkflowBundleResponse409 | exportWorkflowBundleResponse413 | exportWorkflowBundleResponse422) & {
  headers: Headers;
};

export type exportWorkflowBundleResponse = (exportWorkflowBundleResponseSuccess | exportWorkflowBundleResponseError)

export const getExportWorkflowBundleUrl = (workspaceId: string,
    workflowId: string,
    params?: ExportWorkflowBundleParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workspaces/${workspaceId}/workflows/${workflowId}/bundle?${stringifiedParams}` : `/api/workspaces/${workspaceId}/workflows/${workflowId}/bundle`
}

/**
 * The workflow, every workflow it links to, and the chosen agents and skills (US1–US3).
 * @summary Export Workflow Bundle
 */
export const exportWorkflowBundle = async (workspaceId: string,
    workflowId: string,
    params?: ExportWorkflowBundleParams, options?: RequestInit): Promise<exportWorkflowBundleResponse> => {

  return customFetch<exportWorkflowBundleResponse>(getExportWorkflowBundleUrl(workspaceId,workflowId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportWorkflowBundleQueryKey = (workspaceId: string,
    workflowId: string,
    params?: ExportWorkflowBundleParams,) => {
    return [
    `/api/workspaces/${workspaceId}/workflows/${workflowId}/bundle`, ...(params ? [params] : [])
    ] as const;
    }


export const getExportWorkflowBundleQueryOptions = <TData = Awaited<ReturnType<typeof exportWorkflowBundle>>, TError = Error>(workspaceId: string,
    workflowId: string,
    params?: ExportWorkflowBundleParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowBundle>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportWorkflowBundleQueryKey(workspaceId,workflowId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportWorkflowBundle>>> = ({ signal }) => exportWorkflowBundle(workspaceId,workflowId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && workflowId !== null && workflowId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowBundle>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ExportWorkflowBundleQueryResult = NonNullable<Awaited<ReturnType<typeof exportWorkflowBundle>>>
export type ExportWorkflowBundleQueryError = Error


export function useExportWorkflowBundle<TData = Awaited<ReturnType<typeof exportWorkflowBundle>>, TError = Error>(
 workspaceId: string,
    workflowId: string,
    params: undefined |  ExportWorkflowBundleParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowBundle>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportWorkflowBundle>>,
          TError,
          Awaited<ReturnType<typeof exportWorkflowBundle>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportWorkflowBundle<TData = Awaited<ReturnType<typeof exportWorkflowBundle>>, TError = Error>(
 workspaceId: string,
    workflowId: string,
    params?: ExportWorkflowBundleParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowBundle>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportWorkflowBundle>>,
          TError,
          Awaited<ReturnType<typeof exportWorkflowBundle>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportWorkflowBundle<TData = Awaited<ReturnType<typeof exportWorkflowBundle>>, TError = Error>(
 workspaceId: string,
    workflowId: string,
    params?: ExportWorkflowBundleParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowBundle>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Export Workflow Bundle
 */

export function useExportWorkflowBundle<TData = Awaited<ReturnType<typeof exportWorkflowBundle>>, TError = Error>(
 workspaceId: string,
    workflowId: string,
    params?: ExportWorkflowBundleParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowBundle>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getExportWorkflowBundleQueryOptions(workspaceId,workflowId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type preflightBundleImportResponse200 = {
  data: BundleImportPreview
  status: 200
}

export type preflightBundleImportResponse401 = {
  data: Error
  status: 401
}

export type preflightBundleImportResponse403 = {
  data: Error
  status: 403
}

export type preflightBundleImportResponse404 = {
  data: Error
  status: 404
}

export type preflightBundleImportResponse413 = {
  data: Error
  status: 413
}

export type preflightBundleImportResponse422 = {
  data: ImportRejection
  status: 422
}

export type preflightBundleImportResponseSuccess = (preflightBundleImportResponse200) & {
  headers: Headers;
};
export type preflightBundleImportResponseError = (preflightBundleImportResponse401 | preflightBundleImportResponse403 | preflightBundleImportResponse404 | preflightBundleImportResponse413 | preflightBundleImportResponse422) & {
  headers: Headers;
};

export type preflightBundleImportResponse = (preflightBundleImportResponseSuccess | preflightBundleImportResponseError)

export const getPreflightBundleImportUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/bundles/preflight`
}

/**
 * What importing this bundle here would do, with these answers (US3). Writes nothing.
 * @summary Preflight Bundle Import
 */
export const preflightBundleImport = async (workspaceId: string,
    bodyPreflightBundleImport: BodyPreflightBundleImport, options?: RequestInit): Promise<preflightBundleImportResponse> => {
    const formData = new FormData();
formData.append(`file`, bodyPreflightBundleImport.file);
if(bodyPreflightBundleImport.answers !== undefined && bodyPreflightBundleImport.answers !== null) {
 formData.append(`answers`, bodyPreflightBundleImport.answers);
 }

  return customFetch<preflightBundleImportResponse>(getPreflightBundleImportUrl(workspaceId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}




export const getPreflightBundleImportMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof preflightBundleImport>>, TError,{workspaceId: string;data: BodyPreflightBundleImport}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof preflightBundleImport>>, TError,{workspaceId: string;data: BodyPreflightBundleImport}, TContext> => {

const mutationKey = ['preflightBundleImport'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof preflightBundleImport>>, {workspaceId: string;data: BodyPreflightBundleImport}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  preflightBundleImport(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreflightBundleImportMutationResult = NonNullable<Awaited<ReturnType<typeof preflightBundleImport>>>
    export type PreflightBundleImportMutationBody = BodyPreflightBundleImport
    export type PreflightBundleImportMutationError = Error | ImportRejection

    /**
 * @summary Preflight Bundle Import
 */
export const usePreflightBundleImport = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof preflightBundleImport>>, TError,{workspaceId: string;data: BodyPreflightBundleImport}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof preflightBundleImport>>,
        TError,
        {workspaceId: string;data: BodyPreflightBundleImport},
        TContext
      > => {
      return useMutation(getPreflightBundleImportMutationOptions(options), queryClient);
    }

export type importBundleResponse200 = {
  data: BundleImportResult
  status: 200
}

export type importBundleResponse401 = {
  data: Error
  status: 401
}

export type importBundleResponse403 = {
  data: Error
  status: 403
}

export type importBundleResponse404 = {
  data: Error
  status: 404
}

export type importBundleResponse413 = {
  data: Error
  status: 413
}

export type importBundleResponse422 = {
  data: ImportRejection
  status: 422
}

export type importBundleResponseSuccess = (importBundleResponse200) & {
  headers: Headers;
};
export type importBundleResponseError = (importBundleResponse401 | importBundleResponse403 | importBundleResponse404 | importBundleResponse413 | importBundleResponse422) & {
  headers: Headers;
};

export type importBundleResponse = (importBundleResponseSuccess | importBundleResponseError)

export const getImportBundleUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/bundles/import`
}

/**
 * Import every workflow, agent and skill the bundle holds, all or nothing (US1, US2).
 * @summary Import Bundle
 */
export const importBundle = async (workspaceId: string,
    bodyImportBundle: BodyImportBundle, options?: RequestInit): Promise<importBundleResponse> => {
    const formData = new FormData();
formData.append(`file`, bodyImportBundle.file);
if(bodyImportBundle.answers !== undefined && bodyImportBundle.answers !== null) {
 formData.append(`answers`, bodyImportBundle.answers);
 }

  return customFetch<importBundleResponse>(getImportBundleUrl(workspaceId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}




export const getImportBundleMutationOptions = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importBundle>>, TError,{workspaceId: string;data: BodyImportBundle}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof importBundle>>, TError,{workspaceId: string;data: BodyImportBundle}, TContext> => {

const mutationKey = ['importBundle'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importBundle>>, {workspaceId: string;data: BodyImportBundle}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  importBundle(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportBundleMutationResult = NonNullable<Awaited<ReturnType<typeof importBundle>>>
    export type ImportBundleMutationBody = BodyImportBundle
    export type ImportBundleMutationError = Error | ImportRejection

    /**
 * @summary Import Bundle
 */
export const useImportBundle = <TError = Error | ImportRejection,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importBundle>>, TError,{workspaceId: string;data: BodyImportBundle}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importBundle>>,
        TError,
        {workspaceId: string;data: BodyImportBundle},
        TContext
      > => {
      return useMutation(getImportBundleMutationOptions(options), queryClient);
    }

export type previewShareToLibraryResponse200 = {
  data: SharePreview
  status: 200
}

export type previewShareToLibraryResponse401 = {
  data: Error
  status: 401
}

export type previewShareToLibraryResponse403 = {
  data: Error
  status: 403
}

export type previewShareToLibraryResponse404 = {
  data: Error
  status: 404
}

export type previewShareToLibraryResponse409 = {
  data: Error
  status: 409
}

export type previewShareToLibraryResponse422 = {
  data: Error
  status: 422
}

export type previewShareToLibraryResponseSuccess = (previewShareToLibraryResponse200) & {
  headers: Headers;
};
export type previewShareToLibraryResponseError = (previewShareToLibraryResponse401 | previewShareToLibraryResponse403 | previewShareToLibraryResponse404 | previewShareToLibraryResponse409 | previewShareToLibraryResponse422) & {
  headers: Headers;
};

export type previewShareToLibraryResponse = (previewShareToLibraryResponseSuccess | previewShareToLibraryResponseError)

export const getPreviewShareToLibraryUrl = (workspaceId: string,
    workflowId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/${workflowId}/share/preview`
}

/**
 * What sharing this workflow would add, update or refuse (US4). Writes nothing.
 * @summary Preview Share To Library
 */
export const previewShareToLibrary = async (workspaceId: string,
    workflowId: string,
    shareRequest: ShareRequest, options?: RequestInit): Promise<previewShareToLibraryResponse> => {

  return customFetch<previewShareToLibraryResponse>(getPreviewShareToLibraryUrl(workspaceId,workflowId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(shareRequest)
  }
);}




export const getPreviewShareToLibraryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewShareToLibrary>>, TError,{workspaceId: string;workflowId: string;data: ShareRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewShareToLibrary>>, TError,{workspaceId: string;workflowId: string;data: ShareRequest}, TContext> => {

const mutationKey = ['previewShareToLibrary'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewShareToLibrary>>, {workspaceId: string;workflowId: string;data: ShareRequest}> = (props) => {
          const {workspaceId,workflowId,data} = props ?? {};

          return  previewShareToLibrary(workspaceId,workflowId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewShareToLibraryMutationResult = NonNullable<Awaited<ReturnType<typeof previewShareToLibrary>>>
    export type PreviewShareToLibraryMutationBody = ShareRequest
    export type PreviewShareToLibraryMutationError = Error

    /**
 * @summary Preview Share To Library
 */
export const usePreviewShareToLibrary = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewShareToLibrary>>, TError,{workspaceId: string;workflowId: string;data: ShareRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof previewShareToLibrary>>,
        TError,
        {workspaceId: string;workflowId: string;data: ShareRequest},
        TContext
      > => {
      return useMutation(getPreviewShareToLibraryMutationOptions(options), queryClient);
    }

export type shareToLibraryResponse200 = {
  data: ShareResult
  status: 200
}

export type shareToLibraryResponse401 = {
  data: Error
  status: 401
}

export type shareToLibraryResponse403 = {
  data: Error
  status: 403
}

export type shareToLibraryResponse404 = {
  data: Error
  status: 404
}

export type shareToLibraryResponse409 = {
  data: Error
  status: 409
}

export type shareToLibraryResponse422 = {
  data: Error
  status: 422
}

export type shareToLibraryResponseSuccess = (shareToLibraryResponse200) & {
  headers: Headers;
};
export type shareToLibraryResponseError = (shareToLibraryResponse401 | shareToLibraryResponse403 | shareToLibraryResponse404 | shareToLibraryResponse409 | shareToLibraryResponse422) & {
  headers: Headers;
};

export type shareToLibraryResponse = (shareToLibraryResponseSuccess | shareToLibraryResponseError)

export const getShareToLibraryUrl = (workspaceId: string,
    workflowId: string,) => {




  return `/api/workspaces/${workspaceId}/workflows/${workflowId}/share`
}

/**
 * Put the workflow, its linked workflows and the chosen parts in the Library (US1, US2).
 * @summary Share To Library
 */
export const shareToLibrary = async (workspaceId: string,
    workflowId: string,
    shareRequest: ShareRequest, options?: RequestInit): Promise<shareToLibraryResponse> => {

  return customFetch<shareToLibraryResponse>(getShareToLibraryUrl(workspaceId,workflowId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(shareRequest)
  }
);}




export const getShareToLibraryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof shareToLibrary>>, TError,{workspaceId: string;workflowId: string;data: ShareRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof shareToLibrary>>, TError,{workspaceId: string;workflowId: string;data: ShareRequest}, TContext> => {

const mutationKey = ['shareToLibrary'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof shareToLibrary>>, {workspaceId: string;workflowId: string;data: ShareRequest}> = (props) => {
          const {workspaceId,workflowId,data} = props ?? {};

          return  shareToLibrary(workspaceId,workflowId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ShareToLibraryMutationResult = NonNullable<Awaited<ReturnType<typeof shareToLibrary>>>
    export type ShareToLibraryMutationBody = ShareRequest
    export type ShareToLibraryMutationError = Error

    /**
 * @summary Share To Library
 */
export const useShareToLibrary = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof shareToLibrary>>, TError,{workspaceId: string;workflowId: string;data: ShareRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof shareToLibrary>>,
        TError,
        {workspaceId: string;workflowId: string;data: ShareRequest},
        TContext
      > => {
      return useMutation(getShareToLibraryMutationOptions(options), queryClient);
    }

export type takeDownSharedItemResponse204 = {
  data: void
  status: 204
}

export type takeDownSharedItemResponse401 = {
  data: Error
  status: 401
}

export type takeDownSharedItemResponse403 = {
  data: Error
  status: 403
}

export type takeDownSharedItemResponse404 = {
  data: Error
  status: 404
}

export type takeDownSharedItemResponse409 = {
  data: Error
  status: 409
}

export type takeDownSharedItemResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type takeDownSharedItemResponseSuccess = (takeDownSharedItemResponse204) & {
  headers: Headers;
};
export type takeDownSharedItemResponseError = (takeDownSharedItemResponse401 | takeDownSharedItemResponse403 | takeDownSharedItemResponse404 | takeDownSharedItemResponse409 | takeDownSharedItemResponse422) & {
  headers: Headers;
};

export type takeDownSharedItemResponse = (takeDownSharedItemResponseSuccess | takeDownSharedItemResponseError)

export const getTakeDownSharedItemUrl = (workspaceId: string,
    kind: LibraryItemKind,
    key: string,) => {




  return `/api/workspaces/${workspaceId}/library/shared/${kind}/${key}`
}

/**
 * Take a shared item out of the Library (US3): its author, or an org admin.
 * @summary Take Down Shared Item
 */
export const takeDownSharedItem = async (workspaceId: string,
    kind: LibraryItemKind,
    key: string, options?: RequestInit): Promise<takeDownSharedItemResponse> => {

  return customFetch<takeDownSharedItemResponse>(getTakeDownSharedItemUrl(workspaceId,kind,key),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getTakeDownSharedItemMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof takeDownSharedItem>>, TError,{workspaceId: string;kind: LibraryItemKind;key: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof takeDownSharedItem>>, TError,{workspaceId: string;kind: LibraryItemKind;key: string}, TContext> => {

const mutationKey = ['takeDownSharedItem'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof takeDownSharedItem>>, {workspaceId: string;kind: LibraryItemKind;key: string}> = (props) => {
          const {workspaceId,kind,key} = props ?? {};

          return  takeDownSharedItem(workspaceId,kind,key,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TakeDownSharedItemMutationResult = NonNullable<Awaited<ReturnType<typeof takeDownSharedItem>>>

    export type TakeDownSharedItemMutationError = Error | HTTPValidationError

    /**
 * @summary Take Down Shared Item
 */
export const useTakeDownSharedItem = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof takeDownSharedItem>>, TError,{workspaceId: string;kind: LibraryItemKind;key: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof takeDownSharedItem>>,
        TError,
        {workspaceId: string;kind: LibraryItemKind;key: string},
        TContext
      > => {
      return useMutation(getTakeDownSharedItemMutationOptions(options), queryClient);
    }

export type previewTakeDownSetResponse200 = {
  data: TakeDownPreview
  status: 200
}

export type previewTakeDownSetResponse401 = {
  data: Error
  status: 401
}

export type previewTakeDownSetResponse403 = {
  data: Error
  status: 403
}

export type previewTakeDownSetResponse404 = {
  data: Error
  status: 404
}

export type previewTakeDownSetResponse409 = {
  data: Error
  status: 409
}

export type previewTakeDownSetResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type previewTakeDownSetResponseSuccess = (previewTakeDownSetResponse200) & {
  headers: Headers;
};
export type previewTakeDownSetResponseError = (previewTakeDownSetResponse401 | previewTakeDownSetResponse403 | previewTakeDownSetResponse404 | previewTakeDownSetResponse409 | previewTakeDownSetResponse422) & {
  headers: Headers;
};

export type previewTakeDownSetResponse = (previewTakeDownSetResponseSuccess | previewTakeDownSetResponseError)

export const getPreviewTakeDownSetUrl = (workspaceId: string,
    kind: LibraryItemKind,
    key: string,) => {




  return `/api/workspaces/${workspaceId}/library/shared/${kind}/${key}/take-down`
}

/**
 * Which linked pieces the same person shared would go with the item (spec 167).
 * @summary Preview Take Down Set
 */
export const previewTakeDownSet = async (workspaceId: string,
    kind: LibraryItemKind,
    key: string, options?: RequestInit): Promise<previewTakeDownSetResponse> => {

  return customFetch<previewTakeDownSetResponse>(getPreviewTakeDownSetUrl(workspaceId,kind,key),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPreviewTakeDownSetQueryKey = (workspaceId: string,
    kind: LibraryItemKind,
    key: string,) => {
    return [
    `/api/workspaces/${workspaceId}/library/shared/${kind}/${key}/take-down`
    ] as const;
    }


export const getPreviewTakeDownSetQueryOptions = <TData = Awaited<ReturnType<typeof previewTakeDownSet>>, TError = Error | HTTPValidationError>(workspaceId: string,
    kind: LibraryItemKind,
    key: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTakeDownSet>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPreviewTakeDownSetQueryKey(workspaceId,kind,key);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof previewTakeDownSet>>> = ({ signal }) => previewTakeDownSet(workspaceId,kind,key, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && kind !== null && kind !== undefined && key !== null && key !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof previewTakeDownSet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type PreviewTakeDownSetQueryResult = NonNullable<Awaited<ReturnType<typeof previewTakeDownSet>>>
export type PreviewTakeDownSetQueryError = Error | HTTPValidationError


export function usePreviewTakeDownSet<TData = Awaited<ReturnType<typeof previewTakeDownSet>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    kind: LibraryItemKind,
    key: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTakeDownSet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewTakeDownSet>>,
          TError,
          Awaited<ReturnType<typeof previewTakeDownSet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePreviewTakeDownSet<TData = Awaited<ReturnType<typeof previewTakeDownSet>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    kind: LibraryItemKind,
    key: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTakeDownSet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewTakeDownSet>>,
          TError,
          Awaited<ReturnType<typeof previewTakeDownSet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function usePreviewTakeDownSet<TData = Awaited<ReturnType<typeof previewTakeDownSet>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    kind: LibraryItemKind,
    key: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTakeDownSet>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Preview Take Down Set
 */

export function usePreviewTakeDownSet<TData = Awaited<ReturnType<typeof previewTakeDownSet>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    kind: LibraryItemKind,
    key: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof previewTakeDownSet>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getPreviewTakeDownSetQueryOptions(workspaceId,kind,key,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type takeDownSetResponse200 = {
  data: TakeDownResult
  status: 200
}

export type takeDownSetResponse401 = {
  data: Error
  status: 401
}

export type takeDownSetResponse403 = {
  data: Error
  status: 403
}

export type takeDownSetResponse404 = {
  data: Error
  status: 404
}

export type takeDownSetResponse409 = {
  data: Error
  status: 409
}

export type takeDownSetResponse422 = {
  data: Error
  status: 422
}

export type takeDownSetResponseSuccess = (takeDownSetResponse200) & {
  headers: Headers;
};
export type takeDownSetResponseError = (takeDownSetResponse401 | takeDownSetResponse403 | takeDownSetResponse404 | takeDownSetResponse409 | takeDownSetResponse422) & {
  headers: Headers;
};

export type takeDownSetResponse = (takeDownSetResponseSuccess | takeDownSetResponseError)

export const getTakeDownSetUrl = (workspaceId: string,
    kind: LibraryItemKind,
    key: string,) => {




  return `/api/workspaces/${workspaceId}/library/shared/${kind}/${key}/take-down`
}

/**
 * Take the item down with the chosen linked pieces, the item last (spec 167).
 * @summary Take Down Set
 */
export const takeDownSet = async (workspaceId: string,
    kind: LibraryItemKind,
    key: string,
    takeDownSetRequest: TakeDownSetRequest, options?: RequestInit): Promise<takeDownSetResponse> => {

  return customFetch<takeDownSetResponse>(getTakeDownSetUrl(workspaceId,kind,key),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(takeDownSetRequest)
  }
);}




export const getTakeDownSetMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof takeDownSet>>, TError,{workspaceId: string;kind: LibraryItemKind;key: string;data: TakeDownSetRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof takeDownSet>>, TError,{workspaceId: string;kind: LibraryItemKind;key: string;data: TakeDownSetRequest}, TContext> => {

const mutationKey = ['takeDownSet'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof takeDownSet>>, {workspaceId: string;kind: LibraryItemKind;key: string;data: TakeDownSetRequest}> = (props) => {
          const {workspaceId,kind,key,data} = props ?? {};

          return  takeDownSet(workspaceId,kind,key,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TakeDownSetMutationResult = NonNullable<Awaited<ReturnType<typeof takeDownSet>>>
    export type TakeDownSetMutationBody = TakeDownSetRequest
    export type TakeDownSetMutationError = Error

    /**
 * @summary Take Down Set
 */
export const useTakeDownSet = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof takeDownSet>>, TError,{workspaceId: string;kind: LibraryItemKind;key: string;data: TakeDownSetRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof takeDownSet>>,
        TError,
        {workspaceId: string;kind: LibraryItemKind;key: string;data: TakeDownSetRequest},
        TContext
      > => {
      return useMutation(getTakeDownSetMutationOptions(options), queryClient);
    }

export type listNotificationsResponse200 = {
  data: NotificationPage
  status: 200
}

export type listNotificationsResponse401 = {
  data: Error
  status: 401
}

export type listNotificationsResponse404 = {
  data: Error
  status: 404
}

export type listNotificationsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listNotificationsResponseSuccess = (listNotificationsResponse200) & {
  headers: Headers;
};
export type listNotificationsResponseError = (listNotificationsResponse401 | listNotificationsResponse404 | listNotificationsResponse422) & {
  headers: Headers;
};

export type listNotificationsResponse = (listNotificationsResponseSuccess | listNotificationsResponseError)

export const getListNotificationsUrl = (params?: ListNotificationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/notifications?${stringifiedParams}` : `/api/notifications`
}

/**
 * A page of the signed-in person's notifications, newest first.
 *
 * Unknown `kind` values and a `created_to` before `created_from` are refused (422).
 * @summary List Notifications
 */
export const listNotifications = async (params?: ListNotificationsParams, options?: RequestInit): Promise<listNotificationsResponse> => {

  return customFetch<listNotificationsResponse>(getListNotificationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListNotificationsQueryKey = (params?: ListNotificationsParams,) => {
    return [
    `/api/notifications`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNotificationsQueryOptions = <TData = Awaited<ReturnType<typeof listNotifications>>, TError = Error | HTTPValidationError>(params?: ListNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNotificationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNotifications>>> = ({ signal }) => listNotifications(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNotificationsQueryResult = NonNullable<Awaited<ReturnType<typeof listNotifications>>>
export type ListNotificationsQueryError = Error | HTTPValidationError


export function useListNotifications<TData = Awaited<ReturnType<typeof listNotifications>>, TError = Error | HTTPValidationError>(
 params: undefined |  ListNotificationsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNotifications>>,
          TError,
          Awaited<ReturnType<typeof listNotifications>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNotifications<TData = Awaited<ReturnType<typeof listNotifications>>, TError = Error | HTTPValidationError>(
 params?: ListNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNotifications>>,
          TError,
          Awaited<ReturnType<typeof listNotifications>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNotifications<TData = Awaited<ReturnType<typeof listNotifications>>, TError = Error | HTTPValidationError>(
 params?: ListNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Notifications
 */

export function useListNotifications<TData = Awaited<ReturnType<typeof listNotifications>>, TError = Error | HTTPValidationError>(
 params?: ListNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNotificationsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readUnreadNotificationCountResponse200 = {
  data: UnreadCount
  status: 200
}

export type readUnreadNotificationCountResponse401 = {
  data: Error
  status: 401
}

export type readUnreadNotificationCountResponseSuccess = (readUnreadNotificationCountResponse200) & {
  headers: Headers;
};
export type readUnreadNotificationCountResponseError = (readUnreadNotificationCountResponse401) & {
  headers: Headers;
};

export type readUnreadNotificationCountResponse = (readUnreadNotificationCountResponseSuccess | readUnreadNotificationCountResponseError)

export const getReadUnreadNotificationCountUrl = () => {




  return `/api/notifications/unread-count`
}

/**
 * @summary Read Unread Notification Count
 */
export const readUnreadNotificationCount = async ( options?: RequestInit): Promise<readUnreadNotificationCountResponse> => {

  return customFetch<readUnreadNotificationCountResponse>(getReadUnreadNotificationCountUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadUnreadNotificationCountQueryKey = () => {
    return [
    `/api/notifications/unread-count`
    ] as const;
    }


export const getReadUnreadNotificationCountQueryOptions = <TData = Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadUnreadNotificationCountQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readUnreadNotificationCount>>> = ({ signal }) => readUnreadNotificationCount({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadUnreadNotificationCountQueryResult = NonNullable<Awaited<ReturnType<typeof readUnreadNotificationCount>>>
export type ReadUnreadNotificationCountQueryError = Error


export function useReadUnreadNotificationCount<TData = Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readUnreadNotificationCount>>,
          TError,
          Awaited<ReturnType<typeof readUnreadNotificationCount>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadUnreadNotificationCount<TData = Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readUnreadNotificationCount>>,
          TError,
          Awaited<ReturnType<typeof readUnreadNotificationCount>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadUnreadNotificationCount<TData = Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Unread Notification Count
 */

export function useReadUnreadNotificationCount<TData = Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readUnreadNotificationCount>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadUnreadNotificationCountQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type countNotificationsResponse200 = {
  data: NotificationCount
  status: 200
}

export type countNotificationsResponse401 = {
  data: Error
  status: 401
}

export type countNotificationsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type countNotificationsResponseSuccess = (countNotificationsResponse200) & {
  headers: Headers;
};
export type countNotificationsResponseError = (countNotificationsResponse401 | countNotificationsResponse422) & {
  headers: Headers;
};

export type countNotificationsResponse = (countNotificationsResponseSuccess | countNotificationsResponseError)

export const getCountNotificationsUrl = (params?: CountNotificationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/notifications/count?${stringifiedParams}` : `/api/notifications/count`
}

/**
 * How many notifications the list returns, across all its pages, for the same
 * filters (spec 130). Before `/{notification_id}`, which would read it as an id.
 * @summary Count Notifications
 */
export const countNotifications = async (params?: CountNotificationsParams, options?: RequestInit): Promise<countNotificationsResponse> => {

  return customFetch<countNotificationsResponse>(getCountNotificationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getCountNotificationsQueryKey = (params?: CountNotificationsParams,) => {
    return [
    `/api/notifications/count`, ...(params ? [params] : [])
    ] as const;
    }


export const getCountNotificationsQueryOptions = <TData = Awaited<ReturnType<typeof countNotifications>>, TError = Error | HTTPValidationError>(params?: CountNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof countNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getCountNotificationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof countNotifications>>> = ({ signal }) => countNotifications(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof countNotifications>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type CountNotificationsQueryResult = NonNullable<Awaited<ReturnType<typeof countNotifications>>>
export type CountNotificationsQueryError = Error | HTTPValidationError


export function useCountNotifications<TData = Awaited<ReturnType<typeof countNotifications>>, TError = Error | HTTPValidationError>(
 params: undefined |  CountNotificationsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof countNotifications>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof countNotifications>>,
          TError,
          Awaited<ReturnType<typeof countNotifications>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useCountNotifications<TData = Awaited<ReturnType<typeof countNotifications>>, TError = Error | HTTPValidationError>(
 params?: CountNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof countNotifications>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof countNotifications>>,
          TError,
          Awaited<ReturnType<typeof countNotifications>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useCountNotifications<TData = Awaited<ReturnType<typeof countNotifications>>, TError = Error | HTTPValidationError>(
 params?: CountNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof countNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Count Notifications
 */

export function useCountNotifications<TData = Awaited<ReturnType<typeof countNotifications>>, TError = Error | HTTPValidationError>(
 params?: CountNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof countNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getCountNotificationsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type readNotificationPreferencesResponse200 = {
  data: NotificationPreferences
  status: 200
}

export type readNotificationPreferencesResponse401 = {
  data: Error
  status: 401
}

export type readNotificationPreferencesResponseSuccess = (readNotificationPreferencesResponse200) & {
  headers: Headers;
};
export type readNotificationPreferencesResponseError = (readNotificationPreferencesResponse401) & {
  headers: Headers;
};

export type readNotificationPreferencesResponse = (readNotificationPreferencesResponseSuccess | readNotificationPreferencesResponseError)

export const getReadNotificationPreferencesUrl = () => {




  return `/api/notifications/preferences`
}

/**
 * The signed-in person's matrix, defaults marked, and their muted workspaces.
 * @summary Read Notification Preferences
 */
export const readNotificationPreferences = async ( options?: RequestInit): Promise<readNotificationPreferencesResponse> => {

  return customFetch<readNotificationPreferencesResponse>(getReadNotificationPreferencesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadNotificationPreferencesQueryKey = () => {
    return [
    `/api/notifications/preferences`
    ] as const;
    }


export const getReadNotificationPreferencesQueryOptions = <TData = Awaited<ReturnType<typeof readNotificationPreferences>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotificationPreferences>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadNotificationPreferencesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readNotificationPreferences>>> = ({ signal }) => readNotificationPreferences({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readNotificationPreferences>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadNotificationPreferencesQueryResult = NonNullable<Awaited<ReturnType<typeof readNotificationPreferences>>>
export type ReadNotificationPreferencesQueryError = Error


export function useReadNotificationPreferences<TData = Awaited<ReturnType<typeof readNotificationPreferences>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotificationPreferences>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readNotificationPreferences>>,
          TError,
          Awaited<ReturnType<typeof readNotificationPreferences>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadNotificationPreferences<TData = Awaited<ReturnType<typeof readNotificationPreferences>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotificationPreferences>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readNotificationPreferences>>,
          TError,
          Awaited<ReturnType<typeof readNotificationPreferences>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadNotificationPreferences<TData = Awaited<ReturnType<typeof readNotificationPreferences>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotificationPreferences>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Notification Preferences
 */

export function useReadNotificationPreferences<TData = Awaited<ReturnType<typeof readNotificationPreferences>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotificationPreferences>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadNotificationPreferencesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type setNotificationPreferenceResponse200 = {
  data: NotificationPreferences
  status: 200
}

export type setNotificationPreferenceResponse401 = {
  data: Error
  status: 401
}

export type setNotificationPreferenceResponse422 = {
  data: Error
  status: 422
}

export type setNotificationPreferenceResponseSuccess = (setNotificationPreferenceResponse200) & {
  headers: Headers;
};
export type setNotificationPreferenceResponseError = (setNotificationPreferenceResponse401 | setNotificationPreferenceResponse422) & {
  headers: Headers;
};

export type setNotificationPreferenceResponse = (setNotificationPreferenceResponseSuccess | setNotificationPreferenceResponseError)

export const getSetNotificationPreferenceUrl = (group: NotificationGroup,
    channel: NotificationChannel,) => {




  return `/api/notifications/preferences/${group}/${channel}`
}

/**
 * Change one cell. In-app "Needs you" cannot be turned off: `NEEDS_YOU_IN_APP_REQUIRED`.
 * @summary Set Notification Preference
 */
export const setNotificationPreference = async (group: NotificationGroup,
    channel: NotificationChannel,
    preferenceChange: PreferenceChange, options?: RequestInit): Promise<setNotificationPreferenceResponse> => {

  return customFetch<setNotificationPreferenceResponse>(getSetNotificationPreferenceUrl(group,channel),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(preferenceChange)
  }
);}




export const getSetNotificationPreferenceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setNotificationPreference>>, TError,{group: NotificationGroup;channel: NotificationChannel;data: PreferenceChange}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setNotificationPreference>>, TError,{group: NotificationGroup;channel: NotificationChannel;data: PreferenceChange}, TContext> => {

const mutationKey = ['setNotificationPreference'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setNotificationPreference>>, {group: NotificationGroup;channel: NotificationChannel;data: PreferenceChange}> = (props) => {
          const {group,channel,data} = props ?? {};

          return  setNotificationPreference(group,channel,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetNotificationPreferenceMutationResult = NonNullable<Awaited<ReturnType<typeof setNotificationPreference>>>
    export type SetNotificationPreferenceMutationBody = PreferenceChange
    export type SetNotificationPreferenceMutationError = Error

    /**
 * @summary Set Notification Preference
 */
export const useSetNotificationPreference = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setNotificationPreference>>, TError,{group: NotificationGroup;channel: NotificationChannel;data: PreferenceChange}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setNotificationPreference>>,
        TError,
        {group: NotificationGroup;channel: NotificationChannel;data: PreferenceChange},
        TContext
      > => {
      return useMutation(getSetNotificationPreferenceMutationOptions(options), queryClient);
    }

export type muteWorkspaceNotificationsResponse200 = {
  data: NotificationPreferences
  status: 200
}

export type muteWorkspaceNotificationsResponse401 = {
  data: Error
  status: 401
}

export type muteWorkspaceNotificationsResponse404 = {
  data: Error
  status: 404
}

export type muteWorkspaceNotificationsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type muteWorkspaceNotificationsResponseSuccess = (muteWorkspaceNotificationsResponse200) & {
  headers: Headers;
};
export type muteWorkspaceNotificationsResponseError = (muteWorkspaceNotificationsResponse401 | muteWorkspaceNotificationsResponse404 | muteWorkspaceNotificationsResponse422) & {
  headers: Headers;
};

export type muteWorkspaceNotificationsResponse = (muteWorkspaceNotificationsResponseSuccess | muteWorkspaceNotificationsResponseError)

export const getMuteWorkspaceNotificationsUrl = (workspaceId: string,) => {




  return `/api/notifications/mutes/${workspaceId}`
}

/**
 * Nothing from this workspace reaches the person but in-app "Needs you". Idempotent.
 * @summary Mute Workspace Notifications
 */
export const muteWorkspaceNotifications = async (workspaceId: string, options?: RequestInit): Promise<muteWorkspaceNotificationsResponse> => {

  return customFetch<muteWorkspaceNotificationsResponse>(getMuteWorkspaceNotificationsUrl(workspaceId),
  {
    ...options,
    method: 'PUT'


  }
);}




export const getMuteWorkspaceNotificationsMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof muteWorkspaceNotifications>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof muteWorkspaceNotifications>>, TError,{workspaceId: string}, TContext> => {

const mutationKey = ['muteWorkspaceNotifications'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof muteWorkspaceNotifications>>, {workspaceId: string}> = (props) => {
          const {workspaceId} = props ?? {};

          return  muteWorkspaceNotifications(workspaceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MuteWorkspaceNotificationsMutationResult = NonNullable<Awaited<ReturnType<typeof muteWorkspaceNotifications>>>

    export type MuteWorkspaceNotificationsMutationError = Error | HTTPValidationError

    /**
 * @summary Mute Workspace Notifications
 */
export const useMuteWorkspaceNotifications = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof muteWorkspaceNotifications>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof muteWorkspaceNotifications>>,
        TError,
        {workspaceId: string},
        TContext
      > => {
      return useMutation(getMuteWorkspaceNotificationsMutationOptions(options), queryClient);
    }

export type unmuteWorkspaceNotificationsResponse200 = {
  data: NotificationPreferences
  status: 200
}

export type unmuteWorkspaceNotificationsResponse401 = {
  data: Error
  status: 401
}

export type unmuteWorkspaceNotificationsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type unmuteWorkspaceNotificationsResponseSuccess = (unmuteWorkspaceNotificationsResponse200) & {
  headers: Headers;
};
export type unmuteWorkspaceNotificationsResponseError = (unmuteWorkspaceNotificationsResponse401 | unmuteWorkspaceNotificationsResponse422) & {
  headers: Headers;
};

export type unmuteWorkspaceNotificationsResponse = (unmuteWorkspaceNotificationsResponseSuccess | unmuteWorkspaceNotificationsResponseError)

export const getUnmuteWorkspaceNotificationsUrl = (workspaceId: string,) => {




  return `/api/notifications/mutes/${workspaceId}`
}

/**
 * Idempotent: unmuting a workspace that is not muted changes nothing.
 * @summary Unmute Workspace Notifications
 */
export const unmuteWorkspaceNotifications = async (workspaceId: string, options?: RequestInit): Promise<unmuteWorkspaceNotificationsResponse> => {

  return customFetch<unmuteWorkspaceNotificationsResponse>(getUnmuteWorkspaceNotificationsUrl(workspaceId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getUnmuteWorkspaceNotificationsMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unmuteWorkspaceNotifications>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unmuteWorkspaceNotifications>>, TError,{workspaceId: string}, TContext> => {

const mutationKey = ['unmuteWorkspaceNotifications'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unmuteWorkspaceNotifications>>, {workspaceId: string}> = (props) => {
          const {workspaceId} = props ?? {};

          return  unmuteWorkspaceNotifications(workspaceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnmuteWorkspaceNotificationsMutationResult = NonNullable<Awaited<ReturnType<typeof unmuteWorkspaceNotifications>>>

    export type UnmuteWorkspaceNotificationsMutationError = Error | HTTPValidationError

    /**
 * @summary Unmute Workspace Notifications
 */
export const useUnmuteWorkspaceNotifications = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unmuteWorkspaceNotifications>>, TError,{workspaceId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof unmuteWorkspaceNotifications>>,
        TError,
        {workspaceId: string},
        TContext
      > => {
      return useMutation(getUnmuteWorkspaceNotificationsMutationOptions(options), queryClient);
    }

export type setNotificationQuietHoursResponse200 = {
  data: NotificationPreferences
  status: 200
}

export type setNotificationQuietHoursResponse401 = {
  data: Error
  status: 401
}

export type setNotificationQuietHoursResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type setNotificationQuietHoursResponseSuccess = (setNotificationQuietHoursResponse200) & {
  headers: Headers;
};
export type setNotificationQuietHoursResponseError = (setNotificationQuietHoursResponse401 | setNotificationQuietHoursResponse422) & {
  headers: Headers;
};

export type setNotificationQuietHoursResponse = (setNotificationQuietHoursResponseSuccess | setNotificationQuietHoursResponseError)

export const getSetNotificationQuietHoursUrl = () => {




  return `/api/notifications/quiet-hours`
}

/**
 * Set a daily window with no alerts and no bundled email (spec 121 FR-005).
 *
 * `HH:MM` in the person's own time zone; `ends` before `starts` crosses midnight.
 * `starts` equal to `ends` is refused with 422. Critical notifications still alert
 * and are still emailed inside it.
 * @summary Set Notification Quiet Hours
 */
export const setNotificationQuietHours = async (quietHoursChange: QuietHoursChange, options?: RequestInit): Promise<setNotificationQuietHoursResponse> => {

  return customFetch<setNotificationQuietHoursResponse>(getSetNotificationQuietHoursUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(quietHoursChange)
  }
);}




export const getSetNotificationQuietHoursMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setNotificationQuietHours>>, TError,{data: QuietHoursChange}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setNotificationQuietHours>>, TError,{data: QuietHoursChange}, TContext> => {

const mutationKey = ['setNotificationQuietHours'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setNotificationQuietHours>>, {data: QuietHoursChange}> = (props) => {
          const {data} = props ?? {};

          return  setNotificationQuietHours(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetNotificationQuietHoursMutationResult = NonNullable<Awaited<ReturnType<typeof setNotificationQuietHours>>>
    export type SetNotificationQuietHoursMutationBody = QuietHoursChange
    export type SetNotificationQuietHoursMutationError = Error | HTTPValidationError

    /**
 * @summary Set Notification Quiet Hours
 */
export const useSetNotificationQuietHours = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setNotificationQuietHours>>, TError,{data: QuietHoursChange}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setNotificationQuietHours>>,
        TError,
        {data: QuietHoursChange},
        TContext
      > => {
      return useMutation(getSetNotificationQuietHoursMutationOptions(options), queryClient);
    }

export type clearNotificationQuietHoursResponse200 = {
  data: NotificationPreferences
  status: 200
}

export type clearNotificationQuietHoursResponse401 = {
  data: Error
  status: 401
}

export type clearNotificationQuietHoursResponseSuccess = (clearNotificationQuietHoursResponse200) & {
  headers: Headers;
};
export type clearNotificationQuietHoursResponseError = (clearNotificationQuietHoursResponse401) & {
  headers: Headers;
};

export type clearNotificationQuietHoursResponse = (clearNotificationQuietHoursResponseSuccess | clearNotificationQuietHoursResponseError)

export const getClearNotificationQuietHoursUrl = () => {




  return `/api/notifications/quiet-hours`
}

/**
 * Quiet hours off. Idempotent.
 * @summary Clear Notification Quiet Hours
 */
export const clearNotificationQuietHours = async ( options?: RequestInit): Promise<clearNotificationQuietHoursResponse> => {

  return customFetch<clearNotificationQuietHoursResponse>(getClearNotificationQuietHoursUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getClearNotificationQuietHoursMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearNotificationQuietHours>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearNotificationQuietHours>>, TError,void, TContext> => {

const mutationKey = ['clearNotificationQuietHours'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearNotificationQuietHours>>, void> = () => {


          return  clearNotificationQuietHours(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearNotificationQuietHoursMutationResult = NonNullable<Awaited<ReturnType<typeof clearNotificationQuietHours>>>

    export type ClearNotificationQuietHoursMutationError = Error

    /**
 * @summary Clear Notification Quiet Hours
 */
export const useClearNotificationQuietHours = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearNotificationQuietHours>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof clearNotificationQuietHours>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getClearNotificationQuietHoursMutationOptions(options), queryClient);
    }

export type streamNotificationsResponse200 = {
  data: string
  status: 200
}

export type streamNotificationsResponse401 = {
  data: Error
  status: 401
}

export type streamNotificationsResponse503 = {
  data: Error
  status: 503
}

export type streamNotificationsResponseSuccess = (streamNotificationsResponse200) & {
  headers: Headers;
};
export type streamNotificationsResponseError = (streamNotificationsResponse401 | streamNotificationsResponse503) & {
  headers: Headers;
};

export type streamNotificationsResponse = (streamNotificationsResponseSuccess | streamNotificationsResponseError)

export const getStreamNotificationsUrl = () => {




  return `/api/notifications/stream`
}

/**
 * The signed-in person's push channel (spec 116 US5, spec 117); a workspace's
 * changes only when they may read it (spec 158).
 * @summary Stream Notifications
 */
export const streamNotifications = async ( options?: RequestInit): Promise<streamNotificationsResponse> => {

  return customFetch<streamNotificationsResponse>(getStreamNotificationsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getStreamNotificationsQueryKey = () => {
    return [
    `/api/notifications/stream`
    ] as const;
    }


export const getStreamNotificationsQueryOptions = <TData = Awaited<ReturnType<typeof streamNotifications>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof streamNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getStreamNotificationsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof streamNotifications>>> = ({ signal }) => streamNotifications({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof streamNotifications>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type StreamNotificationsQueryResult = NonNullable<Awaited<ReturnType<typeof streamNotifications>>>
export type StreamNotificationsQueryError = Error


export function useStreamNotifications<TData = Awaited<ReturnType<typeof streamNotifications>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof streamNotifications>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof streamNotifications>>,
          TError,
          Awaited<ReturnType<typeof streamNotifications>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useStreamNotifications<TData = Awaited<ReturnType<typeof streamNotifications>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof streamNotifications>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof streamNotifications>>,
          TError,
          Awaited<ReturnType<typeof streamNotifications>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useStreamNotifications<TData = Awaited<ReturnType<typeof streamNotifications>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof streamNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Stream Notifications
 */

export function useStreamNotifications<TData = Awaited<ReturnType<typeof streamNotifications>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof streamNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getStreamNotificationsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type markAllNotificationsReadResponse200 = {
  data: InboxChange
  status: 200
}

export type markAllNotificationsReadResponse401 = {
  data: Error
  status: 401
}

export type markAllNotificationsReadResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type markAllNotificationsReadResponseSuccess = (markAllNotificationsReadResponse200) & {
  headers: Headers;
};
export type markAllNotificationsReadResponseError = (markAllNotificationsReadResponse401 | markAllNotificationsReadResponse422) & {
  headers: Headers;
};

export type markAllNotificationsReadResponse = (markAllNotificationsReadResponseSuccess | markAllNotificationsReadResponseError)

export const getMarkAllNotificationsReadUrl = () => {




  return `/api/notifications/read-all`
}

/**
 * Mark read everything the filter matches — the list's filters, as a JSON body
 * (spec 130). Without a body, every notification, archived ones too, as before.
 * `changed` counts those that were unread; `unread` is the count after.
 * @summary Mark All Notifications Read
 */
export const markAllNotificationsRead = async (notificationFilterNull?: NotificationFilter | null, options?: RequestInit): Promise<markAllNotificationsReadResponse> => {

  return customFetch<markAllNotificationsReadResponse>(getMarkAllNotificationsReadUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(notificationFilterNull)
  }
);}




export const getMarkAllNotificationsReadMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markAllNotificationsRead>>, TError,{data?: NotificationFilter | null}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof markAllNotificationsRead>>, TError,{data?: NotificationFilter | null}, TContext> => {

const mutationKey = ['markAllNotificationsRead'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markAllNotificationsRead>>, {data?: NotificationFilter | null}> = (props) => {
          const {data} = props ?? {};

          return  markAllNotificationsRead(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MarkAllNotificationsReadMutationResult = NonNullable<Awaited<ReturnType<typeof markAllNotificationsRead>>>
    export type MarkAllNotificationsReadMutationBody = NotificationFilter | null | undefined
    export type MarkAllNotificationsReadMutationError = Error | HTTPValidationError

    /**
 * @summary Mark All Notifications Read
 */
export const useMarkAllNotificationsRead = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markAllNotificationsRead>>, TError,{data?: NotificationFilter | null}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof markAllNotificationsRead>>,
        TError,
        {data?: NotificationFilter | null},
        TContext
      > => {
      return useMutation(getMarkAllNotificationsReadMutationOptions(options), queryClient);
    }

export type archiveAllNotificationsResponse200 = {
  data: InboxChange
  status: 200
}

export type archiveAllNotificationsResponse401 = {
  data: Error
  status: 401
}

export type archiveAllNotificationsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type archiveAllNotificationsResponseSuccess = (archiveAllNotificationsResponse200) & {
  headers: Headers;
};
export type archiveAllNotificationsResponseError = (archiveAllNotificationsResponse401 | archiveAllNotificationsResponse422) & {
  headers: Headers;
};

export type archiveAllNotificationsResponse = (archiveAllNotificationsResponseSuccess | archiveAllNotificationsResponseError)

export const getArchiveAllNotificationsUrl = () => {




  return `/api/notifications/archive-all`
}

/**
 * Archive everything the filter matches — the list's filters, as a JSON body; `{}`
 * is the default list (spec 130). Archiving also reads. `changed` counts those not
 * already archived and read.
 *
 * Unlike `read-all`, the body is required: archiving everything should be asked for
 * deliberately, with `{}`, not fall out of an empty request. `read-all` takes no
 * body only because it did before spec 130 and its callers still send none.
 * @summary Archive All Notifications
 */
export const archiveAllNotifications = async (notificationFilter: NotificationFilter, options?: RequestInit): Promise<archiveAllNotificationsResponse> => {

  return customFetch<archiveAllNotificationsResponse>(getArchiveAllNotificationsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(notificationFilter)
  }
);}




export const getArchiveAllNotificationsMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveAllNotifications>>, TError,{data: NotificationFilter}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof archiveAllNotifications>>, TError,{data: NotificationFilter}, TContext> => {

const mutationKey = ['archiveAllNotifications'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof archiveAllNotifications>>, {data: NotificationFilter}> = (props) => {
          const {data} = props ?? {};

          return  archiveAllNotifications(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ArchiveAllNotificationsMutationResult = NonNullable<Awaited<ReturnType<typeof archiveAllNotifications>>>
    export type ArchiveAllNotificationsMutationBody = NotificationFilter
    export type ArchiveAllNotificationsMutationError = Error | HTTPValidationError

    /**
 * @summary Archive All Notifications
 */
export const useArchiveAllNotifications = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveAllNotifications>>, TError,{data: NotificationFilter}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof archiveAllNotifications>>,
        TError,
        {data: NotificationFilter},
        TContext
      > => {
      return useMutation(getArchiveAllNotificationsMutationOptions(options), queryClient);
    }

export type changeNotificationsResponse200 = {
  data: InboxChange
  status: 200
}

export type changeNotificationsResponse401 = {
  data: Error
  status: 401
}

export type changeNotificationsResponse422 = {
  data: Error
  status: 422
}

export type changeNotificationsResponseSuccess = (changeNotificationsResponse200) & {
  headers: Headers;
};
export type changeNotificationsResponseError = (changeNotificationsResponse401 | changeNotificationsResponse422) & {
  headers: Headers;
};

export type changeNotificationsResponse = (changeNotificationsResponseSuccess | changeNotificationsResponseError)

export const getChangeNotificationsUrl = () => {




  return `/api/notifications/bulk`
}

/**
 * Mark chosen notifications read or unread, or archive or unarchive them (spec 130).
 *
 * At most 200 ids. Ids that are not the signed-in person's are skipped, not refused;
 * `changed` counts only those not already in the asked-for state. Archiving also
 * reads; unarchiving keeps the read state.
 *
 * `changed` is deliberately one opaque number: an id that is someone else's counts
 * exactly like one that does not exist, so the answer never tells whether another
 * person's notification exists.
 * @summary Change Notifications
 */
export const changeNotifications = async (notificationBulkChange: NotificationBulkChange, options?: RequestInit): Promise<changeNotificationsResponse> => {

  return customFetch<changeNotificationsResponse>(getChangeNotificationsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(notificationBulkChange)
  }
);}




export const getChangeNotificationsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changeNotifications>>, TError,{data: NotificationBulkChange}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof changeNotifications>>, TError,{data: NotificationBulkChange}, TContext> => {

const mutationKey = ['changeNotifications'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof changeNotifications>>, {data: NotificationBulkChange}> = (props) => {
          const {data} = props ?? {};

          return  changeNotifications(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ChangeNotificationsMutationResult = NonNullable<Awaited<ReturnType<typeof changeNotifications>>>
    export type ChangeNotificationsMutationBody = NotificationBulkChange
    export type ChangeNotificationsMutationError = Error

    /**
 * @summary Change Notifications
 */
export const useChangeNotifications = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changeNotifications>>, TError,{data: NotificationBulkChange}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof changeNotifications>>,
        TError,
        {data: NotificationBulkChange},
        TContext
      > => {
      return useMutation(getChangeNotificationsMutationOptions(options), queryClient);
    }

export type markTaskNotificationsReadResponse200 = {
  data: UnreadCount
  status: 200
}

export type markTaskNotificationsReadResponse401 = {
  data: Error
  status: 401
}

export type markTaskNotificationsReadResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type markTaskNotificationsReadResponseSuccess = (markTaskNotificationsReadResponse200) & {
  headers: Headers;
};
export type markTaskNotificationsReadResponseError = (markTaskNotificationsReadResponse401 | markTaskNotificationsReadResponse422) & {
  headers: Headers;
};

export type markTaskNotificationsReadResponse = (markTaskNotificationsReadResponseSuccess | markTaskNotificationsReadResponseError)

export const getMarkTaskNotificationsReadUrl = (taskId: string,) => {




  return `/api/notifications/tasks/${taskId}/read`
}

/**
 * @summary Mark Task Notifications Read
 */
export const markTaskNotificationsRead = async (taskId: string, options?: RequestInit): Promise<markTaskNotificationsReadResponse> => {

  return customFetch<markTaskNotificationsReadResponse>(getMarkTaskNotificationsReadUrl(taskId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getMarkTaskNotificationsReadMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markTaskNotificationsRead>>, TError,{taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof markTaskNotificationsRead>>, TError,{taskId: string}, TContext> => {

const mutationKey = ['markTaskNotificationsRead'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markTaskNotificationsRead>>, {taskId: string}> = (props) => {
          const {taskId} = props ?? {};

          return  markTaskNotificationsRead(taskId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MarkTaskNotificationsReadMutationResult = NonNullable<Awaited<ReturnType<typeof markTaskNotificationsRead>>>

    export type MarkTaskNotificationsReadMutationError = Error | HTTPValidationError

    /**
 * @summary Mark Task Notifications Read
 */
export const useMarkTaskNotificationsRead = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markTaskNotificationsRead>>, TError,{taskId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof markTaskNotificationsRead>>,
        TError,
        {taskId: string},
        TContext
      > => {
      return useMutation(getMarkTaskNotificationsReadMutationOptions(options), queryClient);
    }

export type readNotificationResponse200 = {
  data: NotificationView
  status: 200
}

export type readNotificationResponse401 = {
  data: Error
  status: 401
}

export type readNotificationResponse404 = {
  data: Error
  status: 404
}

export type readNotificationResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type readNotificationResponseSuccess = (readNotificationResponse200) & {
  headers: Headers;
};
export type readNotificationResponseError = (readNotificationResponse401 | readNotificationResponse404 | readNotificationResponse422) & {
  headers: Headers;
};

export type readNotificationResponse = (readNotificationResponseSuccess | readNotificationResponseError)

export const getReadNotificationUrl = (notificationId: string,) => {




  return `/api/notifications/${notificationId}`
}

/**
 * @summary Read Notification
 */
export const readNotification = async (notificationId: string, options?: RequestInit): Promise<readNotificationResponse> => {

  return customFetch<readNotificationResponse>(getReadNotificationUrl(notificationId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadNotificationQueryKey = (notificationId: string,) => {
    return [
    `/api/notifications/${notificationId}`
    ] as const;
    }


export const getReadNotificationQueryOptions = <TData = Awaited<ReturnType<typeof readNotification>>, TError = Error | HTTPValidationError>(notificationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotification>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadNotificationQueryKey(notificationId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readNotification>>> = ({ signal }) => readNotification(notificationId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: notificationId !== null && notificationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readNotification>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadNotificationQueryResult = NonNullable<Awaited<ReturnType<typeof readNotification>>>
export type ReadNotificationQueryError = Error | HTTPValidationError


export function useReadNotification<TData = Awaited<ReturnType<typeof readNotification>>, TError = Error | HTTPValidationError>(
 notificationId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotification>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readNotification>>,
          TError,
          Awaited<ReturnType<typeof readNotification>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadNotification<TData = Awaited<ReturnType<typeof readNotification>>, TError = Error | HTTPValidationError>(
 notificationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotification>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readNotification>>,
          TError,
          Awaited<ReturnType<typeof readNotification>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadNotification<TData = Awaited<ReturnType<typeof readNotification>>, TError = Error | HTTPValidationError>(
 notificationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotification>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Notification
 */

export function useReadNotification<TData = Awaited<ReturnType<typeof readNotification>>, TError = Error | HTTPValidationError>(
 notificationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotification>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadNotificationQueryOptions(notificationId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type markNotificationReadResponse200 = {
  data: NotificationView
  status: 200
}

export type markNotificationReadResponse401 = {
  data: Error
  status: 401
}

export type markNotificationReadResponse404 = {
  data: Error
  status: 404
}

export type markNotificationReadResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type markNotificationReadResponseSuccess = (markNotificationReadResponse200) & {
  headers: Headers;
};
export type markNotificationReadResponseError = (markNotificationReadResponse401 | markNotificationReadResponse404 | markNotificationReadResponse422) & {
  headers: Headers;
};

export type markNotificationReadResponse = (markNotificationReadResponseSuccess | markNotificationReadResponseError)

export const getMarkNotificationReadUrl = (notificationId: string,) => {




  return `/api/notifications/${notificationId}/read`
}

/**
 * @summary Mark Notification Read
 */
export const markNotificationRead = async (notificationId: string, options?: RequestInit): Promise<markNotificationReadResponse> => {

  return customFetch<markNotificationReadResponse>(getMarkNotificationReadUrl(notificationId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getMarkNotificationReadMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markNotificationRead>>, TError,{notificationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof markNotificationRead>>, TError,{notificationId: string}, TContext> => {

const mutationKey = ['markNotificationRead'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markNotificationRead>>, {notificationId: string}> = (props) => {
          const {notificationId} = props ?? {};

          return  markNotificationRead(notificationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MarkNotificationReadMutationResult = NonNullable<Awaited<ReturnType<typeof markNotificationRead>>>

    export type MarkNotificationReadMutationError = Error | HTTPValidationError

    /**
 * @summary Mark Notification Read
 */
export const useMarkNotificationRead = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markNotificationRead>>, TError,{notificationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof markNotificationRead>>,
        TError,
        {notificationId: string},
        TContext
      > => {
      return useMutation(getMarkNotificationReadMutationOptions(options), queryClient);
    }

export type markNotificationUnreadResponse200 = {
  data: NotificationView
  status: 200
}

export type markNotificationUnreadResponse401 = {
  data: Error
  status: 401
}

export type markNotificationUnreadResponse404 = {
  data: Error
  status: 404
}

export type markNotificationUnreadResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type markNotificationUnreadResponseSuccess = (markNotificationUnreadResponse200) & {
  headers: Headers;
};
export type markNotificationUnreadResponseError = (markNotificationUnreadResponse401 | markNotificationUnreadResponse404 | markNotificationUnreadResponse422) & {
  headers: Headers;
};

export type markNotificationUnreadResponse = (markNotificationUnreadResponseSuccess | markNotificationUnreadResponseError)

export const getMarkNotificationUnreadUrl = (notificationId: string,) => {




  return `/api/notifications/${notificationId}/unread`
}

/**
 * Unread again (spec 120 FR-010). Never emailed a second time.
 * @summary Mark Notification Unread
 */
export const markNotificationUnread = async (notificationId: string, options?: RequestInit): Promise<markNotificationUnreadResponse> => {

  return customFetch<markNotificationUnreadResponse>(getMarkNotificationUnreadUrl(notificationId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getMarkNotificationUnreadMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markNotificationUnread>>, TError,{notificationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof markNotificationUnread>>, TError,{notificationId: string}, TContext> => {

const mutationKey = ['markNotificationUnread'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markNotificationUnread>>, {notificationId: string}> = (props) => {
          const {notificationId} = props ?? {};

          return  markNotificationUnread(notificationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MarkNotificationUnreadMutationResult = NonNullable<Awaited<ReturnType<typeof markNotificationUnread>>>

    export type MarkNotificationUnreadMutationError = Error | HTTPValidationError

    /**
 * @summary Mark Notification Unread
 */
export const useMarkNotificationUnread = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markNotificationUnread>>, TError,{notificationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof markNotificationUnread>>,
        TError,
        {notificationId: string},
        TContext
      > => {
      return useMutation(getMarkNotificationUnreadMutationOptions(options), queryClient);
    }

export type acknowledgeNotificationResponse200 = {
  data: NotificationView
  status: 200
}

export type acknowledgeNotificationResponse401 = {
  data: Error
  status: 401
}

export type acknowledgeNotificationResponse404 = {
  data: Error
  status: 404
}

export type acknowledgeNotificationResponse422 = {
  data: Error
  status: 422
}

export type acknowledgeNotificationResponseSuccess = (acknowledgeNotificationResponse200) & {
  headers: Headers;
};
export type acknowledgeNotificationResponseError = (acknowledgeNotificationResponse401 | acknowledgeNotificationResponse404 | acknowledgeNotificationResponse422) & {
  headers: Headers;
};

export type acknowledgeNotificationResponse = (acknowledgeNotificationResponseSuccess | acknowledgeNotificationResponseError)

export const getAcknowledgeNotificationUrl = (notificationId: string,) => {




  return `/api/notifications/${notificationId}/acknowledge`
}

/**
 * "I'm on it" (spec 123 US1): ownership, not resolution. Reads it, and stops its
 * cause from escalating. Only an open Action-required or Critical notification:
 * otherwise `NOT_ACKNOWLEDGEABLE` (422). Idempotent: acknowledging one already
 * acknowledged answers 200 with the notification as it is — `acknowledged_at` keeps
 * its first value — even once it has since been resolved.
 * @summary Acknowledge Notification
 */
export const acknowledgeNotification = async (notificationId: string, options?: RequestInit): Promise<acknowledgeNotificationResponse> => {

  return customFetch<acknowledgeNotificationResponse>(getAcknowledgeNotificationUrl(notificationId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getAcknowledgeNotificationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acknowledgeNotification>>, TError,{notificationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof acknowledgeNotification>>, TError,{notificationId: string}, TContext> => {

const mutationKey = ['acknowledgeNotification'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof acknowledgeNotification>>, {notificationId: string}> = (props) => {
          const {notificationId} = props ?? {};

          return  acknowledgeNotification(notificationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AcknowledgeNotificationMutationResult = NonNullable<Awaited<ReturnType<typeof acknowledgeNotification>>>

    export type AcknowledgeNotificationMutationError = Error

    /**
 * @summary Acknowledge Notification
 */
export const useAcknowledgeNotification = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acknowledgeNotification>>, TError,{notificationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof acknowledgeNotification>>,
        TError,
        {notificationId: string},
        TContext
      > => {
      return useMutation(getAcknowledgeNotificationMutationOptions(options), queryClient);
    }

export type archiveNotificationResponse200 = {
  data: NotificationView
  status: 200
}

export type archiveNotificationResponse401 = {
  data: Error
  status: 401
}

export type archiveNotificationResponse404 = {
  data: Error
  status: 404
}

export type archiveNotificationResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type archiveNotificationResponseSuccess = (archiveNotificationResponse200) & {
  headers: Headers;
};
export type archiveNotificationResponseError = (archiveNotificationResponse401 | archiveNotificationResponse404 | archiveNotificationResponse422) & {
  headers: Headers;
};

export type archiveNotificationResponse = (archiveNotificationResponseSuccess | archiveNotificationResponseError)

export const getArchiveNotificationUrl = (notificationId: string,) => {




  return `/api/notifications/${notificationId}/archive`
}

/**
 * @summary Archive Notification
 */
export const archiveNotification = async (notificationId: string, options?: RequestInit): Promise<archiveNotificationResponse> => {

  return customFetch<archiveNotificationResponse>(getArchiveNotificationUrl(notificationId),
  {
    ...options,
    method: 'POST'


  }
);}




export const getArchiveNotificationMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveNotification>>, TError,{notificationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof archiveNotification>>, TError,{notificationId: string}, TContext> => {

const mutationKey = ['archiveNotification'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof archiveNotification>>, {notificationId: string}> = (props) => {
          const {notificationId} = props ?? {};

          return  archiveNotification(notificationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ArchiveNotificationMutationResult = NonNullable<Awaited<ReturnType<typeof archiveNotification>>>

    export type ArchiveNotificationMutationError = Error | HTTPValidationError

    /**
 * @summary Archive Notification
 */
export const useArchiveNotification = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveNotification>>, TError,{notificationId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof archiveNotification>>,
        TError,
        {notificationId: string},
        TContext
      > => {
      return useMutation(getArchiveNotificationMutationOptions(options), queryClient);
    }

export type readNotificationPolicyResponse200 = {
  data: NotificationPolicy
  status: 200
}

export type readNotificationPolicyResponse401 = {
  data: Error
  status: 401
}

export type readNotificationPolicyResponse403 = {
  data: Error
  status: 403
}

export type readNotificationPolicyResponseSuccess = (readNotificationPolicyResponse200) & {
  headers: Headers;
};
export type readNotificationPolicyResponseError = (readNotificationPolicyResponse401 | readNotificationPolicyResponse403) & {
  headers: Headers;
};

export type readNotificationPolicyResponse = (readNotificationPolicyResponseSuccess | readNotificationPolicyResponseError)

export const getReadNotificationPolicyUrl = () => {




  return `/api/admin/notification-policy`
}

/**
 * The escalation delay and its change history, newest first. Org admins only.
 * @summary Read Notification Policy
 */
export const readNotificationPolicy = async ( options?: RequestInit): Promise<readNotificationPolicyResponse> => {

  return customFetch<readNotificationPolicyResponse>(getReadNotificationPolicyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadNotificationPolicyQueryKey = () => {
    return [
    `/api/admin/notification-policy`
    ] as const;
    }


export const getReadNotificationPolicyQueryOptions = <TData = Awaited<ReturnType<typeof readNotificationPolicy>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotificationPolicy>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadNotificationPolicyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readNotificationPolicy>>> = ({ signal }) => readNotificationPolicy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readNotificationPolicy>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadNotificationPolicyQueryResult = NonNullable<Awaited<ReturnType<typeof readNotificationPolicy>>>
export type ReadNotificationPolicyQueryError = Error


export function useReadNotificationPolicy<TData = Awaited<ReturnType<typeof readNotificationPolicy>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotificationPolicy>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readNotificationPolicy>>,
          TError,
          Awaited<ReturnType<typeof readNotificationPolicy>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadNotificationPolicy<TData = Awaited<ReturnType<typeof readNotificationPolicy>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotificationPolicy>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readNotificationPolicy>>,
          TError,
          Awaited<ReturnType<typeof readNotificationPolicy>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadNotificationPolicy<TData = Awaited<ReturnType<typeof readNotificationPolicy>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotificationPolicy>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Notification Policy
 */

export function useReadNotificationPolicy<TData = Awaited<ReturnType<typeof readNotificationPolicy>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readNotificationPolicy>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadNotificationPolicyQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type setNotificationPolicyResponse200 = {
  data: NotificationPolicy
  status: 200
}

export type setNotificationPolicyResponse401 = {
  data: Error
  status: 401
}

export type setNotificationPolicyResponse403 = {
  data: Error
  status: 403
}

export type setNotificationPolicyResponse422 = {
  data: Error
  status: 422
}

export type setNotificationPolicyResponseSuccess = (setNotificationPolicyResponse200) & {
  headers: Headers;
};
export type setNotificationPolicyResponseError = (setNotificationPolicyResponse401 | setNotificationPolicyResponse403 | setNotificationPolicyResponse422) & {
  headers: Headers;
};

export type setNotificationPolicyResponse = (setNotificationPolicyResponseSuccess | setNotificationPolicyResponseError)

export const getSetNotificationPolicyUrl = () => {




  return `/api/admin/notification-policy`
}

/**
 * Set the escalation delay. Every change is recorded with who made it; setting the
 * value it already has records nothing. Org admins only.
 * @summary Set Notification Policy
 */
export const setNotificationPolicy = async (notificationPolicyChange: NotificationPolicyChange, options?: RequestInit): Promise<setNotificationPolicyResponse> => {

  return customFetch<setNotificationPolicyResponse>(getSetNotificationPolicyUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(notificationPolicyChange)
  }
);}




export const getSetNotificationPolicyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setNotificationPolicy>>, TError,{data: NotificationPolicyChange}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setNotificationPolicy>>, TError,{data: NotificationPolicyChange}, TContext> => {

const mutationKey = ['setNotificationPolicy'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setNotificationPolicy>>, {data: NotificationPolicyChange}> = (props) => {
          const {data} = props ?? {};

          return  setNotificationPolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetNotificationPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof setNotificationPolicy>>>
    export type SetNotificationPolicyMutationBody = NotificationPolicyChange
    export type SetNotificationPolicyMutationError = Error

    /**
 * @summary Set Notification Policy
 */
export const useSetNotificationPolicy = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setNotificationPolicy>>, TError,{data: NotificationPolicyChange}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setNotificationPolicy>>,
        TError,
        {data: NotificationPolicyChange},
        TContext
      > => {
      return useMutation(getSetNotificationPolicyMutationOptions(options), queryClient);
    }

export type unsubscribeFromEmailResponse200 = {
  data: string
  status: 200
}

export type unsubscribeFromEmailResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type unsubscribeFromEmailResponseSuccess = (unsubscribeFromEmailResponse200) & {
  headers: Headers;
};
export type unsubscribeFromEmailResponseError = (unsubscribeFromEmailResponse422) & {
  headers: Headers;
};

export type unsubscribeFromEmailResponse = (unsubscribeFromEmailResponseSuccess | unsubscribeFromEmailResponseError)

export const getUnsubscribeFromEmailUrl = (params: UnsubscribeFromEmailParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/notifications/email/unsubscribe?${stringifiedParams}` : `/api/notifications/email/unsubscribe`
}

/**
 * The email footer's "Stop these emails" link, opened in a browser (US3).
 * @summary Unsubscribe From Email
 */
export const unsubscribeFromEmail = async (params: UnsubscribeFromEmailParams, options?: RequestInit): Promise<unsubscribeFromEmailResponse> => {

  return customFetch<unsubscribeFromEmailResponse>(getUnsubscribeFromEmailUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getUnsubscribeFromEmailQueryKey = (params?: UnsubscribeFromEmailParams,) => {
    return [
    `/api/notifications/email/unsubscribe`, ...(params ? [params] : [])
    ] as const;
    }


export const getUnsubscribeFromEmailQueryOptions = <TData = Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError = HTTPValidationError>(params: UnsubscribeFromEmailParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getUnsubscribeFromEmailQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof unsubscribeFromEmail>>> = ({ signal }) => unsubscribeFromEmail(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type UnsubscribeFromEmailQueryResult = NonNullable<Awaited<ReturnType<typeof unsubscribeFromEmail>>>
export type UnsubscribeFromEmailQueryError = HTTPValidationError


export function useUnsubscribeFromEmail<TData = Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError = HTTPValidationError>(
 params: UnsubscribeFromEmailParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof unsubscribeFromEmail>>,
          TError,
          Awaited<ReturnType<typeof unsubscribeFromEmail>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useUnsubscribeFromEmail<TData = Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError = HTTPValidationError>(
 params: UnsubscribeFromEmailParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof unsubscribeFromEmail>>,
          TError,
          Awaited<ReturnType<typeof unsubscribeFromEmail>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useUnsubscribeFromEmail<TData = Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError = HTTPValidationError>(
 params: UnsubscribeFromEmailParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Unsubscribe From Email
 */

export function useUnsubscribeFromEmail<TData = Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError = HTTPValidationError>(
 params: UnsubscribeFromEmailParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromEmail>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getUnsubscribeFromEmailQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type unsubscribeFromEmailOneClickResponse200 = {
  data: string
  status: 200
}

export type unsubscribeFromEmailOneClickResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type unsubscribeFromEmailOneClickResponseSuccess = (unsubscribeFromEmailOneClickResponse200) & {
  headers: Headers;
};
export type unsubscribeFromEmailOneClickResponseError = (unsubscribeFromEmailOneClickResponse422) & {
  headers: Headers;
};

export type unsubscribeFromEmailOneClickResponse = (unsubscribeFromEmailOneClickResponseSuccess | unsubscribeFromEmailOneClickResponseError)

export const getUnsubscribeFromEmailOneClickUrl = (params: UnsubscribeFromEmailOneClickParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/notifications/email/unsubscribe?${stringifiedParams}` : `/api/notifications/email/unsubscribe`
}

/**
 * RFC 8058 one-click: what a mail client sends from the ``List-Unsubscribe`` header.
 *
 * The form body (``List-Unsubscribe=One-Click``) carries nothing the token does not.
 * @summary Unsubscribe From Email One Click
 */
export const unsubscribeFromEmailOneClick = async (params: UnsubscribeFromEmailOneClickParams, options?: RequestInit): Promise<unsubscribeFromEmailOneClickResponse> => {

  return customFetch<unsubscribeFromEmailOneClickResponse>(getUnsubscribeFromEmailOneClickUrl(params),
  {
    ...options,
    method: 'POST'


  }
);}




export const getUnsubscribeFromEmailOneClickMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unsubscribeFromEmailOneClick>>, TError,{params: UnsubscribeFromEmailOneClickParams}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unsubscribeFromEmailOneClick>>, TError,{params: UnsubscribeFromEmailOneClickParams}, TContext> => {

const mutationKey = ['unsubscribeFromEmailOneClick'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unsubscribeFromEmailOneClick>>, {params: UnsubscribeFromEmailOneClickParams}> = (props) => {
          const {params} = props ?? {};

          return  unsubscribeFromEmailOneClick(params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnsubscribeFromEmailOneClickMutationResult = NonNullable<Awaited<ReturnType<typeof unsubscribeFromEmailOneClick>>>

    export type UnsubscribeFromEmailOneClickMutationError = HTTPValidationError

    /**
 * @summary Unsubscribe From Email One Click
 */
export const useUnsubscribeFromEmailOneClick = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unsubscribeFromEmailOneClick>>, TError,{params: UnsubscribeFromEmailOneClickParams}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof unsubscribeFromEmailOneClick>>,
        TError,
        {params: UnsubscribeFromEmailOneClickParams},
        TContext
      > => {
      return useMutation(getUnsubscribeFromEmailOneClickMutationOptions(options), queryClient);
    }

export type registerWorkspaceFileChangesResponse201 = {
  data: RegisterChangesResponse
  status: 201
}

export type registerWorkspaceFileChangesResponse403 = {
  data: Error
  status: 403
}

export type registerWorkspaceFileChangesResponse404 = {
  data: Error
  status: 404
}

export type registerWorkspaceFileChangesResponse422 = {
  data: Error
  status: 422
}

export type registerWorkspaceFileChangesResponseSuccess = (registerWorkspaceFileChangesResponse201) & {
  headers: Headers;
};
export type registerWorkspaceFileChangesResponseError = (registerWorkspaceFileChangesResponse403 | registerWorkspaceFileChangesResponse404 | registerWorkspaceFileChangesResponse422) & {
  headers: Headers;
};

export type registerWorkspaceFileChangesResponse = (registerWorkspaceFileChangesResponseSuccess | registerWorkspaceFileChangesResponseError)

export const getRegisterWorkspaceFileChangesUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/files/register`
}

/**
 * @summary Register Workspace File Changes
 */
export const registerWorkspaceFileChanges = async (workspaceId: string,
    registerChangesRequest: RegisterChangesRequest, options?: RequestInit): Promise<registerWorkspaceFileChangesResponse> => {

  return customFetch<registerWorkspaceFileChangesResponse>(getRegisterWorkspaceFileChangesUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(registerChangesRequest)
  }
);}




export const getRegisterWorkspaceFileChangesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof registerWorkspaceFileChanges>>, TError,{workspaceId: string;data: RegisterChangesRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof registerWorkspaceFileChanges>>, TError,{workspaceId: string;data: RegisterChangesRequest}, TContext> => {

const mutationKey = ['registerWorkspaceFileChanges'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof registerWorkspaceFileChanges>>, {workspaceId: string;data: RegisterChangesRequest}> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  registerWorkspaceFileChanges(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegisterWorkspaceFileChangesMutationResult = NonNullable<Awaited<ReturnType<typeof registerWorkspaceFileChanges>>>
    export type RegisterWorkspaceFileChangesMutationBody = RegisterChangesRequest
    export type RegisterWorkspaceFileChangesMutationError = Error

    /**
 * @summary Register Workspace File Changes
 */
export const useRegisterWorkspaceFileChanges = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof registerWorkspaceFileChanges>>, TError,{workspaceId: string;data: RegisterChangesRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof registerWorkspaceFileChanges>>,
        TError,
        {workspaceId: string;data: RegisterChangesRequest},
        TContext
      > => {
      return useMutation(getRegisterWorkspaceFileChangesMutationOptions(options), queryClient);
    }

export type getScheduleContextResponse200 = {
  data: ScheduleContextView
  status: 200
}

export type getScheduleContextResponse403 = {
  data: Error
  status: 403
}

export type getScheduleContextResponse404 = {
  data: Error
  status: 404
}

export type getScheduleContextResponse409 = {
  data: Error
  status: 409
}

export type getScheduleContextResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getScheduleContextResponseSuccess = (getScheduleContextResponse200) & {
  headers: Headers;
};
export type getScheduleContextResponseError = (getScheduleContextResponse403 | getScheduleContextResponse404 | getScheduleContextResponse409 | getScheduleContextResponse422) & {
  headers: Headers;
};

export type getScheduleContextResponse = (getScheduleContextResponseSuccess | getScheduleContextResponseError)

export const getGetScheduleContextUrl = (workspaceId: string,
    taskId: string,
    executionId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/schedule-context`
}

/**
 * What a scheduling decision is told (spec 183): the parent, its comments, this
 * batch's tasks, the trigger and the earlier picks. Nothing from another batch.
 * @summary Get Schedule Context
 */
export const getScheduleContext = async (workspaceId: string,
    taskId: string,
    executionId: string, options?: RequestInit): Promise<getScheduleContextResponse> => {

  return customFetch<getScheduleContextResponse>(getGetScheduleContextUrl(workspaceId,taskId,executionId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetScheduleContextQueryKey = (workspaceId: string,
    taskId: string,
    executionId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/schedule-context`
    ] as const;
    }


export const getGetScheduleContextQueryOptions = <TData = Awaited<ReturnType<typeof getScheduleContext>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getScheduleContext>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetScheduleContextQueryKey(workspaceId,taskId,executionId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getScheduleContext>>> = ({ signal }) => getScheduleContext(workspaceId,taskId,executionId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined && executionId !== null && executionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getScheduleContext>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetScheduleContextQueryResult = NonNullable<Awaited<ReturnType<typeof getScheduleContext>>>
export type GetScheduleContextQueryError = Error | HTTPValidationError


export function useGetScheduleContext<TData = Awaited<ReturnType<typeof getScheduleContext>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getScheduleContext>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getScheduleContext>>,
          TError,
          Awaited<ReturnType<typeof getScheduleContext>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetScheduleContext<TData = Awaited<ReturnType<typeof getScheduleContext>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getScheduleContext>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getScheduleContext>>,
          TError,
          Awaited<ReturnType<typeof getScheduleContext>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetScheduleContext<TData = Awaited<ReturnType<typeof getScheduleContext>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getScheduleContext>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Schedule Context
 */

export function useGetScheduleContext<TData = Awaited<ReturnType<typeof getScheduleContext>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getScheduleContext>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetScheduleContextQueryOptions(workspaceId,taskId,executionId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type postScheduleResultResponse201 = {
  data: ScheduleResultView
  status: 201
}

export type postScheduleResultResponse403 = {
  data: Error
  status: 403
}

export type postScheduleResultResponse404 = {
  data: Error
  status: 404
}

export type postScheduleResultResponse409 = {
  data: Error
  status: 409
}

export type postScheduleResultResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type postScheduleResultResponseSuccess = (postScheduleResultResponse201) & {
  headers: Headers;
};
export type postScheduleResultResponseError = (postScheduleResultResponse403 | postScheduleResultResponse404 | postScheduleResultResponse409 | postScheduleResultResponse422) & {
  headers: Headers;
};

export type postScheduleResultResponse = (postScheduleResultResponseSuccess | postScheduleResultResponseError)

export const getPostScheduleResultUrl = (workspaceId: string,
    taskId: string,
    executionId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/schedule-result`
}

/**
 * A scheduling decision's choice: the tasks of its batch to start now (spec 183).
 *
 * Each valid Backlog task of the batch starts as a person's Start would, with the
 * system as actor; every other id is ignored and reported. Once per decision.
 * @summary Post Schedule Result
 */
export const postScheduleResult = async (workspaceId: string,
    taskId: string,
    executionId: string,
    postScheduleResultRequest: PostScheduleResultRequest, options?: RequestInit): Promise<postScheduleResultResponse> => {

  return customFetch<postScheduleResultResponse>(getPostScheduleResultUrl(workspaceId,taskId,executionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(postScheduleResultRequest)
  }
);}




export const getPostScheduleResultMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postScheduleResult>>, TError,{workspaceId: string;taskId: string;executionId: string;data: PostScheduleResultRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postScheduleResult>>, TError,{workspaceId: string;taskId: string;executionId: string;data: PostScheduleResultRequest}, TContext> => {

const mutationKey = ['postScheduleResult'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postScheduleResult>>, {workspaceId: string;taskId: string;executionId: string;data: PostScheduleResultRequest}> = (props) => {
          const {workspaceId,taskId,executionId,data} = props ?? {};

          return  postScheduleResult(workspaceId,taskId,executionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostScheduleResultMutationResult = NonNullable<Awaited<ReturnType<typeof postScheduleResult>>>
    export type PostScheduleResultMutationBody = PostScheduleResultRequest
    export type PostScheduleResultMutationError = Error | HTTPValidationError

    /**
 * @summary Post Schedule Result
 */
export const usePostScheduleResult = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postScheduleResult>>, TError,{workspaceId: string;taskId: string;executionId: string;data: PostScheduleResultRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postScheduleResult>>,
        TError,
        {workspaceId: string;taskId: string;executionId: string;data: PostScheduleResultRequest},
        TContext
      > => {
      return useMutation(getPostScheduleResultMutationOptions(options), queryClient);
    }

export type listAgentSubtasksResponse200 = {
  data: AgentSubtaskList
  status: 200
}

export type listAgentSubtasksResponse403 = {
  data: Error
  status: 403
}

export type listAgentSubtasksResponse404 = {
  data: Error
  status: 404
}

export type listAgentSubtasksResponse409 = {
  data: Error
  status: 409
}

export type listAgentSubtasksResponse422 = {
  data: Error
  status: 422
}

export type listAgentSubtasksResponseSuccess = (listAgentSubtasksResponse200) & {
  headers: Headers;
};
export type listAgentSubtasksResponseError = (listAgentSubtasksResponse403 | listAgentSubtasksResponse404 | listAgentSubtasksResponse409 | listAgentSubtasksResponse422) & {
  headers: Headers;
};

export type listAgentSubtasksResponse = (listAgentSubtasksResponseSuccess | listAgentSubtasksResponseError)

export const getListAgentSubtasksUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view/children`
}

/**
 * The subtasks the calling run's sub-workflow step created (spec 119 FR-010,
 * spec 132): this step's batch, not an earlier step's.
 * @summary List Agent Subtasks
 */
export const listAgentSubtasks = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<listAgentSubtasksResponse> => {

  return customFetch<listAgentSubtasksResponse>(getListAgentSubtasksUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentSubtasksQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view/children`
    ] as const;
    }


export const getListAgentSubtasksQueryOptions = <TData = Awaited<ReturnType<typeof listAgentSubtasks>>, TError = Error>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAgentSubtasks>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentSubtasksQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentSubtasks>>> = ({ signal }) => listAgentSubtasks(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentSubtasks>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListAgentSubtasksQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentSubtasks>>>
export type ListAgentSubtasksQueryError = Error


export function useListAgentSubtasks<TData = Awaited<ReturnType<typeof listAgentSubtasks>>, TError = Error>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAgentSubtasks>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAgentSubtasks>>,
          TError,
          Awaited<ReturnType<typeof listAgentSubtasks>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAgentSubtasks<TData = Awaited<ReturnType<typeof listAgentSubtasks>>, TError = Error>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAgentSubtasks>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAgentSubtasks>>,
          TError,
          Awaited<ReturnType<typeof listAgentSubtasks>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAgentSubtasks<TData = Awaited<ReturnType<typeof listAgentSubtasks>>, TError = Error>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAgentSubtasks>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Agent Subtasks
 */

export function useListAgentSubtasks<TData = Awaited<ReturnType<typeof listAgentSubtasks>>, TError = Error>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAgentSubtasks>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListAgentSubtasksQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type getAgentSubtaskResponse200 = {
  data: AgentSubtaskDetail
  status: 200
}

export type getAgentSubtaskResponse403 = {
  data: Error
  status: 403
}

export type getAgentSubtaskResponse404 = {
  data: Error
  status: 404
}

export type getAgentSubtaskResponse409 = {
  data: Error
  status: 409
}

export type getAgentSubtaskResponse422 = {
  data: Error
  status: 422
}

export type getAgentSubtaskResponseSuccess = (getAgentSubtaskResponse200) & {
  headers: Headers;
};
export type getAgentSubtaskResponseError = (getAgentSubtaskResponse403 | getAgentSubtaskResponse404 | getAgentSubtaskResponse409 | getAgentSubtaskResponse422) & {
  headers: Headers;
};

export type getAgentSubtaskResponse = (getAgentSubtaskResponseSuccess | getAgentSubtaskResponseError)

export const getGetAgentSubtaskUrl = (workspaceId: string,
    taskId: string,
    childId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view/children/${childId}`
}

/**
 * One subtask of the calling step's batch (spec 132); another step's is 404.
 * @summary Get Agent Subtask
 */
export const getAgentSubtask = async (workspaceId: string,
    taskId: string,
    childId: string, options?: RequestInit): Promise<getAgentSubtaskResponse> => {

  return customFetch<getAgentSubtaskResponse>(getGetAgentSubtaskUrl(workspaceId,taskId,childId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentSubtaskQueryKey = (workspaceId: string,
    taskId: string,
    childId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view/children/${childId}`
    ] as const;
    }


export const getGetAgentSubtaskQueryOptions = <TData = Awaited<ReturnType<typeof getAgentSubtask>>, TError = Error>(workspaceId: string,
    taskId: string,
    childId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAgentSubtask>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentSubtaskQueryKey(workspaceId,taskId,childId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentSubtask>>> = ({ signal }) => getAgentSubtask(workspaceId,taskId,childId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined && childId !== null && childId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentSubtask>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAgentSubtaskQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentSubtask>>>
export type GetAgentSubtaskQueryError = Error


export function useGetAgentSubtask<TData = Awaited<ReturnType<typeof getAgentSubtask>>, TError = Error>(
 workspaceId: string,
    taskId: string,
    childId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAgentSubtask>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAgentSubtask>>,
          TError,
          Awaited<ReturnType<typeof getAgentSubtask>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAgentSubtask<TData = Awaited<ReturnType<typeof getAgentSubtask>>, TError = Error>(
 workspaceId: string,
    taskId: string,
    childId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAgentSubtask>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAgentSubtask>>,
          TError,
          Awaited<ReturnType<typeof getAgentSubtask>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAgentSubtask<TData = Awaited<ReturnType<typeof getAgentSubtask>>, TError = Error>(
 workspaceId: string,
    taskId: string,
    childId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAgentSubtask>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Agent Subtask
 */

export function useGetAgentSubtask<TData = Awaited<ReturnType<typeof getAgentSubtask>>, TError = Error>(
 workspaceId: string,
    taskId: string,
    childId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAgentSubtask>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetAgentSubtaskQueryOptions(workspaceId,taskId,childId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type updateAgentSubtaskResponse200 = {
  data: AgentSubtask
  status: 200
}

export type updateAgentSubtaskResponse403 = {
  data: Error
  status: 403
}

export type updateAgentSubtaskResponse404 = {
  data: Error
  status: 404
}

export type updateAgentSubtaskResponse409 = {
  data: Error
  status: 409
}

export type updateAgentSubtaskResponse422 = {
  data: Error
  status: 422
}

export type updateAgentSubtaskResponseSuccess = (updateAgentSubtaskResponse200) & {
  headers: Headers;
};
export type updateAgentSubtaskResponseError = (updateAgentSubtaskResponse403 | updateAgentSubtaskResponse404 | updateAgentSubtaskResponse409 | updateAgentSubtaskResponse422) & {
  headers: Headers;
};

export type updateAgentSubtaskResponse = (updateAgentSubtaskResponseSuccess | updateAgentSubtaskResponseError)

export const getUpdateAgentSubtaskUrl = (workspaceId: string,
    taskId: string,
    childId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view/children/${childId}`
}

/**
 * Edit a subtask of the calling step's batch (spec 132); another step's is 404.
 * @summary Update Agent Subtask
 */
export const updateAgentSubtask = async (workspaceId: string,
    taskId: string,
    childId: string,
    agentSubtaskUpdate: AgentSubtaskUpdate, options?: RequestInit): Promise<updateAgentSubtaskResponse> => {

  return customFetch<updateAgentSubtaskResponse>(getUpdateAgentSubtaskUrl(workspaceId,taskId,childId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(agentSubtaskUpdate)
  }
);}




export const getUpdateAgentSubtaskMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAgentSubtask>>, TError,{workspaceId: string;taskId: string;childId: string;data: AgentSubtaskUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAgentSubtask>>, TError,{workspaceId: string;taskId: string;childId: string;data: AgentSubtaskUpdate}, TContext> => {

const mutationKey = ['updateAgentSubtask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAgentSubtask>>, {workspaceId: string;taskId: string;childId: string;data: AgentSubtaskUpdate}> = (props) => {
          const {workspaceId,taskId,childId,data} = props ?? {};

          return  updateAgentSubtask(workspaceId,taskId,childId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAgentSubtaskMutationResult = NonNullable<Awaited<ReturnType<typeof updateAgentSubtask>>>
    export type UpdateAgentSubtaskMutationBody = AgentSubtaskUpdate
    export type UpdateAgentSubtaskMutationError = Error

    /**
 * @summary Update Agent Subtask
 */
export const useUpdateAgentSubtask = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAgentSubtask>>, TError,{workspaceId: string;taskId: string;childId: string;data: AgentSubtaskUpdate}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateAgentSubtask>>,
        TError,
        {workspaceId: string;taskId: string;childId: string;data: AgentSubtaskUpdate},
        TContext
      > => {
      return useMutation(getUpdateAgentSubtaskMutationOptions(options), queryClient);
    }

export type deleteAgentSubtaskResponse204 = {
  data: void
  status: 204
}

export type deleteAgentSubtaskResponse403 = {
  data: Error
  status: 403
}

export type deleteAgentSubtaskResponse404 = {
  data: Error
  status: 404
}

export type deleteAgentSubtaskResponse409 = {
  data: Error
  status: 409
}

export type deleteAgentSubtaskResponse422 = {
  data: Error
  status: 422
}

export type deleteAgentSubtaskResponseSuccess = (deleteAgentSubtaskResponse204) & {
  headers: Headers;
};
export type deleteAgentSubtaskResponseError = (deleteAgentSubtaskResponse403 | deleteAgentSubtaskResponse404 | deleteAgentSubtaskResponse409 | deleteAgentSubtaskResponse422) & {
  headers: Headers;
};

export type deleteAgentSubtaskResponse = (deleteAgentSubtaskResponseSuccess | deleteAgentSubtaskResponseError)

export const getDeleteAgentSubtaskUrl = (workspaceId: string,
    taskId: string,
    childId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view/children/${childId}`
}

/**
 * Delete a subtask of the calling step's batch (spec 132); another step's is 404.
 * @summary Delete Agent Subtask
 */
export const deleteAgentSubtask = async (workspaceId: string,
    taskId: string,
    childId: string, options?: RequestInit): Promise<deleteAgentSubtaskResponse> => {

  return customFetch<deleteAgentSubtaskResponse>(getDeleteAgentSubtaskUrl(workspaceId,taskId,childId),
  {
    ...options,
    method: 'DELETE'


  }
);}




export const getDeleteAgentSubtaskMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAgentSubtask>>, TError,{workspaceId: string;taskId: string;childId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteAgentSubtask>>, TError,{workspaceId: string;taskId: string;childId: string}, TContext> => {

const mutationKey = ['deleteAgentSubtask'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAgentSubtask>>, {workspaceId: string;taskId: string;childId: string}> = (props) => {
          const {workspaceId,taskId,childId} = props ?? {};

          return  deleteAgentSubtask(workspaceId,taskId,childId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAgentSubtaskMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAgentSubtask>>>

    export type DeleteAgentSubtaskMutationError = Error

    /**
 * @summary Delete Agent Subtask
 */
export const useDeleteAgentSubtask = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAgentSubtask>>, TError,{workspaceId: string;taskId: string;childId: string}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteAgentSubtask>>,
        TError,
        {workspaceId: string;taskId: string;childId: string},
        TContext
      > => {
      return useMutation(getDeleteAgentSubtaskMutationOptions(options), queryClient);
    }

export type registerTaskExternalReferenceResponse201 = {
  data: ExternalReferenceView
  status: 201
}

export type registerTaskExternalReferenceResponse403 = {
  data: Error
  status: 403
}

export type registerTaskExternalReferenceResponse422 = {
  data: Error
  status: 422
}

export type registerTaskExternalReferenceResponseSuccess = (registerTaskExternalReferenceResponse201) & {
  headers: Headers;
};
export type registerTaskExternalReferenceResponseError = (registerTaskExternalReferenceResponse403 | registerTaskExternalReferenceResponse422) & {
  headers: Headers;
};

export type registerTaskExternalReferenceResponse = (registerTaskExternalReferenceResponseSuccess | registerTaskExternalReferenceResponseError)

export const getRegisterTaskExternalReferenceUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view/external-references`
}

/**
 * @summary Register Task External Reference
 */
export const registerTaskExternalReference = async (workspaceId: string,
    taskId: string,
    registerExternalReferenceRequest: RegisterExternalReferenceRequest, options?: RequestInit): Promise<registerTaskExternalReferenceResponse> => {

  return customFetch<registerTaskExternalReferenceResponse>(getRegisterTaskExternalReferenceUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(registerExternalReferenceRequest)
  }
);}




export const getRegisterTaskExternalReferenceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof registerTaskExternalReference>>, TError,{workspaceId: string;taskId: string;data: RegisterExternalReferenceRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof registerTaskExternalReference>>, TError,{workspaceId: string;taskId: string;data: RegisterExternalReferenceRequest}, TContext> => {

const mutationKey = ['registerTaskExternalReference'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof registerTaskExternalReference>>, {workspaceId: string;taskId: string;data: RegisterExternalReferenceRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  registerTaskExternalReference(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegisterTaskExternalReferenceMutationResult = NonNullable<Awaited<ReturnType<typeof registerTaskExternalReference>>>
    export type RegisterTaskExternalReferenceMutationBody = RegisterExternalReferenceRequest
    export type RegisterTaskExternalReferenceMutationError = Error

    /**
 * @summary Register Task External Reference
 */
export const useRegisterTaskExternalReference = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof registerTaskExternalReference>>, TError,{workspaceId: string;taskId: string;data: RegisterExternalReferenceRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof registerTaskExternalReference>>,
        TError,
        {workspaceId: string;taskId: string;data: RegisterExternalReferenceRequest},
        TContext
      > => {
      return useMutation(getRegisterTaskExternalReferenceMutationOptions(options), queryClient);
    }

export type getTaskAgentViewResponse200 = {
  data: TaskView
  status: 200
}

export type getTaskAgentViewResponse403 = {
  data: Error
  status: 403
}

export type getTaskAgentViewResponse404 = {
  data: Error
  status: 404
}

export type getTaskAgentViewResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getTaskAgentViewResponseSuccess = (getTaskAgentViewResponse200) & {
  headers: Headers;
};
export type getTaskAgentViewResponseError = (getTaskAgentViewResponse403 | getTaskAgentViewResponse404 | getTaskAgentViewResponse422) & {
  headers: Headers;
};

export type getTaskAgentViewResponse = (getTaskAgentViewResponseSuccess | getTaskAgentViewResponseError)

export const getGetTaskAgentViewUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view`
}

/**
 * @summary Get Task Agent View
 */
export const getTaskAgentView = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<getTaskAgentViewResponse> => {

  return customFetch<getTaskAgentViewResponse>(getGetTaskAgentViewUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTaskAgentViewQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view`
    ] as const;
    }


export const getGetTaskAgentViewQueryOptions = <TData = Awaited<ReturnType<typeof getTaskAgentView>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentView>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTaskAgentViewQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTaskAgentView>>> = ({ signal }) => getTaskAgentView(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentView>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetTaskAgentViewQueryResult = NonNullable<Awaited<ReturnType<typeof getTaskAgentView>>>
export type GetTaskAgentViewQueryError = Error | HTTPValidationError


export function useGetTaskAgentView<TData = Awaited<ReturnType<typeof getTaskAgentView>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentView>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskAgentView>>,
          TError,
          Awaited<ReturnType<typeof getTaskAgentView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTaskAgentView<TData = Awaited<ReturnType<typeof getTaskAgentView>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentView>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskAgentView>>,
          TError,
          Awaited<ReturnType<typeof getTaskAgentView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTaskAgentView<TData = Awaited<ReturnType<typeof getTaskAgentView>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentView>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Task Agent View
 */

export function useGetTaskAgentView<TData = Awaited<ReturnType<typeof getTaskAgentView>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentView>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetTaskAgentViewQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type getTaskAgentViewCommentsResponse200 = {
  data: AgentTaskCommentList
  status: 200
}

export type getTaskAgentViewCommentsResponse403 = {
  data: Error
  status: 403
}

export type getTaskAgentViewCommentsResponse404 = {
  data: Error
  status: 404
}

export type getTaskAgentViewCommentsResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getTaskAgentViewCommentsResponseSuccess = (getTaskAgentViewCommentsResponse200) & {
  headers: Headers;
};
export type getTaskAgentViewCommentsResponseError = (getTaskAgentViewCommentsResponse403 | getTaskAgentViewCommentsResponse404 | getTaskAgentViewCommentsResponse422) & {
  headers: Headers;
};

export type getTaskAgentViewCommentsResponse = (getTaskAgentViewCommentsResponseSuccess | getTaskAgentViewCommentsResponseError)

export const getGetTaskAgentViewCommentsUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view/comments`
}

/**
 * @summary Get Task Agent View Comments
 */
export const getTaskAgentViewComments = async (workspaceId: string,
    taskId: string, options?: RequestInit): Promise<getTaskAgentViewCommentsResponse> => {

  return customFetch<getTaskAgentViewCommentsResponse>(getGetTaskAgentViewCommentsUrl(workspaceId,taskId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTaskAgentViewCommentsQueryKey = (workspaceId: string,
    taskId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view/comments`
    ] as const;
    }


export const getGetTaskAgentViewCommentsQueryOptions = <TData = Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTaskAgentViewCommentsQueryKey(workspaceId,taskId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTaskAgentViewComments>>> = ({ signal }) => getTaskAgentViewComments(workspaceId,taskId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetTaskAgentViewCommentsQueryResult = NonNullable<Awaited<ReturnType<typeof getTaskAgentViewComments>>>
export type GetTaskAgentViewCommentsQueryError = Error | HTTPValidationError


export function useGetTaskAgentViewComments<TData = Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskAgentViewComments>>,
          TError,
          Awaited<ReturnType<typeof getTaskAgentViewComments>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTaskAgentViewComments<TData = Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskAgentViewComments>>,
          TError,
          Awaited<ReturnType<typeof getTaskAgentViewComments>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTaskAgentViewComments<TData = Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Task Agent View Comments
 */

export function useGetTaskAgentViewComments<TData = Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskAgentViewComments>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetTaskAgentViewCommentsQueryOptions(workspaceId,taskId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type addTaskAgentViewCommentResponse201 = {
  data: TaskCommentView
  status: 201
}

export type addTaskAgentViewCommentResponse403 = {
  data: Error
  status: 403
}

export type addTaskAgentViewCommentResponse404 = {
  data: Error
  status: 404
}

export type addTaskAgentViewCommentResponse422 = {
  data: Error
  status: 422
}

export type addTaskAgentViewCommentResponseSuccess = (addTaskAgentViewCommentResponse201) & {
  headers: Headers;
};
export type addTaskAgentViewCommentResponseError = (addTaskAgentViewCommentResponse403 | addTaskAgentViewCommentResponse404 | addTaskAgentViewCommentResponse422) & {
  headers: Headers;
};

export type addTaskAgentViewCommentResponse = (addTaskAgentViewCommentResponseSuccess | addTaskAgentViewCommentResponseError)

export const getAddTaskAgentViewCommentUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/agent-view/comments`
}

/**
 * @summary Add Task Agent View Comment
 */
export const addTaskAgentViewComment = async (workspaceId: string,
    taskId: string,
    createTaskCommentRequest: CreateTaskCommentRequest, options?: RequestInit): Promise<addTaskAgentViewCommentResponse> => {

  return customFetch<addTaskAgentViewCommentResponse>(getAddTaskAgentViewCommentUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(createTaskCommentRequest)
  }
);}




export const getAddTaskAgentViewCommentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addTaskAgentViewComment>>, TError,{workspaceId: string;taskId: string;data: CreateTaskCommentRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addTaskAgentViewComment>>, TError,{workspaceId: string;taskId: string;data: CreateTaskCommentRequest}, TContext> => {

const mutationKey = ['addTaskAgentViewComment'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addTaskAgentViewComment>>, {workspaceId: string;taskId: string;data: CreateTaskCommentRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  addTaskAgentViewComment(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddTaskAgentViewCommentMutationResult = NonNullable<Awaited<ReturnType<typeof addTaskAgentViewComment>>>
    export type AddTaskAgentViewCommentMutationBody = CreateTaskCommentRequest
    export type AddTaskAgentViewCommentMutationError = Error

    /**
 * @summary Add Task Agent View Comment
 */
export const useAddTaskAgentViewComment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addTaskAgentViewComment>>, TError,{workspaceId: string;taskId: string;data: CreateTaskCommentRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof addTaskAgentViewComment>>,
        TError,
        {workspaceId: string;taskId: string;data: CreateTaskCommentRequest},
        TContext
      > => {
      return useMutation(getAddTaskAgentViewCommentMutationOptions(options), queryClient);
    }

export type listWorkspaceTasksAgentViewResponse200 = {
  data: TaskView[]
  status: 200
}

export type listWorkspaceTasksAgentViewResponse403 = {
  data: Error
  status: 403
}

export type listWorkspaceTasksAgentViewResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listWorkspaceTasksAgentViewResponseSuccess = (listWorkspaceTasksAgentViewResponse200) & {
  headers: Headers;
};
export type listWorkspaceTasksAgentViewResponseError = (listWorkspaceTasksAgentViewResponse403 | listWorkspaceTasksAgentViewResponse422) & {
  headers: Headers;
};

export type listWorkspaceTasksAgentViewResponse = (listWorkspaceTasksAgentViewResponseSuccess | listWorkspaceTasksAgentViewResponseError)

export const getListWorkspaceTasksAgentViewUrl = (workspaceId: string,) => {




  return `/api/workspaces/${workspaceId}/agent-view/tasks`
}

/**
 * @summary List Workspace Tasks Agent View
 */
export const listWorkspaceTasksAgentView = async (workspaceId: string, options?: RequestInit): Promise<listWorkspaceTasksAgentViewResponse> => {

  return customFetch<listWorkspaceTasksAgentViewResponse>(getListWorkspaceTasksAgentViewUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWorkspaceTasksAgentViewQueryKey = (workspaceId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/agent-view/tasks`
    ] as const;
    }


export const getListWorkspaceTasksAgentViewQueryOptions = <TData = Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError = Error | HTTPValidationError>(workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWorkspaceTasksAgentViewQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>> = ({ signal }) => listWorkspaceTasksAgentView(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListWorkspaceTasksAgentViewQueryResult = NonNullable<Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>>
export type ListWorkspaceTasksAgentViewQueryError = Error | HTTPValidationError


export function useListWorkspaceTasksAgentView<TData = Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaceTasksAgentView<TData = Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWorkspaceTasksAgentView<TData = Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Workspace Tasks Agent View
 */

export function useListWorkspaceTasksAgentView<TData = Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError = Error | HTTPValidationError>(
 workspaceId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceTasksAgentView>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListWorkspaceTasksAgentViewQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type moveTaskFromBacklogResponse200 = {
  data: MoveTaskResponse
  status: 200
}

export type moveTaskFromBacklogResponse403 = {
  data: Error
  status: 403
}

export type moveTaskFromBacklogResponse404 = {
  data: Error
  status: 404
}

export type moveTaskFromBacklogResponse409 = {
  data: Error
  status: 409
}

export type moveTaskFromBacklogResponse422 = {
  data: Error
  status: 422
}

export type moveTaskFromBacklogResponseSuccess = (moveTaskFromBacklogResponse200) & {
  headers: Headers;
};
export type moveTaskFromBacklogResponseError = (moveTaskFromBacklogResponse403 | moveTaskFromBacklogResponse404 | moveTaskFromBacklogResponse409 | moveTaskFromBacklogResponse422) & {
  headers: Headers;
};

export type moveTaskFromBacklogResponse = (moveTaskFromBacklogResponseSuccess | moveTaskFromBacklogResponseError)

export const getMoveTaskFromBacklogUrl = (workspaceId: string,
    taskId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/move-from-backlog`
}

/**
 * @summary Move Task From Backlog
 */
export const moveTaskFromBacklog = async (workspaceId: string,
    taskId: string,
    moveTaskRequest: MoveTaskRequest, options?: RequestInit): Promise<moveTaskFromBacklogResponse> => {

  return customFetch<moveTaskFromBacklogResponse>(getMoveTaskFromBacklogUrl(workspaceId,taskId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(moveTaskRequest)
  }
);}




export const getMoveTaskFromBacklogMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof moveTaskFromBacklog>>, TError,{workspaceId: string;taskId: string;data: MoveTaskRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof moveTaskFromBacklog>>, TError,{workspaceId: string;taskId: string;data: MoveTaskRequest}, TContext> => {

const mutationKey = ['moveTaskFromBacklog'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof moveTaskFromBacklog>>, {workspaceId: string;taskId: string;data: MoveTaskRequest}> = (props) => {
          const {workspaceId,taskId,data} = props ?? {};

          return  moveTaskFromBacklog(workspaceId,taskId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MoveTaskFromBacklogMutationResult = NonNullable<Awaited<ReturnType<typeof moveTaskFromBacklog>>>
    export type MoveTaskFromBacklogMutationBody = MoveTaskRequest
    export type MoveTaskFromBacklogMutationError = Error

    /**
 * @summary Move Task From Backlog
 */
export const useMoveTaskFromBacklog = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof moveTaskFromBacklog>>, TError,{workspaceId: string;taskId: string;data: MoveTaskRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof moveTaskFromBacklog>>,
        TError,
        {workspaceId: string;taskId: string;data: MoveTaskRequest},
        TContext
      > => {
      return useMutation(getMoveTaskFromBacklogMutationOptions(options), queryClient);
    }

export type recordExecutionResultResponse201 = {
  data: RecordExecutionResultResponse
  status: 201
}

export type recordExecutionResultResponse403 = {
  data: Error
  status: 403
}

export type recordExecutionResultResponse422 = {
  data: Error
  status: 422
}

export type recordExecutionResultResponseSuccess = (recordExecutionResultResponse201) & {
  headers: Headers;
};
export type recordExecutionResultResponseError = (recordExecutionResultResponse403 | recordExecutionResultResponse422) & {
  headers: Headers;
};

export type recordExecutionResultResponse = (recordExecutionResultResponseSuccess | recordExecutionResultResponseError)

export const getRecordExecutionResultUrl = (workspaceId: string,
    taskId: string,
    executionId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/result`
}

/**
 * @summary Record Execution Result
 */
export const recordExecutionResult = async (workspaceId: string,
    taskId: string,
    executionId: string,
    recordExecutionResultRequest: RecordExecutionResultRequest, options?: RequestInit): Promise<recordExecutionResultResponse> => {

  return customFetch<recordExecutionResultResponse>(getRecordExecutionResultUrl(workspaceId,taskId,executionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(recordExecutionResultRequest)
  }
);}




export const getRecordExecutionResultMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordExecutionResult>>, TError,{workspaceId: string;taskId: string;executionId: string;data: RecordExecutionResultRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recordExecutionResult>>, TError,{workspaceId: string;taskId: string;executionId: string;data: RecordExecutionResultRequest}, TContext> => {

const mutationKey = ['recordExecutionResult'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recordExecutionResult>>, {workspaceId: string;taskId: string;executionId: string;data: RecordExecutionResultRequest}> = (props) => {
          const {workspaceId,taskId,executionId,data} = props ?? {};

          return  recordExecutionResult(workspaceId,taskId,executionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecordExecutionResultMutationResult = NonNullable<Awaited<ReturnType<typeof recordExecutionResult>>>
    export type RecordExecutionResultMutationBody = RecordExecutionResultRequest
    export type RecordExecutionResultMutationError = Error

    /**
 * @summary Record Execution Result
 */
export const useRecordExecutionResult = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordExecutionResult>>, TError,{workspaceId: string;taskId: string;executionId: string;data: RecordExecutionResultRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof recordExecutionResult>>,
        TError,
        {workspaceId: string;taskId: string;executionId: string;data: RecordExecutionResultRequest},
        TContext
      > => {
      return useMutation(getRecordExecutionResultMutationOptions(options), queryClient);
    }

export type listArtifactsForExecutionResponse200 = {
  data: TaskArtifactsView
  status: 200
}

export type listArtifactsForExecutionResponse403 = {
  data: Error
  status: 403
}

export type listArtifactsForExecutionResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type listArtifactsForExecutionResponseSuccess = (listArtifactsForExecutionResponse200) & {
  headers: Headers;
};
export type listArtifactsForExecutionResponseError = (listArtifactsForExecutionResponse403 | listArtifactsForExecutionResponse422) & {
  headers: Headers;
};

export type listArtifactsForExecutionResponse = (listArtifactsForExecutionResponseSuccess | listArtifactsForExecutionResponseError)

export const getListArtifactsForExecutionUrl = (workspaceId: string,
    taskId: string,
    executionId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/artifacts`
}

/**
 * @summary List Artifacts For Execution
 */
export const listArtifactsForExecution = async (workspaceId: string,
    taskId: string,
    executionId: string, options?: RequestInit): Promise<listArtifactsForExecutionResponse> => {

  return customFetch<listArtifactsForExecutionResponse>(getListArtifactsForExecutionUrl(workspaceId,taskId,executionId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListArtifactsForExecutionQueryKey = (workspaceId: string,
    taskId: string,
    executionId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/artifacts`
    ] as const;
    }


export const getListArtifactsForExecutionQueryOptions = <TData = Awaited<ReturnType<typeof listArtifactsForExecution>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listArtifactsForExecution>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListArtifactsForExecutionQueryKey(workspaceId,taskId,executionId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listArtifactsForExecution>>> = ({ signal }) => listArtifactsForExecution(workspaceId,taskId,executionId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined && executionId !== null && executionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listArtifactsForExecution>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListArtifactsForExecutionQueryResult = NonNullable<Awaited<ReturnType<typeof listArtifactsForExecution>>>
export type ListArtifactsForExecutionQueryError = Error | HTTPValidationError


export function useListArtifactsForExecution<TData = Awaited<ReturnType<typeof listArtifactsForExecution>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listArtifactsForExecution>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listArtifactsForExecution>>,
          TError,
          Awaited<ReturnType<typeof listArtifactsForExecution>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListArtifactsForExecution<TData = Awaited<ReturnType<typeof listArtifactsForExecution>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listArtifactsForExecution>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listArtifactsForExecution>>,
          TError,
          Awaited<ReturnType<typeof listArtifactsForExecution>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListArtifactsForExecution<TData = Awaited<ReturnType<typeof listArtifactsForExecution>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listArtifactsForExecution>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Artifacts For Execution
 */

export function useListArtifactsForExecution<TData = Awaited<ReturnType<typeof listArtifactsForExecution>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listArtifactsForExecution>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListArtifactsForExecutionQueryOptions(workspaceId,taskId,executionId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type recordArtifactEventsResponse200 = {
  data: RecordArtifactEventsResponse
  status: 200
}

export type recordArtifactEventsResponse403 = {
  data: Error
  status: 403
}

export type recordArtifactEventsResponse422 = {
  data: Error
  status: 422
}

export type recordArtifactEventsResponseSuccess = (recordArtifactEventsResponse200) & {
  headers: Headers;
};
export type recordArtifactEventsResponseError = (recordArtifactEventsResponse403 | recordArtifactEventsResponse422) & {
  headers: Headers;
};

export type recordArtifactEventsResponse = (recordArtifactEventsResponseSuccess | recordArtifactEventsResponseError)

export const getRecordArtifactEventsUrl = (workspaceId: string,
    taskId: string,
    executionId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/artifacts`
}

/**
 * @summary Record Artifact Events
 */
export const recordArtifactEvents = async (workspaceId: string,
    taskId: string,
    executionId: string,
    recordArtifactEventsRequest: RecordArtifactEventsRequest, options?: RequestInit): Promise<recordArtifactEventsResponse> => {

  return customFetch<recordArtifactEventsResponse>(getRecordArtifactEventsUrl(workspaceId,taskId,executionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(recordArtifactEventsRequest)
  }
);}




export const getRecordArtifactEventsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordArtifactEvents>>, TError,{workspaceId: string;taskId: string;executionId: string;data: RecordArtifactEventsRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recordArtifactEvents>>, TError,{workspaceId: string;taskId: string;executionId: string;data: RecordArtifactEventsRequest}, TContext> => {

const mutationKey = ['recordArtifactEvents'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recordArtifactEvents>>, {workspaceId: string;taskId: string;executionId: string;data: RecordArtifactEventsRequest}> = (props) => {
          const {workspaceId,taskId,executionId,data} = props ?? {};

          return  recordArtifactEvents(workspaceId,taskId,executionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecordArtifactEventsMutationResult = NonNullable<Awaited<ReturnType<typeof recordArtifactEvents>>>
    export type RecordArtifactEventsMutationBody = RecordArtifactEventsRequest
    export type RecordArtifactEventsMutationError = Error

    /**
 * @summary Record Artifact Events
 */
export const useRecordArtifactEvents = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordArtifactEvents>>, TError,{workspaceId: string;taskId: string;executionId: string;data: RecordArtifactEventsRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof recordArtifactEvents>>,
        TError,
        {workspaceId: string;taskId: string;executionId: string;data: RecordArtifactEventsRequest},
        TContext
      > => {
      return useMutation(getRecordArtifactEventsMutationOptions(options), queryClient);
    }

export type readRunForgeTokenResponse200 = {
  data: RunForgeToken
  status: 200
}

export type readRunForgeTokenResponse403 = {
  data: Error
  status: 403
}

export type readRunForgeTokenResponse404 = {
  data: Error
  status: 404
}

export type readRunForgeTokenResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type readRunForgeTokenResponseSuccess = (readRunForgeTokenResponse200) & {
  headers: Headers;
};
export type readRunForgeTokenResponseError = (readRunForgeTokenResponse403 | readRunForgeTokenResponse404 | readRunForgeTokenResponse422) & {
  headers: Headers;
};

export type readRunForgeTokenResponse = (readRunForgeTokenResponseSuccess | readRunForgeTokenResponseError)

export const getReadRunForgeTokenUrl = (params: ReadRunForgeTokenParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/executions/current/forge-token?${stringifiedParams}` : `/api/executions/current/forge-token`
}

/**
 * The run's current token for one forge host, good for a while yet (FR-011).
 * @summary Read Run Forge Token
 */
export const readRunForgeToken = async (params: ReadRunForgeTokenParams, options?: RequestInit): Promise<readRunForgeTokenResponse> => {

  return customFetch<readRunForgeTokenResponse>(getReadRunForgeTokenUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getReadRunForgeTokenQueryKey = (params?: ReadRunForgeTokenParams,) => {
    return [
    `/api/executions/current/forge-token`, ...(params ? [params] : [])
    ] as const;
    }


export const getReadRunForgeTokenQueryOptions = <TData = Awaited<ReturnType<typeof readRunForgeToken>>, TError = Error | HTTPValidationError>(params: ReadRunForgeTokenParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readRunForgeToken>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getReadRunForgeTokenQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof readRunForgeToken>>> = ({ signal }) => readRunForgeToken(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof readRunForgeToken>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ReadRunForgeTokenQueryResult = NonNullable<Awaited<ReturnType<typeof readRunForgeToken>>>
export type ReadRunForgeTokenQueryError = Error | HTTPValidationError


export function useReadRunForgeToken<TData = Awaited<ReturnType<typeof readRunForgeToken>>, TError = Error | HTTPValidationError>(
 params: ReadRunForgeTokenParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof readRunForgeToken>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof readRunForgeToken>>,
          TError,
          Awaited<ReturnType<typeof readRunForgeToken>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadRunForgeToken<TData = Awaited<ReturnType<typeof readRunForgeToken>>, TError = Error | HTTPValidationError>(
 params: ReadRunForgeTokenParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readRunForgeToken>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof readRunForgeToken>>,
          TError,
          Awaited<ReturnType<typeof readRunForgeToken>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useReadRunForgeToken<TData = Awaited<ReturnType<typeof readRunForgeToken>>, TError = Error | HTTPValidationError>(
 params: ReadRunForgeTokenParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readRunForgeToken>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read Run Forge Token
 */

export function useReadRunForgeToken<TData = Awaited<ReturnType<typeof readRunForgeToken>>, TError = Error | HTTPValidationError>(
 params: ReadRunForgeTokenParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof readRunForgeToken>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getReadRunForgeTokenQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type getReplyContextResponse200 = {
  data: ReplyContextView
  status: 200
}

export type getReplyContextResponse403 = {
  data: Error
  status: 403
}

export type getReplyContextResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type getReplyContextResponseSuccess = (getReplyContextResponse200) & {
  headers: Headers;
};
export type getReplyContextResponseError = (getReplyContextResponse403 | getReplyContextResponse422) & {
  headers: Headers;
};

export type getReplyContextResponse = (getReplyContextResponseSuccess | getReplyContextResponseError)

export const getGetReplyContextUrl = (workspaceId: string,
    taskId: string,
    executionId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/reply-context`
}

/**
 * What a reply run is told: its prompt, its task's folder and the run's branches.
 * @summary Get Reply Context
 */
export const getReplyContext = async (workspaceId: string,
    taskId: string,
    executionId: string, options?: RequestInit): Promise<getReplyContextResponse> => {

  return customFetch<getReplyContextResponse>(getGetReplyContextUrl(workspaceId,taskId,executionId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetReplyContextQueryKey = (workspaceId: string,
    taskId: string,
    executionId: string,) => {
    return [
    `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/reply-context`
    ] as const;
    }


export const getGetReplyContextQueryOptions = <TData = Awaited<ReturnType<typeof getReplyContext>>, TError = Error | HTTPValidationError>(workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplyContext>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetReplyContextQueryKey(workspaceId,taskId,executionId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getReplyContext>>> = ({ signal }) => getReplyContext(workspaceId,taskId,executionId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && taskId !== null && taskId !== undefined && executionId !== null && executionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getReplyContext>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetReplyContextQueryResult = NonNullable<Awaited<ReturnType<typeof getReplyContext>>>
export type GetReplyContextQueryError = Error | HTTPValidationError


export function useGetReplyContext<TData = Awaited<ReturnType<typeof getReplyContext>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplyContext>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReplyContext>>,
          TError,
          Awaited<ReturnType<typeof getReplyContext>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetReplyContext<TData = Awaited<ReturnType<typeof getReplyContext>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplyContext>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReplyContext>>,
          TError,
          Awaited<ReturnType<typeof getReplyContext>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetReplyContext<TData = Awaited<ReturnType<typeof getReplyContext>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplyContext>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get Reply Context
 */

export function useGetReplyContext<TData = Awaited<ReturnType<typeof getReplyContext>>, TError = Error | HTTPValidationError>(
 workspaceId: string,
    taskId: string,
    executionId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplyContext>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetReplyContextQueryOptions(workspaceId,taskId,executionId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type postReplyAnswerResponse201 = {
  data: ReplyAnswerView
  status: 201
}

export type postReplyAnswerResponse403 = {
  data: Error
  status: 403
}

export type postReplyAnswerResponse422 = {
  data: HTTPValidationError
  status: 422
}

export type postReplyAnswerResponseSuccess = (postReplyAnswerResponse201) & {
  headers: Headers;
};
export type postReplyAnswerResponseError = (postReplyAnswerResponse403 | postReplyAnswerResponse422) & {
  headers: Headers;
};

export type postReplyAnswerResponse = (postReplyAnswerResponseSuccess | postReplyAnswerResponseError)

export const getPostReplyAnswerUrl = (workspaceId: string,
    taskId: string,
    executionId: string,) => {




  return `/api/workspaces/${workspaceId}/tasks/${taskId}/executions/${executionId}/reply-answer`
}

/**
 * A reply run's answer, as the agent's message on its thread (once).
 * @summary Post Reply Answer
 */
export const postReplyAnswer = async (workspaceId: string,
    taskId: string,
    executionId: string,
    postReplyAnswerRequest: PostReplyAnswerRequest, options?: RequestInit): Promise<postReplyAnswerResponse> => {

  return customFetch<postReplyAnswerResponse>(getPostReplyAnswerUrl(workspaceId,taskId,executionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    body: JSON.stringify(postReplyAnswerRequest)
  }
);}




export const getPostReplyAnswerMutationOptions = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postReplyAnswer>>, TError,{workspaceId: string;taskId: string;executionId: string;data: PostReplyAnswerRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postReplyAnswer>>, TError,{workspaceId: string;taskId: string;executionId: string;data: PostReplyAnswerRequest}, TContext> => {

const mutationKey = ['postReplyAnswer'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postReplyAnswer>>, {workspaceId: string;taskId: string;executionId: string;data: PostReplyAnswerRequest}> = (props) => {
          const {workspaceId,taskId,executionId,data} = props ?? {};

          return  postReplyAnswer(workspaceId,taskId,executionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostReplyAnswerMutationResult = NonNullable<Awaited<ReturnType<typeof postReplyAnswer>>>
    export type PostReplyAnswerMutationBody = PostReplyAnswerRequest
    export type PostReplyAnswerMutationError = Error | HTTPValidationError

    /**
 * @summary Post Reply Answer
 */
export const usePostReplyAnswer = <TError = Error | HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postReplyAnswer>>, TError,{workspaceId: string;taskId: string;executionId: string;data: PostReplyAnswerRequest}, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postReplyAnswer>>,
        TError,
        {workspaceId: string;taskId: string;executionId: string;data: PostReplyAnswerRequest},
        TContext
      > => {
      return useMutation(getPostReplyAnswerMutationOptions(options), queryClient);
    }


export const getHealthResponseMock = (): Health200 => ({
        [faker.string.alphanumeric(5)]: faker.string.alpha({length: {min: 10, max: 20}})
      })

export const getReadCurrentUserResponseMock = (overrideResponse: Partial<Extract<CurrentUserView, object>> = {}): CurrentUserView => ({id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}}), roles: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), is_org_admin: faker.datatype.boolean(), ...overrideResponse})

export const getSetCurrentUserTimezoneResponseMock = (overrideResponse: Partial<Extract<UserTimezone, object>> = {}): UserTimezone => ({timezone: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), ...overrideResponse})

export const getReadCurrentUserCommitEmailResponseMock = (overrideResponse: Partial<Extract<UserCommitEmail, object>> = {}): UserCommitEmail => ({commit_email: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), ...overrideResponse})

export const getSetCurrentUserCommitEmailResponseMock = (overrideResponse: Partial<Extract<UserCommitEmail, object>> = {}): UserCommitEmail => ({commit_email: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), ...overrideResponse})

export const getListAssignableUsersResponseMock = (overrideResponse: Partial<Extract<UserListPage, object>> = {}): UserListPage => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})})), ...overrideResponse})

export const getListMyForgeAccountsResponseMock = (overrideResponse: Partial<Extract<ForgeAccountList, object>> = {}): ForgeAccountList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), forge_username: faker.string.alpha({length: {min: 10, max: 20}}), forge_commit_email: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), scopes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), scopes_unknown: faker.datatype.boolean(), expires_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), status: faker.helpers.arrayElement(Object.values(ForgeAccountStatus)), status_reason: faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(ForgeAccountReason)),null,]), last_verified_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), link_method: faker.helpers.arrayElement(Object.values(LinkMethod))})), ...overrideResponse})

export const getListForgeHostsResponseMock = (overrideResponse: Partial<Extract<ForgeHostList, object>> = {}): ForgeHostList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), linked: faker.datatype.boolean(), oauth_available: faker.datatype.boolean(), workspace_ids: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.uuid())), undefined]), required_workspace_ids: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.uuid())), undefined])})), ...overrideResponse})

export const getLinkForgeAccountResponseMock = (overrideResponse: Partial<Extract<LinkResult, object>> = {}): LinkResult => ({account: {id: faker.string.uuid(), provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), forge_username: faker.string.alpha({length: {min: 10, max: 20}}), forge_commit_email: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), scopes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), scopes_unknown: faker.datatype.boolean(), expires_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), status: faker.helpers.arrayElement(Object.values(ForgeAccountStatus)), status_reason: faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(ForgeAccountReason)),null,]), last_verified_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), link_method: faker.helpers.arrayElement(Object.values(LinkMethod))}, warnings: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ("scopes_unknown")), ...overrideResponse})

export const getRemindForgeAccountResponseMock = (overrideResponse: Partial<Extract<ForgeReminderView, object>> = {}): ForgeReminderView => ({host: faker.string.alpha({length: {min: 10, max: 20}}), reminded_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getListPeopleForgeAccountsResponseMock = (overrideResponse: Partial<Extract<PeopleLinksPage, object>> = {}): PeopleLinksPage => ({hosts: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), unused: faker.datatype.boolean(), workspace_ids: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.uuid())), undefined]), required_workspace_ids: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.uuid())), undefined])})), required_workspace_ids: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.uuid())), items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({person: {id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})}, missing_count: faker.number.int(), links: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), forge_username: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(ForgeAccountStatus)), status_reason: faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(ForgeAccountReason)),null,]), expires_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), last_verified_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), link_method: faker.helpers.arrayElement(Object.values(LinkMethod))})), reminders: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({host: faker.string.alpha({length: {min: 10, max: 20}}), reminded_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined])})), total: faker.number.int(), page: faker.number.int(), page_size: faker.number.int(), ...overrideResponse})

export const getListForgePoliciesResponseMock = (overrideResponse: Partial<Extract<ForgePolicyList, object>> = {}): ForgePolicyList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workspace_id: faker.string.uuid(), changed_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), ...overrideResponse})

export const getSetForgePolicyResponseMock = (overrideResponse: Partial<Extract<ForgePolicyView, object>> = {}): ForgePolicyView => ({workspace_id: faker.string.uuid(), required: faker.datatype.boolean(), ...overrideResponse})

export const getReadPersonalAccountStandingResponseMock = (overrideResponse: Partial<Extract<PersonalAccountStanding, object>> = {}): PersonalAccountStanding => ({required: faker.datatype.boolean(), missing: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), reason: faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const)})), ...overrideResponse})

export const getListForgeAppsResponseMock = (overrideResponse: Partial<Extract<ForgeAppList, object>> = {}): ForgeAppList => ({redirect_uri: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), available: faker.datatype.boolean(), items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), client_id: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), configured: faker.datatype.boolean(), changed_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,])})), ...overrideResponse})

export const getSetForgeAppResponseMock = (overrideResponse: Partial<Extract<ForgeAppView, object>> = {}): ForgeAppView => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), client_id: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), configured: faker.datatype.boolean(), changed_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), ...overrideResponse})

export const getStartForgeOauthResponseMock = (overrideResponse: Partial<Extract<OAuthStartView, object>> = {}): OAuthStartView => ({authorize_url: faker.string.alpha({length: {min: 10, max: 20}}), ...overrideResponse})

export const getListMyAiToolsResponseMock = (overrideResponse: Partial<Extract<AiToolList, object>> = {}): AiToolList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({client_name: faker.string.alpha({length: {min: 10, max: 20}}), client_version: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), first_used_at: faker.date.past().toISOString().slice(0, 19) + 'Z', last_used_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), ...overrideResponse})

export const getCreateWorkspaceResponseMock = (overrideResponse: Partial<Extract<WorkspaceAggregate, object>> = {}): WorkspaceAggregate => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), storage_prefix: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(WorkspaceStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workflows: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])})), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean()})), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), labels: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), concurrency: faker.helpers.arrayElement([faker.helpers.arrayElement([{limit: faker.number.int(), running: faker.number.int(), queued: faker.number.int()},null,]), undefined]), visibility: faker.helpers.arrayElement(Object.values(WorkspaceVisibility)), access: faker.helpers.arrayElement([faker.helpers.arrayElement([{role: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkspaceRole)),null,]), undefined]), can_work: faker.datatype.boolean(), can_manage: faker.datatype.boolean(), enforced: faker.datatype.boolean(), as_org_admin: faker.datatype.boolean()},null,]), undefined]), ...overrideResponse})

export const getListWorkspacesResponseMock = (overrideResponse: Partial<Extract<WorkspaceListPage, object>> = {}): WorkspaceListPage => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(WorkspaceStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), counts: faker.helpers.arrayElement([faker.helpers.arrayElement([{workflows: faker.number.int({min: 0}), tasks: faker.number.int({min: 0})},null,]), undefined]), workflow_names: faker.helpers.arrayElement([faker.helpers.arrayElement([{names: Array.from({ length: faker.number.int({min: 1, max: 3}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), total: faker.number.int({min: 0})},null,]), undefined]), last_activity_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), visibility: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkspaceVisibility)),null,]), undefined]), access: faker.helpers.arrayElement([faker.helpers.arrayElement([{role: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkspaceRole)),null,]), undefined]), can_work: faker.datatype.boolean(), can_manage: faker.datatype.boolean(), enforced: faker.datatype.boolean(), as_org_admin: faker.datatype.boolean()},null,]), undefined])})), total: faker.number.int(), limit: faker.number.int(), offset: faker.number.int(), ...overrideResponse})

export const getReadWorkspaceResponseMock = (overrideResponse: Partial<Extract<WorkspaceAggregate, object>> = {}): WorkspaceAggregate => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), storage_prefix: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(WorkspaceStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workflows: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])})), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean()})), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), labels: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), concurrency: faker.helpers.arrayElement([faker.helpers.arrayElement([{limit: faker.number.int(), running: faker.number.int(), queued: faker.number.int()},null,]), undefined]), visibility: faker.helpers.arrayElement(Object.values(WorkspaceVisibility)), access: faker.helpers.arrayElement([faker.helpers.arrayElement([{role: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkspaceRole)),null,]), undefined]), can_work: faker.datatype.boolean(), can_manage: faker.datatype.boolean(), enforced: faker.datatype.boolean(), as_org_admin: faker.datatype.boolean()},null,]), undefined]), ...overrideResponse})

export const getUpdateWorkspaceResponseMock = (overrideResponse: Partial<Extract<WorkspaceAggregate, object>> = {}): WorkspaceAggregate => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), storage_prefix: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(WorkspaceStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workflows: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])})), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean()})), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), labels: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), concurrency: faker.helpers.arrayElement([faker.helpers.arrayElement([{limit: faker.number.int(), running: faker.number.int(), queued: faker.number.int()},null,]), undefined]), visibility: faker.helpers.arrayElement(Object.values(WorkspaceVisibility)), access: faker.helpers.arrayElement([faker.helpers.arrayElement([{role: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkspaceRole)),null,]), undefined]), can_work: faker.datatype.boolean(), can_manage: faker.datatype.boolean(), enforced: faker.datatype.boolean(), as_org_admin: faker.datatype.boolean()},null,]), undefined]), ...overrideResponse})

export const getListWorkspaceMembersResponseMock = (overrideResponse: Partial<Extract<WorkspaceMembersView, object>> = {}): WorkspaceMembersView => ({members: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({user: {id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})}, role: faker.helpers.arrayElement(Object.values(WorkspaceRole)), added_at: faker.date.past().toISOString().slice(0, 19) + 'Z', added_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), is_active: faker.datatype.boolean()})), visibility: faker.helpers.arrayElement(Object.values(WorkspaceVisibility)), non_member_followers: faker.number.int(), ...overrideResponse})

export const getAddWorkspaceMemberResponseMock = (overrideResponse: Partial<Extract<WorkspaceMembersView, object>> = {}): WorkspaceMembersView => ({members: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({user: {id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})}, role: faker.helpers.arrayElement(Object.values(WorkspaceRole)), added_at: faker.date.past().toISOString().slice(0, 19) + 'Z', added_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), is_active: faker.datatype.boolean()})), visibility: faker.helpers.arrayElement(Object.values(WorkspaceVisibility)), non_member_followers: faker.number.int(), ...overrideResponse})

export const getUpdateWorkspaceMemberResponseMock = (overrideResponse: Partial<Extract<WorkspaceMembersView, object>> = {}): WorkspaceMembersView => ({members: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({user: {id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})}, role: faker.helpers.arrayElement(Object.values(WorkspaceRole)), added_at: faker.date.past().toISOString().slice(0, 19) + 'Z', added_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), is_active: faker.datatype.boolean()})), visibility: faker.helpers.arrayElement(Object.values(WorkspaceVisibility)), non_member_followers: faker.number.int(), ...overrideResponse})

export const getRemoveWorkspaceMemberResponseMock = (overrideResponse: Partial<Extract<MemberRemoval, object>> = {}): MemberRemoval => ({gate_assignments_cleared: faker.number.int(), step_assignees_cleared: faker.number.int(), follows_dropped: faker.number.int(), ...overrideResponse})

export const getListWorkspacePeopleResponseMock = (overrideResponse: Partial<Extract<WorkspacePeople, object>> = {}): WorkspacePeople => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})})), ...overrideResponse})

export const getRequestWorkspaceAccessResponseMock = (overrideResponse: Partial<Extract<MyAccessRequest, object>> = {}): MyAccessRequest => ({requested_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getListWorkspaceAccessRequestsResponseMock = (overrideResponse: Partial<Extract<AccessRequests, object>> = {}): AccessRequests => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), user: {id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})}, requested_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), ...overrideResponse})

export const getReadMyWorkspaceAccessRequestResponseMock = (overrideResponse: Partial<Extract<MyAccessRequest, object>> = {}): MyAccessRequest => ({requested_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getAddFromWorkspaceAccessRequestResponseMock = (overrideResponse: Partial<Extract<WorkspaceMembersView, object>> = {}): WorkspaceMembersView => ({members: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({user: {id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})}, role: faker.helpers.arrayElement(Object.values(WorkspaceRole)), added_at: faker.date.past().toISOString().slice(0, 19) + 'Z', added_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), is_active: faker.datatype.boolean()})), visibility: faker.helpers.arrayElement(Object.values(WorkspaceVisibility)), non_member_followers: faker.number.int(), ...overrideResponse})

export const getReadWorkspaceAccessLogResponseMock = (overrideResponse: Partial<Extract<AccessLogPage, object>> = {}): AccessLogPage => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workspace: {id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}})}, kind: faker.helpers.arrayElement(Object.values(AccessEventKind)), at: faker.date.past().toISOString().slice(0, 19) + 'Z', actor: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), subject: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), as_org_admin: faker.datatype.boolean(), detail: {}})), next_before: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), ...overrideResponse})

export const getCreateWorkflowResponseMock = (overrideResponse: Partial<Extract<WorkflowView, object>> = {}): WorkflowView => ({id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getCreateWorkflowDraftResponseMock = (overrideResponse: Partial<Extract<WorkflowView, object>> = {}): WorkflowView => ({id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getReplaceWorkflowResponseMock = (overrideResponse: Partial<Extract<WorkflowView, object>> = {}): WorkflowView => ({id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getRenameWorkflowResponseMock = (overrideResponse: Partial<Extract<WorkflowView, object>> = {}): WorkflowView => ({id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getGetWorkflowForEditingResponseMock = (overrideResponse: Partial<Extract<WorkflowEditView, object>> = {}): WorkflowEditView => ({workflow: {id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])}, pending_changes: faker.helpers.arrayElement([faker.helpers.arrayElement([{definition: {name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), inputs: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), undefined]), phases: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined])}, revision: faker.string.uuid(), based_on_version: faker.number.int(), outdated: faker.datatype.boolean(), saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), saved_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), saved_at: faker.date.past().toISOString().slice(0, 19) + 'Z'},null,]), undefined]), ...overrideResponse})

export const getSaveWorkflowPendingChangesResponseMock = (overrideResponse: Partial<Extract<WorkflowEditView, object>> = {}): WorkflowEditView => ({workflow: {id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])}, pending_changes: faker.helpers.arrayElement([faker.helpers.arrayElement([{definition: {name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), inputs: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), undefined]), phases: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined])}, revision: faker.string.uuid(), based_on_version: faker.number.int(), outdated: faker.datatype.boolean(), saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), saved_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), saved_at: faker.date.past().toISOString().slice(0, 19) + 'Z'},null,]), undefined]), ...overrideResponse})

export const getPublishWorkflowPendingChangesResponseMock = (overrideResponse: Partial<Extract<WorkflowView, object>> = {}): WorkflowView => ({id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getImportWorkflowResponseMock = (overrideResponse: Partial<Extract<WorkflowImportResult, object>> = {}): WorkflowImportResult => ({workflow: {id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])}, warnings: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({code: "assignee_not_found", message: faker.string.alpha({length: {min: 10, max: 20}}), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), value: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), undefined]), substitutions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(ReferenceKind)), from_value: faker.string.alpha({length: {min: 10, max: 20}}), to_value: faker.string.alpha({length: {min: 10, max: 20}}), step_ids: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), undefined])})), undefined]), ...overrideResponse})

export const getPreflightWorkflowResponseMock = (overrideResponse: Partial<Extract<ImportPreflight, object>> = {}): ImportPreflight => ({violations: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({category: faker.helpers.arrayElement(Object.values(ViolationCategory)), message: faker.string.alpha({length: {min: 10, max: 20}}), path: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), line: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), column: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), actual: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), phase_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), field: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), undefined]), unresolved: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(ReferenceKind)), value: faker.string.alpha({length: {min: 10, max: 20}}), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocking: faker.datatype.boolean()})), undefined]), importable: faker.datatype.boolean(), ...overrideResponse})

export const getCreateTaskResponseMock = (overrideResponse: Partial<Extract<TaskView, object>> = {}): TaskView => ({id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean(), ...overrideResponse})

export const getMoveTaskResponseMock = (overrideResponse: Partial<Extract<MoveTaskResponse, object>> = {}): MoveTaskResponse => ({task: {id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean()}, execution: {id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])}, ...overrideResponse})

export const getStartLinkedTasksResponseMock = (overrideResponse: Partial<Extract<StartLinkedTasksResponse, object>> = {}): StartLinkedTasksResponse => ({results: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), outcome: faker.helpers.arrayElement(['started','refused'] as const), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), undefined]), ...overrideResponse})

export const getSplitTaskResponseMock = (overrideResponse: Partial<Extract<SplitTaskResponse, object>> = {}): SplitTaskResponse => ({task: {id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean()}, moved_task_ids: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.uuid())), ...overrideResponse})

export const getSetTaskStepAssigneeResponseMock = (overrideResponse: Partial<Extract<TaskView, object>> = {}): TaskView => ({id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean(), ...overrideResponse})

export const getClearTaskStepAssigneeResponseMock = (overrideResponse: Partial<Extract<TaskView, object>> = {}): TaskView => ({id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean(), ...overrideResponse})

export const getRenameTaskResponseMock = (overrideResponse: Partial<Extract<TaskView, object>> = {}): TaskView => ({id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean(), ...overrideResponse})

export const getSetTaskDescriptionResponseMock = (overrideResponse: Partial<Extract<TaskView, object>> = {}): TaskView => ({id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean(), ...overrideResponse})

export const getAddTaskCommentResponseMock = (overrideResponse: Partial<Extract<TaskCommentView, object>> = {}): TaskCommentView => ({id: faker.string.uuid(), task_id: faker.string.uuid(), body: faker.string.alpha({length: {min: 10, max: 20}}), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType)), actor: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', caused_by_signal_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), caused_by_trigger_profile_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), ...overrideResponse})

export const getListTaskCommentsResponseMock = (overrideResponse: Partial<Extract<TaskCommentList, object>> = {}): TaskCommentList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), task_id: faker.string.uuid(), body: faker.string.alpha({length: {min: 10, max: 20}}), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType)), actor: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', caused_by_signal_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), caused_by_trigger_profile_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined])})), ...overrideResponse})

export const getListTaskMovesResponseMock = (overrideResponse: Partial<Extract<TaskMoveList, object>> = {}): TaskMoveList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), task_id: faker.string.uuid(), from_position: faker.string.alpha({length: {min: 10, max: 20}}), to_position: faker.string.alpha({length: {min: 10, max: 20}}), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType)), reason: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), transition_id: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), task_version: faker.number.int(), caused_by_signal_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), caused_by_trigger_profile_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), ...overrideResponse})

export const getListRunActivityResponseChangeEntryMock = (overrideResponse: Partial<ChangeEntry> = {}): ChangeEntry => ({...{kind: "change", id: faker.string.uuid(), at: faker.date.past().toISOString().slice(0, 19) + 'Z', op: faker.helpers.arrayElement(['create','edit','delete','split'] as const), subtask_id: faker.string.uuid(), subtask_title: faker.string.alpha({length: {min: 10, max: 20}}), subtask_exists: faker.datatype.boolean(), changes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({field: faker.helpers.arrayElement(['title','description'] as const), before: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), after: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,])})), split: faker.helpers.arrayElement([faker.helpers.arrayElement([{direction: faker.helpers.arrayElement(['out','in'] as const), other_task_id: faker.string.uuid(), other_task_title: faker.string.alpha({length: {min: 10, max: 20}}), moved: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}})}))},null,]), undefined]), actor_type: faker.helpers.arrayElement(['user','ai','system'] as const), actor: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined])}, ...overrideResponse});

export const getListRunActivityResponseAiChangeGroupEntryMock = (overrideResponse: Partial<AiChangeGroupEntry> = {}): AiChangeGroupEntry => ({...{kind: "ai_group", execution_id: faker.string.uuid(), step_id: faker.string.alpha({length: {min: 10, max: 20}}), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), at: faker.date.past().toISOString().slice(0, 19) + 'Z', triggered_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), context_comment: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), author: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), excerpt: faker.string.alpha({length: {min: 10, max: 20}}), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z'},null,]), undefined]), changes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: "change", id: faker.string.uuid(), at: faker.date.past().toISOString().slice(0, 19) + 'Z', op: faker.helpers.arrayElement(['create','edit','delete','split'] as const), subtask_id: faker.string.uuid(), subtask_title: faker.string.alpha({length: {min: 10, max: 20}}), subtask_exists: faker.datatype.boolean(), changes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({field: faker.helpers.arrayElement(['title','description'] as const), before: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), after: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,])})), split: faker.helpers.arrayElement([faker.helpers.arrayElement([{direction: faker.helpers.arrayElement(['out','in'] as const), other_task_id: faker.string.uuid(), other_task_title: faker.string.alpha({length: {min: 10, max: 20}}), moved: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}})}))},null,]), undefined]), actor_type: faker.helpers.arrayElement(['user','ai','system'] as const), actor: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined])}))}, ...overrideResponse});

export const getListRunActivityResponseRerunEntryMock = (overrideResponse: Partial<RerunEntry> = {}): RerunEntry => ({...{kind: "rerun", execution_id: faker.string.uuid(), at: faker.date.past().toISOString().slice(0, 19) + 'Z', step_id: faker.string.alpha({length: {min: 10, max: 20}}), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), triggered_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined])}, ...overrideResponse});

export const getListRunActivityResponseStopEntryMock = (overrideResponse: Partial<StopEntry> = {}): StopEntry => ({...{kind: "stop", execution_id: faker.string.uuid(), at: faker.date.past().toISOString().slice(0, 19) + 'Z', step_id: faker.string.alpha({length: {min: 10, max: 20}}), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), stopped_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined])}, ...overrideResponse});

export const getListRunActivityResponseScheduleEntryMock = (overrideResponse: Partial<ScheduleEntry> = {}): ScheduleEntry => ({...{kind: "schedule", execution_id: faker.string.uuid(), at: faker.date.past().toISOString().slice(0, 19) + 'Z', reason: faker.string.alpha({length: {min: 10, max: 20}}), started: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}))}, ...overrideResponse});

export const getListRunActivityResponseSchedulerNoteEntryMock = (overrideResponse: Partial<SchedulerNoteEntry> = {}): SchedulerNoteEntry => ({...{kind: "note", id: faker.string.uuid(), at: faker.date.past().toISOString().slice(0, 19) + 'Z', body: faker.string.alpha({length: {min: 10, max: 20}}), author: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), used: faker.datatype.boolean()}, ...overrideResponse});

export const getListRunActivityResponseMock = (overrideResponse: Partial<Extract<RunActivityList, object>> = {}): RunActivityList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.helpers.arrayElement([{...getListRunActivityResponseChangeEntryMock()},{...getListRunActivityResponseAiChangeGroupEntryMock()},{...getListRunActivityResponseRerunEntryMock()},{...getListRunActivityResponseStopEntryMock()},{...getListRunActivityResponseScheduleEntryMock()},{...getListRunActivityResponseSchedulerNoteEntryMock()},]))), ...overrideResponse})

export const getAddSchedulerNoteResponseMock = (overrideResponse: Partial<Extract<SchedulerNoteEntry, object>> = {}): SchedulerNoteEntry => ({kind: "note", id: faker.string.uuid(), at: faker.date.past().toISOString().slice(0, 19) + 'Z', body: faker.string.alpha({length: {min: 10, max: 20}}), author: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), used: faker.datatype.boolean(), ...overrideResponse})

export const getGetExecutionTranscriptResponseTextEntryMock = (overrideResponse: Partial<TextEntry> = {}): TextEntry => ({...{type: "text", phase: faker.helpers.arrayElement(['main','transition_selection'] as const), role: faker.helpers.arrayElement(['assistant','user'] as const), content: faker.string.alpha({length: {min: 10, max: 20}})}, ...overrideResponse});

export const getGetExecutionTranscriptResponseToolCallEntryMock = (overrideResponse: Partial<ToolCallEntry> = {}): ToolCallEntry => ({...{type: "tool_call", phase: faker.helpers.arrayElement(['main','transition_selection'] as const), tool_name: faker.string.alpha({length: {min: 10, max: 20}}), input: {}, summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, ...overrideResponse});

export const getGetExecutionTranscriptResponseToolResultEntryMock = (overrideResponse: Partial<ToolResultEntry> = {}): ToolResultEntry => ({...{type: "tool_result", phase: faker.helpers.arrayElement(['main','transition_selection'] as const), tool_use_id: faker.string.alpha({length: {min: 10, max: 20}}), is_error: faker.datatype.boolean(), content: faker.string.alpha({length: {min: 10, max: 20}})}, ...overrideResponse});

export const getGetExecutionTranscriptResponseResultEntryMock = (overrideResponse: Partial<ResultEntry> = {}): ResultEntry => ({...{type: "result", phase: faker.helpers.arrayElement(['main','transition_selection'] as const), duration_ms: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.float({fractionDigits: 2}),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), stop_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), usage: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined])}, ...overrideResponse});

export const getGetExecutionTranscriptResponseMock = (overrideResponse: Partial<Extract<ExecutionTranscriptView, object>> = {}): ExecutionTranscriptView => ({transcript: faker.helpers.arrayElement([faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.helpers.arrayElement([{...getGetExecutionTranscriptResponseTextEntryMock()},{...getGetExecutionTranscriptResponseToolCallEntryMock()},{...getGetExecutionTranscriptResponseToolResultEntryMock()},{...getGetExecutionTranscriptResponseResultEntryMock()},]))),null,]), undefined]), prompt_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), ...overrideResponse})

export const getCreateTaskRelationResponseMock = (overrideResponse: Partial<Extract<TaskRelationView, object>> = {}): TaskRelationView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), parent_task_id: faker.string.uuid(), child_task_id: faker.string.uuid(), relation_type: faker.helpers.arrayElement(['plain','blocks','relates_to','duplicates'] as const), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', parent_task_title: faker.string.alpha({length: {min: 10, max: 20}}), parent_task_workflow_id: faker.string.uuid(), parent_task_hidden: faker.datatype.boolean(), child_task_title: faker.string.alpha({length: {min: 10, max: 20}}), child_task_workflow_id: faker.string.uuid(), child_task_hidden: faker.datatype.boolean(), ...overrideResponse})

export const getGetDirectTaskRelationsResponseMock = (overrideResponse: Partial<Extract<DirectTaskRelationsResponse, object>> = {}): DirectTaskRelationsResponse => ({task_id: faker.string.uuid(), relations: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), parent_task_id: faker.string.uuid(), child_task_id: faker.string.uuid(), relation_type: faker.helpers.arrayElement(['plain','blocks','relates_to','duplicates'] as const), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', parent_task_title: faker.string.alpha({length: {min: 10, max: 20}}), parent_task_workflow_id: faker.string.uuid(), parent_task_hidden: faker.datatype.boolean(), child_task_title: faker.string.alpha({length: {min: 10, max: 20}}), child_task_workflow_id: faker.string.uuid(), child_task_hidden: faker.datatype.boolean()})), ...overrideResponse})

export const getGetRelatedTasksResponseMock = (overrideResponse: Partial<Extract<RelatedTasksResponse, object>> = {}): RelatedTasksResponse => ({task_id: faker.string.uuid(), workflow_groups: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workflow_id: faker.string.uuid(), workflow_name: faker.string.alpha({length: {min: 10, max: 20}}), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), step_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskStatus)), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), relationship: faker.helpers.arrayElement(['parent','child'] as const), depth: faker.number.int({min: 1}), relation_type: faker.helpers.arrayElement(['plain','blocks','relates_to','duplicates'] as const), via_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined])})), siblings_available: faker.number.int()})), ...overrideResponse})

export const getCreateRelatedTaskResponseMock = (overrideResponse: Partial<Extract<CreateRelatedTaskResponse, object>> = {}): CreateRelatedTaskResponse => ({task: {id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean()}, relation: {id: faker.string.uuid(), workspace_id: faker.string.uuid(), parent_task_id: faker.string.uuid(), child_task_id: faker.string.uuid(), relation_type: faker.helpers.arrayElement(['plain','blocks','relates_to','duplicates'] as const), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', parent_task_title: faker.string.alpha({length: {min: 10, max: 20}}), parent_task_workflow_id: faker.string.uuid(), parent_task_hidden: faker.datatype.boolean(), child_task_title: faker.string.alpha({length: {min: 10, max: 20}}), child_task_workflow_id: faker.string.uuid(), child_task_hidden: faker.datatype.boolean()}, ...overrideResponse})

export const getGetTaskSiblingsResponseMock = (overrideResponse: Partial<Extract<SiblingsResponse, object>> = {}): SiblingsResponse => ({workflow_id: faker.string.uuid(), siblings: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), step_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskStatus))})), ...overrideResponse})

export const getReadTaskFollowResponseMock = (overrideResponse: Partial<Extract<TaskFollowState, object>> = {}): TaskFollowState => ({task_id: faker.string.uuid(), following: faker.datatype.boolean(), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FollowReason)),null,]), undefined]), ...overrideResponse})

export const getFollowTaskResponseMock = (overrideResponse: Partial<Extract<TaskFollowState, object>> = {}): TaskFollowState => ({task_id: faker.string.uuid(), following: faker.datatype.boolean(), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FollowReason)),null,]), undefined]), ...overrideResponse})

export const getUnfollowTaskResponseMock = (overrideResponse: Partial<Extract<TaskFollowState, object>> = {}): TaskFollowState => ({task_id: faker.string.uuid(), following: faker.datatype.boolean(), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FollowReason)),null,]), undefined]), ...overrideResponse})

export const getReplaceTaskLabelsResponseMock = (overrideResponse: Partial<Extract<TaskLabelList, object>> = {}): TaskLabelList => ({labels: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), ...overrideResponse})

export const getGetTaskResultsResponseMock = (overrideResponse: Partial<Extract<TaskResultsView, object>> = {}): TaskResultsView => ({executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({execution_id: faker.string.uuid(), step_id: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), started_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), ended_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), result: faker.helpers.arrayElement([{summary: faker.string.alpha({length: {min: 10, max: 20}}), links: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({url: faker.helpers.fromRegExp("^https?://"), label: faker.string.alpha({length: {min: 1, max: 200}}), kind: faker.helpers.arrayElement(['pull_request','merge_request','other'] as const)}))},null,]), artifacts: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({artifact_id: faker.string.uuid(), role: faker.helpers.arrayElement(['created','updated'] as const)})), undefined]), artifact_record_incomplete: faker.datatype.boolean()})), undefined]), files: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({path: faker.string.alpha({length: {min: 10, max: 20}}), event_type: faker.helpers.arrayElement(Object.values(FileChangeEventType)), source_path: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), occurred_at: faker.date.past().toISOString().slice(0, 19) + 'Z', step_id: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), step_name: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), execution_id: faker.helpers.arrayElement([faker.string.uuid(),null,])})), undefined]), also_changed: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({path: faker.string.alpha({length: {min: 10, max: 20}}), event_type: faker.helpers.arrayElement(Object.values(FileChangeEventType)), source_path: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), occurred_at: faker.date.past().toISOString().slice(0, 19) + 'Z', step_id: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), step_name: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), execution_id: faker.helpers.arrayElement([faker.string.uuid(),null,])})), undefined]), ...overrideResponse})

export const getGetTaskArtifactsResponseMock = (overrideResponse: Partial<Extract<TaskArtifactsView, object>> = {}): TaskArtifactsView => ({artifacts: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), kind: faker.helpers.arrayElement(['merge_request','pull_request','branch'] as const), forge_host: faker.string.alpha({length: {min: 10, max: 20}}), repository: faker.string.alpha({length: {min: 10, max: 20}}), number: faker.helpers.arrayElement([faker.number.int(),null,]), web_url: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), title: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), source_branch: faker.string.alpha({length: {min: 10, max: 20}}), target_branch: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), latest_commit: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), created_by_execution_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_by: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({execution_id: faker.string.uuid(), last_pushed_at: faker.date.past().toISOString().slice(0, 19) + 'Z', commit_sha: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,])})), undefined])})), undefined]), ...overrideResponse})

export const getPostRunThreadMessageResponseMock = (overrideResponse: Partial<Extract<ThreadMessageView, object>> = {}): ThreadMessageView => ({id: faker.string.uuid(), execution_id: faker.string.uuid(), author_kind: faker.helpers.arrayElement(Object.values(ThreadAuthorKind)), author: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), body: faker.string.alpha({length: {min: 10, max: 20}}), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', reply: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(Object.values(ReplyState)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), attempts: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({execution_id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined])})), undefined])},null,]), undefined]), answers_message_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), cut_short: faker.datatype.boolean(), reply_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), added_to_task_context_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), task_comment_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), ...overrideResponse})

export const getListRunThreadMessagesResponseMock = (overrideResponse: Partial<Extract<ThreadMessagePage, object>> = {}): ThreadMessagePage => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), execution_id: faker.string.uuid(), author_kind: faker.helpers.arrayElement(Object.values(ThreadAuthorKind)), author: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), body: faker.string.alpha({length: {min: 10, max: 20}}), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', reply: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(Object.values(ReplyState)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), attempts: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({execution_id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined])})), undefined])},null,]), undefined]), answers_message_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), cut_short: faker.datatype.boolean(), reply_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), added_to_task_context_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), task_comment_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined])})), next_before: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), ...overrideResponse})

export const getRetryRunThreadReplyResponseMock = (overrideResponse: Partial<Extract<ThreadMessageView, object>> = {}): ThreadMessageView => ({id: faker.string.uuid(), execution_id: faker.string.uuid(), author_kind: faker.helpers.arrayElement(Object.values(ThreadAuthorKind)), author: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), body: faker.string.alpha({length: {min: 10, max: 20}}), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', reply: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(Object.values(ReplyState)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), attempts: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({execution_id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined])})), undefined])},null,]), undefined]), answers_message_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), cut_short: faker.datatype.boolean(), reply_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), added_to_task_context_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), task_comment_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), ...overrideResponse})

export const getAddRunThreadMessageToTaskContextResponseMock = (overrideResponse: Partial<Extract<AddedToTaskContextView, object>> = {}): AddedToTaskContextView => ({comment_id: faker.string.uuid(), ...overrideResponse})

export const getAttachFileToTaskResponseMock = (overrideResponse: Partial<Extract<AttachmentView, object>> = {}): AttachmentView => ({name: faker.string.alpha({length: {min: 10, max: 20}}), path: faker.string.alpha({length: {min: 10, max: 20}}), size: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), content_type: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), modified_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getListTaskAttachmentsResponseMock = (overrideResponse: Partial<Extract<TaskAttachmentsView, object>> = {}): TaskAttachmentsView => ({folder_path: faker.string.alpha({length: {min: 10, max: 20}}), attachments: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({name: faker.string.alpha({length: {min: 10, max: 20}}), path: faker.string.alpha({length: {min: 10, max: 20}}), size: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), content_type: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), modified_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])})), undefined]), ...overrideResponse})

export const getListWorkspaceFilesResponseMock = (overrideResponse: Partial<Extract<FileListing, object>> = {}): FileListing => ({path: faker.string.alpha({length: {min: 10, max: 20}}), entries: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({name: faker.string.alpha({length: {min: 10, max: 20}}), path: faker.string.alpha({length: {min: 10, max: 20}}), kind: faker.helpers.arrayElement(['file','directory'] as const), size: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), content_type: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), modified_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])})), ...overrideResponse})

export const getReadWorkspaceFileContentResponseMock = (): unknown | ArrayBuffer => (faker.helpers.arrayElement([undefined, new ArrayBuffer(faker.number.int({ min: 1, max: 64 }))]))

export const getUploadWorkspaceFileResponseMock = (overrideResponse: Partial<Extract<FileChangeEventView, object>> = {}): FileChangeEventView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), path: faker.string.alpha({length: {min: 10, max: 20}}), event_type: faker.helpers.arrayElement(Object.values(FileChangeEventType)), actor: {kind: faker.helpers.arrayElement(['human','ai','unattributed'] as const), user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), user_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), task_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, occurred_at: faker.date.past().toISOString().slice(0, 19) + 'Z', source_path: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), ...overrideResponse})

export const getMoveWorkspaceFileResponseMock = (overrideResponse: Partial<Extract<MoveFileResult, object>> = {}): MoveFileResult => ({source_path: faker.string.alpha({length: {min: 10, max: 20}}), destination_path: faker.string.alpha({length: {min: 10, max: 20}}), kind: faker.helpers.arrayElement(['file','directory'] as const), moved_count: faker.number.int(), source_emptied: faker.datatype.boolean(), occurred_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getGetWorkspaceFileHistoryResponseMock = (overrideResponse: Partial<Extract<FileHistoryPage, object>> = {}): FileHistoryPage => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), path: faker.string.alpha({length: {min: 10, max: 20}}), event_type: faker.helpers.arrayElement(Object.values(FileChangeEventType)), actor: {kind: faker.helpers.arrayElement(['human','ai','unattributed'] as const), user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), user_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), task_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, occurred_at: faker.date.past().toISOString().slice(0, 19) + 'Z', source_path: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), total: faker.number.int(), limit: faker.number.int(), offset: faker.number.int(), ...overrideResponse})

export const getListWorkspaceSecretsResponseMock = (overrideResponse: Partial<Extract<WorkspaceSecretList, object>> = {}): WorkspaceSecretList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), kind: faker.helpers.arrayElement(Object.values(WorkspaceSecretKind)), value: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', managed_by: faker.helpers.arrayElement([{connection_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}})},null,])})), ...overrideResponse})

export const getCreateWorkspaceSecretResponseMock = (overrideResponse: Partial<Extract<WorkspaceSecretView, object>> = {}): WorkspaceSecretView => ({key: faker.string.alpha({length: {min: 10, max: 20}}), kind: faker.helpers.arrayElement(Object.values(WorkspaceSecretKind)), value: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', managed_by: faker.helpers.arrayElement([{connection_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), ...overrideResponse})

export const getReplaceWorkspaceSecretValueResponseMock = (overrideResponse: Partial<Extract<WorkspaceSecretView, object>> = {}): WorkspaceSecretView => ({key: faker.string.alpha({length: {min: 10, max: 20}}), kind: faker.helpers.arrayElement(Object.values(WorkspaceSecretKind)), value: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', managed_by: faker.helpers.arrayElement([{connection_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), ...overrideResponse})

export const getUpdateWorkspaceSecretDescriptionResponseMock = (overrideResponse: Partial<Extract<WorkspaceSecretView, object>> = {}): WorkspaceSecretView => ({key: faker.string.alpha({length: {min: 10, max: 20}}), kind: faker.helpers.arrayElement(Object.values(WorkspaceSecretKind)), value: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', managed_by: faker.helpers.arrayElement([{connection_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), ...overrideResponse})

export const getCreateTriggerProfileResponseMock = (overrideResponse: Partial<Extract<TriggerProfileView, object>> = {}): TriggerProfileView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), enabled: faker.datatype.boolean(), connection_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), target_workflow_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), schedule_type: faker.string.alpha({length: {min: 10, max: 20}}), interval_seconds: faker.helpers.arrayElement([faker.number.int(),null,]), cron_expression: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), timezone: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), config: {
        [faker.string.alphanumeric(5)]: {}
      }, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', deleted_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getListTriggerProfilesResponseMock = (): TriggerProfileView[] => (Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), enabled: faker.datatype.boolean(), connection_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), target_workflow_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), schedule_type: faker.string.alpha({length: {min: 10, max: 20}}), interval_seconds: faker.helpers.arrayElement([faker.number.int(),null,]), cron_expression: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), timezone: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), config: {
        [faker.string.alphanumeric(5)]: {}
      }, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', deleted_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])})))

export const getGetTriggerProfileResponseMock = (overrideResponse: Partial<Extract<TriggerProfileView, object>> = {}): TriggerProfileView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), enabled: faker.datatype.boolean(), connection_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), target_workflow_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), schedule_type: faker.string.alpha({length: {min: 10, max: 20}}), interval_seconds: faker.helpers.arrayElement([faker.number.int(),null,]), cron_expression: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), timezone: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), config: {
        [faker.string.alphanumeric(5)]: {}
      }, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', deleted_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getUpdateTriggerProfileResponseMock = (overrideResponse: Partial<Extract<TriggerProfileView, object>> = {}): TriggerProfileView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), enabled: faker.datatype.boolean(), connection_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), target_workflow_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), schedule_type: faker.string.alpha({length: {min: 10, max: 20}}), interval_seconds: faker.helpers.arrayElement([faker.number.int(),null,]), cron_expression: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), timezone: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), config: {
        [faker.string.alphanumeric(5)]: {}
      }, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', deleted_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getListTriggerProfileRunsResponseMock = (): TriggerProfileRunView[] => (Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), trigger_profile_id: faker.string.uuid(), status: faker.string.alpha({length: {min: 10, max: 20}}), started_at: faker.date.past().toISOString().slice(0, 19) + 'Z', completed_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), signals_created: faker.number.int(), signals_actioned: faker.number.int(), error: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), details: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      },null,])})))

export const getListTriggerProfileSignalsResponseMock = (overrideResponse: Partial<Extract<SignalListPage, object>> = {}): SignalListPage => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), external_key: faker.string.alpha({length: {min: 10, max: 20}}), related_task_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), status: faker.string.alpha({length: {min: 10, max: 20}}), payload: {
        [faker.string.alphanumeric(5)]: {}
      }, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', processed_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), error: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,])})), total: faker.number.int(), limit: faker.number.int(), offset: faker.number.int(), ...overrideResponse})

export const getGetVariableManifestResponseMock = (overrideResponse: Partial<Extract<VariableManifest, object>> = {}): VariableManifest => ({driver_type: faker.string.alpha({length: {min: 10, max: 20}}), groups: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({name: faker.string.alpha({length: {min: 10, max: 20}}), fields: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}))})), ...overrideResponse})

export const getCreateLabelResponseMock = (overrideResponse: Partial<Extract<LabelView, object>> = {}): LabelView => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getUpdateLabelResponseMock = (overrideResponse: Partial<Extract<LabelView, object>> = {}): LabelView => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getReadWorkspaceUsageResponseMock = (overrideResponse: Partial<Extract<WorkspaceUsageSummary, object>> = {}): WorkspaceUsageSummary => ({period: {key: faker.string.alpha({length: {min: 10, max: 20}}), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), starts_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ends_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_current: faker.datatype.boolean()}, totals: {input_tokens: faker.number.int(), output_tokens: faker.number.int(), cache_read_tokens: faker.number.int(), cache_creation_tokens: faker.number.int(), total_tokens: faker.number.int()}, run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), spend: {amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), unmeasured_run_count: faker.number.int(), unpriced_run_count: faker.number.int()}, limit: faker.helpers.arrayElement([{amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), is_override: faker.datatype.boolean(), consumed_amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), consumed_ratio: faker.number.float({fractionDigits: 2}), threshold: faker.helpers.arrayElement(['under','approaching','exhausted'] as const), period: {key: faker.string.alpha({length: {min: 10, max: 20}}), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), starts_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ends_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_current: faker.datatype.boolean()}},null,]), by_day: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({date: faker.date.past().toISOString().slice(0, 10), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), parts: faker.helpers.arrayElement([faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), deleted: faker.datatype.boolean(), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int()})),null,]), undefined])})), by_workflow: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), threshold: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['under','approaching','exhausted'] as const),null,]), undefined]), limit_consumed_amount: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), limit_period: faker.helpers.arrayElement([faker.helpers.arrayElement([{key: faker.string.alpha({length: {min: 10, max: 20}}), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), starts_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ends_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_current: faker.datatype.boolean()},null,]), undefined]), deleted: faker.datatype.boolean()})), by_model: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int()})), by_person: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int()})), earliest_period_key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), ...overrideResponse})

export const getReadWorkspaceAiModelsResponseMock = (overrideResponse: Partial<Extract<WorkspaceAIModels, object>> = {}): WorkspaceAIModels => ({period: {key: faker.string.alpha({length: {min: 10, max: 20}}), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), starts_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ends_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_current: faker.datatype.boolean()}, earliest_period_key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), entries: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({profile: faker.helpers.arrayElement([{slug: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), ai_provider_id: faker.string.uuid(), model: faker.string.alpha({length: {min: 10, max: 20}}), effort: faker.helpers.arrayElement(Object.values(LLMProfileEffort)), max_turns: faker.helpers.arrayElement([faker.number.int(),null,]), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z'},null,]), deleted: faker.datatype.boolean(), deleted_profile: faker.helpers.arrayElement([{key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), uses: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workflow_id: faker.string.uuid(), workflow_key: faker.string.alpha({length: {min: 10, max: 20}}), workflow_name: faker.string.alpha({length: {min: 10, max: 20}}), draft: faker.datatype.boolean(), steps: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), unpublished: faker.datatype.boolean()}))})), runs: {run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), input_tokens: faker.number.int(), output_tokens: faker.number.int(), cache_read_tokens: faker.number.int(), cache_creation_tokens: faker.number.int()}, by_model: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({model_key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), runs: {run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), input_tokens: faker.number.int(), output_tokens: faker.number.int(), cache_read_tokens: faker.number.int(), cache_creation_tokens: faker.number.int()}}))})), workflows: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workflow_id: faker.string.uuid(), workflow_key: faker.string.alpha({length: {min: 10, max: 20}}), workflow_name: faker.string.alpha({length: {min: 10, max: 20}}), draft: faker.datatype.boolean(), runs: {run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), input_tokens: faker.number.int(), output_tokens: faker.number.int(), cache_read_tokens: faker.number.int(), cache_creation_tokens: faker.number.int()}, steps: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(['ai_step','human_gate','linked_workflow'] as const), order: faker.number.int(), profile_slug: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), unpublished: faker.datatype.boolean()}))})), undefined]), ...overrideResponse})

export const getReadWorkspaceAiModelSpendResponseMock = (overrideResponse: Partial<Extract<WorkspaceAIModelSpend, object>> = {}): WorkspaceAIModelSpend => ({period: {key: faker.string.alpha({length: {min: 10, max: 20}}), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), starts_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ends_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_current: faker.datatype.boolean()}, by_workflow: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), deleted: faker.datatype.boolean(), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), unpriced_run_count: faker.number.int(), children: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), deleted: faker.datatype.boolean(), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), unpriced_run_count: faker.number.int()}))})), by_profile: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), deleted: faker.datatype.boolean(), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), unpriced_run_count: faker.number.int(), children: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), deleted: faker.datatype.boolean(), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), unpriced_run_count: faker.number.int()}))})), by_connection: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), deleted: faker.datatype.boolean(), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), unpriced_run_count: faker.number.int(), children: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), deleted: faker.datatype.boolean(), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int(), unpriced_run_count: faker.number.int()}))})), by_day: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({date: faker.date.past().toISOString().slice(0, 10), parts: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), label: faker.string.alpha({length: {min: 10, max: 20}}), deleted: faker.datatype.boolean(), amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), total_tokens: faker.number.int(), run_count: faker.number.int(), unmeasured_run_count: faker.number.int()}))})), ...overrideResponse})

export const getReadOrganizationUsageResponseMock = (overrideResponse: Partial<Extract<OrganizationUsageSummary, object>> = {}): OrganizationUsageSummary => ({period: {key: faker.string.alpha({length: {min: 10, max: 20}}), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), starts_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ends_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_current: faker.datatype.boolean()}, spend: {amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), unmeasured_run_count: faker.number.int(), unpriced_run_count: faker.number.int()}, totals: {input_tokens: faker.number.int(), output_tokens: faker.number.int(), cache_read_tokens: faker.number.int(), cache_creation_tokens: faker.number.int(), total_tokens: faker.number.int()}, default_limit: faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), workspaces_at_limit: faker.number.int(), workspaces_approaching_limit: faker.number.int(), workspaces: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workspace_id: faker.string.uuid(), workspace_name: faker.string.alpha({length: {min: 10, max: 20}}), spend: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), limit: faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), is_override: faker.datatype.boolean(), threshold: faker.helpers.arrayElement([faker.helpers.arrayElement(['under','approaching','exhausted'] as const),null,]), total_tokens: faker.number.int(), period_type: faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(PeriodType)),null,]), limit_consumed_amount: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), limit_period: faker.helpers.arrayElement([faker.helpers.arrayElement([{key: faker.string.alpha({length: {min: 10, max: 20}}), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), starts_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ends_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_current: faker.datatype.boolean()},null,]), undefined]), default_consumed_amount: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), default_period: faker.helpers.arrayElement([faker.helpers.arrayElement([{key: faker.string.alpha({length: {min: 10, max: 20}}), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), starts_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ends_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_current: faker.datatype.boolean()},null,]), undefined]), deleted: faker.datatype.boolean()})), earliest_period_key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), ai_providers_count: faker.number.int(), ...overrideResponse})

export const getSetModelPriceResponseMock = (overrideResponse: Partial<Extract<ModelPriceView, object>> = {}): ModelPriceView => ({id: faker.string.uuid(), model_key: faker.string.alpha({length: {min: 10, max: 20}}), effective_from: faker.date.past().toISOString().slice(0, 19) + 'Z', input_rate: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), output_rate: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), cache_read_rate: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), cache_creation_rate: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', created_by_user_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), ...overrideResponse})

export const getReadUnpricedModelsResponseMock = (overrideResponse: Partial<Extract<UnpricedModelsView, object>> = {}): UnpricedModelsView => ({period: {key: faker.string.alpha({length: {min: 10, max: 20}}), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), starts_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ends_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_current: faker.datatype.boolean()}, models: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({model_key: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), run_count: faker.number.int(), total_tokens: faker.number.int()})), ...overrideResponse})

export const getReadModelPricesResponseMock = (overrideResponse: Partial<Extract<ModelPricesView, object>> = {}): ModelPricesView => ({prices: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), model_key: faker.string.alpha({length: {min: 10, max: 20}}), effective_from: faker.date.past().toISOString().slice(0, 19) + 'Z', input_rate: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), output_rate: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), cache_read_rate: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), cache_creation_rate: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', created_by_user_id: faker.helpers.arrayElement([faker.string.uuid(),null,])})), ...overrideResponse})

export const getReadWorkflowLimitsResponseMock = (): WorkflowLimitRow[] => (Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workflow_id: faker.string.uuid(), workflow_name: faker.string.alpha({length: {min: 10, max: 20}}), workspace_id: faker.string.uuid(), workspace_name: faker.string.alpha({length: {min: 10, max: 20}}), spend: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), limit: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), threshold: faker.helpers.arrayElement(['under','approaching','exhausted'] as const), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), limit_consumed_amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), limit_period: {key: faker.string.alpha({length: {min: 10, max: 20}}), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), starts_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ends_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_current: faker.datatype.boolean()}})))

export const getSetWorkflowLimitResponseMock = (overrideResponse: Partial<Extract<WorkflowLimitView, object>> = {}): WorkflowLimitView => ({amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), blocks_workflows: faker.number.int(), exceeds_workspace_limit: faker.datatype.boolean(), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), ...overrideResponse})

export const getSetDefaultLimitResponseMock = (overrideResponse: Partial<Extract<LimitView, object>> = {}): LimitView => ({amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), blocks_workspaces: faker.number.int(), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), ...overrideResponse})

export const getSetWorkspaceLimitResponseMock = (overrideResponse: Partial<Extract<LimitView, object>> = {}): LimitView => ({amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), blocks_workspaces: faker.number.int(), period_type: faker.helpers.arrayElement(Object.values(PeriodType)), ...overrideResponse})

export const getReadWorkspaceConcurrencyResponseMock = (overrideResponse: Partial<Extract<WorkspaceConcurrencyView, object>> = {}): WorkspaceConcurrencyView => ({limit: faker.number.int(), source: faker.helpers.arrayElement(Object.values(LimitSource)), clamped: faker.datatype.boolean(), ceiling: faker.number.int(), running: faker.number.int(), queued: faker.number.int(), ...overrideResponse})

export const getReadOrganizationConcurrencyResponseMock = (overrideResponse: Partial<Extract<OrganizationConcurrencyView, object>> = {}): OrganizationConcurrencyView => ({default: faker.helpers.arrayElement([faker.number.int(),null,]), fallback: faker.number.int(), ceiling: faker.number.int(), workspaces: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workspace_id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), limit: faker.number.int(), source: faker.helpers.arrayElement(Object.values(LimitSource)), clamped: faker.datatype.boolean(), running: faker.number.int(), queued: faker.number.int()})), ...overrideResponse})

export const getSetDefaultConcurrencyLimitResponseMock = (overrideResponse: Partial<Extract<ConcurrencyLimitView, object>> = {}): ConcurrencyLimitView => ({workspace_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), value: faker.number.int(), ...overrideResponse})

export const getSetWorkspaceConcurrencyLimitResponseMock = (overrideResponse: Partial<Extract<ConcurrencyLimitView, object>> = {}): ConcurrencyLimitView => ({workspace_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), value: faker.number.int(), ...overrideResponse})

export const getReadWorkspaceMemoryResponseMock = (overrideResponse: Partial<Extract<WorkspaceMemoryView, object>> = {}): WorkspaceMemoryView => ({limit_mib: faker.number.int(), unit: faker.helpers.arrayElement(['MiB','GiB'] as const), source: faker.helpers.arrayElement(Object.values(LimitSource)), minimum_mib: faker.number.int(), ...overrideResponse})

export const getReadOrganizationMemoryResponseMock = (overrideResponse: Partial<Extract<OrganizationMemoryView, object>> = {}): OrganizationMemoryView => ({default_mib: faker.helpers.arrayElement([faker.number.int(),null,]), default_unit: faker.helpers.arrayElement([faker.helpers.arrayElement(['MiB','GiB'] as const),null,]), fallback_mib: faker.number.int(), minimum_mib: faker.number.int(), workspaces: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workspace_id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), limit_mib: faker.number.int(), unit: faker.helpers.arrayElement(['MiB','GiB'] as const), source: faker.helpers.arrayElement(Object.values(LimitSource))})), ...overrideResponse})

export const getSetDefaultMemoryLimitResponseMock = (overrideResponse: Partial<Extract<MemoryLimitView, object>> = {}): MemoryLimitView => ({workspace_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), value_mib: faker.number.int(), unit: faker.helpers.arrayElement(['MiB','GiB'] as const), ...overrideResponse})

export const getSetWorkspaceMemoryLimitResponseMock = (overrideResponse: Partial<Extract<MemoryLimitView, object>> = {}): MemoryLimitView => ({workspace_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), value_mib: faker.number.int(), unit: faker.helpers.arrayElement(['MiB','GiB'] as const), ...overrideResponse})

export const getReadOrganizationResponseMock = (overrideResponse: Partial<Extract<OrganizationView, object>> = {}): OrganizationView => ({name: faker.string.alpha({length: {min: 10, max: 20}}), environment: faker.helpers.arrayElement(Object.values(OrganizationEnvironment)), updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getUpdateOrganizationResponseMock = (overrideResponse: Partial<Extract<OrganizationView, object>> = {}): OrganizationView => ({name: faker.string.alpha({length: {min: 10, max: 20}}), environment: faker.helpers.arrayElement(Object.values(OrganizationEnvironment)), updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getListOrganizationWorkspacesResponseMock = (overrideResponse: Partial<Extract<AdminWorkspaces, object>> = {}): AdminWorkspaces => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), visibility: faker.helpers.arrayElement(Object.values(WorkspaceVisibility)), owners: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({user: {id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})}, is_active: faker.datatype.boolean()})), member_count: faker.number.int(), has_active_owner: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', last_activity_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), without_active_owner: faker.number.int(), total: faker.number.int(), ...overrideResponse})

export const getExportOrganizationWorkspacesResponseMock = (): string => (faker.word.sample())

export const getReadOrganizationAccessLogResponseMock = (overrideResponse: Partial<Extract<AccessLogPage, object>> = {}): AccessLogPage => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workspace: {id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}})}, kind: faker.helpers.arrayElement(Object.values(AccessEventKind)), at: faker.date.past().toISOString().slice(0, 19) + 'Z', actor: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), subject: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), as_org_admin: faker.datatype.boolean(), detail: {}})), next_before: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), ...overrideResponse})

export const getExportOrganizationAccessLogResponseMock = (): string => (faker.word.sample())

export const getListLlmProfilesResponseMock = (overrideResponse: Partial<Extract<LLMProfileList, object>> = {}): LLMProfileList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({slug: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), ai_provider_id: faker.string.uuid(), model: faker.string.alpha({length: {min: 10, max: 20}}), effort: faker.helpers.arrayElement(Object.values(LLMProfileEffort)), max_turns: faker.helpers.arrayElement([faker.number.int(),null,]), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), ...overrideResponse})

export const getCreateLlmProfileResponseMock = (overrideResponse: Partial<Extract<LLMProfileView, object>> = {}): LLMProfileView => ({slug: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), ai_provider_id: faker.string.uuid(), model: faker.string.alpha({length: {min: 10, max: 20}}), effort: faker.helpers.arrayElement(Object.values(LLMProfileEffort)), max_turns: faker.helpers.arrayElement([faker.number.int(),null,]), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getReadLlmProfilesAdminOverviewResponseLLMProfileAdminOverviewItemMock = (overrideResponse: Partial<LLMProfileAdminOverviewItem> = {}): LLMProfileAdminOverviewItem => ({...{slug: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), model: faker.string.alpha({length: {min: 10, max: 20}}), effort: faker.helpers.arrayElement(Object.values(LLMProfileEffort)), max_turns: faker.helpers.arrayElement([faker.number.int(),null,]), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ai_provider: {id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), provider_type: faker.helpers.arrayElement(Object.values(AIProviderType)), connection_status: faker.helpers.arrayElement(Object.values(AIProviderConnectionStatus)), is_default: faker.datatype.boolean()}, spend: {amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), unmeasured_run_count: faker.number.int(), unpriced_run_count: faker.number.int(), unattributed_run_count: faker.number.int()}, tokens: {input_tokens: faker.number.int(), output_tokens: faker.number.int(), cache_read_tokens: faker.number.int(), cache_creation_tokens: faker.number.int(), total_tokens: faker.number.int()}, usage: {workspace_count: faker.number.int(), workflow_count: faker.number.int(), workflow_step_count: faker.number.int(), workspaces: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}})})), workflows: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), workspace_id: faker.string.uuid(), workspace_name: faker.string.alpha({length: {min: 10, max: 20}})}))}, deleted: false}, ...overrideResponse});

export const getReadLlmProfilesAdminOverviewResponseLLMProfileDeletedAdminOverviewItemMock = (overrideResponse: Partial<LLMProfileDeletedAdminOverviewItem> = {}): LLMProfileDeletedAdminOverviewItem => ({...{deleted: true, key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), spend: {amount: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), currency: faker.string.alpha({length: {min: 10, max: 20}}), unmeasured_run_count: faker.number.int(), unpriced_run_count: faker.number.int(), unattributed_run_count: faker.number.int()}, tokens: {input_tokens: faker.number.int(), output_tokens: faker.number.int(), cache_read_tokens: faker.number.int(), cache_creation_tokens: faker.number.int(), total_tokens: faker.number.int()}}, ...overrideResponse});

export const getReadLlmProfilesAdminOverviewResponseMock = (overrideResponse: Partial<Extract<LLMProfileAdminOverview, object>> = {}): LLMProfileAdminOverview => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.helpers.arrayElement([{...getReadLlmProfilesAdminOverviewResponseLLMProfileAdminOverviewItemMock()},{...getReadLlmProfilesAdminOverviewResponseLLMProfileDeletedAdminOverviewItemMock()},]))), ...overrideResponse})

export const getUpdateLlmProfileResponseMock = (overrideResponse: Partial<Extract<LLMProfileView, object>> = {}): LLMProfileView => ({slug: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), ai_provider_id: faker.string.uuid(), model: faker.string.alpha({length: {min: 10, max: 20}}), effort: faker.helpers.arrayElement(Object.values(LLMProfileEffort)), max_turns: faker.helpers.arrayElement([faker.number.int(),null,]), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getListAiProvidersResponseMock = (overrideResponse: Partial<Extract<AIProviderList, object>> = {}): AIProviderList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), provider_type: faker.helpers.arrayElement(Object.values(AIProviderType)), base_url: faker.string.alpha({length: {min: 10, max: 20}}), has_token: faker.datatype.boolean(), connection_status: faker.helpers.arrayElement(Object.values(AIProviderConnectionStatus)), connection_message: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), connection_checked_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), ...overrideResponse})

export const getCreateAiProviderResponseMock = (overrideResponse: Partial<Extract<AIProviderView, object>> = {}): AIProviderView => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), provider_type: faker.helpers.arrayElement(Object.values(AIProviderType)), base_url: faker.string.alpha({length: {min: 10, max: 20}}), has_token: faker.datatype.boolean(), connection_status: faker.helpers.arrayElement(Object.values(AIProviderConnectionStatus)), connection_message: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), connection_checked_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getGetAiProviderResponseMock = (overrideResponse: Partial<Extract<AIProviderView, object>> = {}): AIProviderView => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), provider_type: faker.helpers.arrayElement(Object.values(AIProviderType)), base_url: faker.string.alpha({length: {min: 10, max: 20}}), has_token: faker.datatype.boolean(), connection_status: faker.helpers.arrayElement(Object.values(AIProviderConnectionStatus)), connection_message: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), connection_checked_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getUpdateAiProviderResponseMock = (overrideResponse: Partial<Extract<AIProviderView, object>> = {}): AIProviderView => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), provider_type: faker.helpers.arrayElement(Object.values(AIProviderType)), base_url: faker.string.alpha({length: {min: 10, max: 20}}), has_token: faker.datatype.boolean(), connection_status: faker.helpers.arrayElement(Object.values(AIProviderConnectionStatus)), connection_message: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), connection_checked_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getTestAiProviderConnectionResponseMock = (overrideResponse: Partial<Extract<AIProviderConnectionTestResult, object>> = {}): AIProviderConnectionTestResult => ({ok: faker.datatype.boolean(), message: faker.string.alpha({length: {min: 10, max: 20}}), ...overrideResponse})

export const getCreateConnectionResponseMock = (overrideResponse: Partial<Extract<ConnectionView, object>> = {}): ConnectionView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), enabled: faker.datatype.boolean(), config: {
        [faker.string.alphanumeric(5)]: {}
      }, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', trigger_profile_count: faker.number.int(), exposed_env: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), health: faker.helpers.arrayElement([faker.helpers.arrayElement([{status: faker.helpers.arrayElement(Object.values(ConnectionHealthStatus)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), checked_at: faker.date.past().toISOString().slice(0, 19) + 'Z'},null,]), undefined]), ...overrideResponse})

export const getListConnectionsResponseMock = (): ConnectionView[] => (Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), enabled: faker.datatype.boolean(), config: {
        [faker.string.alphanumeric(5)]: {}
      }, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', trigger_profile_count: faker.number.int(), exposed_env: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), health: faker.helpers.arrayElement([faker.helpers.arrayElement([{status: faker.helpers.arrayElement(Object.values(ConnectionHealthStatus)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), checked_at: faker.date.past().toISOString().slice(0, 19) + 'Z'},null,]), undefined])})))

export const getListConnectionProvidersResponseMock = (): ConnectionProviderView[] => (Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({type: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}}), category: faker.string.alpha({length: {min: 10, max: 20}}), blurb: faker.string.alpha({length: {min: 10, max: 20}}), fields: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({name: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.string.alpha({length: {min: 10, max: 20}}), kind: faker.helpers.arrayElement(['secret','url','text','toggle'] as const), required: faker.datatype.boolean(), advanced: faker.datatype.boolean(), default: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), help: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), env: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), depends_on: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), exposed_env: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), token_env: faker.string.alpha({length: {min: 10, max: 20}}), token_url: faker.string.alpha({length: {min: 10, max: 20}}), token_scopes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), multi_instance: faker.datatype.boolean()})))

export const getGetConnectionResponseMock = (overrideResponse: Partial<Extract<ConnectionView, object>> = {}): ConnectionView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), enabled: faker.datatype.boolean(), config: {
        [faker.string.alphanumeric(5)]: {}
      }, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', trigger_profile_count: faker.number.int(), exposed_env: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), health: faker.helpers.arrayElement([faker.helpers.arrayElement([{status: faker.helpers.arrayElement(Object.values(ConnectionHealthStatus)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), checked_at: faker.date.past().toISOString().slice(0, 19) + 'Z'},null,]), undefined]), ...overrideResponse})

export const getUpdateConnectionResponseMock = (overrideResponse: Partial<Extract<ConnectionView, object>> = {}): ConnectionView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), type: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), enabled: faker.datatype.boolean(), config: {
        [faker.string.alphanumeric(5)]: {}
      }, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', trigger_profile_count: faker.number.int(), exposed_env: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), health: faker.helpers.arrayElement([faker.helpers.arrayElement([{status: faker.helpers.arrayElement(Object.values(ConnectionHealthStatus)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), checked_at: faker.date.past().toISOString().slice(0, 19) + 'Z'},null,]), undefined]), ...overrideResponse})

export const getTestConnectionResponseMock = (overrideResponse: Partial<Extract<ConnectionTestResult, object>> = {}): ConnectionTestResult => ({ok: faker.datatype.boolean(), message: faker.string.alpha({length: {min: 10, max: 20}}), ...overrideResponse})

export const getCreateExecutionImageResponseMock = (overrideResponse: Partial<Extract<ExecutionImageView, object>> = {}): ExecutionImageView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), connection_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), image_reference: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getListExecutionImagesResponseMock = (): ExecutionImageView[] => (Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), connection_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), image_reference: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})))

export const getGetExecutionImageResponseMock = (overrideResponse: Partial<Extract<ExecutionImageView, object>> = {}): ExecutionImageView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), connection_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), image_reference: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getUpdateExecutionImageResponseMock = (overrideResponse: Partial<Extract<ExecutionImageView, object>> = {}): ExecutionImageView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), connection_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), image_reference: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getSetDefaultExecutionImageResponseMock = (overrideResponse: Partial<Extract<ExecutionImageView, object>> = {}): ExecutionImageView => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), connection_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), image_reference: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_default: faker.datatype.boolean(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getListLibraryItemsResponseMock = (overrideResponse: Partial<Extract<LibraryCatalogView, object>> = {}): LibraryCatalogView => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(LibraryItemKind)), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.string.alpha({length: {min: 10, max: 20}}), digest: faker.string.alpha({length: {min: 10, max: 20}}), installable: faker.datatype.boolean(), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), requires: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(LibraryItemKind)), key: faker.string.alpha({length: {min: 10, max: 20}})})), undefined]), state: faker.helpers.arrayElement(Object.values(LibraryItemState)), files: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({path: faker.string.alpha({length: {min: 10, max: 20}}), size: faker.number.int()})), undefined]), origin: faker.helpers.arrayElement(Object.values(LibraryItemOrigin)), shared_by: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), shared_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), can_take_down: faker.datatype.boolean()})), ...overrideResponse})

export const getReadLibraryItemFileResponseMock = (overrideResponse: Partial<Extract<LibraryFileView, object>> = {}): LibraryFileView => ({path: faker.string.alpha({length: {min: 10, max: 20}}), content_type: faker.string.alpha({length: {min: 10, max: 20}}), text: faker.string.alpha({length: {min: 10, max: 20}}), size: faker.number.int(), truncated: faker.datatype.boolean(), ...overrideResponse})

export const getInstallLibraryItemResponseMock = (overrideResponse: Partial<Extract<LibraryInstallResult, object>> = {}): LibraryInstallResult => ({blocked: faker.datatype.boolean(), outcomes: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(LibraryItemKind)), key: faker.string.alpha({length: {min: 10, max: 20}}), outcome: faker.helpers.arrayElement(Object.values(LibraryOutcome)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), undefined]), unresolved: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(ReferenceKind)), value: faker.string.alpha({length: {min: 10, max: 20}}), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocking: faker.datatype.boolean()})), undefined]), substitutions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(ReferenceKind)), from_value: faker.string.alpha({length: {min: 10, max: 20}}), to_value: faker.string.alpha({length: {min: 10, max: 20}}), step_ids: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), undefined])})), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), ...overrideResponse})

export const getUpdateLibraryItemResponseMock = (overrideResponse: Partial<Extract<LibraryInstallResult, object>> = {}): LibraryInstallResult => ({blocked: faker.datatype.boolean(), outcomes: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(LibraryItemKind)), key: faker.string.alpha({length: {min: 10, max: 20}}), outcome: faker.helpers.arrayElement(Object.values(LibraryOutcome)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), undefined]), unresolved: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(ReferenceKind)), value: faker.string.alpha({length: {min: 10, max: 20}}), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocking: faker.datatype.boolean()})), undefined]), substitutions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(ReferenceKind)), from_value: faker.string.alpha({length: {min: 10, max: 20}}), to_value: faker.string.alpha({length: {min: 10, max: 20}}), step_ids: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), undefined])})), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), ...overrideResponse})

export const getPreviewWorkflowBundleResponseMock = (overrideResponse: Partial<Extract<BundlePreview, object>> = {}): BundlePreview => ({root: faker.string.alpha({length: {min: 10, max: 20}}), workflows: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), root: faker.datatype.boolean(), has_pending_changes: faker.datatype.boolean()})), agents: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(['agent','skill'] as const), key: faker.string.alpha({length: {min: 10, max: 20}}), suggested: faker.datatype.boolean()})), skills: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(['agent','skill'] as const), key: faker.string.alpha({length: {min: 10, max: 20}}), suggested: faker.datatype.boolean()})), blockers: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), ...overrideResponse})

export const getExportWorkflowBundleResponseMock = (): ArrayBuffer => (new ArrayBuffer(faker.number.int({ min: 1, max: 64 })))

export const getPreflightBundleImportResponseMock = (overrideResponse: Partial<Extract<BundleImportPreview, object>> = {}): BundleImportPreview => ({root: faker.string.alpha({length: {min: 10, max: 20}}), source_workspace: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), exported_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(['workflow','agent','skill'] as const), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), root: faker.datatype.boolean(), state: faker.helpers.arrayElement(Object.values(BundleItemState)), answer: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['use_existing','rename','replace'] as const),null,]), undefined]), new_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), suggested_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), used_by: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), undefined])})), unresolved: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(ReferenceKind)), value: faker.string.alpha({length: {min: 10, max: 20}}), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocking: faker.datatype.boolean()})), undefined]), violations: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({category: faker.helpers.arrayElement(Object.values(ViolationCategory)), message: faker.string.alpha({length: {min: 10, max: 20}}), path: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), line: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), column: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), actual: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), phase_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), field: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), undefined]), blockers: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), undefined]), importable: faker.datatype.boolean(), ...overrideResponse})

export const getImportBundleResponseMock = (overrideResponse: Partial<Extract<BundleImportResult, object>> = {}): BundleImportResult => ({blocked: faker.datatype.boolean(), outcomes: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(LibraryItemKind)), key: faker.string.alpha({length: {min: 10, max: 20}}), outcome: faker.helpers.arrayElement(Object.values(LibraryOutcome)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), undefined]), unresolved: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(ReferenceKind)), value: faker.string.alpha({length: {min: 10, max: 20}}), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocking: faker.datatype.boolean()})), undefined]), substitutions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(Object.values(ReferenceKind)), from_value: faker.string.alpha({length: {min: 10, max: 20}}), to_value: faker.string.alpha({length: {min: 10, max: 20}}), step_ids: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), undefined])})), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), display_order: faker.number.int(), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', inputs: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), input_type: faker.helpers.arrayElement(Object.values(WorkflowInputType)), required: faker.datatype.boolean(), display_order: faker.number.int(), default_value: faker.helpers.arrayElement([faker.helpers.arrayElement([null,]), undefined]), configuration: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {}
      }, undefined])})), phases: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), steps: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), prompt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), llm: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), mode: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(WorkflowGateMode)),null,]), undefined]), assignee_user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), workflow: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), start_children: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(StartChildren)),null,]), undefined]), scheduling_instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 4000}}),null,]), undefined]), transitions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({key: faker.string.alpha({length: {min: 10, max: 20}}), to: faker.string.alpha({length: {min: 10, max: 20}}), when: faker.string.alpha({length: {min: 10, max: 20}}), label: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), traversal_limit: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined])})), undefined])})), undefined])})), start_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, done_step: {id: faker.string.alpha({length: {min: 10, max: 20}}), type: faker.helpers.arrayElement(Object.values(WorkflowStepType)), name: faker.string.alpha({length: {min: 10, max: 20}})}, status: faker.helpers.arrayElement(Object.values(WorkflowStatus)), version: faker.number.int(), layout: faker.helpers.arrayElement([faker.helpers.arrayElement([{nodes: faker.helpers.arrayElement([{
        [faker.string.alphanumeric(5)]: {x: faker.number.float({fractionDigits: 2}), y: faker.number.float({fractionDigits: 2})}
      }, undefined])},null,]), undefined]), start: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), image: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), has_pending_changes: faker.datatype.boolean(), pending_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), pending_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), pending_outdated: faker.datatype.boolean(), last_saved_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), last_saved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), ...overrideResponse})

export const getPreviewShareToLibraryResponseMock = (overrideResponse: Partial<Extract<SharePreview, object>> = {}): SharePreview => ({pieces: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(['workflow','agent','skill'] as const), key: faker.string.alpha({length: {min: 10, max: 20}}), outcome: faker.helpers.arrayElement(Object.values(ShareOutcome)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), can_use_library: faker.datatype.boolean()})), undefined]), blockers: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.alpha({length: {min: 10, max: 20}}))), undefined]), shareable: faker.datatype.boolean(), ...overrideResponse})

export const getShareToLibraryResponseMock = (overrideResponse: Partial<Extract<ShareResult, object>> = {}): ShareResult => ({pieces: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(['workflow','agent','skill'] as const), key: faker.string.alpha({length: {min: 10, max: 20}}), outcome: faker.helpers.arrayElement(Object.values(ShareOutcome)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), can_use_library: faker.datatype.boolean()})), ...overrideResponse})

export const getPreviewTakeDownSetResponseMock = (overrideResponse: Partial<Extract<TakeDownPreview, object>> = {}): TakeDownPreview => ({item: {kind: faker.helpers.arrayElement(['workflow','agent','skill'] as const), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}})}, also: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(['workflow','agent','skill'] as const), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}})})), undefined]), staying: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(['workflow','agent','skill'] as const), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}}), reason: faker.string.alpha({length: {min: 10, max: 20}})})), undefined]), ...overrideResponse})

export const getTakeDownSetResponseMock = (overrideResponse: Partial<Extract<TakeDownResult, object>> = {}): TakeDownResult => ({taken_down: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({kind: faker.helpers.arrayElement(['workflow','agent','skill'] as const), key: faker.string.alpha({length: {min: 10, max: 20}}), name: faker.string.alpha({length: {min: 10, max: 20}})})), ...overrideResponse})

export const getListNotificationsResponseMock = (overrideResponse: Partial<Extract<NotificationPage, object>> = {}): NotificationPage => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), kind: faker.helpers.arrayElement(Object.values(NotificationKind)), group: faker.helpers.arrayElement(Object.values(NotificationGroup)), workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, task_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), workflow_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), actor: faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), payload: {task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), child_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), child_task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), spend_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), trigger_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_excerpt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), last_failed_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), escalated_kind: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationKind)),null,]), undefined]), waited_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), connection_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), connection_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), provider: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), connection_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), period_type: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), waiting_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), thread_message_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), forge_host: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), expires_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), forge_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_by: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_role: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_left: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), gate_assignments_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), step_assignees_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), follows_dropped: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), access_request_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined])}, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', read_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), archived_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), severity: faker.helpers.arrayElement(Object.values(NotificationSeverity)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationReason)),null,]), undefined]), resolved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), acknowledged_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])})), next_before: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), ...overrideResponse})

export const getReadUnreadNotificationCountResponseMock = (overrideResponse: Partial<Extract<UnreadCount, object>> = {}): UnreadCount => ({unread: faker.number.int(), ...overrideResponse})

export const getCountNotificationsResponseMock = (overrideResponse: Partial<Extract<NotificationCount, object>> = {}): NotificationCount => ({total: faker.number.int(), ...overrideResponse})

export const getReadNotificationPreferencesResponseMock = (overrideResponse: Partial<Extract<NotificationPreferences, object>> = {}): NotificationPreferences => ({cells: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({group: faker.helpers.arrayElement(Object.values(NotificationGroup)), channel: faker.helpers.arrayElement(Object.values(NotificationChannel)), enabled: faker.datatype.boolean(), is_default: faker.datatype.boolean(), required: faker.datatype.boolean(), default_kinds: faker.helpers.arrayElement([faker.helpers.arrayElements(Object.values(NotificationKind)), undefined])})), mutes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, muted_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), timezone: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), email_enabled: faker.datatype.boolean(), quiet_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([{starts: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$"), ends: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$")},null,]), undefined]), ...overrideResponse})

export const getSetNotificationPreferenceResponseMock = (overrideResponse: Partial<Extract<NotificationPreferences, object>> = {}): NotificationPreferences => ({cells: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({group: faker.helpers.arrayElement(Object.values(NotificationGroup)), channel: faker.helpers.arrayElement(Object.values(NotificationChannel)), enabled: faker.datatype.boolean(), is_default: faker.datatype.boolean(), required: faker.datatype.boolean(), default_kinds: faker.helpers.arrayElement([faker.helpers.arrayElements(Object.values(NotificationKind)), undefined])})), mutes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, muted_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), timezone: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), email_enabled: faker.datatype.boolean(), quiet_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([{starts: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$"), ends: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$")},null,]), undefined]), ...overrideResponse})

export const getMuteWorkspaceNotificationsResponseMock = (overrideResponse: Partial<Extract<NotificationPreferences, object>> = {}): NotificationPreferences => ({cells: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({group: faker.helpers.arrayElement(Object.values(NotificationGroup)), channel: faker.helpers.arrayElement(Object.values(NotificationChannel)), enabled: faker.datatype.boolean(), is_default: faker.datatype.boolean(), required: faker.datatype.boolean(), default_kinds: faker.helpers.arrayElement([faker.helpers.arrayElements(Object.values(NotificationKind)), undefined])})), mutes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, muted_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), timezone: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), email_enabled: faker.datatype.boolean(), quiet_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([{starts: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$"), ends: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$")},null,]), undefined]), ...overrideResponse})

export const getUnmuteWorkspaceNotificationsResponseMock = (overrideResponse: Partial<Extract<NotificationPreferences, object>> = {}): NotificationPreferences => ({cells: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({group: faker.helpers.arrayElement(Object.values(NotificationGroup)), channel: faker.helpers.arrayElement(Object.values(NotificationChannel)), enabled: faker.datatype.boolean(), is_default: faker.datatype.boolean(), required: faker.datatype.boolean(), default_kinds: faker.helpers.arrayElement([faker.helpers.arrayElements(Object.values(NotificationKind)), undefined])})), mutes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, muted_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), timezone: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), email_enabled: faker.datatype.boolean(), quiet_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([{starts: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$"), ends: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$")},null,]), undefined]), ...overrideResponse})

export const getSetNotificationQuietHoursResponseMock = (overrideResponse: Partial<Extract<NotificationPreferences, object>> = {}): NotificationPreferences => ({cells: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({group: faker.helpers.arrayElement(Object.values(NotificationGroup)), channel: faker.helpers.arrayElement(Object.values(NotificationChannel)), enabled: faker.datatype.boolean(), is_default: faker.datatype.boolean(), required: faker.datatype.boolean(), default_kinds: faker.helpers.arrayElement([faker.helpers.arrayElements(Object.values(NotificationKind)), undefined])})), mutes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, muted_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), timezone: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), email_enabled: faker.datatype.boolean(), quiet_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([{starts: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$"), ends: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$")},null,]), undefined]), ...overrideResponse})

export const getClearNotificationQuietHoursResponseMock = (overrideResponse: Partial<Extract<NotificationPreferences, object>> = {}): NotificationPreferences => ({cells: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({group: faker.helpers.arrayElement(Object.values(NotificationGroup)), channel: faker.helpers.arrayElement(Object.values(NotificationChannel)), enabled: faker.datatype.boolean(), is_default: faker.datatype.boolean(), required: faker.datatype.boolean(), default_kinds: faker.helpers.arrayElement([faker.helpers.arrayElements(Object.values(NotificationKind)), undefined])})), mutes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, muted_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), timezone: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), email_enabled: faker.datatype.boolean(), quiet_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([{starts: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$"), ends: faker.helpers.fromRegExp("^([01]\\d|2[0-3]):[0-5]\\d$")},null,]), undefined]), ...overrideResponse})

export const getStreamNotificationsResponseMock = (): string => (faker.word.sample())

export const getMarkAllNotificationsReadResponseMock = (overrideResponse: Partial<Extract<InboxChange, object>> = {}): InboxChange => ({unread: faker.number.int(), changed: faker.number.int(), ...overrideResponse})

export const getArchiveAllNotificationsResponseMock = (overrideResponse: Partial<Extract<InboxChange, object>> = {}): InboxChange => ({unread: faker.number.int(), changed: faker.number.int(), ...overrideResponse})

export const getChangeNotificationsResponseMock = (overrideResponse: Partial<Extract<InboxChange, object>> = {}): InboxChange => ({unread: faker.number.int(), changed: faker.number.int(), ...overrideResponse})

export const getMarkTaskNotificationsReadResponseMock = (overrideResponse: Partial<Extract<UnreadCount, object>> = {}): UnreadCount => ({unread: faker.number.int(), ...overrideResponse})

export const getReadNotificationResponseMock = (overrideResponse: Partial<Extract<NotificationView, object>> = {}): NotificationView => ({id: faker.string.uuid(), kind: faker.helpers.arrayElement(Object.values(NotificationKind)), group: faker.helpers.arrayElement(Object.values(NotificationGroup)), workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, task_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), workflow_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), actor: faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), payload: {task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), child_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), child_task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), spend_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), trigger_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_excerpt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), last_failed_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), escalated_kind: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationKind)),null,]), undefined]), waited_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), connection_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), connection_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), provider: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), connection_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), period_type: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), waiting_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), thread_message_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), forge_host: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), expires_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), forge_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_by: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_role: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_left: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), gate_assignments_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), step_assignees_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), follows_dropped: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), access_request_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined])}, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', read_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), archived_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), severity: faker.helpers.arrayElement(Object.values(NotificationSeverity)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationReason)),null,]), undefined]), resolved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), acknowledged_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getMarkNotificationReadResponseMock = (overrideResponse: Partial<Extract<NotificationView, object>> = {}): NotificationView => ({id: faker.string.uuid(), kind: faker.helpers.arrayElement(Object.values(NotificationKind)), group: faker.helpers.arrayElement(Object.values(NotificationGroup)), workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, task_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), workflow_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), actor: faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), payload: {task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), child_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), child_task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), spend_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), trigger_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_excerpt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), last_failed_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), escalated_kind: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationKind)),null,]), undefined]), waited_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), connection_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), connection_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), provider: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), connection_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), period_type: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), waiting_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), thread_message_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), forge_host: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), expires_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), forge_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_by: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_role: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_left: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), gate_assignments_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), step_assignees_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), follows_dropped: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), access_request_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined])}, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', read_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), archived_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), severity: faker.helpers.arrayElement(Object.values(NotificationSeverity)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationReason)),null,]), undefined]), resolved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), acknowledged_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getMarkNotificationUnreadResponseMock = (overrideResponse: Partial<Extract<NotificationView, object>> = {}): NotificationView => ({id: faker.string.uuid(), kind: faker.helpers.arrayElement(Object.values(NotificationKind)), group: faker.helpers.arrayElement(Object.values(NotificationGroup)), workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, task_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), workflow_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), actor: faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), payload: {task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), child_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), child_task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), spend_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), trigger_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_excerpt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), last_failed_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), escalated_kind: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationKind)),null,]), undefined]), waited_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), connection_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), connection_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), provider: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), connection_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), period_type: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), waiting_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), thread_message_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), forge_host: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), expires_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), forge_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_by: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_role: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_left: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), gate_assignments_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), step_assignees_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), follows_dropped: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), access_request_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined])}, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', read_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), archived_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), severity: faker.helpers.arrayElement(Object.values(NotificationSeverity)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationReason)),null,]), undefined]), resolved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), acknowledged_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getAcknowledgeNotificationResponseMock = (overrideResponse: Partial<Extract<NotificationView, object>> = {}): NotificationView => ({id: faker.string.uuid(), kind: faker.helpers.arrayElement(Object.values(NotificationKind)), group: faker.helpers.arrayElement(Object.values(NotificationGroup)), workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, task_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), workflow_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), actor: faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), payload: {task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), child_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), child_task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), spend_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), trigger_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_excerpt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), last_failed_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), escalated_kind: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationKind)),null,]), undefined]), waited_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), connection_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), connection_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), provider: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), connection_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), period_type: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), waiting_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), thread_message_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), forge_host: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), expires_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), forge_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_by: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_role: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_left: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), gate_assignments_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), step_assignees_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), follows_dropped: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), access_request_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined])}, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', read_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), archived_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), severity: faker.helpers.arrayElement(Object.values(NotificationSeverity)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationReason)),null,]), undefined]), resolved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), acknowledged_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getArchiveNotificationResponseMock = (overrideResponse: Partial<Extract<NotificationView, object>> = {}): NotificationView => ({id: faker.string.uuid(), kind: faker.helpers.arrayElement(Object.values(NotificationKind)), group: faker.helpers.arrayElement(Object.values(NotificationGroup)), workspace: {id: faker.string.uuid(), name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, task_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), workflow_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), actor: faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), payload: {task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), child_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), child_task_title: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), spend_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), trigger_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_excerpt: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), failure_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), last_failed_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), escalated_kind: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationKind)),null,]), undefined]), waited_hours: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), connection_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), connection_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), provider: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), connection_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), period_type: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), waiting_count: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), thread_message_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), forge_host: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), expires_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), forge_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_by: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_role: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), member_left: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), gate_assignments_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), step_assignees_cleared: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), follows_dropped: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), access_request_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined])}, created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', read_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), archived_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), severity: faker.helpers.arrayElement(Object.values(NotificationSeverity)), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(NotificationReason)),null,]), undefined]), resolved_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), acknowledged_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ...overrideResponse})

export const getReadNotificationPolicyResponseMock = (overrideResponse: Partial<Extract<NotificationPolicy, object>> = {}): NotificationPolicy => ({escalation_delay: faker.helpers.arrayElement(Object.values(EscalationDelay)), history: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({actor: faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), changed_at: faker.date.past().toISOString().slice(0, 19) + 'Z', field: faker.string.alpha({length: {min: 10, max: 20}}), before: faker.string.alpha({length: {min: 10, max: 20}}), after: faker.string.alpha({length: {min: 10, max: 20}})})), ...overrideResponse})

export const getSetNotificationPolicyResponseMock = (overrideResponse: Partial<Extract<NotificationPolicy, object>> = {}): NotificationPolicy => ({escalation_delay: faker.helpers.arrayElement(Object.values(EscalationDelay)), history: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({actor: faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), changed_at: faker.date.past().toISOString().slice(0, 19) + 'Z', field: faker.string.alpha({length: {min: 10, max: 20}}), before: faker.string.alpha({length: {min: 10, max: 20}}), after: faker.string.alpha({length: {min: 10, max: 20}})})), ...overrideResponse})

export const getUnsubscribeFromEmailResponseMock = (): string => (faker.word.sample())

export const getUnsubscribeFromEmailOneClickResponseMock = (): string => (faker.word.sample())

export const getRegisterWorkspaceFileChangesResponseMock = (overrideResponse: Partial<Extract<RegisterChangesResponse, object>> = {}): RegisterChangesResponse => ({events: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workspace_id: faker.string.uuid(), path: faker.string.alpha({length: {min: 10, max: 20}}), event_type: faker.helpers.arrayElement(Object.values(FileChangeEventType)), actor: {kind: faker.helpers.arrayElement(['human','ai','unattributed'] as const), user_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), user_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), step_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), task_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])}, occurred_at: faker.date.past().toISOString().slice(0, 19) + 'Z', source_path: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), ...overrideResponse})

export const getGetScheduleContextResponseMock = (overrideResponse: Partial<Extract<ScheduleContextView, object>> = {}): ScheduleContextView => ({prompt: faker.string.alpha({length: {min: 10, max: 20}}), notes: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({at: faker.date.past().toISOString().slice(0, 19) + 'Z', author: faker.string.alpha({length: {min: 10, max: 20}}), body: faker.string.alpha({length: {min: 10, max: 20}})})), parent: {id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.string.alpha({length: {min: 10, max: 20}}), comments: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({author: faker.string.alpha({length: {min: 10, max: 20}}), author_kind: faker.helpers.arrayElement(['user','agent','system'] as const), at: faker.date.past().toISOString().slice(0, 19) + 'Z', body: faker.string.alpha({length: {min: 10, max: 20}}), new_since_last_pick: faker.datatype.boolean()}))}, trigger: {kind: faker.helpers.arrayElement(['parent_run_ended','child_done'] as const), child_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), step_report: faker.string.alpha({length: {min: 10, max: 20}})}, subtasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const), created_order: faker.number.int()})), previous_picks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({at: faker.date.past().toISOString().slice(0, 19) + 'Z', started: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.uuid())), reason: faker.string.alpha({length: {min: 10, max: 20}})})), instructions: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), ...overrideResponse})

export const getPostScheduleResultResponseMock = (overrideResponse: Partial<Extract<ScheduleResultView, object>> = {}): ScheduleResultView => ({started: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => (faker.string.uuid())), ignored: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.alpha({length: {min: 10, max: 20}}), reason: faker.string.alpha({length: {min: 10, max: 20}})})), refused: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), reason: faker.string.alpha({length: {min: 10, max: 20}})})), ...overrideResponse})

export const getListAgentSubtasksResponseMock = (overrideResponse: Partial<Extract<AgentSubtaskList, object>> = {}): AgentSubtaskList => ({children: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), ...overrideResponse})

export const getGetAgentSubtaskResponseMock = (overrideResponse: Partial<Extract<AgentSubtaskDetail, object>> = {}): AgentSubtaskDetail => ({id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const), comments: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), task_id: faker.string.uuid(), body: faker.string.alpha({length: {min: 10, max: 20}}), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType)), actor: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', caused_by_signal_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), caused_by_trigger_profile_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined])})), ...overrideResponse})

export const getUpdateAgentSubtaskResponseMock = (overrideResponse: Partial<Extract<AgentSubtask, object>> = {}): AgentSubtask => ({id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const), ...overrideResponse})

export const getRegisterTaskExternalReferenceResponseMock = (overrideResponse: Partial<Extract<ExternalReferenceView, object>> = {}): ExternalReferenceView => ({kind: faker.string.alpha({length: {min: 10, max: 20}}), connection_id: faker.string.uuid(), repository: faker.string.alpha({length: {min: 10, max: 20}}), external_number: faker.number.int(), ...overrideResponse})

export const getGetTaskAgentViewResponseMock = (overrideResponse: Partial<Extract<TaskView, object>> = {}): TaskView => ({id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean(), ...overrideResponse})

export const getGetTaskAgentViewCommentsResponseMock = (overrideResponse: Partial<Extract<AgentTaskCommentList, object>> = {}): AgentTaskCommentList => ({items: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), task_id: faker.string.uuid(), body: faker.string.alpha({length: {min: 10, max: 20}}), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType)), actor: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', caused_by_signal_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), caused_by_trigger_profile_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), new_for_this_run: faker.datatype.boolean()})), ...overrideResponse})

export const getAddTaskAgentViewCommentResponseMock = (overrideResponse: Partial<Extract<TaskCommentView, object>> = {}): TaskCommentView => ({id: faker.string.uuid(), task_id: faker.string.uuid(), body: faker.string.alpha({length: {min: 10, max: 20}}), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType)), actor: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', caused_by_signal_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), caused_by_trigger_profile_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), ...overrideResponse})

export const getListWorkspaceTasksAgentViewResponseMock = (): TaskView[] => (Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean()})))

export const getMoveTaskFromBacklogResponseMock = (overrideResponse: Partial<Extract<MoveTaskResponse, object>> = {}): MoveTaskResponse => ({task: {id: faker.string.uuid(), workflow_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), description: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), folder_path: faker.string.alpha({length: {min: 10, max: 20}}), input_values: {
        [faker.string.alphanumeric(5)]: {}
      }, position: faker.string.alpha({length: {min: 10, max: 20}}), version: faker.number.int(), status: faker.helpers.arrayElement(Object.values(TaskStatus)), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_at: faker.date.past().toISOString().slice(0, 19) + 'Z', workdir: faker.helpers.arrayElement([faker.helpers.arrayElement([{pvc_name: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(Object.values(TaskWorkdirStatus)), last_used_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined]), executions: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])})), undefined]), subtree_cost: faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"), subtree_cost_complete: faker.datatype.boolean(), chain_in_progress: faker.datatype.boolean(), hidden_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), source_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), created_by_execution_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), parent_task_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.uuid(),null,]), undefined]), labels: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), name: faker.string.alpha({length: {min: 10, max: 20}}), color: faker.helpers.arrayElement(Object.values(LabelColor)), actor_type: faker.helpers.arrayElement(Object.values(TaskActorType))})), undefined]), created_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), created_via: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), comment_count: faker.number.int(), assignee: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), assignee_is_override: faker.datatype.boolean(), needs_review: faker.datatype.boolean(), needs_attention: faker.datatype.boolean(), linked_tasks: faker.helpers.arrayElement([faker.helpers.arrayElement([{done: faker.number.int(), total: faker.number.int(), not_started: faker.number.int(), tasks: Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({task_id: faker.string.uuid(), title: faker.string.alpha({length: {min: 10, max: 20}}), status: faker.helpers.arrayElement(['done','needs_attention','needs_review','not_started','in_progress'] as const)})), workflow_name: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), scheduler: faker.helpers.arrayElement([faker.helpers.arrayElement([{state: faker.helpers.arrayElement(['idle','picking','stalled'] as const), last_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), last_decided_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined])},null,]), undefined])},null,]), undefined]), is_subtask: faker.datatype.boolean()}, execution: {id: faker.string.uuid(), status: faker.helpers.arrayElement(Object.values(TaskExecutionStatus)), step_id_snapshot: faker.string.alpha({length: {min: 10, max: 20}}), step_kind_snapshot: faker.helpers.arrayElement(Object.values(WorkflowStepKind)), result_summary: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), error_message: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), blocked_scope: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(SpendLimitScope)),null,]), undefined]), queue_position: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), queued_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(QueuedReason)),null,]), undefined]), started_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), ended_at: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), undefined]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', chosen_transition_key: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), chosen_transition_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), is_join_decision: faker.datatype.boolean(), file_capture_status: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(Object.values(FileCaptureStatus)),null,]), undefined]), file_capture_reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), model_snapshot: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cost: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.fromRegExp("^(?!^[-+.]*$)[+-]?0*\\d*\\.?\\d*$"),null,]), undefined]), currency: faker.string.alpha({length: {min: 10, max: 20}}), max_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), num_turns: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_budget_mib: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.number.int(),null,]), undefined]), memory_enforced: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.datatype.boolean(),null,]), undefined]), memory_stops: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({command: faker.string.alpha({length: {min: 10, max: 20}}), tool_use_id: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), cause: faker.helpers.arrayElement(['budget','pod_headroom','daemon_headroom'] as const), budget_mib: faker.number.int(), peak_mib: faker.number.int(), exit_code: faker.number.int(), stopped_at: faker.date.past().toISOString().slice(0, 19) + 'Z'})), undefined]), started_by: faker.helpers.arrayElement([faker.helpers.arrayElement([{id: faker.string.uuid(), email: faker.string.alpha({length: {min: 10, max: 20}}), display_name: faker.string.alpha({length: {min: 10, max: 20}})},null,]), undefined]), credential_source: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['personal','shared','none','required'] as const),null,]), undefined]), forge_credentials: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({provider: faker.helpers.arrayElement(Object.values(ForgeProvider)), host: faker.string.alpha({length: {min: 10, max: 20}}), source: faker.helpers.arrayElement(['personal','shared','required'] as const), forge_username: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), reason: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.helpers.arrayElement(['not_linked','broken','expired','revoked','inactive_owner','unreachable'] as const),null,]), undefined])})), undefined])}, ...overrideResponse})

export const getRecordExecutionResultResponseMock = (overrideResponse: Partial<Extract<RecordExecutionResultResponse, object>> = {}): RecordExecutionResultResponse => ({execution_id: faker.string.uuid(), recorded_at: faker.date.past().toISOString().slice(0, 19) + 'Z', ...overrideResponse})

export const getListArtifactsForExecutionResponseMock = (overrideResponse: Partial<Extract<TaskArtifactsView, object>> = {}): TaskArtifactsView => ({artifacts: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({id: faker.string.uuid(), kind: faker.helpers.arrayElement(['merge_request','pull_request','branch'] as const), forge_host: faker.string.alpha({length: {min: 10, max: 20}}), repository: faker.string.alpha({length: {min: 10, max: 20}}), number: faker.helpers.arrayElement([faker.number.int(),null,]), web_url: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), title: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), source_branch: faker.string.alpha({length: {min: 10, max: 20}}), target_branch: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), latest_commit: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), created_by_execution_id: faker.helpers.arrayElement([faker.string.uuid(),null,]), created_at: faker.date.past().toISOString().slice(0, 19) + 'Z', updated_by: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({execution_id: faker.string.uuid(), last_pushed_at: faker.date.past().toISOString().slice(0, 19) + 'Z', commit_sha: faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,])})), undefined])})), undefined]), ...overrideResponse})

export const getRecordArtifactEventsResponseMock = (overrideResponse: Partial<Extract<RecordArtifactEventsResponse, object>> = {}): RecordArtifactEventsResponse => ({recorded: faker.number.int(), skipped: faker.number.int(), ...overrideResponse})

export const getReadRunForgeTokenResponseMock = (overrideResponse: Partial<Extract<RunForgeToken, object>> = {}): RunForgeToken => ({token: faker.string.alpha({length: {min: 10, max: 20}}), expires_at: faker.helpers.arrayElement([faker.date.past().toISOString().slice(0, 19) + 'Z',null,]), link_method: faker.helpers.arrayElement(Object.values(LinkMethod)), ...overrideResponse})

export const getGetReplyContextResponseMock = (overrideResponse: Partial<Extract<ReplyContextView, object>> = {}): ReplyContextView => ({prompt: faker.string.alpha({length: {min: 10, max: 20}}), task_folder: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined]), checkouts: faker.helpers.arrayElement([Array.from({ length: faker.number.int({min: 1, max: 10}) }, (_, i) => i + 1).map(() => ({forge_host: faker.string.alpha({length: {min: 10, max: 20}}), repository: faker.string.alpha({length: {min: 10, max: 20}}), branch: faker.string.alpha({length: {min: 10, max: 20}}), commit_sha: faker.helpers.arrayElement([faker.helpers.arrayElement([faker.string.alpha({length: {min: 10, max: 20}}),null,]), undefined])})), undefined]), max_answer_length: faker.number.int(), ...overrideResponse})

export const getPostReplyAnswerResponseMock = (overrideResponse: Partial<Extract<ReplyAnswerView, object>> = {}): ReplyAnswerView => ({message_id: faker.string.uuid(), ...overrideResponse})


export const getHealthMockHandler = (overrideResponse?: Health200 | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<Health200> | Health200), options?: RequestHandlerOptions) => {
  return http.get('*/health', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getHealthResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadCurrentUserMockHandler = (overrideResponse?: CurrentUserView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<CurrentUserView> | CurrentUserView), options?: RequestHandlerOptions) => {
  return http.get('*/api/users/me', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadCurrentUserResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetCurrentUserTimezoneMockHandler = (overrideResponse?: UserTimezone | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<UserTimezone> | UserTimezone), options?: RequestHandlerOptions) => {
  return http.put('*/api/users/me/timezone', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetCurrentUserTimezoneResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadCurrentUserCommitEmailMockHandler = (overrideResponse?: UserCommitEmail | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<UserCommitEmail> | UserCommitEmail), options?: RequestHandlerOptions) => {
  return http.get('*/api/users/me/commit-email', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadCurrentUserCommitEmailResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetCurrentUserCommitEmailMockHandler = (overrideResponse?: UserCommitEmail | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<UserCommitEmail> | UserCommitEmail), options?: RequestHandlerOptions) => {
  return http.put('*/api/users/me/commit-email', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetCurrentUserCommitEmailResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListAssignableUsersMockHandler = (overrideResponse?: UserListPage | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<UserListPage> | UserListPage), options?: RequestHandlerOptions) => {
  return http.get('*/api/users', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListAssignableUsersResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListMyForgeAccountsMockHandler = (overrideResponse?: ForgeAccountList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ForgeAccountList> | ForgeAccountList), options?: RequestHandlerOptions) => {
  return http.get('*/api/users/me/forge-accounts', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListMyForgeAccountsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListForgeHostsMockHandler = (overrideResponse?: ForgeHostList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ForgeHostList> | ForgeHostList), options?: RequestHandlerOptions) => {
  return http.get('*/api/users/me/forge-hosts', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListForgeHostsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getLinkForgeAccountMockHandler = (overrideResponse?: LinkResult | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<LinkResult> | LinkResult), options?: RequestHandlerOptions) => {
  return http.put('*/api/users/me/forge-accounts/:host', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getLinkForgeAccountResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUnlinkForgeAccountMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/users/me/forge-accounts/:host', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getRevokeForgeAccountMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/organization/users/:userId/forge-accounts/:host', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getRemindForgeAccountMockHandler = (overrideResponse?: ForgeReminderView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ForgeReminderView> | ForgeReminderView), options?: RequestHandlerOptions) => {
  return http.post('*/api/organization/users/:userId/forge-accounts/:host/remind', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getRemindForgeAccountResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListPeopleForgeAccountsMockHandler = (overrideResponse?: PeopleLinksPage | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<PeopleLinksPage> | PeopleLinksPage), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/forge-accounts', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListPeopleForgeAccountsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListForgePoliciesMockHandler = (overrideResponse?: ForgePolicyList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ForgePolicyList> | ForgePolicyList), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/forge-policy', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListForgePoliciesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetForgePolicyMockHandler = (overrideResponse?: ForgePolicyView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<ForgePolicyView> | ForgePolicyView), options?: RequestHandlerOptions) => {
  return http.put('*/api/organization/forge-policy/:workspaceId', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetForgePolicyResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadPersonalAccountStandingMockHandler = (overrideResponse?: PersonalAccountStanding | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<PersonalAccountStanding> | PersonalAccountStanding), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/personal-accounts', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadPersonalAccountStandingResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListForgeAppsMockHandler = (overrideResponse?: ForgeAppList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ForgeAppList> | ForgeAppList), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/forge-apps', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListForgeAppsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetForgeAppMockHandler = (overrideResponse?: ForgeAppView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<ForgeAppView> | ForgeAppView), options?: RequestHandlerOptions) => {
  return http.put('*/api/organization/forge-apps/:host', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetForgeAppResponseMock(),
      { status: 200
      })
  }, options)
}

export const getRemoveForgeAppMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/organization/forge-apps/:host', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getStartForgeOauthMockHandler = (overrideResponse?: OAuthStartView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<OAuthStartView> | OAuthStartView), options?: RequestHandlerOptions) => {
  return http.post('*/api/users/me/forge-accounts/:host/oauth', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getStartForgeOauthResponseMock(),
      { status: 200
      })
  }, options)
}

export const getFinishForgeOauthMockHandler = (overrideResponse?: unknown | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<unknown> | unknown), options?: RequestHandlerOptions) => {
  return http.get('*/api/forge-oauth/callback', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 200
      })
  }, options)
}

export const getListMyAiToolsMockHandler = (overrideResponse?: AiToolList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<AiToolList> | AiToolList), options?: RequestHandlerOptions) => {
  return http.get('*/api/users/me/ai-tools', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListMyAiToolsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDisconnectMyAiToolsMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.post('*/api/users/me/ai-tools/disconnect', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getCreateWorkspaceMockHandler = (overrideResponse?: WorkspaceAggregate | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<WorkspaceAggregate> | WorkspaceAggregate), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateWorkspaceResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListWorkspacesMockHandler = (overrideResponse?: WorkspaceListPage | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkspaceListPage> | WorkspaceListPage), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListWorkspacesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadWorkspaceMockHandler = (overrideResponse?: WorkspaceAggregate | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkspaceAggregate> | WorkspaceAggregate), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadWorkspaceResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUpdateWorkspaceMockHandler = (overrideResponse?: WorkspaceAggregate | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<WorkspaceAggregate> | WorkspaceAggregate), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateWorkspaceResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteWorkspaceMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getListWorkspaceMembersMockHandler = (overrideResponse?: WorkspaceMembersView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkspaceMembersView> | WorkspaceMembersView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/members', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListWorkspaceMembersResponseMock(),
      { status: 200
      })
  }, options)
}

export const getAddWorkspaceMemberMockHandler = (overrideResponse?: WorkspaceMembersView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<WorkspaceMembersView> | WorkspaceMembersView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/members', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getAddWorkspaceMemberResponseMock(),
      { status: 201
      })
  }, options)
}

export const getUpdateWorkspaceMemberMockHandler = (overrideResponse?: WorkspaceMembersView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<WorkspaceMembersView> | WorkspaceMembersView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId/members/:userId', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateWorkspaceMemberResponseMock(),
      { status: 200
      })
  }, options)
}

export const getRemoveWorkspaceMemberMockHandler = (overrideResponse?: MemberRemoval | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<MemberRemoval> | MemberRemoval), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/members/:userId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getRemoveWorkspaceMemberResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListWorkspacePeopleMockHandler = (overrideResponse?: WorkspacePeople | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkspacePeople> | WorkspacePeople), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/people', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListWorkspacePeopleResponseMock(),
      { status: 200
      })
  }, options)
}

export const getRequestWorkspaceAccessMockHandler = (overrideResponse?: MyAccessRequest | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<MyAccessRequest> | MyAccessRequest), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/access-requests', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getRequestWorkspaceAccessResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListWorkspaceAccessRequestsMockHandler = (overrideResponse?: AccessRequests | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<AccessRequests> | AccessRequests), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/access-requests', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListWorkspaceAccessRequestsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadMyWorkspaceAccessRequestMockHandler = (overrideResponse?: MyAccessRequest | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<MyAccessRequest> | MyAccessRequest), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/access-requests/mine', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadMyWorkspaceAccessRequestResponseMock(),
      { status: 200
      })
  }, options)
}

export const getAddFromWorkspaceAccessRequestMockHandler = (overrideResponse?: WorkspaceMembersView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<WorkspaceMembersView> | WorkspaceMembersView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/access-requests/:requestId/add', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getAddFromWorkspaceAccessRequestResponseMock(),
      { status: 201
      })
  }, options)
}

export const getDismissWorkspaceAccessRequestMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/access-requests/:requestId/dismiss', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getReadWorkspaceAccessLogMockHandler = (overrideResponse?: AccessLogPage | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<AccessLogPage> | AccessLogPage), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/access-log', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadWorkspaceAccessLogResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCreateWorkflowMockHandler = (overrideResponse?: WorkflowView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<WorkflowView> | WorkflowView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/workflows', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateWorkflowResponseMock(),
      { status: 201
      })
  }, options)
}

export const getCreateWorkflowDraftMockHandler = (overrideResponse?: WorkflowView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<WorkflowView> | WorkflowView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/workflows/drafts', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateWorkflowDraftResponseMock(),
      { status: 201
      })
  }, options)
}

export const getReplaceWorkflowMockHandler = (overrideResponse?: WorkflowView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<WorkflowView> | WorkflowView), options?: RequestHandlerOptions) => {
  return http.put('*/api/workspaces/:workspaceId/workflows/:workflowId', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReplaceWorkflowResponseMock(),
      { status: 200
      })
  }, options)
}

export const getRenameWorkflowMockHandler = (overrideResponse?: WorkflowView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<WorkflowView> | WorkflowView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId/workflows/:workflowId', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getRenameWorkflowResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteWorkflowMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/workflows/:workflowId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getGetWorkflowForEditingMockHandler = (overrideResponse?: WorkflowEditView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkflowEditView> | WorkflowEditView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/workflows/:workflowId/pending-changes', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetWorkflowForEditingResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSaveWorkflowPendingChangesMockHandler = (overrideResponse?: WorkflowEditView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<WorkflowEditView> | WorkflowEditView), options?: RequestHandlerOptions) => {
  return http.put('*/api/workspaces/:workspaceId/workflows/:workflowId/pending-changes', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSaveWorkflowPendingChangesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDiscardWorkflowPendingChangesMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/workflows/:workflowId/pending-changes', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getPublishWorkflowPendingChangesMockHandler = (overrideResponse?: WorkflowView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<WorkflowView> | WorkflowView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/workflows/:workflowId/pending-changes/publish', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getPublishWorkflowPendingChangesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getImportWorkflowMockHandler = (overrideResponse?: WorkflowImportResult | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<WorkflowImportResult> | WorkflowImportResult), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/workflows/import', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getImportWorkflowResponseMock(),
      { status: 201
      })
  }, options)
}

export const getPreflightWorkflowMockHandler = (overrideResponse?: ImportPreflight | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ImportPreflight> | ImportPreflight), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/workflows/import/preflight', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getPreflightWorkflowResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCreateTaskMockHandler = (overrideResponse?: TaskView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<TaskView> | TaskView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/workflows/:workflowId/tasks', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateTaskResponseMock(),
      { status: 201
      })
  }, options)
}

export const getStopTaskMockHandler = (overrideResponse?: unknown | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<unknown> | unknown), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/stop', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 200
      })
  }, options)
}

export const getRerunTaskMockHandler = (overrideResponse?: unknown | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<unknown> | unknown), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/rerun', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 202
      })
  }, options)
}

export const getHideTaskMockHandler = (overrideResponse?: unknown | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<unknown> | unknown), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/hide', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 200
      })
  }, options)
}

export const getUnhideTaskMockHandler = (overrideResponse?: unknown | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<unknown> | unknown), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/unhide', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 200
      })
  }, options)
}

export const getMoveTaskMockHandler = (overrideResponse?: MoveTaskResponse | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<MoveTaskResponse> | MoveTaskResponse), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/move', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getMoveTaskResponseMock(),
      { status: 200
      })
  }, options)
}

export const getStartLinkedTasksMockHandler = (overrideResponse?: StartLinkedTasksResponse | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<StartLinkedTasksResponse> | StartLinkedTasksResponse), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/linked-tasks/start', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getStartLinkedTasksResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSplitTaskMockHandler = (overrideResponse?: SplitTaskResponse | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<SplitTaskResponse> | SplitTaskResponse), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/split', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSplitTaskResponseMock(),
      { status: 201
      })
  }, options)
}

export const getSetTaskStepAssigneeMockHandler = (overrideResponse?: TaskView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<TaskView> | TaskView), options?: RequestHandlerOptions) => {
  return http.put('*/api/workspaces/:workspaceId/tasks/:taskId/step-assignee', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetTaskStepAssigneeResponseMock(),
      { status: 200
      })
  }, options)
}

export const getClearTaskStepAssigneeMockHandler = (overrideResponse?: TaskView | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<TaskView> | TaskView), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/tasks/:taskId/step-assignee', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getClearTaskStepAssigneeResponseMock(),
      { status: 200
      })
  }, options)
}

export const getRenameTaskMockHandler = (overrideResponse?: TaskView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<TaskView> | TaskView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId/tasks/:taskId/title', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getRenameTaskResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetTaskDescriptionMockHandler = (overrideResponse?: TaskView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<TaskView> | TaskView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId/tasks/:taskId/description', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetTaskDescriptionResponseMock(),
      { status: 200
      })
  }, options)
}

export const getAddTaskCommentMockHandler = (overrideResponse?: TaskCommentView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<TaskCommentView> | TaskCommentView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/comments', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getAddTaskCommentResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListTaskCommentsMockHandler = (overrideResponse?: TaskCommentList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TaskCommentList> | TaskCommentList), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/comments', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListTaskCommentsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListTaskMovesMockHandler = (overrideResponse?: TaskMoveList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TaskMoveList> | TaskMoveList), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/moves', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListTaskMovesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListRunActivityMockHandler = (overrideResponse?: RunActivityList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<RunActivityList> | RunActivityList), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/activity', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListRunActivityResponseMock(),
      { status: 200
      })
  }, options)
}

export const getAddSchedulerNoteMockHandler = (overrideResponse?: SchedulerNoteEntry | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<SchedulerNoteEntry> | SchedulerNoteEntry), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/scheduler-notes', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getAddSchedulerNoteResponseMock(),
      { status: 201
      })
  }, options)
}

export const getGetExecutionTranscriptMockHandler = (overrideResponse?: ExecutionTranscriptView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ExecutionTranscriptView> | ExecutionTranscriptView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/transcript', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetExecutionTranscriptResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCreateTaskRelationMockHandler = (overrideResponse?: TaskRelationView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<TaskRelationView> | TaskRelationView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/task-relations', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateTaskRelationResponseMock(),
      { status: 201
      })
  }, options)
}

export const getDeleteTaskRelationMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/task-relations/:relationId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getGetDirectTaskRelationsMockHandler = (overrideResponse?: DirectTaskRelationsResponse | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<DirectTaskRelationsResponse> | DirectTaskRelationsResponse), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/task-relations', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetDirectTaskRelationsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getGetRelatedTasksMockHandler = (overrideResponse?: RelatedTasksResponse | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<RelatedTasksResponse> | RelatedTasksResponse), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/related-tasks', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetRelatedTasksResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCreateRelatedTaskMockHandler = (overrideResponse?: CreateRelatedTaskResponse | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<CreateRelatedTaskResponse> | CreateRelatedTaskResponse), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/related-tasks', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateRelatedTaskResponseMock(),
      { status: 201
      })
  }, options)
}

export const getGetTaskSiblingsMockHandler = (overrideResponse?: SiblingsResponse | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<SiblingsResponse> | SiblingsResponse), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/related-tasks/siblings', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetTaskSiblingsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadTaskFollowMockHandler = (overrideResponse?: TaskFollowState | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TaskFollowState> | TaskFollowState), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/follow', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadTaskFollowResponseMock(),
      { status: 200
      })
  }, options)
}

export const getFollowTaskMockHandler = (overrideResponse?: TaskFollowState | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<TaskFollowState> | TaskFollowState), options?: RequestHandlerOptions) => {
  return http.put('*/api/workspaces/:workspaceId/tasks/:taskId/follow', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getFollowTaskResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUnfollowTaskMockHandler = (overrideResponse?: TaskFollowState | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<TaskFollowState> | TaskFollowState), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/tasks/:taskId/follow', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUnfollowTaskResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReplaceTaskLabelsMockHandler = (overrideResponse?: TaskLabelList | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<TaskLabelList> | TaskLabelList), options?: RequestHandlerOptions) => {
  return http.put('*/api/workspaces/:workspaceId/tasks/:taskId/labels', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReplaceTaskLabelsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteTaskMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/tasks/:taskId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getGetTaskResultsMockHandler = (overrideResponse?: TaskResultsView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TaskResultsView> | TaskResultsView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/results', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetTaskResultsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getGetTaskArtifactsMockHandler = (overrideResponse?: TaskArtifactsView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TaskArtifactsView> | TaskArtifactsView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/artifacts', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetTaskArtifactsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getPostRunThreadMessageMockHandler = (overrideResponse?: ThreadMessageView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ThreadMessageView> | ThreadMessageView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/thread/messages', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getPostRunThreadMessageResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListRunThreadMessagesMockHandler = (overrideResponse?: ThreadMessagePage | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ThreadMessagePage> | ThreadMessagePage), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/thread/messages', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListRunThreadMessagesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getRetryRunThreadReplyMockHandler = (overrideResponse?: ThreadMessageView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ThreadMessageView> | ThreadMessageView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/thread/messages/:messageId/retry', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getRetryRunThreadReplyResponseMock(),
      { status: 202
      })
  }, options)
}

export const getAddRunThreadMessageToTaskContextMockHandler = (overrideResponse?: AddedToTaskContextView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<AddedToTaskContextView> | AddedToTaskContextView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/thread/messages/:messageId/add-to-task-context', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getAddRunThreadMessageToTaskContextResponseMock(),
      { status: 201
      })
  }, options)
}

export const getAttachFileToTaskMockHandler = (overrideResponse?: AttachmentView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<AttachmentView> | AttachmentView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/attachments', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getAttachFileToTaskResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListTaskAttachmentsMockHandler = (overrideResponse?: TaskAttachmentsView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TaskAttachmentsView> | TaskAttachmentsView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/attachments', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListTaskAttachmentsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getRemoveTaskAttachmentMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/tasks/:taskId/attachments/:name', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getListWorkspaceFilesMockHandler = (overrideResponse?: FileListing | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<FileListing> | FileListing), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/files', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListWorkspaceFilesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteWorkspaceFileMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/files', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getReadWorkspaceFileContentMockHandler = (overrideResponse?: unknown | ArrayBuffer | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<unknown | ArrayBuffer> | unknown | ArrayBuffer), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/files/content', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {

  const binaryBody = overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadWorkspaceFileContentResponseMock();
    return HttpResponse.arrayBuffer(
      binaryBody instanceof ArrayBuffer
        ? binaryBody
        : new ArrayBuffer(0),
      { status: 200,
        headers: { 'Content-Type': 'application/octet-stream' }
      })
  }, options)
}

export const getUploadWorkspaceFileMockHandler = (overrideResponse?: FileChangeEventView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<FileChangeEventView> | FileChangeEventView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/files/content', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUploadWorkspaceFileResponseMock(),
      { status: 200
      })
  }, options)
}

export const getMoveWorkspaceFileMockHandler = (overrideResponse?: MoveFileResult | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<MoveFileResult> | MoveFileResult), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/files/move', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getMoveWorkspaceFileResponseMock(),
      { status: 200
      })
  }, options)
}

export const getGetWorkspaceFileHistoryMockHandler = (overrideResponse?: FileHistoryPage | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<FileHistoryPage> | FileHistoryPage), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/files/history', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetWorkspaceFileHistoryResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListWorkspaceSecretsMockHandler = (overrideResponse?: WorkspaceSecretList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkspaceSecretList> | WorkspaceSecretList), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/secrets', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListWorkspaceSecretsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCreateWorkspaceSecretMockHandler = (overrideResponse?: WorkspaceSecretView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<WorkspaceSecretView> | WorkspaceSecretView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/secrets', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateWorkspaceSecretResponseMock(),
      { status: 201
      })
  }, options)
}

export const getReplaceWorkspaceSecretValueMockHandler = (overrideResponse?: WorkspaceSecretView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<WorkspaceSecretView> | WorkspaceSecretView), options?: RequestHandlerOptions) => {
  return http.put('*/api/workspaces/:workspaceId/secrets/:key', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReplaceWorkspaceSecretValueResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUpdateWorkspaceSecretDescriptionMockHandler = (overrideResponse?: WorkspaceSecretView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<WorkspaceSecretView> | WorkspaceSecretView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId/secrets/:key', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateWorkspaceSecretDescriptionResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteWorkspaceSecretMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/secrets/:key', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getCreateTriggerProfileMockHandler = (overrideResponse?: TriggerProfileView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<TriggerProfileView> | TriggerProfileView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/trigger-profiles', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateTriggerProfileResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListTriggerProfilesMockHandler = (overrideResponse?: TriggerProfileView[] | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TriggerProfileView[]> | TriggerProfileView[]), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/trigger-profiles', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListTriggerProfilesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getGetTriggerProfileMockHandler = (overrideResponse?: TriggerProfileView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TriggerProfileView> | TriggerProfileView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/trigger-profiles/:triggerProfileId', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetTriggerProfileResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUpdateTriggerProfileMockHandler = (overrideResponse?: TriggerProfileView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<TriggerProfileView> | TriggerProfileView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId/trigger-profiles/:triggerProfileId', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateTriggerProfileResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteTriggerProfileMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/trigger-profiles/:triggerProfileId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getListTriggerProfileRunsMockHandler = (overrideResponse?: TriggerProfileRunView[] | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TriggerProfileRunView[]> | TriggerProfileRunView[]), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/trigger-profiles/:triggerProfileId/runs', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListTriggerProfileRunsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListTriggerProfileSignalsMockHandler = (overrideResponse?: SignalListPage | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<SignalListPage> | SignalListPage), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/trigger-profiles/:triggerProfileId/signals', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListTriggerProfileSignalsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getGetVariableManifestMockHandler = (overrideResponse?: VariableManifest | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<VariableManifest> | VariableManifest), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/trigger-profile-types/:type/variable-manifest', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetVariableManifestResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCreateLabelMockHandler = (overrideResponse?: LabelView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<LabelView> | LabelView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/labels', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateLabelResponseMock(),
      { status: 201
      })
  }, options)
}

export const getUpdateLabelMockHandler = (overrideResponse?: LabelView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<LabelView> | LabelView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId/labels/:labelId', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateLabelResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteLabelMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/labels/:labelId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getReadWorkspaceUsageMockHandler = (overrideResponse?: WorkspaceUsageSummary | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkspaceUsageSummary> | WorkspaceUsageSummary), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/usage', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadWorkspaceUsageResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadWorkspaceAiModelsMockHandler = (overrideResponse?: WorkspaceAIModels | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkspaceAIModels> | WorkspaceAIModels), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/ai-models', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadWorkspaceAiModelsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadWorkspaceAiModelSpendMockHandler = (overrideResponse?: WorkspaceAIModelSpend | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkspaceAIModelSpend> | WorkspaceAIModelSpend), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/ai-models/spend', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadWorkspaceAiModelSpendResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadOrganizationUsageMockHandler = (overrideResponse?: OrganizationUsageSummary | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<OrganizationUsageSummary> | OrganizationUsageSummary), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/usage', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadOrganizationUsageResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetModelPriceMockHandler = (overrideResponse?: ModelPriceView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ModelPriceView> | ModelPriceView), options?: RequestHandlerOptions) => {
  return http.post('*/api/organization/usage/prices', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetModelPriceResponseMock(),
      { status: 201
      })
  }, options)
}

export const getReadUnpricedModelsMockHandler = (overrideResponse?: UnpricedModelsView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<UnpricedModelsView> | UnpricedModelsView), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/usage/unpriced-models', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadUnpricedModelsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadModelPricesMockHandler = (overrideResponse?: ModelPricesView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ModelPricesView> | ModelPricesView), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/usage/model-prices', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadModelPricesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadWorkflowLimitsMockHandler = (overrideResponse?: WorkflowLimitRow[] | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkflowLimitRow[]> | WorkflowLimitRow[]), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/usage/workflow-limits', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadWorkflowLimitsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetWorkflowLimitMockHandler = (overrideResponse?: WorkflowLimitView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<WorkflowLimitView> | WorkflowLimitView), options?: RequestHandlerOptions) => {
  return http.put('*/api/organization/usage/workflow-limit/:workflowId', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetWorkflowLimitResponseMock(),
      { status: 200
      })
  }, options)
}

export const getClearWorkflowLimitMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/organization/usage/workflow-limit/:workflowId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getSetDefaultLimitMockHandler = (overrideResponse?: LimitView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<LimitView> | LimitView), options?: RequestHandlerOptions) => {
  return http.put('*/api/organization/usage/limit', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetDefaultLimitResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetWorkspaceLimitMockHandler = (overrideResponse?: LimitView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<LimitView> | LimitView), options?: RequestHandlerOptions) => {
  return http.put('*/api/organization/usage/limit/:workspaceId', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetWorkspaceLimitResponseMock(),
      { status: 200
      })
  }, options)
}

export const getClearWorkspaceLimitMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/organization/usage/limit/:workspaceId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getReadWorkspaceConcurrencyMockHandler = (overrideResponse?: WorkspaceConcurrencyView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkspaceConcurrencyView> | WorkspaceConcurrencyView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/concurrency', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadWorkspaceConcurrencyResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadOrganizationConcurrencyMockHandler = (overrideResponse?: OrganizationConcurrencyView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<OrganizationConcurrencyView> | OrganizationConcurrencyView), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/concurrency', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadOrganizationConcurrencyResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetDefaultConcurrencyLimitMockHandler = (overrideResponse?: ConcurrencyLimitView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<ConcurrencyLimitView> | ConcurrencyLimitView), options?: RequestHandlerOptions) => {
  return http.put('*/api/organization/concurrency/limit', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetDefaultConcurrencyLimitResponseMock(),
      { status: 200
      })
  }, options)
}

export const getClearDefaultConcurrencyLimitMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/organization/concurrency/limit', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getSetWorkspaceConcurrencyLimitMockHandler = (overrideResponse?: ConcurrencyLimitView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<ConcurrencyLimitView> | ConcurrencyLimitView), options?: RequestHandlerOptions) => {
  return http.put('*/api/organization/concurrency/limit/:workspaceId', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetWorkspaceConcurrencyLimitResponseMock(),
      { status: 200
      })
  }, options)
}

export const getClearWorkspaceConcurrencyLimitMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/organization/concurrency/limit/:workspaceId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getReadWorkspaceMemoryMockHandler = (overrideResponse?: WorkspaceMemoryView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<WorkspaceMemoryView> | WorkspaceMemoryView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/memory', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadWorkspaceMemoryResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadOrganizationMemoryMockHandler = (overrideResponse?: OrganizationMemoryView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<OrganizationMemoryView> | OrganizationMemoryView), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/memory', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadOrganizationMemoryResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetDefaultMemoryLimitMockHandler = (overrideResponse?: MemoryLimitView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<MemoryLimitView> | MemoryLimitView), options?: RequestHandlerOptions) => {
  return http.put('*/api/organization/memory/limit', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetDefaultMemoryLimitResponseMock(),
      { status: 200
      })
  }, options)
}

export const getClearDefaultMemoryLimitMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/organization/memory/limit', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getSetWorkspaceMemoryLimitMockHandler = (overrideResponse?: MemoryLimitView | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<MemoryLimitView> | MemoryLimitView), options?: RequestHandlerOptions) => {
  return http.put('*/api/organization/memory/limit/:workspaceId', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetWorkspaceMemoryLimitResponseMock(),
      { status: 200
      })
  }, options)
}

export const getClearWorkspaceMemoryLimitMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/organization/memory/limit/:workspaceId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getReadOrganizationMockHandler = (overrideResponse?: OrganizationView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<OrganizationView> | OrganizationView), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadOrganizationResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUpdateOrganizationMockHandler = (overrideResponse?: OrganizationView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<OrganizationView> | OrganizationView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/organization', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateOrganizationResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListOrganizationWorkspacesMockHandler = (overrideResponse?: AdminWorkspaces | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<AdminWorkspaces> | AdminWorkspaces), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/workspaces', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListOrganizationWorkspacesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getExportOrganizationWorkspacesMockHandler = (overrideResponse?: string | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<string> | string), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/workspaces.csv', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {

  const resolvedBody = overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getExportOrganizationWorkspacesResponseMock();
    const textBody = typeof resolvedBody === 'string' ? resolvedBody : JSON.stringify(resolvedBody ?? null);
    return HttpResponse.text(textBody,
      { status: 200
      })
  }, options)
}

export const getReadOrganizationAccessLogMockHandler = (overrideResponse?: AccessLogPage | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<AccessLogPage> | AccessLogPage), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/workspace-access-log', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadOrganizationAccessLogResponseMock(),
      { status: 200
      })
  }, options)
}

export const getExportOrganizationAccessLogMockHandler = (overrideResponse?: string | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<string> | string), options?: RequestHandlerOptions) => {
  return http.get('*/api/organization/workspace-access-log.csv', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {

  const resolvedBody = overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getExportOrganizationAccessLogResponseMock();
    const textBody = typeof resolvedBody === 'string' ? resolvedBody : JSON.stringify(resolvedBody ?? null);
    return HttpResponse.text(textBody,
      { status: 200
      })
  }, options)
}

export const getListLlmProfilesMockHandler = (overrideResponse?: LLMProfileList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<LLMProfileList> | LLMProfileList), options?: RequestHandlerOptions) => {
  return http.get('*/api/llm-profiles', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListLlmProfilesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCreateLlmProfileMockHandler = (overrideResponse?: LLMProfileView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<LLMProfileView> | LLMProfileView), options?: RequestHandlerOptions) => {
  return http.post('*/api/llm-profiles', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateLlmProfileResponseMock(),
      { status: 201
      })
  }, options)
}

export const getReadLlmProfilesAdminOverviewMockHandler = (overrideResponse?: LLMProfileAdminOverview | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<LLMProfileAdminOverview> | LLMProfileAdminOverview), options?: RequestHandlerOptions) => {
  return http.get('*/api/llm-profiles/admin-overview', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadLlmProfilesAdminOverviewResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUpdateLlmProfileMockHandler = (overrideResponse?: LLMProfileView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<LLMProfileView> | LLMProfileView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/llm-profiles/:slug', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateLlmProfileResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteLlmProfileMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/llm-profiles/:slug', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getSetDefaultLlmProfileMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.post('*/api/llm-profiles/:slug/default', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getListAiProvidersMockHandler = (overrideResponse?: AIProviderList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<AIProviderList> | AIProviderList), options?: RequestHandlerOptions) => {
  return http.get('*/api/ai-providers', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListAiProvidersResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCreateAiProviderMockHandler = (overrideResponse?: AIProviderView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<AIProviderView> | AIProviderView), options?: RequestHandlerOptions) => {
  return http.post('*/api/ai-providers', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateAiProviderResponseMock(),
      { status: 201
      })
  }, options)
}

export const getGetAiProviderMockHandler = (overrideResponse?: AIProviderView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<AIProviderView> | AIProviderView), options?: RequestHandlerOptions) => {
  return http.get('*/api/ai-providers/:providerId', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetAiProviderResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUpdateAiProviderMockHandler = (overrideResponse?: AIProviderView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<AIProviderView> | AIProviderView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/ai-providers/:providerId', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateAiProviderResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteAiProviderMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/ai-providers/:providerId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getSetDefaultAiProviderMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.post('*/api/ai-providers/:providerId/default', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getTestAiProviderConnectionMockHandler = (overrideResponse?: AIProviderConnectionTestResult | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<AIProviderConnectionTestResult> | AIProviderConnectionTestResult), options?: RequestHandlerOptions) => {
  return http.post('*/api/ai-providers/:providerId/test', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getTestAiProviderConnectionResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCreateConnectionMockHandler = (overrideResponse?: ConnectionView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ConnectionView> | ConnectionView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/connections', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateConnectionResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListConnectionsMockHandler = (overrideResponse?: ConnectionView[] | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ConnectionView[]> | ConnectionView[]), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/connections', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListConnectionsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListConnectionProvidersMockHandler = (overrideResponse?: ConnectionProviderView[] | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ConnectionProviderView[]> | ConnectionProviderView[]), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/connections/catalog', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListConnectionProvidersResponseMock(),
      { status: 200
      })
  }, options)
}

export const getGetConnectionMockHandler = (overrideResponse?: ConnectionView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ConnectionView> | ConnectionView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/connections/:connectionId', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetConnectionResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUpdateConnectionMockHandler = (overrideResponse?: ConnectionView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<ConnectionView> | ConnectionView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId/connections/:connectionId', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateConnectionResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteConnectionMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/connections/:connectionId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getTestConnectionMockHandler = (overrideResponse?: ConnectionTestResult | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ConnectionTestResult> | ConnectionTestResult), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/connections/:connectionId/test', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getTestConnectionResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCreateExecutionImageMockHandler = (overrideResponse?: ExecutionImageView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ExecutionImageView> | ExecutionImageView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/execution-images', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCreateExecutionImageResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListExecutionImagesMockHandler = (overrideResponse?: ExecutionImageView[] | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ExecutionImageView[]> | ExecutionImageView[]), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/execution-images', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListExecutionImagesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getGetExecutionImageMockHandler = (overrideResponse?: ExecutionImageView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ExecutionImageView> | ExecutionImageView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/execution-images/:imageId', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetExecutionImageResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUpdateExecutionImageMockHandler = (overrideResponse?: ExecutionImageView | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<ExecutionImageView> | ExecutionImageView), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId/execution-images/:imageId', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateExecutionImageResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteExecutionImageMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/execution-images/:imageId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getSetDefaultExecutionImageMockHandler = (overrideResponse?: ExecutionImageView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ExecutionImageView> | ExecutionImageView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/execution-images/:imageId/set-default', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetDefaultExecutionImageResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListLibraryItemsMockHandler = (overrideResponse?: LibraryCatalogView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<LibraryCatalogView> | LibraryCatalogView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/library/items', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListLibraryItemsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadLibraryItemFileMockHandler = (overrideResponse?: LibraryFileView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<LibraryFileView> | LibraryFileView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/library/items/:kind/:key/file', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadLibraryItemFileResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUninstallLibraryItemMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/library/items/:kind/:key', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getInstallLibraryItemMockHandler = (overrideResponse?: LibraryInstallResult | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<LibraryInstallResult> | LibraryInstallResult), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/library/install', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getInstallLibraryItemResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUpdateLibraryItemMockHandler = (overrideResponse?: LibraryInstallResult | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<LibraryInstallResult> | LibraryInstallResult), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/library/update', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateLibraryItemResponseMock(),
      { status: 200
      })
  }, options)
}

export const getPreviewWorkflowBundleMockHandler = (overrideResponse?: BundlePreview | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<BundlePreview> | BundlePreview), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/workflows/:workflowId/bundle/preview', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getPreviewWorkflowBundleResponseMock(),
      { status: 200
      })
  }, options)
}

export const getExportWorkflowBundleMockHandler = (overrideResponse?: ArrayBuffer | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ArrayBuffer> | ArrayBuffer), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/workflows/:workflowId/bundle', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {

  const binaryBody = overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getExportWorkflowBundleResponseMock();
    return HttpResponse.arrayBuffer(
      binaryBody instanceof ArrayBuffer
        ? binaryBody
        : new ArrayBuffer(0),
      { status: 200,
        headers: { 'Content-Type': 'application/octet-stream' }
      })
  }, options)
}

export const getPreflightBundleImportMockHandler = (overrideResponse?: BundleImportPreview | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<BundleImportPreview> | BundleImportPreview), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/bundles/preflight', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getPreflightBundleImportResponseMock(),
      { status: 200
      })
  }, options)
}

export const getImportBundleMockHandler = (overrideResponse?: BundleImportResult | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<BundleImportResult> | BundleImportResult), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/bundles/import', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getImportBundleResponseMock(),
      { status: 200
      })
  }, options)
}

export const getPreviewShareToLibraryMockHandler = (overrideResponse?: SharePreview | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<SharePreview> | SharePreview), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/workflows/:workflowId/share/preview', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getPreviewShareToLibraryResponseMock(),
      { status: 200
      })
  }, options)
}

export const getShareToLibraryMockHandler = (overrideResponse?: ShareResult | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ShareResult> | ShareResult), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/workflows/:workflowId/share', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getShareToLibraryResponseMock(),
      { status: 200
      })
  }, options)
}

export const getTakeDownSharedItemMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/library/shared/:kind/:key', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getPreviewTakeDownSetMockHandler = (overrideResponse?: TakeDownPreview | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TakeDownPreview> | TakeDownPreview), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/library/shared/:kind/:key/take-down', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getPreviewTakeDownSetResponseMock(),
      { status: 200
      })
  }, options)
}

export const getTakeDownSetMockHandler = (overrideResponse?: TakeDownResult | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<TakeDownResult> | TakeDownResult), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/library/shared/:kind/:key/take-down', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getTakeDownSetResponseMock(),
      { status: 200
      })
  }, options)
}

export const getListNotificationsMockHandler = (overrideResponse?: NotificationPage | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<NotificationPage> | NotificationPage), options?: RequestHandlerOptions) => {
  return http.get('*/api/notifications', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListNotificationsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadUnreadNotificationCountMockHandler = (overrideResponse?: UnreadCount | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<UnreadCount> | UnreadCount), options?: RequestHandlerOptions) => {
  return http.get('*/api/notifications/unread-count', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadUnreadNotificationCountResponseMock(),
      { status: 200
      })
  }, options)
}

export const getCountNotificationsMockHandler = (overrideResponse?: NotificationCount | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<NotificationCount> | NotificationCount), options?: RequestHandlerOptions) => {
  return http.get('*/api/notifications/count', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getCountNotificationsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadNotificationPreferencesMockHandler = (overrideResponse?: NotificationPreferences | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<NotificationPreferences> | NotificationPreferences), options?: RequestHandlerOptions) => {
  return http.get('*/api/notifications/preferences', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadNotificationPreferencesResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetNotificationPreferenceMockHandler = (overrideResponse?: NotificationPreferences | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<NotificationPreferences> | NotificationPreferences), options?: RequestHandlerOptions) => {
  return http.put('*/api/notifications/preferences/:group/:channel', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetNotificationPreferenceResponseMock(),
      { status: 200
      })
  }, options)
}

export const getMuteWorkspaceNotificationsMockHandler = (overrideResponse?: NotificationPreferences | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<NotificationPreferences> | NotificationPreferences), options?: RequestHandlerOptions) => {
  return http.put('*/api/notifications/mutes/:workspaceId', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getMuteWorkspaceNotificationsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUnmuteWorkspaceNotificationsMockHandler = (overrideResponse?: NotificationPreferences | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<NotificationPreferences> | NotificationPreferences), options?: RequestHandlerOptions) => {
  return http.delete('*/api/notifications/mutes/:workspaceId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUnmuteWorkspaceNotificationsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetNotificationQuietHoursMockHandler = (overrideResponse?: NotificationPreferences | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<NotificationPreferences> | NotificationPreferences), options?: RequestHandlerOptions) => {
  return http.put('*/api/notifications/quiet-hours', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetNotificationQuietHoursResponseMock(),
      { status: 200
      })
  }, options)
}

export const getClearNotificationQuietHoursMockHandler = (overrideResponse?: NotificationPreferences | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<NotificationPreferences> | NotificationPreferences), options?: RequestHandlerOptions) => {
  return http.delete('*/api/notifications/quiet-hours', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getClearNotificationQuietHoursResponseMock(),
      { status: 200
      })
  }, options)
}

export const getStreamNotificationsMockHandler = (overrideResponse?: string | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<string> | string), options?: RequestHandlerOptions) => {
  return http.get('*/api/notifications/stream', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {

  const resolvedBody = overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getStreamNotificationsResponseMock();
    const textBody = typeof resolvedBody === 'string' ? resolvedBody : JSON.stringify(resolvedBody ?? null);
    return HttpResponse.text(textBody,
      { status: 200
      })
  }, options)
}

export const getMarkAllNotificationsReadMockHandler = (overrideResponse?: InboxChange | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<InboxChange> | InboxChange), options?: RequestHandlerOptions) => {
  return http.post('*/api/notifications/read-all', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getMarkAllNotificationsReadResponseMock(),
      { status: 200
      })
  }, options)
}

export const getArchiveAllNotificationsMockHandler = (overrideResponse?: InboxChange | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<InboxChange> | InboxChange), options?: RequestHandlerOptions) => {
  return http.post('*/api/notifications/archive-all', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getArchiveAllNotificationsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getChangeNotificationsMockHandler = (overrideResponse?: InboxChange | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<InboxChange> | InboxChange), options?: RequestHandlerOptions) => {
  return http.post('*/api/notifications/bulk', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getChangeNotificationsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getMarkTaskNotificationsReadMockHandler = (overrideResponse?: UnreadCount | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<UnreadCount> | UnreadCount), options?: RequestHandlerOptions) => {
  return http.post('*/api/notifications/tasks/:taskId/read', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getMarkTaskNotificationsReadResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadNotificationMockHandler = (overrideResponse?: NotificationView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<NotificationView> | NotificationView), options?: RequestHandlerOptions) => {
  return http.get('*/api/notifications/:notificationId', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadNotificationResponseMock(),
      { status: 200
      })
  }, options)
}

export const getMarkNotificationReadMockHandler = (overrideResponse?: NotificationView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<NotificationView> | NotificationView), options?: RequestHandlerOptions) => {
  return http.post('*/api/notifications/:notificationId/read', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getMarkNotificationReadResponseMock(),
      { status: 200
      })
  }, options)
}

export const getMarkNotificationUnreadMockHandler = (overrideResponse?: NotificationView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<NotificationView> | NotificationView), options?: RequestHandlerOptions) => {
  return http.post('*/api/notifications/:notificationId/unread', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getMarkNotificationUnreadResponseMock(),
      { status: 200
      })
  }, options)
}

export const getAcknowledgeNotificationMockHandler = (overrideResponse?: NotificationView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<NotificationView> | NotificationView), options?: RequestHandlerOptions) => {
  return http.post('*/api/notifications/:notificationId/acknowledge', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getAcknowledgeNotificationResponseMock(),
      { status: 200
      })
  }, options)
}

export const getArchiveNotificationMockHandler = (overrideResponse?: NotificationView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<NotificationView> | NotificationView), options?: RequestHandlerOptions) => {
  return http.post('*/api/notifications/:notificationId/archive', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getArchiveNotificationResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadNotificationPolicyMockHandler = (overrideResponse?: NotificationPolicy | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<NotificationPolicy> | NotificationPolicy), options?: RequestHandlerOptions) => {
  return http.get('*/api/admin/notification-policy', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadNotificationPolicyResponseMock(),
      { status: 200
      })
  }, options)
}

export const getSetNotificationPolicyMockHandler = (overrideResponse?: NotificationPolicy | ((info: Parameters<Parameters<typeof http.put>[1]>[0]) => Promise<NotificationPolicy> | NotificationPolicy), options?: RequestHandlerOptions) => {
  return http.put('*/api/admin/notification-policy', async (info: Parameters<Parameters<typeof http.put>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getSetNotificationPolicyResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUnsubscribeFromEmailMockHandler = (overrideResponse?: string | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<string> | string), options?: RequestHandlerOptions) => {
  return http.get('*/api/notifications/email/unsubscribe', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {

  const resolvedBody = overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUnsubscribeFromEmailResponseMock();
    const textBody = typeof resolvedBody === 'string' ? resolvedBody : JSON.stringify(resolvedBody ?? null);
    return HttpResponse.html(textBody,
      { status: 200
      })
  }, options)
}

export const getUnsubscribeFromEmailOneClickMockHandler = (overrideResponse?: string | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<string> | string), options?: RequestHandlerOptions) => {
  return http.post('*/api/notifications/email/unsubscribe', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {

  const resolvedBody = overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUnsubscribeFromEmailOneClickResponseMock();
    const textBody = typeof resolvedBody === 'string' ? resolvedBody : JSON.stringify(resolvedBody ?? null);
    return HttpResponse.html(textBody,
      { status: 200
      })
  }, options)
}

export const getRegisterWorkspaceFileChangesMockHandler = (overrideResponse?: RegisterChangesResponse | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<RegisterChangesResponse> | RegisterChangesResponse), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/files/register', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getRegisterWorkspaceFileChangesResponseMock(),
      { status: 201
      })
  }, options)
}

export const getGetScheduleContextMockHandler = (overrideResponse?: ScheduleContextView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ScheduleContextView> | ScheduleContextView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/schedule-context', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetScheduleContextResponseMock(),
      { status: 200
      })
  }, options)
}

export const getPostScheduleResultMockHandler = (overrideResponse?: ScheduleResultView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ScheduleResultView> | ScheduleResultView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/schedule-result', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getPostScheduleResultResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListAgentSubtasksMockHandler = (overrideResponse?: AgentSubtaskList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<AgentSubtaskList> | AgentSubtaskList), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/agent-view/children', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListAgentSubtasksResponseMock(),
      { status: 200
      })
  }, options)
}

export const getGetAgentSubtaskMockHandler = (overrideResponse?: AgentSubtaskDetail | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<AgentSubtaskDetail> | AgentSubtaskDetail), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/agent-view/children/:childId', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetAgentSubtaskResponseMock(),
      { status: 200
      })
  }, options)
}

export const getUpdateAgentSubtaskMockHandler = (overrideResponse?: AgentSubtask | ((info: Parameters<Parameters<typeof http.patch>[1]>[0]) => Promise<AgentSubtask> | AgentSubtask), options?: RequestHandlerOptions) => {
  return http.patch('*/api/workspaces/:workspaceId/tasks/:taskId/agent-view/children/:childId', async (info: Parameters<Parameters<typeof http.patch>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getUpdateAgentSubtaskResponseMock(),
      { status: 200
      })
  }, options)
}

export const getDeleteAgentSubtaskMockHandler = (overrideResponse?: void | ((info: Parameters<Parameters<typeof http.delete>[1]>[0]) => Promise<void> | void), options?: RequestHandlerOptions) => {
  return http.delete('*/api/workspaces/:workspaceId/tasks/:taskId/agent-view/children/:childId', async (info: Parameters<Parameters<typeof http.delete>[1]>[0]) => {
  if (typeof overrideResponse === 'function') {await overrideResponse(info); }

    return new HttpResponse(null,
      { status: 204
      })
  }, options)
}

export const getRegisterTaskExternalReferenceMockHandler = (overrideResponse?: ExternalReferenceView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ExternalReferenceView> | ExternalReferenceView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/agent-view/external-references', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getRegisterTaskExternalReferenceResponseMock(),
      { status: 201
      })
  }, options)
}

export const getGetTaskAgentViewMockHandler = (overrideResponse?: TaskView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TaskView> | TaskView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/agent-view', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetTaskAgentViewResponseMock(),
      { status: 200
      })
  }, options)
}

export const getGetTaskAgentViewCommentsMockHandler = (overrideResponse?: AgentTaskCommentList | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<AgentTaskCommentList> | AgentTaskCommentList), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/agent-view/comments', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetTaskAgentViewCommentsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getAddTaskAgentViewCommentMockHandler = (overrideResponse?: TaskCommentView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<TaskCommentView> | TaskCommentView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/agent-view/comments', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getAddTaskAgentViewCommentResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListWorkspaceTasksAgentViewMockHandler = (overrideResponse?: TaskView[] | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TaskView[]> | TaskView[]), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/agent-view/tasks', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListWorkspaceTasksAgentViewResponseMock(),
      { status: 200
      })
  }, options)
}

export const getMoveTaskFromBacklogMockHandler = (overrideResponse?: MoveTaskResponse | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<MoveTaskResponse> | MoveTaskResponse), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/move-from-backlog', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getMoveTaskFromBacklogResponseMock(),
      { status: 200
      })
  }, options)
}

export const getRecordExecutionResultMockHandler = (overrideResponse?: RecordExecutionResultResponse | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<RecordExecutionResultResponse> | RecordExecutionResultResponse), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/result', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getRecordExecutionResultResponseMock(),
      { status: 201
      })
  }, options)
}

export const getListArtifactsForExecutionMockHandler = (overrideResponse?: TaskArtifactsView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<TaskArtifactsView> | TaskArtifactsView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/artifacts', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getListArtifactsForExecutionResponseMock(),
      { status: 200
      })
  }, options)
}

export const getRecordArtifactEventsMockHandler = (overrideResponse?: RecordArtifactEventsResponse | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<RecordArtifactEventsResponse> | RecordArtifactEventsResponse), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/artifacts', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getRecordArtifactEventsResponseMock(),
      { status: 200
      })
  }, options)
}

export const getReadRunForgeTokenMockHandler = (overrideResponse?: RunForgeToken | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<RunForgeToken> | RunForgeToken), options?: RequestHandlerOptions) => {
  return http.get('*/api/executions/current/forge-token', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getReadRunForgeTokenResponseMock(),
      { status: 200
      })
  }, options)
}

export const getGetReplyContextMockHandler = (overrideResponse?: ReplyContextView | ((info: Parameters<Parameters<typeof http.get>[1]>[0]) => Promise<ReplyContextView> | ReplyContextView), options?: RequestHandlerOptions) => {
  return http.get('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/reply-context', async (info: Parameters<Parameters<typeof http.get>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getGetReplyContextResponseMock(),
      { status: 200
      })
  }, options)
}

export const getPostReplyAnswerMockHandler = (overrideResponse?: ReplyAnswerView | ((info: Parameters<Parameters<typeof http.post>[1]>[0]) => Promise<ReplyAnswerView> | ReplyAnswerView), options?: RequestHandlerOptions) => {
  return http.post('*/api/workspaces/:workspaceId/tasks/:taskId/executions/:executionId/reply-answer', async (info: Parameters<Parameters<typeof http.post>[1]>[0]) => {


    return HttpResponse.json(overrideResponse !== undefined
    ? (typeof overrideResponse === "function" ? await overrideResponse(info) : overrideResponse)
    : getPostReplyAnswerResponseMock(),
      { status: 201
      })
  }, options)
}
export const getProductXNextServiceMock = () => [
  getHealthMockHandler(),
  getReadCurrentUserMockHandler(),
  getSetCurrentUserTimezoneMockHandler(),
  getReadCurrentUserCommitEmailMockHandler(),
  getSetCurrentUserCommitEmailMockHandler(),
  getListAssignableUsersMockHandler(),
  getListMyForgeAccountsMockHandler(),
  getListForgeHostsMockHandler(),
  getLinkForgeAccountMockHandler(),
  getUnlinkForgeAccountMockHandler(),
  getRevokeForgeAccountMockHandler(),
  getRemindForgeAccountMockHandler(),
  getListPeopleForgeAccountsMockHandler(),
  getListForgePoliciesMockHandler(),
  getSetForgePolicyMockHandler(),
  getReadPersonalAccountStandingMockHandler(),
  getListForgeAppsMockHandler(),
  getSetForgeAppMockHandler(),
  getRemoveForgeAppMockHandler(),
  getStartForgeOauthMockHandler(),
  getFinishForgeOauthMockHandler(),
  getListMyAiToolsMockHandler(),
  getDisconnectMyAiToolsMockHandler(),
  getCreateWorkspaceMockHandler(),
  getListWorkspacesMockHandler(),
  getReadWorkspaceMockHandler(),
  getUpdateWorkspaceMockHandler(),
  getDeleteWorkspaceMockHandler(),
  getListWorkspaceMembersMockHandler(),
  getAddWorkspaceMemberMockHandler(),
  getUpdateWorkspaceMemberMockHandler(),
  getRemoveWorkspaceMemberMockHandler(),
  getListWorkspacePeopleMockHandler(),
  getRequestWorkspaceAccessMockHandler(),
  getListWorkspaceAccessRequestsMockHandler(),
  getReadMyWorkspaceAccessRequestMockHandler(),
  getAddFromWorkspaceAccessRequestMockHandler(),
  getDismissWorkspaceAccessRequestMockHandler(),
  getReadWorkspaceAccessLogMockHandler(),
  getCreateWorkflowMockHandler(),
  getCreateWorkflowDraftMockHandler(),
  getReplaceWorkflowMockHandler(),
  getRenameWorkflowMockHandler(),
  getDeleteWorkflowMockHandler(),
  getGetWorkflowForEditingMockHandler(),
  getSaveWorkflowPendingChangesMockHandler(),
  getDiscardWorkflowPendingChangesMockHandler(),
  getPublishWorkflowPendingChangesMockHandler(),
  getImportWorkflowMockHandler(),
  getPreflightWorkflowMockHandler(),
  getCreateTaskMockHandler(),
  getStopTaskMockHandler(),
  getRerunTaskMockHandler(),
  getHideTaskMockHandler(),
  getUnhideTaskMockHandler(),
  getMoveTaskMockHandler(),
  getStartLinkedTasksMockHandler(),
  getSplitTaskMockHandler(),
  getSetTaskStepAssigneeMockHandler(),
  getClearTaskStepAssigneeMockHandler(),
  getRenameTaskMockHandler(),
  getSetTaskDescriptionMockHandler(),
  getAddTaskCommentMockHandler(),
  getListTaskCommentsMockHandler(),
  getListTaskMovesMockHandler(),
  getListRunActivityMockHandler(),
  getAddSchedulerNoteMockHandler(),
  getGetExecutionTranscriptMockHandler(),
  getCreateTaskRelationMockHandler(),
  getDeleteTaskRelationMockHandler(),
  getGetDirectTaskRelationsMockHandler(),
  getGetRelatedTasksMockHandler(),
  getCreateRelatedTaskMockHandler(),
  getGetTaskSiblingsMockHandler(),
  getReadTaskFollowMockHandler(),
  getFollowTaskMockHandler(),
  getUnfollowTaskMockHandler(),
  getReplaceTaskLabelsMockHandler(),
  getDeleteTaskMockHandler(),
  getGetTaskResultsMockHandler(),
  getGetTaskArtifactsMockHandler(),
  getPostRunThreadMessageMockHandler(),
  getListRunThreadMessagesMockHandler(),
  getRetryRunThreadReplyMockHandler(),
  getAddRunThreadMessageToTaskContextMockHandler(),
  getAttachFileToTaskMockHandler(),
  getListTaskAttachmentsMockHandler(),
  getRemoveTaskAttachmentMockHandler(),
  getListWorkspaceFilesMockHandler(),
  getDeleteWorkspaceFileMockHandler(),
  getReadWorkspaceFileContentMockHandler(),
  getUploadWorkspaceFileMockHandler(),
  getMoveWorkspaceFileMockHandler(),
  getGetWorkspaceFileHistoryMockHandler(),
  getListWorkspaceSecretsMockHandler(),
  getCreateWorkspaceSecretMockHandler(),
  getReplaceWorkspaceSecretValueMockHandler(),
  getUpdateWorkspaceSecretDescriptionMockHandler(),
  getDeleteWorkspaceSecretMockHandler(),
  getCreateTriggerProfileMockHandler(),
  getListTriggerProfilesMockHandler(),
  getGetTriggerProfileMockHandler(),
  getUpdateTriggerProfileMockHandler(),
  getDeleteTriggerProfileMockHandler(),
  getListTriggerProfileRunsMockHandler(),
  getListTriggerProfileSignalsMockHandler(),
  getGetVariableManifestMockHandler(),
  getCreateLabelMockHandler(),
  getUpdateLabelMockHandler(),
  getDeleteLabelMockHandler(),
  getReadWorkspaceUsageMockHandler(),
  getReadWorkspaceAiModelsMockHandler(),
  getReadWorkspaceAiModelSpendMockHandler(),
  getReadOrganizationUsageMockHandler(),
  getSetModelPriceMockHandler(),
  getReadUnpricedModelsMockHandler(),
  getReadModelPricesMockHandler(),
  getReadWorkflowLimitsMockHandler(),
  getSetWorkflowLimitMockHandler(),
  getClearWorkflowLimitMockHandler(),
  getSetDefaultLimitMockHandler(),
  getSetWorkspaceLimitMockHandler(),
  getClearWorkspaceLimitMockHandler(),
  getReadWorkspaceConcurrencyMockHandler(),
  getReadOrganizationConcurrencyMockHandler(),
  getSetDefaultConcurrencyLimitMockHandler(),
  getClearDefaultConcurrencyLimitMockHandler(),
  getSetWorkspaceConcurrencyLimitMockHandler(),
  getClearWorkspaceConcurrencyLimitMockHandler(),
  getReadWorkspaceMemoryMockHandler(),
  getReadOrganizationMemoryMockHandler(),
  getSetDefaultMemoryLimitMockHandler(),
  getClearDefaultMemoryLimitMockHandler(),
  getSetWorkspaceMemoryLimitMockHandler(),
  getClearWorkspaceMemoryLimitMockHandler(),
  getReadOrganizationMockHandler(),
  getUpdateOrganizationMockHandler(),
  getListOrganizationWorkspacesMockHandler(),
  getExportOrganizationWorkspacesMockHandler(),
  getReadOrganizationAccessLogMockHandler(),
  getExportOrganizationAccessLogMockHandler(),
  getListLlmProfilesMockHandler(),
  getCreateLlmProfileMockHandler(),
  getReadLlmProfilesAdminOverviewMockHandler(),
  getUpdateLlmProfileMockHandler(),
  getDeleteLlmProfileMockHandler(),
  getSetDefaultLlmProfileMockHandler(),
  getListAiProvidersMockHandler(),
  getCreateAiProviderMockHandler(),
  getGetAiProviderMockHandler(),
  getUpdateAiProviderMockHandler(),
  getDeleteAiProviderMockHandler(),
  getSetDefaultAiProviderMockHandler(),
  getTestAiProviderConnectionMockHandler(),
  getCreateConnectionMockHandler(),
  getListConnectionsMockHandler(),
  getListConnectionProvidersMockHandler(),
  getGetConnectionMockHandler(),
  getUpdateConnectionMockHandler(),
  getDeleteConnectionMockHandler(),
  getTestConnectionMockHandler(),
  getCreateExecutionImageMockHandler(),
  getListExecutionImagesMockHandler(),
  getGetExecutionImageMockHandler(),
  getUpdateExecutionImageMockHandler(),
  getDeleteExecutionImageMockHandler(),
  getSetDefaultExecutionImageMockHandler(),
  getListLibraryItemsMockHandler(),
  getReadLibraryItemFileMockHandler(),
  getUninstallLibraryItemMockHandler(),
  getInstallLibraryItemMockHandler(),
  getUpdateLibraryItemMockHandler(),
  getPreviewWorkflowBundleMockHandler(),
  getExportWorkflowBundleMockHandler(),
  getPreflightBundleImportMockHandler(),
  getImportBundleMockHandler(),
  getPreviewShareToLibraryMockHandler(),
  getShareToLibraryMockHandler(),
  getTakeDownSharedItemMockHandler(),
  getPreviewTakeDownSetMockHandler(),
  getTakeDownSetMockHandler(),
  getListNotificationsMockHandler(),
  getReadUnreadNotificationCountMockHandler(),
  getCountNotificationsMockHandler(),
  getReadNotificationPreferencesMockHandler(),
  getSetNotificationPreferenceMockHandler(),
  getMuteWorkspaceNotificationsMockHandler(),
  getUnmuteWorkspaceNotificationsMockHandler(),
  getSetNotificationQuietHoursMockHandler(),
  getClearNotificationQuietHoursMockHandler(),
  getStreamNotificationsMockHandler(),
  getMarkAllNotificationsReadMockHandler(),
  getArchiveAllNotificationsMockHandler(),
  getChangeNotificationsMockHandler(),
  getMarkTaskNotificationsReadMockHandler(),
  getReadNotificationMockHandler(),
  getMarkNotificationReadMockHandler(),
  getMarkNotificationUnreadMockHandler(),
  getAcknowledgeNotificationMockHandler(),
  getArchiveNotificationMockHandler(),
  getReadNotificationPolicyMockHandler(),
  getSetNotificationPolicyMockHandler(),
  getUnsubscribeFromEmailMockHandler(),
  getUnsubscribeFromEmailOneClickMockHandler(),
  getRegisterWorkspaceFileChangesMockHandler(),
  getGetScheduleContextMockHandler(),
  getPostScheduleResultMockHandler(),
  getListAgentSubtasksMockHandler(),
  getGetAgentSubtaskMockHandler(),
  getUpdateAgentSubtaskMockHandler(),
  getDeleteAgentSubtaskMockHandler(),
  getRegisterTaskExternalReferenceMockHandler(),
  getGetTaskAgentViewMockHandler(),
  getGetTaskAgentViewCommentsMockHandler(),
  getAddTaskAgentViewCommentMockHandler(),
  getListWorkspaceTasksAgentViewMockHandler(),
  getMoveTaskFromBacklogMockHandler(),
  getRecordExecutionResultMockHandler(),
  getListArtifactsForExecutionMockHandler(),
  getRecordArtifactEventsMockHandler(),
  getReadRunForgeTokenMockHandler(),
  getGetReplyContextMockHandler(),
  getPostReplyAnswerMockHandler()
]
